import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Banknote,
  Bell,
  Building2,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  History,
  IndianRupee,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Plus,
  Printer,
  Search,
  Settings,
  ShieldCheck,
  Target,
  UserRound,
  UsersRound,
  XCircle
} from "lucide-react";
import { DEFAULT_SETTINGS, WEEKDAYS } from "./data/seedData.js";
import { currency, eachDate, formatDisplayDate, formatMonth, monthRange, toDateOnly } from "./lib/date.js";
import { generatePayrollRun } from "./lib/payroll.js";
import { buildDailyAttendance } from "./lib/attendance.js";
import { validateGeofence } from "./lib/geofence.js";
import { downloadBlob, exportFullBackupExcel, exportPayrollExcel, generateSalarySlipPdf } from "./lib/exporters.js";
import {
  createAuditLog,
  hasSupabaseConfig,
  loadAppData,
  persistAuditLog,
  persistEntity,
  fetchEntityById,
  saveLocalState,
  getAttendancePhotoUrl,
  uploadAttendancePhoto,
  uuid,
  createAuthAccount,
  deleteEntity
} from "./services/db.js";
import { getFileUrl, uploadCompressedImage } from "./services/fileUpload.js";
import { supabase } from "./services/supabaseClient.js";
import NewOrderV2 from "./components/NewOrderV2.jsx";
import PurchaseV1 from "./components/PurchaseV1.jsx";
import InventoryV1 from "./components/InventoryV1.jsx";
import InternalChatV1 from "./components/InternalChatV1.jsx";
import HrmsV2Preview from "./components/HrmsV2Preview.jsx";

const APP_UPDATE_LOG = {
  version: "2026-06-22.5",
  title: "Latest update: Business role presets",
  changes: [
    "Users now has business role presets like Store Manager, CN Manager, Lab / Order Manager, Inventory Manager, Purchase Manager, Accounts, Audit Manager, HR Admin and Director / CEO.",
    "Selecting a preset automatically sets the correct app role, designation, store access style and permissions.",
    "Manual permissions are now grouped by department so access control is easier to understand.",
    "Employee login creation now also supports permission presets from the same Add/Edit Employee panel.",
    "The user detail view now shows Business role clearly instead of only technical permission keys."
  ]
};

const TABS = [
  ["dashboard", "My Cockpit", ShieldCheck],
  ["optical", "Optical CRM", FileText],
  ["newOrderV2", "New Order V2", FileText],
  ["purchaseV1", "Purchase V1", FileText],
  ["inventoryV1", "Inventory V1", FileText],
  ["stores", "Stores", Building2],
  ["hrms", "HRMS", CalendarDays],
  ["hrmsV2Preview", "HRMS V2 Preview", CalendarDays],
  ["tasks", "Tasks", ClipboardList],
  ["internalChatV1", "Internal Chat", FileText],
  ["communication", "Communication", FileText],
  ["reports", "Reports", Download],
  ["tutorialHelp", "Tutorial / Help", HelpCircle],
  ["backup", "Backup", Download],
  ["companyLedger", "Company Ledger", Banknote],
  ["users", "Users", UsersRound],
  ["dailyReconciliation", "Daily Reconciliation", Banknote]
];

const emptyEmployee = {
  employeeCode: "",
  fullName: "",
  phone: "",
  email: "",
  storeId: "",
  designation: "",
  reportingManagerId: "",
  monthlySalary: 0,
  weeklyOffDay: "",
  joiningDate: new Date().toISOString().slice(0, 10),
  exitDate: "",
  active: true,
  openingLeaveBalance: 0,
  openingLoanBalance: 0,
  incentiveEligible: true,
  reimbursementEligible: true,
  bankDetails: "",
  notes: ""
};

const PHONE_LOGIN_DOMAIN = "phone.framesbay.in";
const PRODUCT_NAME = "SparkingCircle";
const TASK_ARCHIVE_DAYS = 30;

function getCompanyName(settings) {
  return String(settings?.companyName || "Your Company").trim() || "Your Company";
}

function getAppName(settings) {
  const appName = String(settings?.appName || PRODUCT_NAME).trim();
  if (!appName || appName === "Attendance & Payroll" || appName === "StaffPay") return PRODUCT_NAME;
  return appName;
}

function getReportingTeamEmployees(allEmployees, managerEmployeeId) {
  if (!managerEmployeeId) return [];
  const team = [];
  const seen = new Set([managerEmployeeId]);
  let frontier = [managerEmployeeId];
  while (frontier.length) {
    const next = [];
    for (const managerId of frontier) {
      allEmployees
        .filter((employee) => employee.reportingManagerId === managerId && !seen.has(employee.id))
        .forEach((employee) => {
          seen.add(employee.id);
          team.push(employee);
          next.push(employee.id);
        });
    }
    frontier = next;
  }
  return team;
}

function getImmediateReportEmployees(allEmployees, managerEmployeeId) {
  if (!managerEmployeeId) return [];
  return allEmployees.filter((employee) => employee.reportingManagerId === managerEmployeeId && employee.active !== false);
}

function getVisibleEmployeesForUser(state, user) {
  if (user.role === "super_admin" || user.permissions?.allEmployees) return state.employees;
  const teamEmployees = getReportingTeamEmployees(state.employees, user.employeeId);
  if (teamEmployees.length) return teamEmployees;
  const visibleStoreIds = user.storeIds || [];
  return state.employees.filter((employee) => visibleStoreIds.includes(employee.storeId));
}

function buildInternalChatEmployees(state = {}, user = {}) {
  const employees = Array.isArray(state.employees) ? state.employees : [];
  const users = Array.isArray(state.users) ? state.users : [];
  const byId = new Map();

  const upsert = (employee) => {
    if (!employee) return;
    const id = employee.id || employee.employeeId || employee.phone || employee.email;
    if (!id) return;
    const existing = byId.get(id) || {};
    byId.set(id, {
      ...existing,
      ...employee,
      id,
      active: employee.active !== false && existing.active !== false,
      fullName: employee.fullName || employee.name || existing.fullName || existing.name || employee.email || employee.phone || "Employee",
      phone: employee.phone || employee.mobile || employee.phoneNumber || existing.phone || existing.mobile || existing.phoneNumber || "",
      email: employee.email || existing.email || "",
      designation: employee.designation || existing.designation || employee.role || existing.role || "Employee",
      storeId: employee.storeId || existing.storeId || employee.storeIds?.[0] || existing.storeIds?.[0] || ""
    });
  };

  employees.forEach(upsert);
  users.forEach((login) => {
    if (!login || login.active === false) return;
    const linkedEmployee = employees.find((employee) => employee.id === login.employeeId) || {};
    upsert({
      ...linkedEmployee,
      id: login.employeeId || linkedEmployee.id || login.id || login.email,
      employeeId: login.employeeId || linkedEmployee.id || login.id,
      fullName: linkedEmployee.fullName || login.name || login.fullName || login.email,
      phone: linkedEmployee.phone || login.phone || login.mobile || login.phoneNumber || "",
      email: linkedEmployee.email || login.email || "",
      designation: linkedEmployee.designation || login.designation || login.role || "Employee",
      storeId: linkedEmployee.storeId || login.storeId || login.storeIds?.[0] || "",
      active: linkedEmployee.active !== false && login.active !== false
    });
  });

  if (user?.employeeId || user?.id || user?.email) {
    const currentEmployee = employees.find((employee) => employee.id === user.employeeId) || {};
    upsert({
      ...currentEmployee,
      id: user.employeeId || currentEmployee.id || user.id || user.email,
      fullName: currentEmployee.fullName || user.name || user.fullName || user.email || "Me",
      phone: currentEmployee.phone || user.phone || user.mobile || "",
      email: currentEmployee.email || user.email || "",
      designation: currentEmployee.designation || user.role || "Employee",
      storeId: currentEmployee.storeId || user.storeIds?.[0] || "",
      active: true
    });
  }

  return Array.from(byId.values())
    .filter((employee) => employee.active !== false)
    .sort((a, b) => String(a.fullName || a.name || "").localeCompare(String(b.fullName || b.name || "")));
}


function hasAppPermission(user, key) {
  if (user.role === "super_admin") return true;
  if (key === "dashboard") return true;
  if (key === "newOrderV2") return true;
  if (key === "purchaseV1") return true;
  if (key === "inventoryV1") return true;
  if (key === "internalChatV1") return true;
  if (key === "hrmsV2Preview") return Boolean(user.role === "super_admin" || user.role === "admin" || user.permissions?.all || user.permissions?.attendance || user.permissions?.payroll || user.permissions?.reports || user.permissions?.can_manage_users);
  if (key === "tutorialHelp") return true;
  const permissions = user.permissions || {};
  const hasDetailedPermissions = Object.values(permissions).some(Boolean);
  if (!hasDetailedPermissions) return true;
  if (key === "users") return Boolean(permissions.users || permissions.can_manage_users || permissions.can_manage_roles);
  if (key === "reports") return Boolean(permissions.reports || permissions.can_view_reports);
  if (key === "communication") return Boolean(permissions.communication || permissions.marketing || permissions.opticalOrders);
  if (key === "cnTracking") {
    return Boolean(
      permissions.cnTracking ||
      permissions.opticalOrders ||
      CN_PERMISSIONS.some((permission) => permissions[permission])
    );
  }
  if (key === "companyLedger") return Boolean(permissions.companyLedger || permissions.reports);
  if (key === "dailyReconciliation") {
    return hasUserPermission(user, "can_access_daily_reconciliation") || hasUserPermission(user, "can_mark_reconciliation_done");
  }
  const permissionKey = {
    users: "users",
    stores: "stores",
    hrms: "attendance",
    optical: "opticalOrders",
    communication: "communication",
    tasks: "work",
    cnTracking: "cnTracking",
    companyLedger: "companyLedger",
    corrections: "corrections",
    advances: "advances",
    reimbursements: "reimbursements",
    collections: "salesTargets",
    incentives: "incentives",
    payroll: "payroll",
    reports: "reports"
  }[key];
  return permissionKey ? Boolean(permissions[permissionKey]) : false;
}

const PRODUCT_PERMISSIONS = [
  "can_view_products",
  "can_add_products",
  "can_edit_products",
  "can_delete_products",
  "can_change_product_price",
  "can_create_stock_inward",
  "can_create_purchase",
  "can_upload_purchase_bill",
  "can_confirm_purchase_extraction",
  "can_import_inventory",
  "can_export_inventory",
  "can_download_inventory_template",
  "can_import_suppliers",
  "can_import_creditors",
  "can_import_purchase_bills",
  "can_import_vendor_dc",
  "can_manage_unmapped_sold_items",
  "can_allow_negative_inventory",
  "can_resolve_negative_inventory",
  "can_map_purchase_to_order",
  "can_map_unmapped_product",
  "can_create_purchase_inward",
  "can_view_supplier_ledger",
  "can_run_vendor_bill_audit",
  "can_view_inventory",
  "can_add_product",
  "can_edit_product",
  "can_view_purchase_price",
  "can_transfer_stock",
  "can_adjust_stock",
  "can_view_cost_price",
  "can_view_margin",
  "can_view_stock_reports",
  "can_view_order_manager_queue",
  "can_decide_stock_or_vendor_order",
  "can_select_vendor_for_order_item",
  "can_create_vendor_order_slip",
  "can_mark_order_placed_with_vendor",
  "can_mark_product_received_from_vendor",
  "can_view_order_timeline",
  "can_view_lab_queue",
  "can_upload_vendor_dc",
  "can_confirm_dc_extraction",
  "can_mark_lab_received",
  "can_mark_fitting_processing",
  "can_mark_ready",
  "can_apply_discount",
  "can_approve_discount",
  "can_receive_payment",
  "can_mark_delivered",
  "can_access_daily_reconciliation",
  "can_mark_reconciliation_done",
  "can_mark_bank_reco_done",
  "can_view_reports",
  "can_manage_users",
  "can_manage_roles"
];

const CN_STATUSES = [
  "New Request",
  "Pickup Awaited",
  "CN Awaited",
  "CN Credited",
  "Rejected",
  "Reapplied",
  "Needs Review",
  "Closed"
];

const CN_WORK_BUCKETS = [
  { key: "new", label: "New Request", action: "Assign Vendor", tone: "mint" },
  { key: "pickup", label: "Pickup Awaited", action: "Mark Picked Up", tone: "yellow" },
  { key: "awaited", label: "CN Awaited", action: "Mark CN Credited", tone: "blue" },
  { key: "ageing30", label: "CN Awaited > 30 Days", action: "Follow up", tone: "red" },
  { key: "credited", label: "CN Credited", action: "Close CN", tone: "mint" },
  { key: "rejected", label: "Rejected", action: "Reapply / Close", tone: "red" },
  { key: "review", label: "Needs Review", action: "Resolve / Update", tone: "red" },
  { key: "closed", label: "Closed", action: "View", tone: "muted" }
];

const CN_REASONS = ["Coating Issue", "Non Adaptation", "Breakage", "Power Issue", "Wrong Order", "Other"];
const CN_PRODUCT_TYPES = ["Lens", "Frame", "Both", "Other"];
const CN_VENDOR_OPTIONS = ["Essilor", "Zeiss", "Asdol", "VRX", "Suprol", "Anvira", "Nikon", "Other"];

const CN_PERMISSIONS = [
  "can_view_cn_tracking",
  "can_create_cn_request",
  "can_process_cn",
  "can_verify_cn_packet",
  "can_upload_vendor_form",
  "can_update_cn_vendor_details",
  "can_update_cn_received",
  "can_update_cn_amount",
  "can_update_cn_ledger",
  "can_close_cn",
  "can_assign_cn_responsible",
  "can_view_all_store_cn",
  "can_override_cn_status",
  "can_print_cn_packet_slip",
  "can_print_order_form",
  "can_export_cn_reports",
  "can_export_vendor_awaited_cn",
  "can_view_internal_cn_export"
];

const LOGIN_PERMISSION_DEFAULTS = {
  users: false,
  attendance: true,
  corrections: true,
  optical: true,
  work: true,
  salesTargets: false,
  payroll: false,
  reports: true,
  advances: false,
  reimbursements: false,
  incentives: false,
  stores: false,
  communication: false,
  marketing: false,
  dailyReconciliation: false,
  bankReco: false,
  can_view_products: true,
  can_add_products: false,
  can_edit_products: false,
  can_delete_products: false,
  can_change_product_price: false,
  can_create_stock_inward: false,
  can_create_purchase: false,
  can_upload_purchase_bill: false,
  can_confirm_purchase_extraction: false,
  can_import_inventory: false,
  can_export_inventory: false,
  can_view_supplier_ledger: false,
  can_run_vendor_bill_audit: false,
  can_transfer_stock: false,
  can_adjust_stock: false,
  can_view_cost_price: false,
  can_view_margin: false,
  can_apply_discount: false,
  can_approve_discount: false,
  can_receive_payment: false,
  can_mark_delivered: false,
  can_access_daily_reconciliation: false,
  can_mark_reconciliation_done: false,
  can_mark_bank_reco_done: false,
  can_view_cn_tracking: false,
  can_create_cn_request: false,
  can_process_cn: false,
  can_verify_cn_packet: false,
  can_upload_vendor_form: false,
  can_update_cn_vendor_details: false,
  can_update_cn_received: false,
  can_update_cn_amount: false,
  can_update_cn_ledger: false,
  can_close_cn: false,
  can_assign_cn_responsible: false,
  can_view_all_store_cn: false,
  can_override_cn_status: false,
  can_print_cn_packet_slip: false,
  can_print_order_form: false,
  can_export_cn_reports: false,
  can_export_vendor_awaited_cn: false,
  can_view_internal_cn_export: false,
  can_view_order_manager_queue: false,
  can_decide_stock_or_vendor_order: false,
  can_select_vendor_for_order_item: false,
  can_create_vendor_order_slip: false,
  can_mark_order_placed_with_vendor: false,
  can_mark_product_received_from_vendor: false,
  can_view_order_timeline: false,
  can_view_lab_queue: false,
  can_mark_lab_received: false,
  can_upload_vendor_dc: false,
  can_confirm_dc_extraction: false,
  can_create_purchase_inward: false,
  can_mark_fitting_processing: false,
  can_mark_ready: false,
  can_manage_stock_audit: false,
  can_view_all_stock_audit: false,
  can_review_stock_audit: false,
  can_manage_store_stock_audit: false,
  can_manage_users: false,
  can_manage_roles: false
};

const BUSINESS_ROLE_PRESETS = [
  {
    key: "owner_super_admin",
    label: "Owner / Super Admin",
    appRole: "super_admin",
    designation: "Owner / Super Admin",
    description: "Full company owner access across every store, employee, module, report and setting.",
    allEmployees: true,
    permissions: ["all"]
  },
  {
    key: "store_staff",
    label: "Store / Sales Staff",
    appRole: "employee",
    designation: "Store / Sales Staff",
    description: "Daily attendance, order entry, customer work, own tasks and basic CN request.",
    allEmployees: false,
    permissions: [
      "attendance",
      "optical",
      "work",
      "can_view_products",
      "can_create_cn_request",
      "can_print_cn_packet_slip",
      "can_print_order_form"
    ]
  },
  {
    key: "store_manager",
    label: "Store Manager",
    appRole: "admin",
    designation: "Store Manager",
    description: "Store-level team, attendance, corrections, orders, discounts, payments and store reports.",
    allEmployees: false,
    permissions: [
      "attendance",
      "corrections",
      "optical",
      "work",
      "reports",
      "reimbursements",
      "stores",
      "can_view_products",
      "can_apply_discount",
      "can_approve_discount",
      "can_receive_payment",
      "can_mark_delivered",
      "can_view_reports",
      "can_print_order_form",
      "can_view_cn_tracking",
      "can_create_cn_request"
    ]
  },
  {
    key: "lab_manager",
    label: "Lab / Order Manager",
    appRole: "admin",
    designation: "Lab / Order Manager",
    description: "Order queue, lab receiving, fitting, vendor DC, ready status and order timeline.",
    allEmployees: false,
    permissions: [
      "attendance",
      "optical",
      "work",
      "reports",
      "can_view_products",
      "can_view_order_manager_queue",
      "can_decide_stock_or_vendor_order",
      "can_select_vendor_for_order_item",
      "can_create_vendor_order_slip",
      "can_mark_order_placed_with_vendor",
      "can_mark_product_received_from_vendor",
      "can_view_order_timeline",
      "can_view_lab_queue",
      "can_mark_lab_received",
      "can_upload_vendor_dc",
      "can_confirm_dc_extraction",
      "can_create_purchase_inward",
      "can_mark_fitting_processing",
      "can_mark_ready",
      "can_print_order_form"
    ]
  },
  {
    key: "cn_manager",
    label: "CN Manager",
    appRole: "admin",
    designation: "CN Manager",
    description: "CN requests, vendor assignment, pickup, CN awaited, CN credited, ledger update and close.",
    allEmployees: true,
    permissions: [
      "attendance",
      "optical",
      "work",
      "reports",
      "can_view_cn_tracking",
      "can_create_cn_request",
      "can_process_cn",
      "can_verify_cn_packet",
      "can_upload_vendor_form",
      "can_update_cn_vendor_details",
      "can_update_cn_received",
      "can_update_cn_amount",
      "can_update_cn_ledger",
      "can_close_cn",
      "can_assign_cn_responsible",
      "can_view_all_store_cn",
      "can_override_cn_status",
      "can_print_cn_packet_slip",
      "can_export_cn_reports",
      "can_export_vendor_awaited_cn",
      "can_view_internal_cn_export"
    ]
  },
  {
    key: "inventory_manager",
    label: "Inventory Manager",
    appRole: "admin",
    designation: "Inventory Manager",
    description: "Product master, stock inward, transfer stock, stock adjustment and stock reports.",
    allEmployees: false,
    permissions: [
      "optical",
      "reports",
      "can_view_products",
      "can_add_products",
      "can_edit_products",
      "can_delete_products",
      "can_change_product_price",
      "can_create_stock_inward",
      "can_transfer_stock",
      "can_adjust_stock",
      "can_view_cost_price",
      "can_view_margin",
      "can_view_reports"
    ]
  },
  {
    key: "purchase_manager",
    label: "Purchase Manager",
    appRole: "admin",
    designation: "Purchase Manager",
    description: "Vendor bills, purchase inward, GST/HSN purchase data, supplier ledger and input credit reports.",
    allEmployees: false,
    permissions: [
      "optical",
      "reports",
      "companyLedger",
      "can_view_products",
      "can_add_products",
      "can_edit_products",
      "can_create_stock_inward",
      "can_create_purchase",
      "can_upload_purchase_bill",
      "can_confirm_purchase_extraction",
      "can_create_purchase_inward",
      "can_view_cost_price",
      "can_view_margin",
      "can_view_reports"
    ]
  },
  {
    key: "accounts_reco_manager",
    label: "Accounts / Reconciliation",
    appRole: "admin",
    designation: "Accounts / Reconciliation",
    description: "Payments, daily reconciliation, company ledger, reports and receivable/payable review.",
    allEmployees: true,
    permissions: [
      "reports",
      "companyLedger",
      "dailyReconciliation",
      "bankReco",
      "can_receive_payment",
      "can_access_daily_reconciliation",
      "can_mark_reconciliation_done",
      "can_mark_bank_reco_done",
      "can_view_reports"
    ]
  },
  {
    key: "audit_manager",
    label: "Stock Audit Manager",
    appRole: "admin",
    designation: "Stock Audit Manager",
    description: "Stock audit sessions, review, approvals and audit reports.",
    allEmployees: true,
    permissions: [
      "reports",
      "optical",
      "can_view_products",
      "can_view_reports",
      "can_manage_stock_audit",
      "can_view_all_stock_audit",
      "can_review_stock_audit",
      "can_manage_store_stock_audit"
    ]
  },
  {
    key: "hr_admin",
    label: "HR / Payroll Admin",
    appRole: "admin",
    designation: "HR / Payroll Admin",
    description: "Employees, attendance, leave, corrections, reimbursements, payroll and HR reports.",
    allEmployees: true,
    permissions: [
      "users",
      "attendance",
      "corrections",
      "work",
      "payroll",
      "reports",
      "advances",
      "reimbursements",
      "incentives",
      "stores",
      "can_view_reports",
      "can_manage_users"
    ]
  },
  {
    key: "director_ceo",
    label: "Director / CEO",
    appRole: "admin",
    designation: "Director / CEO",
    description: "Company-wide view, reports and approvals without changing owner login.",
    allEmployees: true,
    permissions: [
      "users",
      "attendance",
      "corrections",
      "optical",
      "work",
      "salesTargets",
      "payroll",
      "reports",
      "advances",
      "reimbursements",
      "incentives",
      "stores",
      "communication",
      "companyLedger",
      "dailyReconciliation",
      "bankReco",
      "can_view_products",
      "can_view_cost_price",
      "can_view_margin",
      "can_apply_discount",
      "can_approve_discount",
      "can_receive_payment",
      "can_mark_delivered",
      "can_access_daily_reconciliation",
      "can_mark_reconciliation_done",
      "can_mark_bank_reco_done",
      "can_view_cn_tracking",
      "can_process_cn",
      "can_assign_cn_responsible",
      "can_view_all_store_cn",
      "can_override_cn_status",
      "can_export_cn_reports",
      "can_export_vendor_awaited_cn",
      "can_view_internal_cn_export",
      "can_view_reports"
    ]
  }
];

const PERMISSION_LABELS = {
  users: "Manage users",
  attendance: "Attendance",
  corrections: "Attendance corrections",
  optical: "Optical Orders",
  work: "Tasks / work",
  salesTargets: "Sales targets",
  payroll: "Payroll",
  reports: "Reports",
  advances: "Advances / loans",
  reimbursements: "Reimbursements",
  incentives: "Incentives",
  stores: "Stores",
  communication: "Communication",
  dailyReconciliation: "Daily Reconciliation",
  companyLedger: "Company Ledger",
  bankReco: "Bank Reco",
  can_access_daily_reconciliation: "Access daily reconciliation",
  can_mark_reconciliation_done: "Mark reconciliation done",
  can_mark_bank_reco_done: "Mark bank reco done",
  can_view_products: "View product master",
  can_add_products: "Add products",
  can_edit_products: "Edit products",
  can_delete_products: "Delete products",
  can_change_product_price: "Change product price",
  can_create_stock_inward: "Create stock inward",
  can_create_purchase: "Create purchase",
  can_upload_purchase_bill: "Upload purchase bill",
  can_confirm_purchase_extraction: "Confirm purchase extraction",
  can_import_inventory: "Import inventory",
  can_export_inventory: "Export inventory",
  can_view_supplier_ledger: "View supplier ledger",
  can_run_vendor_bill_audit: "Run vendor bill audit",
  can_transfer_stock: "Transfer stock",
  can_adjust_stock: "Adjust stock",
  can_view_cost_price: "View cost price",
  can_view_margin: "View margin",
  can_apply_discount: "Apply discount",
  can_approve_discount: "Approve discount",
  can_receive_payment: "Receive payment",
  can_mark_delivered: "Mark delivered",
  can_view_cn_tracking: "View CN tracking",
  can_create_cn_request: "Create CN request",
  can_process_cn: "CN Manager / process CN",
  can_verify_cn_packet: "Verify CN packet",
  can_upload_vendor_form: "Upload vendor form",
  can_update_cn_vendor_details: "Update CN vendor details",
  can_update_cn_received: "Update CN received",
  can_update_cn_amount: "Update CN amount",
  can_update_cn_ledger: "Update CN ledger",
  can_close_cn: "Close CN",
  can_assign_cn_responsible: "Assign CN Manager",
  can_view_all_store_cn: "View all-store CN",
  can_override_cn_status: "Override CN status",
  can_print_cn_packet_slip: "Print CN packet slip",
  can_print_order_form: "Print Order Form",
  can_export_cn_reports: "Export CN reports",
  can_export_vendor_awaited_cn: "Export vendor awaited CN",
  can_view_internal_cn_export: "View internal CN export",
  can_view_order_manager_queue: "Order Manager queue",
  can_decide_stock_or_vendor_order: "Decide stock / vendor order",
  can_select_vendor_for_order_item: "Select vendor for order item",
  can_create_vendor_order_slip: "Create vendor order slip",
  can_mark_order_placed_with_vendor: "Mark vendor order placed",
  can_mark_product_received_from_vendor: "Mark vendor product received",
  can_view_order_timeline: "View order timeline",
  can_view_lab_queue: "Lab Manager queue",
  can_mark_lab_received: "Mark lab received",
  can_upload_vendor_dc: "Upload vendor DC",
  can_confirm_dc_extraction: "Confirm DC extraction",
  can_create_purchase_inward: "Create purchase inward from DC",
  can_mark_fitting_processing: "Mark fitting / processing",
  can_mark_ready: "Mark order ready",
  can_manage_stock_audit: "Manage stock audit",
  can_view_all_stock_audit: "View all stock audits",
  can_review_stock_audit: "Review stock audit",
  can_manage_store_stock_audit: "Manage store stock audit",
  can_manage_users: "Manage users",
  can_manage_roles: "Manage roles"
};

const PERMISSION_GROUPS = [
  {
    title: "Daily App Access",
    items: ["users", "attendance", "corrections", "optical", "work", "salesTargets", "payroll", "reports", "advances", "reimbursements", "incentives", "stores", "communication"]
  },
  {
    title: "Products, Inventory and Purchase",
    items: ["can_view_products", "can_add_products", "can_edit_products", "can_delete_products", "can_change_product_price", "can_create_stock_inward", "can_create_purchase", "can_upload_purchase_bill", "can_confirm_purchase_extraction", "can_import_inventory", "can_export_inventory", "can_view_supplier_ledger", "can_run_vendor_bill_audit", "can_transfer_stock", "can_adjust_stock", "can_view_cost_price", "can_view_margin"]
  },
  {
    title: "Sales, Payment and Orders",
    items: ["can_apply_discount", "can_approve_discount", "can_receive_payment", "can_mark_delivered", "can_print_order_form", "can_view_order_manager_queue", "can_decide_stock_or_vendor_order", "can_select_vendor_for_order_item", "can_create_vendor_order_slip", "can_mark_order_placed_with_vendor", "can_mark_product_received_from_vendor", "can_view_order_timeline"]
  },
  {
    title: "Lab",
    items: ["can_view_lab_queue", "can_mark_lab_received", "can_upload_vendor_dc", "can_confirm_dc_extraction", "can_create_purchase_inward", "can_mark_fitting_processing", "can_mark_ready"]
  },
  {
    title: "CN Tracking",
    items: ["can_view_cn_tracking", "can_create_cn_request", "can_process_cn", "can_verify_cn_packet", "can_upload_vendor_form", "can_update_cn_vendor_details", "can_update_cn_received", "can_update_cn_amount", "can_update_cn_ledger", "can_close_cn", "can_assign_cn_responsible", "can_view_all_store_cn", "can_override_cn_status", "can_print_cn_packet_slip", "can_export_cn_reports", "can_export_vendor_awaited_cn", "can_view_internal_cn_export"]
  },
  {
    title: "Accounts, Audit and Admin",
    items: ["dailyReconciliation", "companyLedger", "bankReco", "can_access_daily_reconciliation", "can_mark_reconciliation_done", "can_mark_bank_reco_done", "can_manage_stock_audit", "can_view_all_stock_audit", "can_review_stock_audit", "can_manage_store_stock_audit", "can_view_reports", "can_manage_users", "can_manage_roles"]
  }
];

function businessRolePresetByKey(roleKey) {
  return BUSINESS_ROLE_PRESETS.find((item) => item.key === roleKey) || null;
}

function businessRoleLabelFromPermissions(permissions = {}) {
  const preset = businessRolePresetByKey(permissions.__businessRoleKey);
  if (preset) return preset.label;
  if (permissions.all) return "Owner / Super Admin";
  return permissions.__businessRoleLabel || "Unassigned";
}

async function refetchSavedEntity(tableName, id, fallback) {
  if (!hasSupabaseConfig || !id) return fallback;
  const saved = await fetchEntityById(tableName, id);
  return saved || fallback;
}

function readableSupabaseError(error) {
  if (!error) return "Unknown Supabase error.";
  return error.message || error.details || error.hint || JSON.stringify(error);
}

function emitGlobalSaveError({ label, tableName, error }) {
  const technicalDetails = readableSupabaseError(error);
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("framesbay-save-failed", {
    detail: {
      title: "Save Failed",
      message: "Your data was not saved online.",
      label,
      tableName,
      technicalDetails
    }
  }));
}

async function persistRequiredEntity(tableName, record, label = "Record") {
  try {
    const persisted = await persistEntity(tableName, record);
    return refetchSavedEntity(tableName, persisted?.id || record?.id, persisted || record);
  } catch (error) {
    console.error(`${label} Supabase save failed`, { tableName, record, error });
    emitGlobalSaveError({ label, tableName, error });
    throw new Error(`${label} was not saved online. Supabase said: ${readableSupabaseError(error)}`);
  }
}

function permissionsForBusinessRole(roleKey) {
  const preset = businessRolePresetByKey(roleKey);
  const permissions = { ...LOGIN_PERMISSION_DEFAULTS };
  if (!preset) {
    return {
      ...permissions,
      __businessRoleKey: "",
      __businessRoleLabel: "Unassigned"
    };
  }
  if (preset.appRole === "super_admin" || preset.permissions.includes("all")) {
    return {
      all: true,
      __businessRoleKey: preset.key,
      __businessRoleLabel: preset.label
    };
  }
  preset.permissions.forEach((permission) => {
    permissions[permission] = true;
  });
  return {
    ...permissions,
    __businessRoleKey: preset.key,
    __businessRoleLabel: preset.label
  };
}

function activePermissionSummary(permissions = {}) {
  if (permissions.all) return "Full access";
  const enabled = Object.keys(permissions)
    .filter((key) => !key.startsWith("__") && permissions[key])
    .map((key) => PERMISSION_LABELS[key] || key);
  return enabled.length ? enabled.join(", ") : "-";
}

function hasUserPermission(user, permission) {
  if (user?.role === "super_admin" || user?.permissions?.all) return true;
  const permissionAliases = {
    can_add_product: "can_add_products",
    can_edit_product: "can_edit_products",
    can_view_purchase_price: "can_view_cost_price",
    can_create_purchase: "can_create_stock_inward",
    can_create_purchase_inward: "can_create_stock_inward",
    can_import_purchase_bills: "can_upload_purchase_bill",
    can_import_vendor_dc: "can_upload_vendor_dc",
    can_export_inventory: "can_view_inventory",
    can_view_inventory: "can_view_products",
    can_view_stock_reports: "can_view_reports"
  };
  if (permission === "can_mark_reconciliation_done" && user?.permissions?.can_mark_bank_reco_done) return true;
  if (permission === "can_access_daily_reconciliation" && (user?.permissions?.bankReco || user?.permissions?.can_mark_bank_reco_done)) return true;
  return Boolean(user?.permissions?.[permission] || user?.permissions?.[permissionAliases[permission]]);
}

function userEmployee(state, user) {
  return state.employees.find((employee) => employee.id === user?.employeeId) || null;
}

function userPrimaryStoreId(state, user) {
  const employee = userEmployee(state, user);
  return employee?.storeId || user?.storeIds?.[0] || state.stores[0]?.id || "";
}

function userDisplayName(state, userIdOrEmployeeId) {
  const user = state.users.find((item) => item.id === userIdOrEmployeeId);
  if (user?.employeeId) return state.employees.find((employee) => employee.id === user.employeeId)?.fullName || user.name || user.email;
  if (user) return user.name || user.email;
  return state.employees.find((employee) => employee.id === userIdOrEmployeeId)?.fullName || "User";
}

function storeShortName(state, storeId) {
  const store = state.stores.find((item) => item.id === storeId);
  return store?.code || store?.name || "Store";
}

function cnResponsibleUserIds(state) {
  const configured = state.settings?.cnTrackingResponsibleUserIds || [];
  if (configured.length) return configured;
  const sumitEmployee = state.employees.find((employee) => /sumit/i.test(employee.fullName || ""));
  const sumitUser = state.users.find((user) => user.employeeId === sumitEmployee?.id);
  if (sumitUser) return [sumitUser.id];
  const permissionUsers = state.users.filter((user) => user.active !== false && (user.permissions?.can_process_cn || user.permissions?.can_verify_cn_packet));
  if (permissionUsers.length) return permissionUsers.map((user) => user.id);
  return state.users.filter((user) => user.role === "super_admin" && user.active !== false).map((user) => user.id);
}

function cnManagerUserIds(state) {
  return cnResponsibleUserIds(state);
}

function isBroadCnUser(user) {
  return Boolean(
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    user?.permissions?.all ||
    user?.permissions?.allEmployees ||
    user?.permissions?.can_view_all_store_cn ||
    user?.permissions?.can_process_cn
  );
}

function isCnResponsible(state, user) {
  return cnResponsibleUserIds(state).includes(user?.id) || hasUserPermission(user, "can_process_cn");
}

function canViewCnCase(state, user, request) {
  if (isBroadCnUser(user) || hasUserPermission(user, "can_view_all_store_cn") || isCnResponsible(state, user)) return true;
  if (request.createdBy === user.id || request.cnResponsibleId === user.id || (request.cnResponsibleIds || []).includes(user.id)) return true;
  return (user.storeIds || []).includes(request.storeId) || userPrimaryStoreId(state, user) === request.storeId;
}

function normalizeCnStatus(status, request = {}) {
  const value = String(status || "").trim();
  if (!value) return "New Request";
  const lower = value.toLowerCase();
  if (request.cnNumber || request.cnReceivedDate || request.cnDate) {
    if (!/reject|closed/.test(lower)) return "CN Credited";
  }
  if (["draft"].includes(lower)) return "Needs Review";
  if (["cn request created", "cn packet printed", "sent for cn processing"].includes(lower)) return "New Request";
  if (["received by cn responsible", "received by cn manager", "vendor identified", "vendor assigned", "vendor pickup awaited", "ready for dispatch"].includes(lower)) return "Pickup Awaited";
  if (["sent to vendor / company", "picked up", "picked up / cn awaited", "cn awaited"].includes(lower)) return "CN Awaited";
  if (["cn received", "cn credited", "ledger pending", "ledger updated"].includes(lower)) return "CN Credited";
  if (["short / difference", "packet incomplete", "problem / incomplete", "problem / resolved", "lost"].includes(lower)) return "Needs Review";
  if (lower === "closed") return "Closed";
  if (lower === "rejected") return "Rejected";
  if (lower === "reapplied" || lower === "requested again") return "Reapplied";
  if (lower === "needs review") return "Needs Review";
  return CN_STATUSES.includes(value) ? value : "Needs Review";
}

function cnAgeDays(request, baseDate = new Date()) {
  const date = request.createdAt || request.createdDate || request.dispatchDate || request.pickupDate || request.cnReceivedDate;
  if (!date) return 0;
  const timestamp = new Date(date).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return Math.max(0, Math.floor((baseDate.getTime() - timestamp) / 86400000));
}

function cnPickupAgeDays(request, baseDate = new Date()) {
  const date = request.pickupDate || request.dispatchDate || request.createdDate || request.createdAt;
  if (!date) return 0;
  const timestamp = new Date(date).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return Math.max(0, Math.floor((baseDate.getTime() - timestamp) / 86400000));
}

function cnReadableDate(value) {
  if (!value) return "";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "";
  return formatDisplayDate(toDateOnly(new Date(timestamp)));
}

function cnTextDateHint(request) {
  const source = [
    request.remarks,
    request.reasonForReturn,
    request.complaintNote,
    request.importedStatusText,
    request.productDetails
  ].filter(Boolean).join(" ");
  const monthMap = {
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12"
  };
  const match = source.match(/\b(\d{1,2})\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*'?(\d{2,4})?\b/i);
  if (!match) return "";
  const day = match[1].padStart(2, "0");
  const month = monthMap[match[2].slice(0, 3).toLowerCase()];
  const rawYear = match[3] || String(request.createdDate || new Date().getFullYear()).slice(0, 4);
  const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
  return cnReadableDate(`${year}-${month}-${day}`);
}

function cnDateSummary(request) {
  const created = cnReadableDate(request.createdDate || request.createdAt);
  const assigned = cnReadableDate(request.vendorAssignedAt);
  const pickup = cnReadableDate(request.pickupDate || request.dispatchDate);
  const cnDate = cnReadableDate(request.cnDate || request.cnReceivedDate);
  return [
    created ? `Created ${created}` : "",
    assigned ? `Vendor assigned ${assigned}` : "",
    pickup ? `Sent/Picked ${pickup}` : "",
    cnDate ? `CN ${cnDate}` : ""
  ].filter(Boolean);
}

function cnStatusTone(request) {
  const bucket = cnBucketKey(request);
  if (bucket === "closed") return cnFinalOutcome(request) === "Rejected" ? "cn-tone-rejected" : cnFinalOutcome(request) === "Partially approved" ? "cn-tone-partial" : "cn-tone-closed";
  if (bucket === "awaited") return "cn-tone-awaited";
  if (bucket === "pickup") return "cn-tone-pickup";
  if (bucket === "review" || bucket === "rejected") return "cn-tone-problem";
  if (bucket === "credited") return "cn-tone-credited";
  return "cn-tone-new";
}

function cnCurrentStatusText(request) {
  const status = normalizeCnStatus(request.status, request);
  const vendor = request.vendorName || "Vendor";
  const assigned = cnReadableDate(request.vendorAssignedAt);
  const pickup = cnReadableDate(request.pickupDate || request.dispatchDate);
  const cnDate = cnReadableDate(request.cnDate || request.cnReceivedDate);
  const noteDate = cnTextDateHint(request);
  if (status === "Closed") return `Closed as ${cnFinalOutcome(request)}${request.closedAt ? ` on ${cnReadableDate(request.closedAt)}` : ""}.`;
  if (status === "CN Credited") return `CN credited${cnDate ? ` on ${cnDate}` : ""}; ${request.ledgerUpdated ? "ledger updated" : "ledger pending"}.`;
  if (status === "CN Awaited") return `${vendor} picked up / received packet${pickup ? ` on ${pickup}` : noteDate ? ` around ${noteDate}` : ""}; credit note is awaited.`;
  if (status === "Pickup Awaited") return `${vendor} assigned${assigned ? ` on ${assigned}` : ""}; vendor pickup is pending.`;
  if (status === "New Request") return "New request created; vendor is not assigned yet.";
  if (status === "Needs Review" && request.vendorName) return `${vendor} appears assigned from old data${noteDate ? `, note date ${noteDate}` : ""}; confirm pickup or mark CN credited.`;
  if (status === "Needs Review") return "Needs review from old data; confirm vendor, pickup, credit or rejection.";
  if (status === "Rejected") return "Rejected case; reapply or close after final decision.";
  if (status === "Reapplied") return `Reapplied case; ${vendor} pickup or CN credit is pending.`;
  return status;
}

function cnBucketKey(request) {
  const status = normalizeCnStatus(request.status, request);
  if (status === "New Request") return "new";
  if (status === "Pickup Awaited" || status === "Reapplied") return "pickup";
  if (status === "CN Awaited") return "awaited";
  if (status === "Needs Review") return "review";
  if (status === "Rejected") return "rejected";
  if (status === "CN Credited") return "credited";
  if (status === "Closed") return "closed";
  return "review";
}

function cnActionNeeded(request) {
  const key = cnBucketKey(request);
  if (key === "new") return "Assign Vendor";
  if (key === "pickup") return "Mark Picked Up";
  if (key === "awaited") return "Mark CN Credited";
  if (key === "review") return "Resolve / Update";
  if (key === "rejected") return "Reapply / Close";
  if (key === "credited") return "Close CN";
  if (key === "closed") return "Closed";
  return "Review";
}

function cnFinalOutcome(request = {}, draft = {}) {
  if (request.closureOutcome) return request.closureOutcome;
  const text = `${request.status || ""} ${request.importedStatusText || ""} ${request.remarks || ""} ${request.closureRemarks || ""}`.toLowerCase();
  const difference = Number(draft.differenceAmount || request.differenceAmount || 0);
  const cnAmount = Number(draft.cnAmount || request.cnAmount || 0);
  const expected = Number(request.expectedCnAmountIncTax || request.expectedCnAmountExTax || request.amountExclusiveTax || 0);
  if (/reject/.test(text)) return "Rejected";
  if (difference > 0 || /part|short|difference/.test(text)) return "Partially approved";
  if (cnAmount > 0 && (!expected || cnAmount >= expected)) return "Approved";
  if (request.cnNumber || /approved|received|credited|ledger|adjust|closed|done/.test(text)) return "Approved";
  return "Closed / needs review";
}

function cnBucketMatches(request, bucketKey) {
  if (bucketKey === "ageing30") return cnBucketKey(request) === "awaited" && cnPickupAgeDays(request) > 30;
  return cnBucketKey(request) === bucketKey;
}

function cnIsAwaitedForVendorExport(request, { includeRejected = false, ageingOnly = false } = {}) {
  const status = normalizeCnStatus(request.status, request);
  if (["Closed", "CN Credited", "Needs Review"].includes(status)) return false;
  if (status === "Rejected" && !includeRejected) return false;
  const awaited = status === "CN Awaited";
  if (!(awaited || (includeRejected && status === "Rejected"))) return false;
  if (ageingOnly && cnPickupAgeDays(request) <= 30) return false;
  return true;
}

function cnLastFollowUpDate(request) {
  const activity = request.cnActivity || [];
  return request.lastFollowUpAt || activity[0]?.at || "";
}

function cnLastFollowUpText(request) {
  const activity = request.cnActivity || [];
  return request.lastFollowUpNote || activity[0]?.note || "";
}

function cnVendorLabel(request) {
  return request.vendorName || request.importedSheetName || "Vendor not assigned";
}

function cnOrderReference(request) {
  return request.previousOrderNo || request.newOrderNo || request.vendorReferenceNo || request.vendorDocumentReference || "";
}

function cnAwaitedExportRows(rows, state, options = {}) {
  const {
    vendor = "all",
    storeId = "all",
    managerId = "all",
    ageingOnly = false,
    includeRejected = false,
    pickupFrom = "",
    pickupTo = "",
    createdFrom = "",
    createdTo = ""
  } = options;
  return rows
    .filter((request) => cnIsAwaitedForVendorExport(request, { includeRejected, ageingOnly }))
    .filter((request) => vendor === "all" || cnVendorLabel(request) === vendor)
    .filter((request) => storeId === "all" || request.storeId === storeId)
    .filter((request) => managerId === "all" || request.cnResponsibleId === managerId || (request.cnResponsibleIds || []).includes(managerId))
    .filter((request) => {
      const pickup = request.pickupDate || request.dispatchDate || "";
      const created = request.createdDate || request.createdAt || "";
      if (pickupFrom && (!pickup || pickup < pickupFrom)) return false;
      if (pickupTo && (!pickup || pickup > pickupTo)) return false;
      if (createdFrom && (!created || created < createdFrom)) return false;
      if (createdTo && (!created || created > createdTo)) return false;
      return true;
    })
    .map((request, index) => ({
      srNo: index + 1,
      vendor: cnVendorLabel(request),
      store: storeShortName(state, request.storeId),
      ticket: request.cnTicketNumber || "",
      orderNo: cnOrderReference(request),
      previousOrderNo: request.previousOrderNo || "",
      newOrderNo: request.newOrderNo || "",
      productType: request.productType || "Lens",
      productDetails: request.productDetails || request.productNameAsPerDc || "",
      reason: request.reasonForReturn || request.importedStatusText || "",
      quantity: request.quantity || 1,
      vendorReferenceNo: request.vendorReferenceNo || "",
      vendorDocumentReference: request.vendorDocumentReference || request.companyFormNo || request.dcNumber || "",
      pickupDate: request.pickupDate || request.dispatchDate || "",
      daysPending: cnPickupAgeDays(request),
      expectedCnAmount: request.expectedCnAmountIncTax || request.expectedCnAmountExTax || request.amountExclusiveTax || "",
      lastFollowUpDate: cnLastFollowUpDate(request),
      lastFollowUpNote: cnLastFollowUpText(request),
      cnManager: request.cnResponsibleId ? userDisplayName(state, request.cnResponsibleId) : (request.cnResponsibleIds || []).map((id) => userDisplayName(state, id)).join(", "),
      remarks: request.remarks || "",
      internalStatus: normalizeCnStatus(request.status, request),
      importedStatusText: request.importedStatusText || "",
      differenceAmount: request.differenceAmount || "",
      ledgerStatus: request.ledgerUpdated ? "Ledger updated" : "Ledger pending"
    }));
}

function vendorShareCnColumns(internal = false) {
  const vendorColumns = [
    ["Sr No.", (row) => row.srNo],
    ["Vendor / Company", (row) => row.vendor],
    ["CN Ticket No.", (row) => row.ticket],
    ["Order No.", (row) => row.orderNo],
    ["Product / Lens Details", (row) => row.productDetails],
    ["Reason", (row) => row.reason],
    ["Quantity", (row) => row.quantity],
    ["Vendor Reference No.", (row) => row.vendorReferenceNo],
    ["Pickup Date", (row) => row.pickupDate],
    ["Days Pending", (row) => row.daysPending],
    ["Remarks", (row) => row.remarks]
  ];
  if (!internal) return vendorColumns;
  return [
    ["Sr No.", (row) => row.srNo],
    ["Vendor / Company", (row) => row.vendor],
    ["Store", (row) => row.store],
    ["CN Ticket No.", (row) => row.ticket],
    ["Previous Order No.", (row) => row.previousOrderNo],
    ["New Order No.", (row) => row.newOrderNo],
    ["Product Type", (row) => row.productType],
    ["Product / Lens Details", (row) => row.productDetails],
    ["Reason", (row) => row.reason],
    ["Quantity", (row) => row.quantity],
    ["Vendor Reference No.", (row) => row.vendorReferenceNo],
    ["DC No / DN No / LRF No / Challan No", (row) => row.vendorDocumentReference],
    ["Pickup Date", (row) => row.pickupDate],
    ["Days Pending After Pickup", (row) => row.daysPending],
    ["Expected CN Amount", (row) => row.expectedCnAmount],
    ["Last Follow-up Date", (row) => row.lastFollowUpDate],
    ["Last Follow-up Note", (row) => row.lastFollowUpNote],
    ["CN Manager", (row) => row.cnManager],
    ["Internal Status", (row) => row.internalStatus],
    ["Old Excel Status", (row) => row.importedStatusText],
    ["Difference Amount", (row) => row.differenceAmount],
    ["Ledger Status", (row) => row.ledgerStatus],
    ["Remarks", (row) => row.remarks]
  ];
}

function safeSheetName(value) {
  return String(value || "Vendor").replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Vendor";
}

async function downloadVendorAwaitedCnExcel({ rows, state, user, vendor = "all", internal = false }) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = user?.name || user?.email || "Framesbay";
  const grouped = rows.reduce((map, row) => {
    const key = vendor === "all" ? row.vendor : vendor;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
    return map;
  }, new Map());
  const columns = vendorShareCnColumns(internal);
  const generatedAt = new Date().toLocaleString("en-IN");
  if (!grouped.size) grouped.set(vendor === "all" ? "No awaited CN" : vendor, []);
  grouped.forEach((vendorRows, vendorName) => {
    const sheet = workbook.addWorksheet(safeSheetName(vendorName));
    sheet.addRow(["FRAMESBAY CN FOLLOW-UP LIST"]);
    sheet.addRow([`Vendor: ${vendorName}`]);
    sheet.addRow([`Status: CN Awaited`]);
    sheet.addRow([`Generated On: ${generatedAt}`]);
    sheet.addRow([`Generated By: ${user?.name || user?.email || "-"}`]);
    sheet.addRow([]);
    sheet.addRow(columns.map(([label]) => label));
    vendorRows.forEach((row) => sheet.addRow(columns.map(([, render]) => render(row))));
    sheet.getRow(1).font = { bold: true, size: 14 };
    sheet.getRow(7).font = { bold: true, color: { argb: "FFFFFFFF" } };
    sheet.getRow(7).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0B4F3B" } };
    sheet.views = [{ state: "frozen", ySplit: 7 }];
    sheet.columns.forEach((column) => {
      let maxLength = 12;
      column.eachCell({ includeEmpty: true }, (cell) => {
        maxLength = Math.max(maxLength, String(cell.value || "").length + 2);
      });
      column.width = Math.min(maxLength, 40);
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const suffix = vendor === "all" ? "vendor-wise" : safeSheetName(vendor).toLowerCase().replace(/\s+/g, "-");
  downloadBlob(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${filePrefix(state.settings)}-awaited-cn-${suffix}.xlsx`);
}

async function downloadVendorAwaitedCnPdf({ rows, state, user, vendor = "all", internal = false }) {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const titleVendor = vendor === "all" ? "All vendors" : vendor;
  doc.setFontSize(15);
  doc.text("FRAMESBAY CN FOLLOW-UP LIST", 36, 36);
  doc.setFontSize(10);
  doc.text(`Vendor: ${titleVendor}`, 36, 54);
  doc.text(`Generated On: ${new Date().toLocaleString("en-IN")}`, 36, 70);
  doc.text(`Generated By: ${user?.name || user?.email || "-"}`, 36, 86);
  const columns = vendorShareCnColumns(internal).slice(0, internal ? 12 : 10);
  let y = 114;
  doc.setFontSize(8);
  doc.text(columns.map(([label]) => label).join(" | "), 36, y);
  y += 14;
  rows.slice(0, 70).forEach((row) => {
    const line = columns.map(([, render]) => String(render(row) || "").slice(0, 26)).join(" | ");
    if (y > 555) {
      doc.addPage();
      y = 36;
    }
    doc.text(line, 36, y);
    y += 13;
  });
  if (rows.length > 70) doc.text(`Only first 70 rows shown in PDF. Use Excel for full list.`, 36, y + 12);
  downloadBlob(doc.output("blob"), `${filePrefix(state.settings)}-awaited-cn-${safeSheetName(titleVendor).toLowerCase().replace(/\s+/g, "-")}.pdf`);
}

function mapImportedCnStatus(text = "", hints = {}) {
  const value = String(text || "").trim();
  const lower = value.toLowerCase();
  const hasCreditedSignal = Boolean(
    hints.cnNumber ||
    hints.cnAmount ||
    hints.cnReceivedDate ||
    /\b(received|recvd|recd|recd cn|cn received|cn recd|cn credited|released|settled)\b/.test(lower)
  );
  const hasRejectedSignal = /reject|out of warranty/.test(lower);
  const hasReapplySignal = /reapplied|requested again|re apply|re-apply/.test(lower);
  const hasShortSignal = /partly|part |short|difference|without tax|less amount|partial/.test(lower);
  const hasSentSignal = /\b(sent|dispatched|dispatch(?:ed)? via|picked up|pickedup|pickup done|vendor picked|collected|returned|return(?:ed)? via|sent to vendor|sent to company|sent to mv|picked by)\b/.test(lower);
  const hasReadySignal = /ready for dispatch|ready to dispatch|\bready\b|check in cn tray|check in cn box|cn tray|cn box/.test(lower);
  const hasAwaitedSignal = /pending|cn pending|awaited|requested|followup|follow up|cn due|due/.test(lower);
  const hasVendorDateHint = /\b(essilor|zeiss|asdol|adol|vrx|suprol|anvira|nikon)\b/.test(lower) && /\b\d{1,2}\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|\-|\/)/.test(lower);

  if (/closed|adjusted|ledger updated/.test(lower)) return { status: "Closed", needsReview: false, closureOutcome: "Approved" };
  if (hasReapplySignal) return { status: "Reapplied", needsReview: false, closureOutcome: "" };
  if (hasRejectedSignal && !hasCreditedSignal) return { status: "Rejected", needsReview: true, closureOutcome: "Rejected" };
  if (hasCreditedSignal) {
    return {
      status: "CN Credited",
      needsReview: false,
      closureOutcome: hasRejectedSignal ? "Rejected" : hasShortSignal ? "Partially approved" : "Approved"
    };
  }
  if (hasSentSignal || hasAwaitedSignal || hasVendorDateHint || hints.dispatchDate || hints.pickupDate) {
    return { status: "CN Awaited", needsReview: false, closureOutcome: "" };
  }
  if (hasReadySignal) {
    return { status: "Pickup Awaited", needsReview: false, closureOutcome: "" };
  }
  if (/lost/.test(lower)) return { status: "Needs Review", needsReview: true, closureOutcome: "" };
  if (!lower) return { status: "Needs Review", needsReview: true };
  return { status: "Needs Review", needsReview: true };
}

function normalizeImportHeader(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function excelCellText(value) {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    if (value.text) return String(value.text);
    if (value.result != null) return excelCellText(value.result);
    if (Array.isArray(value.richText)) return value.richText.map((part) => part.text || "").join("");
    return "";
  }
  return String(value).trim();
}

function excelDate(value) {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const text = excelCellText(value);
  if (!text) return "";
  return normalizeDateInput(text);
}

function importPick(row, names) {
  for (const name of names) {
    const key = Object.keys(row).find((candidate) => candidate === normalizeImportHeader(name));
    if (key && row[key]) return row[key];
  }
  return "";
}

function importNumber(value) {
  const clean = String(value || "").replace(/[^0-9.-]/g, "");
  return clean ? Number(clean) : 0;
}

function excelRowColor(row) {
  const cell = row.getCell(1);
  return cell?.fill?.fgColor?.argb || cell?.fill?.bgColor?.argb || "";
}

function cnDuplicateKey(record) {
  const vendor = String(record.vendorName || "").trim().toLowerCase();
  const order = String(record.previousOrderNo || "").trim().toLowerCase();
  const reference = String(record.vendorReferenceNo || record.companyFormNo || record.dcNumber || record.vendorDocumentReference || "").trim().toLowerCase();
  if (!vendor || (!order && !reference)) return "";
  return [
    vendor,
    order,
    reference
  ].join("|");
}

const CN_IMPORT_FIELDS = [
  { key: "vendorName", label: "Vendor / Company", aliases: ["Vendor", "Company", "Vendor Company", "Lens Company"] },
  { key: "createdDate", label: "Created Date", aliases: ["Date", "Date.", "CN group date", "CN Group Date", "Created Date"] },
  { key: "pickupDate", label: "Pickup / Dispatch Date", aliases: ["Dispatch Dt.", "Dispatch Dt", "Dispatch date", "Dispatch Date", "Picked up date", "Pickup date", "Sent date", "Dispatched Date"] },
  { key: "previousOrderNo", label: "Order No", aliases: ["Order no", "Order No", "Framesbay Ord no.", "Framesbay order no", "FB Ord No", "FB Ord. No", "FB Ord. No.", "Framesbay Ord", "Framesbay Order", "Previous Order No", "Order Number"] },
  { key: "newOrderNo", label: "New Order No", aliases: ["New order no", "New Order", "New Order No"] },
  { key: "vendorReferenceNo", label: "Vendor Reference No", aliases: ["Slip number/CN Number", "Slip number", "Slip No", "LRF Number", "LRF No", "Goods Return Form", "Chaalan no", "Challan No", "Slip/LRF No", "LRF", "Challan", "DC No", "DN No"] },
  { key: "vendorDocumentReference", label: "Vendor Document Reference", aliases: ["VRX Order number", "Zeiss Ord no", "Suprol Ord. No.", "Adol Bill number", "DN no", "DC number", "Vendor Order No", "Bill No", "SAP Code", "DC", "DN"] },
  { key: "productDetails", label: "Product / Lens Details", aliases: ["Lense type", "Lens Type", "Product", "Lens", "Item", "Product Details", "Lens Details"] },
  { key: "reasonForReturn", label: "Reason", aliases: ["Reason", "Return Reason", "Complaint"] },
  { key: "quantity", label: "Quantity", aliases: ["Qty(pcs)", "Qty", "Quantity"] },
  { key: "expectedCnAmountExTax", label: "Expected CN Amount", aliases: ["Lense cost", "Lens Cost", "Expected CN Amount", "Expected CN Amount Ex Tax"] },
  { key: "expectedCnAmountIncTax", label: "Expected CN Amount Inc Tax", aliases: ["Lense cost inclusive tax", "Lense cost( incl. tax)", "Lense cost(incl tax)", "Cost(inc tax)", "Cost incl tax", "Lens Cost Inclusive Tax", "Lens Cost Inc Tax", "Inclusive Tax"] },
  { key: "importedStatusText", label: "Status", aliases: ["Status", "Current Status", "CN Status", "Remarks Status"] },
  { key: "cnReceivedDate", label: "CN Received Date", aliases: ["Date of CN Received", "Date of CN Recevied", "Date of CN received", "CN Release Date", "CN release date", "CN Received Date", "CN Received Dt"] },
  { key: "cnNumber", label: "CN Number", aliases: ["CN Number", "Credit Note No", "Credit Note Number"] },
  { key: "cnAmount", label: "CN Amount / Settlement Amount", aliases: ["Settlement Amount", "Settlement amount", "CN Amount", "CN Amt", "Credit Amount", "Settled Amount"] },
  { key: "differenceAmount", label: "Difference Amount", aliases: ["Short", "Short Amount", "CN Amt. Diff", "Difference in settlement", "Difference Amount", "Difference", "Short / Difference"] },
  { key: "remarks", label: "Remarks", aliases: ["Comments", "Upendras comment", "Remarks", "Note"] }
];

const CN_IMPORT_HEADER_HINTS = CN_IMPORT_FIELDS.flatMap((field) => field.aliases).map(normalizeImportHeader).filter(Boolean);

function cnImportHeaderScore(values) {
  return values.reduce((score, value) => {
    const header = normalizeImportHeader(value);
    if (!header) return score;
    return score + (CN_IMPORT_HEADER_HINTS.some((hint) => header === hint || header.includes(hint) || hint.includes(header)) ? 1 : 0);
  }, 0);
}

function cnColumnLooksLikeDate(sheet, columnIndex, startRow) {
  let dateCount = 0;
  let checkedCount = 0;
  const endRow = Math.min(sheet.rowCount, startRow + 25);
  for (let rowNumber = startRow; rowNumber <= endRow; rowNumber += 1) {
    const text = excelCellText(sheet.getRow(rowNumber).getCell(columnIndex).value);
    if (!text) continue;
    checkedCount += 1;
    if (normalizeDateInput(text)) dateCount += 1;
    if (checkedCount >= 8) break;
  }
  return dateCount >= Math.max(2, Math.ceil(checkedCount * 0.5));
}

function repairCnHeaders(sheet, headerRowIndex, headersOriginal) {
  const headers = [...headersOriginal];
  headers.forEach((header, index) => {
    if (String(header || "").trim()) return;
    const columnIndex = index + 1;
    let repaired = "";
    for (let rowNumber = headerRowIndex - 1; rowNumber >= 1; rowNumber -= 1) {
      const aboveText = excelCellText(sheet.getRow(rowNumber).getCell(columnIndex).value);
      if (aboveText && cnImportHeaderScore([aboveText]) > 0) {
        repaired = aboveText;
        break;
      }
    }
    if (!repaired && cnColumnLooksLikeDate(sheet, columnIndex, headerRowIndex + 1)) repaired = "Date";
    headers[index] = repaired;
  });
  return headers;
}

function cnHeaderMatchScore(header, aliases) {
  const normalizedHeader = normalizeImportHeader(header);
  if (!normalizedHeader) return 0;
  return aliases.reduce((best, alias) => {
    const normalizedAlias = normalizeImportHeader(alias);
    if (!normalizedAlias) return best;
    if (normalizedHeader === normalizedAlias) return Math.max(best, 100);
    if (normalizedHeader.includes(normalizedAlias) || normalizedAlias.includes(normalizedHeader)) return Math.max(best, 60);
    const headerWords = new Set(normalizedHeader.split(" ").filter(Boolean));
    const aliasWords = normalizedAlias.split(" ").filter(Boolean);
    const matches = aliasWords.filter((word) => headerWords.has(word)).length;
    return Math.max(best, matches ? matches * 12 : 0);
  }, 0);
}

function detectCnHeaderMapping(headersOriginal, manualMapping = {}) {
  return CN_IMPORT_FIELDS.reduce((mapping, field) => {
    const manualValue = manualMapping?.[field.key];
    if (manualValue !== undefined && manualValue !== "" && Number(manualValue) >= 0) {
      mapping[field.key] = Number(manualValue);
      return mapping;
    }
    let bestIndex = -1;
    let bestScore = 0;
    headersOriginal.forEach((header, index) => {
      const score = cnHeaderMatchScore(header, field.aliases);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });
    mapping[field.key] = bestScore >= 45 ? bestIndex : -1;
    return mapping;
  }, {});
}

function cnImportValueFromMapping(row, mapping, fieldKey) {
  const index = Number(mapping?.[fieldKey]);
  if (!Number.isFinite(index) || index < 0) return "";
  return excelCellText(row.getCell(index + 1).value);
}

function cnImportDateFromMapping(row, mapping, fieldKey) {
  const index = Number(mapping?.[fieldKey]);
  if (!Number.isFinite(index) || index < 0) return "";
  return excelDate(row.getCell(index + 1).value);
}

function cnImportPossibleDuplicate(record, existingRecords, currentRows) {
  const vendor = String(record.vendorName || "").trim().toLowerCase();
  const order = String(record.previousOrderNo || "").trim().toLowerCase();
  const reference = String(record.vendorReferenceNo || record.vendorDocumentReference || "").trim().toLowerCase();
  if (!vendor || (!order && !reference)) return false;
  const all = [...(existingRecords || []), ...currentRows.map((row) => row.record)];
  return all.some((item) => {
    const itemVendor = String(item.vendorName || "").trim().toLowerCase();
    const itemOrder = String(item.previousOrderNo || "").trim().toLowerCase();
    const itemReference = String(item.vendorReferenceNo || item.vendorDocumentReference || item.companyFormNo || item.dcNumber || "").trim().toLowerCase();
    return itemVendor === vendor && ((order && itemOrder === order) || (reference && itemReference === reference));
  });
}

function cnImportSheetWarnings(sheetPreview) {
  const warnings = [];
  const mapped = sheetPreview.mapping || {};
  const rows = sheetPreview.rows || [];
  if (mapped.importedStatusText < 0) warnings.push("Status column was not detected.");
  if (mapped.previousOrderNo < 0) warnings.push("Order number column was not detected.");
  if (mapped.vendorReferenceNo < 0 && mapped.vendorDocumentReference < 0) warnings.push("Vendor reference column was not detected.");
  if (mapped.pickupDate < 0 || !rows.some((row) => row.record.pickupDate || row.record.dispatchDate)) warnings.push("No pickup/dispatch date found. Awaited ageing may use created date.");
  const needsReviewCount = rows.filter((row) => row.needsReview).length;
  if (rows.length && needsReviewCount / rows.length > 0.25) warnings.push("Many rows are going to Needs Review. Please check mapping before import.");
  const creditedMissing = rows.filter((row) => normalizeCnStatus(row.record.status, row.record) === "CN Credited" && (!row.record.cnNumber || !Number(row.record.cnAmount || 0))).length;
  if (creditedMissing) warnings.push(`${creditedMissing} credited row(s) are missing CN number or amount.`);
  return warnings;
}

function buildCnImportErrorRows(preview) {
  const errors = [];
  (preview?.sheets || []).forEach((sheet) => {
    (sheet.warnings || []).forEach((warning) => {
      errors.push({
        sheetName: sheet.sheetName,
        rowNumber: "",
        reason: warning,
        missingFields: "",
        suggestedFix: "Review the detected mapping and choose the correct Excel column before import."
      });
    });
  });
  (preview?.rows || []).forEach((row) => {
    const missing = [];
    if (!row.record.previousOrderNo) missing.push("Order No");
    if (!row.record.vendorReferenceNo && !row.record.vendorDocumentReference) missing.push("Vendor Reference");
    if (!row.record.importedStatusText) missing.push("Status");
    if (row.missingImportant || row.needsReview || row.possibleDuplicate || row.isDuplicate) {
      errors.push({
        sheetName: row.sheetName,
        rowNumber: row.rowNumber,
        reason: row.isDuplicate ? "Duplicate row" : row.possibleDuplicate ? "Possible duplicate" : row.needsReview ? "Needs review" : "Missing important fields",
        missingFields: missing.join(", "),
        suggestedFix: row.isDuplicate ? "Already exists. Do not import unless this is a separate CN." : "Open mapping preview, correct columns if needed, or edit this CN after import."
      });
    }
  });
  return errors;
}

async function parseCnExcelWorkbook(file, state, user, options = {}) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const manualMappings = options.manualMappings || {};
  const existingRecords = state.cnRequests || [];
  const existingKeys = new Set(existingRecords.map(cnDuplicateKey));
  const existingCount = (state.cnRequests || []).length;
  const rows = [];
  const sheets = [];
  workbook.eachSheet((sheet) => {
    let headerRowIndex = 0;
    let headersOriginal = [];
    let bestScore = 0;
    sheet.eachRow((row, rowNumber) => {
      const values = row.values.slice(1).map(excelCellText);
      const usefulValues = values.filter(Boolean);
      const score = cnImportHeaderScore(values);
      if (usefulValues.length >= 2 && score > bestScore) {
        bestScore = score;
        headersOriginal = values;
        headerRowIndex = rowNumber;
      }
    });
    if (!headersOriginal.length || bestScore < 2) {
      sheets.push({
        sheetName: sheet.name,
        vendorName: sheet.name,
        headerRowIndex: 0,
        headersOriginal: [],
        mapping: {},
        mappedFields: [],
        unmappedFields: CN_IMPORT_FIELDS.map((field) => field.label),
        totalRows: 0,
        sampleRows: [],
        rows: [],
        statusCounts: {},
        warnings: ["Could not detect a header row on this sheet."]
      });
      return;
    }
    headersOriginal = repairCnHeaders(sheet, headerRowIndex, headersOriginal);
    const mapping = detectCnHeaderMapping(headersOriginal, manualMappings[sheet.name] || {});
    const sheetRows = [];
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRowIndex) return;
      const raw = {};
      headersOriginal.forEach((header, index) => {
        raw[normalizeImportHeader(header) || `column_${index + 1}`] = excelCellText(row.getCell(index + 1).value);
      });
      const mappedValues = CN_IMPORT_FIELDS.reduce((values, field) => {
        values[field.key] = cnImportValueFromMapping(row, mapping, field.key);
        return values;
      }, {});
      const hasAnyValue = Object.values(mappedValues).some(Boolean) || Object.values(raw).some(Boolean);
      if (!hasAnyValue) return;
      const importedStatusText = mappedValues.importedStatusText;
      const remarks = mappedValues.remarks;
      const reference = mappedValues.vendorReferenceNo;
      const vendorDocumentReference = mappedValues.vendorDocumentReference;
      const createdDate = cnImportDateFromMapping(row, mapping, "createdDate") || new Date().toISOString().slice(0, 10);
      const pickupDate = cnImportDateFromMapping(row, mapping, "pickupDate");
      const cnReceivedDate = cnImportDateFromMapping(row, mapping, "cnReceivedDate");
      const cnNumber = mappedValues.cnNumber;
      const cnAmount = importNumber(mappedValues.cnAmount);
      const statusMap = mapImportedCnStatus([importedStatusText, remarks].filter(Boolean).join(" "), {
        dispatchDate: pickupDate,
        pickupDate,
        cnReceivedDate,
        cnNumber,
        cnAmount
      });
      const record = {
        id: uuid("cn"),
        cnTicketNumber: `CN-IMP-${String(existingCount + rows.length + 1).padStart(5, "0")}`,
        cnaNumber: `IMP-${String(rows.length + 1).padStart(4, "0")}`,
        storeId: state.stores[0]?.id || "",
        vendorName: mappedValues.vendorName || sheet.name,
        vendorReferenceNo: reference,
        vendorDocumentReference,
        companyFormNo: vendorDocumentReference,
        previousOrderNo: mappedValues.previousOrderNo,
        newOrderNo: mappedValues.newOrderNo,
        productDetails: mappedValues.productDetails,
        productType: /frame/i.test(mappedValues.productDetails || "") ? "Frame" : "Lens",
        quantity: importNumber(mappedValues.quantity) || 1,
        reasonForReturn: mappedValues.reasonForReturn || "Other",
        amountExclusiveTax: importNumber(mappedValues.expectedCnAmountExTax),
        expectedCnAmountExTax: importNumber(mappedValues.expectedCnAmountExTax),
        expectedCnAmountIncTax: importNumber(mappedValues.expectedCnAmountIncTax),
        importedStatusText,
        importedColorCode: excelRowColor(row),
        status: statusMap.status,
        cnReceivedDate,
        cnDate: cnReceivedDate,
        cnNumber,
        cnAmount,
        settlementAmount: cnAmount,
        differenceAmount: importNumber(mappedValues.differenceAmount),
        remarks,
        dispatchDate: pickupDate,
        pickupDate,
        source: "excel_import",
        importedSheetName: sheet.name,
        importedRowNumber: rowNumber,
        createdBy: user.id,
        createdByName: user.name || user.email,
        createdByImportUser: user.id,
        importedAt: new Date().toISOString(),
        createdDate,
        createdAt: createdDate ? new Date(`${createdDate}T00:00:00`).toISOString() : new Date().toISOString(),
        closedAt: statusMap.status === "Closed" ? (cnReceivedDate || createdDate || new Date().toISOString()) : "",
        closureOutcome: statusMap.closureOutcome || "",
        needsReview: statusMap.needsReview,
        ledgerUpdated: statusMap.status === "Closed"
      };
      const missingImportant = !record.previousOrderNo && !record.vendorReferenceNo && !record.vendorDocumentReference;
      const duplicateKey = cnDuplicateKey(record);
      const isDuplicate = Boolean(duplicateKey && (existingKeys.has(duplicateKey) || rows.some((item) => cnDuplicateKey(item.record) === duplicateKey)));
      const possibleDuplicate = !isDuplicate && cnImportPossibleDuplicate(record, existingRecords, rows);
      const importRow = {
        sheetName: sheet.name,
        rowNumber,
        raw,
        record: { ...record, needsReview: Boolean(record.needsReview || missingImportant) },
        isDuplicate,
        possibleDuplicate,
        missingImportant,
        needsReview: Boolean(record.needsReview || missingImportant)
      };
      rows.push(importRow);
      sheetRows.push(importRow);
    });
    const statusCounts = CN_STATUSES.reduce((counts, status) => {
      counts[status] = sheetRows.filter((row) => normalizeCnStatus(row.record.status, row.record) === status).length;
      return counts;
    }, {});
    const mappedFields = CN_IMPORT_FIELDS.filter((field) => Number(mapping[field.key]) >= 0).map((field) => field.label);
    const unmappedFields = CN_IMPORT_FIELDS.filter((field) => Number(mapping[field.key]) < 0).map((field) => field.label);
    const sheetPreview = {
      sheetName: sheet.name,
      vendorName: sheet.name,
      headerRowIndex,
      headersOriginal,
      mapping,
      mappedFields,
      unmappedFields,
      totalRows: sheetRows.length,
      sampleRows: sheetRows.slice(0, 5),
      rows: sheetRows,
      statusCounts,
      warnings: []
    };
    sheetPreview.warnings = cnImportSheetWarnings(sheetPreview);
    sheets.push(sheetPreview);
  });
  const statusCounts = CN_STATUSES.reduce((counts, status) => {
    counts[status] = rows.filter((row) => normalizeCnStatus(row.record.status, row.record) === status).length;
    return counts;
  }, {});
  const duplicateRows = rows.filter((row) => row.isDuplicate);
  const possibleDuplicateRows = rows.filter((row) => row.possibleDuplicate);
  const needsReviewRows = rows.filter((row) => row.needsReview || row.missingImportant);
  const readyRows = rows.filter((row) => !row.isDuplicate && (!row.possibleDuplicate || options.includePossibleDuplicates));
  const preview = {
    file,
    rows,
    sheets,
    duplicates: duplicateRows,
    possibleDuplicates: possibleDuplicateRows,
    needsReview: needsReviewRows,
    ready: readyRows,
    statusCounts,
    manualMappings,
    includePossibleDuplicates: Boolean(options.includePossibleDuplicates),
    errorRows: [],
    batch: {
      id: uuid("cn-import"),
      fileName: file.name,
      uploadedBy: user.id,
      uploadedAt: new Date().toISOString(),
      totalRows: rows.length,
      importedRows: readyRows.length,
      duplicateRows: duplicateRows.length,
      failedRows: 0,
      needsReviewRows: needsReviewRows.length,
      status: "preview"
    },
    message: `${rows.length} rows found. ${readyRows.length} ready, ${duplicateRows.length} duplicates, ${possibleDuplicateRows.length} possible duplicates, ${needsReviewRows.length} need review.`
  };
  preview.errorRows = buildCnImportErrorRows(preview);
  return preview;
}

function nextCnNumbers(state) {
  const year = new Date().getFullYear();
  const count = (state.cnRequests || []).filter((request) => String(request.cnTicketNumber || "").includes(`CN-${year}-`)).length + 1;
  return {
    cnTicketNumber: `CN-${year}-${String(count).padStart(5, "0")}`,
    cnaNumber: String((state.cnRequests || []).length + 1).padStart(3, "0")
  };
}

function parseCnTextForDetails(text) {
  const source = String(text || "");
  const amount = source.match(/(?:total|amount|credit\s*note|cn|rs|inr|₹)\D{0,18}([0-9]{2,}(?:[,0-9]*)(?:\.\d{1,2})?)/i) || source.match(/(?:rs|inr|₹)?\s*([0-9]{2,}(?:[,0-9]*)(?:\.\d{1,2})?)/i);
  const taxAmount = source.match(/(?:tax|gst|cgst|sgst|igst)\D{0,18}([0-9]{1,}(?:[,0-9]*)(?:\.\d{1,2})?)/i);
  const totalAmount = source.match(/(?:grand\s*total|total)\D{0,18}([0-9]{2,}(?:[,0-9]*)(?:\.\d{1,2})?)/i);
  const date = source.match(/(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4})/);
  const cnNumber = source.match(/\b(?:CN|CREDIT\s*NOTE|CRN|NOTE|CREDIT\s*NO)[\s:#-]*([A-Z0-9/-]{3,})/i);
  const vendorReference = source.match(/\b(?:DC|DN|LRF|CHALLAN|REF|REFERENCE|SAP|BILL)[\s:#-]*([A-Z0-9/-]{3,})/i);
  const orderNumber = source.match(/\b(?:ORDER|ORD)[\s:#-]*([A-Z0-9/-]{3,})/i);
  const vendorMatch = CN_VENDOR_OPTIONS.filter((vendor) => vendor !== "Other").find((vendor) => source.toLowerCase().includes(vendor.toLowerCase()));
  return {
    vendorName: vendorMatch || "",
    cnNumber: cnNumber?.[1] || "",
    cnDate: date?.[1] ? normalizeDateInput(date[1]) : "",
    cnAmount: amount?.[1] ? Number(amount[1].replace(/,/g, "")) : "",
    taxAmount: taxAmount?.[1] ? Number(taxAmount[1].replace(/,/g, "")) : "",
    totalAmount: totalAmount?.[1] ? Number(totalAmount[1].replace(/,/g, "")) : "",
    vendorReferenceNo: vendorReference?.[1] || "",
    orderNumber: orderNumber?.[1] || "",
    remarks: ""
  };
}

function normalizeDateInput(value) {
  const text = String(value || "").trim();
  let match = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (match) return `${match[1]}-${match[2].padStart(2, "0")}-${match[3].padStart(2, "0")}`;
  match = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (!match) return "";
  const [, day, month, rawYear] = match;
  const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

async function tryReadTextFromImage(file, label = "photo") {
  if (!file) return { text: "", confidence: 0, status: "failed", message: `No ${label} selected.` };
  try {
    const TesseractModule = await import("tesseract.js");
    const recognizer = TesseractModule.recognize || TesseractModule.default?.recognize;
    if (recognizer) {
      const result = await recognizer(file, "eng");
      const text = result?.data?.text || "";
      const confidence = Math.max(0, Math.min(1, Number(result?.data?.confidence || 0) / 100));
      return { text, confidence, status: text ? "extracted" : "failed", message: text ? `Text read from ${label}. Please verify before saving.` : "No readable text found." };
    }
  } catch (error) {
    if (!/Failed to fetch|import|module|network/i.test(String(error?.message || ""))) {
      return { text: "", confidence: 0, status: "failed", message: error.message || `${label} could not be read automatically.` };
    }
  }
  try {
    if (globalThis.TextDetector && globalThis.createImageBitmap) {
      const detector = new globalThis.TextDetector();
      const bitmap = await createImageBitmap(file);
      const detected = await detector.detect(bitmap);
      const text = (detected || []).map((item) => item.rawValue || item.rawText || item.text || "").filter(Boolean).join("\n");
      return { text, confidence: text ? 0.62 : 0, status: text ? "low_confidence" : "failed", message: text ? "Text read with browser OCR. Please verify." : "Browser OCR could not find text." };
    }
  } catch (error) {
    return { text: "", confidence: 0, status: "failed", message: error.message || "Photo could not be read automatically." };
  }
  return {
    text: "",
    confidence: 0,
    status: "failed",
    message: `This browser could not read the ${label} automatically. Photo is saved; enter details manually.`
  };
}

function parsePurchaseDocumentText(text = "", state = {}) {
  const source = String(text || "").replace(/\s+/g, " ").trim();
  const gstin = source.match(/\b[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]\b/i)?.[0] || "";
  const invoice = source.match(/\b(?:invoice|inv|bill|dc|challan)\s*(?:no|number|#|:)?\s*([A-Z0-9/-]{2,})/i)?.[1] || "";
  const total = source.match(/\b(?:grand\s*total|invoice\s*total|net\s*amount|total)\D{0,18}([0-9]{2,}(?:[,0-9]*)(?:\.\d{1,2})?)/i)?.[1] || "";
  const taxAmount = source.match(/\b(?:gst|cgst|sgst|igst|tax)\D{0,18}([0-9]{1,}(?:[,0-9]*)(?:\.\d{1,2})?)/i)?.[1] || "";
  const date = source.match(/\b(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4})\b/)?.[1] || "";
  const hsnMatches = [...source.matchAll(/\b(?:HSN|SAC)\D{0,8}([0-9]{4,8})\b/gi)].map((match) => match[1]);
  const supplier = (state.crmSuppliers || []).find((row) => {
    const name = String(row.supplierName || "").toLowerCase();
    return name && source.toLowerCase().includes(name);
  });
  return {
    supplierId: supplier?.id || "",
    supplierName: supplier?.supplierName || "",
    gstin: gstin || supplier?.gstin || "",
    documentNo: invoice,
    documentDate: date ? normalizeDateInput(date) : "",
    totalAmount: total ? Number(total.replace(/,/g, "")) : "",
    taxAmount: taxAmount ? Number(taxAmount.replace(/,/g, "")) : "",
    hsnCodes: [...new Set(hsnMatches)],
    lineItemsText: source.slice(0, 1800)
  };
}

function buildCnExtractionResult({ imageText = "", fileName = "", request = {} }) {
  const sourceText = [
    imageText,
    fileName,
    request.vendorName,
    request.vendorReferenceNo,
    request.vendorDocumentReference,
    request.companyFormNo,
    request.previousOrderNo,
    request.newOrderNo
  ].filter(Boolean).join("\n");
  const fields = parseCnTextForDetails(sourceText);
  const foundKeys = ["vendorName", "cnNumber", "cnDate", "cnAmount", "taxAmount", "totalAmount", "vendorReferenceNo", "orderNumber"].filter((key) => fields[key]);
  const confidence = Math.min(0.92, foundKeys.length ? 0.34 + foundKeys.length * 0.08 : 0.12);
  const lowConfidenceFields = ["cnNumber", "cnDate", "cnAmount"].filter((key) => !fields[key]);
  return {
    fields,
    confidenceScore: Number(confidence.toFixed(2)),
    lowConfidenceFields,
    status: confidence >= 0.65 ? "extracted" : confidence > 0.2 ? "low_confidence" : "failed",
    extractedText: imageText
  };
}

function createAppNotification({
  userId,
  module,
  title,
  message,
  linkedRecordType,
  linkedRecordId,
  type = "app_event",
  priority = "normal",
  entityType = linkedRecordType || "",
  entityId = linkedRecordId || "",
  storeId = "",
  createdBy = ""
}) {
  return {
    id: uuid("notification"),
    userId,
    module,
    title,
    message,
    type,
    priority,
    entityType,
    entityId,
    storeId,
    readAt: null,
    createdBy,
    linkedRecordType,
    linkedRecordId,
    isRead: false,
    createdAt: new Date().toISOString()
  };
}

function isSuperAdminUser(user = {}) {
  return (
    user.active !== false &&
    (
      user.role === "super_admin" ||
      user.role === "Super Admin" ||
      user.permissions?.all ||
      user.permissions?.__businessRoleKey === "owner_super_admin" ||
      /owner|super admin|director|ceo/i.test(user.permissions?.__businessRoleLabel || "")
    )
  );
}

function createNotificationForSuperAdmins(state, event) {
  const superAdmins = (state.users || []).filter(isSuperAdminUser);
  return superAdmins.map((admin) => createAppNotification({
    userId: admin.id,
    module: event.module || event.entityType || "App",
    title: event.title,
    message: event.message,
    type: event.type || "app_event",
    priority: event.priority || "normal",
    entityType: event.entityType || event.linkedRecordType || "",
    entityId: event.entityId || event.linkedRecordId || "",
    storeId: event.storeId || "",
    linkedRecordType: event.linkedRecordType || event.entityType || "",
    linkedRecordId: event.linkedRecordId || event.entityId || "",
    createdBy: event.createdBy || ""
  }));
}

async function saveNotifications(updateState, notifications) {
  if (!notifications.length) return [];
  const saved = [];
  for (const notification of notifications) {
    try {
      await persistOptionalEntity("notifications", notification);
      saved.push(notification);
    } catch (error) {
      console.error("Notification creation failed", { notification, error });
    }
  }
  if (saved.length) {
    updateState((current) => ({ ...current, notifications: [...saved, ...(current.notifications || [])] }));
  }
  return saved;
}

async function notifySuperAdmins(state, updateState, event) {
  const notifications = createNotificationForSuperAdmins(state, event);
  return saveNotifications(updateState, notifications);
}

function dateOffset(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function orderTotal(order) {
  return Number(order?.payment?.totalAmount || order?.totalAmount || 0);
}

function orderCollection(order) {
  const payment = order?.payment || {};
  const collectionRows = payment.collections || payment.paymentHistory || [];
  const laterCollections = collectionRows.reduce((sum, row) => sum + Number(row.amount || row.amountReceived || 0), 0);
  return Number(payment.advance || payment.advanceReceived || payment.paidAmount || 0) + laterCollections;
}

function orderBalance(order) {
  const calculatedBalance = Math.max(0, orderTotal(order) - orderCollection(order));
  if (orderTotal(order) > 0) return calculatedBalance;
  return Number(order?.payment?.balance || 0);
}

function computedOrderBalance(order) {
  return Math.max(0, orderTotal(order) - orderCollection(order));
}

function orderPaymentStatus(order) {
  const payment = order?.payment || {};
  if (computedOrderBalance(order) <= 0) return "Paid";
  return payment.status || "Pending";
}

function orderPaymentHistory(order) {
  return order?.payment?.collections || order?.payment?.paymentHistory || [];
}

function orderDeliveryStatus(order, state) {
  const trackingRow = (state?.opticalDeliveryTrackingEntries || []).find((row) => row.slipId === order?.id);
  return order?.orderDetails?.deliveryStatus || trackingRow?.status || order?.deliveryStatus || OPTICAL_DELIVERY_STATUS;
}

const COLLECT_BALANCE_PAYMENT_MODES = ["Cash", "UPI", "Card", "Paytm", "Bank Transfer", "Other"];

function localDateTimeInputValue(date = new Date()) {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function emptyCollectBalanceRow(amount = "") {
  return {
    id: uuid("collect-row"),
    amount,
    paymentMode: "",
    receivedAt: localDateTimeInputValue(),
    note: ""
  };
}

function normalizeCustomerOrderPaymentRows(rows = []) {
  return rows
    .map((row) => ({
      ...row,
      amount: Number(row.amount || 0),
      paymentMode: String(row.paymentMode || row.mode || "").trim(),
      receivedAt: row.receivedAt || row.collectedAt || "",
      note: String(row.note || row.ref || "").trim()
    }))
    .filter((row) => row.amount || row.paymentMode || row.receivedAt || row.note);
}

async function persistCustomerOrderBalanceCollection({ state, updateState, order, rows, receivedBy, user }) {
  if (!order?.id) throw new Error("Order not found. Please refresh and try again.");
  const balanceBefore = computedOrderBalance(order);
  const normalizedRows = normalizeCustomerOrderPaymentRows(rows);
  if (!normalizedRows.length) throw new Error("Add at least one payment row.");
  const invalidRow = normalizedRows.find((row) => !row.amount || row.amount <= 0 || !row.paymentMode);
  if (invalidRow) throw new Error("Every payment row needs amount greater than 0 and payment mode.");
  const amount = normalizedRows.reduce((sum, row) => sum + row.amount, 0);
  if (!amount || amount <= 0) throw new Error("Total collected cannot be zero.");
  if (amount > balanceBefore) throw new Error(`Total collected cannot be more than pending balance ${opticalMoney(balanceBefore)}.`);

  const now = new Date().toISOString();
  const collectionReferenceId = uuid("opt-payment-batch");
  const collector = receivedBy || user.name || user.email || "";
  const collectionRows = normalizedRows.map((row) => ({
    id: uuid("opt-payment-collection"),
    collectionReferenceId,
    amount: row.amount,
    paymentMode: row.paymentMode,
    receivedBy: collector,
    receivedByUserId: user.id,
    notes: row.note,
    collectedAt: row.receivedAt ? new Date(row.receivedAt).toISOString() : now
  }));
  const nextCollections = [...collectionRows, ...orderPaymentHistory(order)];
  const totalAmount = orderTotal(order);
  const advanceReceived = Number(order.payment?.advanceReceived || order.payment?.advance || 0);
  const totalCollected = advanceReceived + nextCollections.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const nextBalance = Math.max(0, totalAmount - totalCollected);
  const nextPaymentStatus = nextBalance <= 0 ? "Paid" : "Pending";
  const splitSummary = collectionRows.map((row) => `${row.paymentMode} ${opticalMoney(row.amount)}`).join(" + ");
  const updatedOrder = {
    ...order,
    payment: {
      ...(order.payment || {}),
      collections: nextCollections,
      paidAfterAdvance: nextCollections.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      totalCollected,
      balance: nextBalance,
      status: nextPaymentStatus
    },
    orderDetails: {
      ...(order.orderDetails || {}),
      paymentTimeline: [
        orderTimelineEvent({
          eventType: "Balance collected",
          oldStatus: order.payment?.status || "Pending",
          newStatus: nextPaymentStatus,
          note: `${opticalMoney(amount)} collected by ${collector}: ${splitSummary}.`,
          user,
          extra: { amount, collectionReferenceId, paymentRows: collectionRows }
        }),
        ...(order.orderDetails?.paymentTimeline || [])
      ]
    },
    updatedAt: now
  };
  const savedOrder = await persistRequiredEntity("optical_fast_slips", updatedOrder, "Order payment");
  const pendingEntry = (state.opticalPendingPaymentEntries || []).find((row) => row.slipId === order.id);
  const savedPendingEntry = pendingEntry
    ? await persistRequiredEntity("optical_pending_payment_entries", {
        ...pendingEntry,
        balance: nextBalance,
        advanceReceived: totalCollected,
        status: nextPaymentStatus,
        updatedAt: now
      }, "Pending payment")
    : null;
  updateState((current) => ({
    ...current,
    opticalFastSlips: (current.opticalFastSlips || []).map((row) => (row.id === savedOrder.id ? savedOrder : row)),
    opticalPendingPaymentEntries: savedPendingEntry
      ? upsertById(current.opticalPendingPaymentEntries || [], savedPendingEntry)
      : (current.opticalPendingPaymentEntries || [])
  }));
  await notifySuperAdmins(state, updateState, {
    module: "Payments",
    title: "Payment Collected",
    message: `${opticalMoney(amount)} collected for order ${savedOrder.orderNumber}.`,
    priority: nextPaymentStatus === "Paid" ? "normal" : "low",
    entityType: "optical_fast_slip",
    entityId: savedOrder.id,
    storeId: savedOrder.storeId,
    createdBy: user.id
  });
  return savedOrder;
}

async function persistCustomerOrderDelivered({ state, updateState, order, user }) {
  if (!order?.id) throw new Error("Order not found. Please refresh and try again.");
  const now = new Date().toISOString();
  const updatedOrder = {
    ...order,
    orderDetails: {
      ...(order.orderDetails || {}),
      deliveryStatus: "Delivered",
      deliveredAt: now,
      deliveredBy: user.name || user.email,
      orderTimeline: [
        orderTimelineEvent({
          eventType: "Order delivered",
          oldStatus: orderDeliveryStatus(order, state),
          newStatus: "Delivered",
          note: `Delivery marked by ${user.name || user.email}.`,
          user
        }),
        ...getOrderTimeline(order)
      ]
    },
    updatedAt: now
  };
  const savedOrder = await persistRequiredEntity("optical_fast_slips", updatedOrder, "Order delivery");
  const existingDelivery = (state.opticalDeliveryTrackingEntries || []).find((row) => row.slipId === order.id);
  const deliveryRow = {
    ...(existingDelivery || {}),
    id: existingDelivery?.id || uuid("opt-delivery"),
    slipId: order.id,
    orderNumber: order.orderNumber,
    customerName: order.customer?.name || order.customerName || "",
    deliveryDate: order.deliveryDate || now.slice(0, 10),
    status: "Delivered",
    storeId: order.storeId,
    salespersonId: order.salespersonId || "",
    createdAt: existingDelivery?.createdAt || now,
    updatedAt: now
  };
  const savedDelivery = await persistRequiredEntity("optical_delivery_tracking_entries", deliveryRow, "Delivery tracking");
  updateState((current) => ({
    ...current,
    opticalFastSlips: (current.opticalFastSlips || []).map((row) => (row.id === savedOrder.id ? savedOrder : row)),
    opticalDeliveryTrackingEntries: upsertById(current.opticalDeliveryTrackingEntries || [], savedDelivery)
  }));
  await notifySuperAdmins(state, updateState, {
    module: "Orders",
    title: "Order Delivered",
    message: `Order ${savedOrder.orderNumber} was marked delivered.`,
    priority: computedOrderBalance(savedOrder) > 0 ? "high" : "normal",
    entityType: "optical_fast_slip",
    entityId: savedOrder.id,
    storeId: savedOrder.storeId,
    createdBy: user.id
  });
  return savedOrder;
}

function monthOf(dateText) {
  return String(dateText || "").slice(0, 7);
}

function localDateOnly(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function localMonthOnly(value = new Date()) {
  return localDateOnly(value).slice(0, 7);
}

function storeManagerName(state, store) {
  const managerId = store?.managerId || store?.manager_id;
  const manager = state.employees.find((employee) => employee.id === managerId) || state.employees.find((employee) => employee.storeId === store?.id && /manager|admin|head/i.test(employee.designation || ""));
  return manager?.fullName || "-";
}

function buildStoreMetrics(state, store, employees, month = localMonthOnly()) {
  const today = localDateOnly();
  const yesterday = dateOffset(-1);
  const storeEmployees = employees.filter((employee) => employee.storeId === store.id && employee.active !== false);
  const employeeIds = new Set(storeEmployees.map((employee) => employee.id));
  const orders = (state.opticalFastSlips || []).filter((order) => order.storeId === store.id);
  const yesterdayOrders = orders.filter((order) => order.date === yesterday);
  const monthOrders = orders.filter((order) => monthOf(order.date) === month);
  const dueTodayOrders = orders.filter((order) => order.deliveryDate === today || order.orderDetails?.deliveryDate === today);
  const readyNotDelivered = orders.filter((order) => /ready/i.test(orderDeliveryStatus(order, state)) && !/delivered/i.test(orderDeliveryStatus(order, state)));
  const deliveredPaymentPending = orders.filter((order) => /delivered/i.test(orderDeliveryStatus(order, state)) && orderBalance(order) > 0);
  const pendingOrders = orders.filter((order) => orderBalance(order) > 0 || !/delivered/i.test(orderDeliveryStatus(order, state)));
  const todayPunches = (state.punches || []).filter((punch) => attendancePunchDate(punch) === today && employeeIds.has(punch.employeeId));
  const presentIds = new Set(todayPunches.map((punch) => punch.employeeId));
  const absentEmployees = storeEmployees.filter((employee) => !presentIds.has(employee.id));
  const lowStock = (state.crmProducts || []).filter((product) => product.storeId === store.id && Number(product.stockQty || 0) <= Number(product.reorderLevel || 0));
  const salespersonTotals = new Map();
  monthOrders.forEach((order) => {
    if (!order.salespersonId) return;
    salespersonTotals.set(order.salespersonId, (salespersonTotals.get(order.salespersonId) || 0) + orderTotal(order));
  });
  const topSalespersonId = [...salespersonTotals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const latestReco = (state.bankReco || [])
    .filter((item) => item.storeId === store.id)
    .slice()
    .sort((a, b) => String(b.recoDate || b.createdAt || "").localeCompare(String(a.recoDate || a.createdAt || "")))[0];
  return {
    todaySale: orders.filter((order) => order.date === today).reduce((sum, order) => sum + orderTotal(order), 0),
    yesterdaySale: yesterdayOrders.reduce((sum, order) => sum + orderTotal(order), 0),
    yesterdayCollection: yesterdayOrders.reduce((sum, order) => sum + orderCollection(order), 0),
    monthSale: monthOrders.reduce((sum, order) => sum + orderTotal(order), 0),
    monthCollection: monthOrders.reduce((sum, order) => sum + orderCollection(order), 0),
    pendingBalance: orders.reduce((sum, order) => sum + orderBalance(order), 0),
    deliveredPaymentPending: deliveredPaymentPending.length,
    ordersDueToday: dueTodayOrders.length,
    pendingOrders: pendingOrders.length,
    readyNotDelivered: readyNotDelivered.length,
    labPending: orders.filter((order) => /lab|pending/i.test(orderDeliveryStatus(order, state))).length,
    staffPresent: presentIds.size,
    staffAbsent: absentEmployees.length,
    latePunch: todayPunches.filter((punch) => punch.type === "in" && new Date(punch.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) > "11:30").length,
    bankRecoStatus: latestReco?.status || "pending",
    bankRecoPending: !latestReco || latestReco.status !== "done",
    lowStockCount: lowStock.length,
    topSalesperson: state.employees.find((employee) => employee.id === topSalespersonId)?.fullName || "-",
    storeManager: storeManagerName(state, store),
    cashSubmitted: latestReco?.cashInHand != null && latestReco?.status === "done",
    posSubmitted: latestReco?.posMachineSettlement != null && latestReco?.status === "done"
  };
}

const OPTICAL_PAYMENT_MODES = ["Cash", "UPI", "Card", "Paytm", "Split", "Credit"];
const OPTICAL_ORDER_TYPES = [
  ["frame_only", "Only frame"],
  ["lens_only", "Only lenses"],
  ["frame_lens", "Frame and lenses"],
  ["contact_solution", "Contact lenses / Solution"],
  ["service", "Service / Repair"]
];
const OPTICAL_ORDER_ITEMS = [
  ["frame", "Frame"],
  ["lens", "Lens"],
  ["service", "Service"],
  ["contactLens", "Contact Lens"],
  ["solution", "Solution"]
];
const OPTICAL_EXTRA_PRODUCT_TYPES = [
  ["frameLens", "Frame + lens set"],
  ["frame", "Only frame"],
  ["lens", "Only lens"],
  ["contactSolution", "Contact lens / solution"],
  ["sunglasses", "Sunglasses"],
  ["accessory", "Accessory"],
  ["service", "Service / repair"],
  ["eyeTest", "Eye test"],
  ["other", "Other"]
];
const OPTICAL_PRODUCT_TYPES = [
  ["frame", "Frame"],
  ["lens", "Lens"],
  ["contactSolution", "Contact Lens / Solution"],
  ["sunglasses", "Sunglasses"],
  ["accessory", "Accessories"],
  ["service", "Service / Repair"],
  ["eyeTest", "Eye Test"]
];
const CRM_LAYERS = [
  ["overview", "Optical CRM"],
  ["masters", "Masters"],
  ["products", "Products"],
  ["inventory", "Inventory"],
  ["purchase", "Purchase"],
  ["sales", "Sales"],
  ["customer", "Customer"],
  ["reports", "Reports"],
  ["sms", "SMS"],
  ["prescription", "Prescription"],
  ["account", "Account"]
];
const CRM_LEGACY_LAYER_MAP = {
  cockpit: "overview",
  orders: "sales",
  customers: "customer",
  payments: "sales",
  communication: "sms",
  appointments: "prescription"
};
const CRM_PLACEHOLDER_GROUPS = {
  masters: ["Branches / Stores", "Suppliers / Vendors", "Product Categories", "Brands", "GST / HSN Master"],
  products: ["Frame", "Sunglasses", "Lens", "Contact Lens", "Solution", "Other", "Non-Chargeable"],
  inventory: [
    "Stock Search",
    "Inventory Levels",
    "Stock Inward",
    "Order-specific Stock",
    "Transfer Stock",
    "Transfer Stock Using Barcode",
    "Pending Transfers",
    "Transfer History",
    "Stock Movement",
    "Track Barcode",
    "Lens Grid View",
    "Inventory Audit",
    "Inventory Adjustment",
    "Low Stock Report",
    "Expiry Report"
  ],
  purchase: [
    "Vendor Orders",
    "Pending Vendor Orders",
    "Vendor Order Slips",
    "Vendor DC Inward",
    "Purchase Inward",
    "Vendor Bill Audit",
    "Vendor-wise Purchases",
    "Purchase Reports"
  ],
  prescription: ["Add Prescription / Eye Test", "Prescription Database", "Patient Details", "Appointments"],
  account: [
    "Expenses",
    "Ledgers",
    "Group Ledgers",
    "Vouchers",
    "Account Payable",
    "Account Receivable",
    "Account Statement",
    "Trading, P&L Statement & Balance Sheet",
    "Referral Payments"
  ]
};
const CRM_PRODUCT_TYPES = ["Frame", "Lens", "Contact Lens", "Solution", "Sunglasses", "Accessory", "Service", "Eye Test"];
const CRM_COMMUNICATION_TYPES = ["Order ready", "Follow-up", "Feedback", "Birthday", "Anniversary", "Payment reminder", "Custom"];
const CRM_DEFAULT_SUPPLIERS = ["Luxottica", "Ray-Ban distributor", "Pure Optics", "Essilor", "Zeiss", "Asdol", "VRX", "Suprol", "Anvira", "Nikon", "Johnson & Johnson", "Bausch + Lomb", "Local", "Other"];
const CRM_DEFAULT_CATEGORIES = [
  ["Frames", "FRM", 18, true, false, false, false],
  ["Sunglasses", "SUN", 18, true, false, false, false],
  ["Lenses", "LEN", 18, false, false, false, true],
  ["Contact Lenses", "CL", 12, false, true, true, true],
  ["Solutions", "SOL", 18, false, true, true, false],
  ["Accessories", "ACC", 18, true, false, false, false],
  ["Other", "OTH", 18, false, false, false, false],
  ["Non-Chargeable", "NC", 0, false, false, false, false]
];
const CRM_DEFAULT_BRANDS = ["Ray-Ban", "Tommy Hilfiger", "Calvin Klein", "Prada", "Burberry", "Pure Optics", "Essilor", "Zeiss", "Asdol", "VRX", "Suprol", "Anvira", "Nikon", "Johnson & Johnson", "Bausch + Lomb", "Local", "Other"];
const CRM_STOCK_STATUSES = ["In Stock", "Reserved", "Sold", "Transferred", "Damaged", "Returned", "CN Pending", "Order-Specific Stock", "In Lab", "Delivered", "Adjusted"];
const CRM_MOVEMENT_TYPES = ["Opening Stock", "Purchase Inward", "Vendor DC Inward", "Sale", "Reserved Against Order", "Transfer Out", "Transfer In", "Adjustment", "Damage", "Return", "CN Return", "Moved to Lab", "Delivered", "Cancelled Order Reversal"];
const OPTICAL_LENS_ORDER_STATUS = "Pending Review";
const OPTICAL_DELIVERY_STATUS = "Order Received";
const ORDER_MANAGER_STATUSES = {
  new: "New Orders Pending Review",
  assigned: "Assigned",
  pickupAwaited: "Pickup Awaited",
  pickedUp: "Picked Up",
  notPickedUp: "Not Picked Up",
  vendorRequired: "Need Vendor Order",
  vendorPlaced: "Vendor Order Placed",
  received: "Product Received",
  sentToLab: "Sent to Lab",
  labReceived: "Lab Received",
  processing: "Fitting / Processing",
  ready: "Ready",
  delayed: "Delayed"
};
const ORDER_ITEM_STATUSES = {
  review: "Pending Review",
  stock: "Stock Reserved",
  transfer: "Stock Transfer Requested",
  vendorRequired: "Vendor Order Required",
  vendorPlaced: "Vendor Order Placed",
  received: "Product Received From Vendor",
  lab: "Sent to Lab",
  ready: "Ready",
  hold: "Hold / Clarify"
};
const ORDER_SENT_METHODS = ["WhatsApp", "Email", "Portal", "Phone", "Other"];
const OPTICAL_SPH_OPTIONS = buildOpticalNumberOptions(20, 0.25);
const OPTICAL_CYL_OPTIONS = buildOpticalNumberOptions(8, 0.25);
const OPTICAL_ADD_OPTIONS = buildOpticalNumberOptions(4, 0.25);

function buildOpticalNumberOptions(max, step) {
  const values = [];
  for (let index = 1; index <= Math.round(max / step); index += 1) {
    values.push((index * step).toFixed(2));
  }
  return values;
}

function nextOpticalOrderNumber(slips) {
  const today = new Date().toISOString().slice(0, 10).replaceAll("-", "");
  const maxSequence = (slips || []).reduce((max, slip) => {
    const match = String(slip.orderNumber || "").match(new RegExp(`^GC-${today}-(\\d+)$`));
    return match ? Math.max(max, Number(match[1] || 0)) : max;
  }, 0);
  return `GC-${today}-${String(maxSequence + 1).padStart(3, "0")}`;
}

function fallbackOpticalOrderNumber() {
  const now = new Date();
  const today = now.toISOString().slice(0, 10).replaceAll("-", "");
  const time = now.toTimeString().slice(0, 8).replaceAll(":", "");
  const milliseconds = String(now.getMilliseconds()).padStart(3, "0");
  const random = String(Math.floor(Math.random() * 1000)).padStart(3, "0");
  return `GC-${today}-${time}${milliseconds}${random}`;
}

function isDuplicateOrderNumberError(error) {
  const message = String(error?.message || error || "");
  return message.includes("optical_fast_slips_order_number_key") || (message.includes("duplicate key") && message.includes("order_number"));
}

async function persistOpticalOrderWithUniqueNumber(orderRecord) {
  try {
    return await persistRequiredEntity("optical_fast_slips", orderRecord, "Order");
  } catch (error) {
    if (!isDuplicateOrderNumberError(error)) throw error;
    const retryOrderNumber = fallbackOpticalOrderNumber();
    return persistRequiredEntity("optical_fast_slips", {
      ...orderRecord,
      orderNumber: retryOrderNumber,
      gcNumber: retryOrderNumber,
      updatedAt: new Date().toISOString()
    }, "Order");
  }
}

function normalizeLocalDateTime(value, fallback = new Date().toISOString()) {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString();
}

function getOrderWorkflow(order) {
  return order?.orderDetails?.managerWorkflow || {
    status: ORDER_MANAGER_STATUSES.new,
    reviewedAt: "",
    reviewedBy: "",
    vendorName: "",
    vendorOrderReference: "",
    orderSentMethod: "",
    labStatus: ""
  };
}

function getOrderTimeline(order) {
  return order?.orderDetails?.orderTimeline || [];
}

function orderTimelineEvent({ eventType, oldStatus, newStatus, note, user, extra = {} }) {
  return {
    id: uuid("order-event"),
    eventType,
    oldStatus: oldStatus || "",
    newStatus: newStatus || "",
    userId: user?.id || "",
    userName: user?.name || user?.email || "User",
    timestamp: new Date().toISOString(),
    note: note || "",
    ...extra
  };
}

function canAdminCorrectStatus(user, permission = "can_override_order_status") {
  return Boolean(
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    user?.permissions?.all ||
    hasUserPermission(user, permission)
  );
}

function statusCorrectionRecord({ oldStatus, newStatus, reason, user, module = "order" }) {
  return {
    id: uuid("status-correction"),
    module,
    oldStatus: oldStatus || "",
    newStatus: newStatus || "",
    reason: reason || "",
    changedBy: user?.id || "",
    changedByName: user?.name || user?.email || "User",
    changedAt: new Date().toISOString()
  };
}

function orderItemStatus(product = {}) {
  return product.itemStatus || product.orderItemStatus || ORDER_ITEM_STATUSES.review;
}

function orderNeedsVendor(order) {
  return (order?.orderDetails?.products || order?.orderDetails?.extraProducts || []).some((product) =>
    [ORDER_ITEM_STATUSES.vendorRequired, ORDER_ITEM_STATUSES.vendorPlaced].includes(orderItemStatus(product))
  );
}

function orderWorkflowStatus(order) {
  const workflow = getOrderWorkflow(order);
  if (workflow.status) return workflow.status;
  if (orderNeedsVendor(order)) return ORDER_MANAGER_STATUSES.vendorRequired;
  return ORDER_MANAGER_STATUSES.new;
}

function emptyOpticalPower() {
  return { sph: "PL", cyl: "0.00", ax: "" };
}

function emptyOpticalPair() {
  return {
    id: uuid("pair"),
    frameDetails: "",
    frameSource: "store_frame",
    frameStatus: "ready",
    framePhoto: "",
    lensDetails: "",
    lensStatus: "ready",
    tintRequired: false,
    tintAsSample: false,
    tintSamplePhoto: "",
    notes: ""
  };
}

function emptyOpticalExtraProduct(type = "frame") {
  return {
    id: uuid("extra"),
    type,
    name: "",
    barcode: "",
    quantity: 1,
    amount: "",
    notes: ""
  };
}

function emptyOpticalProduct(type = "frame") {
  return {
    id: uuid("product"),
    type,
    name: "",
    quantity: 1,
    amount: "",
    source: type === "frame" || type === "sunglasses" ? "store" : "selected",
    status: "ready",
    photo: "",
    requiresPower: ["lens", "contactSolution", "eyeTest"].includes(type),
    tintRequired: false,
    tintAsSample: false,
    notes: ""
  };
}

function opticalProductLabel(type) {
  return OPTICAL_PRODUCT_TYPES.find(([key]) => key === type)?.[1]
    || OPTICAL_EXTRA_PRODUCT_TYPES.find(([key]) => key === type)?.[1]
    || "Product";
}

function productNeedsPower(product) {
  return ["lens", "contactSolution", "eyeTest"].includes(product?.type) && product?.requiresPower !== false;
}

function productNeedsContactLens(product) {
  return product?.type === "contactSolution" && product?.requiresPower !== false;
}

function orderProductSearchText(product = {}) {
  return `${product.productId || ""} ${product.barcode || ""} ${product.name || ""}`.trim().toLowerCase();
}

function findMatchingCrmProduct(product = {}, crmProducts = []) {
  const barcode = String(product.barcode || "").trim().toLowerCase();
  if (product.productId) return crmProducts.find((row) => row.id === product.productId) || null;
  if (barcode) {
    const barcodeMatch = crmProducts.find((row) => String(row.barcode || "").trim().toLowerCase() === barcode);
    if (barcodeMatch) return barcodeMatch;
  }
  const name = String(product.name || "").trim().toLowerCase();
  if (!name) return null;
  return crmProducts.find((row) => String(row.productName || row.name || "").trim().toLowerCase() === name) || null;
}

function normalizeOrderProductForMaster(product = {}, crmProducts = []) {
  const matched = findMatchingCrmProduct(product, crmProducts);
  if (!matched) {
    return {
      ...product,
      productId: product.productId || "",
      productMasterStatus: "Product Master Pending",
      inventoryStatus: "Inventory Pending",
      itemStatus: product.itemStatus || ORDER_ITEM_STATUSES.vendorRequired
    };
  }
  return {
    ...product,
    productId: matched.id,
    name: product.name || matched.productName || matched.name || "",
    barcode: product.barcode || matched.barcode || "",
    productMasterStatus: "Product Matched",
    inventoryStatus: Number(matched.stockQty || 0) > 0 ? "Stock Available" : "Negative Inventory / Purchase Pending",
    itemStatus: product.itemStatus || (Number(matched.stockQty || 0) > 0 ? ORDER_ITEM_STATUSES.stock : ORDER_ITEM_STATUSES.vendorRequired)
  };
}

function simpleCsvRows(text) {
  return String(text || "")
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .map((line) => {
      const cells = [];
      let current = "";
      let quoted = false;
      for (const char of line) {
        if (char === "\"") quoted = !quoted;
        else if (char === "," && !quoted) {
          cells.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      cells.push(current.trim());
      return cells;
    });
}

function deriveOrderTypeFromProducts(products = []) {
  const types = new Set(products.map((product) => product.type));
  if (types.has("eyeTest") && types.size === 1) return "eye_test";
  if ((types.has("frame") || types.has("sunglasses")) && types.has("lens")) return "frame_lens";
  if (types.has("frame") || types.has("sunglasses")) return "frame_only";
  if (types.has("lens")) return "lens_only";
  if (types.has("contactSolution")) return "contact_solution";
  if (types.has("service")) return "service";
  return products.length ? "mixed" : "order";
}

function productsFromLegacyOrder(details = {}) {
  if (Array.isArray(details.products) && details.products.length) {
    return details.products.map((product, index) => ({
      ...product,
      barcode: product.barcode || (index === 0 ? details.barcode || "" : "")
    }));
  }
  const products = [];
  (details.opticalPairs || []).forEach((pair) => {
    if (pair.frameDetails || pair.frameStatus || pair.frameSource || pair.framePhoto) {
      products.push({
        ...emptyOpticalProduct("frame"),
        id: pair.id ? `${pair.id}-frame` : uuid("product"),
        name: pair.frameDetails || "",
        source: pair.frameSource || "store",
        status: pair.frameStatus || "ready",
        photo: pair.framePhoto || "",
        barcode: pair.frameBarcode || "",
        notes: pair.notes || ""
      });
    }
    if (pair.lensDetails || pair.lensStatus) {
      products.push({
        ...emptyOpticalProduct("lens"),
        id: pair.id ? `${pair.id}-lens` : uuid("product"),
        name: pair.lensDetails || "",
        status: pair.lensStatus || "ready",
        barcode: pair.lensBarcode || "",
        tintRequired: Boolean(pair.tintRequired),
        tintAsSample: Boolean(pair.tintAsSample),
        notes: pair.notes || ""
      });
    }
  });
  (details.extraProducts || []).forEach((product) => {
    products.push({
      ...emptyOpticalProduct(product.type === "contactSolution" ? "contactSolution" : product.type || "accessory"),
      ...product,
      id: product.id || uuid("product")
    });
  });
  if (!products.length && details.contactLensDetails) {
    products.push({ ...emptyOpticalProduct("contactSolution"), name: details.contactLensDetails });
  }
  if (!products.length && details.serviceDetails) {
    products.push({ ...emptyOpticalProduct("service"), name: details.serviceDetails });
  }
  if (!products.length) {
    const selected = details.selectedItems || itemsForOpticalOrderType(details.orderType || "frame_lens");
    selected.forEach((item) => {
      const type = item === "contactLens" || item === "solution" ? "contactSolution" : item;
      products.push(emptyOpticalProduct(type));
    });
  }
  return products;
}

function itemsForOpticalOrderType(orderType) {
  return {
    frame_only: ["frame"],
    lens_only: ["lens"],
    frame_lens: ["frame", "lens"],
    contact_solution: ["contactLens", "solution"],
    contact_lens: ["contactLens", "solution"],
    solution: ["contactLens", "solution"],
    service: ["service"]
  }[orderType] || [];
}

function labelForOpticalOrderType(orderType) {
  const legacyLabels = {
    contact_lens: "Contact lenses / Solution",
    solution: "Contact lenses / Solution",
    eye_test: "Eye Test",
    mixed: "Mixed products",
    order: "Order"
  };
  return legacyLabels[orderType] || OPTICAL_ORDER_TYPES.find(([key]) => key === orderType)?.[1] || "Order";
}

function emptyOpticalForm({ storeId = "", salespersonId = "", orderNumber = "" } = {}) {
  const today = new Date().toISOString().slice(0, 10);
  const delivery = new Date();
  delivery.setDate(delivery.getDate() + 3);
  return {
    orderNumber,
    date: today,
    deliveryDate: delivery.toISOString().slice(0, 10),
    storeId,
    salespersonId,
    customerId: "",
    phone: "",
    customerName: "",
    address: "",
    dob: "",
    email: "",
    orderType: "frame_only",
    selectedItems: ["frame"],
    noPhone: false,
    products: [emptyOpticalProduct("frame")],
    extraProducts: [],
    opticalPairs: [emptyOpticalPair()],
    frameDetails: "",
    frameSource: "store_frame",
    frameStatus: "ready",
    ownFramePhoto: "",
    lensDetails: "",
    lensStatus: "ready",
    serviceDetails: "",
    contactLensDetails: "",
    specialNotes: "",
    rightDistance: emptyOpticalPower(),
    leftDistance: emptyOpticalPower(),
    sameAddition: true,
    rightAdd: { sph: "+0.25", cyl: "", ax: "" },
    leftAdd: { sph: "+0.25", cyl: "", ax: "" },
    autoContactPower: false,
    contactRight: emptyOpticalPower(),
    contactLeft: emptyOpticalPower(),
    measurements: { pd: "", fh: "", a: "", b: "", dbl: "", dia: "" },
    prescribedBy: "",
    totalAmount: "",
    discountType: "amount",
    discountValue: "",
    advanceReceived: "",
    paymentMode: "UPI",
    splitPayments: { cash: "", upi: "", card: "", paytm: "" },
    paymentNotes: ""
  };
}

function opticalMoney(value) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(value || 0));
}

function opticalPowerLine(power = {}) {
  return `SPH ${power.sph || "-"} / CYL ${power.cyl || "-"} / AX ${power.ax || "-"}`;
}

function convertToContactLensPower(value) {
  if (!value || ["PL", "0.00"].includes(value)) return value || "";
  const number = Number(String(value).replace("+", ""));
  if (!Number.isFinite(number)) return value;
  const converted = number / (1 - 0.012 * number);
  const rounded = Math.round(converted * 4) / 4;
  return `${rounded >= 0 ? "+" : "-"}${Math.abs(rounded).toFixed(2)}`;
}

const IMAGE_UPLOAD_DEFAULTS = {
  attendance: { maxWidth: 800, quality: 0.64, targetMaxBytes: 500 * 1024 },
  document: { maxWidth: 1200, quality: 0.66, targetMaxBytes: 700 * 1024 }
};

async function uploadAppImage({
  file,
  dataUrl,
  module,
  recordType,
  recordId,
  storeId,
  staffId,
  fileType,
  uploadedBy,
  profile = "document"
}) {
  const options = IMAGE_UPLOAD_DEFAULTS[profile] || IMAGE_UPLOAD_DEFAULTS.document;
  return uploadCompressedImage({
    file,
    dataUrl,
    module,
    recordType,
    recordId,
    storeId,
    staffId,
    fileType,
    uploadedBy,
    ...options
  });
}

function useStoredFileUrl(src) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let active = true;
    setUrl("");
    if (!src) return () => {
      active = false;
    };
    getFileUrl(src)
      .then((fileUrl) => {
        if (active) setUrl(fileUrl);
      })
      .catch(() => {
        if (active) setUrl(String(src || ""));
      });
    return () => {
      active = false;
    };
  }, [src]);

  return url;
}

function isModuleEnabled(settings, key) {
  return settings?.enabledModules?.[key] !== false;
}

function targetProgress(target) {
  const targetAmount = Number(target?.targetAmount || 0);
  if (!targetAmount) return 0;
  return Math.round((Number(target?.achievedAmount || 0) / targetAmount) * 100);
}

function cumulativeTargetProgress(targets) {
  const targetAmount = targets.reduce((total, target) => total + Number(target?.targetAmount || 0), 0);
  if (!targetAmount) return 0;
  const achievedAmount = targets.reduce((total, target) => total + Number(target?.achievedAmount || 0), 0);
  return Math.round((achievedAmount / targetAmount) * 100);
}

function isTaskArchived(task, now = new Date()) {
  if (task?.status === "archived") return true;
  if (task?.status !== "completed" || !task.completedAt) return false;
  const completedAt = new Date(task.completedAt);
  if (Number.isNaN(completedAt.getTime())) return false;
  return now.getTime() - completedAt.getTime() >= TASK_ARCHIVE_DAYS * 24 * 60 * 60 * 1000;
}

function taskStatusLabel(task) {
  if (isTaskArchived(task)) return "Archived";
  if (task?.status === "completed") return "Completed";
  return "Pending";
}

function getBrandInitials(settings) {
  const words = getCompanyName(settings)
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
  return initials || "CO";
}

function filePrefix(settings) {
  const slug = getCompanyName(settings)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "attendance-payroll";
}

function BrandMark({ settings }) {
  const logo = settings?.companyLogo;
  const logoUrl = useStoredFileUrl(logo);
  return (
    <span className="brand-mark" aria-hidden="true">
      {logo && logoUrl ? <img src={logoUrl} alt="" /> : getBrandInitials(settings)}
    </span>
  );
}

function BrandLockup({ settings, subtitle, compact = false }) {
  return (
    <div className={`brand-lockup ${compact ? "compact" : ""}`}>
      <BrandMark settings={settings} />
      <div>
        <strong>{getCompanyName(settings)}</strong>
        <small>{subtitle || getAppName(settings)}</small>
      </div>
    </div>
  );
}

function normalizePhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function isPhoneLogin(value) {
  const raw = String(value || "").trim();
  return !raw.includes("@") && normalizePhone(raw).length >= 10;
}

function loginIdentifierToAuthEmail(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (isPhoneLogin(raw)) return `${normalizePhone(raw)}@${PHONE_LOGIN_DOMAIN}`;
  return raw;
}

function displayLoginId(value) {
  const raw = String(value || "");
  if (raw.endsWith(`@${PHONE_LOGIN_DOMAIN}`)) return `+${raw.replace(`@${PHONE_LOGIN_DOMAIN}`, "")}`;
  return raw;
}

function suggestedEmployeeLogin(employee) {
  if (!employee) return "";
  if (employee.phone) return employee.phone;
  if (employee.email) return employee.email;
  const slug = String(employee.fullName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.|\.$/g, "");
  return `${slug || "employee"}@company.in`;
}

function percent(value) {
  return `${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
}

function attendanceStatusClass(row) {
  if ([row?.punchIn, row?.punchOut].some((punch) => punch?.insideGeofence === false)) return "status-outside";
  const code = row?.summary?.code || row?.code || "";
  if (code === "P" || code === "P*") return "status-present";
  if (code === "HD") return "status-half";
  if (code === "A") return "status-absent";
  if (code === "M") return "status-missing";
  if (code === "WO") return "status-weekly-off";
  return "";
}

function attendanceKpiTone(summary) {
  if ([summary?.punchIn, summary?.punchOut].some((punch) => punch?.insideGeofence === false)) return "kpi-outside";
  if (summary?.code === "P" || summary?.code === "P*") return "kpi-present";
  if (summary?.code === "HD") return "kpi-early";
  if (summary?.code === "A") return "kpi-absent";
  if (summary?.code === "M") return "kpi-pending";
  if (summary?.code === "WO") return "kpi-approval";
  return "kpi-pending";
}

function attendanceValueForCode(code) {
  const normalized = code || "P";
  if (normalized === "P" || normalized === "P*") {
    return { code: normalized, status: normalized === "P*" ? "Present on weekly off" : "Present", presentUnits: 1, absentUnits: 0 };
  }
  if (normalized === "HD") return { code: "HD", status: "Half Day", presentUnits: 0.5, absentUnits: 0.5 };
  if (normalized === "L") return { code: "L", status: "Leave", presentUnits: 0, absentUnits: 1 };
  if (normalized === "EX") return { code: "EX", status: "Exception approved", presentUnits: 1, absentUnits: 0 };
  if (normalized === "M") return { code: "M", status: "Missing punch", presentUnits: 0, absentUnits: 1 };
  return { code: "A", status: "Absent", presentUnits: 0, absentUnits: 1 };
}

function attendanceOverrideFor(overrides, employeeId, date) {
  return [...(overrides || [])].reverse().find((item) => item.employeeId === employeeId && item.date === date);
}

function applyAttendanceOverride(summary, override) {
  if (!override) return summary;
  const correctedPunchIn = override.punchInTime
    ? {
        ...(summary.punchIn || {}),
        type: "in",
        timestamp: localDateTimeIso(summary.date, override.punchInTime),
        manualOverride: true
      }
    : summary.punchIn;
  const correctedPunchOut = override.punchOutTime
    ? {
        ...(summary.punchOut || {}),
        type: "out",
        timestamp: localDateTimeIso(summary.date, override.punchOutTime),
        manualOverride: true
      }
    : summary.punchOut;
  return {
    ...summary,
    punchIn: correctedPunchIn,
    punchOut: correctedPunchOut,
    code: override.code,
    status: override.status || "Admin corrected",
    presentUnits: Number(override.presentUnits ?? summary.presentUnits),
    absentUnits: Number(override.absentUnits ?? summary.absentUnits),
    correctionId: override.id
  };
}

function attendancePunchDate(punch) {
  return punch?.date || localDateOnly(punch?.timestamp || punch?.createdAt);
}

function attendanceShiftForEmployee(employee, settings = {}) {
  return {
    startTime: employee?.shiftStart || settings.defaultShiftStart,
    fullDayOutBy: employee?.fullDayOutBy || settings.fullDayOutBy,
    halfDayCutoff: employee?.halfDayCutoff || settings.halfDayCutoff,
    graceMinutes: employee?.graceMinutes ?? settings.graceMinutes
  };
}

function buildEmployeeAttendanceDay(state, employee, date, options = {}) {
  if (!employee || !date) return null;
  const today = localDateOnly();
  const punches = (state.punches || []).filter(
    (punch) => punch.employeeId === employee.id && attendancePunchDate(punch) === date
  );
  const baseSummary =
    options.futureAsUpcoming && date > today
      ? {
          date,
          punches,
          code: "-",
          status: "Upcoming",
          punchIn: null,
          punchOut: null,
          late: false,
          earlyDeparture: false,
          presentUnits: 0,
          absentUnits: 0,
          isWeeklyOffDate: false
        }
      : buildDailyAttendance({
          date,
          punches,
          weeklyOffDay: employee.weeklyOffDay,
          shift: attendanceShiftForEmployee(employee, state.settings)
        });
  const summary = applyAttendanceOverride(baseSummary, attendanceOverrideFor(state.attendanceOverrides, employee.id, date));
  return {
    id: `${date}-${employee.id}`,
    employee,
    store: (state.stores || []).find((store) => store.id === employee.storeId),
    punches,
    punchIn: summary.punchIn,
    punchOut: summary.punchOut,
    summary
  };
}

function localDateTimeIso(date, time) {
  if (!date || !time) return "";
  return new Date(`${date}T${time}:00`).toISOString();
}

function addDays(date, days) {
  const cursor = new Date(`${date}T00:00:00.000Z`);
  cursor.setUTCDate(cursor.getUTCDate() + Number(days || 0));
  return toDateOnly(cursor);
}

function leaveRangeDays(fromDate, toDate) {
  if (!fromDate || !toDate || toDate < fromDate) return 0;
  return eachDate(fromDate, toDate).length;
}

function groupLeaveApplications(rows) {
  const buckets = new Map();
  rows.forEach((row) => {
    const value = row.requestedValue || {};
    const key =
      value.leaveGroupId ||
      [
        row.employeeId,
        row.requestedBy || "",
        row.reason || "",
        value.leaveType || value.code || "",
        row.status || "",
        String(row.createdAt || "").slice(0, 16)
      ].join("|");
    if (!buckets.has(key)) {
      buckets.set(key, { ...row, id: key, children: [] });
    }
    buckets.get(key).children.push(row);
  });

  return [...buckets.values()].map((group) => {
    const dates = [...new Set(group.children.map((row) => row.date).filter(Boolean))].sort();
    const firstChild = group.children[0] || group;
    const value = firstChild.requestedValue || {};
    const fromDate = value.fromDate || dates[0] || firstChild.date;
    const toDate = value.toDate || dates.at(-1) || firstChild.date;
    const days = Number(value.days || dates.length || 1);
    return {
      ...firstChild,
      id: value.leaveGroupId || group.id,
      date: fromDate,
      requestedValue: {
        ...value,
        fromDate,
        toDate,
        days
      },
      children: group.children
    };
  }).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

function initials(name) {
  return String(name || "FB")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "FB";
}

function avatarTone(value) {
  const tones = ["tone-a", "tone-b", "tone-c", "tone-d", "tone-e"];
  const code = String(value || "")
    .split("")
    .reduce((total, char) => total + char.charCodeAt(0), 0);
  return tones[code % tones.length];
}

function formatCoordinates(latitude, longitude) {
  if (latitude == null || longitude == null) return "";
  return `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`;
}

function mapsLink(latitude, longitude) {
  if (latitude == null || longitude == null) return "";
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

function CoordinateLink({ latitude, longitude }) {
  const coordinates = formatCoordinates(latitude, longitude);
  const href = mapsLink(latitude, longitude);
  if (!coordinates || !href) return <>-</>;
  return (
    <a className="map-link" href={href} target="_blank" rel="noreferrer">
      {coordinates}
    </a>
  );
}

function parseCoordinates(value) {
  const match = String(value || "").match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function latestEffectiveRow(rows, dateKey = "effectiveDate") {
  return [...(rows || [])].sort((a, b) => String(b[dateKey] || "").localeCompare(String(a[dateKey] || "")))[0] || null;
}

function sheetValue(value) {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function downloadSheet(filename, rows, columns) {
  const csv = [
    columns.map(([label]) => label).join(","),
    ...rows.map((row) =>
      columns
        .map(([, render]) => {
          const value = sheetValue(render(row)).replace(/"/g, '""');
          return `"${value}"`;
        })
        .join(",")
    )
  ].join("\n");
  downloadBlob(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }), filename);
}

function pickCnCompatRecord(record) {
  const allowed = new Set([
    "id",
    "cnTicketNumber",
    "cnaNumber",
    "storeId",
    "previousOrderNo",
    "newOrderNo",
    "productDetails",
    "quantity",
    "reasonForReturn",
    "otherReasonText",
    "complaintNote",
    "oldItemPhoto",
    "createdBy",
    "createdByName",
    "createdDate",
    "cnResponsibleId",
    "status",
    "packetChecklist",
    "missingItemReason",
    "vendorName",
    "productNameAsPerDc",
    "dcNumber",
    "companyFormNo",
    "dispatchDate",
    "collectedBy",
    "amountExclusiveTax",
    "vendorFormPhoto",
    "cnCopyPhoto",
    "cnNumber",
    "cnDate",
    "cnAmount",
    "settlementAmount",
    "differenceAmount",
    "remarks",
    "ledgerUpdated",
    "closedAt",
    "createdAt",
    "updatedAt"
  ]);
  return Object.fromEntries(Object.entries(record || {}).filter(([key]) => allowed.has(key)));
}

async function persistOptionalEntity(tableName, record) {
  let syncError = null;
  try {
    await persistEntity(tableName, record);
    return { savedOnline: true };
  } catch (error) {
    syncError = error;
    const firstMessage = String(error?.message || "").toLowerCase();
    if (firstMessage.includes("schema cache")) {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      try {
        await persistEntity(tableName, record);
        return { savedOnline: true, retried: true };
      } catch (retryError) {
        syncError = retryError;
      }
    }
    const message = String(syncError?.message || "").toLowerCase();
    if (tableName === "cn_requests" && (message.includes("schema cache") || message.includes("column") || message.includes("does not exist"))) {
      try {
        await persistEntity(tableName, pickCnCompatRecord(record));
        return { savedOnline: true, compatibilityMode: true };
      } catch (fallbackError) {
        syncError = fallbackError;
      }
    }
    if (message.includes("does not exist") || message.includes("schema cache") || message.includes("relation")) {
      const subject = tableName === "cn_requests" ? "CN table" : "order table";
      return { savedOnline: false, reason: `Online ${subject} is not active yet. Supabase said: ${syncError?.message || "table/column not found"}` };
    }
    throw syncError;
  }
}

function requireOnlinePersistence(result, label = "Record") {
  if (result?.savedOnline === false) {
    throw new Error(`${label} was not saved online. ${result.reason || "Supabase rejected the save."} Run the latest complete order SQL / CN repair SQL in Supabase, refresh the app, and try again.`);
  }
  return result;
}

function useActionStatus() {
  const [actions, setActions] = useState({});

  const setAction = (key, patch) => {
    setActions((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }));
  };

  const runAction = async (key, labels, action, onError) => {
    const currentStatus = actions[key]?.status;
    if (currentStatus === "loading" || currentStatus === "success") return null;
    const loadingText = labels.loading || "Saving...";
    console.log(`[action:${key}] start`);
    setAction(key, { status: "loading", text: loadingText });
    try {
      const result = await action((stepText) => {
        if (stepText) setAction(key, { status: "loading", text: stepText });
      });
      console.log(`[action:${key}] success`, result || "");
      setAction(key, { status: "success", text: labels.success || "Saved ✓" });
      window.setTimeout(() => {
        setActions((current) => {
          if (current[key]?.status !== "success") return current;
          const next = { ...current };
          delete next[key];
          return next;
        });
      }, 1500);
      return result;
    } catch (error) {
      console.error(`[action:${key}] failed`, error);
      setAction(key, { status: "error", text: labels.error || "Try Again" });
      onError?.(error);
      return null;
    }
  };

  const actionState = (key) => actions[key] || { status: "idle", text: "" };

  return { actionState, runAction };
}

function ActionButton({ action, labels, onClick, className = "primary-button", disabled = false, children, ...props }) {
  const status = action?.status || "idle";
  const text =
    action?.text ||
    (status === "loading" ? labels?.loading : status === "success" ? labels?.success : status === "error" ? labels?.error : children);
  return (
    <button
      {...props}
      className={`${className} action-button-state ${status !== "idle" ? `is-${status}` : ""}`}
      type="button"
      disabled={disabled || status === "loading" || status === "success"}
      aria-busy={status === "loading"}
      onClick={onClick}
    >
      {text}
    </button>
  );
}

function useAppState() {
  const [state, setState] = useState(null);
  const [mode, setMode] = useState("local");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAppData()
      .then((result) => {
        setState(result.state);
        setMode(result.mode);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const updateState = (updater) => {
    setState((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      if (!hasSupabaseConfig) saveLocalState(next);
      return next;
    });
  };

  return { state, mode, loading, error, updateState };
}

function LoginScreen({ state, onLogin, mode, onBack }) {
  const [selectedUser, setSelectedUser] = useState(state.users[0]?.id || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [helpMessage, setHelpMessage] = useState("");
  const appName = getAppName(state.settings);

  const supabaseLogin = async () => {
    setError("");
    setHelpMessage("");
    const authEmail = loginIdentifierToAuthEmail(email);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email: authEmail, password });
    if (authError) {
      setError(authError.message);
      return;
    }

    try {
      await supabase.rpc("claim_user_profile_by_email");
    } catch {
      // Existing linked users can still be checked below.
    }

    const { data: profile, error: profileError } = await supabase
      .from("users")
      .select("*")
      .eq("auth_user_id", data.user.id)
      .maybeSingle();

    if (profileError || !profile) {
      await supabase.auth.signOut();
      setError("This login is not linked to any active company user yet.");
      return;
    }

    if (profile.active === false) {
      await supabase.auth.signOut();
      setError("This login has been deactivated. Please contact Super Admin.");
      return;
    }

    window.location.reload();
  };

  return (
    <main className="login-screen">
      <section className="login-panel">
        <BrandLockup settings={state.settings} subtitle={mode === "supabase" ? "Company app connected" : "Local setup mode"} />
        <span className="login-kicker">{appName}</span>
        <h1>Login</h1>
        <p>Use your email/phone and password. Admins open management tools; employees open Punch In / Punch Out.</p>
        {mode === "supabase" ? (
          <>
            <Input
              label="Email or phone"
              value={email}
              onChange={setEmail}
              placeholder="9876543210 or name@company.com"
            />
            <Input label="Password" type="password" value={password} onChange={setPassword} />
            {error ? <p className="error-text">{error}</p> : null}
            <div className="login-actions">
              <button className="primary-button" type="button" onClick={supabaseLogin}>
                Continue
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setHelpMessage("Ask Super Admin to reset your password from Users. Phone logins do not use OTP in this low-cost version.")}
              >
                Forgot password?
              </button>
            </div>
            {helpMessage ? <p className="notice">{helpMessage}</p> : null}
            <div className="login-help">
              <strong>Role based access</strong>
              <span>Employee logins see only their attendance and salary slip. Admin logins see only their permitted stores and tools.</span>
            </div>
            {onBack ? (
              <button className="secondary-button full-width" type="button" onClick={onBack}>
                Back to home
              </button>
            ) : null}
          </>
        ) : (
          <>
            <label>
              User
              <select value={selectedUser} onChange={(event) => setSelectedUser(event.target.value)}>
                {state.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} - {user.role.replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <button className="primary-button" type="button" onClick={() => onLogin(selectedUser)}>
              Continue
            </button>
          </>
        )}
      </section>
    </main>
  );
}

function PublicHome({ state, mode, updateState }) {
  const [view, setView] = useState("home");
  const [company, setCompany] = useState({
    companyName: "",
    appName: PRODUCT_NAME,
    companyLogo: ""
  });
  const [owner, setOwner] = useState({ name: "", loginId: "", password: "" });
  const [message, setMessage] = useState("");

  const addLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessage("Please select a logo image.");
      return;
    }
    try {
      const upload = await uploadAppImage({
        file,
        module: "company",
        recordType: "company_settings",
        recordId: company.companyName || "new-company",
        fileType: "other",
        profile: "document"
      });
      setCompany((current) => ({ ...current, companyLogo: upload.storagePath || upload.path || upload.url }));
      setMessage("Logo added.");
    } catch (error) {
      setMessage(error.message || "Logo could not be uploaded. You can add it later from Settings.");
    }
  };

  const createCompany = async () => {
    setMessage("Creating company...");
    try {
      if (!company.companyName || !owner.name || !owner.loginId || !owner.password) {
        throw new Error("Company name, owner name, owner login and password are required.");
      }
      const settings = { ...state.settings, ...company };
      const authEmail = loginIdentifierToAuthEmail(owner.loginId);
      const ownerEmployee = {
        id: uuid("emp"),
        employeeCode: "OWNER-001",
        fullName: owner.name,
        phone: "",
        email: authEmail,
        storeId: null,
        designation: "Owner / Super Admin",
        reportingManagerId: null,
        monthlySalary: 0,
        weeklyOffDay: "",
        joiningDate: new Date().toISOString().slice(0, 10),
        exitDate: null,
        active: true,
        openingLeaveBalance: 0,
        openingLoanBalance: 0,
        incentiveEligible: false,
        reimbursementEligible: true,
        bankDetails: "",
        notes: "Company owner profile."
      };
      const ownerUser = {
        id: uuid("user"),
        authUserId: null,
        name: owner.name,
        email: authEmail,
        role: "super_admin",
        employeeId: ownerEmployee.id,
        storeIds: [],
        permissions: { all: true },
        active: true
      };

      if (mode === "supabase") {
        const authResult = await supabase.auth.signUp({
          email: authEmail,
          password: owner.password,
          options: { data: { created_by_app: "staffpay_attendance" } }
        });
        if (authResult.error) throw authResult.error;
        if (authResult.data.user?.id) ownerUser.authUserId = authResult.data.user.id;
        await persistRequiredEntity("app_settings", { id: "global", settings }, "Company settings");
        await persistRequiredEntity("employees", ownerEmployee, "Owner employee");
        await persistRequiredEntity("users", ownerUser, "Owner login");
        setMessage("Company created. Please login with the owner ID.");
        setView("login");
        return;
      }

      updateState((current) => ({
        ...current,
        settings,
        employees: [ownerEmployee],
        users: [ownerUser],
        currentUserId: ownerUser.id
      }));
    } catch (error) {
      setMessage(error.message || "Company could not be created.");
    }
  };

  if (view === "login") {
    return <LoginScreen state={state} mode={mode} onLogin={(id) => updateState((current) => ({ ...current, currentUserId: id }))} onBack={() => setView("home")} />;
  }

  if (view === "setup") {
    return (
      <main className="public-home">
        <nav className="public-nav">
          <BrandLockup settings={{ ...state.settings, ...company }} subtitle={`${PRODUCT_NAME} HRM`} compact />
          <button className="secondary-button" type="button" onClick={() => setView("login")}>Login</button>
        </nav>
        <section className="setup-hero">
          <div>
            <span className="login-kicker">Try for free</span>
            <h1>Create your company workspace</h1>
            <p>Add company branding and create the first owner login. After that, the owner can add stores, employees, salaries, attendance rules and payroll.</p>
          </div>
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <div className="brand-settings-card">
              <BrandMark settings={{ ...state.settings, ...company }} />
              <div>
                <strong>{company.companyName || "Your company"}</strong>
                <span>{company.appName}</span>
              </div>
              <label className="logo-upload">
                Upload logo
                <input type="file" accept="image/*" onChange={(event) => addLogo(event.target.files?.[0])} />
              </label>
            </div>
            <div className="form-grid">
              <Input label="Company name" value={company.companyName} onChange={(companyName) => setCompany({ ...company, companyName })} />
              <Input label="App name" value={company.appName} onChange={(appName) => setCompany({ ...company, appName })} />
              <Input label="Owner / Super Admin name" value={owner.name} onChange={(name) => setOwner({ ...owner, name })} />
              <Input label="Owner login email or phone" value={owner.loginId} onChange={(loginId) => setOwner({ ...owner, loginId })} placeholder="9876543210 or owner@company.com" />
              <Input label="Owner password" type="password" value={owner.password} onChange={(password) => setOwner({ ...owner, password })} />
            </div>
            <button className="primary-button" type="button" onClick={createCompany}>Create company</button>
            {message ? <p className="notice">{message}</p> : null}
            <p className="helper-text">For selling to many companies, the clean low-cost setup is one Cloudflare + Supabase workspace per company. A shared multi-company database is a later security upgrade.</p>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="public-home">
      <nav className="public-nav">
        <BrandLockup settings={{ ...state.settings, companyName: PRODUCT_NAME, appName: "HRM" }} subtitle="HR, Attendance & Payroll" compact />
        <div className="public-nav-links">
          <a href="#features">Features</a>
          <a href="#customers">Customers</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="public-actions">
          <button className="secondary-button" type="button" onClick={() => setView("login")}>Log In</button>
          <button className="primary-button" type="button" onClick={() => setView("setup")}>Try for free</button>
        </div>
      </nav>
      <section className="public-hero">
        <span className="login-kicker">{PRODUCT_NAME} HRM</span>
        <h1>People power companies. We power people.</h1>
        <p>Simple attendance, live GPS punch, photo proof, payroll, salary slips and reports for small companies.</p>
        <div className="hero-actions">
          <button className="primary-button" type="button" onClick={() => setView("setup")}>Create company</button>
          <button className="secondary-button" type="button" onClick={() => setView("login")}>Login</button>
        </div>
      </section>
      <section className="public-feature-strip" id="features">
        {[
          ["Live attendance", "Camera, GPS and geofence proof."],
          ["Payroll", "Salary, deductions, reimbursements and slips."],
          ["Reports", "Download attendance, payroll and audit sheets."]
        ].map(([title, body]) => (
          <article className="panel" key={title}>
            <strong>{title}</strong>
            <span>{body}</span>
          </article>
        ))}
      </section>
    </main>
  );
}

function CameraCapture({ onCancel, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("Camera permission is required for attendance punch."));

    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setError("Camera is still starting. Try again in a moment.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0);
    const capturedAt = new Date();
    const stamp = `Captured ${capturedAt.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "medium",
      hour12: true
    })}`;
    const padding = Math.max(14, Math.round(canvas.width * 0.025));
    const fontSize = Math.max(18, Math.round(canvas.width * 0.035));
    context.font = `700 ${fontSize}px Arial`;
    const textWidth = context.measureText(stamp).width;
    const boxHeight = fontSize + padding * 1.6;
    context.fillStyle = "rgba(0, 0, 0, 0.62)";
    context.fillRect(0, canvas.height - boxHeight, Math.min(canvas.width, textWidth + padding * 2), boxHeight);
    context.fillStyle = "#ffffff";
    context.fillText(stamp, padding, canvas.height - padding);
    onCapture(canvas.toDataURL("image/jpeg", 0.86));
  };

  return (
    <div className="modal-backdrop">
      <section className="modal">
        <div className="modal-head">
          <h2>Live photo proof</h2>
          <button className="icon-only" type="button" onClick={onCancel} aria-label="Close camera">
            <XCircle size={20} />
          </button>
        </div>
        {error ? <p className="error-text">{error}</p> : <video ref={videoRef} autoPlay playsInline muted />}
        <div className="button-row">
          <button className="secondary-button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary-button" type="button" onClick={capture}>
            <Camera size={18} /> Capture photo
          </button>
        </div>
      </section>
    </div>
  );
}

function EmployeeDashboard({ state, user, updateState, onLogout }) {
  const employee = state.employees.find((item) => item.id === user.employeeId);
  const store = state.stores.find((item) => item.id === employee?.storeId);
  const companyName = getCompanyName(state.settings);
  const today = localDateOnly();
  const todayPunches = state.punches.filter((punch) => punch.employeeId === employee?.id && attendancePunchDate(punch) === today);
  const sortedTodayPunches = [...todayPunches].sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
  const punchIn = sortedTodayPunches.find((punch) => punch.type === "in");
  const punchOut = [...sortedTodayPunches].reverse().find((punch) => punch.type === "out");
  const hasIn = Boolean(punchIn);
  const hasOut = Boolean(punchOut);
  const [pendingType, setPendingType] = useState("");
  const [message, setMessage] = useState("");
  const [activeLayer, setActiveLayer] = useState("cockpit");
  const [cnFocus, setCnFocus] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedAttendanceMonth, setSelectedAttendanceMonth] = useState(localMonthOnly());
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(today);
  const [taskView, setTaskView] = useState("assigned");
  const [editingLeaveId, setEditingLeaveId] = useState("");
  const [editingCorrectionId, setEditingCorrectionId] = useState("");
  const [employeeTaskForm, setEmployeeTaskForm] = useState({
    title: "",
    description: "",
    assignedTo: "",
    dueDate: today,
    priority: "Normal"
  });
  const [correctionForm, setCorrectionForm] = useState({
    date: today,
    requestedCode: "P",
    reason: ""
  });
  const [leaveForm, setLeaveForm] = useState({
    fromDate: today,
    toDate: today,
    days: 1,
    leaveType: "full_day",
    reason: ""
  });

  const formatPunchTime = (punch) =>
    punch?.timestamp
      ? new Date(punch.timestamp).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit"
        })
      : "-";

  const formatDistance = (punch) =>
    punch?.distanceMeters == null ? "Not calculated" : `${Math.round(Number(punch.distanceMeters))} m`;

  const formatAccuracy = (punch) =>
    punch?.gpsAccuracy == null ? "-" : `${Math.round(Number(punch.gpsAccuracy))} m accuracy`;
  const formatLocationLabel = (punch) => {
    if (!punch) return "-";
    if (punch.manualOverride) return "Manual correction";
    if (punch.insideGeofence == null) return "Location not available";
    return punch.insideGeofence ? "Inside geofence" : "Outside geofence";
  };

  if (!employee) {
    return (
      <div className="employee-home">
        <div className="empty-state">
          This login is not linked to an employee yet. Ask Super Admin to create the employee email in Employees and
          Supabase Authentication.
        </div>
      </div>
    );
  }

  const savePunch = async (type, photoDataUrl) => {
    setMessage("Capturing GPS location...");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        const geofence = validateGeofence({ currentLocation, store, settings: state.settings });
        if (!geofence.allowed) {
          setPendingType("");
          setMessage(geofence.reason);
          return;
        }

        try {
          setMessage("Compressing and uploading attendance photo...");
          const photoPath = await uploadAttendancePhoto({
            dataUrl: photoDataUrl,
            employeeId: employee.id,
            storeId: employee.storeId,
            uploadedBy: user.id,
            type
          });
          const punch = {
            id: uuid("punch"),
            employeeId: employee.id,
            storeId: employee.storeId,
            date: today,
            type,
            timestamp: new Date().toISOString(),
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            gpsAccuracy: currentLocation.accuracy,
            distanceMeters: geofence.distanceMeters,
            insideGeofence: geofence.insideGeofence,
            exceptionRequired: geofence.exceptionRequired || false,
            photoPath,
            deviceInfo: navigator.userAgent
          };
          const savedPunch = await persistRequiredEntity("attendance_punches", punch, "Attendance punch");
          updateState((current) => ({ ...current, punches: [...current.punches, savedPunch] }));
          await notifySuperAdmins(state, updateState, {
            module: "Attendance",
            title: type === "in" ? "Employee Punched In" : "Employee Punched Out",
            message: `${employee.fullName} ${type === "in" ? "punched in" : "punched out"} at ${store?.name || "store"}${geofence.insideGeofence === false ? " outside geofence" : ""}.`,
            priority: geofence.insideGeofence === false ? "high" : "normal",
            entityType: "attendance_punch",
            entityId: savedPunch.id,
            storeId: employee.storeId,
            createdBy: user.id
          });
          setMessage(
            geofence.insideGeofence
              ? `${type === "in" ? "Punch in" : "Punch out"} saved inside geofence.`
              : `${type === "in" ? "Punch in" : "Punch out"} saved outside geofence for admin review.`
          );
        } catch (error) {
          setMessage(error.message);
        } finally {
          setPendingType("");
        }
      },
      () => {
        setPendingType("");
        setMessage("Location permission is required for attendance punch.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const ownPayroll = [...state.payrollRuns]
    .flatMap((run) => run.items || [])
    .filter((item) => item.employeeId === employee.id)
    .at(-1);
  const allActiveEmployees = (state.employees || []).filter((item) => item.active !== false);
  const employeeName = (id) => state.employees.find((item) => item.id === id)?.fullName || "Employee";
  const taskSenderName = (task) => {
    const sender = state.users.find((item) => item.id === task.assignedBy);
    return sender?.employeeId ? employeeName(sender.employeeId) : sender?.name || "User";
  };
  const ownAttendanceRows = state.punches.filter((punch) => punch.employeeId === employee.id).slice().reverse();
  const ownTasks = (state.workTasks || []).filter((task) => task.assignedTo === employee.id && !isTaskArchived(task));
  const sentTasks = (state.workTasks || []).filter((task) => task.assignedBy === user.id && !isTaskArchived(task));
  const ownTargets = (state.salesTargets || []).filter((target) => target.employeeId === employee.id);
  const todaySummary = buildEmployeeAttendanceDay(state, employee, today)?.summary || {};
  const todayPunchIn = todaySummary.punchIn || punchIn;
  const todayPunchOut = todaySummary.punchOut || punchOut;
  const currentMonth = localMonthOnly();
  const currentMonthRange = monthRange(currentMonth);
  const monthDates = eachDate(currentMonthRange.start, currentMonthRange.end)
    .filter((date) => date <= today && (!employee.joiningDate || date >= employee.joiningDate));
  const attendanceSummaries = monthDates.map((date) => buildEmployeeAttendanceDay(state, employee, date)?.summary).filter(Boolean);
  const lateCount = attendanceSummaries.filter((summary) => summary.late).length;
  const absentCount = attendanceSummaries.filter((summary) => summary.code === "A").length;
  const halfDayCount = attendanceSummaries.filter((summary) => summary.code === "HD").length;
  const missingCount = attendanceSummaries.filter((summary) => summary.code === "M").length;
  const outsideCount = state.punches.filter((punch) => punch.employeeId === employee.id && attendancePunchDate(punch).startsWith(currentMonth) && punch.insideGeofence === false).length;
  const attendanceScore = Math.max(0, Math.min(100, 100 - lateCount * 2 - halfDayCount * 8 - absentCount * 12 - missingCount * 5 - outsideCount * 5));
  const pendingTasks = ownTasks.filter((task) => task.status !== "completed");
  const openSentTasks = sentTasks.filter((task) => task.status !== "completed").length;
  const monthTargets = ownTargets.filter((target) => !target.month || target.month === currentMonth);
  const targetAverage = monthTargets.length
    ? Math.round(monthTargets.reduce((total, target) => total + targetProgress(target), 0) / monthTargets.length)
    : 0;
  const myLeaveApplications = groupLeaveApplications(
    state.corrections.filter((item) => item.employeeId === employee.id && item.requestedValue?.kind === "leave_application")
  );
  const pendingLeaveApplications = myLeaveApplications.filter((item) => item.status === "pending");
  const myCorrectionRequests = state.corrections
    .filter((item) => item.employeeId === employee.id && item.requestedValue?.kind !== "leave_application")
    .slice()
    .reverse();
  const pendingCorrectionRequests = myCorrectionRequests.filter((item) => item.status === "pending");
  const cockpitItems = [
    ["score", "Attendance score", attendanceScore, "kpi-present"],
    ["punch", todaySummary.status || "Mark attendance", todaySummary.code || (hasIn ? (hasOut ? "Done" : "Out") : "In"), attendanceKpiTone(todaySummary)],
    ["late", "Late comings", lateCount, "kpi-late"],
    ["absent", "Leave / absent", absentCount, "kpi-absent"],
    ["leave", "Leave applications", pendingLeaveApplications.length, "kpi-approval"],
    ["halfDay", "Half days", halfDayCount, "kpi-early"],
    ["tasks", "Tasks pending", pendingTasks.length, "kpi-approval"],
    ["sentTasks", "Tasks I sent", openSentTasks, "kpi-pending"],
    ...(isModuleEnabled(state.settings, "opticalOrders") ? [["optical", "Optical orders", (state.opticalFastSlips || []).filter((slip) => slip.salespersonId === employee.id && slip.date === today).length, "kpi-target"]] : []),
    ...(isModuleEnabled(state.settings, "salesTargets") ? [["targets", "Target achieved", `${targetAverage}%`, "kpi-target"]] : [])
  ];
  const ownAttendanceColumns = [
    ["Date", (punch) => punch.date],
    ["Time", (punch) => formatPunchTime(punch)],
    ["Type", (punch) => punch.type],
    ["Distance", (punch) => formatDistance(punch)],
    ["Location", (punch) => (punch.insideGeofence ? "Inside" : "Outside")],
    ["Photo", (punch) => (punch.photoPath ? "Saved" : "-")]
  ];
  const attendanceMonthRange = monthRange(selectedAttendanceMonth);
  const attendanceMonthDates = eachDate(attendanceMonthRange.start, attendanceMonthRange.end);
  const resolvedAttendanceDate = attendanceMonthDates.includes(selectedAttendanceDate)
    ? selectedAttendanceDate
    : attendanceMonthDates.includes(today)
      ? today
      : attendanceMonthDates[0];
  const getOwnDay = (date) => {
    const row = buildEmployeeAttendanceDay(state, employee, date, { futureAsUpcoming: true });
    const punches = row?.punches || [];
    const summary = row?.summary || {};
    return {
      date,
      punches,
      punchIn: summary.punchIn,
      punchOut: summary.punchOut,
      summary,
      outside: punches.some((punch) => punch.insideGeofence === false)
    };
  };
  const ownCalendarDays = attendanceMonthDates.map(getOwnDay);
  const selectedOwnDay = getOwnDay(resolvedAttendanceDate);
  const selectedDateTasks = (state.workTasks || []).filter((task) => {
    const completedDate = task.completedAt ? String(task.completedAt).slice(0, 10) : "";
    const isMine = (task.assignedTo === employee.id || task.assignedBy === user.id) && !isTaskArchived(task);
    return isMine && (task.dueDate === resolvedAttendanceDate || completedDate === resolvedAttendanceDate);
  });

  const saveEmployeeTask = async () => {
    setMessage("");
    try {
      const assignedTo = employeeTaskForm.assignedTo || allActiveEmployees[0]?.id || employee.id;
      if (!employeeTaskForm.title.trim()) throw new Error("Task title is required.");
      const row = {
        id: uuid("task"),
        ...employeeTaskForm,
        title: employeeTaskForm.title.trim(),
        assignedTo,
        assignedBy: user.id,
        status: "assigned",
        reviewStatus: "not_submitted",
        createdAt: new Date().toISOString()
      };
      const savedTask = await persistRequiredEntity("work_tasks", row, "Task");
      updateState((current) => ({ ...current, workTasks: [savedTask, ...(current.workTasks || [])] }));
      setEmployeeTaskForm({ title: "", description: "", assignedTo, dueDate: today, priority: "Normal" });
      setTaskView("sent");
      setMessage(`Task sent to ${employeeName(assignedTo)}.`);
    } catch (error) {
      setMessage(error.message || "Task could not be saved. Ask admin to run the Work module SQL once.");
    }
  };

  const completeTask = async (task) => {
    try {
      const updated = {
        ...task,
        status: "completed",
        completedAt: new Date().toISOString(),
        completedBy: employee.id,
        reviewStatus: "pending_review"
      };
      const savedTask = await persistRequiredEntity("work_tasks", updated, "Task");
      updateState((current) => ({
        ...current,
        workTasks: (current.workTasks || []).map((item) => (item.id === task.id ? savedTask : item))
      }));
      setMessage("Task marked complete. Manager can now review it in Work.");
    } catch (error) {
      setMessage(error.message || "Task could not be updated. Ask admin to run the Work module SQL once.");
    }
  };

  const submitOwnCorrection = async () => {
    setMessage("");
    try {
      if (!correctionForm.date || !correctionForm.reason.trim()) {
        throw new Error("Date and reason are required for correction request.");
      }
      const existingCorrection = editingCorrectionId
        ? state.corrections.find((item) => item.id === editingCorrectionId && item.status === "pending")
        : null;
      const correction = {
        ...(existingCorrection || {}),
        id: existingCorrection?.id || uuid("correction"),
        employeeId: employee.id,
        date: correctionForm.date,
        oldValue: existingCorrection?.oldValue || null,
        requestedValue: {
          code: correctionForm.requestedCode,
          status: "Correction requested",
          presentUnits: correctionForm.requestedCode === "P" ? 1 : correctionForm.requestedCode === "HD" ? 0.5 : 0,
          absentUnits: correctionForm.requestedCode === "P" ? 0 : correctionForm.requestedCode === "HD" ? 0.5 : 1
        },
        requestedBy: employee.fullName,
        reason: correctionForm.reason.trim(),
        status: "pending",
        createdAt: existingCorrection?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const savedCorrection = await persistRequiredEntity("attendance_corrections", correction, "Attendance correction");
      updateState((current) => ({
        ...current,
        corrections: existingCorrection
          ? (current.corrections || []).map((item) => (item.id === savedCorrection.id ? savedCorrection : item))
          : [savedCorrection, ...(current.corrections || [])]
      }));
      setCorrectionForm({ date: today, requestedCode: "P", reason: "" });
      setEditingCorrectionId("");
      setMessage(existingCorrection ? "Correction request updated." : "Correction request sent. Your manager / HR can review it.");
    } catch (error) {
      setMessage(error.message || "Correction request could not be saved.");
    }
  };

  const editCorrectionRequest = (item) => {
    if (item.status !== "pending") {
      setMessage("Only pending correction requests can be edited.");
      return;
    }
    setEditingCorrectionId(item.id);
    setCorrectionForm({
      date: item.date || today,
      requestedCode: item.requestedValue?.code || "P",
      reason: item.reason || ""
    });
    setActiveLayer("corrections");
    setMessage("Editing pending correction request.");
  };

  const cancelCorrectionRequest = async (item) => {
    if (item.status !== "pending") {
      setMessage("Only pending correction requests can be cancelled.");
      return;
    }
    try {
      const updated = { ...item, status: "cancelled", decidedAt: new Date().toISOString(), approvedBy: employee.fullName };
      const savedCorrection = await persistRequiredEntity("attendance_corrections", updated, "Attendance correction");
      updateState((current) => ({
        ...current,
        corrections: (current.corrections || []).map((row) => (row.id === item.id ? savedCorrection : row)),
        attendanceOverrides: (current.attendanceOverrides || []).filter((row) => row.id !== item.id)
      }));
      if (editingCorrectionId === item.id) {
        setEditingCorrectionId("");
        setCorrectionForm({ date: today, requestedCode: "P", reason: "" });
      }
      setMessage("Correction request cancelled.");
    } catch (error) {
      setMessage(error.message || "Correction request could not be cancelled.");
    }
  };

  const submitLeaveApplication = async () => {
    setMessage("");
    try {
      if (!leaveForm.fromDate || !leaveForm.toDate || !leaveForm.reason.trim()) {
        throw new Error("Leave dates and reason are required.");
      }
      if (leaveForm.toDate < leaveForm.fromDate) {
        throw new Error("Leave end date cannot be before start date.");
      }
      const leaveDates = eachDate(leaveForm.fromDate, leaveForm.toDate);
      if (!leaveDates.length) throw new Error("Please select at least one leave day.");
      const editingLeave = editingLeaveId ? myLeaveApplications.find((item) => item.id === editingLeaveId) : null;
      const editingLeaveChildIds = new Set((editingLeave?.children || []).map((item) => item.id));
      const leaveDateSet = new Set(leaveDates);
      const hasPendingOverlap = state.corrections.some(
        (item) =>
          item.employeeId === employee.id &&
          item.status === "pending" &&
          !editingLeaveChildIds.has(item.id) &&
          item.requestedValue?.kind === "leave_application" &&
          leaveDateSet.has(item.date)
      );
      if (hasPendingOverlap) {
        throw new Error("A pending leave application already exists for one or more selected dates.");
      }
      const leaveGroupId = uuid("leave-group");
      const createdAt = new Date().toISOString();
      const requestedValue = leaveForm.leaveType === "half_day"
        ? {
            ...attendanceValueForCode("HD"),
            kind: "leave_application",
            leaveType: "Half day leave",
            leaveGroupId,
            fromDate: leaveForm.fromDate,
            toDate: leaveForm.toDate,
            days: leaveDates.length
          }
        : {
            ...attendanceValueForCode("L"),
            kind: "leave_application",
            leaveType: "Full day leave",
            leaveGroupId,
            fromDate: leaveForm.fromDate,
            toDate: leaveForm.toDate,
            days: leaveDates.length
          };
      const rows = leaveDates.map((date) => ({
        id: uuid("leave"),
        employeeId: employee.id,
        date,
        oldValue: null,
        requestedValue,
        requestedBy: employee.fullName,
        reason: leaveForm.reason.trim(),
        status: "pending",
        createdAt
      }));
      const cancelledRows = editingLeave
        ? (editingLeave.children || [editingLeave]).map((row) => ({
            ...row,
            status: "cancelled",
            decidedAt: createdAt,
            approvedBy: employee.fullName,
            updatedAt: createdAt
          }))
        : [];
      const savedCancelledRows = [];
      for (const row of cancelledRows) {
        savedCancelledRows.push(await persistRequiredEntity("attendance_corrections", row, "Leave application"));
      }
      const savedRows = [];
      for (const row of rows) {
        savedRows.push(await persistRequiredEntity("attendance_corrections", row, "Leave application"));
      }
      const cancelledById = new Map(savedCancelledRows.map((row) => [row.id, row]));
      updateState((current) => ({
        ...current,
        corrections: [
          ...savedRows,
          ...(current.corrections || []).map((item) => cancelledById.get(item.id) || item)
        ],
        attendanceOverrides: (current.attendanceOverrides || []).filter((item) => !cancelledById.has(item.id))
      }));
      setLeaveForm({ fromDate: today, toDate: today, days: 1, leaveType: "full_day", reason: "" });
      setEditingLeaveId("");
      setMessage(`${editingLeave ? "Leave application updated" : "Leave application sent"} as one request for ${savedRows.length} day${savedRows.length === 1 ? "" : "s"}.`);
    } catch (error) {
      setMessage(error.message || "Leave application could not be sent.");
    }
  };

  const editLeaveApplication = (item) => {
    if (item.status !== "pending") {
      setMessage("Only pending leave applications can be edited.");
      return;
    }
    const fromDate = item.requestedValue?.fromDate || item.date || today;
    const toDate = item.requestedValue?.toDate || item.date || today;
    setEditingLeaveId(item.id);
    setLeaveForm({
      fromDate,
      toDate,
      days: leaveRangeDays(fromDate, toDate) || item.requestedValue?.days || 1,
      leaveType: String(item.requestedValue?.leaveType || "").toLowerCase().includes("half") ? "half_day" : "full_day",
      reason: item.reason || ""
    });
    setActiveLayer("leave");
    setMessage("Editing pending leave application.");
  };

  const cancelLeaveApplication = async (item) => {
    if (item.status !== "pending") {
      setMessage("Only pending leave applications can be cancelled.");
      return;
    }
    try {
      const rows = item.children?.length ? item.children : [item];
      const cancelledAt = new Date().toISOString();
      const updatedRows = rows.map((row) => ({
        ...row,
        status: "cancelled",
        decidedAt: cancelledAt,
        approvedBy: employee.fullName,
        updatedAt: cancelledAt
      }));
      const savedRows = [];
      for (const row of updatedRows) {
        savedRows.push(await persistRequiredEntity("attendance_corrections", row, "Leave application"));
      }
      const updatedById = new Map(savedRows.map((row) => [row.id, row]));
      updateState((current) => ({
        ...current,
        corrections: (current.corrections || []).map((row) => updatedById.get(row.id) || row),
        attendanceOverrides: (current.attendanceOverrides || []).filter((row) => !updatedById.has(row.id))
      }));
      if (editingLeaveId === item.id) {
        setEditingLeaveId("");
        setLeaveForm({ fromDate: today, toDate: today, days: 1, leaveType: "full_day", reason: "" });
      }
      setMessage("Leave application cancelled.");
    } catch (error) {
      setMessage(error.message || "Leave application could not be cancelled.");
    }
  };

  const createEmployeeOrderV2Id = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  };

  const saveEmployeeNewOrderV2 = async (draft = {}) => {
    const orderDate = new Date().toISOString().slice(0, 10);
    const now = new Date().toISOString();
    const assignedStore = store || (state.stores || []).find((row) => row.id === employee?.storeId) || (state.stores || [])[0];
    if (!employee?.id) throw new Error("Employee profile is required before saving order.");
    if (!assignedStore?.id) throw new Error("Store is required before saving order.");
    const cleanPhone = String(draft.customerMobile || "").trim();
    const customerName = String(draft.customerName || "").trim();
    if (!customerName) throw new Error("Customer name is required before saving.");

    const orderProducts = (draft.products || [])
      .filter((product) => product.type || product.name || Number(product.amount || 0) > 0)
      .map((product, index) => {
        const mappedType = product.type === "sunglass"
          ? "sunglasses"
          : ["contact_lens", "solution"].includes(product.type)
            ? "contactSolution"
            : product.type === "accessory"
              ? "service"
              : product.type || "service";
        return {
          id: createEmployeeOrderV2Id(),
          type: mappedType,
          productId: "",
          barcode: "",
          name: String(product.name || opticalProductLabel(mappedType) || `Product ${index + 1}`).trim(),
          quantity: Number(product.quantity || 1),
          amount: Number(product.amount || 0),
          source: product.source || "from_store",
          status: "ready",
          photo: "",
          requiresPower: ["lens", "contactSolution"].includes(mappedType),
          tintRequired: false,
          tintAsSample: false,
          notes: product.note || product.notes || ""
        };
      });
    if (!orderProducts.length) throw new Error("Add at least one product before saving.");

    const subtotalAmount = Number(draft.subtotal || orderProducts.reduce((sum, product) => sum + Number(product.amount || 0), 0));
    const discountType = draft.discountType || "amount";
    const rawDiscountValue = Number(draft.discountValue || 0);
    const discountAmount = Number(draft.discount ?? (discountType === "percent" ? Math.round((subtotalAmount * rawDiscountValue) / 100) : rawDiscountValue));
    const totalAmount = Math.max(0, Number(draft.netTotal ?? (subtotalAmount - discountAmount)));
    const splitPayments = (draft.payments || [])
      .filter((payment) => Number(payment.amount || 0) > 0)
      .map((payment) => ({
        id: createEmployeeOrderV2Id(),
        amount: Number(payment.amount || 0),
        mode: payment.mode || "Cash",
        ref: payment.ref || payment.note || "",
        receivedAt: normalizeLocalDateTime(payment.receivedAt, now),
        receivedBy: employee.fullName || user.name || user.email || ""
      }));
    const advanceReceived = splitPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    if (advanceReceived > totalAmount) throw new Error("Paid amount cannot be more than order total.");
    const balance = Math.max(0, totalAmount - advanceReceived);
    const paymentMode = splitPayments.length > 1 ? "Split" : splitPayments[0]?.mode || "Cash";
    const orderNumber = fallbackOpticalOrderNumber();
    const deliveryDate = dateOffset(3);
    const walkInId = cleanPhone ? "" : `WALKIN-${orderDate.replaceAll("-", "")}-${String((state.opticalFastSlips || []).length + 1).padStart(3, "0")}`;
    const selectedCustomer = draft.selectedCustomerId
      ? (state.opticalCustomers || []).find((customer) => customer.id === draft.selectedCustomerId)
      : null;
    const existingCustomer = selectedCustomer || (cleanPhone ? (state.opticalCustomers || []).find((customer) => customer.phone === cleanPhone) : null);
    const customer = {
      ...(existingCustomer || {}),
      id: existingCustomer?.id || createEmployeeOrderV2Id(),
      phone: cleanPhone,
      noPhone: !cleanPhone,
      walkInId,
      name: customerName,
      address: existingCustomer?.address || "",
      dob: existingCustomer?.dob || null,
      email: existingCustomer?.email || "",
      storeId: assignedStore.id,
      createdAt: existingCustomer?.createdAt || now,
      updatedAt: now
    };
    const slipId = createEmployeeOrderV2Id();
    const frameProducts = orderProducts.filter((product) => ["frame", "sunglasses"].includes(product.type));
    const lensProducts = orderProducts.filter((product) => product.type === "lens");
    const productPairCount = Math.max(frameProducts.length, lensProducts.length);
    const productPairs = Array.from({ length: productPairCount }).map((_, index) => ({
      id: createEmployeeOrderV2Id(),
      frameDetails: frameProducts[index]?.name || "",
      frameSource: frameProducts[index]?.source || "from_store",
      frameStatus: frameProducts[index]?.status || "ready",
      framePhoto: "",
      lensDetails: lensProducts[index]?.name || "",
      lensStatus: lensProducts[index]?.status || "ready",
      tintRequired: false,
      tintAsSample: false,
      tintSamplePhoto: "",
      notes: [frameProducts[index]?.notes, lensProducts[index]?.notes].filter(Boolean).join(" | ")
    }));
    const pairSummary = orderProducts
      .map((product, index) => `${index + 1}. ${opticalProductLabel(product.type)} - ${product.name || "Item"}`)
      .join(" | ");
    const contactSummary = orderProducts.filter((product) => product.type === "contactSolution").map((product) => product.name || product.notes).filter(Boolean).join(" | ");
    const serviceSummary = orderProducts.filter((product) => ["service", "eyeTest"].includes(product.type)).map((product) => product.name || product.notes).filter(Boolean).join(" | ");
    const savedCustomer = await persistRequiredEntity("optical_customers", customer, "Customer");
    const orderRecord = {
      id: slipId,
      orderNumber,
      gcNumber: orderNumber,
      storeId: assignedStore.id,
      storeName: assignedStore.name || "",
      storePhone: assignedStore.phone || "",
      salespersonId: employee.id,
      salespersonName: employee.fullName || user.name || user.email || "",
      salespersonDesignation: employee.designation || user.designation || "",
      date: orderDate,
      deliveryDate,
      customerId: savedCustomer.id,
      customer: savedCustomer,
      orderDetails: {
        orderType: deriveOrderTypeFromProducts(orderProducts),
        selectedItems: [...new Set(orderProducts.map((product) => product.type))],
        products: orderProducts,
        extraProducts: orderProducts,
        opticalPairs: productPairs,
        frameDetails: frameProducts.map((product) => product.name).filter(Boolean).join(" | "),
        frameSource: frameProducts[0]?.source || "from_store",
        frameStatus: frameProducts[0]?.status || "ready",
        ownFramePhoto: "",
        lensDetails: lensProducts.map((product) => product.name).filter(Boolean).join(" | "),
        lensStatus: lensProducts[0]?.status || "ready",
        contactLensDetails: contactSummary,
        serviceDetails: serviceSummary,
        specialNotes: [draft.notes?.internal, draft.notes?.product, draft.notes?.delivery, draft.notes?.print, draft.notes?.customer].filter(Boolean).join(" | "),
        notes: draft.notes || {},
        pairSummary,
        editLog: [],
        managerWorkflow: {
          status: ORDER_MANAGER_STATUSES.new,
          reviewedAt: "",
          reviewedBy: "",
          vendorName: "",
          vendorOrderReference: "",
          orderSentMethod: ""
        },
        orderTimeline: [
          orderTimelineEvent({
            eventType: "Order created",
            oldStatus: "",
            newStatus: ORDER_MANAGER_STATUSES.new,
            note: "Customer order created from NewOrderV2.",
            user
          })
        ],
        lastEditedAt: null,
        lastEditedBy: null
      },
      power: {
        rightEye: { distance: {}, add: {} },
        leftEye: { distance: {}, add: {} },
        contactLens: { autoConverted: false, rightEye: {}, leftEye: {} },
        measurements: {},
        prescribedBy: ""
      },
      payment: {
        subtotalAmount,
        discountType,
        discountValue: rawDiscountValue,
        discountAmount,
        totalAmount,
        advanceReceived,
        balance,
        paymentMode,
        splitPayments,
        notes: "",
        status: balance > 0 ? "Pending" : "Paid"
      },
      generatedViews: { customerSlip: true, internalStoreSlip: true, labLensCompanySlip: true },
      createdAt: now,
      updatedAt: now
    };
    const savedOrderRecord = await persistOpticalOrderWithUniqueNumber(orderRecord);
    const finalOrderNumber = savedOrderRecord.orderNumber || orderNumber;
    const dailyEntry = {
      id: createEmployeeOrderV2Id(),
      slipId,
      orderNumber: finalOrderNumber,
      date: orderDate,
      customerName: savedCustomer.name,
      phone: savedCustomer.phone,
      subtotalAmount,
      discountAmount,
      totalAmount,
      advanceReceived,
      balance,
      paymentMode,
      deliveryDate,
      deliveryStatus: OPTICAL_DELIVERY_STATUS,
      storeId: assignedStore.id,
      salespersonId: employee.id,
      createdAt: now,
      updatedAt: now
    };
    const deliveryEntry = {
      id: createEmployeeOrderV2Id(),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      deliveryDate,
      status: OPTICAL_DELIVERY_STATUS,
      storeId: assignedStore.id,
      salespersonId: employee.id,
      createdAt: now,
      updatedAt: now
    };
    const lensOrderEntry = {
      id: createEmployeeOrderV2Id(),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      lensDetails: pairSummary,
      contactLensDetails: contactSummary,
      power: savedOrderRecord.power || orderRecord.power,
      vendor: "",
      vendorOrderNumber: "",
      status: OPTICAL_LENS_ORDER_STATUS,
      storeId: assignedStore.id,
      salespersonId: employee.id,
      createdAt: now,
      updatedAt: now
    };
    const pendingPaymentEntry = balance > 0 ? {
      id: createEmployeeOrderV2Id(),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      phone: savedCustomer.phone,
      balance,
      subtotalAmount,
      discountAmount,
      totalAmount,
      advanceReceived,
      status: "Pending",
      storeId: assignedStore.id,
      salespersonId: employee.id,
      createdAt: now,
      updatedAt: now
    } : null;
    const [savedDailyEntry, savedDeliveryEntry, savedLensOrderEntry, savedPendingPaymentEntry] = await Promise.all([
      persistRequiredEntity("optical_daily_register_entries", dailyEntry, "Daily register"),
      persistRequiredEntity("optical_delivery_tracking_entries", deliveryEntry, "Delivery tracking"),
      persistRequiredEntity("optical_lens_order_entries", lensOrderEntry, "Lens order"),
      pendingPaymentEntry ? persistRequiredEntity("optical_pending_payment_entries", pendingPaymentEntry, "Pending payment") : Promise.resolve(null)
    ]);
    const log = createAuditLog({
      entityType: "optical_fast_slip",
      entityId: savedOrderRecord.id,
      action: "created",
      oldValue: null,
      newValue: savedOrderRecord,
      reason: "NewOrderV2 Order Form created",
      effectiveDate: orderDate,
      user
    });
    await persistAuditLog(log).catch(() => null);
    updateState((current) => ({
      ...current,
      opticalCustomers: upsertById(current.opticalCustomers || [], savedCustomer),
      opticalFastSlips: upsertById(current.opticalFastSlips || [], savedOrderRecord),
      opticalDailyRegisterEntries: upsertById(current.opticalDailyRegisterEntries || [], savedDailyEntry),
      opticalDeliveryTrackingEntries: upsertById(current.opticalDeliveryTrackingEntries || [], savedDeliveryEntry),
      opticalLensOrderEntries: upsertById(current.opticalLensOrderEntries || [], savedLensOrderEntry),
      opticalPendingPaymentEntries: savedPendingPaymentEntry
        ? upsertById(current.opticalPendingPaymentEntries || [], savedPendingPaymentEntry)
        : (current.opticalPendingPaymentEntries || []),
      auditLogs: [log, ...(current.auditLogs || [])]
    }));
    await notifySuperAdmins(state, updateState, {
      module: "Orders",
      title: "New Order Created",
      message: `Order ${savedOrderRecord.orderNumber} was created at ${savedOrderRecord.storeName || assignedStore.name || "store"}.`,
      priority: "normal",
      entityType: "optical_fast_slip",
      entityId: savedOrderRecord.id,
      storeId: savedOrderRecord.storeId,
      createdBy: user.id
    });
    if (savedPendingPaymentEntry) {
      await notifySuperAdmins(state, updateState, {
        module: "Payments",
        title: "Pending Payment Created",
        message: `${currency(savedPendingPaymentEntry.balance)} pending for order ${savedOrderRecord.orderNumber}.`,
        priority: "normal",
        entityType: "optical_pending_payment",
        entityId: savedPendingPaymentEntry.id,
        storeId: savedOrderRecord.storeId,
        createdBy: user.id
      });
    }
    return savedOrderRecord;
  };

  const employeeNewOrderV2Orders = (state.opticalFastSlips || [])
    .filter((order) => order.salespersonId === employee?.id || order.storeId === employee?.storeId)
    .sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));

  const collectEmployeeNewOrderV2Balance = (order, draft = {}) =>
    persistCustomerOrderBalanceCollection({
      state,
      updateState,
      order,
      rows: draft.rows || [],
      receivedBy: draft.receivedBy || employee?.fullName || user.name || user.email,
      user
    });

  const markEmployeeNewOrderV2Delivered = (order) =>
    persistCustomerOrderDelivered({ state, updateState, order, user });

  const markEmployeeNotificationRead = async (notification) => {
    const updated = { ...notification, isRead: true, readAt: notification.readAt || new Date().toISOString() };
    await persistOptionalEntity("notifications", updated).catch(() => null);
    updateState((current) => ({
      ...current,
      notifications: (current.notifications || []).map((row) => (row.id === notification.id ? updated : row))
    }));
  };

  const markAllEmployeeNotificationsRead = async () => {
    const now = new Date().toISOString();
    const unread = (state.notifications || []).filter((notification) => notification.userId === user.id && !notification.isRead);
    const updatedRows = unread.map((notification) => ({ ...notification, isRead: true, readAt: notification.readAt || now }));
    await Promise.all(updatedRows.map((notification) => persistOptionalEntity("notifications", notification).catch((error) => console.error("Notification mark-read failed", error))));
    updateState((current) => ({
      ...current,
      notifications: (current.notifications || []).map((notification) => updatedRows.find((row) => row.id === notification.id) || notification)
    }));
  };

  const openEmployeeNotification = async (notification) => {
    await markEmployeeNotificationRead(notification);
    if (notification.module === "CN Tracking") {
      setActiveLayer("cnTracking");
      setCnFocus({ requestId: notification.linkedRecordId });
    }
  };

  return (
    <section className="employee-home">
      <div className="employee-app-top">
        <button className="mobile-menu-button employee-menu-button" type="button" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
          <Menu size={22} />
        </button>
        <BrandMark settings={state.settings} />
        <div>
          <span>{companyName}</span>
          <strong>{activeLayer === "cockpit" ? "My Cockpit" : employee.fullName}</strong>
        </div>
        {activeLayer !== "cockpit" ? (
          <button
            className="mobile-cockpit-button"
            type="button"
            onClick={() => {
              setActiveLayer("cockpit");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <ShieldCheck size={16} />
            Cockpit
          </button>
        ) : null}
        <NotificationBell
          notifications={state.notifications || []}
          user={user}
          onOpen={openEmployeeNotification}
          onMarkRead={markEmployeeNotificationRead}
          onMarkAllRead={markAllEmployeeNotificationsRead}
        />
      </div>
      {menuOpen ? (
        <div className="mobile-menu-layer employee-menu-layer" role="dialog" aria-modal="true">
          <button className="mobile-menu-scrim employee-menu-scrim" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
          <aside className="mobile-menu-panel employee-menu-panel">
            <div className="mobile-menu-head">
              <BrandLockup settings={state.settings} subtitle={employee.designation || "Employee"} compact />
              <button className="icon-only" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
                <XCircle size={18} />
              </button>
            </div>
            <nav className="mobile-menu-nav" aria-label="Employee screens">
              {[
                ["cockpit", "My Cockpit", ShieldCheck],
                ["punch", "Punch In / Out", Camera],
                ...(isModuleEnabled(state.settings, "opticalOrders") ? [["newOrderV2", "New Order", FileText]] : []),
                ...(isModuleEnabled(state.settings, "opticalOrders") ? [["optical", "Optical Orders", FileText]] : []),
                ["internalChatV1", "Internal Chat", UsersRound],
                ["tasks", "My Tasks", ClipboardList],
                ["targets", "My Targets", Target],
                ["attendance", "My Attendance", CalendarDays],
                ["leave", "Leave", CalendarDays],
                ["corrections", "Corrections", CheckCircle2],
                ["salary", "Salary Slip", FileSpreadsheet]
              ].map(([key, label, Icon]) => (
                <button
                  key={key}
                  className={activeLayer === key ? "active" : ""}
                  type="button"
                  onClick={() => {
                    setActiveLayer(key);
                    setMenuOpen(false);
                  }}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
            <div className="mobile-menu-actions">
              <InstallAppButton />
              <button className="secondary-button" type="button" onClick={onLogout}>
                <LogOut size={16} /> Logout
              </button>
            </div>
          </aside>
        </div>
      ) : null}
      {activeLayer === "cockpit" ? (
        <>
          <div className="employee-profile-strip">
            <i className={`row-avatar ${avatarTone(employee.fullName)}`}>{initials(employee.fullName)}</i>
            <div>
              <span className="status-chip">{employee.designation || "Employee"}</span>
              <strong>{employee.fullName}</strong>
              <small>{store?.name || "No store assigned"}</small>
            </div>
          </div>
          <div className="kpi-grid employee-cockpit-grid">
            {cockpitItems.map(([key, label, value, tone]) => (
              <Kpi
                key={key}
                label={label}
                value={value}
                tone={tone}
                active={activeLayer === key}
                onClick={() => {
          if (["score", "late", "absent", "halfDay"].includes(key)) {
            setActiveLayer("attendance");
            return;
          }
                  if (key === "sentTasks") {
                    setTaskView("sent");
                    setActiveLayer("tasks");
                    return;
                  }
                  if (key === "tasks") setTaskView("assigned");
                  setActiveLayer(key);
                }}
              />
            ))}
          </div>
          <p className="helper-text employee-score-note">
            Score uses this month only. Grace time is included, so arrival inside grace is not counted as late.
          </p>
        </>
      ) : null}
      {activeLayer === "punch" ? (
      <div className="employee-card">
        <div className="employee-summary-head">
          <i className={`row-avatar ${avatarTone(employee.fullName)}`}>{initials(employee.fullName)}</i>
          <div>
            <span className="status-chip">Employee attendance</span>
            <h1>{employee.fullName}</h1>
            <p>{store?.name || "No store assigned"}</p>
          </div>
        </div>
        <div className="today-line">
          <CalendarDays size={17} />
          <span>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short" })}</span>
        </div>
        <div className="punch-status">
          <strong>Today status: {todaySummary.status} ({todaySummary.code})</strong>
          <strong>{todayPunchIn ? `Punch-in: ${formatPunchTime(todayPunchIn)}` : "Waiting for punch-in"}</strong>
          <strong>{todayPunchOut ? `Punch-out: ${formatPunchTime(todayPunchOut)}` : "Punch-out pending"}</strong>
        </div>
        <div className="punch-grid">
          <button className="punch-button in" type="button" disabled={hasIn} onClick={() => setPendingType("in")}>
            <Camera size={26} /> Punch In
          </button>
          <button className="punch-button out" type="button" disabled={!hasIn || hasOut} onClick={() => setPendingType("out")}>
            <Camera size={26} /> Punch Out
          </button>
        </div>
        <div className="punch-detail-grid">
          <article>
            <span>Punch-in location</span>
            <strong>{formatLocationLabel(todayPunchIn)}</strong>
            <small>{todayPunchIn ? `${formatDistance(todayPunchIn)} · ${formatAccuracy(todayPunchIn)}` : "Location will show after punch-in"}</small>
          </article>
          <article>
            <span>Punch-in photo</span>
            <strong>{todayPunchIn?.photoPath ? "Photo saved" : "-"}</strong>
            <small>{todayPunchIn?.timestamp ? new Date(todayPunchIn.timestamp).toLocaleString("en-IN") : "Live camera photo required"}</small>
          </article>
          <article>
            <span>Punch-out location</span>
            <strong>{formatLocationLabel(todayPunchOut)}</strong>
            <small>{todayPunchOut ? `${formatDistance(todayPunchOut)} · ${formatAccuracy(todayPunchOut)}` : "Pending"}</small>
          </article>
          <article>
            <span>Punch-out photo</span>
            <strong>{todayPunchOut?.photoPath ? "Photo saved" : "-"}</strong>
            <small>{todayPunchOut?.timestamp ? new Date(todayPunchOut.timestamp).toLocaleString("en-IN") : "Pending"}</small>
          </article>
        </div>
        {message ? <p className="notice">{message}</p> : null}
      </div>
      ) : null}

      {activeLayer === "optical" ? (
        <OpticalOrdersPanel state={state} updateState={updateState} employees={[employee]} user={user} employeeMode />
      ) : null}

      {activeLayer === "newOrderV2" ? (
        <NewOrderV2
          customers={state.opticalCustomers || []}
          orders={employeeNewOrderV2Orders}
          onSaveOrder={saveEmployeeNewOrderV2}
          onMarkDelivered={markEmployeeNewOrderV2Delivered}
        />
      ) : null}

      {activeLayer === "cnTracking" ? (
        <CnTrackingPanel state={state} updateState={updateState} user={user} focus={cnFocus} onFocusHandled={() => setCnFocus(null)} />
      ) : null}

      {activeLayer === "internalChatV1" ? (
        <InternalChatV1
          employees={buildInternalChatEmployees(state, user)}
          users={state.users || []}
          stores={state.stores || []}
          user={user}
          currentEmployee={employee}
        />
      ) : null}

      {activeLayer === "tasks" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel">
          <div className="panel-title-row">
            <div>
              <h2>Send Task</h2>
              <p>Assign work to anyone, including your manager or owner.</p>
            </div>
            <ClipboardList size={18} />
          </div>
          <div className="form-grid">
            <Input label="Task title" value={employeeTaskForm.title} onChange={(title) => setEmployeeTaskForm({ ...employeeTaskForm, title })} />
            <label>
              Send to
              <select value={employeeTaskForm.assignedTo || allActiveEmployees[0]?.id || ""} onChange={(event) => setEmployeeTaskForm({ ...employeeTaskForm, assignedTo: event.target.value })}>
                {allActiveEmployees.map((item) => <option key={item.id} value={item.id}>{item.fullName}</option>)}
              </select>
            </label>
            <Input label="Due date" type="date" value={employeeTaskForm.dueDate} onChange={(dueDate) => setEmployeeTaskForm({ ...employeeTaskForm, dueDate })} />
            <label>
              Priority
              <select value={employeeTaskForm.priority} onChange={(event) => setEmployeeTaskForm({ ...employeeTaskForm, priority: event.target.value })}>
                <option>Normal</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </label>
          </div>
          <textarea placeholder="Task details" value={employeeTaskForm.description} onChange={(event) => setEmployeeTaskForm({ ...employeeTaskForm, description: event.target.value })} />
          <button className="primary-button" type="button" onClick={saveEmployeeTask}>Send task</button>
        </article>
        <article className="panel">
          <div className="panel-title-row">
            <h2>{taskView === "sent" ? "Tasks I Sent" : "Tasks For Me"}</h2>
            <span className="status-pill">{taskView === "sent" ? sentTasks.length : ownTasks.length} total</span>
          </div>
          <div className="status-tabs" role="tablist" aria-label="My task filters">
            <button className={taskView === "assigned" ? "active" : ""} type="button" onClick={() => setTaskView("assigned")}>
              For me
              <strong>{pendingTasks.length}</strong>
            </button>
            <button className={taskView === "sent" ? "active" : ""} type="button" onClick={() => setTaskView("sent")}>
              Sent by me
              <strong>{openSentTasks}</strong>
            </button>
          </div>
          <div className="task-list">
            {(taskView === "sent" ? sentTasks : ownTasks).length ? (taskView === "sent" ? sentTasks : ownTasks).slice(0, 30).map((task) => (
              <label className={`task-check-card ${task.status === "completed" ? "done" : ""}`} key={task.id}>
                {task.assignedTo === employee.id ? (
                  <input type="checkbox" checked={task.status === "completed"} disabled={task.status === "completed"} onChange={() => completeTask(task)} />
                ) : <i className="task-status-dot" />}
                <span>
                  <strong>{task.title}</strong>
                  <small>
                    {taskView === "sent" ? `To ${employeeName(task.assignedTo)}` : `By ${taskSenderName(task)}`} · {taskStatusLabel(task)} · Due {task.dueDate || "-"}
                  </small>
                </span>
              </label>
            )) : <p>{taskView === "sent" ? "No tasks sent by you yet." : "No tasks assigned yet."}</p>}
          </div>
        </article>
      </div>
      ) : null}
      {activeLayer === "targets" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel">
          <div className="panel-title-row">
            <h2>My Sales Targets</h2>
            <Target size={18} />
          </div>
          <div className="task-list">
            {ownTargets.length ? ownTargets.slice(0, 5).map((target) => {
              const progress = Math.min(100, Math.round((Number(target.achievedAmount || 0) / Math.max(1, Number(target.targetAmount || 0))) * 100));
              return (
                <div className="target-card-mini" key={target.id}>
                  <strong>{formatMonth(target.month || new Date().toISOString().slice(0, 7))}</strong>
                  <span>{currency(target.achievedAmount || 0)} / {currency(target.targetAmount || 0)}</span>
                  <i><b style={{ width: `${progress}%` }} /></i>
                </div>
              );
            }) : <p>No sales target assigned yet.</p>}
          </div>
        </article>
      </div>
      ) : null}
      {activeLayer === "attendance" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel attendance-calendar-panel">
          <div className="panel-title-row">
            <div>
              <h2>{formatMonth(selectedAttendanceMonth)}</h2>
              <p>Tap a date to see your in time, out time, status and location proof.</p>
            </div>
            <input
              className="month-picker"
              type="month"
              value={selectedAttendanceMonth}
              onChange={(event) => {
                const nextMonth = event.target.value;
                const range = monthRange(nextMonth);
                setSelectedAttendanceMonth(nextMonth);
                setSelectedAttendanceDate(eachDate(range.start, range.end).includes(today) ? today : range.start);
              }}
              aria-label="My attendance month"
            />
          </div>
          <div className="calendar-week-head">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="attendance-calendar">
            {Array.from({ length: (new Date(`${attendanceMonthDates[0]}T00:00:00`).getDay() + 6) % 7 }).map((_, index) => (
              <span key={`blank-${index}`} className="calendar-blank" />
            ))}
            {ownCalendarDays.map((day) => {
              const dateNumber = Number(day.date.slice(-2));
              const isSelected = day.date === resolvedAttendanceDate;
              const hasIssue = ["A", "M", "HD"].includes(day.summary.code) || day.outside;
              return (
                <button
                  key={day.date}
                  className={[
                    "calendar-day",
                    isSelected ? "active" : "",
                    day.date === today ? "today" : "",
                    hasIssue ? "has-issue" : ""
                  ].filter(Boolean).join(" ")}
                  type="button"
                  onClick={() => setSelectedAttendanceDate(day.date)}
                >
                  <strong>{dateNumber}</strong>
                  <span>{day.summary.code}</span>
                  <small>{day.summary.late ? "Late" : day.summary.earlyDeparture ? "Early" : day.outside ? "Outside" : day.summary.status}</small>
                </button>
              );
            })}
          </div>
        </article>
        <article className="panel day-attendance-panel">
          <div className="panel-title-row">
            <div>
              <h2>{formatDisplayDate(resolvedAttendanceDate)}</h2>
              <p>{selectedOwnDay.summary.status} ({selectedOwnDay.summary.code})</p>
            </div>
            <button className="mini-button" type="button" onClick={() => downloadSheet(`${employee.employeeCode}-my-attendance.csv`, ownAttendanceRows, ownAttendanceColumns)}>
              Download
            </button>
          </div>
          <div className="punch-detail-grid employee-date-detail">
            <article>
              <span>In time</span>
              <strong>{formatPunchTime(selectedOwnDay.punchIn)}</strong>
              <small>{selectedOwnDay.punchIn?.timestamp ? new Date(selectedOwnDay.punchIn.timestamp).toLocaleString("en-IN") : "No punch-in"}</small>
            </article>
            <article>
              <span>Out time</span>
              <strong>{formatPunchTime(selectedOwnDay.punchOut)}</strong>
              <small>{selectedOwnDay.punchOut?.timestamp ? new Date(selectedOwnDay.punchOut.timestamp).toLocaleString("en-IN") : "No punch-out"}</small>
            </article>
            <article>
              <span>Location</span>
              <strong>{selectedOwnDay.punchIn ? (selectedOwnDay.punchIn.insideGeofence ? "Inside" : "Outside") : "-"}</strong>
              <small>{selectedOwnDay.punchIn ? `${formatDistance(selectedOwnDay.punchIn)} · ${formatAccuracy(selectedOwnDay.punchIn)}` : "No location proof"}</small>
            </article>
            <article>
              <span>Flags</span>
              <strong>{selectedOwnDay.summary.late ? "Late" : selectedOwnDay.summary.earlyDeparture ? "Early" : selectedOwnDay.outside ? "Outside" : "Clear"}</strong>
              <small>{selectedDateTasks.length} task(s) linked to this date</small>
            </article>
          </div>
          <div className="task-list">
            {selectedDateTasks.length ? selectedDateTasks.map((task) => (
              <div className={`task-date-card ${task.status === "completed" ? "done" : ""}`} key={task.id}>
                <span>
                  <strong>{task.title}</strong>
                  <small>
                    {task.assignedTo === employee.id ? `By ${taskSenderName(task)}` : `To ${employeeName(task.assignedTo)}`} · {taskStatusLabel(task)} · Due {task.dueDate || "-"}
                  </small>
                </span>
                <b>{task.status === "completed" ? "DONE" : "OPEN"}</b>
              </div>
            )) : <p>No tasks linked to this date.</p>}
          </div>
        </article>
      </div>
      ) : null}
      {activeLayer === "leave" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel">
          <div className="panel-title-row">
            <div>
              <h2>Apply Leave</h2>
              <p>Your manager / HR must approve before it affects attendance and salary.</p>
            </div>
            <CalendarDays size={18} />
          </div>
          <div className="form-grid">
            <Input
              label="From date"
              type="date"
              value={leaveForm.fromDate}
              onChange={(fromDate) => {
                const days = Math.max(1, Number(leaveForm.days || 1));
                setLeaveForm({ ...leaveForm, fromDate, days, toDate: addDays(fromDate, days - 1) });
              }}
            />
            <Input
              label="Leave days"
              type="number"
              value={leaveForm.days || leaveRangeDays(leaveForm.fromDate, leaveForm.toDate) || 1}
              onChange={(daysValue) => {
                const days = Math.max(1, Number(daysValue || 1));
                setLeaveForm({ ...leaveForm, days, toDate: addDays(leaveForm.fromDate, days - 1) });
              }}
            />
            <Input
              label="To date"
              type="date"
              value={leaveForm.toDate}
              onChange={(toDate) => setLeaveForm({ ...leaveForm, toDate, days: leaveRangeDays(leaveForm.fromDate, toDate) || 1 })}
            />
            <label>
              Leave type
              <select value={leaveForm.leaveType} onChange={(event) => setLeaveForm({ ...leaveForm, leaveType: event.target.value })}>
                <option value="full_day">Full day leave</option>
                <option value="half_day">Half day leave</option>
              </select>
            </label>
          </div>
          <div className="button-row leave-duration-row">
            {[1, 2, 7, 15, 30].map((days) => (
              <button
                key={days}
                className="mini-button"
                type="button"
                onClick={() => setLeaveForm({ ...leaveForm, days, toDate: addDays(leaveForm.fromDate, days - 1) })}
              >
                {days} day{days === 1 ? "" : "s"}
              </button>
            ))}
          </div>
          <p className="helper-text">
            Selected: {leaveRangeDays(leaveForm.fromDate, leaveForm.toDate)} day{leaveRangeDays(leaveForm.fromDate, leaveForm.toDate) === 1 ? "" : "s"} · {formatDisplayDate(leaveForm.fromDate)} to {formatDisplayDate(leaveForm.toDate)}
          </p>
          <textarea placeholder="Reason for leave" value={leaveForm.reason} onChange={(event) => setLeaveForm({ ...leaveForm, reason: event.target.value })} />
          <div className="button-row">
            <button className="primary-button" type="button" onClick={submitLeaveApplication}>
              {editingLeaveId ? "Save leave changes" : "Send leave application"}
            </button>
            {editingLeaveId ? (
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setEditingLeaveId("");
                  setLeaveForm({ fromDate: today, toDate: today, days: 1, leaveType: "full_day", reason: "" });
                  setMessage("");
                }}
              >
                Stop editing
              </button>
            ) : null}
          </div>
          {message ? <p className="notice">{message}</p> : null}
        </article>
        <article className="panel">
          <div className="panel-title-row">
            <h2>My Leave Requests</h2>
            <span className="status-pill">
              {pendingLeaveApplications.length} pending
            </span>
          </div>
          <div className="task-list">
            {myLeaveApplications.map((item) => (
              <div className="task-date-card" key={item.id}>
                <span>
                  <strong>
                    {formatDisplayDate(item.requestedValue?.fromDate || item.date)}
                    {" to "}
                    {formatDisplayDate(item.requestedValue?.toDate || item.date)}
                    {" · "}
                    {item.requestedValue?.leaveType || item.requestedValue?.status || "Leave"}
                  </strong>
                  <small>{item.requestedValue?.days || item.children?.length || 1} day{Number(item.requestedValue?.days || item.children?.length || 1) === 1 ? "" : "s"} · {item.reason || "-"} · {item.status}</small>
                </span>
                <span className="task-actions-inline">
                  <b>{item.status}</b>
                  {item.status === "pending" ? (
                    <>
                      <button className="mini-button" type="button" onClick={() => editLeaveApplication(item)}>Edit</button>
                      <button className="mini-button danger" type="button" onClick={() => cancelLeaveApplication(item)}>Cancel</button>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </article>
      </div>
      ) : null}
      {activeLayer === "corrections" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel">
          <div className="panel-title-row">
            <div>
              <h2>Raise Correction</h2>
              <p>Missed punch, wrong status or leave marking request.</p>
            </div>
            <CheckCircle2 size={18} />
          </div>
          <div className="form-grid">
            <Input label="Date" type="date" value={correctionForm.date} onChange={(date) => setCorrectionForm({ ...correctionForm, date })} />
            <label>
              Requested status
              <select value={correctionForm.requestedCode} onChange={(event) => setCorrectionForm({ ...correctionForm, requestedCode: event.target.value })}>
                {["P", "HD", "A", "L", "EX", "M"].map((code) => <option key={code}>{code}</option>)}
              </select>
            </label>
          </div>
          <textarea placeholder="Reason, missed punch time, or explanation" value={correctionForm.reason} onChange={(event) => setCorrectionForm({ ...correctionForm, reason: event.target.value })} />
          <div className="button-row">
            <button className="primary-button" type="button" onClick={submitOwnCorrection}>
              {editingCorrectionId ? "Save correction changes" : "Send correction request"}
            </button>
            {editingCorrectionId ? (
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setEditingCorrectionId("");
                  setCorrectionForm({ date: today, requestedCode: "P", reason: "" });
                  setMessage("");
                }}
              >
                Stop editing
              </button>
            ) : null}
          </div>
          {message ? <p className="notice">{message}</p> : null}
        </article>
        <article className="panel">
          <div className="panel-title-row">
            <h2>My Requests</h2>
            <span className="status-pill">{pendingCorrectionRequests.length} pending</span>
          </div>
          <div className="task-list">
            {myCorrectionRequests.map((item) => (
              <div className="task-date-card" key={item.id}>
                <span>
                  <strong>{item.date} · {item.requestedValue?.code || "-"}</strong>
                  <small>{item.reason || "-"} · {item.status}</small>
                </span>
                <span className="task-actions-inline">
                  <b>{item.status}</b>
                  {item.status === "pending" ? (
                    <>
                      <button className="mini-button" type="button" onClick={() => editCorrectionRequest(item)}>Edit</button>
                      <button className="mini-button danger" type="button" onClick={() => cancelCorrectionRequest(item)}>Cancel</button>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </article>
      </div>
      ) : null}
      {activeLayer === "salary" ? (
      <div className="panel-grid two employee-layer-grid">
        <article className="panel">
          <h2>Latest Salary Slip</h2>
          {ownPayroll ? (
            <>
              <p className="big-money">{currency(ownPayroll.netPayable)}</p>
              <button
                className="secondary-button"
                type="button"
                onClick={async () => {
                  const blob = await generateSalarySlipPdf({ payrollItem: ownPayroll, employee, store, companyName: state.settings.companyName });
                  downloadBlob(blob, `${employee.employeeCode}-${ownPayroll.month}-salary-slip.pdf`);
                }}
              >
                <Download size={17} /> Download slip
              </button>
            </>
          ) : (
            <p>No locked salary slip is available yet.</p>
          )}
        </article>
      </div>
      ) : null}
      {pendingType ? <CameraCapture onCancel={() => setPendingType("")} onCapture={(photo) => savePunch(pendingType, photo)} /> : null}
    </section>
  );
}

function Kpi({ label, value, onClick, active, tone }) {
  return (
    <button className={["kpi", tone || "", active ? "active" : ""].filter(Boolean).join(" ")} type="button" onClick={onClick}>
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

const TUTORIAL_ROLES = ["All", "Store Staff", "Store Manager", "Admin", "Audit Manager"];
const TUTORIAL_MODULES = ["All", "Overview", "Sales", "Register", "Payment", "Delivery", "Lab", "Lens Desk", "Stock Audit", "Stock Master", "Settings", "FAQ"];
const TUTORIAL_SECTIONS = [
  {
    title: "App Overview",
    module: "Overview",
    roles: ["All", "Store Staff", "Store Manager", "Admin", "Audit Manager"],
    summary: "SparkingCircle Optical OS keeps sales, orders, payment, attendance, stock audit and reports in one place.",
    steps: [
      "Store staff use it for sale entry, slips, payments, delivery updates and assigned stock audit work.",
      "Store managers check store sale, collection, pending payments, delivery status and audit completion.",
      "Admins/Super Admins manage stores, users, permissions, reports, payroll/HR and company-wide data.",
      "Audit Managers check stock audit proof, missing/damaged items and approve, reject or ask for recheck."
    ]
  },
  {
    title: "How to Enter a Sale / Order Form",
    module: "Sales",
    roles: ["Store Staff", "Store Manager", "Admin"],
    summary: "Use the sale/order screen when a customer places a new optical order.",
    steps: [
      "Open Optical CRM, then Sales or Punch Sale if you are using the shortcut.",
      "Enter customer phone first if available. If the customer does not want to share phone, continue with name only.",
      "Select existing customer or create a new customer, then enter customer name.",
      "Select delivery date and check the order number / GC number.",
      "Add products one by one: frame, lens, contact lens/solution, sunglasses, accessories, service/repair or eye test.",
      "For frame/lens order, enter frame details, lens details and eye power/prescription.",
      "Enter total amount, rupees discount if any, advance received and payment mode.",
      "Check auto balance, add notes if needed, then save the slip.",
      "After saving, the app prepares customer slip, store/internal slip, lab/order form, daily register, pending payment if balance exists, and delivery tracking."
    ]
  },
  {
    title: "How to View or Share Slips",
    module: "Sales",
    roles: ["Store Staff", "Store Manager", "Admin"],
    summary: "Saved orders can be opened later without making the page long.",
    steps: [
      "Open Optical CRM, then Sales / Orders / Order History depending on your screen.",
      "Tap the order row once to open details.",
      "Use customer copy for the customer, internal copy for store/admin, and lab/order form for fitting/lab work.",
      "Lab/order form should hide unnecessary customer payment details and focus on frame, lens and power.",
      "Use Print or Share if the button is available on that slip."
    ]
  },
  {
    title: "Daily Register",
    module: "Register",
    roles: ["Store Manager", "Admin"],
    summary: "Daily register means the day-wise sale and collection record.",
    steps: [
      "Every saved sale should appear in the daily register automatically.",
      "Sale means bill/order value. Collection means money received today.",
      "Advance received is today’s collection. Balance remains pending until collected.",
      "Use filters to check today’s sale, today’s collection, cash, UPI, card or Paytm collection.",
      "If customer pays later, that later payment is old pending collection, not a fresh sale."
    ]
  },
  {
    title: "Pending Payment",
    module: "Payment",
    roles: ["Store Staff", "Store Manager", "Admin"],
    summary: "Pending payment is created when total order amount is more than advance received.",
    steps: [
      "Open pending payment/customer balance list.",
      "Search by customer name, mobile number, order number or GC number.",
      "Open the customer/order and collect balance.",
      "Select payment mode and save payment.",
      "Once full balance is collected, payment status should become paid."
    ]
  },
  {
    title: "Delivery / Order Status",
    module: "Delivery",
    roles: ["Store Staff", "Store Manager", "Admin"],
    summary: "Delivery status tells whether order is pending, lab pending, ready or delivered.",
    steps: [
      "Open the saved order or delivery tracking list.",
      "Mark sent to lab when frame/lens leaves for lab work.",
      "Mark lens ordered when the lens/company order is placed.",
      "Mark ready when order is ready at store.",
      "Mark delivered only after customer receives the order.",
      "Managers/admins can see store-wise due, pending and delivered-but-payment-pending orders."
    ]
  },
  {
    title: "Lab / Lens Company Slip",
    module: "Lab",
    roles: ["Store Staff", "Store Manager", "Admin"],
    summary: "Lab slip/order form goes with the frame so fitting can happen even without opening the app.",
    steps: [
      "Print Order Form / Lab Slip after saving an optical order.",
      "It should show order number, store, delivery date, frame details, lens details and eye power.",
      "Phone number and payment amount should not be needed for lab/fitting work.",
      "Use this slip for lens company/lab communication and physical fitting."
    ]
  },
  {
    title: "Lens Order Desk",
    module: "Lens Desk",
    roles: ["Store Manager", "Admin"],
    summary: "Lens desk is for tracking lenses that need vendor/company order.",
    steps: [
      "Admin/manager opens the order manager or lens order queue.",
      "Select vendor/company where lens is ordered.",
      "Mark ordered once order is placed.",
      "Mark received when lens arrives.",
      "Mark sent to lab/fitting when frame and lens move ahead.",
      "Use pending lens order list to follow up."
    ]
  },
  {
    title: "Stock Audit",
    module: "Stock Audit",
    roles: ["Store Staff", "Store Manager", "Admin", "Audit Manager"],
    summary: "Stock Audit checks whether physical stock matches system stock.",
    steps: [
      "Weekly Stock Audit is a soft random audit. Hard Stock Audit checks every available item in a store.",
      "Weekly audit normally selects 20 items per store, unless store has fewer items.",
      "Selection prefers 20% above ₹5000, 30% above ₹3000 and 50% random items.",
      "If high-value items are not enough, the app fills from available stock and records the adjustment note.",
      "Staff opens assigned audit, scans/enters barcode, uploads real photo and marks Found, Missing, Damaged, Sold but not updated, Transferred but not updated, Wrong barcode or Needs admin review.",
      "Missing/damaged/wrong items go to Audit Manager review. Store staff cannot close mismatch by themselves.",
      "Audit Manager checks photo proof and approves, rejects or asks for recheck.",
      "Use audit history/export to see store-wise, item-wise and repeat mismatch records."
    ]
  },
  {
    title: "Stock Master",
    module: "Stock Master",
    roles: ["Admin", "Audit Manager"],
    summary: "Stock Master is the barcode-wise item list used for audit.",
    steps: [
      "Each stock item has barcode, article name, brand, category, MRP, store and status.",
      "Status can be available, sold, transferred, damaged, missing or inactive.",
      "Use manual add for one item.",
      "Use CSV upload when adding many items together.",
      "CSV columns: barcode, article_name, brand, category, mrp, store_code, status."
    ]
  },
  {
    title: "Store Staff Guide",
    module: "Overview",
    roles: ["Store Staff"],
    summary: "Daily work for store staff.",
    steps: [
      "Enter sale/order form quickly and save.",
      "Print/share the required slip.",
      "Update payment when customer pays balance.",
      "Update delivery when order is ready or delivered.",
      "Complete assigned stock audit items with barcode and real photo."
    ]
  },
  {
    title: "Store Manager Guide",
    module: "Overview",
    roles: ["Store Manager"],
    summary: "Daily work for store manager.",
    steps: [
      "Check daily register and today’s collection.",
      "Check pending payments and follow up.",
      "Check order/delivery status.",
      "Check store audit completion and pending mismatch.",
      "Add/track expenses if that feature is enabled for your role."
    ]
  },
  {
    title: "Admin / Super Admin Guide",
    module: "Settings",
    roles: ["Admin"],
    summary: "Company control and setup work.",
    steps: [
      "View all stores, all sales, pending payments and reports as per permission.",
      "Manage users, roles and permissions.",
      "Manage stores and store access.",
      "Manage stock master and stock audit if permission is given.",
      "Download reports and backups regularly."
    ]
  },
  {
    title: "Audit Manager Guide",
    module: "Stock Audit",
    roles: ["Audit Manager"],
    summary: "Stock audit control work.",
    steps: [
      "Open Stock Audit dashboard to view all store audits.",
      "Check who completed and who has not completed audit.",
      "Open mismatch items and view uploaded proof photos.",
      "Approve, reject or ask for recheck.",
      "Use audit history to track repeat missing/damaged/wrong barcode items."
    ]
  }
];

const TUTORIAL_FAQS = [
  ["How do I enter a new sale?", "Open Optical CRM → Sales/Punch Sale, enter customer, products, amount and save."],
  ["What if customer has paid full amount?", "Enter total amount and same amount as advance received. Balance becomes zero."],
  ["What if customer has balance pending?", "Enter advance received only. App creates pending payment for the balance."],
  ["What if wrong amount was entered?", "Tell manager/admin. The correction should be logged so history remains clear."],
  ["What if barcode does not scan?", "Type barcode manually. If still not found, mark the item for review instead of guessing."],
  ["What if item is missing?", "In Stock Audit, mark Missing, add note, upload photo if possible. Audit Manager will review."],
  ["What if photo upload fails?", "Check internet, try again, or save note and retry from a stronger network."],
  ["What if I selected wrong audit status?", "Ask Audit Manager/Admin to reject or ask recheck, then verify again correctly."],
  ["What if delivery date changes?", "Open order and update delivery date/status if your role allows it."],
  ["What if customer comes later to pay balance?", "Open Pending Payment, collect amount, select payment mode and save."],
  ["What if stock is in another store?", "Manager/admin should use stock transfer flow or mark audit as transferred but not updated."],
  ["What if store has fewer than 20 audit items?", "Weekly audit will select all available stock items instead of forcing 20."]
];

function TutorialHelpPanel() {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const normalizedQuery = query.trim().toLowerCase();
  const guideRows = TUTORIAL_SECTIONS.filter((section) => {
    const roleMatch = roleFilter === "All" || section.roles.includes(roleFilter) || section.roles.includes("All");
    const moduleMatch = moduleFilter === "All" || section.module === moduleFilter;
    const text = `${section.title} ${section.module} ${section.summary} ${section.steps.join(" ")}`.toLowerCase();
    return roleMatch && moduleMatch && (!normalizedQuery || text.includes(normalizedQuery));
  });
  const faqRows = TUTORIAL_FAQS.filter(([question, answer]) => {
    if (moduleFilter !== "All" && moduleFilter !== "FAQ") return false;
    const text = `${question} ${answer}`.toLowerCase();
    return !normalizedQuery || text.includes(normalizedQuery);
  });
  return (
    <section className="tutorial-help-page">
      <Header title="Tutorial / Help" subtitle="Simple guide for store staff, managers, admins and audit managers.">
        <span className="status-chip">Training</span>
      </Header>
      <article className="panel tutorial-search-panel">
        <div className="staff-search-bar dashboard-search">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search sale, payment, delivery, stock audit..." />
        </div>
        <div className="filter-strip">
          <label>
            Role
            <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
              {TUTORIAL_ROLES.map((role) => <option key={role}>{role}</option>)}
            </select>
          </label>
          <label>
            Module
            <select value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)}>
              {TUTORIAL_MODULES.map((module) => <option key={module}>{module}</option>)}
            </select>
          </label>
        </div>
      </article>
      <div className="tutorial-card-grid">
        {guideRows.map((section) => (
          <OpticalSection key={`${section.module}-${section.title}`} title={section.title} subtitle={`${section.module} · ${section.roles.filter((role) => role !== "All").join(", ") || "Everyone"}`}>
            <p>{section.summary}</p>
            <ol className="tutorial-steps">
              {section.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
          </OpticalSection>
        ))}
        {faqRows.length ? (
          <article className="panel tutorial-faq-panel">
            <div className="panel-title-row">
              <h2>FAQ</h2>
              <span className="status-pill">{faqRows.length} answers</span>
            </div>
            {faqRows.map(([question, answer]) => (
              <OpticalSection key={question} title={question} subtitle="Quick answer">
                <p>{answer}</p>
              </OpticalSection>
            ))}
          </article>
        ) : null}
        {!guideRows.length && !faqRows.length ? <article className="panel empty-state">No help topic matched. Try a different search.</article> : null}
      </div>
    </section>
  );
}

function AdminShell({ state, user, updateState, onLogout }) {
  const requestedScreen = new URLSearchParams(window.location.search).get("screen");
  const [tab, setTab] = useState(requestedScreen || "dashboard");
  const [workFocus, setWorkFocus] = useState("");
  const [crmFocus, setCrmFocus] = useState("");
  const [cnFocus, setCnFocus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [opticalExpanded, setOpticalExpanded] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const companyName = getCompanyName(state.settings);
  const visibleEmployees = getVisibleEmployeesForUser(state, user);
  const hasDirectReports = getImmediateReportEmployees(state.employees, user.employeeId).length > 0;
  const visibleStoreIds = user.role === "super_admin"
    ? state.stores.map((store) => store.id)
    : [...new Set(visibleEmployees.map((employee) => employee.storeId).filter(Boolean))];
  const [selectedMonth, setSelectedMonth] = useState(localMonthOnly());
  const moduleKeyForTab = {
    hrms: "attendance",
    optical: "opticalOrders",
    tasks: "work"
  };
  const allowedTabs = TABS.filter(([key]) => {
    if (user.role !== "super_admin" && key === "backup") return false;
    if (key === "hrms" && user.role !== "super_admin" && hasDirectReports) {
      return isModuleEnabled(state.settings, "attendance");
    }
    if (key === "tasks") {
      if (user.role !== "super_admin" && !hasAppPermission(user, "tasks") && !user.permissions?.salesTargets) return false;
      return isModuleEnabled(state.settings, "work") || isModuleEnabled(state.settings, "salesTargets");
    }
    if (key === "dailyReconciliation" && user.role !== "super_admin" && !hasAppPermission(user, "dailyReconciliation")) return false;
    if (!hasAppPermission(user, key)) return false;
    const moduleKey = moduleKeyForTab[key];
    return moduleKey ? isModuleEnabled(state.settings, moduleKey) : true;
  });
  const hiddenRouteTabs = ["cnTracking"];
  const canOpenHiddenRoute = (key) => hiddenRouteTabs.includes(key) && hasAppPermission(user, key);
  const activeTab = allowedTabs.find(([key]) => key === tab) || (canOpenHiddenRoute(tab) ? [tab, "Purchase Return", FileText] : allowedTabs[0]);
  const labelForTab = ([key, label]) => (key === "dashboard" ? "My Cockpit" : label);
  const mobileQuickTabs = allowedTabs.filter(([key]) => ["dashboard", "optical", "stores", "hrms", "tasks", "reports"].includes(key));
  const storeOptions = state.stores.filter((store) => visibleEmployees.some((employee) => employee.storeId === store.id));
  const canAccessCrmSection = (key) => {
    if (user.role === "super_admin" || user.permissions?.all) return true;
    if (!hasAppPermission(user, "optical")) return false;
    const permissions = user.permissions || {};
    const hasDetailedPermissions = Object.values(permissions).some(Boolean);
    if (!hasDetailedPermissions) return true;
    if (key === "products") {
      return [
        "can_view_products",
        "can_add_products",
        "can_edit_products",
        "can_delete_products",
        "can_change_product_price"
      ].some((permission) => hasUserPermission(user, permission));
    }
    if (key === "inventory") {
      return [
        "can_view_products",
        "can_create_stock_inward",
        "can_transfer_stock",
        "can_adjust_stock"
      ].some((permission) => hasUserPermission(user, permission));
    }
    if (key === "reports") return hasUserPermission(user, "can_view_reports") || permissions.reports;
    if (key === "account") return permissions.companyLedger || permissions.reports;
    return true;
  };
  const crmMenuItems = CRM_LAYERS.filter(([key]) => key !== "overview" && canAccessCrmSection(key));
  const openCrmSection = (key = "overview") => {
    setTab("optical");
    setCrmFocus(key);
    setOpticalExpanded(true);
  };
  const toggleOpticalMenu = () => {
    if (tab !== "optical") {
      openCrmSection("overview");
      return;
    }
    setOpticalExpanded((current) => !current);
  };

  useEffect(() => {
    if (!allowedTabs.some(([key]) => key === tab) && !canOpenHiddenRoute(tab)) setTab(allowedTabs[0]?.[0] || "dashboard");
  }, [allowedTabs, tab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  const addAudit = async (log) => {
    await persistAuditLog(log).catch(() => null);
    updateState((current) => ({ ...current, auditLogs: [log, ...current.auditLogs] }));
  };

  const markNotificationRead = async (notification) => {
    const updated = { ...notification, isRead: true, readAt: notification.readAt || new Date().toISOString() };
    await persistOptionalEntity("notifications", updated).catch(() => null);
    updateState((current) => ({
      ...current,
      notifications: (current.notifications || []).map((item) => (item.id === notification.id ? updated : item))
    }));
  };

  const markAllNotificationsRead = async () => {
    const now = new Date().toISOString();
    const unread = (state.notifications || []).filter((notification) => notification.userId === user.id && !notification.isRead);
    const updatedRows = unread.map((notification) => ({ ...notification, isRead: true, readAt: notification.readAt || now }));
    await Promise.all(updatedRows.map((notification) => persistOptionalEntity("notifications", notification).catch((error) => console.error("Notification mark-read failed", error))));
    updateState((current) => ({
      ...current,
      notifications: (current.notifications || []).map((notification) => updatedRows.find((row) => row.id === notification.id) || notification)
    }));
  };

  const openNotification = async (notification) => {
    await markNotificationRead(notification);
    if (notification.module === "CN Tracking") {
      setTab("cnTracking");
      setCnFocus({ requestId: notification.linkedRecordId, list: notification.linkedRecordId ? "" : "new" });
    }
  };

  const createNewOrderV2Id = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  };

  const saveNewOrderV2 = async (draft = {}) => {
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date().toISOString();
    const allowedStoreIds = visibleStoreIds.length ? visibleStoreIds : state.stores.map((store) => store.id);
    const employeeProfile = (state.employees || []).find((employee) => employee.id === user.employeeId) || null;
    const store = (state.stores || []).find((row) => row.id === selectedStoreId && selectedStoreId !== "all")
      || (state.stores || []).find((row) => row.id === employeeProfile?.storeId)
      || (state.stores || []).find((row) => allowedStoreIds.includes(row.id))
      || (state.stores || [])[0];
    const salesperson = employeeProfile || visibleEmployees[0] || null;
    const cleanPhone = String(draft.customerMobile || "").trim();
    const customerName = String(draft.customerName || "").trim();
    if (!customerName) throw new Error("Customer name is required before saving.");
    if (!store?.id) throw new Error("Store is required before saving.");

    const orderProducts = (draft.products || [])
      .filter((product) => product.type || product.name || Number(product.amount || 0) > 0)
      .map((product, index) => {
        const mappedType = product.type === "sunglass"
          ? "sunglasses"
          : ["contact_lens", "solution"].includes(product.type)
            ? "contactSolution"
            : product.type === "accessory"
              ? "service"
              : product.type || "service";
        return {
          id: createNewOrderV2Id("nov2-product"),
          type: mappedType,
          productId: "",
          barcode: "",
          name: String(product.name || opticalProductLabel(mappedType) || `Product ${index + 1}`).trim(),
          quantity: Number(product.quantity || 1),
          amount: Number(product.amount || 0),
          source: product.source || "from_store",
          status: "ready",
          photo: "",
          requiresPower: ["lens", "contactSolution"].includes(mappedType),
          tintRequired: false,
          tintAsSample: false,
          notes: product.note || product.notes || ""
        };
      });
    if (!orderProducts.length) throw new Error("Add at least one product before saving.");

    const subtotalAmount = Number(draft.subtotal || orderProducts.reduce((sum, product) => sum + Number(product.amount || 0), 0));
    const discountType = draft.discountType || "amount";
    const rawDiscountValue = Number(draft.discountValue || 0);
    const discountAmount = Number(draft.discount ?? (discountType === "percent" ? Math.round((subtotalAmount * rawDiscountValue) / 100) : rawDiscountValue));
    const totalAmount = Math.max(0, Number(draft.netTotal ?? (subtotalAmount - discountAmount)));
    const splitPayments = (draft.payments || [])
      .filter((payment) => Number(payment.amount || 0) > 0)
      .map((payment) => ({
        id: createNewOrderV2Id("nov2-payment"),
        amount: Number(payment.amount || 0),
        mode: payment.mode || "Cash",
        ref: payment.ref || payment.note || "",
        receivedAt: normalizeLocalDateTime(payment.receivedAt, now),
        receivedBy: user.name || user.email || ""
      }));
    const advanceReceived = splitPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    if (advanceReceived > totalAmount) throw new Error("Paid amount cannot be more than order total.");
    const balance = Math.max(0, totalAmount - advanceReceived);
    const paymentMode = splitPayments.length > 1 ? "Split" : splitPayments[0]?.mode || "Cash";
    const orderNumber = fallbackOpticalOrderNumber();
    const deliveryDate = dateOffset(3);
    const walkInId = cleanPhone ? "" : `WALKIN-${today.replaceAll("-", "")}-${String((state.opticalFastSlips || []).length + 1).padStart(3, "0")}`;
    const selectedCustomer = draft.selectedCustomerId
      ? (state.opticalCustomers || []).find((customer) => customer.id === draft.selectedCustomerId)
      : null;
    const existingCustomer = selectedCustomer || (cleanPhone ? (state.opticalCustomers || []).find((customer) => customer.phone === cleanPhone) : null);
    const customer = {
      ...(existingCustomer || {}),
      id: existingCustomer?.id || createNewOrderV2Id("nov2-customer"),
      phone: cleanPhone,
      noPhone: !cleanPhone,
      walkInId,
      name: customerName,
      address: existingCustomer?.address || "",
      dob: existingCustomer?.dob || null,
      email: existingCustomer?.email || "",
      storeId: store.id,
      createdAt: existingCustomer?.createdAt || now,
      updatedAt: now
    };
    const slipId = createNewOrderV2Id("nov2-slip");
    const frameProducts = orderProducts.filter((product) => ["frame", "sunglasses"].includes(product.type));
    const lensProducts = orderProducts.filter((product) => product.type === "lens");
    const productPairCount = Math.max(frameProducts.length, lensProducts.length);
    const productPairs = Array.from({ length: productPairCount }).map((_, index) => ({
      id: createNewOrderV2Id("nov2-pair"),
      frameDetails: frameProducts[index]?.name || "",
      frameSource: frameProducts[index]?.source || "from_store",
      frameStatus: frameProducts[index]?.status || "ready",
      framePhoto: "",
      lensDetails: lensProducts[index]?.name || "",
      lensStatus: lensProducts[index]?.status || "ready",
      tintRequired: false,
      tintAsSample: false,
      tintSamplePhoto: "",
      notes: [frameProducts[index]?.notes, lensProducts[index]?.notes].filter(Boolean).join(" | ")
    }));
    const pairSummary = orderProducts
      .map((product, index) => `${index + 1}. ${opticalProductLabel(product.type)} - ${product.name || "Item"}`)
      .join(" | ");
    const contactSummary = orderProducts.filter((product) => product.type === "contactSolution").map((product) => product.name || product.notes).filter(Boolean).join(" | ");
    const serviceSummary = orderProducts.filter((product) => ["service", "eyeTest"].includes(product.type)).map((product) => product.name || product.notes).filter(Boolean).join(" | ");
    const savedCustomer = await persistRequiredEntity("optical_customers", customer, "Customer");
    const orderRecord = {
      id: slipId,
      orderNumber,
      gcNumber: orderNumber,
      storeId: store.id,
      storeName: store.name || "",
      storePhone: store.phone || "",
      salespersonId: salesperson?.id || user.employeeId || "",
      salespersonName: salesperson?.fullName || user.name || user.email || "",
      salespersonDesignation: salesperson?.designation || user.designation || "",
      date: today,
      deliveryDate,
      customerId: savedCustomer.id,
      customer: savedCustomer,
      orderDetails: {
        orderType: deriveOrderTypeFromProducts(orderProducts),
        selectedItems: [...new Set(orderProducts.map((product) => product.type))],
        products: orderProducts,
        extraProducts: orderProducts,
        opticalPairs: productPairs,
        frameDetails: frameProducts.map((product) => product.name).filter(Boolean).join(" | "),
        frameSource: frameProducts[0]?.source || "from_store",
        frameStatus: frameProducts[0]?.status || "ready",
        ownFramePhoto: "",
        lensDetails: lensProducts.map((product) => product.name).filter(Boolean).join(" | "),
        lensStatus: lensProducts[0]?.status || "ready",
        contactLensDetails: contactSummary,
        serviceDetails: serviceSummary,
        specialNotes: [draft.notes?.internal, draft.notes?.product, draft.notes?.delivery, draft.notes?.print, draft.notes?.customer].filter(Boolean).join(" | "),
        notes: draft.notes || {},
        pairSummary,
        editLog: [],
        managerWorkflow: {
          status: ORDER_MANAGER_STATUSES.new,
          reviewedAt: "",
          reviewedBy: "",
          vendorName: "",
          vendorOrderReference: "",
          orderSentMethod: ""
        },
        orderTimeline: [
          orderTimelineEvent({
            eventType: "Order created",
            oldStatus: "",
            newStatus: ORDER_MANAGER_STATUSES.new,
            note: "Customer order created from NewOrderV2.",
            user
          })
        ],
        lastEditedAt: null,
        lastEditedBy: null
      },
      power: {
        rightEye: { distance: {}, add: {} },
        leftEye: { distance: {}, add: {} },
        contactLens: { autoConverted: false, rightEye: {}, leftEye: {} },
        measurements: {},
        prescribedBy: ""
      },
      payment: {
        subtotalAmount,
        discountType,
        discountValue: rawDiscountValue,
        discountAmount,
        totalAmount,
        advanceReceived,
        balance,
        paymentMode,
        splitPayments,
        notes: "",
        status: balance > 0 ? "Pending" : "Paid"
      },
      generatedViews: { customerSlip: true, internalStoreSlip: true, labLensCompanySlip: true },
      createdAt: now,
      updatedAt: now
    };
    const savedOrderRecord = await persistOpticalOrderWithUniqueNumber(orderRecord);
    const finalOrderNumber = savedOrderRecord.orderNumber || orderNumber;
    const dailyEntry = {
      id: createNewOrderV2Id("nov2-daily"),
      slipId,
      orderNumber: finalOrderNumber,
      date: today,
      customerName: savedCustomer.name,
      phone: savedCustomer.phone,
      subtotalAmount,
      discountAmount,
      totalAmount,
      advanceReceived,
      balance,
      paymentMode,
      deliveryDate,
      deliveryStatus: OPTICAL_DELIVERY_STATUS,
      storeId: store.id,
      salespersonId: salesperson?.id || user.employeeId || "",
      createdAt: now,
      updatedAt: now
    };
    const deliveryEntry = {
      id: createNewOrderV2Id("nov2-delivery"),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      deliveryDate,
      status: OPTICAL_DELIVERY_STATUS,
      storeId: store.id,
      salespersonId: salesperson?.id || user.employeeId || "",
      createdAt: now,
      updatedAt: now
    };
    const lensOrderEntry = {
      id: createNewOrderV2Id("nov2-lens-order"),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      lensDetails: pairSummary,
      contactLensDetails: contactSummary,
      power: savedOrderRecord.power || orderRecord.power,
      vendor: "",
      vendorOrderNumber: "",
      status: OPTICAL_LENS_ORDER_STATUS,
      storeId: store.id,
      salespersonId: salesperson?.id || user.employeeId || "",
      createdAt: now,
      updatedAt: now
    };
    const pendingPaymentEntry = balance > 0 ? {
      id: createNewOrderV2Id("nov2-pending-payment"),
      slipId,
      orderNumber: finalOrderNumber,
      customerName: savedCustomer.name,
      phone: savedCustomer.phone,
      balance,
      subtotalAmount,
      discountAmount,
      totalAmount,
      advanceReceived,
      status: "Pending",
      storeId: store.id,
      salespersonId: salesperson?.id || user.employeeId || "",
      createdAt: now,
      updatedAt: now
    } : null;
    const [savedDailyEntry, savedDeliveryEntry, savedLensOrderEntry, savedPendingPaymentEntry] = await Promise.all([
      persistRequiredEntity("optical_daily_register_entries", dailyEntry, "Daily register"),
      persistRequiredEntity("optical_delivery_tracking_entries", deliveryEntry, "Delivery tracking"),
      persistRequiredEntity("optical_lens_order_entries", lensOrderEntry, "Lens order"),
      pendingPaymentEntry ? persistRequiredEntity("optical_pending_payment_entries", pendingPaymentEntry, "Pending payment") : Promise.resolve(null)
    ]);
    const log = createAuditLog({
      entityType: "optical_fast_slip",
      entityId: savedOrderRecord.id,
      action: "created",
      oldValue: null,
      newValue: savedOrderRecord,
      reason: "NewOrderV2 Order Form created",
      effectiveDate: today,
      user
    });
    await addAudit(log);
    updateState((current) => ({
      ...current,
      opticalCustomers: upsertById(current.opticalCustomers || [], savedCustomer),
      opticalFastSlips: upsertById(current.opticalFastSlips || [], savedOrderRecord),
      opticalDailyRegisterEntries: upsertById(current.opticalDailyRegisterEntries || [], savedDailyEntry),
      opticalDeliveryTrackingEntries: upsertById(current.opticalDeliveryTrackingEntries || [], savedDeliveryEntry),
      opticalLensOrderEntries: upsertById(current.opticalLensOrderEntries || [], savedLensOrderEntry),
      opticalPendingPaymentEntries: savedPendingPaymentEntry
        ? upsertById(current.opticalPendingPaymentEntries || [], savedPendingPaymentEntry)
        : (current.opticalPendingPaymentEntries || [])
    }));
    await notifySuperAdmins(state, updateState, {
      module: "Orders",
      title: "New Order Created",
      message: `Order ${savedOrderRecord.orderNumber} was created at ${savedOrderRecord.storeName || store.name || "store"}.`,
      priority: "normal",
      entityType: "optical_fast_slip",
      entityId: savedOrderRecord.id,
      storeId: savedOrderRecord.storeId,
      createdBy: user.id
    });
    if (savedPendingPaymentEntry) {
      await notifySuperAdmins(state, updateState, {
        module: "Payments",
        title: "Pending Payment Created",
        message: `${currency(savedPendingPaymentEntry.balance)} pending for order ${savedOrderRecord.orderNumber}.`,
        priority: "normal",
        entityType: "optical_pending_payment",
        entityId: savedPendingPaymentEntry.id,
        storeId: savedOrderRecord.storeId,
        createdBy: user.id
      });
    }
    return savedOrderRecord;
  };

  const adminNewOrderV2Orders = (state.opticalFastSlips || [])
    .filter((order) => {
      if (user.role === "super_admin") return selectedStoreId === "all" || !selectedStoreId || order.storeId === selectedStoreId;
      return visibleStoreIds.includes(order.storeId);
    })
    .sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));

  const collectAdminNewOrderV2Balance = (order, draft = {}) =>
    persistCustomerOrderBalanceCollection({
      state,
      updateState,
      order,
      rows: draft.rows || [],
      receivedBy: draft.receivedBy || user.name || user.email,
      user
    });

  const markAdminNewOrderV2Delivered = (order) =>
    persistCustomerOrderDelivered({ state, updateState, order, user });

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <BrandLockup settings={state.settings} subtitle={user.role.replace("_", " ")} compact />
        <nav>
          {allowedTabs.map(([key, label, Icon]) => (
            <div className={key === "optical" ? "sidebar-group" : ""} key={key}>
              <button
                className={tab === key ? "active" : ""}
                type="button"
                onClick={() => {
                  if (key === "optical") {
                    toggleOpticalMenu();
                    return;
                  }
                  setTab(key);
                }}
              >
                <Icon size={17} /> {labelForTab([key, label])}
                {key === "optical" ? <ChevronRight className={opticalExpanded ? "sidebar-chevron open" : "sidebar-chevron"} size={15} /> : null}
              </button>
              {key === "optical" && opticalExpanded ? (
                <div className="sidebar-subnav" aria-label="Optical CRM sections">
                  {crmMenuItems.map(([sectionKey, sectionLabel]) => (
                    <button
                      key={sectionKey}
                      className={tab === "optical" && (crmFocus === sectionKey || (!crmFocus && sectionKey === "products")) ? "active" : ""}
                      type="button"
                      onClick={() => openCrmSection(sectionKey)}
                    >
                      {sectionLabel}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </nav>
      </aside>
      <main className="admin-main">
        <div className="desktop-notification-dock">
          <NotificationBell
            notifications={state.notifications || []}
            user={user}
            onOpen={openNotification}
            onMarkRead={markNotificationRead}
            onMarkAllRead={markAllNotificationsRead}
          />
        </div>
        <div className="mobile-admin-header">
          <button className="mobile-menu-button" type="button" aria-label="Open menu" onClick={() => setMobileMenuOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="mobile-brand-row">
            <BrandMark settings={state.settings} />
            <div>
            <span className="status-chip">{companyName}</span>
            <strong>{activeTab ? labelForTab(activeTab) : "My Cockpit"}</strong>
            </div>
          </div>
          {tab !== "dashboard" ? (
            <button
              className="mobile-cockpit-button"
              type="button"
              onClick={() => {
                setTab("dashboard");
                setWorkFocus("");
              }}
            >
              <ShieldCheck size={16} />
              Cockpit
            </button>
          ) : null}
          <NotificationBell
            notifications={state.notifications || []}
            user={user}
            onOpen={openNotification}
            onMarkRead={markNotificationRead}
            onMarkAllRead={markAllNotificationsRead}
          />
        </div>
        <LatestUpdateBanner />
        {mobileMenuOpen ? (
          <div className="mobile-menu-layer" role="dialog" aria-modal="true">
            <button className="mobile-menu-scrim" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)} />
            <aside className="mobile-menu-panel">
              <div className="mobile-menu-head">
                <BrandLockup settings={state.settings} subtitle={user.role.replace("_", " ")} compact />
                <button className="icon-only" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                  <XCircle size={18} />
                </button>
              </div>
              <label className="menu-select">
                Branch
                <select value={selectedStoreId} onChange={(event) => setSelectedStoreId(event.target.value)}>
                  <option value="all">All Branches</option>
                  {storeOptions.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </label>
              <nav className="mobile-menu-nav" aria-label="App screens">
                {allowedTabs.map(([key, label, Icon]) => (
                  <div className={key === "optical" ? "mobile-menu-group" : ""} key={key}>
                    <button
                      className={tab === key ? "active" : ""}
                      type="button"
                      onClick={() => {
                        if (key === "optical") {
                          toggleOpticalMenu();
                          return;
                        }
                        setTab(key);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <Icon size={18} />
                      <span>{labelForTab([key, label])}</span>
                      {key === "optical" ? <ChevronRight className={opticalExpanded ? "sidebar-chevron open" : "sidebar-chevron"} size={15} /> : null}
                    </button>
                    {key === "optical" && opticalExpanded ? (
                      <div className="mobile-menu-subnav" aria-label="Optical CRM sections">
                        {crmMenuItems.map(([sectionKey, sectionLabel]) => (
                          <button
                            key={sectionKey}
                            className={tab === "optical" && crmFocus === sectionKey ? "active" : ""}
                            type="button"
                            onClick={() => {
                              openCrmSection(sectionKey);
                              setMobileMenuOpen(false);
                            }}
                          >
                            {sectionLabel}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </nav>
              <div className="mobile-menu-actions">
                <InstallAppButton />
                <button className="secondary-button" type="button" onClick={onLogout}>
                  <LogOut size={16} /> Logout
                </button>
              </div>
            </aside>
          </div>
        ) : null}
        {tab === "dashboard" && (
          <Dashboard
            state={state}
            user={user}
            visibleEmployees={visibleEmployees}
            selectedMonth={selectedMonth}
            selectedStoreId={selectedStoreId}
            setSelectedStoreId={setSelectedStoreId}
            setTab={setTab}
            setWorkFocus={setWorkFocus}
            setCrmFocus={setCrmFocus}
            updateState={updateState}
          />
        )}
        {tab === "users" && (
          <UsersPanel state={state} updateState={updateState} user={user} employees={visibleEmployees} addAudit={addAudit} />
        )}
        {tab === "stores" && <StoresPanel state={state} updateState={updateState} user={user} addAudit={addAudit} />}
        {tab === "hrms" && (
          <AttendancePanel
            state={state}
            updateState={updateState}
            employees={visibleEmployees}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            user={user}
            addAudit={addAudit}
          />
        )}
        {tab === "hrmsV2Preview" && (
          <HrmsV2Preview
            state={state}
            employees={state.employees || []}
            stores={state.stores || []}
            selectedMonth={selectedMonth}
            user={user}
          />
        )}
        {tab === "cnTracking" && <CnTrackingPanel state={state} updateState={updateState} user={user} focus={cnFocus} onFocusHandled={() => setCnFocus(null)} />}
        {tab === "optical" && (
          <CrmPanel state={state} updateState={updateState} employees={visibleEmployees} user={user} addAudit={addAudit} focus={crmFocus} onFocusHandled={() => setCrmFocus("")} />
        )}
        {tab === "newOrderV2" && (
          <NewOrderV2
            customers={state.opticalCustomers || []}
            orders={adminNewOrderV2Orders}
            onSaveOrder={saveNewOrderV2}
            onMarkDelivered={markAdminNewOrderV2Delivered}
          />
        )}
        {tab === "purchaseV1" && <PurchaseV1 />}
        {tab === "inventoryV1" && <InventoryV1 />}
        {tab === "internalChatV1" && (
          <InternalChatV1
            employees={state.employees || []}
            stores={state.stores || []}
            user={user}
            currentEmployee={userEmployee(state, user)}
          />
        )}
        {tab === "tasks" && (
          <WorkPanel
            state={state}
            updateState={updateState}
            employees={visibleEmployees}
            user={user}
            focus={workFocus}
            onFocusHandled={() => setWorkFocus("")}
          />
        )}
        {tab === "communication" && <CommunicationPanel state={state} updateState={updateState} user={user} />}
        {tab === "reports" && <ReportsPanel state={state} employees={visibleEmployees} selectedMonth={selectedMonth} />}
        {tab === "tutorialHelp" && <TutorialHelpPanel />}
        {tab === "backup" && <BackupPanel state={state} employees={visibleEmployees} selectedMonth={selectedMonth} />}
        {tab === "companyLedger" && <CompanyLedgerPanel state={state} />}
        {tab === "dailyReconciliation" && <BankRecoPanel state={state} updateState={updateState} user={user} />}
      </main>
      <nav className="mobile-bottom-nav" aria-label="Quick admin screens">
        {mobileQuickTabs.map(([key, label, Icon]) => (
          <button key={key} className={tab === key ? "active" : ""} type="button" onClick={() => setTab(key)}>
            <Icon size={18} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function OpticalSection({ title, subtitle, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <details className="order-collapse" open={isOpen} onToggle={(event) => setIsOpen(event.currentTarget.open)}>
      <summary>
        <span>
          <strong>{title}</strong>
          {subtitle ? <small>{subtitle}</small> : null}
        </span>
        <ChevronRight size={18} />
      </summary>
      <div className="order-collapse-body">{children}</div>
    </details>
  );
}

function PhotoPreview({ src, label, onRemove }) {
  const url = useStoredFileUrl(src);
  if (!src) return null;
  return (
    <figure className="order-photo-preview">
      {url ? <img src={url} alt={label} /> : <div className="file-thumbnail-loading">Loading photo...</div>}
      <figcaption>
        <span>{label}</span>
        {onRemove ? <button className="mini-button danger" type="button" onClick={onRemove}>Remove</button> : null}
      </figcaption>
    </figure>
  );
}

function CrmPanel({ state, updateState, employees, user, addAudit, focus, onFocusHandled }) {
  const [layer, setLayer] = useState("overview");
  const exportPrefix = filePrefix(state.settings);
  const employeeIds = new Set(employees.map((employee) => employee.id));
  const storeIds = new Set(employees.map((employee) => employee.storeId).filter(Boolean));
  const scopedOrders = (state.opticalFastSlips || []).filter((slip) => !slip.salespersonId || employeeIds.has(slip.salespersonId) || storeIds.has(slip.storeId));
  const scopedCustomers = (state.opticalCustomers || []).filter((customer) => !customer.storeId || storeIds.has(customer.storeId) || user.role === "super_admin");
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = scopedOrders.filter((slip) => slip.date === today);
  const pendingOrders = scopedOrders.filter((slip) => computedOrderBalance(slip) > 0);
  const pendingDeliveries = (state.opticalDeliveryTrackingEntries || []).filter((row) => row.status !== "Delivered" && (!row.storeId || storeIds.has(row.storeId) || user.role === "super_admin"));
  const lowStockRows = (state.crmProducts || []).filter((product) => Number(product.stockQty || 0) <= Number(product.reorderLevel || 0));
  const pendingFollowUps = (state.crmCommunications || []).filter((item) => item.status !== "done");
  const crmOverviewCards = [
    ["masters", "Masters", "Stores, suppliers, product categories, brands and GST/HSN setup."],
    ["products", "Products", "Frame, lens, contact lens, solution and other product masters."],
    ["inventory", "Inventory", "Stock levels, transfers, audit, adjustment history and barcode tracking."],
    ["purchase", "Purchase", "Vendor orders, DC inward, purchase inward and bill audit."],
    ["sales", "Sales", `${todayOrders.length} order(s) today. Existing Punch Sale flow stays here.`],
    ["customer", "Customer", `${scopedCustomers.length} customer record(s) and customer CRM.`],
    ["reports", "Reports", "CRM sheets and optical business reports."],
    ["sms", "SMS", "Customer messages, reminders, order-ready messages and follow-ups."],
    ["prescription", "Prescription", "Eye test, prescription database, patient details and appointments."],
    ["account", "Account", "Ledgers, expenses, receivables, payables and account statements."]
  ];

  useEffect(() => {
    if (!focus) return;
    setLayer(CRM_LEGACY_LAYER_MAP[focus] || focus);
    onFocusHandled?.();
  }, [focus, onFocusHandled]);

  return (
    <section className="module-detail-screen">
      <Header title="Optical CRM" subtitle="Customers, prescription, sales/POS, orders, payments, inventory, purchase, follow-ups and customer reports in one place.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-orders.csv`, scopedOrders, [
          ["Order", (row) => row.orderNumber],
          ["Date", (row) => row.date],
          ["Customer", (row) => row.customer?.name || row.customerName],
          ["Phone", (row) => row.customer?.phone],
          ["Total", (row) => row.payment?.totalAmount],
          ["Discount", (row) => row.payment?.discountAmount || 0],
          ["Balance", (row) => computedOrderBalance(row)]
        ])}>
          <FileSpreadsheet size={17} /> CRM sheet
        </button>
      </Header>
      <div className="status-tabs crm-layer-tabs" role="tablist" aria-label="CRM sections">
        {CRM_LAYERS.map(([key, label]) => (
          <button key={key} className={layer === key ? "active" : ""} type="button" onClick={() => setLayer(key)}>
            {label}
          </button>
        ))}
      </div>
      {layer === "overview" ? (
        <>
          <div className="module-tile-grid crm-overview-grid">
            {crmOverviewCards.map(([key, title, subtitle]) => (
              <button className="module-tile-card crm-module-card" type="button" key={key} onClick={() => setLayer(key)}>
                <FileText size={26} />
                <strong>{title}</strong>
                <small>{subtitle}</small>
              </button>
            ))}
          </div>
          <div className="panel-grid two">
            <article className="panel dashboard-detail">
              <div className="panel-title-row">
                <h2>Pending Orders</h2>
                <button className="mini-button" type="button" onClick={() => setLayer("sales")}>Open</button>
              </div>
              <CrmOrderList state={state} rows={pendingOrders.slice(0, 20)} />
            </article>
            <article className="panel dashboard-detail">
              <div className="panel-title-row">
                <h2>Follow-ups</h2>
                <button className="mini-button" type="button" onClick={() => setLayer("sms")}>Open</button>
              </div>
              <CrmCommunicationList rows={pendingFollowUps.slice(0, 20)} />
            </article>
          </div>
        </>
      ) : null}
      {layer === "masters" ? <CrmMastersLayer state={state} updateState={updateState} user={user} /> : null}
      {layer === "products" ? (
        <CrmProductsLayer state={state} updateState={updateState} user={user} />
      ) : null}
      {layer === "inventory" ? (
        <CrmPlaceholderGroup
          title="Inventory"
          subtitle="Inventory module structure is ready. Existing stock/product controls remain protected by permissions."
          items={CRM_PLACEHOLDER_GROUPS.inventory}
          extra={<CrmInventoryLayer state={state} updateState={updateState} user={user} />}
        />
      ) : null}
      {layer === "purchase" ? <CrmPurchaseLayer state={state} updateState={updateState} user={user} /> : null}
      {layer === "sales" ? <OpticalOrdersPanel state={state} updateState={updateState} employees={employees} user={user} addAudit={addAudit} /> : null}
      {layer === "customer" ? <CrmCustomersLayer state={state} updateState={updateState} customers={scopedCustomers} orders={scopedOrders} /> : null}
      {layer === "reports" ? <CrmReportsLayer state={state} orders={scopedOrders} customers={scopedCustomers} /> : null}
      {layer === "sms" ? <CrmComingSoonLayer title="SMS" items={["Order-ready messages", "Payment reminders", "Review requests", "Customer messages", "Internal communication"]} /> : null}
      {layer === "prescription" ? (
        <CrmPlaceholderGroup
          title="Prescription"
          subtitle="Prescription module structure is ready. Current add-prescription and appointment screens remain available below."
          items={CRM_PLACEHOLDER_GROUPS.prescription}
          extra={
            <div className="panel-grid two">
              <CrmPrescriptionLayer state={state} updateState={updateState} customers={scopedCustomers} />
              <CrmAppointmentLayer state={state} updateState={updateState} customers={scopedCustomers} />
            </div>
          }
        />
      ) : null}
      {layer === "account" ? <CrmPlaceholderGroup title="Account" subtitle="Accounting structure is ready for ledgers, vouchers, receivables and payables." items={CRM_PLACEHOLDER_GROUPS.account} /> : null}
    </section>
  );
}

function CrmPlaceholderGroup({ title, subtitle, items, notice, extra }) {
  return (
    <div className="crm-placeholder-stack">
      <article className="panel">
        <div className="panel-title-row">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
        </div>
        {notice ? <p className="notice">{notice}</p> : null}
        <div className="module-tile-grid crm-submodule-grid">
          {items.map((item) => (
            <div className="module-tile-card crm-submodule-card" key={item}>
              <FileText size={22} />
              <strong>{item}</strong>
              <small>Module setup in progress</small>
            </div>
          ))}
        </div>
      </article>
      {extra || null}
    </div>
  );
}

function CrmComingSoonLayer({ title, items }) {
  return (
    <article className="panel">
      <h2>{title}</h2>
      <p>This module structure is ready. Detailed screens will be built in the next CRM phase.</p>
      <div className="module-tile-grid crm-submodule-grid">
        {items.map((item) => (
          <div className="module-tile-card crm-submodule-card" key={item}>
            <FileText size={22} />
            <strong>{item}</strong>
            <small>Coming Soon</small>
          </div>
        ))}
      </div>
    </article>
  );
}

function CrmOrderList({ state, rows }) {
  return (
    <DetailList
      rows={rows}
      getTitle={(row) => `${row.orderNumber} · ${row.customer?.name || row.customerName || "Customer"}`}
      getMeta={(row) => `${row.storeName || "-"} · ${row.salespersonName || "-"} · ${opticalMoney(row.payment?.totalAmount || 0)}`}
      getStatus={(row) => computedOrderBalance(row) > 0 ? "DUE" : "PAID"}
      renderDetails={(row) => (
        <div className="detail-fields">
          <h3>{row.orderNumber}</h3>
          <p><strong>Customer:</strong> {row.customer?.name || "-"}</p>
          <p><strong>Phone:</strong> {row.customer?.phone || "-"}</p>
          <p><strong>Store:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || row.storeName || "-"}</p>
          <p><strong>Total:</strong> {opticalMoney(row.payment?.totalAmount || 0)}</p>
          <p><strong>Discount:</strong> {opticalMoney(row.payment?.discountAmount || 0)}</p>
          <p><strong>Collected:</strong> {opticalMoney(orderCollection(row))}</p>
          <p><strong>Balance:</strong> {opticalMoney(computedOrderBalance(row))}</p>
        </div>
      )}
    />
  );
}

function CrmCommunicationList({ rows }) {
  return (
    <DetailList
      rows={rows}
      getTitle={(row) => row.customerName || row.title || "Follow-up"}
      getMeta={(row) => `${row.messageType || "Follow-up"} · ${row.followUpDate || row.createdAt?.slice(0, 10) || "-"}`}
      getStatus={(row) => row.status || "pending"}
      renderDetails={(row) => (
        <div className="detail-fields">
          <h3>{row.customerName || "Communication"}</h3>
          <p><strong>Phone:</strong> {row.customerPhone || "-"}</p>
          <p><strong>Type:</strong> {row.messageType || "-"}</p>
          <p><strong>Follow-up:</strong> {row.followUpDate || "-"}</p>
          <p><strong>Notes:</strong> {row.notes || "-"}</p>
        </div>
      )}
    />
  );
}

function CrmMastersLayer({ state, updateState, user }) {
  const [active, setActive] = useState("suppliers");
  const [message, setMessage] = useState("");
  const canManage = hasUserPermission(user, "can_manage_roles") || hasUserPermission(user, "can_manage_users") || user.role === "super_admin";
  const [supplierForm, setSupplierForm] = useState({
    supplierName: "",
    supplierType: "Multiple",
    contactPerson: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    paymentTerms: "",
    creditCycle: "",
    defaultHsn: "",
    defaultGstRate: 18,
    active: true
  });
  const [categoryForm, setCategoryForm] = useState({
    categoryName: "",
    categoryCode: "",
    gstRate: 18,
    hsnCode: "",
    barcodeRequired: false,
    batchRequired: false,
    expiryRequired: false,
    powerRequired: false,
    active: true
  });
  const [brandForm, setBrandForm] = useState({ brandName: "", categoryName: "", supplierId: "", active: true });
  const [gstForm, setGstForm] = useState({ hsnCode: "", description: "", categoryName: "", gstRate: 18, cgstRate: 9, sgstRate: 9, igstRate: 18, effectiveFrom: new Date().toISOString().slice(0, 10), active: true });
  const exportPrefix = filePrefix(state.settings);

  const saveMaster = async (tableName, stateKey, form, reset, label) => {
    setMessage("");
    try {
      if (!canManage) throw new Error("Only admin/owner can edit CRM masters.");
      const row = { id: uuid(tableName), ...form, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      const savedRow = await persistRequiredEntity(tableName, row, label);
      updateState((current) => ({ ...current, [stateKey]: upsertById(current[stateKey] || [], savedRow) }));
      reset();
      setMessage(`${label} saved successfully.`);
    } catch (error) {
      console.error(`${label} save failed`, error);
      setMessage(error.message || `${label} could not be saved.`);
    }
  };

  const seedDefaults = async () => {
    setMessage("");
    try {
      if (!canManage) throw new Error("Only admin/owner can seed CRM masters.");
      const now = new Date().toISOString();
      const existingSupplierNames = new Set((state.crmSuppliers || []).map((row) => String(row.supplierName || "").toLowerCase()));
      const suppliers = CRM_DEFAULT_SUPPLIERS
        .filter((name) => !existingSupplierNames.has(name.toLowerCase()))
        .map((name) => ({ id: uuid("crm-supplier"), supplierName: name, supplierType: "Multiple", active: true, createdAt: now, updatedAt: now }));
      const existingCategoryNames = new Set((state.crmProductCategories || []).map((row) => String(row.categoryName || "").toLowerCase()));
      const categories = CRM_DEFAULT_CATEGORIES
        .filter(([name]) => !existingCategoryNames.has(name.toLowerCase()))
        .map(([categoryName, categoryCode, gstRate, barcodeRequired, batchRequired, expiryRequired, powerRequired]) => ({
          id: uuid("crm-category"),
          categoryName,
          categoryCode,
          gstRate,
          hsnCode: "",
          barcodeRequired,
          batchRequired,
          expiryRequired,
          powerRequired,
          active: true,
          createdAt: now,
          updatedAt: now
        }));
      const existingBrandNames = new Set((state.crmBrands || []).map((row) => String(row.brandName || "").toLowerCase()));
      const brands = CRM_DEFAULT_BRANDS
        .filter((brandName) => !existingBrandNames.has(brandName.toLowerCase()))
        .map((brandName) => ({ id: uuid("crm-brand"), brandName, categoryName: "", supplierId: "", active: true, createdAt: now, updatedAt: now }));
      for (const row of suppliers) await persistOptionalEntity("crm_suppliers", row);
      for (const row of categories) await persistOptionalEntity("crm_product_categories", row);
      for (const row of brands) await persistOptionalEntity("crm_brands", row);
      updateState((current) => ({
        ...current,
        crmSuppliers: [...suppliers, ...(current.crmSuppliers || [])],
        crmProductCategories: [...categories, ...(current.crmProductCategories || [])],
        crmBrands: [...brands, ...(current.crmBrands || [])]
      }));
      setMessage("Default suppliers, categories and brands added without touching existing records.");
    } catch (error) {
      setMessage(error.message || "Default masters could not be added.");
    }
  };

  const masterTabs = [
    ["stores", "Branches / Stores"],
    ["suppliers", "Suppliers"],
    ["categories", "Categories"],
    ["brands", "Brands"],
    ["gst", "GST / HSN"]
  ];

  return (
    <div className="crm-placeholder-stack">
      <article className="panel">
        <div className="panel-title-row">
          <div>
            <h2>CRM Masters</h2>
            <p>Stores, vendors, product categories, brands and tax setup.</p>
          </div>
          <button className="secondary-button" type="button" onClick={seedDefaults}>Add default optical masters</button>
        </div>
        {message ? <p className="notice">{message}</p> : null}
        <div className="status-tabs crm-layer-tabs">
          {masterTabs.map(([key, label]) => <button key={key} className={active === key ? "active" : ""} type="button" onClick={() => setActive(key)}>{label}</button>)}
        </div>
      </article>

      {active === "stores" ? (
        <article className="panel dashboard-detail">
          <div className="panel-title-row">
            <h2>Branches / Stores</h2>
            <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-branches.csv`, state.stores || [], [
              ["Branch", (row) => row.name],
              ["Code", (row) => row.code],
              ["Address", (row) => row.address],
              ["Phone", (row) => row.phone],
              ["Active", (row) => row.active !== false ? "Yes" : "No"]
            ])}><FileSpreadsheet size={17} /> Branch sheet</button>
          </div>
          <DetailList
            rows={state.stores || []}
            getTitle={(row) => row.name}
            getMeta={(row) => `${row.code || "-"} · ${row.address || "-"}`}
            getStatus={(row) => row.active === false ? "Inactive" : "Active"}
            renderDetails={(row) => (
              <div className="detail-fields">
                <p><strong>Branch ID:</strong> {row.id}</p>
                <p><strong>Code:</strong> {row.code || "-"}</p>
                <p><strong>GSTIN:</strong> {row.gstin || "-"}</p>
                <p><strong>Phone:</strong> {row.phone || "-"}</p>
                <p><strong>Manager:</strong> {state.employees.find((employee) => employee.id === row.managerId)?.fullName || "-"}</p>
              </div>
            )}
          />
        </article>
      ) : null}

      {active === "suppliers" ? (
        <div className="panel-grid two">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Add supplier / vendor</h2>
            <p className="notice">Accounting ledger is disabled until proper vendor ledger is built. Supplier records are for selection/reference only.</p>
            <div className="form-grid">
              <Input label="Supplier name" value={supplierForm.supplierName} onChange={(supplierName) => setSupplierForm({ ...supplierForm, supplierName })} />
              <label>Type<select value={supplierForm.supplierType} onChange={(event) => setSupplierForm({ ...supplierForm, supplierType: event.target.value })}>{["Frame", "Sunglass", "Lens", "Contact Lens", "Solution", "Accessories", "Multiple"].map((item) => <option key={item}>{item}</option>)}</select></label>
              <Input label="Contact person" value={supplierForm.contactPerson} onChange={(contactPerson) => setSupplierForm({ ...supplierForm, contactPerson })} />
              <Input label="Phone" type="tel" inputMode="numeric" value={supplierForm.phone} onChange={(phone) => setSupplierForm({ ...supplierForm, phone: phone.replace(/\D/g, "") })} />
              <Input label="Email" value={supplierForm.email} onChange={(email) => setSupplierForm({ ...supplierForm, email })} />
              <Input label="GSTIN" value={supplierForm.gstin} onChange={(gstin) => setSupplierForm({ ...supplierForm, gstin })} />
              <Input label="Payment terms" value={supplierForm.paymentTerms} onChange={(paymentTerms) => setSupplierForm({ ...supplierForm, paymentTerms })} />
              <Input label="Credit cycle" value={supplierForm.creditCycle} onChange={(creditCycle) => setSupplierForm({ ...supplierForm, creditCycle })} />
            </div>
            <textarea placeholder="Address" value={supplierForm.address} onChange={(event) => setSupplierForm({ ...supplierForm, address: event.target.value })} />
            <button className="primary-button" type="button" onClick={() => saveMaster("crm_suppliers", "crmSuppliers", supplierForm, () => setSupplierForm({ ...supplierForm, supplierName: "", contactPerson: "", phone: "", email: "", gstin: "", address: "" }), "Supplier")}>Save supplier</button>
          </form>
          <MasterList title="Suppliers" rows={state.crmSuppliers || []} exportPrefix={exportPrefix} filename="suppliers" columns={[["Supplier", (row) => row.supplierName], ["Type", (row) => row.supplierType], ["Phone", (row) => row.phone], ["GSTIN", (row) => row.gstin]]} getTitle={(row) => row.supplierName} getMeta={(row) => `${row.supplierType || "-"} · ${row.phone || "-"}`} />
        </div>
      ) : null}

      {active === "categories" ? (
        <div className="panel-grid two">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Add category</h2>
            <div className="form-grid">
              <Input label="Category name" value={categoryForm.categoryName} onChange={(categoryName) => setCategoryForm({ ...categoryForm, categoryName })} />
              <Input label="Code" value={categoryForm.categoryCode} onChange={(categoryCode) => setCategoryForm({ ...categoryForm, categoryCode })} />
              <Input label="HSN/SAC" value={categoryForm.hsnCode} onChange={(hsnCode) => setCategoryForm({ ...categoryForm, hsnCode })} />
              <Input label="GST rate" type="number" value={categoryForm.gstRate} onChange={(gstRate) => setCategoryForm({ ...categoryForm, gstRate })} />
            </div>
            <div className="checkbox-grid">
              {["barcodeRequired", "batchRequired", "expiryRequired", "powerRequired"].map((key) => (
                <label key={key}><input type="checkbox" checked={Boolean(categoryForm[key])} onChange={(event) => setCategoryForm({ ...categoryForm, [key]: event.target.checked })} /> {key.replace(/([A-Z])/g, " $1")}</label>
              ))}
            </div>
            <button className="primary-button" type="button" onClick={() => saveMaster("crm_product_categories", "crmProductCategories", categoryForm, () => setCategoryForm({ ...categoryForm, categoryName: "", categoryCode: "", hsnCode: "" }), "Category")}>Save category</button>
          </form>
          <MasterList title="Categories" rows={state.crmProductCategories || []} exportPrefix={exportPrefix} filename="categories" columns={[["Category", (row) => row.categoryName], ["Code", (row) => row.categoryCode], ["GST", (row) => row.gstRate], ["HSN", (row) => row.hsnCode]]} getTitle={(row) => row.categoryName} getMeta={(row) => `${row.categoryCode || "-"} · GST ${row.gstRate || 0}%`} />
        </div>
      ) : null}

      {active === "brands" ? (
        <div className="panel-grid two">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Add brand</h2>
            <div className="form-grid">
              <Input label="Brand name" value={brandForm.brandName} onChange={(brandName) => setBrandForm({ ...brandForm, brandName })} />
              <label>Category<select value={brandForm.categoryName} onChange={(event) => setBrandForm({ ...brandForm, categoryName: event.target.value })}><option value="">Any</option>{(state.crmProductCategories || []).map((row) => <option key={row.id}>{row.categoryName}</option>)}</select></label>
              <label>Supplier<select value={brandForm.supplierId} onChange={(event) => setBrandForm({ ...brandForm, supplierId: event.target.value })}><option value="">Optional</option>{(state.crmSuppliers || []).map((row) => <option key={row.id} value={row.id}>{row.supplierName}</option>)}</select></label>
            </div>
            <button className="primary-button" type="button" onClick={() => saveMaster("crm_brands", "crmBrands", brandForm, () => setBrandForm({ ...brandForm, brandName: "" }), "Brand")}>Save brand</button>
          </form>
          <MasterList title="Brands" rows={state.crmBrands || []} exportPrefix={exportPrefix} filename="brands" columns={[["Brand", (row) => row.brandName], ["Category", (row) => row.categoryName], ["Supplier", (row) => (state.crmSuppliers || []).find((supplier) => supplier.id === row.supplierId)?.supplierName || ""]]} getTitle={(row) => row.brandName} getMeta={(row) => row.categoryName || "Any category"} />
        </div>
      ) : null}

      {active === "gst" ? (
        <div className="panel-grid two">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Add GST / HSN</h2>
            <div className="form-grid">
              <Input label="HSN/SAC code" value={gstForm.hsnCode} onChange={(hsnCode) => setGstForm({ ...gstForm, hsnCode })} />
              <Input label="Description" value={gstForm.description} onChange={(description) => setGstForm({ ...gstForm, description })} />
              <Input label="Category" value={gstForm.categoryName} onChange={(categoryName) => setGstForm({ ...gstForm, categoryName })} />
              <Input label="GST rate" type="number" value={gstForm.gstRate} onChange={(gstRate) => setGstForm({ ...gstForm, gstRate, cgstRate: Number(gstRate || 0) / 2, sgstRate: Number(gstRate || 0) / 2, igstRate: Number(gstRate || 0) })} />
              <Input label="Effective from" type="date" value={gstForm.effectiveFrom} onChange={(effectiveFrom) => setGstForm({ ...gstForm, effectiveFrom })} />
            </div>
            <button className="primary-button" type="button" onClick={() => saveMaster("crm_gst_hsn_master", "crmGstHsnMaster", gstForm, () => setGstForm({ ...gstForm, hsnCode: "", description: "", categoryName: "" }), "GST / HSN row")}>Save GST / HSN</button>
          </form>
          <MasterList title="GST / HSN Master" rows={state.crmGstHsnMaster || []} exportPrefix={exportPrefix} filename="gst-hsn" columns={[["HSN", (row) => row.hsnCode], ["Description", (row) => row.description], ["Category", (row) => row.categoryName], ["GST", (row) => row.gstRate]]} getTitle={(row) => row.hsnCode || row.description} getMeta={(row) => `${row.categoryName || "-"} · GST ${row.gstRate || 0}%`} />
        </div>
      ) : null}
    </div>
  );
}

function MasterList({ title, rows, exportPrefix, filename, columns, getTitle, getMeta }) {
  return (
    <article className="panel dashboard-detail">
      <div className="panel-title-row">
        <h2>{title}</h2>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-${filename}.csv`, rows, columns)}>
          <FileSpreadsheet size={17} /> Sheet
        </button>
      </div>
      <DetailList
        rows={rows}
        getTitle={getTitle}
        getMeta={getMeta}
        getStatus={(row) => row.active === false ? "Inactive" : "Active"}
        renderDetails={(row) => (
          <div className="detail-fields">
            {columns.map(([label, render]) => <p key={label}><strong>{label}:</strong> {render(row) || "-"}</p>)}
          </div>
        )}
      />
    </article>
  );
}

function DynamicProductFields({ productType, form, setForm }) {
  const textField = (label, key) => <Input label={label} value={form[key] || ""} onChange={(value) => setForm({ ...form, [key]: value })} />;
  if (["Frame", "Sunglasses"].includes(productType)) {
    return (
      <OpticalSection title={`${productType} details`} subtitle="Model, colour, size and fitting attributes">
        <div className="form-grid">
          {textField("Model", "model")}
          {textField("Colour", "colour")}
          {textField("Size", "size")}
          {textField("Temple size", "templeSize")}
          {textField("Bridge size", "bridgeSize")}
          {textField("Lens width", "lensWidth")}
          {textField("Material", "material")}
          {textField("Shape", "shape")}
          {textField("Gender", "gender")}
          {textField("Rim type", "rimType")}
        </div>
      </OpticalSection>
    );
  }
  if (productType === "Lens") {
    return (
      <OpticalSection title="Spectacle lens details" subtitle="Lens type, index, coating and design">
        <div className="form-grid">
          {textField("Lens type", "lensType")}
          {textField("Index", "lensIndex")}
          {textField("Coating", "coating")}
          {textField("Design", "design")}
          {textField("Corridor", "corridor")}
          {textField("Tint / photochromic", "tint")}
          {textField("Bluecut", "bluecut")}
          {textField("Drive safe", "driveSafe")}
          {textField("Power details", "powerDetails")}
        </div>
      </OpticalSection>
    );
  }
  if (productType === "Contact Lens") {
    return (
      <OpticalSection title="Contact lens details" subtitle="Power, pack, batch and expiry">
        <div className="form-grid">
          {textField("Lens type", "lensType")}
          {textField("Spherical / toric / multifocal", "contactLensType")}
          {textField("Base curve", "baseCurve")}
          {textField("Diameter", "diameter")}
          {textField("Power", "powerDetails")}
          {textField("Cylinder", "cylinder")}
          {textField("Axis", "axis")}
          {textField("ADD", "addPower")}
          {textField("Pack size", "packSize")}
          {textField("Batch no", "batchNo")}
          <Input label="Expiry date" type="date" value={form.expiryDate || ""} onChange={(expiryDate) => setForm({ ...form, expiryDate })} />
        </div>
      </OpticalSection>
    );
  }
  if (productType === "Solution") {
    return (
      <OpticalSection title="Solution details" subtitle="Size, batch and expiry">
        <div className="form-grid">
          {textField("ML / size", "size")}
          {textField("Batch no", "batchNo")}
          <Input label="Expiry date" type="date" value={form.expiryDate || ""} onChange={(expiryDate) => setForm({ ...form, expiryDate })} />
        </div>
      </OpticalSection>
    );
  }
  return null;
}

function GenericSearchSelect({
  label,
  value,
  onSelect,
  options = [],
  getKey = (row) => row.id,
  getLabel = (row) => row.name || row.productName || row.supplierName || row.orderNumber || "",
  getMeta = () => "",
  getSearchText = (row) => `${getLabel(row)} ${getMeta(row)}`,
  placeholder = "Type 2 letters to search",
  minChars = 2,
  disabled = false
}) {
  const selected = options.find((row) => String(getKey(row)) === String(value));
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const matches = normalizedQuery.length >= minChars
    ? options.filter((row) => getSearchText(row).toLowerCase().includes(normalizedQuery)).slice(0, 8)
    : [];
  return (
    <label className="search-select-field">
      {label}
      {selected ? (
        <span className="selected-search-pill">
          <strong>{getLabel(selected)}</strong>
          <small>{getMeta(selected)}</small>
          <button type="button" disabled={disabled} onClick={() => { onSelect(null); setQuery(""); }}>Clear</button>
        </span>
      ) : null}
      <input value={query} disabled={disabled} onChange={(event) => setQuery(event.target.value)} placeholder={placeholder} autoComplete="off" />
      {matches.length ? (
        <div className="search-select-results">
          {matches.map((row) => (
            <button type="button" key={getKey(row)} onClick={() => { onSelect(row); setQuery(""); }}>
              <strong>{getLabel(row)}</strong>
              <small>{getMeta(row)}</small>
            </button>
          ))}
        </div>
      ) : normalizedQuery.length >= minChars && !selected ? (
        <small className="helper-text">No master match. You can still type manually.</small>
      ) : null}
    </label>
  );
}

function ProductAutocomplete({ state, value, onSelect, label = "Search product master" }) {
  return (
    <GenericSearchSelect
      label={label}
      value={value}
      options={state.crmProducts || []}
      onSelect={onSelect}
      getLabel={(row) => row.productName || row.name || row.productCode || "Product"}
      getMeta={(row) => `${row.productType || row.categoryName || "-"} · ${row.barcode || row.productCode || "-"} · ${opticalMoney(row.sellingPrice || row.mrp || 0)}`}
      getSearchText={(row) => `${row.productName || row.name || ""} ${row.productCode || ""} ${row.barcode || ""} ${row.brandName || row.brand || ""} ${row.categoryName || ""}`}
    />
  );
}

function CustomerAutocomplete({ state, value, onSelect, label = "Search existing customer" }) {
  return (
    <GenericSearchSelect
      label={label}
      value={value}
      options={state.opticalCustomers || []}
      onSelect={onSelect}
      getLabel={(row) => row.name || row.customerName || row.walkInId || "Customer"}
      getMeta={(row) => `${row.phone || row.walkInId || "No phone"} · ${row.address || "-"}`}
      getSearchText={(row) => `${row.name || ""} ${row.customerName || ""} ${row.phone || ""} ${row.walkInId || ""} ${row.address || ""}`}
    />
  );
}

function VendorAutocomplete({ state, value, onSelect, label = "Search vendor / supplier" }) {
  return (
    <GenericSearchSelect
      label={label}
      value={value}
      options={state.crmSuppliers || []}
      onSelect={onSelect}
      getLabel={(row) => row.supplierName || row.vendorName || "Vendor"}
      getMeta={(row) => `${row.supplierType || "-"} · ${row.phone || row.gstin || "-"}`}
      getSearchText={(row) => `${row.supplierName || ""} ${row.vendorName || ""} ${row.phone || ""} ${row.gstin || ""}`}
    />
  );
}

function BarcodeAutocomplete({ state, value, onSelect, label = "Search barcode" }) {
  return (
    <GenericSearchSelect
      label={label}
      value={value}
      options={state.crmProducts || []}
      onSelect={onSelect}
      getKey={(row) => row.barcode || row.id}
      getLabel={(row) => row.barcode || row.productCode || "Barcode"}
      getMeta={(row) => row.productName || row.name || ""}
      getSearchText={(row) => `${row.barcode || ""} ${row.productCode || ""} ${row.productName || row.name || ""}`}
    />
  );
}

function OrderAutocomplete({ state, value, onSelect, label = "Search order" }) {
  return (
    <GenericSearchSelect
      label={label}
      value={value}
      options={state.opticalFastSlips || []}
      onSelect={onSelect}
      getLabel={(row) => row.orderNumber || row.gcNumber || "Order"}
      getMeta={(row) => `${row.customer?.name || row.customerName || "Customer"} · ${row.storeName || "-"}`}
      getSearchText={(row) => `${row.orderNumber || ""} ${row.gcNumber || ""} ${row.customer?.name || row.customerName || ""} ${row.customer?.phone || ""}`}
    />
  );
}

function CrmProductsLayer({ state, updateState, user }) {
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const canViewProducts = hasUserPermission(user, "can_view_products") || hasAppPermission(user, "optical");
  const canAddProducts = hasUserPermission(user, "can_add_products");
  const canChangePrice = hasUserPermission(user, "can_change_product_price");
  const canViewCost = hasUserPermission(user, "can_view_cost_price");
  const canViewMargin = hasUserPermission(user, "can_view_margin");
  const [form, setForm] = useState({
    productCode: "",
    productType: "Frame",
    categoryName: "",
    brandName: "",
    storeId: state.stores[0]?.id || "",
    model: "",
    productName: "",
    name: "",
    barcode: "",
    hsnCode: "",
    gstRate: 18,
    mrp: "",
    purchasePrice: "",
    costPrice: "",
    sellingPrice: "",
    supplierId: "",
    active: true,
    stockQty: 0,
    reorderLevel: 1,
    notes: ""
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const exportPrefix = filePrefix(state.settings);
  const products = (state.crmProducts || []).filter((row) => {
    const text = `${row.productCode || ""} ${row.name || ""} ${row.productName || ""} ${row.brandName || row.brand || ""} ${row.barcode || ""}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });
  const handlePhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const id = form.id || uuid("crm-product-photo");
      const result = await uploadCompressedImage({
        file,
        module: "products",
        recordType: "product",
        recordId: id,
        storeId: form.storeId || state.stores[0]?.id || "",
        fileType: "product_photo",
        uploadedBy: user.id,
        maxWidth: 1200,
        quality: 0.66
      });
      setForm((current) => ({ ...current, productPhoto: result.storagePath || result.path || result.url }));
    } catch (error) {
      setMessage(error.message || "Product photo could not be uploaded.");
    } finally {
      setUploadingPhoto(false);
    }
  };
  const saveProduct = async () => {
    setMessage("");
    try {
      if (!canAddProducts) throw new Error("You do not have permission to add products.");
      const productName = form.productName.trim() || form.name.trim();
      if (!productName) throw new Error("Product name is required.");
      const now = new Date().toISOString();
      const row = {
        id: uuid("crm-product"),
        ...form,
        name: productName,
        productName,
        categoryName: form.categoryName,
        brandName: form.brandName,
        gstRate: Number(form.gstRate || 0),
        mrp: Number(form.mrp || 0),
        purchasePrice: Number(form.purchasePrice || form.costPrice || 0),
        costPrice: Number(form.purchasePrice || form.costPrice || 0),
        sellingPrice: Number(form.sellingPrice || form.mrp || 0),
        stockQty: Number(form.stockQty || 0),
        reorderLevel: Number(form.reorderLevel || 0),
        storeId: form.storeId || state.stores[0]?.id || "",
        createdAt: now,
        updatedAt: now
      };
      const result = await persistOptionalEntity("crm_products", row);
      const stockRow = {
        id: uuid("crm-stock-level"),
        productId: row.id,
        productName: row.productName,
        storeId: form.storeId || state.stores[0]?.id || "",
        barcode: row.barcode,
        batchNo: row.batchNo || "",
        expiryDate: row.expiryDate || null,
        quantity: row.stockQty,
        reservedQty: 0,
        availableQty: row.stockQty,
        costPrice: row.costPrice,
        mrp: row.mrp,
        supplierId: row.supplierId || null,
        status: "In Stock",
        createdAt: now,
        updatedAt: now
      };
      await persistOptionalEntity("crm_inventory_stock", stockRow);
      updateState((current) => ({
        ...current,
        crmProducts: upsertById(current.crmProducts || [], row),
        crmInventoryStock: upsertById(current.crmInventoryStock || [], stockRow)
      }));
      setForm({ ...form, productCode: "", productName: "", name: "", model: "", barcode: "", stockQty: 0, purchasePrice: "", costPrice: "", sellingPrice: "", mrp: "", notes: "", productPhoto: "" });
      setMessage(result.reason || "Product saved.");
    } catch (error) {
      setMessage(error.message || "Product could not be saved.");
    }
  };

  if (!canViewProducts) return <article className="panel"><h2>Products</h2><p className="notice">You do not have permission to view product master.</p></article>;

  return (
    <div className="crm-placeholder-stack">
      <CrmPlaceholderGroup title="Products" subtitle="Dynamic product master by optical product type." items={CRM_PLACEHOLDER_GROUPS.products} />
      <div className="panel-grid two">
        {canAddProducts ? (
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Add product</h2>
            {message ? <p className="notice">{message}</p> : null}
            <div className="form-grid">
              <Input label="SKU / Product code" value={form.productCode} onChange={(productCode) => setForm({ ...form, productCode })} />
              <label>Product type<select value={form.productType} onChange={(event) => setForm({ ...form, productType: event.target.value })}>{CRM_PRODUCT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label>Category<select value={form.categoryName} onChange={(event) => {
                const category = (state.crmProductCategories || []).find((row) => row.categoryName === event.target.value);
                setForm({ ...form, categoryName: event.target.value, gstRate: category?.gstRate ?? form.gstRate, hsnCode: category?.hsnCode || form.hsnCode });
              }}><option value="">Select</option>{(state.crmProductCategories || []).map((row) => <option key={row.id}>{row.categoryName}</option>)}</select></label>
              <label>Brand<select value={form.brandName} onChange={(event) => setForm({ ...form, brandName: event.target.value })}><option value="">Select</option>{(state.crmBrands || []).map((row) => <option key={row.id}>{row.brandName}</option>)}</select></label>
              <label>Opening stock branch<select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
              <Input label="Product name" value={form.productName} onChange={(productName) => setForm({ ...form, productName })} />
              <Input label="Barcode (optional)" value={form.barcode} onChange={(barcode) => setForm({ ...form, barcode })} />
              <Input label="HSN" value={form.hsnCode} onChange={(hsnCode) => setForm({ ...form, hsnCode })} />
              <Input label="GST rate" type="number" value={form.gstRate} onChange={(gstRate) => setForm({ ...form, gstRate })} />
              <Input label="MRP" type="number" value={form.mrp} onChange={(mrp) => setForm({ ...form, mrp })} />
              {canViewCost ? <Input label="Purchase price" type="number" value={form.purchasePrice} onChange={(purchasePrice) => setForm({ ...form, purchasePrice, costPrice: purchasePrice })} /> : null}
              {canChangePrice ? <Input label="Selling price" type="number" value={form.sellingPrice} onChange={(sellingPrice) => setForm({ ...form, sellingPrice })} /> : null}
              <label>Supplier<select value={form.supplierId} onChange={(event) => setForm({ ...form, supplierId: event.target.value })}><option value="">Optional</option>{(state.crmSuppliers || []).map((row) => <option key={row.id} value={row.id}>{row.supplierName}</option>)}</select></label>
              <Input label="Opening stock" type="number" value={form.stockQty} onChange={(stockQty) => setForm({ ...form, stockQty })} />
            </div>
            <DynamicProductFields productType={form.productType} form={form} setForm={setForm} />
            <OpticalSection title="Product photo" subtitle="Optional compressed upload">
              <input type="file" accept="image/*" onChange={(event) => handlePhoto(event.target.files?.[0])} />
              {uploadingPhoto ? <p className="helper-text">Uploading compressed photo...</p> : null}
              <PhotoPreview src={form.productPhoto} label="Product photo" onRemove={() => setForm({ ...form, productPhoto: "" })} />
            </OpticalSection>
            <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            <button className="primary-button" type="button" onClick={saveProduct}>Save product</button>
          </form>
        ) : (
          <article className="panel"><h2>Product master locked</h2><p className="notice">Only permitted users can add/edit products, prices and purchase values.</p></article>
        )}
        <article className="panel dashboard-detail">
          <div className="panel-title-row">
            <h2>Product Master</h2>
            <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-product-master.csv`, products, [
              ["Code", (row) => row.productCode],
              ["Type", (row) => row.productType],
              ["Category", (row) => row.categoryName || row.category],
              ["Brand", (row) => row.brandName || row.brand],
              ["Product", (row) => row.productName || row.name],
              ["Barcode", (row) => row.barcode],
              ["HSN", (row) => row.hsnCode],
              ["GST", (row) => row.gstRate],
              ["MRP", (row) => row.mrp],
              ...(canViewCost ? [["Purchase", (row) => row.purchasePrice || row.costPrice]] : []),
              ...(canChangePrice ? [["Selling", (row) => row.sellingPrice]] : []),
              ...(canViewMargin ? [["Margin", (row) => Number(row.sellingPrice || 0) - Number(row.purchasePrice || row.costPrice || 0)]] : [])
            ])}><FileSpreadsheet size={17} /> Product sheet</button>
          </div>
          <div className="staff-search-bar dashboard-search">
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search product, barcode, brand" />
          </div>
          <DetailList
            rows={products}
            getTitle={(row) => row.productName || row.name}
            getMeta={(row) => `${row.productType || "-"} · ${row.brandName || row.brand || "-"} · Stock ${row.stockQty || 0}`}
            getStatus={(row) => row.active === false ? "Inactive" : "Active"}
            renderDetails={(row) => (
              <div className="detail-fields">
                <PhotoPreview src={row.productPhoto} label="Product photo" />
                <p><strong>Code:</strong> {row.productCode || "-"}</p>
                <p><strong>Barcode:</strong> {row.barcode || "-"}</p>
                <p><strong>Category:</strong> {row.categoryName || row.category || "-"}</p>
                <p><strong>HSN/GST:</strong> {row.hsnCode || "-"} / {row.gstRate || 0}%</p>
                <p><strong>MRP:</strong> {opticalMoney(row.mrp || 0)}</p>
                {canViewCost ? <p><strong>Purchase:</strong> {opticalMoney(row.purchasePrice || row.costPrice || 0)}</p> : null}
                {canChangePrice ? <p><strong>Selling:</strong> {opticalMoney(row.sellingPrice || 0)}</p> : null}
              </div>
            )}
          />
        </article>
      </div>
    </div>
  );
}

function upsertById(rows, row) {
  return rows.some((item) => item.id === row.id)
    ? rows.map((item) => (item.id === row.id ? row : item))
    : [row, ...rows];
}

function CrmCustomersLayer({ state, updateState, customers, orders }) {
  const [query, setQuery] = useState("");
  const exportPrefix = filePrefix(state.settings);
  const rows = customers.filter((customer) => {
    const text = `${customer.name || ""} ${customer.phone || ""} ${customer.walkInId || ""}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });
  return (
    <article className="panel dashboard-detail">
      <div className="panel-title-row">
        <h2>Customer Directory</h2>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-customers.csv`, rows, [
          ["Name", (row) => row.name],
          ["Phone", (row) => row.phone || row.walkInId],
          ["Email", (row) => row.email],
          ["Address", (row) => row.address],
          ["Orders", (row) => orders.filter((order) => order.customerId === row.id).length]
        ])}>
          <FileSpreadsheet size={17} /> Customers sheet
        </button>
      </div>
      <div className="staff-search-bar dashboard-search">
        <Search size={17} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search customer" />
      </div>
      <DetailList
        rows={rows}
        getTitle={(row) => row.name || "Walk-in customer"}
        getMeta={(row) => `${row.phone || row.walkInId || "No phone"} · ${orders.filter((order) => order.customerId === row.id).length} orders`}
        getStatus={(row) => orders.filter((order) => order.customerId === row.id).length}
        renderDetails={(row) => {
          const customerOrders = orders.filter((order) => order.customerId === row.id);
          return (
            <div className="detail-fields">
              <h3>{row.name || "Customer"}</h3>
              <p><strong>Phone:</strong> {row.phone || "-"}</p>
              <p><strong>Walk-in ID:</strong> {row.walkInId || "-"}</p>
              <p><strong>Email:</strong> {row.email || "-"}</p>
              <p><strong>Address:</strong> {row.address || "-"}</p>
              <p><strong>Total orders:</strong> {customerOrders.length}</p>
              <p><strong>Total value:</strong> {opticalMoney(customerOrders.reduce((sum, order) => sum + Number(order.payment?.totalAmount || 0), 0))}</p>
            </div>
          );
        }}
      />
    </article>
  );
}

function CrmPaymentsLayer({ state, orders }) {
  const exportPrefix = filePrefix(state.settings);
  const dueRows = (orders || []).filter((order) => computedOrderBalance(order) > 0);
  return (
    <article className="panel dashboard-detail">
      <div className="panel-title-row">
        <div>
          <h2>Payments</h2>
          <p>Delivered/payment pending and customer balances.</p>
        </div>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-payment-dues.csv`, dueRows, [
          ["Order", (row) => row.orderNumber],
          ["Date", (row) => row.date],
          ["Customer", (row) => row.customer?.name || row.customerName],
          ["Phone", (row) => row.customer?.phone],
          ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || row.storeName],
          ["Total", (row) => row.payment?.totalAmount || 0],
          ["Collected", (row) => orderCollection(row)],
          ["Balance", (row) => computedOrderBalance(row)],
          ["Delivery", (row) => orderDeliveryStatus(row, state)]
        ])}>
          <FileSpreadsheet size={17} /> Payment sheet
        </button>
      </div>
      <DetailList
        rows={dueRows}
        getTitle={(row) => `${row.customer?.name || row.customerName || "Customer"} · ${row.orderNumber}`}
        getMeta={(row) => `${state.stores.find((store) => store.id === row.storeId)?.code || row.storeName || "-"} · Total ${opticalMoney(row.payment?.totalAmount || 0)}`}
        getStatus={(row) => opticalMoney(computedOrderBalance(row))}
        renderDetails={(row) => (
          <div className="detail-fields">
            <h3>{row.orderNumber}</h3>
            <p><strong>Customer:</strong> {row.customer?.name || row.customerName || "-"}</p>
            <p><strong>Phone:</strong> {row.customer?.phone || "-"}</p>
            <p><strong>Total:</strong> {opticalMoney(row.payment?.totalAmount || 0)}</p>
            <p><strong>Collected:</strong> {opticalMoney(orderCollection(row))}</p>
            <p><strong>Balance:</strong> {opticalMoney(computedOrderBalance(row))}</p>
            <p><strong>Payment:</strong> {orderPaymentStatus(row)}</p>
            <p><strong>Delivery:</strong> {orderDeliveryStatus(row, state)}</p>
            {orderPaymentHistory(row).length ? (
              <div className="audit-mini-list">
                <strong>Payment history</strong>
                {orderPaymentHistory(row).map((paymentRow) => (
                  <small key={paymentRow.id || paymentRow.collectedAt}>
                    {opticalMoney(paymentRow.amount)} · {paymentRow.paymentMode || "-"} · {paymentRow.receivedBy || "-"} · {paymentRow.collectedAt ? new Date(paymentRow.collectedAt).toLocaleString("en-IN") : "-"}
                  </small>
                ))}
              </div>
            ) : null}
          </div>
        )}
      />
    </article>
  );
}

const STOCK_AUDIT_CATEGORIES = ["frame", "sunglasses", "contact_lens", "solution", "accessory"];
const STOCK_AUDIT_ITEM_STATUSES = ["available", "sold", "transferred", "damaged", "missing", "inactive"];
const STOCK_AUDIT_VERIFY_STATUSES = [
  "Found",
  "Missing",
  "Damaged",
  "Sold but not updated",
  "Transferred but not updated",
  "Wrong barcode",
  "Needs admin review"
];
const STOCK_AUDIT_MISMATCH_STATUSES = new Set(STOCK_AUDIT_VERIFY_STATUSES.filter((status) => status !== "Found"));

function stockAuditStoreIds(state, user, canManageAll) {
  if (canManageAll || user?.role === "super_admin" || user?.permissions?.all) return state.stores.map((store) => store.id);
  const employee = userEmployee(state, user);
  return Array.from(new Set([...(user?.storeIds || []), employee?.storeId].filter(Boolean)));
}

function stockAuditAgeLabel(value) {
  if (!value) return "Never";
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24)));
  if (!Number.isFinite(days)) return "Never";
  return days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"} ago`;
}

function stockAuditSelectItems(items, targetCount) {
  const selected = [];
  const selectedIds = new Set();
  const addFrom = (rows, bucket, limit) => {
    let added = 0;
    for (const item of rows) {
      if (selectedIds.has(item.id) || added >= limit || selected.length >= targetCount) continue;
      selected.push({ item, bucket });
      selectedIds.add(item.id);
      added += 1;
    }
    return added;
  };
  const recentCutoff = Date.now() - 1000 * 60 * 60 * 24 * 21;
  const highRisk = items.filter((item) => STOCK_AUDIT_MISMATCH_STATUSES.has(item.lastAuditStatus));
  const normal = items
    .filter((item) => !STOCK_AUDIT_MISMATCH_STATUSES.has(item.lastAuditStatus))
    .filter((item) => !item.lastAuditedAt || new Date(item.lastAuditedAt).getTime() < recentCutoff);
  const fallback = items.filter((item) => !selectedIds.has(item.id));
  const target = Math.min(Number(targetCount || 20), items.length);
  const above5000Target = Math.round(target * 0.2);
  const above3000Target = Math.round(target * 0.3);
  const notes = [];
  addFrom(highRisk, "high_risk", target);
  const above5000Added = addFrom(normal.filter((item) => Number(item.mrp || 0) > 5000), "above_5000", above5000Target);
  if (above5000Added < above5000Target) notes.push(`Above 5000 short by ${above5000Target - above5000Added}`);
  const above3000Added = addFrom(normal.filter((item) => Number(item.mrp || 0) > 3000), "above_3000", above3000Target + (above5000Target - above5000Added));
  if (above3000Added < above3000Target) notes.push(`Above 3000 short by ${above3000Target - above3000Added}`);
  addFrom(normal, "random", target - selected.length);
  addFrom(fallback, "fallback", target - selected.length);
  return {
    selected,
    note: notes.length ? `${notes.join("; ")}. Balance filled from available stock.` : "Default mix applied."
  };
}

function StockAuditModule({ state, updateState, user }) {
  const [tab, setTab] = useState("dashboard");
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [verifyDrafts, setVerifyDrafts] = useState({});
  const [csvPreview, setCsvPreview] = useState(null);
  const canManageAll =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    hasUserPermission(user, "can_manage_stock_audit") ||
    hasUserPermission(user, "can_view_all_stock_audit") ||
    hasUserPermission(user, "can_review_stock_audit");
  const canCreateAudit =
    canManageAll ||
    hasUserPermission(user, "can_create_stock_audit") ||
    hasUserPermission(user, "can_manage_store_stock_audit");
  const canReview = canManageAll || hasUserPermission(user, "can_review_stock_audit");
  const canImportStock = canManageAll || hasUserPermission(user, "can_create_stock_inward") || hasUserPermission(user, "can_add_products");
  const visibleStoreIds = stockAuditStoreIds(state, user, canManageAll);
  const visibleStores = state.stores.filter((store) => visibleStoreIds.includes(store.id));
  const firstStoreId = visibleStores[0]?.id || state.stores[0]?.id || "";
  const [manualForm, setManualForm] = useState({
    storeId: firstStoreId,
    barcode: "",
    articleName: "",
    brand: "",
    category: "frame",
    mrp: "",
    status: "available"
  });
  const [weeklyForm, setWeeklyForm] = useState({
    storeId: firstStoreId,
    targetCount: 20,
    assignedTo: user?.id || "",
    deadlineAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 2).toISOString().slice(0, 16)
  });
  const [hardForm, setHardForm] = useState({
    storeId: firstStoreId,
    assignedTo: user?.id || "",
    deadlineAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString().slice(0, 16)
  });
  const stockItems = state.stockItems || [];
  const auditSessions = state.auditSessions || [];
  const auditSessionItems = state.auditSessionItems || [];
  const auditPhotos = state.auditPhotos || [];
  const auditReviews = state.auditReviews || [];
  const storeName = (storeId) => state.stores.find((store) => store.id === storeId)?.name || "Store";
  const staffName = (userId) => state.users.find((item) => item.id === userId)?.name || state.employees.find((item) => item.id === userId)?.fullName || userId || "-";
  const visibleStock = stockItems.filter((item) => visibleStoreIds.includes(item.currentStoreId || item.storeId));
  const visibleSessions = auditSessions.filter((session) =>
    visibleStoreIds.includes(session.storeId) ||
    session.assignedTo === user?.id ||
    canManageAll
  );
  const selectedSession = visibleSessions.find((session) => session.id === selectedSessionId) || visibleSessions[0] || null;
  const selectedItems = auditSessionItems.filter((item) => item.auditSessionId === selectedSession?.id);
  const filteredStock = visibleStock.filter((item) => {
    const text = `${item.barcode || ""} ${item.articleName || ""} ${item.brand || ""} ${item.category || ""} ${storeName(item.currentStoreId || item.storeId)}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });
  const reviewRows = auditSessionItems
    .filter((item) => {
      const session = auditSessions.find((row) => row.id === item.auditSessionId);
      if (!session || (!canManageAll && !visibleStoreIds.includes(session.storeId))) return false;
      const isMismatch = STOCK_AUDIT_MISMATCH_STATUSES.has(item.verificationStatus);
      if (reviewFilter === "all") return isMismatch || item.reviewStatus !== "approved";
      return isMismatch && (item.reviewStatus || "pending") === reviewFilter;
    })
    .map((item) => ({ ...item, session: auditSessions.find((row) => row.id === item.auditSessionId) }));
  const sessionStats = (session) => {
    const rows = auditSessionItems.filter((item) => item.auditSessionId === session.id);
    return {
      total: rows.length,
      done: rows.filter((item) => item.verificationStatus && item.verificationStatus !== "pending").length,
      found: rows.filter((item) => item.verificationStatus === "Found").length,
      mismatch: rows.filter((item) => STOCK_AUDIT_MISMATCH_STATUSES.has(item.verificationStatus)).length,
      pendingReview: rows.filter((item) => STOCK_AUDIT_MISMATCH_STATUSES.has(item.verificationStatus) && (item.reviewStatus || "pending") === "pending").length
    };
  };
  const logAuditAction = async (row) => {
    const payload = { id: uuid("audit-activity"), userId: user?.id || "", createdAt: new Date().toISOString(), ...row };
    await persistOptionalEntity("audit_activity_logs", payload).catch(() => null);
    updateState((current) => ({ ...current, auditActivityLogs: upsertById(current.auditActivityLogs || [], payload) }));
  };
  const saveStockItem = async () => {
    setMessage("");
    try {
      if (!canImportStock) throw new Error("You do not have permission to add stock items.");
      if (!manualForm.barcode.trim()) throw new Error("Barcode is required.");
      if (!manualForm.articleName.trim()) throw new Error("Article name is required.");
      if (stockItems.some((item) => item.barcode === manualForm.barcode.trim())) throw new Error("This barcode already exists.");
      const now = new Date().toISOString();
      const row = {
        id: uuid("stock-item"),
        storeId: manualForm.storeId,
        currentStoreId: manualForm.storeId,
        barcode: manualForm.barcode.trim(),
        articleName: manualForm.articleName.trim(),
        brand: manualForm.brand.trim(),
        category: manualForm.category,
        mrp: Number(manualForm.mrp || 0),
        status: manualForm.status,
        lastAuditedAt: null,
        lastAuditStatus: "",
        createdAt: now,
        updatedAt: now
      };
      await persistOptionalEntity("stock_items", row);
      updateState((current) => ({ ...current, stockItems: upsertById(current.stockItems || [], row) }));
      setManualForm({ ...manualForm, barcode: "", articleName: "", brand: "", mrp: "" });
      setMessage("Stock item saved.");
    } catch (error) {
      setMessage(error.message || "Stock item could not be saved.");
    }
  };
  const previewStockCsv = async (file) => {
    if (!file) return;
    setMessage("");
    try {
      if (!canImportStock) throw new Error("You do not have permission to import stock.");
      const rows = simpleCsvRows(await file.text());
      const headers = (rows[0] || []).map((cell) => cell.toLowerCase().replace(/[^a-z0-9]+/g, "_"));
      const valueAt = (cells, names) => {
        const index = headers.findIndex((header) => names.includes(header));
        return index >= 0 ? cells[index] || "" : "";
      };
      const mapped = rows.slice(1).map((cells, index) => {
        const storeCode = valueAt(cells, ["store_code", "store", "branch"]);
        const store = state.stores.find((item) => item.code === storeCode || item.name === storeCode) || visibleStores[0] || {};
        return {
          id: `preview-${index}`,
          barcode: valueAt(cells, ["barcode", "bar_code"]),
          articleName: valueAt(cells, ["article_name", "article", "name", "product_name"]),
          brand: valueAt(cells, ["brand"]),
          category: valueAt(cells, ["category", "product_type"]) || "frame",
          mrp: Number(valueAt(cells, ["mrp", "retail_price", "selling_price"]) || 0),
          storeId: store.id || "",
          storeCode,
          status: valueAt(cells, ["status"]) || "available"
        };
      }).filter((row) => row.barcode || row.articleName);
      setCsvPreview({
        fileName: file.name,
        rows: mapped,
        duplicateRows: mapped.filter((row) => stockItems.some((item) => item.barcode === row.barcode)).length,
        missingRows: mapped.filter((row) => !row.barcode || !row.articleName || !row.storeId).length
      });
      setMessage("Stock CSV preview ready.");
    } catch (error) {
      setMessage(error.message || "CSV could not be read.");
    }
  };
  const confirmStockCsv = async () => {
    setMessage("");
    try {
      if (!csvPreview) throw new Error("No CSV preview found.");
      const now = new Date().toISOString();
      const existing = new Set(stockItems.map((item) => item.barcode));
      const rows = csvPreview.rows
        .filter((row) => row.barcode && row.articleName && row.storeId && !existing.has(row.barcode))
        .map((row) => ({
          id: uuid("stock-item"),
          storeId: row.storeId,
          currentStoreId: row.storeId,
          barcode: row.barcode,
          articleName: row.articleName,
          brand: row.brand,
          category: STOCK_AUDIT_CATEGORIES.includes(row.category) ? row.category : "frame",
          mrp: row.mrp,
          status: STOCK_AUDIT_ITEM_STATUSES.includes(row.status) ? row.status : "available",
          lastAuditedAt: null,
          lastAuditStatus: "",
          createdAt: now,
          updatedAt: now
        }));
      for (const row of rows) await persistOptionalEntity("stock_items", row);
      updateState((current) => ({ ...current, stockItems: [...rows, ...(current.stockItems || [])] }));
      setCsvPreview(null);
      setMessage(`Stock import completed. ${rows.length} item${rows.length === 1 ? "" : "s"} added.`);
    } catch (error) {
      setMessage(error.message || "Stock import could not be completed.");
    }
  };
  const createAuditSession = async (auditType) => {
    setMessage("");
    try {
      if (!canCreateAudit) throw new Error("You do not have permission to create audits.");
      const form = auditType === "weekly_stock_audit" ? weeklyForm : hardForm;
      if (!form.storeId) throw new Error("Select store.");
      const available = stockItems.filter((item) => (item.currentStoreId || item.storeId) === form.storeId && item.status === "available");
      if (!available.length) throw new Error("No available stock items found for this store.");
      const chosen = auditType === "weekly_stock_audit"
        ? stockAuditSelectItems(available, Number(form.targetCount || 20))
        : { selected: available.map((item) => ({ item, bucket: "hard_audit" })), note: "Hard audit includes every available item in this store." };
      const now = new Date().toISOString();
      const session = {
        id: uuid("audit-session"),
        storeId: form.storeId,
        auditType,
        status: "assigned",
        targetCount: chosen.selected.length,
        createdBy: user?.id || "",
        assignedTo: form.assignedTo || user?.id || "",
        deadlineAt: form.deadlineAt ? new Date(form.deadlineAt).toISOString() : null,
        completedAt: null,
        mixAdjustmentNote: chosen.note,
        createdAt: now
      };
      const sessionItems = chosen.selected.map(({ item, bucket }) => ({
        id: uuid("audit-session-item"),
        auditSessionId: session.id,
        stockItemId: item.id,
        barcode: item.barcode,
        expectedStoreId: form.storeId,
        mrp: Number(item.mrp || 0),
        selectionBucket: bucket,
        verificationStatus: "pending",
        scannedBarcode: "",
        verifiedBy: null,
        verifiedAt: null,
        note: "",
        reviewStatus: "pending",
        createdAt: now
      }));
      await persistOptionalEntity("audit_sessions", session);
      for (const row of sessionItems) await persistOptionalEntity("audit_session_items", row);
      await logAuditAction({ auditSessionId: session.id, action: "audit_created", details: { auditType, storeId: form.storeId, count: sessionItems.length } });
      await notifySuperAdmins(state, updateState, {
        module: "Stock Audit",
        title: "Stock Audit Generated",
        message: `${auditType === "weekly_stock_audit" ? "Weekly" : "Hard"} stock audit created for ${storeName(form.storeId)} with ${sessionItems.length} item${sessionItems.length === 1 ? "" : "s"}.`,
        priority: "normal",
        entityType: "audit_session",
        entityId: session.id,
        storeId: form.storeId,
        createdBy: user?.id || ""
      });
      updateState((current) => ({
        ...current,
        auditSessions: upsertById(current.auditSessions || [], session),
        auditSessionItems: [...sessionItems, ...(current.auditSessionItems || [])]
      }));
      setSelectedSessionId(session.id);
      setTab(auditType === "weekly_stock_audit" ? "weekly" : "hard");
      setMessage(`${auditType === "weekly_stock_audit" ? "Weekly" : "Hard"} stock audit created with ${sessionItems.length} item${sessionItems.length === 1 ? "" : "s"}.`);
    } catch (error) {
      setMessage(error.message || "Audit could not be created.");
    }
  };
  const updateVerifyDraft = (itemId, patch) => {
    setVerifyDrafts((current) => ({
      ...current,
      [itemId]: { verificationStatus: "Found", scannedBarcode: "", note: "", ...(current[itemId] || {}), ...patch }
    }));
  };
  const verifyAuditItem = async (item, photoFile) => {
    setMessage("");
    try {
      if (!selectedSession) throw new Error("Select audit session.");
      const draft = { verificationStatus: "Found", scannedBarcode: item.barcode, note: "", ...(verifyDrafts[item.id] || {}) };
      if (!draft.verificationStatus) throw new Error("Select verification status.");
      const now = new Date().toISOString();
      let photoRow = null;
      if (photoFile) {
        const uploaded = await uploadCompressedImage({
          file: photoFile,
          module: "stock_audit",
          recordType: "audit_session_item",
          recordId: item.id,
          storeId: selectedSession.storeId,
          fileType: "other",
          uploadedBy: user?.id || "",
          maxWidth: 800,
          quality: 0.66
        });
        photoRow = {
          id: uuid("audit-photo"),
          auditSessionItemId: item.id,
          photoUrl: uploaded.storagePath || uploaded.path || uploaded.url || "",
          uploadedBy: user?.id || "",
          uploadedAt: now
        };
        await persistOptionalEntity("audit_photos", photoRow);
      }
      const updatedItem = {
        ...item,
        verificationStatus: draft.verificationStatus,
        scannedBarcode: draft.scannedBarcode || item.barcode,
        verifiedBy: user?.id || "",
        verifiedAt: now,
        note: draft.note || "",
        reviewStatus: STOCK_AUDIT_MISMATCH_STATUSES.has(draft.verificationStatus) ? "pending" : "approved"
      };
      const sourceStock = stockItems.find((row) => row.id === item.stockItemId);
      const updatedStock = sourceStock ? {
        ...sourceStock,
        lastAuditedAt: now,
        lastAuditStatus: draft.verificationStatus,
        status: draft.verificationStatus === "Missing" ? "missing" : draft.verificationStatus === "Damaged" ? "damaged" : sourceStock.status,
        updatedAt: now
      } : null;
      await persistOptionalEntity("audit_session_items", updatedItem);
      if (updatedStock) await persistOptionalEntity("stock_items", updatedStock);
      await logAuditAction({ auditSessionId: selectedSession.id, auditSessionItemId: item.id, action: "item_verified", details: { status: draft.verificationStatus, barcode: item.barcode } });
      const nextItems = auditSessionItems.map((row) => row.id === item.id ? updatedItem : row);
      const sessionRows = nextItems.filter((row) => row.auditSessionId === selectedSession.id);
      const allDone = sessionRows.every((row) => row.verificationStatus && row.verificationStatus !== "pending");
      const hasMismatch = sessionRows.some((row) => STOCK_AUDIT_MISMATCH_STATUSES.has(row.verificationStatus));
      const updatedSession = allDone ? {
        ...selectedSession,
        status: hasMismatch ? "under_review" : "completed",
        completedAt: hasMismatch ? null : now
      } : selectedSession.status === "assigned" ? { ...selectedSession, status: "in_progress" } : selectedSession;
      await persistOptionalEntity("audit_sessions", updatedSession);
      if (photoRow) {
        await notifySuperAdmins(state, updateState, {
          module: "Stock Audit",
          title: "Audit Proof Uploaded",
          message: `Photo proof uploaded for barcode ${item.barcode} at ${storeName(selectedSession.storeId)}.`,
          priority: "normal",
          entityType: "audit_session_item",
          entityId: item.id,
          storeId: selectedSession.storeId,
          createdBy: user?.id || ""
        });
      }
      if (STOCK_AUDIT_MISMATCH_STATUSES.has(draft.verificationStatus)) {
        await notifySuperAdmins(state, updateState, {
          module: "Stock Audit",
          title: "Audit Issue Found",
          message: `Item ${item.barcode} was marked ${draft.verificationStatus} at ${storeName(selectedSession.storeId)}.`,
          priority: "high",
          entityType: "audit_session_item",
          entityId: item.id,
          storeId: selectedSession.storeId,
          createdBy: user?.id || ""
        });
      }
      updateState((current) => ({
        ...current,
        auditSessions: upsertById(current.auditSessions || [], updatedSession),
        auditSessionItems: upsertById(current.auditSessionItems || [], updatedItem),
        auditPhotos: photoRow ? upsertById(current.auditPhotos || [], photoRow) : (current.auditPhotos || []),
        stockItems: updatedStock ? upsertById(current.stockItems || [], updatedStock) : (current.stockItems || [])
      }));
      setVerifyDrafts((current) => ({ ...current, [item.id]: { verificationStatus: "Found", scannedBarcode: "", note: "" } }));
      setMessage("Audit item saved.");
    } catch (error) {
      setMessage(error.message || "Audit item could not be saved.");
    }
  };
  const reviewAuditItem = async (item, reviewAction) => {
    setMessage("");
    try {
      if (!canReview) throw new Error("Only Audit Manager / Admin can review mismatches.");
      const note = window.prompt("Review note", reviewAction === "approved" ? "Approved after checking proof" : "");
      const now = new Date().toISOString();
      const updated = { ...item, reviewStatus: reviewAction, reviewedBy: user?.id || "", reviewedAt: now };
      const review = {
        id: uuid("audit-review"),
        auditSessionItemId: item.id,
        reviewedBy: user?.id || "",
        reviewAction,
        reviewNote: note || "",
        reviewedAt: now
      };
      await persistOptionalEntity("audit_session_items", updated);
      await persistOptionalEntity("audit_reviews", review);
      await logAuditAction({ auditSessionId: item.auditSessionId, auditSessionItemId: item.id, action: `review_${reviewAction}`, details: { note: note || "" } });
      const session = auditSessions.find((row) => row.id === item.auditSessionId);
      await notifySuperAdmins(state, updateState, {
        module: "Stock Audit",
        title: `Audit ${reviewAction === "approved" ? "Approved" : reviewAction === "rejected" ? "Rejected" : "Review Updated"}`,
        message: `Barcode ${item.barcode} review changed to ${reviewAction}${session ? ` at ${storeName(session.storeId)}` : ""}.`,
        priority: reviewAction === "rejected" ? "high" : "normal",
        entityType: "audit_session_item",
        entityId: item.id,
        storeId: session?.storeId || "",
        createdBy: user?.id || ""
      });
      updateState((current) => ({
        ...current,
        auditSessionItems: upsertById(current.auditSessionItems || [], updated),
        auditReviews: upsertById(current.auditReviews || [], review)
      }));
      setMessage(`Review saved: ${reviewAction}.`);
    } catch (error) {
      setMessage(error.message || "Review could not be saved.");
    }
  };
  const exportAudit = () => {
    const rows = visibleSessions.flatMap((session) =>
      auditSessionItems.filter((item) => item.auditSessionId === session.id).map((item) => ({
        session,
        item,
        stock: stockItems.find((row) => row.id === item.stockItemId) || {}
      }))
    );
    downloadSheet(`${filePrefix(state.settings)}-stock-audit.csv`, rows, [
      ["Store", (row) => storeName(row.session.storeId)],
      ["Audit Type", (row) => row.session.auditType],
      ["Session Status", (row) => row.session.status],
      ["Barcode", (row) => row.item.barcode],
      ["Article", (row) => row.stock.articleName],
      ["Brand", (row) => row.stock.brand],
      ["MRP", (row) => row.item.mrp],
      ["Verification", (row) => row.item.verificationStatus],
      ["Review", (row) => row.item.reviewStatus],
      ["Verified By", (row) => staffName(row.item.verifiedBy)],
      ["Verified At", (row) => row.item.verifiedAt],
      ["Note", (row) => row.item.note]
    ]);
  };
  const auditTabs = [
    ["dashboard", "Audit Manager"],
    ["master", "Stock Master"],
    ["weekly", "Weekly Stock Audit"],
    ["hard", "Hard Stock Audit"]
  ];
  const kpis = [
    ["Stock Items", visibleStock.length],
    ["Open Audits", visibleSessions.filter((session) => !["completed", "cancelled"].includes(session.status)).length],
    ["Pending Review", reviewRows.filter((row) => (row.reviewStatus || "pending") === "pending").length],
    ["Missing/Damaged", visibleStock.filter((item) => ["missing", "damaged"].includes(item.status)).length]
  ];
  return (
    <section className="stock-audit-module">
      <div className="panel-title-row">
        <div>
          <h2>Stock Audit</h2>
          <p>Weekly random checks, hard stock audit, photo proof and manager review.</p>
        </div>
        <button className="secondary-button" type="button" onClick={exportAudit}><FileSpreadsheet size={17} /> Audit sheet</button>
      </div>
      {message ? <p className="notice">{message}</p> : null}
      <div className="status-tabs crm-layer-tabs" role="tablist" aria-label="Stock audit sections">
        {auditTabs.map(([key, label]) => (
          <button key={key} className={tab === key ? "active" : ""} type="button" onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === "dashboard" ? (
        <div className="panel-grid two">
          <article className="panel dashboard-detail">
            <div className="order-workflow-kpis">
              {kpis.map(([label, value]) => <div className="order-workflow-kpi" key={label}><span>{label}</span><strong>{value}</strong></div>)}
            </div>
            <div className="panel-title-row">
              <h3>Audit Sessions</h3>
              <span className="status-pill">{visibleSessions.length} total</span>
            </div>
            <DetailList
              rows={visibleSessions}
              getTitle={(row) => `${storeName(row.storeId)} · ${row.auditType === "hard_stock_audit" ? "Hard" : "Weekly"}`}
              getMeta={(row) => {
                const stats = sessionStats(row);
                return `${row.status} · ${stats.done}/${stats.total} checked · deadline ${row.deadlineAt ? formatDisplayDate(row.deadlineAt.slice(0, 10)) : "-"}`;
              }}
              getStatus={(row) => sessionStats(row).pendingReview ? `${sessionStats(row).pendingReview} review` : row.status}
              renderDetails={(row) => {
                const stats = sessionStats(row);
                return (
                  <div className="detail-fields">
                    <h3>{storeName(row.storeId)}</h3>
                    <p><strong>Type:</strong> {row.auditType === "hard_stock_audit" ? "Hard Stock Audit" : "Weekly Stock Audit"}</p>
                    <p><strong>Assigned to:</strong> {staffName(row.assignedTo)}</p>
                    <p><strong>Progress:</strong> {stats.done}/{stats.total} checked</p>
                    <p><strong>Found:</strong> {stats.found}</p>
                    <p><strong>Mismatch:</strong> {stats.mismatch}</p>
                    <p><strong>Pending review:</strong> {stats.pendingReview}</p>
                    <p><strong>Mix note:</strong> {row.mixAdjustmentNote || "-"}</p>
                    <button className="primary-button" type="button" onClick={() => { setSelectedSessionId(row.id); setTab(row.auditType === "hard_stock_audit" ? "hard" : "weekly"); }}>Open audit</button>
                  </div>
                );
              }}
            />
          </article>
          <article className="panel dashboard-detail">
            <div className="panel-title-row">
              <h3>Pending Review</h3>
              <label className="compact-select">Filter<select value={reviewFilter} onChange={(event) => setReviewFilter(event.target.value)}>
                <option value="pending">Pending</option>
                <option value="recheck_required">Recheck</option>
                <option value="all">All mismatch</option>
              </select></label>
            </div>
            <DetailList
              rows={reviewRows}
              getTitle={(row) => row.barcode}
              getMeta={(row) => `${storeName(row.session?.storeId)} · ${row.verificationStatus} · ${row.note || "No note"}`}
              getStatus={(row) => row.reviewStatus || "pending"}
              renderDetails={(row) => {
                const stock = stockItems.find((item) => item.id === row.stockItemId) || {};
                const photos = auditPhotos.filter((photo) => photo.auditSessionItemId === row.id);
                return (
                  <div className="detail-fields">
                    <h3>{stock.articleName || row.barcode}</h3>
                    <p><strong>Store:</strong> {storeName(row.session?.storeId)}</p>
                    <p><strong>Brand:</strong> {stock.brand || "-"}</p>
                    <p><strong>MRP:</strong> {opticalMoney(row.mrp || stock.mrp || 0)}</p>
                    <p><strong>Status submitted:</strong> {row.verificationStatus}</p>
                    <p><strong>Verified by:</strong> {staffName(row.verifiedBy)}</p>
                    <p><strong>Verified at:</strong> {row.verifiedAt ? new Date(row.verifiedAt).toLocaleString() : "-"}</p>
                    <p><strong>Photo:</strong> {photos.length ? `${photos.length} proof photo(s)` : "No photo"}</p>
                    {photos.map((photo) => <PhotoPreview key={photo.id} src={photo.photoUrl} label="Stock audit proof" />)}
                    <div className="button-row">
                      <button className="secondary-button" type="button" disabled={!canReview} onClick={() => reviewAuditItem(row, "approved")}>Approve</button>
                      <button className="secondary-button" type="button" disabled={!canReview} onClick={() => reviewAuditItem(row, "rejected")}>Reject</button>
                      <button className="secondary-button" type="button" disabled={!canReview} onClick={() => reviewAuditItem(row, "recheck_required")}>Ask recheck</button>
                    </div>
                  </div>
                );
              }}
            />
          </article>
        </div>
      ) : null}
      {tab === "master" ? (
        <div className="panel-grid two">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h3>Add Stock Item</h3>
            <div className="form-grid">
              <label>Store<select value={manualForm.storeId} onChange={(event) => setManualForm({ ...manualForm, storeId: event.target.value })}>{visibleStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
              <Input label="Barcode" value={manualForm.barcode} onChange={(barcode) => setManualForm({ ...manualForm, barcode })} />
              <Input label="Article name" value={manualForm.articleName} onChange={(articleName) => setManualForm({ ...manualForm, articleName })} />
              <Input label="Brand" value={manualForm.brand} onChange={(brand) => setManualForm({ ...manualForm, brand })} />
              <label>Category<select value={manualForm.category} onChange={(event) => setManualForm({ ...manualForm, category: event.target.value })}>{STOCK_AUDIT_CATEGORIES.map((item) => <option key={item} value={item}>{item.replace("_", " ")}</option>)}</select></label>
              <Input label="MRP" type="number" value={manualForm.mrp} onChange={(mrp) => setManualForm({ ...manualForm, mrp })} />
              <label>Status<select value={manualForm.status} onChange={(event) => setManualForm({ ...manualForm, status: event.target.value })}>{STOCK_AUDIT_ITEM_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            </div>
            <button className="primary-button" type="button" onClick={saveStockItem}>Save stock item</button>
          </form>
          <article className="panel dashboard-detail">
            <div className="panel-title-row">
              <h3>Stock Master</h3>
              <button className="secondary-button" type="button" onClick={() => downloadSheet(`${filePrefix(state.settings)}-stock-master.csv`, visibleStock, [
                ["Barcode", (row) => row.barcode],
                ["Article", (row) => row.articleName],
                ["Brand", (row) => row.brand],
                ["Category", (row) => row.category],
                ["MRP", (row) => row.mrp],
                ["Store", (row) => storeName(row.currentStoreId || row.storeId)],
                ["Status", (row) => row.status],
                ["Last Audit", (row) => row.lastAuditStatus],
                ["Last Audited At", (row) => row.lastAuditedAt]
              ])}><FileSpreadsheet size={17} /> Stock sheet</button>
            </div>
            <div className="staff-search-bar dashboard-search">
              <Search size={17} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search barcode, article, brand, store" />
            </div>
            <DetailList
              rows={filteredStock}
              getTitle={(row) => row.articleName || row.barcode}
              getMeta={(row) => `${row.barcode} · ${storeName(row.currentStoreId || row.storeId)} · MRP ${opticalMoney(row.mrp || 0)}`}
              getStatus={(row) => row.status}
              renderDetails={(row) => (
                <div className="detail-fields">
                  <h3>{row.articleName}</h3>
                  <p><strong>Barcode:</strong> {row.barcode}</p>
                  <p><strong>Brand:</strong> {row.brand || "-"}</p>
                  <p><strong>Category:</strong> {row.category}</p>
                  <p><strong>Store:</strong> {storeName(row.currentStoreId || row.storeId)}</p>
                  <p><strong>Last audit:</strong> {row.lastAuditStatus || "-"} · {stockAuditAgeLabel(row.lastAuditedAt)}</p>
                </div>
              )}
            />
          </article>
          <article className="panel dashboard-detail">
            <h3>CSV Import</h3>
            <p>Columns: barcode, article_name, brand, category, mrp, store_code, status.</p>
            <input type="file" accept=".csv" onChange={(event) => previewStockCsv(event.target.files?.[0])} />
            {csvPreview ? (
              <div className="order-collapse-body">
                <div className="order-workflow-kpis">
                  <div className="order-workflow-kpi"><span>Total</span><strong>{csvPreview.rows.length}</strong></div>
                  <div className="order-workflow-kpi"><span>Duplicates</span><strong>{csvPreview.duplicateRows}</strong></div>
                  <div className="order-workflow-kpi"><span>Needs review</span><strong>{csvPreview.missingRows}</strong></div>
                </div>
                <DetailList
                  rows={csvPreview.rows.slice(0, 10)}
                  getTitle={(row) => row.articleName || "Missing article"}
                  getMeta={(row) => `${row.barcode || "-"} · ${row.storeCode || storeName(row.storeId)} · ${row.category}`}
                  getStatus={(row) => row.barcode && row.articleName && row.storeId ? "Ready" : "Review"}
                  renderDetails={(row) => (
                    <div className="detail-fields">
                      <p><strong>Brand:</strong> {row.brand || "-"}</p>
                      <p><strong>MRP:</strong> {opticalMoney(row.mrp || 0)}</p>
                      <p><strong>Status:</strong> {row.status}</p>
                    </div>
                  )}
                />
                <div className="button-row">
                  <button className="primary-button" type="button" onClick={confirmStockCsv}>Confirm import</button>
                  <button className="secondary-button" type="button" onClick={() => setCsvPreview(null)}>Cancel</button>
                </div>
              </div>
            ) : null}
          </article>
        </div>
      ) : null}
      {tab === "weekly" || tab === "hard" ? (
        <div className="panel-grid two">
          {canCreateAudit ? (
            <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
              <h3>{tab === "weekly" ? "Create Weekly Audit" : "Create Hard Audit"}</h3>
              <div className="form-grid">
                <label>Store<select value={tab === "weekly" ? weeklyForm.storeId : hardForm.storeId} onChange={(event) => tab === "weekly" ? setWeeklyForm({ ...weeklyForm, storeId: event.target.value }) : setHardForm({ ...hardForm, storeId: event.target.value })}>{visibleStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
                {tab === "weekly" ? <Input label="Target items" type="number" value={weeklyForm.targetCount} onChange={(targetCount) => setWeeklyForm({ ...weeklyForm, targetCount })} /> : null}
                <label>Assigned to<select value={tab === "weekly" ? weeklyForm.assignedTo : hardForm.assignedTo} onChange={(event) => tab === "weekly" ? setWeeklyForm({ ...weeklyForm, assignedTo: event.target.value }) : setHardForm({ ...hardForm, assignedTo: event.target.value })}>
                  {state.users.filter((item) => item.active !== false).map((item) => <option key={item.id} value={item.id}>{item.name || item.email}</option>)}
                </select></label>
                <Input label="Deadline" type="datetime-local" value={tab === "weekly" ? weeklyForm.deadlineAt : hardForm.deadlineAt} onChange={(deadlineAt) => tab === "weekly" ? setWeeklyForm({ ...weeklyForm, deadlineAt }) : setHardForm({ ...hardForm, deadlineAt })} />
              </div>
              <button className="primary-button" type="button" onClick={() => createAuditSession(tab === "weekly" ? "weekly_stock_audit" : "hard_stock_audit")}>
                {tab === "weekly" ? "Generate weekly audit" : "Start hard audit"}
              </button>
            </form>
          ) : null}
          <article className="panel dashboard-detail">
            <div className="panel-title-row">
              <h3>{tab === "weekly" ? "Weekly Audit Items" : "Hard Audit Items"}</h3>
              <label className="compact-select">Session<select value={selectedSession?.id || ""} onChange={(event) => setSelectedSessionId(event.target.value)}>
                {visibleSessions.filter((session) => session.auditType === (tab === "weekly" ? "weekly_stock_audit" : "hard_stock_audit")).map((session) => <option key={session.id} value={session.id}>{storeName(session.storeId)} · {new Date(session.createdAt).toLocaleDateString()}</option>)}
              </select></label>
            </div>
            {selectedSession ? <p className="notice">{selectedSession.mixAdjustmentNote || "Audit ready."}</p> : null}
            <DetailList
              rows={selectedItems}
              getTitle={(row) => stockItems.find((item) => item.id === row.stockItemId)?.articleName || row.barcode}
              getMeta={(row) => `${row.barcode} · ${row.selectionBucket} · MRP ${opticalMoney(row.mrp || 0)}`}
              getStatus={(row) => row.verificationStatus === "pending" ? "Pending" : row.verificationStatus}
              renderDetails={(row) => {
                const stock = stockItems.find((item) => item.id === row.stockItemId) || {};
                const draft = { verificationStatus: "Found", scannedBarcode: row.barcode, note: "", ...(verifyDrafts[row.id] || {}) };
                const photos = auditPhotos.filter((photo) => photo.auditSessionItemId === row.id);
                return (
                  <div className="detail-fields">
                    <h3>{stock.articleName || row.barcode}</h3>
                    <p><strong>Expected store:</strong> {storeName(row.expectedStoreId)}</p>
                    <p><strong>Brand:</strong> {stock.brand || "-"}</p>
                    <p><strong>Last audit:</strong> {stock.lastAuditStatus || "-"} · {stockAuditAgeLabel(stock.lastAuditedAt)}</p>
                    <div className="form-grid">
                      <Input label="Scanned / entered barcode" value={draft.scannedBarcode} onChange={(scannedBarcode) => updateVerifyDraft(row.id, { scannedBarcode })} />
                      <label>Verification<select value={draft.verificationStatus} onChange={(event) => updateVerifyDraft(row.id, { verificationStatus: event.target.value })}>{STOCK_AUDIT_VERIFY_STATUSES.map((status) => <option key={status}>{status}</option>)}</select></label>
                    </div>
                    <textarea placeholder="Note optional" value={draft.note} onChange={(event) => updateVerifyDraft(row.id, { note: event.target.value })} />
                    <p><strong>Photo proof:</strong> {photos.length ? `${photos.length} uploaded` : "Optional but recommended"}</p>
                    {photos.map((photo) => <PhotoPreview key={photo.id} src={photo.photoUrl} label="Stock audit proof" />)}
                    <label className="file-button">
                      <Camera size={17} /> Upload proof & save
                      <input type="file" accept="image/*" capture="environment" onChange={(event) => verifyAuditItem(row, event.target.files?.[0])} />
                    </label>
                    <button className="secondary-button" type="button" onClick={() => verifyAuditItem(row, null)}>Save without photo</button>
                  </div>
                );
              }}
            />
          </article>
        </div>
      ) : null}
    </section>
  );
}

function CrmInventoryLayer({ state, updateState, user }) {
  const [inventoryMode, setInventoryMode] = useState("stock_audit");
  const [message, setMessage] = useState("");
  const canViewProducts = hasUserPermission(user, "can_view_products") || hasAppPermission(user, "optical");
  const canAddProducts = hasUserPermission(user, "can_add_products");
  const canChangePrice = hasUserPermission(user, "can_change_product_price");
  const canViewCost = hasUserPermission(user, "can_view_cost_price");
  const canViewMargin = hasUserPermission(user, "can_view_margin");
  const [form, setForm] = useState({
    productCode: "",
    name: "",
    productType: "Frame",
    brand: "",
    category: "",
    storeId: state.stores[0]?.id || "",
    stockQty: 0,
    reorderLevel: 2,
    costPrice: "",
    sellingPrice: "",
    notes: ""
  });
  const exportPrefix = filePrefix(state.settings);
  const products = state.crmProducts || [];
  const canTransferStock = hasUserPermission(user, "can_transfer_stock");
  const canAdjustStock = hasUserPermission(user, "can_adjust_stock");
  const canImportInventory = hasUserPermission(user, "can_import_inventory");
  const canExportInventory = hasUserPermission(user, "can_export_inventory") || canViewProducts;
  const canDownloadTemplate = hasUserPermission(user, "can_download_inventory_template") || canImportInventory;
  const canManageUnmapped = hasUserPermission(user, "can_manage_unmapped_sold_items") || hasUserPermission(user, "can_map_unmapped_product");
  const [stockQuery, setStockQuery] = useState("");
  const [inventoryImportPreview, setInventoryImportPreview] = useState(null);
  const [transferForm, setTransferForm] = useState({
    productId: products[0]?.id || "",
    fromStoreId: state.stores[0]?.id || "",
    toStoreId: state.stores[1]?.id || state.stores[0]?.id || "",
    quantity: 1,
    remarks: ""
  });
  const [adjustmentForm, setAdjustmentForm] = useState({
    productId: products[0]?.id || "",
    storeId: state.stores[0]?.id || "",
    quantity: 0,
    movementType: "Adjustment",
    remarks: ""
  });
  const stockRows = (state.crmInventoryStock?.length ? state.crmInventoryStock : products.map((product) => ({
    id: `stock-${product.id}`,
    productId: product.id,
    productName: product.productName || product.name,
    storeId: product.storeId,
    barcode: product.barcode,
    batchNo: product.batchNo,
    expiryDate: product.expiryDate,
    quantity: product.stockQty || 0,
    reservedQty: 0,
    availableQty: product.stockQty || 0,
    costPrice: product.costPrice || product.purchasePrice || 0,
    mrp: product.mrp || product.sellingPrice || 0,
    status: "In Stock"
  }))).filter((row) => {
    const product = products.find((item) => item.id === row.productId) || {};
    const text = `${row.productName || ""} ${product.name || ""} ${product.productName || ""} ${row.barcode || ""} ${row.batchNo || ""} ${state.stores.find((store) => store.id === row.storeId)?.name || ""}`.toLowerCase();
    return text.includes(stockQuery.toLowerCase());
  });
  const lowStockRows = products.filter((product) => Number(product.stockQty || 0) <= Number(product.reorderLevel || 0));
  const expiryRows = stockRows.filter((row) => row.expiryDate && new Date(row.expiryDate) <= new Date(Date.now() + 1000 * 60 * 60 * 24 * 90));
  const unmappedRows = (state.crmUnmappedSoldItems || []).filter((row) => row.status !== "resolved" && row.resolutionAction !== "resolved");
  const myUnmappedRows = unmappedRows.filter((row) => row.salespersonId === user.employeeId || row.createdBy === user.id);
  const negativeRows = (state.crmStockMovements || []).filter((row) => row.negativeInventory || Number(row.quantity || 0) < 0 && row.actionNeeded);
  const inventoryModeTabs = (
    <div className="status-tabs crm-layer-tabs" role="tablist" aria-label="Inventory module">
      <button className={inventoryMode === "stock_audit" ? "active" : ""} type="button" onClick={() => setInventoryMode("stock_audit")}>Stock Audit</button>
      <button className={inventoryMode === "stock_master" ? "active" : ""} type="button" onClick={() => setInventoryMode("stock_master")}>Inventory Master</button>
    </div>
  );
  if (inventoryMode === "stock_audit") {
    return (
      <>
        {inventoryModeTabs}
        <StockAuditModule state={state} updateState={updateState} user={user} />
      </>
    );
  }
  const updateProductStock = (rows, productId, delta) => rows.map((item) => item.id === productId ? { ...item, stockQty: Number(item.stockQty || 0) + delta, updatedAt: new Date().toISOString() } : item);
  const downloadInventoryTemplate = () => {
    downloadSheet(`${exportPrefix}-inventory-import-template.csv`, [
      {
        productCode: "SKU-001",
        barcode: "890000000001",
        productName: "Frame model / Lens name",
        productType: "Frame",
        brandName: "Brand",
        categoryName: "Frame",
        storeCode: "NOIDA",
        batchNo: "",
        expiryDate: "",
        quantity: 10,
        purchasePrice: 500,
        sellingPrice: 1500,
        mrp: 1999,
        supplierName: "Vendor"
      }
    ], [
      ["Product Code", (row) => row.productCode],
      ["Barcode", (row) => row.barcode],
      ["Product Name", (row) => row.productName],
      ["Product Type", (row) => row.productType],
      ["Brand", (row) => row.brandName],
      ["Category", (row) => row.categoryName],
      ["Store Code", (row) => row.storeCode],
      ["Batch No", (row) => row.batchNo],
      ["Expiry Date", (row) => row.expiryDate],
      ["Quantity", (row) => row.quantity],
      ["Purchase Price", (row) => row.purchasePrice],
      ["Selling Price", (row) => row.sellingPrice],
      ["MRP", (row) => row.mrp],
      ["Supplier", (row) => row.supplierName]
    ]);
  };
  const previewInventoryImport = async (file) => {
    if (!file) return;
    setMessage("");
    try {
      if (!canImportInventory) throw new Error("You do not have permission to import inventory.");
      let rows = [];
      if (/\.(xlsx|xls)$/i.test(file.name || "")) {
        const ExcelModule = await import("exceljs");
        const ExcelJS = ExcelModule.default || ExcelModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(await file.arrayBuffer());
        const sheet = workbook.worksheets[0];
        rows = [];
        sheet.eachRow((row) => rows.push(row.values.slice(1).map((cell) => {
          if (cell == null) return "";
          if (typeof cell === "object" && cell.text) return cell.text;
          if (typeof cell === "object" && cell.result != null) return cell.result;
          return String(cell);
        })));
      } else {
        const text = await file.text();
        rows = simpleCsvRows(text);
      }
      const headers = rows[0] || [];
      const dataRows = rows.slice(1).map((cells, index) => ({
        id: `preview-${index}`,
        productCode: cells[0] || "",
        barcode: cells[1] || "",
        productName: cells[2] || "",
        productType: cells[3] || "",
        brandName: cells[4] || "",
        categoryName: cells[5] || "",
        storeCode: cells[6] || "",
        batchNo: cells[7] || "",
        expiryDate: cells[8] || "",
        quantity: Number(cells[9] || 0),
        purchasePrice: Number(cells[10] || 0),
        sellingPrice: Number(cells[11] || 0),
        mrp: Number(cells[12] || 0),
        supplierName: cells[13] || ""
      }));
      setInventoryImportPreview({
        fileName: file.name,
        headers,
        rows: dataRows,
        duplicateRows: dataRows.filter((row) => products.some((product) => product.barcode && row.barcode && product.barcode === row.barcode)).length,
        missingRows: dataRows.filter((row) => !row.productName).length
      });
      setMessage("Inventory preview ready. Confirm only after checking sample rows.");
    } catch (error) {
      setMessage(error.message || "Inventory file could not be read.");
    }
  };
  const confirmInventoryImport = async () => {
    setMessage("");
    try {
      if (!inventoryImportPreview) throw new Error("No inventory preview to import.");
      const now = new Date().toISOString();
      const importBatch = {
        id: uuid("crm-inventory-import"),
        importType: "inventory",
        fileName: inventoryImportPreview.fileName,
        totalRows: inventoryImportPreview.rows.length,
        importedRows: inventoryImportPreview.rows.length - inventoryImportPreview.missingRows,
        duplicateRows: inventoryImportPreview.duplicateRows,
        failedRows: inventoryImportPreview.missingRows,
        previewRows: inventoryImportPreview.rows.slice(0, 20),
        summary: {
          duplicateRows: inventoryImportPreview.duplicateRows,
          missingRows: inventoryImportPreview.missingRows
        },
        uploadedBy: user.name || user.email,
        uploadedAt: now,
        confirmedBy: user.name || user.email,
        confirmedAt: now,
        status: "imported",
        createdAt: now,
        updatedAt: now
      };
      const rowsToImport = inventoryImportPreview.rows.filter((row) => row.productName);
      const productRows = rowsToImport.map((row) => {
        const store = state.stores.find((item) => item.code === row.storeCode || item.name === row.storeCode) || state.stores[0] || {};
        return {
          id: uuid("crm-product"),
          ...row,
          name: row.productName,
          storeId: store.id || "",
          stockQty: row.quantity,
          costPrice: row.purchasePrice,
          active: true,
          createdAt: now,
          updatedAt: now
        };
      });
      for (const row of productRows) await persistOptionalEntity("crm_products", row);
      await persistOptionalEntity("crm_inventory_imports", importBatch);
      updateState((current) => ({
        ...current,
        crmProducts: [...productRows, ...(current.crmProducts || [])],
        crmInventoryImports: [importBatch, ...(current.crmInventoryImports || [])]
      }));
      setInventoryImportPreview(null);
      setMessage(`Inventory import completed. ${productRows.length} rows imported.`);
    } catch (error) {
      setMessage(error.message || "Inventory import could not be confirmed.");
    }
  };
  const resolveUnmappedItem = async (row, product) => {
    try {
      if (!canManageUnmapped) throw new Error("You do not have permission to resolve unmapped sold items.");
      const updated = {
        ...row,
        productId: product?.id || row.productId || "",
        resolvedProductName: product?.productName || product?.name || row.resolvedProductName || "",
        resolutionAction: product ? "mapped_to_product" : "noted_for_purchase",
        status: product ? "resolved" : "purchase_pending",
        resolvedBy: user.name || user.email,
        resolvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      await persistOptionalEntity("crm_unmapped_sold_items", updated);
      updateState((current) => ({
        ...current,
        crmUnmappedSoldItems: (current.crmUnmappedSoldItems || []).map((item) => item.id === updated.id ? updated : item)
      }));
      setMessage(product ? "Unmapped sale item linked to product master." : "Item marked for purchase follow-up.");
    } catch (error) {
      setMessage(error.message || "Unmapped item could not be updated.");
    }
  };
  const saveTransfer = async () => {
    setMessage("");
    try {
      if (!canTransferStock) throw new Error("You do not have permission to transfer stock.");
      if (!transferForm.productId) throw new Error("Select product.");
      if (!transferForm.fromStoreId || !transferForm.toStoreId || transferForm.fromStoreId === transferForm.toStoreId) throw new Error("Select two different stores.");
      const qty = Number(transferForm.quantity || 0);
      if (qty <= 0) throw new Error("Quantity must be more than zero.");
      const now = new Date().toISOString();
      const product = products.find((item) => item.id === transferForm.productId) || {};
      const row = {
        id: uuid("crm-transfer"),
        ...transferForm,
        productName: product.productName || product.name || "",
        quantity: qty,
        status: "pending",
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      const outMovement = {
        id: uuid("crm-move"),
        productId: transferForm.productId,
        productName: row.productName,
        storeId: transferForm.fromStoreId,
        movementType: "Transfer Out",
        quantity: -qty,
        toStoreId: transferForm.toStoreId,
        referenceType: "stock_transfer",
        referenceId: row.id,
        remarks: transferForm.remarks,
        createdBy: user.name || user.email,
        createdAt: now
      };
      const inMovement = {
        ...outMovement,
        id: uuid("crm-move"),
        storeId: transferForm.toStoreId,
        movementType: "Transfer In",
        quantity: qty,
        fromStoreId: transferForm.fromStoreId
      };
      await persistOptionalEntity("crm_stock_transfers", row);
      await persistOptionalEntity("crm_stock_movements", outMovement);
      await persistOptionalEntity("crm_stock_movements", inMovement);
      updateState((current) => ({
        ...current,
        crmStockTransfers: upsertById(current.crmStockTransfers || [], row),
        crmStockMovements: [inMovement, outMovement, ...(current.crmStockMovements || [])],
        crmProducts: updateProductStock(current.crmProducts || [], transferForm.productId, 0)
      }));
      setTransferForm({ ...transferForm, quantity: 1, remarks: "" });
      setMessage("Stock transfer recorded.");
    } catch (error) {
      setMessage(error.message || "Stock transfer could not be saved.");
    }
  };
  const saveAdjustment = async () => {
    setMessage("");
    try {
      if (!canAdjustStock) throw new Error("You do not have permission to adjust stock.");
      if (!adjustmentForm.productId) throw new Error("Select product.");
      const qty = Number(adjustmentForm.quantity || 0);
      if (!qty) throw new Error("Enter adjustment quantity. Use minus for reduction.");
      const now = new Date().toISOString();
      const product = products.find((item) => item.id === adjustmentForm.productId) || {};
      const row = {
        id: uuid("crm-adjustment"),
        ...adjustmentForm,
        productName: product.productName || product.name || "",
        quantity: qty,
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      const movement = {
        id: uuid("crm-move"),
        productId: adjustmentForm.productId,
        productName: row.productName,
        storeId: adjustmentForm.storeId,
        movementType: adjustmentForm.movementType,
        quantity: qty,
        referenceType: "stock_adjustment",
        referenceId: row.id,
        remarks: adjustmentForm.remarks,
        createdBy: user.name || user.email,
        createdAt: now
      };
      await persistOptionalEntity("crm_stock_adjustments", row);
      await persistOptionalEntity("crm_stock_movements", movement);
      updateState((current) => ({
        ...current,
        crmStockAdjustments: upsertById(current.crmStockAdjustments || [], row),
        crmStockMovements: upsertById(current.crmStockMovements || [], movement),
        crmProducts: updateProductStock(current.crmProducts || [], adjustmentForm.productId, qty)
      }));
      setAdjustmentForm({ ...adjustmentForm, quantity: 0, remarks: "" });
      setMessage("Stock adjustment saved.");
    } catch (error) {
      setMessage(error.message || "Stock adjustment could not be saved.");
    }
  };
  const saveProduct = async () => {
    setMessage("");
    try {
      if (!canAddProducts) throw new Error("You do not have permission to add products.");
      if (!form.name.trim()) throw new Error("Product name is required.");
      const now = new Date().toISOString();
      const row = {
        id: uuid("crm-product"),
        ...form,
        stockQty: Number(form.stockQty || 0),
        reorderLevel: Number(form.reorderLevel || 0),
        costPrice: Number(form.costPrice || 0),
        sellingPrice: Number(form.sellingPrice || 0),
        createdAt: now,
        updatedAt: now
      };
      const result = await persistOptionalEntity("crm_products", row);
      updateState((current) => ({ ...current, crmProducts: upsertById(current.crmProducts || [], row) }));
      setForm({ ...form, productCode: "", name: "", brand: "", category: "", stockQty: 0, costPrice: "", sellingPrice: "", notes: "" });
      setMessage(result.reason || "Product saved.");
    } catch (error) {
      setMessage(error.message || "Product could not be saved.");
    }
  };
  if (!canViewProducts) {
    return (
      <article className="panel">
        <h2>Inventory</h2>
        <p className="notice">You do not have permission to view product master.</p>
      </article>
    );
  }

  return (
    <>
    {inventoryModeTabs}
    <div className="panel-grid two">
      {canAddProducts ? (
      <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
        <h2>Add product / stock</h2>
        {message ? <p className="notice">{message}</p> : null}
        <div className="form-grid">
          <Input label="SKU / code" value={form.productCode} onChange={(productCode) => setForm({ ...form, productCode })} />
          <Input label="Product name" value={form.name} onChange={(name) => setForm({ ...form, name })} />
          <label>Type<select value={form.productType} onChange={(event) => setForm({ ...form, productType: event.target.value })}>{CRM_PRODUCT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
          <Input label="Brand" value={form.brand} onChange={(brand) => setForm({ ...form, brand })} />
          <Input label="Category" value={form.category} onChange={(category) => setForm({ ...form, category })} />
          <label>Store<select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
          <Input label="Stock qty" type="number" value={form.stockQty} onChange={(stockQty) => setForm({ ...form, stockQty })} />
          <Input label="Reorder level" type="number" value={form.reorderLevel} onChange={(reorderLevel) => setForm({ ...form, reorderLevel })} />
          {canViewCost ? <Input label="Cost price" type="number" value={form.costPrice} onChange={(costPrice) => setForm({ ...form, costPrice })} /> : null}
          {canChangePrice ? <Input label="Selling price" type="number" value={form.sellingPrice} onChange={(sellingPrice) => setForm({ ...form, sellingPrice })} /> : null}
        </div>
        <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        <button className="primary-button" type="button" onClick={saveProduct}>Save product</button>
      </form>
      ) : (
        <article className="panel">
          <h2>Product master locked</h2>
          <p className="notice">You can view products, but adding/editing product master is restricted.</p>
        </article>
      )}
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <h2>Inventory</h2>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-inventory.csv`, products, [
            ["Code", (row) => row.productCode],
            ["Name", (row) => row.name],
            ["Type", (row) => row.productType],
            ["Brand", (row) => row.brand],
            ["Stock", (row) => row.stockQty],
            ["Reorder", (row) => row.reorderLevel],
            ...(canChangePrice ? [["Selling", (row) => row.sellingPrice]] : []),
            ...(canViewCost ? [["Cost", (row) => row.costPrice]] : []),
            ...(canViewMargin ? [["Margin", (row) => Number(row.sellingPrice || 0) - Number(row.costPrice || 0)]] : [])
          ])}><FileSpreadsheet size={17} /> Inventory sheet</button>
        </div>
        <DetailList
          rows={products}
          getTitle={(row) => row.name}
          getMeta={(row) => `${row.productType || "-"} · Stock ${row.stockQty || 0} · ${state.stores.find((store) => store.id === row.storeId)?.code || ""}`}
          getStatus={(row) => Number(row.stockQty || 0) <= Number(row.reorderLevel || 0) ? "LOW" : "OK"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.name}</h3>
              <p><strong>SKU:</strong> {row.productCode || "-"}</p>
              <p><strong>Type:</strong> {row.productType || "-"}</p>
              <p><strong>Brand:</strong> {row.brand || "-"}</p>
              <p><strong>Stock:</strong> {row.stockQty || 0}</p>
              {canViewCost ? <p><strong>Cost:</strong> {opticalMoney(row.costPrice || 0)}</p> : null}
              {canChangePrice ? <p><strong>Selling:</strong> {opticalMoney(row.sellingPrice || 0)}</p> : null}
              {canViewMargin ? <p><strong>Margin:</strong> {opticalMoney(Number(row.sellingPrice || 0) - Number(row.costPrice || 0))}</p> : null}
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <div>
            <h2>Branch-wise Stock</h2>
            <p>Search by product, barcode, batch or branch.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-stock-levels.csv`, stockRows, [
            ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Product", (row) => row.productName],
            ["Barcode", (row) => row.barcode],
            ["Batch", (row) => row.batchNo],
            ["Expiry", (row) => row.expiryDate],
            ["Qty", (row) => row.quantity],
            ["Reserved", (row) => row.reservedQty || 0],
            ["Available", (row) => row.availableQty ?? row.quantity],
            ["Status", (row) => row.status || "In Stock"]
          ])}><FileSpreadsheet size={17} /> Stock sheet</button>
        </div>
        <div className="staff-search-bar dashboard-search">
          <Search size={17} />
          <input value={stockQuery} onChange={(event) => setStockQuery(event.target.value)} placeholder="Search stock / barcode / branch" />
        </div>
        <DetailList
          rows={stockRows}
          getTitle={(row) => row.productName || products.find((item) => item.id === row.productId)?.name || "Stock item"}
          getMeta={(row) => `${state.stores.find((store) => store.id === row.storeId)?.name || "-"} · Barcode ${row.barcode || "-"} · Qty ${row.quantity || 0}`}
          getStatus={(row) => row.status || "In Stock"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>Branch:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || "-"}</p>
              <p><strong>Available:</strong> {row.availableQty ?? row.quantity ?? 0}</p>
              <p><strong>Reserved:</strong> {row.reservedQty || 0}</p>
              <p><strong>Batch:</strong> {row.batchNo || "-"}</p>
              <p><strong>Expiry:</strong> {row.expiryDate || "-"}</p>
              {canViewCost ? <p><strong>Cost:</strong> {opticalMoney(row.costPrice || 0)}</p> : null}
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <h2>Low Stock / Expiry</h2>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-low-stock-expiry.csv`, [...lowStockRows, ...expiryRows], [
            ["Product", (row) => row.productName || row.name],
            ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Stock", (row) => row.stockQty ?? row.quantity],
            ["Reorder", (row) => row.reorderLevel || ""],
            ["Expiry", (row) => row.expiryDate || ""]
          ])}><FileSpreadsheet size={17} /> Alert sheet</button>
        </div>
        <DetailList
          rows={[...lowStockRows.map((row) => ({ ...row, alertType: "Low stock" })), ...expiryRows.map((row) => ({ ...row, alertType: "Expiry" }))]}
          getTitle={(row) => row.productName || row.name}
          getMeta={(row) => `${row.alertType} · ${row.expiryDate || `Stock ${row.stockQty || row.quantity || 0}`}`}
          getStatus={(row) => row.alertType}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>Alert:</strong> {row.alertType}</p>
              <p><strong>Barcode:</strong> {row.barcode || "-"}</p>
              <p><strong>Branch:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || "-"}</p>
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <div>
            <h2>Unmapped Sold Items</h2>
            <p>Orders can be punched fast. Items not found in product master land here for mapping/purchase follow-up.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-unmapped-sold-items.csv`, unmappedRows, [
            ["Order", (row) => row.orderNumber],
            ["Item", (row) => row.enteredProductName],
            ["Category", (row) => row.categoryGuessed],
            ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Qty", (row) => row.quantity],
            ["Status", (row) => row.status],
            ["Salesperson", (row) => row.salespersonName]
          ])}><FileSpreadsheet size={17} /> Queue sheet</button>
        </div>
        <DetailList
          rows={canManageUnmapped ? unmappedRows : myUnmappedRows}
          getTitle={(row) => row.enteredProductName || "Unmapped item"}
          getMeta={(row) => `${row.orderNumber || "-"} · ${row.categoryGuessed || "-"} · ${row.salespersonName || "-"}`}
          getStatus={(row) => row.status || "Product Master Pending"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>Order:</strong> {row.orderNumber || "-"}</p>
              <p><strong>Barcode:</strong> {row.barcode || "-"}</p>
              <p><strong>Sale:</strong> {opticalMoney(row.salePrice || 0)} · Qty {row.quantity || 1}</p>
              <p><strong>Action needed:</strong> {row.actionNeeded || "Map product / order from vendor"}</p>
              {canManageUnmapped ? (
                <div className="form-grid">
                  <ProductAutocomplete state={state} value={row.productId || ""} onSelect={(product) => product ? resolveUnmappedItem(row, product) : null} />
                  <button className="secondary-button" type="button" onClick={() => resolveUnmappedItem(row, null)}>Mark purchase follow-up</button>
                </div>
              ) : null}
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <div>
            <h2>Inventory Import / Export</h2>
            <p>Use preview first. Nothing is imported blindly.</p>
          </div>
          <div className="button-row">
            {canDownloadTemplate ? <button className="secondary-button" type="button" onClick={downloadInventoryTemplate}><Download size={17} /> Template</button> : null}
            {canExportInventory ? <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-negative-inventory.csv`, negativeRows, [
              ["Order", (row) => row.orderNumber],
              ["Item", (row) => row.enteredProductName || row.productName],
              ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
              ["Qty", (row) => row.quantity],
              ["Action", (row) => row.actionNeeded]
            ])}><FileSpreadsheet size={17} /> Negative stock</button> : null}
          </div>
        </div>
        {canImportInventory ? (
          <>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={(event) => previewInventoryImport(event.target.files?.[0])} />
            {inventoryImportPreview ? (
              <div className="order-collapse-body">
                <div className="order-workflow-kpis">
                  <div className="order-workflow-kpi"><span>Total rows</span><strong>{inventoryImportPreview.rows.length}</strong></div>
                  <div className="order-workflow-kpi"><span>Duplicates</span><strong>{inventoryImportPreview.duplicateRows}</strong></div>
                  <div className="order-workflow-kpi"><span>Needs review</span><strong>{inventoryImportPreview.missingRows}</strong></div>
                </div>
                <DetailList
                  rows={inventoryImportPreview.rows.slice(0, 10)}
                  getTitle={(row) => row.productName || "Missing product name"}
                  getMeta={(row) => `${row.productType || "-"} · ${row.barcode || "-"} · Qty ${row.quantity || 0}`}
                  getStatus={(row) => row.productName ? "Ready" : "Needs Review"}
                  renderDetails={(row) => (
                    <div className="detail-fields">
                      <p><strong>Brand:</strong> {row.brandName || "-"}</p>
                      <p><strong>Store:</strong> {row.storeCode || "-"}</p>
                      <p><strong>Supplier:</strong> {row.supplierName || "-"}</p>
                    </div>
                  )}
                />
                <div className="button-row">
                  <button className="primary-button" type="button" onClick={confirmInventoryImport}>Confirm import</button>
                  <button className="secondary-button" type="button" onClick={() => setInventoryImportPreview(null)}>Cancel</button>
                </div>
              </div>
            ) : null}
          </>
        ) : <p className="notice">Inventory import is restricted by permission.</p>}
      </article>
      {canTransferStock ? (
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>Transfer Stock</h2>
          <div className="form-grid">
            <label>Product<select value={transferForm.productId} onChange={(event) => setTransferForm({ ...transferForm, productId: event.target.value })}>{products.map((product) => <option key={product.id} value={product.id}>{product.productName || product.name}</option>)}</select></label>
            <label>From branch<select value={transferForm.fromStoreId} onChange={(event) => setTransferForm({ ...transferForm, fromStoreId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <label>To branch<select value={transferForm.toStoreId} onChange={(event) => setTransferForm({ ...transferForm, toStoreId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <Input label="Quantity" type="number" value={transferForm.quantity} onChange={(quantity) => setTransferForm({ ...transferForm, quantity })} />
          </div>
          <textarea placeholder="Transfer remarks" value={transferForm.remarks} onChange={(event) => setTransferForm({ ...transferForm, remarks: event.target.value })} />
          <button className="primary-button" type="button" onClick={saveTransfer}>Save transfer</button>
        </form>
      ) : null}
      {canAdjustStock ? (
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>Inventory Adjustment</h2>
          <div className="form-grid">
            <label>Product<select value={adjustmentForm.productId} onChange={(event) => setAdjustmentForm({ ...adjustmentForm, productId: event.target.value })}>{products.map((product) => <option key={product.id} value={product.id}>{product.productName || product.name}</option>)}</select></label>
            <label>Branch<select value={adjustmentForm.storeId} onChange={(event) => setAdjustmentForm({ ...adjustmentForm, storeId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <label>Movement<select value={adjustmentForm.movementType} onChange={(event) => setAdjustmentForm({ ...adjustmentForm, movementType: event.target.value })}>{CRM_MOVEMENT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
            <Input label="Quantity change" type="number" value={adjustmentForm.quantity} onChange={(quantity) => setAdjustmentForm({ ...adjustmentForm, quantity })} />
          </div>
          <textarea placeholder="Reason / audit note" value={adjustmentForm.remarks} onChange={(event) => setAdjustmentForm({ ...adjustmentForm, remarks: event.target.value })} />
          <button className="primary-button" type="button" onClick={saveAdjustment}>Save adjustment</button>
        </form>
      ) : null}
    </div>
    </>
  );
}

function CrmPurchaseLayer({ state, updateState, user }) {
  const [message, setMessage] = useState("");
  const canCreateStockInward = hasUserPermission(user, "can_create_stock_inward");
  const canViewCost = hasUserPermission(user, "can_view_cost_price");
  const canTransferStock = hasUserPermission(user, "can_transfer_stock") || canCreateStockInward;
  const [form, setForm] = useState({
    supplierId: "",
    vendorName: "",
    supplierGstin: "",
    invoiceNumber: "",
    billDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    storeId: state.stores[0]?.id || "",
    taxRule: "include",
    taxType: "CGST/SGST",
    supplierPaymentTerms: "",
    productId: state.crmProducts?.[0]?.id || "",
    quantity: 1,
    totalAmount: "",
    paidAmount: "",
    status: "pending",
    notes: ""
  });
  const [documentMessage, setDocumentMessage] = useState("");
  const [documentBusy, setDocumentBusy] = useState(false);
  const [extractionDraft, setExtractionDraft] = useState(null);
  const [purchaseImportType, setPurchaseImportType] = useState("purchase_bills");
  const [purchaseImportPreview, setPurchaseImportPreview] = useState(null);
  const [purchaseTab, setPurchaseTab] = useState("new_purchase");
  const [purchaseLines, setPurchaseLines] = useState([
    { id: uuid("purchase-line"), productId: "", productType: "Frame", productName: "", productCode: "", details: "", barcode: "", hsnCode: "", gstRate: 5, quantity: 1, unitPrice: "", discountAmount: "", purchasePrice: "", retailPrice: "", sellingPrice: "", notes: "" }
  ]);
  const [transferForm, setTransferForm] = useState({
    fromStoreId: state.stores[0]?.id || "",
    toStoreId: state.stores[1]?.id || state.stores[0]?.id || "",
    productId: "",
    productName: "",
    barcode: "",
    quantity: 1,
    dcNumber: "",
    transferDate: new Date().toISOString().slice(0, 10),
    proofFilePath: "",
    notes: ""
  });
  const [transferMessage, setTransferMessage] = useState("");
  const exportPrefix = filePrefix(state.settings);
  const purchases = state.crmPurchases || [];
  const purchaseInvoices = state.crmPurchaseInvoices || [];
  const suppliers = state.crmSuppliers || [];
  const stockTransfers = state.crmStockTransfers || [];
  const canImportPurchases = hasUserPermission(user, "can_import_purchase_bills") || hasUserPermission(user, "can_import_vendor_dc");
  const vendorQueue = (state.opticalFastSlips || []).filter((order) =>
    [
      ORDER_MANAGER_STATUSES.vendorRequired,
      ORDER_MANAGER_STATUSES.vendorPlaced,
      ORDER_MANAGER_STATUSES.received,
      ORDER_MANAGER_STATUSES.sentToLab,
      ORDER_MANAGER_STATUSES.delayed
    ].includes(orderWorkflowStatus(order)) || orderNeedsVendor(order)
  );
  const vendorQueueCounts = [
    ["Need Vendor Order", vendorQueue.filter((order) => orderWorkflowStatus(order) === ORDER_MANAGER_STATUSES.vendorRequired || orderNeedsVendor(order)).length],
    ["Vendor Order Placed", vendorQueue.filter((order) => orderWorkflowStatus(order) === ORDER_MANAGER_STATUSES.vendorPlaced).length],
    ["Product Received", vendorQueue.filter((order) => orderWorkflowStatus(order) === ORDER_MANAGER_STATUSES.received).length],
    ["Sent to Lab", vendorQueue.filter((order) => orderWorkflowStatus(order) === ORDER_MANAGER_STATUSES.sentToLab).length]
  ];
  const updatePurchaseLine = (lineId, patch) => {
    setPurchaseLines((current) => current.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
  };
  const addPurchaseLine = () => {
    setPurchaseLines((current) => [
      ...current,
      { id: uuid("purchase-line"), productId: "", productType: "Frame", productName: "", productCode: "", details: "", barcode: "", hsnCode: "", gstRate: 5, quantity: 1, unitPrice: "", discountAmount: "", purchasePrice: "", retailPrice: "", sellingPrice: "", notes: "" }
    ]);
  };
  const removePurchaseLine = (lineId) => {
    setPurchaseLines((current) => current.length > 1 ? current.filter((line) => line.id !== lineId) : current);
  };
  const calculatePurchaseLine = (line) => {
    const quantity = Math.max(0, Number(line.quantity || 0));
    const unitPrice = Number(line.unitPrice || line.purchasePrice || 0);
    const discountAmount = Number(line.discountAmount || 0);
    const gstRate = Math.max(0, Number(line.gstRate || 0));
    const grossAmount = quantity * unitPrice;
    const purchaseAmount = Math.max(0, grossAmount - discountAmount);
    const basePrice = form.taxRule === "include" && gstRate ? purchaseAmount / (1 + gstRate / 100) : purchaseAmount;
    const taxAmount = form.taxRule === "include" ? purchaseAmount - basePrice : basePrice * (gstRate / 100);
    const totalPurchasePrice = form.taxRule === "include" ? purchaseAmount : basePrice + taxAmount;
    return { quantity, unitPrice, discountAmount, gstRate, grossAmount, basePrice, taxAmount, totalPurchasePrice };
  };
  const purchaseLineTotals = purchaseLines.map((line) => ({ ...line, totals: calculatePurchaseLine(line) }));
  const purchaseSummary = purchaseLineTotals.reduce((summary, line) => ({
    quantity: summary.quantity + line.totals.quantity,
    unitAmount: summary.unitAmount + line.totals.grossAmount,
    discount: summary.discount + line.totals.discountAmount,
    basePrice: summary.basePrice + line.totals.basePrice,
    taxAmount: summary.taxAmount + line.totals.taxAmount,
    totalPurchasePrice: summary.totalPurchasePrice + line.totals.totalPurchasePrice
  }), { quantity: 0, unitAmount: 0, discount: 0, basePrice: 0, taxAmount: 0, totalPurchasePrice: 0 });
  const purchaseLineTotal = purchaseSummary.totalPurchasePrice;
  const creditorBalance = Math.max(0, Number(form.totalAmount || purchaseLineTotal || 0) - Number(form.paidAmount || 0));
  const supplierLedgerRows = suppliers.map((supplier) => {
    const supplierInvoices = purchaseInvoices.filter((invoice) => invoice.supplierId === supplier.id || String(invoice.supplierName || "").toLowerCase() === String(supplier.supplierName || "").toLowerCase());
    const totalBilled = supplierInvoices.reduce((sum, invoice) => sum + Number(invoice.totalAmount || 0), 0);
    const totalPaid = supplierInvoices.reduce((sum, invoice) => sum + Number(invoice.paidAmount || 0), 0);
    const inputCredit = supplierInvoices.reduce((sum, invoice) => sum + Number(invoice.inputCreditAmount || invoice.taxAmount || 0), 0);
    return { ...supplier, totalBilled, totalPaid, creditorBalance: totalBilled - totalPaid, inputCredit };
  });
  const inputCreditRows = purchaseInvoices.map((invoice) => ({
    ...invoice,
    inputCreditAmount: Number(invoice.inputCreditAmount || invoice.taxAmount || 0),
    taxableValue: Number(invoice.taxableValue || 0)
  })).filter((invoice) => invoice.inputCreditAmount || invoice.gstin || invoice.supplierName);
  const purchaseTabs = [
    ["new_purchase", "New Purchase", purchases.length],
    ["bill_dc", "Bill / DC Upload", (state.crmPurchaseInvoices || []).filter((row) => row.auditStatus === "Needs Review").length],
    ["import", "Import", state.crmPurchaseImports?.length || 0],
    ["register", "Purchase Register", purchases.length],
    ["vendors", "Vendor References", supplierLedgerRows.length],
    ["input_credit", "GST Input Credit", inputCreditRows.length],
    ["stock_transfer", "Stock Transfer", stockTransfers.length],
    ["review", "Review Queue", state.crmPurchaseInvoices?.length || 0],
    ["vendor_orders", "Vendor Orders", vendorQueue.length]
  ];
  const savePurchase = async () => {
    setMessage("");
    try {
      if (!canCreateStockInward) throw new Error("You do not have permission to create stock inward.");
      if (!form.vendorName.trim()) throw new Error("Vendor name is required.");
      const validLines = purchaseLines.filter((line) => line.productId || line.productName || line.barcode || Number(line.quantity || 0) || Number(line.purchasePrice || 0));
      if (!validLines.length) throw new Error("Add at least one purchase item.");
      const now = new Date().toISOString();
      const invoiceId = uuid("crm-purchase-invoice");
      let supplierId = form.supplierId || "";
      let supplierRecord = null;
      if (!supplierId) {
        supplierRecord = {
          id: uuid("crm-supplier"),
          supplierName: form.vendorName.trim(),
          gstin: form.supplierGstin,
          paymentTerms: form.supplierPaymentTerms,
          supplierType: "Vendor",
          active: true,
          createdAt: now,
          updatedAt: now
        };
        await persistOptionalEntity("crm_suppliers", supplierRecord).catch(() => null);
        supplierId = supplierRecord.id;
      }
      const invoiceRecord = {
        id: invoiceId,
        supplierId: supplierId || null,
        supplierName: form.vendorName.trim(),
        gstin: form.supplierGstin,
        storeId: form.storeId,
        documentType: "Purchase Bill",
        documentNo: form.invoiceNumber,
        documentDate: form.billDate,
        invoiceNumber: form.invoiceNumber,
        billDate: form.billDate,
        dueDate: form.dueDate || "",
        taxRule: form.taxRule,
        taxType: form.taxType,
        supplierPaymentTerms: form.supplierPaymentTerms,
        taxableValue: Number(purchaseSummary.basePrice || 0),
        taxAmount: Number(purchaseSummary.taxAmount || 0),
        inputCreditAmount: Number(purchaseSummary.taxAmount || 0),
        totalAmount: Number(form.totalAmount || purchaseLineTotal || 0),
        paidAmount: Number(form.paidAmount || 0),
        creditorBalance,
        lineItems: purchaseLineTotals.map((line) => ({
          productType: line.productType,
          productName: line.productName,
          productCode: line.productCode || line.barcode,
          hsnCode: line.hsnCode,
          gstRate: line.totals.gstRate,
          quantity: line.totals.quantity,
          unitPrice: line.totals.unitPrice,
          discountAmount: line.totals.discountAmount,
          basePrice: line.totals.basePrice,
          taxAmount: line.totals.taxAmount,
          totalPurchasePrice: line.totals.totalPurchasePrice,
          retailPrice: line.retailPrice || line.sellingPrice
        })),
        auditStatus: "Stock Inward Done",
        remarks: form.notes,
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      const purchaseRows = validLines.map((line) => {
        const product = (state.crmProducts || []).find((item) => item.id === line.productId);
        const totals = calculatePurchaseLine(line);
        const quantity = totals.quantity;
        const purchasePrice = totals.unitPrice;
        return {
          id: uuid("crm-purchase"),
          vendorName: form.vendorName.trim(),
          supplierId: supplierId || null,
          supplierGstin: form.supplierGstin,
          invoiceNumber: form.invoiceNumber,
          billDate: form.billDate,
          storeId: form.storeId,
          productId: line.productId || null,
          productName: product?.productName || product?.name || line.productName || "Manual item",
          productType: line.productType || product?.productType || product?.categoryName || "",
          productCode: line.productCode || product?.productCode || product?.sku || line.barcode || product?.barcode || "",
          details: line.details || line.notes || "",
          barcode: line.barcode || product?.barcode || "",
          hsnCode: line.hsnCode || product?.hsnCode || "",
          gstRate: totals.gstRate,
          quantity,
          unitPrice: totals.unitPrice,
          discountAmount: totals.discountAmount,
          basePrice: totals.basePrice,
          taxAmount: totals.taxAmount,
          inputCreditAmount: totals.taxAmount,
          purchasePrice,
          retailPrice: Number(line.retailPrice || line.sellingPrice || product?.sellingPrice || product?.mrp || 0),
          sellingPrice: Number(line.sellingPrice || line.retailPrice || product?.sellingPrice || 0),
          totalPurchasePrice: totals.totalPurchasePrice,
          totalAmount: totals.totalPurchasePrice,
          paidAmount: Number(form.paidAmount || 0),
          status: form.status,
          productMatchStatus: line.productId ? "Product Matched" : "Product Master Pending",
          notes: line.notes || form.notes,
          purchaseInvoiceId: invoiceId,
          createdAt: now,
          updatedAt: now
        };
      });
      const movements = purchaseRows.map((row) => ({
        id: uuid("crm-stock"),
        productId: row.productId || null,
        productName: row.productName,
        barcode: row.barcode || "",
        movementType: row.productId ? "Purchase Inward" : "Purchase Inward - Product Master Pending",
        quantity: Number(row.quantity || 0),
        storeId: row.storeId,
        referenceType: "crm_purchase_invoice",
        referenceId: invoiceId,
          actionNeeded: row.productId ? "" : "Map purchase item to product master",
          notes: `Purchase ${form.invoiceNumber || ""} · HSN ${row.hsnCode || "-"} · GST ${row.gstRate || 0}%`,
        createdAt: now
      }));
      const ledgerEntry = {
        id: uuid("crm-supplier-ledger"),
        supplierId: supplierId || null,
        supplierName: form.vendorName.trim(),
        invoiceId,
        documentNo: form.invoiceNumber,
        entryDate: form.billDate,
        entryType: "Purchase Bill",
        debitAmount: Number(form.paidAmount || 0),
        creditAmount: Number(form.totalAmount || purchaseLineTotal || 0),
        balanceAmount: creditorBalance,
        gstin: form.supplierGstin,
        remarks: form.notes,
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      for (const row of purchaseRows) await persistOptionalEntity("crm_purchases", row);
      for (const movement of movements) await persistOptionalEntity("crm_stock_movements", movement);
      await persistOptionalEntity("crm_purchase_invoices", invoiceRecord);
      await persistOptionalEntity("crm_supplier_ledger_entries", ledgerEntry).catch(() => null);
      updateState((current) => ({
        ...current,
        crmSuppliers: supplierRecord ? upsertById(current.crmSuppliers || [], supplierRecord) : (current.crmSuppliers || []),
        crmPurchases: [...purchaseRows, ...(current.crmPurchases || [])],
        crmPurchaseInvoices: upsertById(current.crmPurchaseInvoices || [], invoiceRecord),
        crmSupplierLedgerEntries: upsertById(current.crmSupplierLedgerEntries || [], ledgerEntry),
        crmStockMovements: [...movements, ...(current.crmStockMovements || [])],
        crmProducts: (current.crmProducts || []).map((item) => {
          const addQty = purchaseRows
            .filter((row) => row.productId === item.id)
            .reduce((sum, row) => sum + Number(row.quantity || 0), 0);
          return addQty ? { ...item, stockQty: Number(item.stockQty || 0) + addQty, updatedAt: now } : item;
        })
      }));
      setForm({ ...form, vendorName: "", supplierId: "", supplierGstin: "", invoiceNumber: "", quantity: 1, totalAmount: "", paidAmount: "", notes: "" });
      setPurchaseLines([{ id: uuid("purchase-line"), productId: "", productType: "Frame", productName: "", productCode: "", details: "", barcode: "", hsnCode: "", gstRate: 5, quantity: 1, unitPrice: "", discountAmount: "", purchasePrice: "", retailPrice: "", sellingPrice: "", notes: "" }]);
      setPurchaseTab("register");
      setMessage("Purchase saved with GST input credit, creditor balance, and stock inward.");
    } catch (error) {
      setMessage(error.message || "Purchase could not be saved.");
    }
  };
  const saveLegacySinglePurchase = async () => {
    setMessage("");
    try {
      if (!canCreateStockInward) throw new Error("You do not have permission to create stock inward.");
      if (!form.vendorName.trim()) throw new Error("Vendor name is required.");
      const now = new Date().toISOString();
      const product = (state.crmProducts || []).find((item) => item.id === form.productId);
      const row = {
        id: uuid("crm-purchase"),
        ...form,
        quantity: Number(form.quantity || 0),
        totalAmount: Number(form.totalAmount || 0),
        paidAmount: Number(form.paidAmount || 0),
        productName: product?.name || "",
        createdAt: now,
        updatedAt: now
      };
      const result = await persistOptionalEntity("crm_purchases", row);
      const movement = {
        id: uuid("crm-stock"),
        productId: form.productId,
        productName: product?.name || "",
        movementType: "purchase",
        quantity: Number(form.quantity || 0),
        notes: `Purchase ${form.invoiceNumber || ""}`,
        createdAt: now
      };
      await persistOptionalEntity("crm_stock_movements", movement);
      updateState((current) => ({
        ...current,
        crmPurchases: upsertById(current.crmPurchases || [], row),
        crmStockMovements: upsertById(current.crmStockMovements || [], movement),
        crmProducts: (current.crmProducts || []).map((item) => item.id === form.productId ? { ...item, stockQty: Number(item.stockQty || 0) + Number(form.quantity || 0), updatedAt: now } : item)
      }));
      setForm({ ...form, vendorName: "", invoiceNumber: "", quantity: 1, totalAmount: "", paidAmount: "", notes: "" });
      setMessage(result.reason || "Purchase saved and stock increased.");
    } catch (error) {
      setMessage(error.message || "Purchase could not be saved.");
    }
  };
  const startDocumentExtraction = async (file) => {
    if (!file) return;
    setDocumentMessage("");
    setDocumentBusy(true);
    try {
      if (!canCreateStockInward) throw new Error("You do not have permission to upload purchase/DC documents.");
      const now = new Date().toISOString();
      let proofFilePath = "";
      let imageText = { text: "", confidence: 0, status: "failed", message: "" };
      if (String(file.type || "").startsWith("image/")) {
        const uploaded = await uploadCompressedImage({
          file,
          module: "purchase",
          recordType: "purchase_document",
          recordId: uuid("purchase-doc"),
          storeId: form.storeId,
          fileType: "other",
          uploadedBy: user.id,
          maxWidth: 1200,
          quality: 0.66
        });
        proofFilePath = uploaded.storagePath || uploaded.path || uploaded.url || "";
        setDocumentMessage("Photo uploaded. Reading printed bill/DC text with free OCR...");
        imageText = await tryReadTextFromImage(file, "purchase bill/DC");
      }
      const fileText = String(file.name || "").replace(/\.[^.]+$/, "");
      const extractedPurchase = parsePurchaseDocumentText(`${imageText.text}\n${fileText}`, state);
      const possibleSupplier = (state.crmSuppliers || []).find((supplier) => fileText.toLowerCase().includes(String(supplier.supplierName || "").toLowerCase()));
      const guessedDocumentType = fileText.toLowerCase().includes("dc") || fileText.toLowerCase().includes("challan") ? "DC" : "Invoice";
      const draft = {
        id: uuid("crm-extraction"),
        documentType: guessedDocumentType,
        documentNo: extractedPurchase.documentNo || form.invoiceNumber || "",
        documentDate: extractedPurchase.documentDate || form.billDate || new Date().toISOString().slice(0, 10),
        supplierName: extractedPurchase.supplierName || possibleSupplier?.supplierName || form.vendorName || "",
        supplierId: extractedPurchase.supplierId || possibleSupplier?.id || "",
        storeId: form.storeId,
        gstin: extractedPurchase.gstin || possibleSupplier?.gstin || "",
        taxAmount: extractedPurchase.taxAmount || "",
        totalAmount: extractedPurchase.totalAmount || form.totalAmount || "",
        lineItemsText: extractedPurchase.lineItemsText || "",
        proofFilePath,
        originalFileName: file.name || "",
        extractedText: imageText.text,
        confidenceLevel: imageText.text ? `${Math.round((imageText.confidence || 0) * 100)}% OCR confidence` : (possibleSupplier ? "Medium confidence" : "Needs Review"),
        extractionStatus: imageText.text ? (imageText.confidence >= 0.65 ? "extracted" : "low_confidence") : "needs_review",
        extractedFields: {
          fileName: file.name,
          supplierName: extractedPurchase.supplierName,
          gstin: extractedPurchase.gstin,
          documentNo: extractedPurchase.documentNo,
          documentDate: extractedPurchase.documentDate,
          totalAmount: extractedPurchase.totalAmount,
          taxAmount: extractedPurchase.taxAmount,
          hsnCodes: extractedPurchase.hsnCodes,
          note: imageText.text ? imageText.message : "Please verify and fill missing bill/DC values before saving."
        },
        createdAt: now,
        updatedAt: now
      };
      setExtractionDraft(draft);
      setDocumentMessage(imageText.text ? "OCR preview is ready. Please verify extracted fields before saving." : "Document preview is ready. OCR could not read text; please fill missing fields before saving.");
    } catch (error) {
      setDocumentMessage(error.message || "Document could not be read.");
    } finally {
      setDocumentBusy(false);
    }
  };
  const confirmDocumentExtraction = async () => {
    setDocumentMessage("");
    try {
      if (!extractionDraft) throw new Error("No document preview to save.");
      const now = new Date().toISOString();
      const extractionRecord = {
        ...extractionDraft,
        confirmedFields: {
          supplierName: extractionDraft.supplierName,
          documentType: extractionDraft.documentType,
          documentNo: extractionDraft.documentNo,
          documentDate: extractionDraft.documentDate,
          gstin: extractionDraft.gstin,
          totalAmount: extractionDraft.totalAmount,
          taxAmount: extractionDraft.taxAmount,
          lineItemsText: extractionDraft.lineItemsText
        },
        extractionStatus: "confirmed",
        updatedAt: now
      };
      const invoiceRecord = {
        id: uuid("crm-purchase-invoice"),
        supplierId: extractionDraft.supplierId || null,
        supplierName: extractionDraft.supplierName,
        storeId: extractionDraft.storeId,
        documentType: extractionDraft.documentType,
        documentNo: extractionDraft.documentNo,
        documentDate: extractionDraft.documentDate,
        gstin: extractionDraft.gstin,
        taxAmount: Number(extractionDraft.taxAmount || 0),
        inputCreditAmount: Number(extractionDraft.taxAmount || 0),
        totalAmount: Number(extractionDraft.totalAmount || 0),
        proofFilePath: extractionDraft.proofFilePath,
        auditStatus: "Needs Review",
        remarks: extractionDraft.lineItemsText,
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      await persistOptionalEntity("crm_document_extractions", extractionRecord);
      await persistOptionalEntity("crm_purchase_invoices", invoiceRecord);
      updateState((current) => ({
        ...current,
        crmDocumentExtractions: upsertById(current.crmDocumentExtractions || [], extractionRecord),
        crmPurchaseInvoices: upsertById(current.crmPurchaseInvoices || [], invoiceRecord)
      }));
      setExtractionDraft(null);
      setDocumentMessage("Purchase/DC document saved for review.");
    } catch (error) {
      setDocumentMessage(error.message || "Document confirmation could not be saved.");
    }
  };
  const previewPurchaseImport = async (file, importType = "purchase_bills") => {
    if (!file) return;
    setDocumentMessage("");
    try {
      if (!canImportPurchases) throw new Error("You do not have permission to import purchase/DC data.");
      let rows = [];
      if (/\.(xlsx|xls)$/i.test(file.name || "")) {
        const ExcelModule = await import("exceljs");
        const ExcelJS = ExcelModule.default || ExcelModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(await file.arrayBuffer());
        const sheet = workbook.worksheets[0];
        sheet.eachRow((row) => rows.push(row.values.slice(1).map((cell) => {
          if (cell == null) return "";
          if (typeof cell === "object" && cell.text) return cell.text;
          if (typeof cell === "object" && cell.result != null) return cell.result;
          return String(cell);
        })));
      } else {
        rows = simpleCsvRows(await file.text());
      }
      const dataRows = rows.slice(1).map((cells, index) => ({
        id: `purchase-preview-${index}`,
        vendorName: cells[0] || "",
        documentType: cells[1] || "Invoice",
        documentNo: cells[2] || "",
        documentDate: cells[3] || "",
        storeCode: cells[4] || "",
        productDetails: cells[5] || "",
        quantity: Number(cells[6] || 0),
        totalAmount: Number(cells[7] || 0),
        paidAmount: Number(cells[8] || 0),
        remarks: cells[9] || ""
      }));
      setPurchaseImportPreview({
        importType,
        fileName: file.name,
        rows: dataRows,
        missingRows: dataRows.filter((row) => !row.vendorName || !row.documentNo).length
      });
      setDocumentMessage("Purchase/DC import preview ready. Confirm after checking sample rows.");
    } catch (error) {
      setDocumentMessage(error.message || "Purchase/DC import file could not be read.");
    }
  };
  const confirmPurchaseImport = async () => {
    setDocumentMessage("");
    try {
      if (!purchaseImportPreview) throw new Error("No purchase import preview to confirm.");
      const now = new Date().toISOString();
      const importBatch = {
        id: uuid("crm-purchase-import"),
        importType: purchaseImportPreview.importType,
        fileName: purchaseImportPreview.fileName,
        totalRows: purchaseImportPreview.rows.length,
        importedRows: purchaseImportPreview.rows.length - purchaseImportPreview.missingRows,
        failedRows: purchaseImportPreview.missingRows,
        previewRows: purchaseImportPreview.rows.slice(0, 20),
        summary: { missingRows: purchaseImportPreview.missingRows },
        uploadedBy: user.name || user.email,
        uploadedAt: now,
        confirmedBy: user.name || user.email,
        confirmedAt: now,
        status: "imported",
        createdAt: now,
        updatedAt: now
      };
      const invoiceRows = purchaseImportPreview.rows.filter((row) => row.vendorName && row.documentNo).map((row) => {
        const store = state.stores.find((item) => item.code === row.storeCode || item.name === row.storeCode) || state.stores[0] || {};
        return {
          id: uuid("crm-purchase-invoice"),
          supplierName: row.vendorName,
          storeId: store.id || "",
          documentType: row.documentType,
          documentNo: row.documentNo,
          documentDate: row.documentDate || new Date().toISOString().slice(0, 10),
          totalAmount: row.totalAmount,
          paidAmount: row.paidAmount,
          auditStatus: "Needs Review",
          remarks: [row.productDetails, row.remarks].filter(Boolean).join(" | "),
          createdBy: user.name || user.email,
          createdAt: now,
          updatedAt: now
        };
      });
      for (const row of invoiceRows) await persistOptionalEntity("crm_purchase_invoices", row);
      await persistOptionalEntity("crm_purchase_imports", importBatch);
      updateState((current) => ({
        ...current,
        crmPurchaseInvoices: [...invoiceRows, ...(current.crmPurchaseInvoices || [])],
        crmPurchaseImports: [importBatch, ...(current.crmPurchaseImports || [])]
      }));
      setPurchaseImportPreview(null);
      setDocumentMessage(`Purchase/DC import completed. ${invoiceRows.length} rows imported for review.`);
    } catch (error) {
      setDocumentMessage(error.message || "Purchase/DC import could not be confirmed.");
    }
  };
  const uploadTransferProof = async (file) => {
    if (!file) return;
    setTransferMessage("");
    try {
      const uploaded = await uploadCompressedImage({
        file,
        module: "purchase",
        recordType: "stock_transfer",
        recordId: uuid("stock-transfer-proof"),
        storeId: transferForm.fromStoreId,
        fileType: "other",
        uploadedBy: user.id,
        maxWidth: 1200,
        quality: 0.66
      });
      setTransferForm((current) => ({ ...current, proofFilePath: uploaded.storagePath || uploaded.path || uploaded.url || "" }));
      setTransferMessage("DC / bill photo attached. Please confirm details before saving transfer.");
    } catch (error) {
      setTransferMessage(error.message || "Transfer proof photo could not be uploaded.");
    }
  };
  const saveStockTransfer = async () => {
    setTransferMessage("");
    try {
      if (!canTransferStock) throw new Error("You do not have permission to transfer stock.");
      if (!transferForm.fromStoreId || !transferForm.toStoreId) throw new Error("Select both stores.");
      if (transferForm.fromStoreId === transferForm.toStoreId) throw new Error("From store and to store cannot be same.");
      if (!transferForm.productId && !transferForm.productName.trim()) throw new Error("Select or type product.");
      const qty = Number(transferForm.quantity || 0);
      if (qty <= 0) throw new Error("Quantity should be more than zero.");
      const now = new Date().toISOString();
      const product = (state.crmProducts || []).find((item) => item.id === transferForm.productId);
      const productName = product?.productName || product?.name || transferForm.productName;
      const transferId = uuid("crm-stock-transfer");
      const transferRecord = {
        id: transferId,
        fromStoreId: transferForm.fromStoreId,
        toStoreId: transferForm.toStoreId,
        productId: transferForm.productId || null,
        productName,
        barcode: transferForm.barcode || product?.barcode || "",
        quantity: qty,
        dcNumber: transferForm.dcNumber,
        transferDate: transferForm.transferDate,
        proofFilePath: transferForm.proofFilePath,
        status: "Transferred",
        remarks: transferForm.notes,
        createdBy: user.name || user.email,
        createdAt: now,
        updatedAt: now
      };
      const outMovement = {
        id: uuid("crm-stock"),
        productId: transferForm.productId || null,
        productName,
        barcode: transferRecord.barcode,
        movementType: "Transfer Out",
        quantity: -Math.abs(qty),
        storeId: transferForm.fromStoreId,
        fromStoreId: transferForm.fromStoreId,
        toStoreId: transferForm.toStoreId,
        referenceType: "crm_stock_transfer",
        referenceId: transferId,
        notes: `Transfer DC ${transferForm.dcNumber || ""}`,
        createdAt: now
      };
      const inMovement = {
        ...outMovement,
        id: uuid("crm-stock"),
        movementType: "Transfer In",
        quantity: Math.abs(qty),
        storeId: transferForm.toStoreId
      };
      await persistOptionalEntity("crm_stock_transfers", transferRecord);
      await persistOptionalEntity("crm_stock_movements", outMovement);
      await persistOptionalEntity("crm_stock_movements", inMovement);
      updateState((current) => ({
        ...current,
        crmStockTransfers: upsertById(current.crmStockTransfers || [], transferRecord),
        crmStockMovements: [inMovement, outMovement, ...(current.crmStockMovements || [])]
      }));
      setTransferForm({
        fromStoreId: state.stores[0]?.id || "",
        toStoreId: state.stores[1]?.id || state.stores[0]?.id || "",
        productId: "",
        productName: "",
        barcode: "",
        quantity: 1,
        dcNumber: "",
        transferDate: new Date().toISOString().slice(0, 10),
        proofFilePath: "",
        notes: ""
      });
      setTransferMessage("Stock transfer saved with transfer-out and transfer-in stock entries.");
    } catch (error) {
      setTransferMessage(error.message || "Stock transfer could not be saved.");
    }
  };
  return (
    <div className="crm-placeholder-stack">
      <article className="panel compact-module-shell">
        <div className="panel-title-row">
          <div>
            <h2>Purchase Management</h2>
            <p>Use tabs for purchase bill entry, vendor DC upload, Excel import, purchase register and vendor order follow-up.</p>
          </div>
          <span className="status-pill">{purchaseLineTotal ? `Current bill ${opticalMoney(purchaseLineTotal)}` : "Ready"}</span>
        </div>
        <div className="status-tabs crm-layer-tabs order-module-tabs">
          {purchaseTabs.map(([key, label, count]) => (
            <button key={key} className={purchaseTab === key ? "active" : ""} type="button" onClick={() => setPurchaseTab(key)}>
              {label}<span>{count}</span>
            </button>
          ))}
        </div>
      </article>
      <article className="panel" hidden={purchaseTab !== "vendor_orders"}>
        <div className="panel-title-row">
          <div>
            <h2>Vendor Orders & Purchase Inward</h2>
            <p>Order Manager decides vendor ordering. Lab Manager updates DC/product received and inward.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-vendor-order-queue.csv`, vendorQueue, [
            ["Order", (row) => row.orderNumber],
            ["Store", (row) => row.storeName || state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Customer", (row) => row.customer?.name || row.customerName || ""],
            ["Delivery", (row) => row.deliveryDate || ""],
            ["Status", (row) => orderWorkflowStatus(row)],
            ["Vendor", (row) => getOrderWorkflow(row).vendorName || ""],
            ["Vendor Ref", (row) => getOrderWorkflow(row).vendorOrderReference || ""],
            ["DC", (row) => getOrderWorkflow(row).dcNumber || ""]
          ])}>
            <FileSpreadsheet size={17} /> Vendor queue sheet
          </button>
        </div>
        <div className="order-workflow-kpis">
          {vendorQueueCounts.map(([label, count]) => (
            <div className="order-workflow-kpi" key={label}>
              <span>{label}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
        <DetailList
          rows={vendorQueue}
          getTitle={(row) => `${row.orderNumber} · ${row.customer?.name || row.customerName || "Customer"}`}
          getMeta={(row) => `${row.storeName || state.stores.find((store) => store.id === row.storeId)?.code || "-"} · Delivery ${row.deliveryDate || "-"}`}
          getStatus={(row) => orderWorkflowStatus(row)}
          renderDetails={(row) => {
            const workflow = getOrderWorkflow(row);
            return (
              <div className="detail-fields">
                <h3>{row.orderNumber}</h3>
                <p><strong>Status:</strong> {orderWorkflowStatus(row)}</p>
                <p><strong>Vendor:</strong> {workflow.vendorName || "-"}</p>
                <p><strong>Vendor order ref:</strong> {workflow.vendorOrderReference || "-"}</p>
                <p><strong>Placed at:</strong> {workflow.vendorOrderPlacedAt ? new Date(workflow.vendorOrderPlacedAt).toLocaleString("en-IN") : "-"}</p>
                <p><strong>DC:</strong> {workflow.dcNumber || "-"} {workflow.dcDate ? `· ${workflow.dcDate}` : ""}</p>
                <p><strong>Received:</strong> {workflow.productReceivedAt ? new Date(workflow.productReceivedAt).toLocaleString("en-IN") : "-"}</p>
                <p><strong>Products:</strong> {(row.orderDetails?.products || []).map((product) => `${product.name || opticalProductLabel(product.type)} (${orderItemStatus(product)})`).join(" | ") || "-"}</p>
                <p className="helper-text">Open Sales → Order History to update vendor/DC/lab actions for this order.</p>
              </div>
            );
          }}
        />
      </article>
      <div className="panel-grid two">
      {canCreateStockInward ? (
      <form className="panel form-panel" hidden={purchaseTab !== "new_purchase"} onSubmit={(event) => event.preventDefault()}>
        <h2>Add purchase</h2>
        {message ? <p className="notice">{message}</p> : null}
        <div className="form-grid">
          <VendorAutocomplete
            state={state}
            value={form.supplierId || ""}
            onSelect={(supplier) => setForm({
              ...form,
              supplierId: supplier?.id || "",
              vendorName: supplier?.supplierName || form.vendorName,
              supplierGstin: supplier?.gstin || form.supplierGstin,
              supplierPaymentTerms: supplier?.paymentTerms || supplier?.creditCycle || form.supplierPaymentTerms
            })}
          />
          <Input label="Vendor" value={form.vendorName} onChange={(vendorName) => setForm({ ...form, vendorName })} />
          <Input label="Vendor GSTIN" value={form.supplierGstin} onChange={(supplierGstin) => setForm({ ...form, supplierGstin })} />
          <Input label="Invoice number" value={form.invoiceNumber} onChange={(invoiceNumber) => setForm({ ...form, invoiceNumber })} />
          <Input label="Bill date" type="date" value={form.billDate} onChange={(billDate) => setForm({ ...form, billDate })} />
          <Input label="Due date" type="date" value={form.dueDate} onChange={(dueDate) => setForm({ ...form, dueDate })} />
          <label>Store<select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
          <label>Tax rule<select value={form.taxRule} onChange={(event) => setForm({ ...form, taxRule: event.target.value })}><option value="include">GST included in price</option><option value="exclude">GST extra</option></select></label>
          <label>Tax type<select value={form.taxType} onChange={(event) => setForm({ ...form, taxType: event.target.value })}><option>CGST/SGST</option><option>IGST</option><option>Composite / No GST</option></select></label>
          <Input label="Payment terms" value={form.supplierPaymentTerms} onChange={(supplierPaymentTerms) => setForm({ ...form, supplierPaymentTerms })} placeholder="30 days / COD / credit" />
          <Input label="Bill amount" type="number" value={form.totalAmount} onChange={(totalAmount) => setForm({ ...form, totalAmount })} placeholder={String(Math.round(purchaseLineTotal || 0))} />
          <Input label="Paid amount" type="number" value={form.paidAmount} onChange={(paidAmount) => setForm({ ...form, paidAmount })} />
          <label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option>pending</option><option>paid</option><option>part-paid</option></select></label>
        </div>
        <div className="order-workflow-kpis">
          <div className="order-workflow-kpi"><span>Total qty</span><strong>{purchaseSummary.quantity}</strong></div>
          <div className="order-workflow-kpi"><span>Taxable value</span><strong>{opticalMoney(purchaseSummary.basePrice)}</strong></div>
          <div className="order-workflow-kpi"><span>GST input credit</span><strong>{opticalMoney(purchaseSummary.taxAmount)}</strong></div>
          <div className="order-workflow-kpi"><span>Bill balance preview</span><strong>{opticalMoney(creditorBalance)}</strong></div>
        </div>
        <div className="purchase-line-stack">
          <div className="panel-title-row">
            <h3>Purchase items</h3>
            <button className="secondary-button" type="button" onClick={addPurchaseLine}>Add item</button>
          </div>
          {purchaseLines.map((line, index) => (
            <details className="extra-product-card product-card" key={line.id} open={index === purchaseLines.length - 1}>
              <summary className="product-line-summary">
                <span>
                  <strong>Item {index + 1}</strong>
                  <small>{line.productName || line.barcode || "Select product or type manually"}</small>
                </span>
                <em>{opticalMoney(Number(line.quantity || 0) * Number(line.purchasePrice || 0))}</em>
              </summary>
              <div className="product-line-body">
                <div className="form-grid">
                  <ProductAutocomplete
                    state={state}
                    value={line.productId || ""}
                    onSelect={(product) => updatePurchaseLine(line.id, product ? {
                      productId: product.id,
                      productType: product.productType || product.categoryName || line.productType,
                      productName: product.productName || product.name || line.productName,
                      productCode: product.productCode || product.sku || product.barcode || line.productCode,
                      details: product.productName || product.name || line.details,
                      barcode: product.barcode || line.barcode || "",
                      hsnCode: line.hsnCode || product.hsnCode || "",
                      gstRate: line.gstRate || product.gstRate || 5,
                      unitPrice: line.unitPrice || product.purchasePrice || product.costPrice || "",
                      purchasePrice: line.purchasePrice || product.purchasePrice || product.costPrice || "",
                      retailPrice: line.retailPrice || product.mrp || product.sellingPrice || "",
                      sellingPrice: line.sellingPrice || product.sellingPrice || product.mrp || ""
                    } : { productId: "" })}
                  />
                  <label>Product type<select value={line.productType} onChange={(event) => updatePurchaseLine(line.id, { productType: event.target.value })}>{CRM_PRODUCT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
                  <Input label="Product code / SKU" value={line.productCode} onChange={(productCode) => updatePurchaseLine(line.id, { productCode })} />
                  <Input label="Product details" value={line.productName} onChange={(productName) => updatePurchaseLine(line.id, { productName })} placeholder="Frame / lens / item name" />
                  <Input label="Barcode optional" value={line.barcode} onChange={(barcode) => updatePurchaseLine(line.id, { barcode })} />
                  <Input label="Qty" type="number" inputMode="decimal" value={line.quantity} onChange={(quantity) => updatePurchaseLine(line.id, { quantity })} />
                  {canViewCost ? <Input label="Unit price" type="number" inputMode="decimal" value={line.unitPrice || line.purchasePrice} onChange={(unitPrice) => updatePurchaseLine(line.id, { unitPrice, purchasePrice: unitPrice })} /> : null}
                  {canViewCost ? <Input label="Discount Rs" type="number" inputMode="decimal" value={line.discountAmount} onChange={(discountAmount) => updatePurchaseLine(line.id, { discountAmount })} /> : null}
                  <Input label="HSN code" value={line.hsnCode} onChange={(hsnCode) => updatePurchaseLine(line.id, { hsnCode })} />
                  <Input label="GST %" type="number" inputMode="decimal" value={line.gstRate} onChange={(gstRate) => updatePurchaseLine(line.id, { gstRate })} />
                  <Input label="Retail / MRP" type="number" inputMode="decimal" value={line.retailPrice || line.sellingPrice} onChange={(retailPrice) => updatePurchaseLine(line.id, { retailPrice, sellingPrice: retailPrice })} />
                </div>
                <div className="order-workflow-kpis compact-kpis">
                  <div className="order-workflow-kpi"><span>Base</span><strong>{opticalMoney(calculatePurchaseLine(line).basePrice)}</strong></div>
                  <div className="order-workflow-kpi"><span>GST</span><strong>{opticalMoney(calculatePurchaseLine(line).taxAmount)}</strong></div>
                  <div className="order-workflow-kpi"><span>Total</span><strong>{opticalMoney(calculatePurchaseLine(line).totalPurchasePrice)}</strong></div>
                </div>
                <label>Item notes<textarea value={line.notes} onChange={(event) => updatePurchaseLine(line.id, { notes: event.target.value })} placeholder="Any product/DC note" /></label>
                {purchaseLines.length > 1 ? <button className="mini-button danger" type="button" onClick={() => removePurchaseLine(line.id)}>Remove item</button> : null}
                {!line.productId ? <p className="helper-text">Manual item allowed. It will remain Product Master Pending until mapped.</p> : null}
              </div>
            </details>
          ))}
          <div className="extra-total-card">
            <span>Total purchase price</span>
            <strong>{opticalMoney(purchaseLineTotal)}</strong>
          </div>
        </div>
        <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        <button className="primary-button" type="button" onClick={savePurchase}>Save purchase</button>
      </form>
      ) : (
        <article className="panel" hidden={purchaseTab !== "new_purchase"}>
          <h2>Purchase / stock inward locked</h2>
          <p className="notice">Only users with stock inward permission can create purchases or increase inventory stock.</p>
        </article>
      )}
      <article className="panel form-panel" hidden={purchaseTab !== "bill_dc"}>
        <h2>Upload Bill / DC</h2>
        <p>Upload supplier bill, vendor DC, challan or invoice. The app prepares a review screen first; you confirm before saving.</p>
        {documentMessage ? <p className="notice">{documentMessage}</p> : null}
        <input type="file" accept="image/*,.pdf" onChange={(event) => startDocumentExtraction(event.target.files?.[0])} />
        {documentBusy ? <p className="helper-text">Uploading and reading document...</p> : null}
        {extractionDraft ? (
          <div className="order-collapse-body">
            <div className="form-grid">
              <label>Document type<select value={extractionDraft.documentType} onChange={(event) => setExtractionDraft({ ...extractionDraft, documentType: event.target.value })}><option>DC</option><option>Bill</option><option>Invoice</option><option>Challan</option><option>Opening Stock</option></select></label>
              <Input label="Supplier" value={extractionDraft.supplierName} onChange={(supplierName) => setExtractionDraft({ ...extractionDraft, supplierName })} />
              <Input label="Document no" value={extractionDraft.documentNo} onChange={(documentNo) => setExtractionDraft({ ...extractionDraft, documentNo })} />
              <Input label="Document date" type="date" value={extractionDraft.documentDate} onChange={(documentDate) => setExtractionDraft({ ...extractionDraft, documentDate })} />
              <Input label="GSTIN" value={extractionDraft.gstin} onChange={(gstin) => setExtractionDraft({ ...extractionDraft, gstin })} />
              <Input label="GST / tax amount" type="number" value={extractionDraft.taxAmount} onChange={(taxAmount) => setExtractionDraft({ ...extractionDraft, taxAmount })} />
              <Input label="Total amount" type="number" value={extractionDraft.totalAmount} onChange={(totalAmount) => setExtractionDraft({ ...extractionDraft, totalAmount })} />
            </div>
            <textarea placeholder="Line items / product details / remarks" value={extractionDraft.lineItemsText} onChange={(event) => setExtractionDraft({ ...extractionDraft, lineItemsText: event.target.value })} />
            {extractionDraft.extractedFields?.hsnCodes?.length ? <p className="helper-text">Possible HSN codes found: {extractionDraft.extractedFields.hsnCodes.join(", ")}</p> : null}
            <p className="helper-text">Confidence: {extractionDraft.confidenceLevel}. Please verify before saving.</p>
            <div className="button-row">
              <button className="primary-button" type="button" onClick={confirmDocumentExtraction}>Confirm and save</button>
              <button className="secondary-button" type="button" onClick={() => setExtractionDraft(null)}>Cancel</button>
            </div>
          </div>
        ) : null}
      </article>
      <article className="panel form-panel" hidden={purchaseTab !== "import"}>
        <h2>Import Purchase / Supplier Data</h2>
        <p>Use this for old supplier bills, vendor DCs, creditors or purchase sheets. Preview appears before anything is saved.</p>
        <div className="form-grid">
          <label>
            Import type
            <select value={purchaseImportType} onChange={(event) => setPurchaseImportType(event.target.value)}>
              <option value="purchase_bills">Purchase bills</option>
              <option value="vendor_dc">Vendor DC / challan</option>
              <option value="suppliers">Suppliers / creditors</option>
              <option value="opening_purchase">Opening purchase history</option>
            </select>
          </label>
          <input type="file" accept=".csv,.xlsx,.xls" disabled={!canImportPurchases} onChange={(event) => previewPurchaseImport(event.target.files?.[0], purchaseImportType)} />
        </div>
        {!canImportPurchases ? <p className="notice">Purchase imports are restricted by permission.</p> : null}
        {purchaseImportPreview ? (
          <div className="order-collapse-body">
            <div className="order-workflow-kpis">
              <div className="order-workflow-kpi"><span>Total rows</span><strong>{purchaseImportPreview.rows.length}</strong></div>
              <div className="order-workflow-kpi"><span>Needs review</span><strong>{purchaseImportPreview.missingRows}</strong></div>
            </div>
            <DetailList
              rows={purchaseImportPreview.rows.slice(0, 10)}
              getTitle={(row) => row.vendorName || "Missing vendor"}
              getMeta={(row) => `${row.documentType || "Document"} · ${row.documentNo || "-"} · ${opticalMoney(row.totalAmount || 0)}`}
              getStatus={(row) => row.vendorName && row.documentNo ? "Ready" : "Needs Review"}
              renderDetails={(row) => (
                <div className="detail-fields">
                  <p><strong>Date:</strong> {row.documentDate || "-"}</p>
                  <p><strong>Store:</strong> {row.storeCode || "-"}</p>
                  <p><strong>Items:</strong> {row.productDetails || "-"}</p>
                </div>
              )}
            />
            <div className="button-row">
              <button className="primary-button" type="button" onClick={confirmPurchaseImport}>Confirm import</button>
              <button className="secondary-button" type="button" onClick={() => setPurchaseImportPreview(null)}>Cancel</button>
            </div>
          </div>
        ) : null}
      </article>
      <article className="panel dashboard-detail" hidden={purchaseTab !== "register"}>
        <div className="panel-title-row">
          <h2>Purchase Register</h2>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-purchases.csv`, purchases, [
            ["Date", (row) => row.billDate],
            ["Vendor", (row) => row.vendorName],
            ["Invoice", (row) => row.invoiceNumber],
            ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Product", (row) => row.productName],
            ["Qty", (row) => row.quantity],
            ...(canViewCost ? [["Amount", (row) => row.totalAmount], ["Paid", (row) => row.paidAmount]] : []),
            ["Status", (row) => row.status]
          ])}><FileSpreadsheet size={17} /> Purchase sheet</button>
        </div>
        <DetailList
          rows={purchases}
          getTitle={(row) => row.vendorName}
          getMeta={(row) => `${row.invoiceNumber || "-"} · ${row.productName || "-"} · ${opticalMoney(row.totalAmount || 0)}`}
          getStatus={(row) => row.status}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.vendorName}</h3>
              <p><strong>Invoice:</strong> {row.invoiceNumber || "-"}</p>
              <p><strong>Store:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || "-"}</p>
              <p><strong>Product:</strong> {row.productName || "-"}</p>
              <p><strong>Quantity:</strong> {row.quantity || 0}</p>
              {canViewCost ? <p><strong>Amount:</strong> {opticalMoney(row.totalAmount || 0)}</p> : null}
              {canViewCost ? <p><strong>Paid:</strong> {opticalMoney(row.paidAmount || 0)}</p> : null}
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail" hidden={purchaseTab !== "vendors"}>
        <div className="panel-title-row">
          <div>
            <h2>Vendor References</h2>
            <p>Accounting ledger is disabled until proper vendor ledger is built.</p>
          </div>
          <button className="secondary-button" type="button" disabled title="Accounting ledger is disabled until proper vendor ledger is built.">
            <FileSpreadsheet size={17} /> Ledger export disabled
          </button>
        </div>
        <p className="notice">Vendor payment entry and final ledger balance are disabled. Do not use this screen for accounting.</p>
        <DetailList
          rows={supplierLedgerRows}
          getTitle={(row) => row.supplierName || "Vendor"}
          getMeta={(row) => `${row.gstin || "GSTIN pending"} · Reference only`}
          getStatus={() => "Ledger disabled"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.supplierName}</h3>
              <p className="notice">Vendor ledger coming soon — do not use for accounting.</p>
              <p><strong>GSTIN:</strong> {row.gstin || "-"}</p>
              <p><strong>Contact:</strong> {[row.contactPerson, row.phone, row.email].filter(Boolean).join(" · ") || "-"}</p>
              <p><strong>Payment terms:</strong> {row.paymentTerms || row.creditCycle || "-"}</p>
              <p><strong>Purchase summary:</strong> {opticalMoney(row.totalBilled || 0)} purchase invoices recorded. This is not a ledger balance.</p>
              <p><strong>Estimated GST input credit:</strong> {opticalMoney(row.inputCredit || 0)}</p>
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail" hidden={purchaseTab !== "input_credit"}>
        <div className="panel-title-row">
          <div>
            <h2>GST Input Credit</h2>
            <p>Month-wise GST credit from purchase bills. Use this for accountant review before filing.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-gst-input-credit.csv`, inputCreditRows, [
            ["Date", (row) => row.documentDate || row.billDate],
            ["Vendor", (row) => row.supplierName || row.vendorName],
            ["GSTIN", (row) => row.gstin],
            ["Invoice", (row) => row.documentNo || row.invoiceNumber],
            ["Tax Rule", (row) => row.taxRule],
            ["Tax Type", (row) => row.taxType],
            ["Taxable Value", (row) => row.taxableValue],
            ["Input Credit", (row) => row.inputCreditAmount],
            ["Total Bill", (row) => row.totalAmount]
          ])}><FileSpreadsheet size={17} /> Input credit sheet</button>
        </div>
        <div className="order-workflow-kpis">
          <div className="order-workflow-kpi"><span>Taxable purchase</span><strong>{opticalMoney(inputCreditRows.reduce((sum, row) => sum + Number(row.taxableValue || 0), 0))}</strong></div>
          <div className="order-workflow-kpi"><span>GST input credit</span><strong>{opticalMoney(inputCreditRows.reduce((sum, row) => sum + Number(row.inputCreditAmount || 0), 0))}</strong></div>
        </div>
        <DetailList
          rows={inputCreditRows}
          getTitle={(row) => `${row.supplierName || row.vendorName || "Vendor"} · ${row.documentNo || row.invoiceNumber || "-"}`}
          getMeta={(row) => `${row.documentDate || row.billDate || "-"} · GSTIN ${row.gstin || "-"} · Taxable ${opticalMoney(row.taxableValue || 0)}`}
          getStatus={(row) => opticalMoney(row.inputCreditAmount || 0)}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>Tax rule:</strong> {row.taxRule || "-"}</p>
              <p><strong>Tax type:</strong> {row.taxType || "-"}</p>
              <p><strong>Total bill:</strong> {opticalMoney(row.totalAmount || 0)}</p>
              <p><strong>Input credit:</strong> {opticalMoney(row.inputCreditAmount || 0)}</p>
              <p><strong>Remarks:</strong> {row.remarks || "-"}</p>
            </div>
          )}
        />
      </article>
      <article className="panel form-panel" hidden={purchaseTab !== "stock_transfer"}>
        <div className="panel-title-row">
          <div>
            <h2>Store Stock Transfer</h2>
            <p>Transfer products from one store to another with DC/photo proof and stock movement trail.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-stock-transfers.csv`, stockTransfers, [
            ["Date", (row) => row.transferDate],
            ["From", (row) => state.stores.find((store) => store.id === row.fromStoreId)?.name || ""],
            ["To", (row) => state.stores.find((store) => store.id === row.toStoreId)?.name || ""],
            ["Product", (row) => row.productName],
            ["Barcode", (row) => row.barcode],
            ["Qty", (row) => row.quantity],
            ["DC", (row) => row.dcNumber],
            ["Status", (row) => row.status]
          ])}><FileSpreadsheet size={17} /> Transfer sheet</button>
        </div>
        {transferMessage ? <p className="notice">{transferMessage}</p> : null}
        {canTransferStock ? (
          <>
            <div className="form-grid">
              <label>From store<select value={transferForm.fromStoreId} onChange={(event) => setTransferForm({ ...transferForm, fromStoreId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
              <label>To store<select value={transferForm.toStoreId} onChange={(event) => setTransferForm({ ...transferForm, toStoreId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
              <ProductAutocomplete
                state={state}
                value={transferForm.productId}
                onSelect={(product) => setTransferForm({
                  ...transferForm,
                  productId: product?.id || "",
                  productName: product?.productName || product?.name || transferForm.productName,
                  barcode: product?.barcode || transferForm.barcode
                })}
              />
              <Input label="Product if not in master" value={transferForm.productName} onChange={(productName) => setTransferForm({ ...transferForm, productName })} />
              <Input label="Barcode optional" value={transferForm.barcode} onChange={(barcode) => setTransferForm({ ...transferForm, barcode })} />
              <Input label="Qty" type="number" inputMode="decimal" value={transferForm.quantity} onChange={(quantity) => setTransferForm({ ...transferForm, quantity })} />
              <Input label="DC / Challan no." value={transferForm.dcNumber} onChange={(dcNumber) => setTransferForm({ ...transferForm, dcNumber })} />
              <Input label="Transfer date" type="date" value={transferForm.transferDate} onChange={(transferDate) => setTransferForm({ ...transferForm, transferDate })} />
              <label>Bill / DC photo<input type="file" accept="image/*,.pdf" onChange={(event) => uploadTransferProof(event.target.files?.[0])} /></label>
            </div>
            <textarea placeholder="Transfer notes" value={transferForm.notes} onChange={(event) => setTransferForm({ ...transferForm, notes: event.target.value })} />
            <button className="primary-button" type="button" onClick={saveStockTransfer}>Save stock transfer</button>
          </>
        ) : <p className="notice">Stock transfer is restricted by permission.</p>}
        <DetailList
          rows={stockTransfers}
          getTitle={(row) => row.productName || "Product transfer"}
          getMeta={(row) => `${state.stores.find((store) => store.id === row.fromStoreId)?.code || "From"} → ${state.stores.find((store) => store.id === row.toStoreId)?.code || "To"} · Qty ${row.quantity || 0}`}
          getStatus={(row) => row.status || "Transferred"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>DC:</strong> {row.dcNumber || "-"}</p>
              <p><strong>Date:</strong> {row.transferDate || "-"}</p>
              <p><strong>Barcode:</strong> {row.barcode || "-"}</p>
              <p><strong>Notes:</strong> {row.remarks || row.notes || "-"}</p>
              <PhotoPreview src={row.proofFilePath} label="DC / bill proof" />
            </div>
          )}
        />
      </article>
      <article className="panel dashboard-detail" hidden={purchaseTab !== "review"}>
        <div className="panel-title-row">
          <h2>Purchase/DC Review Queue</h2>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-crm-purchase-documents.csv`, state.crmPurchaseInvoices || [], [
            ["Supplier", (row) => row.supplierName],
            ["Type", (row) => row.documentType],
            ["Document No", (row) => row.documentNo],
            ["Date", (row) => row.documentDate],
            ["Amount", (row) => row.totalAmount],
            ["Audit", (row) => row.auditStatus]
          ])}><FileSpreadsheet size={17} /> Document sheet</button>
        </div>
        <DetailList
          rows={state.crmPurchaseInvoices || []}
          getTitle={(row) => `${row.supplierName || "Supplier"} · ${row.documentNo || "-"}`}
          getMeta={(row) => `${row.documentType || "Document"} · ${row.documentDate || "-"} · ${opticalMoney(row.totalAmount || 0)}`}
          getStatus={(row) => row.auditStatus || "Needs Review"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <p><strong>GSTIN:</strong> {row.gstin || "-"}</p>
              <p><strong>Branch:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || "-"}</p>
              <p><strong>Remarks/items:</strong> {row.remarks || "-"}</p>
              <PhotoPreview src={row.proofFilePath} label="Document proof" />
            </div>
          )}
        />
      </article>
    </div>
    </div>
  );
}

function CrmCommunicationLayer({ state, updateState, user }) {
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    customerName: "",
    customerPhone: "",
    messageType: "Follow-up",
    followUpDate: new Date().toISOString().slice(0, 10),
    status: "pending",
    notes: ""
  });
  const rows = state.crmCommunications || [];
  const saveCommunication = async () => {
    setMessage("");
    try {
      if (!form.customerName.trim()) throw new Error("Customer name is required.");
      const row = { id: uuid("crm-communication"), ...form, createdBy: user.name || user.email, createdAt: new Date().toISOString() };
      const result = await persistOptionalEntity("crm_communications", row);
      updateState((current) => ({ ...current, crmCommunications: upsertById(current.crmCommunications || [], row) }));
      setForm({ ...form, customerName: "", customerPhone: "", notes: "" });
      setMessage(result.reason || "Communication saved.");
    } catch (error) {
      setMessage(error.message || "Communication could not be saved.");
    }
  };
  const markDone = async (row) => {
    const updated = { ...row, status: "done", updatedAt: new Date().toISOString() };
    await persistOptionalEntity("crm_communications", updated);
    updateState((current) => ({ ...current, crmCommunications: upsertById(current.crmCommunications || [], updated) }));
  };
  return (
    <div className="panel-grid two">
      <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
        <h2>Add follow-up</h2>
        {message ? <p className="notice">{message}</p> : null}
        <div className="form-grid">
          <Input label="Customer" value={form.customerName} onChange={(customerName) => setForm({ ...form, customerName })} />
          <Input label="Phone" type="tel" inputMode="numeric" value={form.customerPhone} onChange={(customerPhone) => setForm({ ...form, customerPhone: customerPhone.replace(/\D/g, "") })} />
          <label>Type<select value={form.messageType} onChange={(event) => setForm({ ...form, messageType: event.target.value })}>{CRM_COMMUNICATION_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
          <Input label="Follow-up date" type="date" value={form.followUpDate} onChange={(followUpDate) => setForm({ ...form, followUpDate })} />
        </div>
        <textarea placeholder="Message / notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        <button className="primary-button" type="button" onClick={saveCommunication}>Save follow-up</button>
      </form>
      <article className="panel dashboard-detail">
        <h2>Communication Queue</h2>
        <DetailList
          rows={rows}
          getTitle={(row) => row.customerName}
          getMeta={(row) => `${row.messageType} · ${row.followUpDate || "-"}`}
          getStatus={(row) => row.status}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.customerName}</h3>
              <p><strong>Phone:</strong> {row.customerPhone || "-"}</p>
              <p><strong>Type:</strong> {row.messageType}</p>
              <p><strong>Follow-up:</strong> {row.followUpDate || "-"}</p>
              <p><strong>Notes:</strong> {row.notes || "-"}</p>
              {row.status !== "done" ? <button className="primary-button" type="button" onClick={() => markDone(row)}>Mark done</button> : null}
            </div>
          )}
        />
      </article>
    </div>
  );
}

function CrmPrescriptionLayer({ state, updateState, customers }) {
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    customerId: customers[0]?.id || "",
    customerName: customers[0]?.name || "",
    customerPhone: customers[0]?.phone || "",
    date: new Date().toISOString().slice(0, 10),
    rightEye: emptyOpticalPower(),
    leftEye: emptyOpticalPower(),
    addPower: "",
    pd: "",
    notes: ""
  });
  const rows = state.crmPrescriptions || [];
  const setEye = (eye, field, value) => setForm((current) => ({ ...current, [eye]: { ...current[eye], [field]: value } }));
  const savePrescription = async () => {
    setMessage("");
    try {
      if (!form.customerName.trim()) throw new Error("Customer name is required.");
      const row = { id: uuid("crm-prescription"), ...form, createdAt: new Date().toISOString() };
      const result = await persistOptionalEntity("crm_prescriptions", row);
      updateState((current) => ({ ...current, crmPrescriptions: upsertById(current.crmPrescriptions || [], row) }));
      setMessage(result.reason || "Prescription saved.");
    } catch (error) {
      setMessage(error.message || "Prescription could not be saved.");
    }
  };
  return (
    <div className="panel-grid two">
      <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
        <h2>Add prescription / eye test</h2>
        {message ? <p className="notice">{message}</p> : null}
        <div className="form-grid">
          <label>Customer<select value={form.customerId} onChange={(event) => {
            const customer = customers.find((item) => item.id === event.target.value);
            setForm({ ...form, customerId: event.target.value, customerName: customer?.name || "", customerPhone: customer?.phone || "" });
          }}><option value="">Manual customer</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name || customer.walkInId}</option>)}</select></label>
          <Input label="Customer name" value={form.customerName} onChange={(customerName) => setForm({ ...form, customerName })} />
          <Input label="Phone" type="tel" inputMode="numeric" value={form.customerPhone} onChange={(customerPhone) => setForm({ ...form, customerPhone: customerPhone.replace(/\D/g, "") })} />
          <Input label="Date" type="date" value={form.date} onChange={(date) => setForm({ ...form, date })} />
        </div>
        <div className="optical-power-list">
          {["rightEye", "leftEye"].map((eye) => (
            <div className="optical-power-card" key={eye}>
              <strong>{eye === "rightEye" ? "Right Eye" : "Left Eye"}</strong>
              <div className="optical-power-grid">
                <Input label="SPH" value={form[eye].sph} onChange={(sph) => setEye(eye, "sph", sph)} />
                <Input label="CYL" value={form[eye].cyl} onChange={(cyl) => setEye(eye, "cyl", cyl)} />
                <Input label="AX" value={form[eye].ax} onChange={(ax) => setEye(eye, "ax", ax)} />
              </div>
            </div>
          ))}
        </div>
        <div className="form-grid">
          <Input label="ADD" value={form.addPower} onChange={(addPower) => setForm({ ...form, addPower })} />
          <Input label="PD" value={form.pd} onChange={(pd) => setForm({ ...form, pd })} />
        </div>
        <textarea placeholder="Prescription notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        <button className="primary-button" type="button" onClick={savePrescription}>Save prescription</button>
      </form>
      <article className="panel dashboard-detail">
        <h2>Prescription Records</h2>
        <DetailList
          rows={rows}
          getTitle={(row) => row.customerName}
          getMeta={(row) => `${row.date} · R ${row.rightEye?.sph || "-"} / L ${row.leftEye?.sph || "-"}`}
          getStatus={() => "RX"}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.customerName}</h3>
              <p><strong>Phone:</strong> {row.customerPhone || "-"}</p>
              <p><strong>Right:</strong> {opticalPowerLine(row.rightEye)}</p>
              <p><strong>Left:</strong> {opticalPowerLine(row.leftEye)}</p>
              <p><strong>ADD:</strong> {row.addPower || "-"}</p>
              <p><strong>PD:</strong> {row.pd || "-"}</p>
              <p><strong>Notes:</strong> {row.notes || "-"}</p>
            </div>
          )}
        />
      </article>
    </div>
  );
}

function CrmAppointmentLayer({ state, updateState, customers }) {
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    customerId: customers[0]?.id || "",
    customerName: customers[0]?.name || "",
    customerPhone: customers[0]?.phone || "",
    appointmentDate: new Date().toISOString().slice(0, 10),
    appointmentTime: "12:00",
    purpose: "Eye test",
    status: "scheduled",
    notes: ""
  });
  const rows = state.crmAppointments || [];
  const saveAppointment = async () => {
    setMessage("");
    try {
      if (!form.customerName.trim()) throw new Error("Customer name is required.");
      const row = { id: uuid("crm-appointment"), ...form, createdAt: new Date().toISOString() };
      const result = await persistOptionalEntity("crm_appointments", row);
      updateState((current) => ({ ...current, crmAppointments: upsertById(current.crmAppointments || [], row) }));
      setMessage(result.reason || "Appointment saved.");
    } catch (error) {
      setMessage(error.message || "Appointment could not be saved.");
    }
  };
  const updateAppointment = async (row, changes) => {
    const updated = { ...row, ...changes, updatedAt: new Date().toISOString() };
    await persistOptionalEntity("crm_appointments", updated);
    updateState((current) => ({ ...current, crmAppointments: upsertById(current.crmAppointments || [], updated) }));
  };
  return (
    <div className="panel-grid two">
      <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
        <h2>Add appointment</h2>
        {message ? <p className="notice">{message}</p> : null}
        <div className="form-grid">
          <label>Customer<select value={form.customerId} onChange={(event) => {
            const customer = customers.find((item) => item.id === event.target.value);
            setForm({ ...form, customerId: event.target.value, customerName: customer?.name || "", customerPhone: customer?.phone || "" });
          }}><option value="">Manual customer</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name || customer.walkInId}</option>)}</select></label>
          <Input label="Customer name" value={form.customerName} onChange={(customerName) => setForm({ ...form, customerName })} />
          <Input label="Phone" type="tel" inputMode="numeric" value={form.customerPhone} onChange={(customerPhone) => setForm({ ...form, customerPhone: customerPhone.replace(/\D/g, "") })} />
          <Input label="Date" type="date" value={form.appointmentDate} onChange={(appointmentDate) => setForm({ ...form, appointmentDate })} />
          <Input label="Time" type="time" value={form.appointmentTime} onChange={(appointmentTime) => setForm({ ...form, appointmentTime })} />
          <Input label="Purpose" value={form.purpose} onChange={(purpose) => setForm({ ...form, purpose })} />
        </div>
        <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        <button className="primary-button" type="button" onClick={saveAppointment}>Save appointment</button>
      </form>
      <article className="panel dashboard-detail">
        <h2>Appointments</h2>
        <DetailList
          rows={rows}
          getTitle={(row) => row.customerName}
          getMeta={(row) => `${row.appointmentDate} ${row.appointmentTime || ""} · ${row.purpose || "-"}`}
          getStatus={(row) => row.status}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.customerName}</h3>
              <p><strong>Phone:</strong> {row.customerPhone || "-"}</p>
              <p><strong>Date:</strong> {row.appointmentDate} {row.appointmentTime}</p>
              <p><strong>Purpose:</strong> {row.purpose}</p>
              <p><strong>Notes:</strong> {row.notes || "-"}</p>
              <div className="button-row">
                <button className="primary-button" type="button" onClick={() => updateAppointment(row, { status: "done" })}>Mark done</button>
                <button className="secondary-button" type="button" onClick={() => updateAppointment(row, { status: "cancelled" })}>Cancel</button>
              </div>
            </div>
          )}
        />
      </article>
    </div>
  );
}

function CrmReportsLayer({ state, orders, customers }) {
  const exportPrefix = filePrefix(state.settings);
  const reportCards = [
    ["Sales Report", `${exportPrefix}-crm-sales.csv`, orders, [["Order", (row) => row.orderNumber], ["Date", (row) => row.date], ["Customer", (row) => row.customer?.name], ["Total", (row) => row.payment?.totalAmount], ["Discount", (row) => row.payment?.discountAmount || 0], ["Balance", (row) => computedOrderBalance(row)]]],
    ["Customer Report", `${exportPrefix}-crm-customers.csv`, customers, [["Name", (row) => row.name], ["Phone", (row) => row.phone || row.walkInId], ["Email", (row) => row.email], ["Address", (row) => row.address]]],
    ["Inventory Report", `${exportPrefix}-crm-inventory.csv`, state.crmProducts || [], [["Code", (row) => row.productCode], ["Name", (row) => row.name], ["Type", (row) => row.productType], ["Stock", (row) => row.stockQty], ["Selling", (row) => row.sellingPrice]]],
    ["Purchase Report", `${exportPrefix}-crm-purchases.csv`, state.crmPurchases || [], [["Date", (row) => row.billDate], ["Vendor", (row) => row.vendorName], ["Invoice", (row) => row.invoiceNumber], ["Amount", (row) => row.totalAmount], ["Paid", (row) => row.paidAmount]]],
    ["Payment Due Report", `${exportPrefix}-crm-dues.csv`, orders.filter((row) => computedOrderBalance(row) > 0), [["Order", (row) => row.orderNumber], ["Customer", (row) => row.customer?.name], ["Phone", (row) => row.customer?.phone], ["Balance", (row) => computedOrderBalance(row)]]],
    ["Prescription Report", `${exportPrefix}-crm-prescriptions.csv`, state.crmPrescriptions || [], [["Date", (row) => row.date], ["Customer", (row) => row.customerName], ["Right", (row) => opticalPowerLine(row.rightEye)], ["Left", (row) => opticalPowerLine(row.leftEye)], ["ADD", (row) => row.addPower]]],
    ["Appointment Report", `${exportPrefix}-crm-appointments.csv`, state.crmAppointments || [], [["Date", (row) => row.appointmentDate], ["Time", (row) => row.appointmentTime], ["Customer", (row) => row.customerName], ["Purpose", (row) => row.purpose], ["Status", (row) => row.status]]],
    ["Communication Report", `${exportPrefix}-crm-communications.csv`, state.crmCommunications || [], [["Follow-up", (row) => row.followUpDate], ["Customer", (row) => row.customerName], ["Phone", (row) => row.customerPhone], ["Type", (row) => row.messageType], ["Status", (row) => row.status]]]
  ];
  return (
    <article className="panel">
      <h2>CRM Reports</h2>
      <div className="module-tile-grid">
        {reportCards.map(([label, filename, rows, columns]) => (
          <button className="module-tile-card report-tile-button" type="button" key={label} onClick={() => downloadSheet(filename, rows, columns)}>
            <FileText size={26} />
            <strong>{label}</strong>
            <small>{rows.length} rows · Download sheet</small>
          </button>
        ))}
      </div>
    </article>
  );
}

function OpticalOrdersPanel({ state, updateState, employees, user, addAudit, employeeMode = false }) {
  const activeEmployees = employees.filter((employee) => employee.active !== false);
  const allowedStoreIds = [...new Set(activeEmployees.map((employee) => employee.storeId).filter(Boolean))];
  const stores = state.stores.filter((store) => user.role === "super_admin" || allowedStoreIds.includes(store.id) || user.storeIds?.includes(store.id));
  const currentEmployee = state.employees.find((employee) => employee.id === user.employeeId);
  const defaultStoreId = currentEmployee?.storeId || stores[0]?.id || "";
  const defaultSalespersonId = currentEmployee?.id || activeEmployees[0]?.id || "";
  const [form, setForm] = useState(() => emptyOpticalForm({
    storeId: defaultStoreId,
    salespersonId: defaultSalespersonId,
    orderNumber: nextOpticalOrderNumber(state.opticalFastSlips)
  }));
  const [message, setMessage] = useState("");
  const [orderTab, setOrderTab] = useState(employeeMode ? "new" : "history");
  const showHistory = orderTab !== "new";
  const [editingSlipId, setEditingSlipId] = useState("");
  const [lastSavedOrder, setLastSavedOrder] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const { actionState: orderActionState, runAction: runOrderActionBase } = useActionStatus();
  const [collectBalanceOrderId, setCollectBalanceOrderId] = useState("");
  const [collectBalanceForm, setCollectBalanceForm] = useState({
    receivedBy: user.name || user.email || "",
    rows: [emptyCollectBalanceRow()]
  });
  const [orderActionDraft, setOrderActionDraft] = useState({
    vendorName: "",
    vendorOrderReference: "",
    orderSentMethod: "WhatsApp",
    remarks: "",
    dcNumber: "",
    dcDate: new Date().toISOString().slice(0, 10),
    receivedQuantity: "",
    receivedProductDetails: "",
    sentThrough: ""
  });
  const [statusCorrectionModal, setStatusCorrectionModal] = useState(null);
  const collectBalanceOrder = (state.opticalFastSlips || []).find((row) => row.id === collectBalanceOrderId) || null;

  const store = state.stores.find((item) => item.id === form.storeId);
  const salesperson = state.employees.find((item) => item.id === form.salespersonId) || currentEmployee;
  const products = form.products || [];
  const productTotal = products.reduce((sum, product) => sum + Number(product.quantity || 1) * Number(product.amount || 0), 0);
  const subtotalAmount = Number(form.totalAmount || productTotal || 0);
  const rawDiscountValue = Math.max(0, Number(form.discountValue || 0));
  const discountAmount = Math.min(subtotalAmount, rawDiscountValue);
  const totalAmount = Math.max(subtotalAmount - discountAmount, 0);
  const extraProductsTotal = (form.extraProducts || []).reduce(
    (sum, product) => sum + Number(product.quantity || 1) * Number(product.amount || 0),
    0
  );
  const splitTotal = Object.values(form.splitPayments || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  const advanceReceived = form.paymentMode === "Split" ? splitTotal : Number(form.advanceReceived || 0);
  const balance = Math.max(totalAmount - advanceReceived, 0);
  const ownStoreSlips = (state.opticalFastSlips || []).filter((slip) =>
    user.role === "super_admin" || allowedStoreIds.includes(slip.storeId) || user.storeIds?.includes(slip.storeId)
  );
  const needsPowerSection = products.some(productNeedsPower);
  const needsContactLensSection = products.some(productNeedsContactLens);
  const canPrintOrderFormSlip = user.role === "employee" || user.role === "admin" || user.role === "super_admin" || hasUserPermission(user, "can_print_order_form");
  const canOrderManager =
    user.role === "super_admin" ||
    user.role === "admin" ||
    hasUserPermission(user, "can_view_order_manager_queue") ||
    hasUserPermission(user, "can_decide_stock_or_vendor_order");
  const canDecideOrder = user.role === "super_admin" || user.role === "admin" || hasUserPermission(user, "can_decide_stock_or_vendor_order");
  const canPlaceVendorOrder = user.role === "super_admin" || user.role === "admin" || hasUserPermission(user, "can_mark_order_placed_with_vendor");
  const canReceiveVendorProduct =
    user.role === "super_admin" ||
    user.role === "admin" ||
    hasUserPermission(user, "can_mark_product_received_from_vendor") ||
    hasUserPermission(user, "can_mark_lab_received");
  const canSeeOrderTimeline =
    user.role === "super_admin" ||
    user.role === "admin" ||
    hasUserPermission(user, "can_view_order_timeline") ||
    canOrderManager ||
    user.role === "employee";
  const canCollectOrderPayment = user.role === "super_admin" || user.role === "admin" || hasUserPermission(user, "can_receive_payment");
  const canMarkOrderDelivered = user.role === "super_admin" || user.role === "admin" || hasUserPermission(user, "can_mark_delivered");
  const canCorrectOrderStatus = canAdminCorrectStatus(user, "can_override_order_status");

  const runOrderAction = (key, labels, action) =>
    runOrderActionBase(
      key,
      labels,
      async (setStep) => {
        setMessage(labels.loading || "Updating...");
        const result = await action(setStep);
        setMessage(labels.successMessage || labels.success || "Updated.");
        return result;
      },
      (error) => setMessage(error?.message || labels.errorMessage || labels.error || "Action failed. Please try again.")
    );
  const orderSheetColumns = [
    ["Order", (row) => row.orderNumber],
    ["Date", (row) => row.date],
    ["Delivery", (row) => row.deliveryDate || ""],
    ["Customer", (row) => row.customer?.name || row.customerName || ""],
    ["Phone / Walk-in", (row) => row.customer?.phone || row.customer?.walkInId || ""],
    ["Store", (row) => row.storeName || state.stores.find((item) => item.id === row.storeId)?.name || ""],
    ["Salesperson", (row) => row.salespersonName || ""],
    ["Order type", (row) => labelForOpticalOrderType(row.orderDetails?.orderType)],
    ["Items", (row) => selectedItemsLabel(row)],
    ["Products", (row) => (row.orderDetails?.products || row.orderDetails?.extraProducts || []).map((product) => `${product.quantity || 1} x ${product.name || opticalProductLabel(product.type)} (${product.amount || 0})`).join(" | ")],
    ["Frame / lens pairs", (row) => (row.orderDetails?.opticalPairs || []).map((pair, index) => `${index + 1}. ${pair.frameDetails || pair.frameStatus || "Frame"} + ${pair.lensDetails || pair.lensStatus || "Lens"}`).join(" | ")],
    ["Frame status", (row) => row.orderDetails?.frameStatus || ""],
    ["Lens status", (row) => row.orderDetails?.lensStatus || ""],
    ["Tint", (row) => (row.orderDetails?.opticalPairs || []).some((pair) => pair.tintRequired) ? "Yes" : "No"],
    ["Contact lens", (row) => row.orderDetails?.contactLensDetails || ""],
    ["Subtotal", (row) => row.payment?.subtotalAmount ?? row.payment?.totalAmount ?? 0],
    ["Discount", (row) => row.payment?.discountAmount || 0],
    ["Total", (row) => row.payment?.totalAmount || 0],
    ["Advance", (row) => row.payment?.advanceReceived || 0],
    ["Balance", (row) => computedOrderBalance(row)],
    ["Payment", (row) => row.payment?.paymentMode || ""],
    ["Last edit", (row) => row.orderDetails?.lastEditedAt || ""]
  ];

  const updatePair = (pairId, changes) => {
    setForm((current) => ({
      ...current,
      opticalPairs: (current.opticalPairs || []).map((pair) => (pair.id === pairId ? { ...pair, ...changes } : pair))
    }));
  };

  const setOrderType = (orderType) => {
    setForm((current) => {
      const selectedItems = itemsForOpticalOrderType(orderType);
      const needsStarterExtra = ["contact_solution", "contact_lens", "solution", "service"].includes(orderType) && !(current.extraProducts || []).length;
      return {
        ...current,
        orderType,
        selectedItems,
        extraProducts: needsStarterExtra
          ? [emptyOpticalExtraProduct(orderType === "service" ? "service" : "contactSolution")]
          : current.extraProducts,
        opticalPairs: orderType === "frame_lens" || orderType === "frame_only" || orderType === "lens_only"
          ? (current.opticalPairs?.length ? current.opticalPairs : [emptyOpticalPair()])
          : current.opticalPairs
      };
    });
  };

  const updateExtraProduct = (productId, changes) => {
    setForm((current) => ({
      ...current,
      extraProducts: (current.extraProducts || []).map((product) => (product.id === productId ? { ...product, ...changes } : product))
    }));
  };

  const addExtraProduct = () => {
    setForm((current) => ({ ...current, extraProducts: [...(current.extraProducts || []), emptyOpticalExtraProduct()] }));
  };

  const removeExtraProduct = (productId) => {
    setForm((current) => ({
      ...current,
      extraProducts: (current.extraProducts || []).filter((product) => product.id !== productId)
    }));
  };

  const updateProduct = (productId, changes) => {
    setForm((current) => ({
      ...current,
      products: (current.products || []).map((product) => (product.id === productId ? { ...product, ...changes } : product))
    }));
  };

  const addProduct = (type = "frame") => {
    setForm((current) => ({ ...current, products: [...(current.products || []), emptyOpticalProduct(type)] }));
  };

  const removeProduct = (productId) => {
    setForm((current) => ({
      ...current,
      products: (current.products || []).length > 1
        ? (current.products || []).filter((product) => product.id !== productId)
        : current.products
    }));
  };

  const addProductPhoto = async (productId, file) => {
    try {
      setMessage("Compressing and uploading product photo...");
      const upload = await uploadAppImage({
        file,
        module: "orders",
        recordType: "optical_order_product",
        recordId: `${form.orderNumber || "draft"}-${productId}`,
        storeId: form.storeId,
        staffId: salesperson?.id || user.employeeId,
        fileType: "order_photo",
        uploadedBy: user.id
      });
      updateProduct(productId, { photo: upload.storagePath || upload.path || upload.url });
      setMessage("Photo uploaded.");
    } catch (error) {
      setMessage(error.message || "Photo could not be added.");
    }
  };

  const addPair = () => {
    setForm((current) => ({ ...current, opticalPairs: [...(current.opticalPairs || []), emptyOpticalPair()] }));
  };

  const removePair = (pairId) => {
    setForm((current) => ({
      ...current,
      opticalPairs: (current.opticalPairs || []).length > 1
        ? (current.opticalPairs || []).filter((pair) => pair.id !== pairId)
        : current.opticalPairs
    }));
  };

  const addPairPhoto = async (pairId, field, file) => {
    try {
      setMessage("Compressing and uploading photo...");
      const upload = await uploadAppImage({
        file,
        module: "orders",
        recordType: "optical_order_pair",
        recordId: `${form.orderNumber || "draft"}-${pairId}`,
        storeId: form.storeId,
        staffId: salesperson?.id || user.employeeId,
        fileType: field === "tintSamplePhoto" ? "prescription_photo" : "order_photo",
        uploadedBy: user.id
      });
      updatePair(pairId, { [field]: upload.storagePath || upload.path || upload.url });
      setMessage("Photo uploaded.");
    } catch (error) {
      setMessage(error.message || "Photo could not be added.");
    }
  };

  const addOwnFramePhoto = async (file) => {
    try {
      setMessage("Compressing and uploading own frame photo...");
      const upload = await uploadAppImage({
        file,
        module: "orders",
        recordType: "optical_order",
        recordId: form.orderNumber || "draft",
        storeId: form.storeId,
        staffId: salesperson?.id || user.employeeId,
        fileType: "order_photo",
        uploadedBy: user.id
      });
      setForm((current) => ({ ...current, ownFramePhoto: upload.storagePath || upload.path || upload.url }));
      setMessage("Photo uploaded.");
    } catch (error) {
      setMessage(error.message || "Photo could not be added.");
    }
  };

  const loadSlipForEdit = (slip) => {
    const createdAt = new Date(slip.createdAt || slip.updatedAt || Date.now()).getTime();
    const within30Minutes = Date.now() - createdAt <= 30 * 60 * 1000;
    const isOriginalSalesperson = slip.salespersonId && slip.salespersonId === user.employeeId;
    const canEdit = user.role !== "employee" || (isOriginalSalesperson && within30Minutes);
    if (!canEdit) {
      setMessage("Salesperson can edit only within 30 minutes. Admin can edit from management login.");
      return;
    }
    const details = slip.orderDetails || {};
    const power = slip.power || {};
    const payment = slip.payment || {};
    setEditingSlipId(slip.id);
    setForm({
      orderNumber: slip.orderNumber,
      date: slip.date || new Date().toISOString().slice(0, 10),
      deliveryDate: slip.deliveryDate || new Date().toISOString().slice(0, 10),
      storeId: slip.storeId || defaultStoreId,
      salespersonId: slip.salespersonId || defaultSalespersonId,
      customerId: slip.customerId || slip.customer?.id || "",
      phone: slip.customer?.phone || "",
      customerName: slip.customer?.name || "",
      address: slip.customer?.address || "",
      dob: slip.customer?.dob || "",
      email: slip.customer?.email || "",
      noPhone: Boolean(slip.customer?.noPhone || !slip.customer?.phone),
      products: productsFromLegacyOrder(details),
      orderType: details.orderType || "frame_lens",
      selectedItems: details.selectedItems || itemsForOpticalOrderType(details.orderType || "frame_lens"),
      extraProducts: details.extraProducts || [],
      opticalPairs: details.opticalPairs?.length ? details.opticalPairs : [emptyOpticalPair()],
      frameDetails: details.frameDetails || "",
      frameSource: details.frameSource || "store_frame",
      frameStatus: details.frameStatus || "ready",
      ownFramePhoto: details.ownFramePhoto || "",
      lensDetails: details.lensDetails || "",
      lensStatus: details.lensStatus || "ready",
      serviceDetails: details.serviceDetails || "",
      contactLensDetails: details.contactLensDetails || "",
      specialNotes: details.specialNotes || "",
      rightDistance: power.rightEye?.distance || emptyOpticalPower(),
      leftDistance: power.leftEye?.distance || emptyOpticalPower(),
      sameAddition: true,
      rightAdd: power.rightEye?.add || { sph: "+0.25", cyl: "", ax: "" },
      leftAdd: power.leftEye?.add || { sph: "+0.25", cyl: "", ax: "" },
      autoContactPower: Boolean(power.contactLens?.autoConverted),
      contactRight: power.contactLens?.rightEye || emptyOpticalPower(),
      contactLeft: power.contactLens?.leftEye || emptyOpticalPower(),
      measurements: power.measurements || { pd: "", fh: "", a: "", b: "", dbl: "", dia: "" },
      prescribedBy: power.prescribedBy || "",
      totalAmount: payment.subtotalAmount ?? payment.totalAmount ?? "",
      discountType: payment.discountType || "amount",
      discountValue: payment.discountValue || "",
      advanceReceived: payment.advanceReceived || "",
      paymentMode: payment.paymentMode || "UPI",
      splitPayments: payment.splitPayments || { cash: "", upi: "", card: "", paytm: "" },
      paymentNotes: payment.notes || ""
    });
    setOrderTab("new");
    setMessage(`Editing ${slip.orderNumber}.`);
  };

  const saveOrderWorkflowUpdate = async (order, changes, eventType, note, extra = {}) => {
    const currentWorkflow = getOrderWorkflow(order);
    const oldStatus = currentWorkflow.status || orderWorkflowStatus(order);
    const newWorkflow = { ...currentWorkflow, ...changes };
    const newStatus = newWorkflow.status || oldStatus;
    const statusCorrections = extra.statusCorrection
      ? [extra.statusCorrection, ...(order.orderDetails?.statusCorrections || [])]
      : (order.orderDetails?.statusCorrections || []);
    const timeline = [
      orderTimelineEvent({ eventType, oldStatus, newStatus, note, user, extra }),
      ...getOrderTimeline(order)
    ];
    const updated = {
      ...order,
      orderDetails: {
        ...(order.orderDetails || {}),
        managerWorkflow: newWorkflow,
        statusCorrections,
        orderTimeline: timeline
      },
      updatedAt: new Date().toISOString()
    };
    const savedOrder = await persistRequiredEntity("optical_fast_slips", updated, "Order status");
    updateState((current) => ({
      ...current,
      opticalFastSlips: (current.opticalFastSlips || []).map((item) => (item.id === savedOrder.id ? savedOrder : item))
    }));
    setMessage(`${savedOrder.orderNumber || order.orderNumber} updated: ${newStatus}`);
    await notifySuperAdmins(state, updateState, {
      module: "Orders",
      title: "Order Updated",
      message: `Order ${savedOrder.orderNumber || order.orderNumber} moved from ${oldStatus} to ${newStatus}.`,
      priority: extra.statusCorrection ? "high" : "normal",
      entityType: "optical_fast_slip",
      entityId: savedOrder.id,
      storeId: savedOrder.storeId,
      createdBy: user.id
    });
    return savedOrder;
  };

  const openOrderStatusCorrection = (order, newStatus, label) => {
    const oldStatus = orderWorkflowStatus(order);
    setStatusCorrectionModal({
      orderId: order.id,
      orderNumber: order.orderNumber,
      oldStatus,
      newStatus,
      label,
      reason: ""
    });
  };

  const confirmOrderStatusCorrection = async () => {
    const draft = statusCorrectionModal;
    if (!draft) return;
    if (!canCorrectOrderStatus) {
      setMessage("Only Admin / Super Admin can move status backward.");
      return;
    }
    if (!draft.reason.trim()) {
      setMessage("Reason is required before changing status backward.");
      return;
    }
    const order = (state.opticalFastSlips || []).find((row) => row.id === draft.orderId);
    if (!order) {
      setMessage("Order not found. Please refresh and try again.");
      return;
    }
    const correction = statusCorrectionRecord({
      oldStatus: draft.oldStatus,
      newStatus: draft.newStatus,
      reason: draft.reason.trim(),
      user,
      module: "order"
    });
    await runOrderAction(
      `order-status-correction-${draft.orderId}-${draft.newStatus}`,
      {
        loading: "Updating Status...",
        success: `${draft.label} ✓`,
        successMessage: `${draft.orderNumber} moved to ${draft.newStatus}.`,
        error: "Could not update. Try Again"
      },
      async () => {
        const updated = await saveOrderWorkflowUpdate(
          order,
          {
            status: draft.newStatus,
            statusCorrectedAt: correction.changedAt,
            statusCorrectedBy: correction.changedByName,
            statusCorrectionReason: correction.reason
          },
          "Admin status correction",
          `Status changed from ${draft.oldStatus} to ${draft.newStatus}. Reason: ${correction.reason}`,
          { correction: true, correctionReason: correction.reason, statusCorrection: correction }
        );
        await persistOptionalEntity("status_change_logs", {
          id: correction.id,
          module: "order",
          recordType: "optical_fast_slip",
          recordId: order.id,
          oldStatus: correction.oldStatus,
          newStatus: correction.newStatus,
          changedBy: correction.changedBy,
          changedByName: correction.changedByName,
          reason: correction.reason,
          createdAt: correction.changedAt
        }).catch(() => null);
        return updated;
      }
    );
    setStatusCorrectionModal(null);
  };

  const openCollectBalance = (order) => {
    setCollectBalanceOrderId(order.id);
    setCollectBalanceForm({
      receivedBy: user.name || user.email || "",
      rows: [emptyCollectBalanceRow("")]
    });
  };

  const collectBalanceRows = collectBalanceForm.rows?.length ? collectBalanceForm.rows : [emptyCollectBalanceRow()];
  const collectBalanceTotal = collectBalanceRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const collectBalancePending = collectBalanceOrder ? computedOrderBalance(collectBalanceOrder) : 0;
  const collectBalanceRemaining = Math.max(0, collectBalancePending - collectBalanceTotal);

  const updateCollectBalanceRow = (rowId, changes) => {
    setCollectBalanceForm((current) => ({
      ...current,
      rows: (current.rows?.length ? current.rows : [emptyCollectBalanceRow()]).map((row) =>
        row.id === rowId ? { ...row, ...changes } : row
      )
    }));
  };

  const addCollectBalanceRow = () => {
    setCollectBalanceForm((current) => ({
      ...current,
      rows: [...(current.rows?.length ? current.rows : [emptyCollectBalanceRow()]), emptyCollectBalanceRow()]
    }));
  };

  const removeCollectBalanceRow = (rowId) => {
    setCollectBalanceForm((current) => {
      const nextRows = (current.rows || []).filter((row) => row.id !== rowId);
      return { ...current, rows: nextRows.length ? nextRows : [emptyCollectBalanceRow()] };
    });
  };

  const saveBalanceCollection = async () => {
    const order = collectBalanceOrder;
    if (!order) throw new Error("Order not found. Please refresh and try again.");
    const balanceBefore = computedOrderBalance(order);
    const normalizedRows = (collectBalanceForm.rows || [])
      .map((row) => ({
        ...row,
        amount: Number(row.amount || 0),
        paymentMode: String(row.paymentMode || "").trim(),
        receivedAt: row.receivedAt || "",
        note: String(row.note || "").trim()
      }))
      .filter((row) => row.amount || row.paymentMode || row.note);
    if (!normalizedRows.length) throw new Error("Add at least one payment row.");
    const invalidRow = normalizedRows.find((row) => !row.amount || row.amount <= 0 || !row.paymentMode);
    if (invalidRow) throw new Error("Every payment row needs amount greater than 0 and payment mode.");
    const amount = normalizedRows.reduce((sum, row) => sum + row.amount, 0);
    if (!amount || amount <= 0) throw new Error("Total collected cannot be zero.");
    if (amount > balanceBefore) throw new Error(`Total collected cannot be more than pending balance ${opticalMoney(balanceBefore)}.`);
    const now = new Date().toISOString();
    const collectionReferenceId = uuid("opt-payment-batch");
    const receivedBy = collectBalanceForm.receivedBy || user.name || user.email;
    const collectionRows = normalizedRows.map((row) => ({
      id: uuid("opt-payment-collection"),
      collectionReferenceId,
      amount: row.amount,
      paymentMode: row.paymentMode,
      receivedBy,
      receivedByUserId: user.id,
      notes: row.note,
      collectedAt: row.receivedAt ? new Date(row.receivedAt).toISOString() : now
    }));
    const nextCollections = [...collectionRows, ...orderPaymentHistory(order)];
    const totalAmount = orderTotal(order);
    const advanceReceived = Number(order.payment?.advanceReceived || order.payment?.advance || 0);
    const totalCollected = advanceReceived + nextCollections.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const nextBalance = Math.max(0, totalAmount - totalCollected);
    const nextPaymentStatus = nextBalance <= 0 ? "Paid" : "Pending";
    const splitSummary = collectionRows.map((row) => `${row.paymentMode} ${opticalMoney(row.amount)}`).join(" + ");
    const updatedOrder = {
      ...order,
      payment: {
        ...(order.payment || {}),
        collections: nextCollections,
        paidAfterAdvance: nextCollections.reduce((sum, row) => sum + Number(row.amount || 0), 0),
        totalCollected,
        balance: nextBalance,
        status: nextPaymentStatus
      },
      orderDetails: {
        ...(order.orderDetails || {}),
        paymentTimeline: [
          orderTimelineEvent({
            eventType: "Balance collected",
            oldStatus: order.payment?.status || "Pending",
            newStatus: nextPaymentStatus,
            note: `${opticalMoney(amount)} collected by ${receivedBy}: ${splitSummary}.`,
            user,
            extra: { amount, collectionReferenceId, paymentRows: collectionRows }
          }),
          ...(order.orderDetails?.paymentTimeline || [])
        ]
      },
      updatedAt: now
    };
    const savedOrder = await persistRequiredEntity("optical_fast_slips", updatedOrder, "Order payment");
    const pendingEntry = (state.opticalPendingPaymentEntries || []).find((row) => row.slipId === order.id);
    const savedPendingEntry = pendingEntry
      ? await persistRequiredEntity("optical_pending_payment_entries", {
          ...pendingEntry,
          balance: nextBalance,
          advanceReceived: totalCollected,
          status: nextPaymentStatus,
          updatedAt: now
        }, "Pending payment")
      : null;
    updateState((current) => ({
      ...current,
      opticalFastSlips: (current.opticalFastSlips || []).map((row) => (row.id === savedOrder.id ? savedOrder : row)),
      opticalPendingPaymentEntries: savedPendingEntry
        ? upsertById(current.opticalPendingPaymentEntries || [], savedPendingEntry)
        : (current.opticalPendingPaymentEntries || [])
    }));
    await notifySuperAdmins(state, updateState, {
      module: "Payments",
      title: "Payment Collected",
      message: `${opticalMoney(amount)} collected for order ${savedOrder.orderNumber}.`,
      priority: nextPaymentStatus === "Paid" ? "normal" : "low",
      entityType: "optical_fast_slip",
      entityId: savedOrder.id,
      storeId: savedOrder.storeId,
      createdBy: user.id
    });
    setCollectBalanceOrderId("");
    setMessage(`${opticalMoney(amount)} collected for ${savedOrder.orderNumber}. Balance ${opticalMoney(nextBalance)}.`);
    return savedOrder;
  };

  const markOrderDelivered = async (order) => {
    const now = new Date().toISOString();
    const updatedOrder = {
      ...order,
      orderDetails: {
        ...(order.orderDetails || {}),
        deliveryStatus: "Delivered",
        deliveredAt: now,
        deliveredBy: user.name || user.email,
        orderTimeline: [
          orderTimelineEvent({
            eventType: "Order delivered",
            oldStatus: orderDeliveryStatus(order, state),
            newStatus: "Delivered",
            note: `Delivery marked by ${user.name || user.email}.`,
            user
          }),
          ...getOrderTimeline(order)
        ]
      },
      updatedAt: now
    };
    const savedOrder = await persistRequiredEntity("optical_fast_slips", updatedOrder, "Order delivery");
    const existingDelivery = (state.opticalDeliveryTrackingEntries || []).find((row) => row.slipId === order.id);
    const deliveryRow = {
      ...(existingDelivery || {}),
      id: existingDelivery?.id || uuid("opt-delivery"),
      slipId: order.id,
      orderNumber: order.orderNumber,
      customerName: order.customer?.name || order.customerName || "",
      deliveryDate: order.deliveryDate || now.slice(0, 10),
      status: "Delivered",
      storeId: order.storeId,
      salespersonId: order.salespersonId || "",
      createdAt: existingDelivery?.createdAt || now,
      updatedAt: now
    };
    const savedDelivery = await persistRequiredEntity("optical_delivery_tracking_entries", deliveryRow, "Delivery tracking");
    updateState((current) => ({
      ...current,
      opticalFastSlips: (current.opticalFastSlips || []).map((row) => (row.id === savedOrder.id ? savedOrder : row)),
      opticalDeliveryTrackingEntries: upsertById(current.opticalDeliveryTrackingEntries || [], savedDelivery)
    }));
    await notifySuperAdmins(state, updateState, {
      module: "Orders",
      title: "Order Delivered",
      message: `Order ${savedOrder.orderNumber} was marked delivered.`,
      priority: computedOrderBalance(savedOrder) > 0 ? "high" : "normal",
      entityType: "optical_fast_slip",
      entityId: savedOrder.id,
      storeId: savedOrder.storeId,
      createdBy: user.id
    });
    setMessage(`${savedOrder.orderNumber} marked Delivered.`);
    return savedOrder;
  };

  const updateOrderProductStatus = async (order, productId, status, eventType, note, extra = {}) => {
    if (!canDecideOrder) {
      setMessage("You do not have permission to update order item workflow.");
      return;
    }
    const products = order.orderDetails?.products || order.orderDetails?.extraProducts || [];
    const item = products.find((product) => product.id === productId);
    const updatedProducts = products.map((product) => (
      product.id === productId
        ? {
            ...product,
            itemStatus: status,
            itemStatusUpdatedAt: new Date().toISOString(),
            itemStatusUpdatedBy: user.name || user.email,
            ...extra
          }
        : product
    ));
    const workflowStatus =
      status === ORDER_ITEM_STATUSES.vendorRequired ? ORDER_MANAGER_STATUSES.vendorRequired :
      status === ORDER_ITEM_STATUSES.vendorPlaced ? ORDER_MANAGER_STATUSES.vendorPlaced :
      status === ORDER_ITEM_STATUSES.received ? ORDER_MANAGER_STATUSES.received :
      status === ORDER_ITEM_STATUSES.lab ? ORDER_MANAGER_STATUSES.sentToLab :
      status === ORDER_ITEM_STATUSES.ready ? ORDER_MANAGER_STATUSES.ready :
      getOrderWorkflow(order).status || ORDER_MANAGER_STATUSES.new;
    const updated = {
      ...order,
      orderDetails: {
        ...(order.orderDetails || {}),
        products: updatedProducts,
        extraProducts: updatedProducts,
        managerWorkflow: {
          ...getOrderWorkflow(order),
          status: workflowStatus,
          reviewedAt: new Date().toISOString(),
          reviewedBy: user.name || user.email
        },
        orderTimeline: [
          orderTimelineEvent({
            eventType,
            oldStatus: orderItemStatus(item),
            newStatus: status,
            note,
            user,
            extra: { itemName: item?.name || opticalProductLabel(item?.type), productId, ...extra }
          }),
          ...getOrderTimeline(order)
        ]
      },
      updatedAt: new Date().toISOString()
    };
    const savedOrder = await persistRequiredEntity("optical_fast_slips", updated, "Order item status");
    updateState((current) => ({
      ...current,
      opticalFastSlips: (current.opticalFastSlips || []).map((row) => (row.id === savedOrder.id ? savedOrder : row))
    }));
    setMessage(`${item?.name || "Item"} marked: ${status}`);
    return savedOrder;
  };

  const markVendorOrderPlaced = async (order) => {
    if (!canPlaceVendorOrder) {
      setMessage("You do not have permission to mark vendor order placed.");
      return;
    }
    if (!orderActionDraft.vendorName.trim()) {
      setMessage("Vendor name is required before marking vendor order placed.");
      return;
    }
    await saveOrderWorkflowUpdate(
      order,
      {
        status: ORDER_MANAGER_STATUSES.vendorPlaced,
        vendorName: orderActionDraft.vendorName.trim(),
        vendorOrderReference: orderActionDraft.vendorOrderReference,
        orderSentMethod: orderActionDraft.orderSentMethod,
        vendorOrderPlacedBy: user.name || user.email,
        vendorOrderPlacedAt: new Date().toISOString(),
        vendorRemarks: orderActionDraft.remarks
      },
      "Order placed with vendor",
      `Order placed with ${orderActionDraft.vendorName} by ${user.name || user.email}.`,
      {
        vendorName: orderActionDraft.vendorName,
        vendorOrderReference: orderActionDraft.vendorOrderReference,
        orderSentMethod: orderActionDraft.orderSentMethod
      }
    );
  };

  const markVendorProductReceived = async (order) => {
    if (!canReceiveVendorProduct) {
      setMessage("You do not have permission to mark product received.");
      return;
    }
    await saveOrderWorkflowUpdate(
      order,
      {
        status: ORDER_MANAGER_STATUSES.received,
        productReceivedBy: user.name || user.email,
        productReceivedAt: new Date().toISOString(),
        vendorName: orderActionDraft.vendorName || getOrderWorkflow(order).vendorName,
        dcNumber: orderActionDraft.dcNumber,
        dcDate: orderActionDraft.dcDate,
        receivedQuantity: orderActionDraft.receivedQuantity,
        receivedProductDetails: orderActionDraft.receivedProductDetails,
        receivedRemarks: orderActionDraft.remarks
      },
      "Product received from vendor",
      `Vendor product received${orderActionDraft.dcNumber ? ` against DC ${orderActionDraft.dcNumber}` : ""}.`,
      {
        dcNumber: orderActionDraft.dcNumber,
        dcDate: orderActionDraft.dcDate,
        receivedQuantity: orderActionDraft.receivedQuantity,
        receivedProductDetails: orderActionDraft.receivedProductDetails
      }
    );
  };

  const markOrderLabStatus = async (order, status, eventType) => {
    if (!canReceiveVendorProduct && !hasUserPermission(user, "can_mark_ready")) {
      setMessage("You do not have permission to update lab status.");
      return;
    }
    await saveOrderWorkflowUpdate(
      order,
      { status, labStatus: status, labUpdatedAt: new Date().toISOString(), labUpdatedBy: user.name || user.email },
      eventType,
      `${status} by ${user.name || user.email}.`
    );
  };

  const setPower = (key, field, value) => {
    setForm((current) => {
      const next = { ...current, [key]: { ...current[key], [field]: value } };
      if (current.autoContactPower && ["rightDistance", "leftDistance"].includes(key)) {
        const contactKey = key === "rightDistance" ? "contactRight" : "contactLeft";
        next[contactKey] = {
          ...next[contactKey],
          [field]: field === "sph" ? convertToContactLensPower(value) : value
        };
      }
      return next;
    });
  };

  const toggleItem = (key) => {
    setForm((current) => ({
      ...current,
      selectedItems: current.selectedItems.includes(key)
        ? current.selectedItems.filter((item) => item !== key)
        : [...current.selectedItems, key]
    }));
  };

  const saveSlip = async () => {
    if (isSavingOrder) return;
    const onlineResults = {};
    const saveOrderEntity = async (tableName, record, label) => {
      try {
        await persistRequiredEntity(tableName, record, label);
        onlineResults[label] = { savedOnline: true };
        return onlineResults[label];
      } catch (error) {
        console.error(`${label} save failed`, error);
        onlineResults[label] = { savedOnline: false, reason: error.message || "online sync failed" };
        throw error;
      }
    };
    const saveRequiredOrder = async (record) => {
      const saveOnce = async (payload) => {
        return persistRequiredEntity("optical_fast_slips", payload, "Order");
      };
      try {
        return await saveOnce(record);
      } catch (error) {
        const message = String(readableSupabaseError(error)).toLowerCase();
        if (record.customerId && (message.includes("customer_id") || message.includes("foreign key"))) {
          console.error("Order save failed with customer_id reference. Retrying without customer_id.", { error, record });
          const withoutCustomerReference = { ...record, customerId: null };
          return saveOnce(withoutCustomerReference);
        }
        console.error("Order Supabase save failed", { error, record });
        throw new Error(`Order was not saved online. Supabase said: ${readableSupabaseError(error)}`);
      }
    };

    setMessage(editingSlipId ? "Saving order edit..." : "Saving order...");
    setIsSavingOrder(true);
    try {
      const cleanPhone = form.phone.trim();
      const orderProducts = (form.products || [])
        .filter((product) => product.type || product.name || product.amount || product.notes || product.barcode)
        .map((product) => normalizeOrderProductForMaster(product, state.crmProducts || []));
      if (!form.customerName.trim()) throw new Error("Customer name is required. Phone can be skipped.");
      if (!orderProducts.length) throw new Error("Add at least one product before saving.");
      if (!form.storeId) throw new Error("Store is required from employee/store assignment.");
      const now = new Date().toISOString();
      const editingSlip = editingSlipId ? (state.opticalFastSlips || []).find((slip) => slip.id === editingSlipId) : null;
      if (editingSlip && user.role === "employee") {
        const createdAt = new Date(editingSlip.createdAt || editingSlip.updatedAt || now).getTime();
        const isOriginalSalesperson = editingSlip.salespersonId === user.employeeId;
        if (!isOriginalSalesperson || Date.now() - createdAt > 30 * 60 * 1000) {
          throw new Error("Salesperson can edit only their own order within 30 minutes.");
        }
      }
      const walkInId = editingSlip?.customer?.walkInId || `WALKIN-${form.date.replaceAll("-", "")}-${String((state.opticalFastSlips || []).length + 1).padStart(3, "0")}`;
      const selectedCustomer = form.customerId
        ? (state.opticalCustomers || []).find((customer) => customer.id === form.customerId)
        : null;
      const existingCustomer = selectedCustomer || (cleanPhone ? (state.opticalCustomers || []).find((customer) => customer.phone === cleanPhone) : null);
      const customer = {
        ...(existingCustomer || {}),
        id: existingCustomer?.id || editingSlip?.customerId || uuid("opt-customer"),
        phone: cleanPhone,
        noPhone: !cleanPhone,
        walkInId: cleanPhone ? "" : walkInId,
        name: form.customerName.trim(),
        address: form.address,
        dob: form.dob || null,
        email: form.email,
        storeId: form.storeId,
        createdAt: existingCustomer?.createdAt || now,
        updatedAt: now
      };
      const slipId = editingSlip?.id || uuid("opt-slip");
      const derivedOrderType = deriveOrderTypeFromProducts(orderProducts);
      const derivedSelectedItems = [...new Set(orderProducts.flatMap((product) => {
        if (product.type === "contactSolution") return ["contactLens", "solution"];
        if (product.type === "eyeTest") return ["eyeTest"];
        if (product.type === "sunglasses") return ["frame"];
        if (product.type === "accessory") return ["service"];
        return [product.type];
      }))];
      const frameProducts = orderProducts.filter((product) => ["frame", "sunglasses"].includes(product.type));
      const lensProducts = orderProducts.filter((product) => product.type === "lens");
      const productPairCount = Math.max(frameProducts.length, lensProducts.length);
      const productPairs = Array.from({ length: productPairCount }).map((_, index) => ({
        id: uuid("pair"),
        frameDetails: frameProducts[index]?.name || "",
        frameSource: frameProducts[index]?.source || "store",
        frameStatus: frameProducts[index]?.status || "ready",
        framePhoto: frameProducts[index]?.photo || "",
        lensDetails: lensProducts[index]?.name || "",
        lensStatus: lensProducts[index]?.status || "ready",
        tintRequired: Boolean(lensProducts[index]?.tintRequired),
        tintAsSample: Boolean(lensProducts[index]?.tintAsSample),
        tintSamplePhoto: "",
        notes: [frameProducts[index]?.notes, lensProducts[index]?.notes].filter(Boolean).join(" | ")
      }));
      const pairSummary = orderProducts
        .map((product, index) => `${index + 1}. ${opticalProductLabel(product.type)} - ${product.name || product.status || "Item"}`)
        .join(" | ");
      const contactSummary = orderProducts.filter((product) => product.type === "contactSolution").map((product) => product.name || product.notes).filter(Boolean).join(" | ");
      const serviceSummary = orderProducts.filter((product) => ["service", "eyeTest"].includes(product.type)).map((product) => product.name || product.notes).filter(Boolean).join(" | ");
      const editedBy = editingSlip
        ? [...(editingSlip.orderDetails?.editLog || []), {
            editedAt: now,
            editedBy: user.name || user.email,
            editedByUserId: user.id,
            reason: "Order edited from app"
          }]
        : [];
      const orderRecord = {
        ...(editingSlip || {}),
        id: slipId,
        orderNumber: form.orderNumber,
        gcNumber: form.orderNumber,
        storeId: form.storeId,
        storeName: store?.name || "",
        storePhone: store?.phone || "",
        salespersonId: salesperson?.id || "",
        salespersonName: salesperson?.fullName || user.name,
        salespersonDesignation: salesperson?.designation || "",
        date: form.date,
        deliveryDate: form.deliveryDate,
        customerId: customer.id,
        customer,
        orderDetails: {
          orderType: derivedOrderType,
          selectedItems: derivedSelectedItems,
          products: orderProducts,
          extraProducts: orderProducts,
          opticalPairs: productPairs,
          frameDetails: frameProducts.map((product) => product.name).filter(Boolean).join(" | "),
          frameSource: frameProducts[0]?.source || form.frameSource,
          frameStatus: frameProducts[0]?.status || form.frameStatus,
          ownFramePhoto: form.ownFramePhoto,
          lensDetails: lensProducts.map((product) => product.name).filter(Boolean).join(" | "),
          lensStatus: lensProducts[0]?.status || form.lensStatus,
          contactLensDetails: contactSummary || form.contactLensDetails,
          serviceDetails: serviceSummary || form.serviceDetails,
          specialNotes: form.specialNotes,
          pairSummary,
          editLog: editedBy,
          managerWorkflow: editingSlip?.orderDetails?.managerWorkflow || {
            status: ORDER_MANAGER_STATUSES.new,
            reviewedAt: "",
            reviewedBy: "",
            vendorName: "",
            vendorOrderReference: "",
            orderSentMethod: ""
          },
          orderTimeline: editingSlip?.orderDetails?.orderTimeline || [
            orderTimelineEvent({
              eventType: "Order created",
              oldStatus: "",
              newStatus: ORDER_MANAGER_STATUSES.new,
              note: "Customer order created from Punch Sale / Order Form.",
              user
            })
          ],
          lastEditedAt: editingSlip ? now : null,
          lastEditedBy: editingSlip ? (user.name || user.email) : null
        },
        power: {
          rightEye: { distance: form.rightDistance, add: form.rightAdd },
          leftEye: { distance: form.leftDistance, add: form.leftAdd },
          contactLens: {
            autoConverted: form.autoContactPower,
            rightEye: form.contactRight,
            leftEye: form.contactLeft
          },
          measurements: form.measurements,
          prescribedBy: form.prescribedBy
        },
        payment: {
          subtotalAmount,
          discountType: "amount",
          discountValue: rawDiscountValue,
          discountAmount,
          totalAmount,
          advanceReceived,
          balance,
          paymentMode: form.paymentMode,
          splitPayments: form.paymentMode === "Split" ? form.splitPayments : null,
          notes: form.paymentNotes,
          status: balance > 0 ? "Pending" : "Paid"
        },
        generatedViews: { customerSlip: true, internalStoreSlip: true, labLensCompanySlip: true },
        createdAt: editingSlip?.createdAt || now,
        updatedAt: now
      };
      const dailyEntry = {
        id: (state.opticalDailyRegisterEntries || []).find((entry) => entry.slipId === slipId)?.id || uuid("opt-daily"),
        slipId,
        orderNumber: form.orderNumber,
        date: form.date,
        customerName: customer.name,
        phone: customer.phone,
        subtotalAmount,
        discountAmount,
        totalAmount,
        advanceReceived,
        balance,
        paymentMode: form.paymentMode,
        deliveryDate: form.deliveryDate,
        deliveryStatus: OPTICAL_DELIVERY_STATUS,
        storeId: form.storeId,
        salespersonId: salesperson?.id || "",
        createdAt: (state.opticalDailyRegisterEntries || []).find((entry) => entry.slipId === slipId)?.createdAt || now
      };
      const deliveryEntry = {
        id: (state.opticalDeliveryTrackingEntries || []).find((entry) => entry.slipId === slipId)?.id || uuid("opt-delivery"),
        slipId,
        orderNumber: form.orderNumber,
        customerName: customer.name,
        deliveryDate: form.deliveryDate,
        status: OPTICAL_DELIVERY_STATUS,
        storeId: form.storeId,
        salespersonId: salesperson?.id || "",
        createdAt: (state.opticalDeliveryTrackingEntries || []).find((entry) => entry.slipId === slipId)?.createdAt || now
      };
      const lensOrderEntry = {
        id: (state.opticalLensOrderEntries || []).find((entry) => entry.slipId === slipId)?.id || uuid("opt-lens-order"),
        slipId,
        orderNumber: form.orderNumber,
        customerName: customer.name,
        lensDetails: pairSummary || form.lensDetails,
        contactLensDetails: form.contactLensDetails,
        power: orderRecord.power,
        vendor: "",
        vendorOrderNumber: "",
        status: OPTICAL_LENS_ORDER_STATUS,
        storeId: form.storeId,
        salespersonId: salesperson?.id || "",
        createdAt: (state.opticalLensOrderEntries || []).find((entry) => entry.slipId === slipId)?.createdAt || now
      };
      const pendingPaymentEntry = balance > 0 ? {
        id: (state.opticalPendingPaymentEntries || []).find((entry) => entry.slipId === slipId)?.id || uuid("opt-payment"),
        slipId,
        orderNumber: form.orderNumber,
        customerName: customer.name,
        phone: customer.phone,
        balance,
        subtotalAmount,
        discountAmount,
        totalAmount,
        advanceReceived,
        status: "Pending",
        storeId: form.storeId,
        salespersonId: salesperson?.id || "",
        createdAt: (state.opticalPendingPaymentEntries || []).find((entry) => entry.slipId === slipId)?.createdAt || now
      } : null;
      const unmappedSaleRows = orderProducts
        .filter((product) => product.productMasterStatus === "Product Master Pending" || product.inventoryStatus === "Inventory Pending")
        .map((product) => ({
          id: uuid("crm-unmapped-sale"),
          orderId: slipId,
          orderItemId: product.id,
          orderNumber: form.orderNumber,
          storeId: form.storeId,
          salespersonId: salesperson?.id || "",
          salespersonName: salesperson?.fullName || user.name,
          enteredProductName: product.name || opticalProductLabel(product.type),
          barcode: product.barcode || "",
          categoryGuessed: opticalProductLabel(product.type),
          quantity: Number(product.quantity || 1),
          salePrice: Number(product.amount || 0),
          productMatchStatus: product.productMasterStatus || "Product Master Pending",
          inventoryStatus: product.inventoryStatus || "Inventory Pending",
          actionNeeded: "Map product master / create purchase inward",
          status: "pending",
          createdBy: user.id,
          createdAt: now,
          updatedAt: now
        }));
      const negativeInventoryMovements = unmappedSaleRows.map((row) => ({
        id: uuid("crm-move"),
        orderId: slipId,
        orderItemId: row.orderItemId,
        orderNumber: form.orderNumber,
        productName: row.enteredProductName,
        enteredProductName: row.enteredProductName,
        storeId: form.storeId,
        movementType: "Sale - Product Master Pending",
        quantity: -Math.abs(Number(row.quantity || 1)),
        referenceType: "optical_fast_slip",
        referenceId: slipId,
        negativeInventory: true,
        actionNeeded: row.actionNeeded,
        remarks: "Order allowed before product master/inventory mapping.",
        createdBy: user.name || user.email,
        createdAt: now
      }));
      orderRecord.unmappedItemCount = unmappedSaleRows.length;
      orderRecord.inventoryPending = unmappedSaleRows.length > 0;
      orderRecord.orderDetails = {
        ...orderRecord.orderDetails,
        unmappedItemCount: unmappedSaleRows.length,
        inventoryPending: unmappedSaleRows.length > 0
      };

      const customerSync = await saveOrderEntity("optical_customers", customer, "Customer");
      let onlineOrderRecord = customerSync?.savedOnline === false
        ? { ...orderRecord, customerId: null }
        : orderRecord;
      const savedOrderRecord = await saveRequiredOrder(onlineOrderRecord);
      await saveOrderEntity("optical_daily_register_entries", dailyEntry, "Daily register");
      await saveOrderEntity("optical_delivery_tracking_entries", deliveryEntry, "Delivery tracking");
      await saveOrderEntity("optical_lens_order_entries", lensOrderEntry, "Lens order");
      if (pendingPaymentEntry) await saveOrderEntity("optical_pending_payment_entries", pendingPaymentEntry, "Pending payment");
      for (const row of unmappedSaleRows) await saveOrderEntity("crm_unmapped_sold_items", row, "Unmapped sold item");
      for (const row of negativeInventoryMovements) await saveOrderEntity("crm_stock_movements", row, "Negative inventory");

      const log = createAuditLog({
        entityType: "optical_fast_slip",
        entityId: orderRecord.id,
        action: editingSlip ? "edited" : "created",
        oldValue: editingSlip || null,
        newValue: savedOrderRecord,
        reason: editingSlip ? "Order edited within permitted window; reporting manager should review if needed." : "Order Form created",
        effectiveDate: form.date,
        user
      });
      const auditAddedByParent = Boolean(addAudit);
      if (addAudit) {
        await addAudit(log);
      } else {
        await persistAuditLog(log).catch(() => null);
      }

      updateState((current) => ({
        ...current,
        opticalCustomers: existingCustomer
          ? (current.opticalCustomers || []).map((item) => (item.id === customer.id ? customer : item))
          : [customer, ...(current.opticalCustomers || [])],
        opticalFastSlips: editingSlip
          ? (current.opticalFastSlips || []).map((item) => (item.id === savedOrderRecord.id ? savedOrderRecord : item))
          : [savedOrderRecord, ...(current.opticalFastSlips || [])],
        opticalDailyRegisterEntries: (current.opticalDailyRegisterEntries || []).some((item) => item.id === dailyEntry.id)
          ? (current.opticalDailyRegisterEntries || []).map((item) => (item.id === dailyEntry.id ? dailyEntry : item))
          : [dailyEntry, ...(current.opticalDailyRegisterEntries || [])],
        opticalDeliveryTrackingEntries: (current.opticalDeliveryTrackingEntries || []).some((item) => item.id === deliveryEntry.id)
          ? (current.opticalDeliveryTrackingEntries || []).map((item) => (item.id === deliveryEntry.id ? deliveryEntry : item))
          : [deliveryEntry, ...(current.opticalDeliveryTrackingEntries || [])],
        opticalLensOrderEntries: (current.opticalLensOrderEntries || []).some((item) => item.id === lensOrderEntry.id)
          ? (current.opticalLensOrderEntries || []).map((item) => (item.id === lensOrderEntry.id ? lensOrderEntry : item))
          : [lensOrderEntry, ...(current.opticalLensOrderEntries || [])],
        opticalPendingPaymentEntries: pendingPaymentEntry
          ? ((current.opticalPendingPaymentEntries || []).some((item) => item.id === pendingPaymentEntry.id)
              ? (current.opticalPendingPaymentEntries || []).map((item) => (item.id === pendingPaymentEntry.id ? pendingPaymentEntry : item))
              : [pendingPaymentEntry, ...(current.opticalPendingPaymentEntries || [])])
          : (current.opticalPendingPaymentEntries || []).filter((item) => item.slipId !== slipId),
        crmUnmappedSoldItems: [...unmappedSaleRows, ...(current.crmUnmappedSoldItems || [])],
        crmStockMovements: [...negativeInventoryMovements, ...(current.crmStockMovements || [])],
        auditLogs: log && !auditAddedByParent ? [log, ...(current.auditLogs || [])] : (current.auditLogs || [])
      }));
      await notifySuperAdmins(state, updateState, {
        module: "Orders",
        title: editingSlip ? "Order Updated" : "New Order Created",
        message: `Order ${savedOrderRecord.orderNumber} was ${editingSlip ? "updated" : "created"} at ${savedOrderRecord.storeName || store?.name || "store"}.`,
        priority: "normal",
        entityType: "optical_fast_slip",
        entityId: savedOrderRecord.id,
        storeId: savedOrderRecord.storeId,
        createdBy: user.id
      });
      if (pendingPaymentEntry) {
        await notifySuperAdmins(state, updateState, {
          module: "Payments",
          title: "Pending Payment Created",
          message: `${currency(pendingPaymentEntry.balance)} pending for order ${savedOrderRecord.orderNumber}.`,
          priority: "normal",
          entityType: "optical_pending_payment",
          entityId: pendingPaymentEntry.id,
          storeId: savedOrderRecord.storeId,
          createdBy: user.id
        });
      }
      setForm(emptyOpticalForm({
        storeId: form.storeId,
        salespersonId: form.salespersonId,
        orderNumber: nextOpticalOrderNumber([savedOrderRecord, ...(state.opticalFastSlips || [])])
      }));
      setEditingSlipId("");
      setOrderTab("history");
      setLastSavedOrder(savedOrderRecord);
      setMessage(`${editingSlip ? "Updated" : "Saved"} ${savedOrderRecord.orderNumber}. Order Form / Lab Slip is ready to print. Saved online.`);
    } catch (error) {
      console.error("Order Form save failed", error);
      setMessage(error.message || "Order Form could not be saved.");
      throw error;
    } finally {
      setIsSavingOrder(false);
    }
  };

  const powerControl = (powerKey, label, value, onChange, numberOptions, signs) => (
    <label className="optical-power-field">
      {label}
      <span className="optical-power-selects">
        <select
          value={["PL", "0.00"].includes(value) ? value : String(value || "").startsWith("-") ? "-" : "+"}
          onChange={(event) => {
            const sign = event.target.value;
            onChange(sign === "PL" || sign === "0.00" ? sign : `${sign}${numberOptions[0] || "0.25"}`);
          }}
        >
          {signs.map((sign) => <option key={sign} value={sign}>{sign === "0.00" ? "0" : sign}</option>)}
        </select>
        <select
          value={["PL", "0.00"].includes(value) ? "" : String(value || "").replace(/^[-+]/, "")}
          disabled={["PL", "0.00"].includes(value)}
          onChange={(event) => {
            const sign = String(value || "").startsWith("-") ? "-" : "+";
            onChange(event.target.value ? `${sign}${event.target.value}` : "");
          }}
        >
          <option value=""></option>
          {numberOptions.map((option) => <option key={`${powerKey}-${option}`} value={option}>{option}</option>)}
        </select>
      </span>
      <strong>{value || "-"}</strong>
    </label>
  );

  const powerCard = (title, key) => (
    <div className="optical-power-card">
      <strong>{title}</strong>
      <div className="optical-power-grid">
        {powerControl(`${key}-sph`, "SPH", form[key].sph, (value) => setPower(key, "sph", value), OPTICAL_SPH_OPTIONS, ["PL", "+", "-"])}
        {powerControl(`${key}-cyl`, "CYL", form[key].cyl, (value) => setPower(key, "cyl", value), OPTICAL_CYL_OPTIONS, ["0.00", "-", "+"])}
        <Input label="AX" type="number" value={form[key].ax} onChange={(ax) => setPower(key, "ax", String(Math.max(0, Math.min(180, Number(ax || 0))) || ""))} placeholder="0-180" />
      </div>
      <p className="helper-text">{opticalPowerLine(form[key])}</p>
    </div>
  );

  const productSpecificFields = (product) => {
    if (["frame", "sunglasses"].includes(product.type)) {
      return (
        <>
          <label>
            Source
            <select value={product.source} onChange={(event) => updateProduct(product.id, { source: event.target.value })}>
              <option value="store">From store</option>
              <option value="own">Customer own frame</option>
              <option value="awaited">Frame awaited</option>
            </select>
          </label>
          <label>
            Status
            <select value={product.status} onChange={(event) => updateProduct(product.id, { status: event.target.value })}>
              <option value="ready">Ready</option>
              <option value="awaited">Awaited</option>
              <option value="customer_will_send">Customer will send later</option>
            </select>
          </label>
        </>
      );
    }
    if (product.type === "lens") {
      return (
        <>
          <label className="toggle-line">
            <input type="checkbox" checked={product.requiresPower !== false} onChange={(event) => updateProduct(product.id, { requiresPower: event.target.checked })} />
            Power needed
          </label>
          <label>
            Lens status
            <select value={product.status} onChange={(event) => updateProduct(product.id, { status: event.target.value })}>
              <option value="selected">Lens selected</option>
              <option value="awaited">Lens awaited</option>
              <option value="customer_will_send">Customer will send later</option>
            </select>
          </label>
          <label className="toggle-line">
            <input type="checkbox" checked={product.tintRequired} onChange={(event) => updateProduct(product.id, { tintRequired: event.target.checked })} />
            Tint required
          </label>
          {product.tintRequired ? (
            <label className="toggle-line">
              <input type="checkbox" checked={product.tintAsSample} onChange={(event) => updateProduct(product.id, { tintAsSample: event.target.checked })} />
              Tint as per sample
            </label>
          ) : null}
        </>
      );
    }
    if (product.type === "eyeTest") {
      return (
        <>
          <Input label="Tested by" value={form.prescribedBy} onChange={(prescribedBy) => setForm({ ...form, prescribedBy })} placeholder="Optometrist / staff name" />
          <p className="helper-text">Power section will open below for eye-test prescription.</p>
        </>
      );
    }
    if (product.type === "contactSolution") {
      return (
        <>
          <label className="toggle-line">
            <input type="checkbox" checked={product.requiresPower !== false} onChange={(event) => updateProduct(product.id, { requiresPower: event.target.checked })} />
            Contact lens power needed
          </label>
          <p className="helper-text">Untick this for solution-only sale.</p>
        </>
      );
    }
    return null;
  };

  const selectedItemsLabel = (slip) =>
    slip.orderDetails?.orderType
      ? labelForOpticalOrderType(slip.orderDetails.orderType)
      : (slip.orderDetails?.selectedItems || []).map((key) => OPTICAL_ORDER_ITEMS.find(([itemKey]) => itemKey === key)?.[1] || key).join(", ") || "Order";

  const managerEditNotices = (state.auditLogs || []).filter(
    (log) =>
      log.entityType === "optical_fast_slip" &&
      log.action === "edited" &&
      ownStoreSlips.some((slip) => slip.id === log.entityId)
  ).slice(0, 5);

  const renderSavedOrderDetails = (row) => (
    <div className="order-collapse-body">
      <div className="order-status-banner">
        <span>Current order status</span>
        <strong>{orderWorkflowStatus(row)}</strong>
        <small>{getOrderWorkflow(row).vendorName ? `Vendor: ${getOrderWorkflow(row).vendorName}` : "Vendor not selected yet"}</small>
      </div>
      <div className="order-detail-grid">
        <p><strong>Phone:</strong> {row.customer?.phone || row.customer?.walkInId || "-"}</p>
        <p><strong>Delivery:</strong> {row.deliveryDate || "-"}</p>
        <p><strong>Order type:</strong> {labelForOpticalOrderType(row.orderDetails?.orderType)}</p>
        <p><strong>Frame:</strong> {row.orderDetails?.frameDetails || row.orderDetails?.pairSummary || "-"}</p>
        <p><strong>Lens:</strong> {row.orderDetails?.lensDetails || row.orderDetails?.pairSummary || "-"}</p>
        <p><strong>Frame status:</strong> {row.orderDetails?.frameStatus || "-"}</p>
        <p><strong>Lens status:</strong> {row.orderDetails?.lensStatus || "-"}</p>
        <p><strong>Contact Lens:</strong> {row.orderDetails?.contactLensDetails || "-"}</p>
        <p><strong>Service:</strong> {row.orderDetails?.serviceDetails || "-"}</p>
      </div>
      {(row.orderDetails?.opticalPairs || []).length ? (
        <div className="order-pair-summary">
          {(row.orderDetails?.opticalPairs || []).map((pair, index) => (
            <article className="order-pair-mini" key={pair.id || index}>
              <strong>Pair {index + 1}</strong>
              <span>{pair.frameDetails || pair.frameStatus || "Frame"} + {pair.lensDetails || pair.lensStatus || "Lens"}</span>
              <small>{pair.tintRequired ? `Tint${pair.tintAsSample ? " as per sample" : ""}` : "No tint"} · {pair.notes || "-"}</small>
              <div className="order-photo-row">
                <PhotoPreview src={pair.framePhoto} label={`Frame ${index + 1}`} />
                <PhotoPreview src={pair.tintSamplePhoto} label={`Tint sample ${index + 1}`} />
              </div>
            </article>
          ))}
        </div>
      ) : null}
      {(row.orderDetails?.products || row.orderDetails?.extraProducts || []).length ? (
        <div className="order-pair-summary">
          {(row.orderDetails?.products || row.orderDetails?.extraProducts || []).map((product, index) => (
            <article className="order-pair-mini" key={product.id || index}>
              <strong>{index + 1}. {opticalProductLabel(product.type)}</strong>
              <span>{product.quantity || 1} x {product.name || "Item"} · {opticalMoney(Number(product.quantity || 1) * Number(product.amount || 0))}</span>
              <small>{product.barcode ? `Barcode: ${product.barcode}` : product.notes || "-"}</small>
              {product.notes && product.barcode ? <small>{product.notes}</small> : null}
              <PhotoPreview src={product.photo} label={opticalProductLabel(product.type)} />
            </article>
          ))}
        </div>
      ) : null}
      {canOrderManager ? (
        <OpticalSection title="Order Manager workflow" subtitle="Review item-wise: stock, transfer, vendor order or hold." defaultOpen>
          <div className="order-manager-stack">
            {(row.orderDetails?.products || row.orderDetails?.extraProducts || []).map((product, index) => (
              <article className="order-workflow-item" key={product.id || index}>
                <div>
                  <strong>{index + 1}. {opticalProductLabel(product.type)} · {product.name || "Item"}</strong>
                  <small>Status: {orderItemStatus(product)} · Qty {product.quantity || 1}</small>
                </div>
                <div className="button-row compact-action-row">
                  <ActionButton className="mini-button" disabled={!canDecideOrder} action={orderActionState(`order-stock-${row.id}-${product.id}`)} labels={{ loading: "Saving...", success: "Saved ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-stock-${row.id}-${product.id}`, { loading: "Saving...", success: "Saved ✓" }, () => updateOrderProductStatus(row, product.id, ORDER_ITEM_STATUSES.stock, "Stock reserved", "Item reserved from current store stock."))}>Use stock</ActionButton>
                  <ActionButton className="mini-button" disabled={!canDecideOrder} action={orderActionState(`order-transfer-${row.id}-${product.id}`)} labels={{ loading: "Saving...", success: "Saved ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-transfer-${row.id}-${product.id}`, { loading: "Saving...", success: "Saved ✓" }, () => updateOrderProductStatus(row, product.id, ORDER_ITEM_STATUSES.transfer, "Stock transfer requested", "Transfer requested from another store."))}>Transfer</ActionButton>
                  <ActionButton className="mini-button" disabled={!canDecideOrder} action={orderActionState(`order-vendor-${row.id}-${product.id}`)} labels={{ loading: "Saving...", success: "Saved ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-vendor-${row.id}-${product.id}`, { loading: "Saving...", success: "Saved ✓" }, () => updateOrderProductStatus(row, product.id, ORDER_ITEM_STATUSES.vendorRequired, "Vendor order required", "Vendor order required for this item."))}>Vendor</ActionButton>
                  <ActionButton className="mini-button danger" disabled={!canDecideOrder} action={orderActionState(`order-hold-${row.id}-${product.id}`)} labels={{ loading: "Saving...", success: "Saved ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-hold-${row.id}-${product.id}`, { loading: "Saving...", success: "Saved ✓" }, () => updateOrderProductStatus(row, product.id, ORDER_ITEM_STATUSES.hold, "Hold / clarify", "Order item held for clarification."))}>Hold</ActionButton>
                </div>
              </article>
            ))}
          </div>
          <div className="order-workflow-form">
            <div className="form-grid">
              <Input label="Vendor name" value={orderActionDraft.vendorName} onChange={(vendorName) => setOrderActionDraft({ ...orderActionDraft, vendorName })} placeholder="Essilor / Zeiss / Asdol" />
              <Input label="Vendor reference optional" value={orderActionDraft.vendorOrderReference} onChange={(vendorOrderReference) => setOrderActionDraft({ ...orderActionDraft, vendorOrderReference })} />
              <label>Sent method<select value={orderActionDraft.orderSentMethod} onChange={(event) => setOrderActionDraft({ ...orderActionDraft, orderSentMethod: event.target.value })}>{ORDER_SENT_METHODS.map((method) => <option key={method}>{method}</option>)}</select></label>
              <Input label="DC number" value={orderActionDraft.dcNumber} onChange={(dcNumber) => setOrderActionDraft({ ...orderActionDraft, dcNumber })} />
              <Input label="DC date" type="date" value={orderActionDraft.dcDate} onChange={(dcDate) => setOrderActionDraft({ ...orderActionDraft, dcDate })} />
              <Input label="Received quantity" type="number" value={orderActionDraft.receivedQuantity} onChange={(receivedQuantity) => setOrderActionDraft({ ...orderActionDraft, receivedQuantity })} />
            </div>
            <label>Product / DC remarks<textarea value={orderActionDraft.receivedProductDetails || orderActionDraft.remarks} onChange={(event) => setOrderActionDraft({ ...orderActionDraft, receivedProductDetails: event.target.value, remarks: event.target.value })} placeholder="Product received, vendor notes, DC details" /></label>
            <div className="button-row">
              <ActionButton className="primary-button" disabled={!canPlaceVendorOrder} action={orderActionState(`order-vendor-placed-${row.id}`)} labels={{ loading: "Updating...", success: "Vendor Order Saved ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-vendor-placed-${row.id}`, { loading: "Updating...", success: "Vendor Order Saved ✓" }, () => markVendorOrderPlaced(row))}>Mark Order Placed With Vendor</ActionButton>
              <ActionButton className="secondary-button" disabled={!canReceiveVendorProduct} action={orderActionState(`order-vendor-received-${row.id}`)} labels={{ loading: "Updating...", success: "Received ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-vendor-received-${row.id}`, { loading: "Updating...", success: "Received ✓" }, () => markVendorProductReceived(row))}>Product Received From Vendor</ActionButton>
              <ActionButton className="secondary-button" disabled={!canReceiveVendorProduct} action={orderActionState(`order-lab-${row.id}`)} labels={{ loading: "Updating...", success: "Sent to Lab ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-lab-${row.id}`, { loading: "Updating...", success: "Sent to Lab ✓" }, () => markOrderLabStatus(row, ORDER_MANAGER_STATUSES.sentToLab, "Sent to lab"))}>Sent to Lab</ActionButton>
              <ActionButton className="secondary-button" disabled={!hasUserPermission(user, "can_mark_ready") && user.role !== "super_admin" && user.role !== "admin"} action={orderActionState(`order-ready-${row.id}`)} labels={{ loading: "Updating...", success: "Ready ✓", error: "Try Again" }} onClick={() => runOrderAction(`order-ready-${row.id}`, { loading: "Updating...", success: "Ready ✓" }, () => markOrderLabStatus(row, ORDER_MANAGER_STATUSES.ready, "Order ready"))}>Ready</ActionButton>
            </div>
          </div>
        </OpticalSection>
      ) : null}
      {canCorrectOrderStatus ? (
        <OpticalSection title="Admin status correction" subtitle="Use only when a status was marked wrongly. Reason is compulsory.">
          <div className="status-correction-box">
            <p><strong>Current status:</strong> {orderWorkflowStatus(row)}</p>
            <div className="button-row compact-action-row">
              <button
                className="secondary-button"
                type="button"
                onClick={() => openOrderStatusCorrection(row, ORDER_MANAGER_STATUSES.assigned, "Moved Back to Assigned")}
              >
                Move Back to Assigned
              </button>
              <button
                className="secondary-button danger"
                type="button"
                onClick={() => openOrderStatusCorrection(row, ORDER_MANAGER_STATUSES.notPickedUp, "Marked as Not Picked Up")}
              >
                Mark as Not Picked Up
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => openOrderStatusCorrection(row, ORDER_MANAGER_STATUSES.pickupAwaited, "Moved to Pickup Awaited")}
              >
                Move to Pickup Awaited
              </button>
            </div>
          </div>
        </OpticalSection>
      ) : null}
      {canSeeOrderTimeline ? (
        <OpticalSection title="Order timeline" subtitle={`${getOrderTimeline(row).length} event(s)`}>
          <div className="order-timeline-list">
            {getOrderTimeline(row).length ? getOrderTimeline(row).map((event) => (
              <article className="order-timeline-item" key={event.id || `${event.timestamp}-${event.eventType}`}>
                <strong>{event.eventType}</strong>
                <span>{event.newStatus || event.note}</span>
                <small>{event.userName || "-"} · {event.timestamp ? new Date(event.timestamp).toLocaleString("en-IN") : "-"}</small>
                {event.note ? <small>{event.note}</small> : null}
                {event.correctionReason ? <small>Reason: {event.correctionReason}</small> : null}
              </article>
            )) : <div className="empty-state compact">No timeline yet.</div>}
          </div>
        </OpticalSection>
      ) : null}
      {row.orderDetails?.statusCorrections?.length ? (
        <OpticalSection title="Status history" subtitle="Admin corrections are kept permanently">
          <div className="audit-mini-list">
            {row.orderDetails.statusCorrections.map((event) => (
              <small key={event.id || event.changedAt}>
                Status changed from {event.oldStatus} to {event.newStatus} · Reason: {event.reason} · {event.changedByName || "-"} · {event.changedAt ? new Date(event.changedAt).toLocaleString("en-IN") : "-"}
              </small>
            ))}
          </div>
        </OpticalSection>
      ) : null}
      <PhotoPreview src={row.orderDetails?.ownFramePhoto} label="Own frame" />
      <div className="order-detail-grid">
        <p><strong>Right:</strong> {opticalPowerLine(row.power?.rightEye?.distance)}</p>
        <p><strong>Left:</strong> {opticalPowerLine(row.power?.leftEye?.distance)}</p>
        <p><strong>Total:</strong> {opticalMoney(row.payment?.totalAmount)}</p>
        <p><strong>Advance:</strong> {opticalMoney(row.payment?.advanceReceived)}</p>
        <p><strong>Balance:</strong> {opticalMoney(computedOrderBalance(row))}</p>
        <p><strong>Payment:</strong> {orderPaymentStatus(row)}</p>
        <p><strong>Delivery:</strong> {orderDeliveryStatus(row, state)}</p>
      </div>
      <OpticalSection title="Payment history" subtitle={`${orderPaymentHistory(row).length} payment entr${orderPaymentHistory(row).length === 1 ? "y" : "ies"}`}>
        <div className="audit-mini-list">
          <small>Advance at order: {opticalMoney(Number(row.payment?.advanceReceived || row.payment?.advance || 0))}</small>
          {orderPaymentHistory(row).length ? orderPaymentHistory(row).map((paymentRow) => (
            <small key={paymentRow.id || paymentRow.collectedAt}>
              {opticalMoney(paymentRow.amount)} · {paymentRow.paymentMode || "-"} · {paymentRow.receivedBy || "-"} · {paymentRow.collectedAt ? new Date(paymentRow.collectedAt).toLocaleString("en-IN") : "-"}
              {paymentRow.notes ? ` · ${paymentRow.notes}` : ""}
            </small>
          )) : <small>No later balance collection yet.</small>}
        </div>
      </OpticalSection>
      <div className="button-row">
        {canCollectOrderPayment && computedOrderBalance(row) > 0 ? (
          <button className="primary-button" type="button" onClick={() => openCollectBalance(row)}>
            Collect Balance
          </button>
        ) : null}
        {canMarkOrderDelivered && orderDeliveryStatus(row, state) !== "Delivered" ? (
          <ActionButton
            className="secondary-button"
            action={orderActionState(`order-delivered-${row.id}`)}
            labels={{ loading: "Saving...", success: "Delivered ✓", error: "Try Again" }}
            onClick={() => runOrderAction(`order-delivered-${row.id}`, { loading: "Saving...", success: "Delivered ✓", successMessage: `${row.orderNumber} marked Delivered.`, error: "Could not mark delivered." }, () => markOrderDelivered(row))}
          >
            Mark Delivered
          </ActionButton>
        ) : null}
        {canPrintOrderFormSlip ? <button className="primary-button" type="button" onClick={() => printOrderFormSlip(row, state)}>
          <Printer size={16} /> Print Order Form / Lab Slip
        </button> : null}
        <button className="secondary-button" type="button" onClick={() => loadSlipForEdit(row)}>
          Edit order
        </button>
      </div>
      {row.orderDetails?.editLog?.length ? (
        <div className="audit-mini-list">
          <strong>Edit log</strong>
          {row.orderDetails.editLog.map((log, index) => (
            <small key={`${log.editedAt}-${index}`}>{new Date(log.editedAt).toLocaleString("en-IN")} · {log.editedBy}</small>
          ))}
        </div>
      ) : null}
    </div>
  );
  const pendingOrderRows = ownStoreSlips.filter((row) =>
    computedOrderBalance(row) > 0 ||
    orderDeliveryStatus(row, state) !== "Delivered" ||
    ![ORDER_MANAGER_STATUSES.ready, "Delivered"].includes(orderWorkflowStatus(row))
  );
  const balancePaymentRows = ownStoreSlips.filter((row) => computedOrderBalance(row) > 0);
  const managerQueueRows = ownStoreSlips.filter((row) =>
    orderNeedsVendor(row) ||
    [
      ORDER_MANAGER_STATUSES.vendorRequired,
      ORDER_MANAGER_STATUSES.vendorPlaced,
      ORDER_MANAGER_STATUSES.received,
      ORDER_MANAGER_STATUSES.sentToLab,
      ORDER_MANAGER_STATUSES.delayed
    ].includes(orderWorkflowStatus(row))
  );
  const orderTabRows = {
    history: ownStoreSlips,
    pending: pendingOrderRows,
    balancePayment: balancePaymentRows,
    manager: managerQueueRows
  };
  const orderTabTitle = {
    history: "Order History",
    pending: "Pending Orders",
    balancePayment: "Balance Payment",
    manager: "Order Manager Queue"
  }[orderTab] || "Order History";
  const orderTabs = [
    ["new", "New Order", null],
    ["history", "Order History", ownStoreSlips.length],
    ["balancePayment", "Balance Payment", balancePaymentRows.length],
    ["pending", "Pending Orders", pendingOrderRows.length],
    ...(canOrderManager ? [["manager", "Order Manager Queue", managerQueueRows.length]] : [])
  ];

  return (
    <section className={employeeMode ? "employee-layer-grid" : ""}>
      <Header title="Optical Order Form" subtitle="Create once. Customer slip, lab slip, daily register, payment and delivery entries are generated from it.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${filePrefix(state.settings)}-optical-orders.csv`, ownStoreSlips, orderSheetColumns)}>
          <FileSpreadsheet size={17} /> Orders sheet
        </button>
      </Header>
      <div className="status-tabs crm-layer-tabs order-module-tabs">
        {orderTabs.map(([key, label, count]) => (
          <button key={key} className={orderTab === key ? "active" : ""} type="button" onClick={() => setOrderTab(key)}>
            {label}{typeof count === "number" ? ` · ${count}` : ""}
          </button>
        ))}
      </div>
      {message ? <p className="notice">{message}</p> : null}
      {statusCorrectionModal ? (
        <div className="modal-backdrop cn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setStatusCorrectionModal(null)}>
          <section className="modal status-correction-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Change Status?</h2>
                <p>You are moving this order from {statusCorrectionModal.oldStatus} to {statusCorrectionModal.newStatus}. Please enter the reason for correction.</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setStatusCorrectionModal(null)} aria-label="Close">×</button>
            </div>
            <label>Reason for correction
              <textarea
                autoFocus
                value={statusCorrectionModal.reason}
                onChange={(event) => setStatusCorrectionModal({ ...statusCorrectionModal, reason: event.target.value })}
                placeholder="Example: staff marked picked up by mistake"
              />
            </label>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => setStatusCorrectionModal(null)}>Cancel</button>
              <ActionButton
                className="primary-button"
                disabled={!statusCorrectionModal.reason.trim()}
                action={orderActionState(`order-status-correction-${statusCorrectionModal.orderId}-${statusCorrectionModal.newStatus}`)}
                labels={{ loading: "Updating Status...", success: `${statusCorrectionModal.label} ✓`, error: "Could not update. Try Again" }}
                onClick={confirmOrderStatusCorrection}
              >
                Confirm Status Change
              </ActionButton>
            </div>
          </section>
        </div>
      ) : null}
      {collectBalanceOrder ? (
        <div className="modal-backdrop cn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setCollectBalanceOrderId("")}>
          <section className="modal status-correction-modal collect-balance-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Collect Balance</h2>
                <p>{collectBalanceOrder.orderNumber} · Pending {opticalMoney(computedOrderBalance(collectBalanceOrder))}</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setCollectBalanceOrderId("")} aria-label="Close">×</button>
            </div>
            <div className="split-payment-summary">
              <span><b>Pending</b>{opticalMoney(collectBalancePending)}</span>
              <span><b>Total collecting</b>{opticalMoney(collectBalanceTotal)}</span>
              <span><b>Remaining</b>{opticalMoney(collectBalanceRemaining)}</span>
            </div>
            <div className="form-grid collect-balance-received-by">
              <Input
                label="Received by"
                value={collectBalanceForm.receivedBy}
                onChange={(receivedBy) => setCollectBalanceForm({ ...collectBalanceForm, receivedBy })}
              />
            </div>
            <div className="split-payment-list">
              {collectBalanceRows.map((row, index) => (
                <article className="split-payment-row" key={row.id}>
                  <div className="split-payment-row-head">
                    <strong>Payment {index + 1}</strong>
                    {collectBalanceRows.length > 1 ? (
                      <button className="mini-button danger" type="button" onClick={() => removeCollectBalanceRow(row.id)}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="form-grid">
                    <Input
                      label="Amount received"
                      type="number"
                      inputMode="decimal"
                      value={row.amount}
                      onChange={(amount) => updateCollectBalanceRow(row.id, { amount })}
                    />
                    <label>Payment mode
                      <select value={row.paymentMode} onChange={(event) => updateCollectBalanceRow(row.id, { paymentMode: event.target.value })}>
                        <option value="">Select source</option>
                        {COLLECT_BALANCE_PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                      </select>
                    </label>
                    <Input
                      label="Received date/time"
                      type="datetime-local"
                      value={row.receivedAt || ""}
                      onChange={(receivedAt) => updateCollectBalanceRow(row.id, { receivedAt })}
                    />
                  </div>
                  <label>Note / reference optional
                    <textarea
                      value={row.note}
                      onChange={(event) => updateCollectBalanceRow(row.id, { note: event.target.value })}
                      placeholder="UPI ref, card approval, cash note"
                    />
                  </label>
                </article>
              ))}
            </div>
            <button className="secondary-button add-split-payment-button" type="button" onClick={addCollectBalanceRow}>
              <Plus size={16} /> Add another payment mode
            </button>
            <div className="button-row collect-balance-actions">
              <button className="secondary-button" type="button" onClick={() => setCollectBalanceOrderId("")}>Cancel</button>
              <ActionButton
                className="primary-button"
                disabled={collectBalanceTotal <= 0 || collectBalanceTotal > collectBalancePending}
                action={orderActionState(`order-collect-balance-${collectBalanceOrder.id}`)}
                labels={{ loading: "Saving...", success: "Saved ✓", error: "Could not save payment." }}
                onClick={() => runOrderAction(
                  `order-collect-balance-${collectBalanceOrder.id}`,
                  { loading: "Saving...", success: "Saved ✓", successMessage: "Payment saved.", error: "Could not save payment." },
                  () => saveBalanceCollection()
                )}
              >
                Save Payment
              </ActionButton>
            </div>
          </section>
        </div>
      ) : null}
      {lastSavedOrder && canPrintOrderFormSlip ? (
        <div className="button-row compact-action-row">
          <button className="primary-button" type="button" onClick={() => printOrderFormSlip(lastSavedOrder, state)}>
            <Printer size={16} /> Print Order Form / Lab Slip
          </button>
        </div>
      ) : null}
      {managerEditNotices.length && !employeeMode ? (
        <article className="panel field-help-card">
          <strong>Recent order edits</strong>
          <span>{managerEditNotices.map((log) => `${log.entityId} edited by ${log.changedBy}`).join(" · ")}</span>
        </article>
      ) : null}
      {!showHistory ? (
        <div className="panel optical-slip-panel">
          <div className="quick-strip">
            <span><b>Date</b>{form.date}</span>
            <span><b>Order / GC</b>{form.orderNumber}</span>
            <span><b>Store</b>{store?.name || "No store"}</span>
            <span><b>Salesperson</b>{salesperson?.fullName || user.name}</span>
          </div>
          {editingSlipId ? <p className="notice">Editing existing order. This edit will be logged for manager review.</p> : null}
          <OpticalSection title="Customer" subtitle="Name first. Phone optional." defaultOpen>
            <div className="form-grid">
              <CustomerAutocomplete
                state={state}
                value={form.customerId || ""}
                onSelect={(customer) => setForm((current) => customer ? ({
                  ...current,
                  customerId: customer.id,
                  phone: customer.phone || "",
                  customerName: customer.name || customer.customerName || "",
                  address: customer.address || "",
                  email: customer.email || "",
                  dob: customer.dob || "",
                  noPhone: Boolean(customer.noPhone || !customer.phone)
                }) : ({
                  ...current,
                  customerId: "",
                  phone: "",
                  customerName: "",
                  address: "",
                  email: "",
                  dob: "",
                  noPhone: false
                }))}
              />
              <label>
                Store
                <select value={form.storeId} disabled={user.role === "employee"} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>
                  {stores.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <Input label="Delivery date" type="date" value={form.deliveryDate} onChange={(deliveryDate) => setForm({ ...form, deliveryDate })} />
              <Input
                label="Phone optional"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                pattern="[0-9]*"
                maxLength={15}
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone: phone.replace(/\D/g, "") })}
                placeholder="Mobile number"
              />
              <Input label="Customer name" value={form.customerName} onChange={(customerName) => setForm({ ...form, customerName })} placeholder="Name" />
              <Input label="Address" value={form.address} onChange={(address) => setForm({ ...form, address })} placeholder="Optional" />
            </div>
            <p className="helper-text">Phone number is optional. If blank, the order will be saved as a walk-in customer.</p>
          </OpticalSection>

          <OpticalSection title="Products" subtitle={`${products.length} product(s) · ${opticalMoney(productTotal)}`} defaultOpen>
            <div className="extra-product-list">
              {products.map((product, index) => (
                <details className="extra-product-card product-card product-line-card" key={product.id} defaultOpen={index === products.length - 1}>
                  <summary className="product-line-summary">
                    <span>
                      <strong>Product {index + 1} · {opticalProductLabel(product.type)}</strong>
                      <small>{product.name || "Tap to add product details"}</small>
                    </span>
                    <em>{opticalMoney(Number(product.quantity || 1) * Number(product.amount || 0))}</em>
                  </summary>
                  <div className="product-line-body">
                    <div className="form-grid">
                      <label>
                        Product
                        <select
                          value={product.type}
                          onChange={(event) => {
                            const nextType = event.target.value;
                            updateProduct(product.id, {
                              type: nextType,
                              source: nextType === "frame" || nextType === "sunglasses" ? "store" : "selected",
                              requiresPower: ["lens", "contactSolution", "eyeTest"].includes(nextType)
                            });
                          }}
                        >
                          {OPTICAL_PRODUCT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                      </label>
                      <ProductAutocomplete
                        state={state}
                        value={product.productId || ""}
                        onSelect={(masterProduct) => updateProduct(product.id, masterProduct ? {
                          productId: masterProduct.id,
                          name: masterProduct.productName || masterProduct.name || product.name,
                          barcode: masterProduct.barcode || product.barcode || "",
                          amount: product.amount || masterProduct.sellingPrice || masterProduct.mrp || "",
                          productMasterStatus: "Product Matched",
                          inventoryStatus: Number(masterProduct.stockQty || 0) > 0 ? "Stock Available" : "Negative Inventory / Purchase Pending"
                        } : {
                          productId: "",
                          productMasterStatus: "Product Master Pending",
                          inventoryStatus: "Inventory Pending"
                        })}
                      />
                      <Input label={`${opticalProductLabel(product.type)} details`} value={product.name} onChange={(name) => updateProduct(product.id, { name })} placeholder="Brand / model / type / description" />
                      <p className="helper-text">{product.productId ? "Product matched with master." : "Manual item allowed. It will go to Product Master Pending queue if not mapped."}</p>
                      <Input label="Barcode optional" value={product.barcode || ""} onChange={(barcode) => updateProduct(product.id, { barcode })} placeholder="Scan or type item barcode" />
                      <Input label="Qty" type="number" inputMode="numeric" value={product.quantity} onChange={(quantity) => updateProduct(product.id, { quantity })} />
                      <Input label="Amount" type="number" inputMode="decimal" value={product.amount} onChange={(amount) => updateProduct(product.id, { amount })} />
                      {productSpecificFields(product)}
                    </div>
                    {["frame", "sunglasses"].includes(product.type) ? (
                      <div className="order-photo-row">
                        <label className="file-button">
                          Add product photo
                          <input type="file" accept="image/*" capture="environment" onChange={(event) => addProductPhoto(product.id, event.target.files?.[0])} />
                        </label>
                        <PhotoPreview src={product.photo} label={opticalProductLabel(product.type)} onRemove={() => updateProduct(product.id, { photo: "" })} />
                      </div>
                    ) : null}
                    <label>Notes<textarea value={product.notes} onChange={(event) => updateProduct(product.id, { notes: event.target.value })} placeholder="Anything special for this product" /></label>
                    {products.length > 1 ? (
                      <button className="mini-button danger" type="button" onClick={() => removeProduct(product.id)}>Remove this product</button>
                    ) : null}
                  </div>
                </details>
              ))}
            </div>
            <div className="extra-total-card">
              <span>Product total</span>
              <strong>{opticalMoney(productTotal)}</strong>
              <small>If Total amount is left blank, this amount will be used automatically.</small>
            </div>
            <button className="primary-button add-product-main" type="button" onClick={() => addProduct("frame")}>
              <Plus size={17} /> Add product
            </button>
          </OpticalSection>

          {needsPowerSection ? (
          <OpticalSection title="Power" subtitle="Right / left eye and measurements">
            <div className="optical-power-list">
              {powerCard("Right Eye Distance", "rightDistance")}
              {powerCard("Left Eye Distance", "leftDistance")}
              <div className="optical-power-card">
                <strong>Addition</strong>
                <label className="toggle-line"><input type="checkbox" checked={form.sameAddition} onChange={(event) => setForm({ ...form, sameAddition: event.target.checked, leftAdd: event.target.checked ? form.rightAdd : form.leftAdd })} /> Same ADD for both eyes</label>
                <div className="optical-power-grid">
                  {powerControl("rightAdd", "Right ADD", form.rightAdd.sph, (sph) => setForm((current) => ({ ...current, rightAdd: { ...current.rightAdd, sph }, leftAdd: current.sameAddition ? { ...current.leftAdd, sph } : current.leftAdd })), OPTICAL_ADD_OPTIONS, ["+", "-"])}
                  {powerControl("leftAdd", "Left ADD", form.leftAdd.sph, (sph) => setForm({ ...form, leftAdd: { ...form.leftAdd, sph } }), OPTICAL_ADD_OPTIONS, ["+", "-"])}
                </div>
              </div>
            </div>
            <div className="measure-grid">
              {["pd", "fh", "a", "b", "dbl", "dia"].map((key) => (
                <Input key={key} label={key.toUpperCase()} value={form.measurements[key]} onChange={(value) => setForm({ ...form, measurements: { ...form.measurements, [key]: value } })} />
              ))}
            </div>
          </OpticalSection>
          ) : null}

          {needsContactLensSection ? (
            <OpticalSection title="Contact lens" subtitle={form.autoContactPower ? "Auto converted from spectacle power" : "Manual CL power"}>
              <label className="toggle-line">
                <input
                  type="checkbox"
                  checked={form.autoContactPower}
                  onChange={(event) => setForm({
                    ...form,
                    autoContactPower: event.target.checked,
                    contactRight: event.target.checked ? { ...form.rightDistance, sph: convertToContactLensPower(form.rightDistance.sph) } : form.contactRight,
                    contactLeft: event.target.checked ? { ...form.leftDistance, sph: convertToContactLensPower(form.leftDistance.sph) } : form.contactLeft
                  })}
                />
                Auto convert spectacle power into contact lens power
              </label>
              <div className="form-grid">
                <label>Contact lens details<textarea value={form.contactLensDetails} onChange={(event) => setForm({ ...form, contactLensDetails: event.target.value })} placeholder="Brand / disposable / BC / DIA / quantity" /></label>
              </div>
              {powerCard("Right Contact Lens Power", "contactRight")}
              {powerCard("Left Contact Lens Power", "contactLeft")}
            </OpticalSection>
          ) : null}

          <OpticalSection title="Service and notes" subtitle="Repair, fitting, prescription">
            <div className="form-grid">
              <label>Service / repair<textarea value={form.serviceDetails} onChange={(event) => setForm({ ...form, serviceDetails: event.target.value })} placeholder="Repair, fitting, nosepad, screw, cleaning" /></label>
              <Input label="Prescribed by" value={form.prescribedBy} onChange={(prescribedBy) => setForm({ ...form, prescribedBy })} />
              <label>Special notes<textarea value={form.specialNotes} onChange={(event) => setForm({ ...form, specialNotes: event.target.value })} /></label>
            </div>
          </OpticalSection>

          <OpticalSection title="Payment" subtitle={`Balance ${opticalMoney(balance)}`} defaultOpen>
            <div className="form-grid">
              <Input label="Bill subtotal" type="number" inputMode="decimal" value={form.totalAmount} onChange={(totalAmount) => setForm({ ...form, totalAmount })} placeholder={`${productTotal || 0}`} />
              <Input label="Discount" type="number" inputMode="decimal" value={form.discountValue} onChange={(discountValue) => setForm({ ...form, discountValue })} placeholder="Rupees only" />
              <div className="balance-card"><span>Total payable</span><strong>{opticalMoney(totalAmount)}</strong><small>Discount {opticalMoney(discountAmount)}</small></div>
              <Input label="Advance received" type="number" value={form.advanceReceived} onChange={(advanceReceived) => setForm({ ...form, advanceReceived })} />
              <label>
                Payment mode
                <select value={form.paymentMode} onChange={(event) => setForm({ ...form, paymentMode: event.target.value })}>
                  {OPTICAL_PAYMENT_MODES.map((mode) => <option key={mode}>{mode}</option>)}
                </select>
              </label>
              <div className="balance-card"><span>Balance</span><strong>{opticalMoney(balance)}</strong></div>
            </div>
            {form.paymentMode === "Split" ? (
              <div className="form-grid">
                {Object.entries({ cash: "Cash", upi: "UPI", card: "Card", paytm: "Paytm" }).map(([key, label]) => (
                  <Input key={key} label={label} type="number" value={form.splitPayments[key]} onChange={(value) => setForm({ ...form, splitPayments: { ...form.splitPayments, [key]: value } })} />
                ))}
              </div>
            ) : null}
          </OpticalSection>
          <div className="button-row">
            <ActionButton
              className="primary-button"
              disabled={isSavingOrder}
              action={orderActionState("order-save")}
              labels={{ loading: "Saving...", success: "Saved ✓", error: "Save Failed. Try Again" }}
              onClick={() => runOrderAction(
                "order-save",
                {
                  loading: "Saving...",
                  success: "Saved ✓",
                  successMessage: "Order saved. Order Form / Lab Slip is ready to print.",
                  error: "Save Failed. Try Again"
                },
                () => saveSlip()
              )}
            >
              {editingSlipId ? "Save order edit" : "Save Order Form"}
            </ActionButton>
            {lastSavedOrder && canPrintOrderFormSlip ? (
              <button className="secondary-button" type="button" onClick={() => printOrderFormSlip(lastSavedOrder, state)}>
                <Printer size={16} /> Print Order Form / Lab Slip
              </button>
            ) : null}
            {editingSlipId ? (
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setEditingSlipId("");
                  setForm(emptyOpticalForm({ storeId: defaultStoreId, salespersonId: defaultSalespersonId, orderNumber: nextOpticalOrderNumber(state.opticalFastSlips) }));
                  setMessage("");
                }}
              >
                Stop editing
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="panel">
          <div className="panel-title-row">
            <h2>{orderTabTitle}</h2>
            <span className="status-pill">{(orderTabRows[orderTab] || ownStoreSlips).length} total</span>
          </div>
          {orderTab === "balancePayment" ? (
            <div className="saved-order-list">
              {balancePaymentRows.length ? balancePaymentRows.map((row) => (
                <article className="order-collapse saved-order-collapse balance-payment-card" key={row.id}>
                  <div className="panel-title-row">
                    <div>
                      <strong>{row.orderNumber} · {row.customer?.name || row.customerName || "Customer"}</strong>
                      <small>{state.stores.find((item) => item.id === row.storeId)?.code || row.storeName || "-"} · {selectedItemsLabel(row)}</small>
                    </div>
                    <span className="status-pill danger">{opticalMoney(computedOrderBalance(row))} due</span>
                  </div>
                  <div className="order-detail-grid">
                    <p><strong>Total:</strong> {opticalMoney(row.payment?.totalAmount)}</p>
                    <p><strong>Advance:</strong> {opticalMoney(row.payment?.advanceReceived)}</p>
                    <p><strong>Paid later:</strong> {opticalMoney(orderPaymentHistory(row).reduce((sum, item) => sum + Number(item.amount || 0), 0))}</p>
                    <p><strong>Payment:</strong> {orderPaymentStatus(row)}</p>
                  </div>
                  <div className="button-row">
                    <button className="primary-button" type="button" onClick={() => openCollectBalance(row)}>
                      Collect Balance
                    </button>
                    <button className="secondary-button" type="button" onClick={() => loadSlipForEdit(row)}>
                      Edit order
                    </button>
                  </div>
                </article>
              )) : (
                <div className="empty-state">No pending balance orders found.</div>
              )}
            </div>
          ) : (
            <div className="saved-order-list">
              {(orderTabRows[orderTab] || ownStoreSlips).length ? (orderTabRows[orderTab] || ownStoreSlips).map((row) => (
                <details className="order-collapse saved-order-collapse" key={row.id}>
                  <summary>
                    <span>
                      <strong>{row.orderNumber} · {row.customer?.name || row.customerName || "Customer"}</strong>
                      <small>{state.stores.find((item) => item.id === row.storeId)?.code || row.storeName || "-"} · {selectedItemsLabel(row)} · {opticalMoney(computedOrderBalance(row))} balance · {orderDeliveryStatus(row, state)}</small>
                    </span>
                    <span className="saved-order-summary-actions">
                      <em>{orderWorkflowStatus(row)}</em>
                      <ChevronRight size={18} />
                    </span>
                  </summary>
                  {renderSavedOrderDetails(row)}
                </details>
              )) : (
                <div className="empty-state">No orders found in this tab.</div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Dashboard({ state, user, visibleEmployees, selectedMonth, selectedStoreId, setTab, setWorkFocus, setCrmFocus, updateState }) {
  const [cockpitModule, setCockpitModule] = useState("");
  const [detailKey, setDetailKey] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hqSearch, setHqSearch] = useState("");
  const today = localDateOnly();
  const scopedEmployees =
    selectedStoreId === "all" ? visibleEmployees : visibleEmployees.filter((employee) => employee.storeId === selectedStoreId);
  const scopedEmployeeIds = new Set(scopedEmployees.map((employee) => employee.id));
  const todayPunches = state.punches.filter(
    (punch) => attendancePunchDate(punch) === today && scopedEmployeeIds.has(punch.employeeId)
  );
  const latestPayroll = state.payrollRuns.find((run) => run.month === selectedMonth);
  const pendingTasks = (state.workTasks || []).filter((task) => task.status !== "completed" && !isTaskArchived(task) && scopedEmployeeIds.has(task.assignedTo));
  const overdueTasks = pendingTasks.filter((task) => task.dueDate && task.dueDate < today);
  const immediateTeam = getImmediateReportEmployees(state.employees, user.employeeId);
  const scopedImmediateTeam = selectedStoreId === "all" ? immediateTeam : immediateTeam.filter((employee) => employee.storeId === selectedStoreId);
  const targetEmployees = user.employeeId ? scopedImmediateTeam : scopedEmployees;
  const targetEmployeeIds = new Set(targetEmployees.map((employee) => employee.id));
  const targetRows = (state.salesTargets || []).filter(
    (target) => targetEmployeeIds.has(target.employeeId) && (!target.month || target.month === selectedMonth)
  );
  const canSeeTargets =
    isModuleEnabled(state.settings, "salesTargets") &&
    (user.role === "super_admin" || user.permissions?.salesTargets || user.permissions?.allEmployees || targetEmployees.length > 0);
  const targetAverage = cumulativeTargetProgress(targetRows);
  const formatTime = (value) =>
    value
      ? new Date(value).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit"
        })
      : "-";

  const attendanceRows = scopedEmployees
    .map((employee) => buildEmployeeAttendanceDay(state, employee, today))
    .filter(Boolean);
  const groupedPendingLeaveApplications = groupLeaveApplications(
    state.corrections.filter(
      (item) =>
        item.status === "pending" &&
        scopedEmployeeIds.has(item.employeeId) &&
        item.requestedValue?.kind === "leave_application"
    )
  );
  const pendingCorrectionRows = state.corrections.filter(
    (item) =>
      item.status === "pending" &&
      scopedEmployeeIds.has(item.employeeId) &&
      item.requestedValue?.kind !== "leave_application"
  );
  const pendingApprovalRows = [...groupedPendingLeaveApplications, ...pendingCorrectionRows];

  const detailMap = {
    todayAttendance: {
      title: "Today's Attendance",
      rows: attendanceRows
    },
    outside: {
      title: "Outside Geofence Attempts",
      rows: attendanceRows.filter((row) => [row.punchIn, row.punchOut].some((punch) => punch?.insideGeofence === false))
    },
    pending: {
      title: "Pending Approvals",
      rows: pendingApprovalRows
    },
    absent: {
      title: "Absent Today",
      rows: attendanceRows.filter((row) => row.summary.code === "A")
    },
    late: {
      title: "Late Comers",
      rows: attendanceRows.filter((row) => row.summary.late)
    },
    early: {
      title: "Early Goers",
      rows: attendanceRows.filter((row) => row.summary.earlyDeparture)
    },
    payable: {
      title: `Monthly Payable - ${formatMonth(selectedMonth)}`,
      rows: (latestPayroll?.items || []).filter((item) => scopedEmployeeIds.has(item.employeeId))
    },
    pendingTasks: {
      title: "Pending Tasks",
      rows: pendingTasks
    },
    overdueTasks: {
      title: "Overdue Tasks",
      rows: overdueTasks
    },
    targets: {
      title: `Target Achievement - ${formatMonth(selectedMonth)}`,
      rows: targetRows
    }
  };

  const selectedDetail = detailKey ? detailMap[detailKey] || detailMap.todayAttendance : null;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredDetailRows = selectedDetail && normalizedSearch
    ? selectedDetail.rows.filter((row) => {
        const employeeName =
          row.employee?.fullName ||
          row.employeeName ||
          state.employees.find((employee) => employee.id === row.employeeId)?.fullName ||
          "";
        return employeeName.toLowerCase().includes(normalizedSearch);
      })
    : selectedDetail?.rows || [];
  const kpis = [
    ["todayAttendance", "Today's attendance", `${attendanceRows.filter((row) => row.summary.presentUnits > 0).length}/${attendanceRows.length}`, "kpi-present"],
    ["outside", "Outside geofence", detailMap.outside.rows.length, "kpi-outside"],
    ["late", "Late comers", detailMap.late.rows.length, "kpi-late"],
    ["early", "Early goers", detailMap.early.rows.length, "kpi-early"],
    ["absent", "Absent today", detailMap.absent.rows.length, "kpi-absent"],
    ["overdueTasks", "Overdue tasks", detailMap.overdueTasks.rows.length, "kpi-absent"],
    ["pendingTasks", "Pending tasks", detailMap.pendingTasks.rows.length, "kpi-pending"],
    ...(canSeeTargets ? [["targets", "Target achieved", `${targetAverage}%`, "kpi-target"]] : []),
    ["pending", "Pending approvals", detailMap.pending.rows.length, "kpi-approval"]
  ];
  const scopedStoreIds = new Set(scopedEmployees.map((employee) => employee.storeId).filter(Boolean));
  const scopedOrders = (state.opticalFastSlips || []).filter((slip) =>
    selectedStoreId === "all" || scopedStoreIds.has(slip.storeId)
  );
  const todayOrders = scopedOrders.filter((slip) => slip.date === today);
  const pendingOrders = scopedOrders.filter((slip) => computedOrderBalance(slip) > 0 || orderDeliveryStatus(slip, state) !== "Delivered");
  const scopedCustomers = (state.opticalCustomers || []).filter((customer) =>
    selectedStoreId === "all" || scopedStoreIds.has(customer.storeId)
  );
  const monthOrders = scopedOrders.filter((slip) => monthOf(slip.date) === selectedMonth);
  const yesterday = dateOffset(-1);
  const yesterdayOrders = scopedOrders.filter((slip) => slip.date === yesterday);
  const deliveredPaymentPending = scopedOrders.filter((slip) => /delivered/i.test(orderDeliveryStatus(slip, state)) && orderBalance(slip) > 0);
  const ordersDueToday = scopedOrders.filter((slip) => slip.deliveryDate === today || slip.orderDetails?.deliveryDate === today);
  const bankRecoRows = state.bankReco || [];
  const bankRecoPendingRows = bankRecoRows.filter((row) => row.status !== "done" && (selectedStoreId === "all" || row.storeId === selectedStoreId));
  const globalResults = hqSearch.trim()
    ? [
        ...scopedCustomers
          .filter((customer) => `${customer.name || ""} ${customer.phone || ""} ${customer.walkInId || ""}`.toLowerCase().includes(hqSearch.toLowerCase()))
          .map((customer) => ({ ...customer, id: `customer-${customer.id}`, resultType: "Customer" })),
        ...scopedOrders
          .filter((order) => `${order.orderNumber || ""} ${order.gcNumber || ""} ${order.customer?.name || ""} ${order.customer?.phone || ""}`.toLowerCase().includes(hqSearch.toLowerCase()))
          .map((order) => ({ ...order, id: `order-${order.id}`, resultType: "Order" })),
        ...visibleEmployees
          .filter((employee) => `${employee.fullName || ""} ${employee.phone || ""} ${employee.employeeCode || ""}`.toLowerCase().includes(hqSearch.toLowerCase()))
          .map((employee) => ({ ...employee, id: `staff-${employee.id}`, resultType: "Staff" })),
        ...(state.crmProducts || [])
          .filter((product) => `${product.name || ""} ${product.productCode || ""} ${product.barcode || ""}`.toLowerCase().includes(hqSearch.toLowerCase()))
          .map((product) => ({ ...product, id: `product-${product.id}`, resultType: "Product" }))
      ].slice(0, 50)
    : [];
  const moduleCards = [
    ["optical", "Optical CRM", `${todayOrders.length} orders`, "kpi-target", () => setTab("optical")],
    ["stores", "Stores", state.stores.filter((store) => store.active !== false).length, "kpi-present", () => setTab("stores")],
    ["attendance", "HRMS", `${attendanceRows.filter((row) => row.summary.presentUnits > 0).length}/${attendanceRows.length}`, "kpi-pending", () => setCockpitModule("attendance")],
    ["reports", "Reports", "Sheets", "kpi-approval", () => setTab("reports")],
    ["dailyReconciliation", "Bank Reco", bankRecoPendingRows.length, "kpi-outside", () => setTab("dailyReconciliation")],
    ["communication", "Communication", "CRM", "kpi-early", () => setTab("communication")],
    ["admin", "Admin", "Access", "kpi-payable", () => setTab("users")]
  ];
  const ownerCards = [
    ["Yesterday Sale", currency(yesterdayOrders.reduce((sum, order) => sum + orderTotal(order), 0))],
    ["Yesterday Collection", currency(yesterdayOrders.reduce((sum, order) => sum + orderCollection(order), 0))],
    ["This Month Sale", currency(monthOrders.reduce((sum, order) => sum + orderTotal(order), 0))],
    ["This Month Collection", currency(monthOrders.reduce((sum, order) => sum + orderCollection(order), 0))],
    ["Delivered Payment Pending", deliveredPaymentPending.length],
    ["Staff Absent Today", detailMap.absent.rows.length],
    ["Orders Due Today", ordersDueToday.length],
    ["Bank Reco Pending / Done", `${bankRecoPendingRows.length}/${bankRecoRows.filter((row) => row.status === "done").length}`]
  ];

  const decideApproval = async (row, status) => {
    if (!updateState) return;
    if (row.requestedValue?.kind === "leave_application") {
      const rows = row.children?.length ? row.children : [row];
      const decidedAt = new Date().toISOString();
      const updatedRows = rows.map((item) => ({ ...item, status, approvedBy: user.name, decidedAt }));
      const savedRows = [];
      for (const item of updatedRows) {
        savedRows.push(await persistRequiredEntity("attendance_corrections", item, "Leave application"));
      }
      const log = createAuditLog({
        entityType: "leave_application",
        entityId: row.id,
        action: `leave_${status}`,
        oldValue: null,
        newValue: { ...row.requestedValue, dates: rows.map((item) => item.date) },
        reason: row.reason,
        effectiveDate: row.requestedValue?.fromDate || row.date,
        user
      });
      await persistAuditLog(log).catch(() => null);
      const updatedById = new Map(savedRows.map((item) => [item.id, item]));
      const updatedIds = new Set(savedRows.map((item) => item.id));
      updateState((current) => ({
        ...current,
        corrections: (current.corrections || []).map((item) => updatedById.get(item.id) || item),
        attendanceOverrides:
          status === "approved"
            ? [
                ...(current.attendanceOverrides || []).filter((item) => !updatedIds.has(item.id)),
                ...savedRows.map((item) => ({ id: item.id, employeeId: item.employeeId, date: item.date, ...item.requestedValue }))
              ]
            : (current.attendanceOverrides || []).filter((item) => !updatedIds.has(item.id)),
        auditLogs: [log, ...(current.auditLogs || [])]
      }));
      return;
    }

    const updated = { ...row, status, approvedBy: user.name, decidedAt: new Date().toISOString() };
    const savedCorrection = await persistRequiredEntity("attendance_corrections", updated, "Attendance correction");
    const log = createAuditLog({
      entityType: "attendance_correction",
      entityId: row.id,
      action: status,
      oldValue: row.oldValue,
      newValue: row.requestedValue,
      reason: row.reason,
      effectiveDate: row.date,
      user
    });
    await persistAuditLog(log).catch(() => null);
    updateState((current) => ({
      ...current,
      corrections: (current.corrections || []).map((item) => (item.id === row.id ? savedCorrection : item)),
      attendanceOverrides:
        status === "approved"
          ? [
              ...(current.attendanceOverrides || []).filter((item) => item.id !== row.id),
              { id: row.id, employeeId: row.employeeId, date: row.date, ...row.requestedValue }
            ]
          : (current.attendanceOverrides || []).filter((item) => item.id !== row.id),
      auditLogs: [log, ...(current.auditLogs || [])]
    }));
  };

  return (
    <section className="app-home-screen">
      <Header title="Framesbay HQ" subtitle="Owner view for sales, stores, HR, reports, bank reco and admin controls.">
        <label className="compact-filter">
          Date
          <input type="date" value={today} readOnly />
        </label>
      </Header>
      <div className="hq-search-row">
        <div className="staff-search-bar dashboard-search">
          <Search size={18} />
          <input
            value={hqSearch}
            onChange={(event) => setHqSearch(event.target.value)}
            placeholder="Search customer, mobile, order, invoice, barcode, staff"
          />
          {hqSearch ? (
            <button type="button" aria-label="Clear search" onClick={() => setHqSearch("")}>
              <XCircle size={17} />
            </button>
          ) : null}
        </div>
      </div>
      {hqSearch ? (
        <article className="panel dashboard-detail">
          <div className="panel-title-row">
            <h2>Search results</h2>
            <button className="mini-button" type="button" onClick={() => setHqSearch("")}>Close</button>
          </div>
          <DetailList
            rows={globalResults}
            getTitle={(row) => row.name || row.fullName || row.customer?.name || row.orderNumber || row.productCode || "Result"}
            getMeta={(row) => `${row.resultType} · ${row.phone || row.customer?.phone || row.employeeCode || row.productType || row.orderNumber || ""}`}
            getStatus={(row) => row.resultType}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.name || row.fullName || row.customer?.name || row.orderNumber || "Result"}</h3>
                <p><strong>Type:</strong> {row.resultType}</p>
                <p><strong>Phone:</strong> {row.phone || row.customer?.phone || "-"}</p>
                <p><strong>Order:</strong> {row.orderNumber || "-"}</p>
                <p><strong>Store:</strong> {state.stores.find((store) => store.id === row.storeId)?.name || row.storeName || "-"}</p>
                <p><strong>Amount:</strong> {row.payment ? opticalMoney(row.payment?.totalAmount || 0) : "-"}</p>
              </div>
            )}
          />
        </article>
      ) : null}
      <div className="cockpit-filter-note">
        {cockpitModule ? (
          <button
            className="mini-button"
            type="button"
            onClick={() => {
              setCockpitModule("");
              setDetailKey("");
              setSearchTerm("");
            }}
          >
            Back to My Cockpit
          </button>
        ) : null}
        <label className="compact-filter">
          Store
          <select value={selectedStoreId} onChange={(event) => setSelectedStoreId(event.target.value)}>
            <option value="all">All Stores</option>
            {state.stores.filter((store) => store.active !== false).map((store) => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        </label>
      </div>
      {!cockpitModule ? (
      <div className="kpi-grid module-cockpit-grid">
        {moduleCards.map(([key, label, value, tone, action]) => (
          <Kpi
            key={key}
            label={label}
            value={value}
            tone={tone}
            active={cockpitModule === key}
            onClick={action}
          />
        ))}
      </div>
      ) : null}
      {!cockpitModule ? (
        <>
          <div className="quick-action-row">
            <button className="primary-button" type="button" onClick={() => { setCrmFocus?.("orders"); setTab("optical"); }}>Punch Sale</button>
            <button className="secondary-button" type="button" onClick={() => { setWorkFocus?.("tasks"); setTab("tasks"); }}>My Tasks</button>
            <button className="secondary-button" type="button" onClick={() => { setCrmFocus?.("payments"); setTab("optical"); }}>Receive Payment</button>
            <button className="secondary-button" type="button" onClick={() => { setCrmFocus?.("orders"); setTab("optical"); }}>Check Order</button>
            <button className="secondary-button" type="button" onClick={() => setTab("dailyReconciliation")}>Bank Reco Pending / Done</button>
          </div>
          <div className="hq-status-card-grid">
            {ownerCards.map(([label, value]) => (
              <article className="panel hq-status-card" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </article>
            ))}
          </div>
        </>
      ) : null}
      {cockpitModule === "attendance" ? (
      <>
      <Header title="Sales Attendance Cockpit" subtitle="Daily attendance, late coming, geofence, absents and approval view." />
      <div className="kpi-grid">
        {kpis.map(([key, label, value, tone]) => (
          <Kpi
            key={key}
            label={label}
            value={value}
            tone={tone}
            active={detailKey === key}
            onClick={() => {
              if (detailKey === key) {
                setDetailKey("");
                setSearchTerm("");
                return;
              }
              setDetailKey(key);
            }}
          />
        ))}
      </div>
      </>
      ) : null}
      {cockpitModule === "crm" ? (
      <section className="module-detail-screen">
        <Header title="CRM Cockpit" subtitle="Orders, customers, tasks and follow-ups will live here in layers.">
          <button className="secondary-button" type="button" onClick={() => setTab("optical")}>
            Open orders
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => {
              setWorkFocus?.("tasks");
              setTab("tasks");
            }}
          >
            Open tasks
          </button>
        </Header>
        <div className="kpi-grid">
          <Kpi label="Orders today" value={todayOrders.length} tone="kpi-present" onClick={() => setTab("optical")} />
          <Kpi label="Pending orders" value={pendingOrders.length} tone="kpi-pending" onClick={() => setTab("optical")} />
          <Kpi label="Customers" value={scopedCustomers.length} tone="kpi-target" onClick={() => setTab("optical")} />
          <Kpi
            label="Pending tasks"
            value={pendingTasks.length}
            tone="kpi-approval"
            onClick={() => {
              setWorkFocus?.("tasks");
              setTab("tasks");
            }}
          />
        </div>
        <div className="panel-grid two">
          <article className="panel dashboard-detail">
            <div className="panel-title-row">
              <h2>Latest CRM Orders</h2>
              <button className="mini-button" type="button" onClick={() => setTab("optical")}>Open all</button>
            </div>
            <DetailList
              rows={scopedOrders.slice(0, 20)}
              getTitle={(row) => `${row.orderNumber} · ${row.customer?.name || row.customerName || "Customer"}`}
              getMeta={(row) => `${row.storeName || "-"} · ${row.salespersonName || "-"} · ${opticalMoney(row.payment?.totalAmount || 0)}`}
              getStatus={(row) => computedOrderBalance(row) > 0 ? "DUE" : "PAID"}
              renderDetails={(row) => (
                <div className="detail-fields">
                  <h3>{row.orderNumber}</h3>
                  <p><strong>Customer:</strong> {row.customer?.name || "-"}</p>
                  <p><strong>Phone:</strong> {row.customer?.phone || "-"}</p>
                  <p><strong>Total:</strong> {opticalMoney(row.payment?.totalAmount || 0)}</p>
                  <p><strong>Discount:</strong> {opticalMoney(row.payment?.discountAmount || 0)}</p>
                  <p><strong>Balance:</strong> {opticalMoney(computedOrderBalance(row))}</p>
                </div>
              )}
            />
          </article>
          <article className="panel dashboard-detail">
            <div className="panel-title-row">
              <h2>CRM Tasks</h2>
              <button className="mini-button" type="button" onClick={() => {
                setWorkFocus?.("tasks");
                setTab("tasks");
              }}>Open all</button>
            </div>
            <DetailList
              rows={pendingTasks.slice(0, 20)}
              getTitle={(row) => row.title}
              getMeta={(row) => `${state.employees.find((employee) => employee.id === row.assignedTo)?.fullName || "Employee"} · Due ${row.dueDate || "-"}`}
              getStatus={(row) => row.dueDate && row.dueDate < today ? "OVERDUE" : "OPEN"}
              renderDetails={(row) => (
                <div className="detail-fields">
                  <h3>{row.title}</h3>
                  <p><strong>Assigned to:</strong> {state.employees.find((employee) => employee.id === row.assignedTo)?.fullName || "-"}</p>
                  <p><strong>Due:</strong> {row.dueDate || "-"}</p>
                  <p><strong>Details:</strong> {row.description || "-"}</p>
                </div>
              )}
            />
          </article>
        </div>
      </section>
      ) : null}
      {cockpitModule === "inventory" ? (
      <section className="module-detail-screen">
        <Header title="Inventory Cockpit" subtitle="Stock, transfer, purchase, barcode and audit layers will be added here." />
        <div className="module-tile-grid">
          {["Products", "Inventory levels", "Transfer stock", "Purchase", "Barcode", "Stock audit", "Closing stock", "Damage / loss"].map((label) => (
            <article className="module-tile-card" key={label}>
              <FileText size={24} />
              <strong>{label}</strong>
              <small>Coming into this module layer.</small>
            </article>
          ))}
        </div>
      </section>
      ) : null}
      {cockpitModule === "communication" ? (
      <section className="module-detail-screen">
        <Header title="Communication Cockpit" subtitle="Order-ready messages, feedback, reminders and customer follow-ups will be added here." />
        <div className="module-tile-grid">
          {["Order ready messages", "Feedback messages", "Customer reminders", "Birthday list", "Anniversary list", "Follow-up queue", "DND customers", "Message history"].map((label) => (
            <article className="module-tile-card" key={label}>
              <FileText size={24} />
              <strong>{label}</strong>
              <small>Prepared as a separate CRM communication layer.</small>
            </article>
          ))}
        </div>
      </section>
      ) : null}
      {cockpitModule === "attendance" && selectedDetail ? (
      <article className="panel dashboard-detail">
        <div className="panel-title-row">
          <h2>{selectedDetail.title}</h2>
          <button
            className="mini-button"
            type="button"
            onClick={() => {
              setDetailKey("");
              setSearchTerm("");
            }}
          >
            Close
          </button>
        </div>
        {detailKey === "pendingTasks" || detailKey === "overdueTasks" || detailKey === "targets" ? (
          <div className="button-row">
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                setWorkFocus?.(detailKey === "targets" ? "targets" : "tasks");
                setTab("tasks");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Open Tasks
            </button>
          </div>
        ) : null}
        <div className="staff-search-bar dashboard-search">
          <Search size={17} />
          <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search this detail" />
          {searchTerm ? (
            <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")}>
              <XCircle size={17} />
            </button>
          ) : null}
        </div>
        {detailKey === "pendingTasks" || detailKey === "overdueTasks" ? (
          <DetailList
            rows={filteredDetailRows}
            getTitle={(row) => row.title}
            getMeta={(row) => `${state.employees.find((employee) => employee.id === row.assignedTo)?.fullName || "Employee"} · Due ${row.dueDate || "-"}`}
            getStatus={(row) => row.dueDate && row.dueDate < today ? "OVERDUE" : row.priority || "OPEN"}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.title}</h3>
                <p><strong>Assigned to:</strong> {state.employees.find((employee) => employee.id === row.assignedTo)?.fullName || "-"}</p>
                <p><strong>Status:</strong> {row.status || "assigned"}</p>
                <p><strong>Due:</strong> {row.dueDate || "-"}</p>
                <p><strong>Priority:</strong> {row.priority || "Normal"}</p>
                <p><strong>Details:</strong> {row.description || "-"}</p>
              </div>
            )}
          />
        ) : detailKey === "targets" ? (
          <DetailList
            rows={filteredDetailRows}
            getTitle={(row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || "Employee"}
            getMeta={(row) => `${formatMonth(row.month || selectedMonth)} · ${currency(row.achievedAmount || 0)} / ${currency(row.targetAmount || 0)}`}
            getStatus={(row) => `${targetProgress(row)}%`}
            renderDetails={(row) => {
              const employee = state.employees.find((item) => item.id === row.employeeId);
              return (
                <div className="detail-fields">
                  <h3>{employee?.fullName || "Employee"}</h3>
                  <p><strong>Month:</strong> {formatMonth(row.month || selectedMonth)}</p>
                  <p><strong>Target:</strong> {currency(row.targetAmount || 0)}</p>
                  <p><strong>Achieved:</strong> {currency(row.achievedAmount || 0)}</p>
                  <p><strong>Percentage:</strong> {targetProgress(row)}%</p>
                  <p><strong>Notes:</strong> {row.notes || "-"}</p>
                </div>
              );
            }}
          />
        ) : detailKey === "pending" ? (
          <DetailList
            rows={filteredDetailRows}
            getTitle={(row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || "Employee"}
            getMeta={(row) => row.requestedValue?.kind === "leave_application"
              ? `${row.requestedValue?.fromDate || row.date} to ${row.requestedValue?.toDate || row.date} · ${row.requestedValue?.days || row.children?.length || 1} day${Number(row.requestedValue?.days || row.children?.length || 1) === 1 ? "" : "s"}`
              : `${row.date} · Requested ${row.requestedValue?.code || "-"}`
            }
            getStatus={(row) => row.status}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{state.employees.find((employee) => employee.id === row.employeeId)?.fullName || "Employee"}</h3>
                {row.requestedValue?.kind === "leave_application" ? (
                  <>
                    <p><strong>Leave dates:</strong> {row.requestedValue?.fromDate || row.date} to {row.requestedValue?.toDate || row.date}</p>
                    <p><strong>Total days:</strong> {row.requestedValue?.days || row.children?.length || 1}</p>
                    <p><strong>Leave type:</strong> {row.requestedValue?.leaveType || "Leave"}</p>
                  </>
                ) : (
                  <>
                    <p><strong>Date:</strong> {row.date}</p>
                    <p><strong>Requested:</strong> {row.requestedValue?.code || "-"}</p>
                  </>
                )}
                <p><strong>Reason:</strong> {row.reason || "-"}</p>
                <p><strong>Status:</strong> {row.status}</p>
                <p><strong>Requested by:</strong> {row.requestedBy || "-"}</p>
                {row.status === "pending" ? (
                  <div className="button-row">
                    <button className="primary-button" type="button" onClick={() => decideApproval(row, "approved")}>
                      {row.requestedValue?.kind === "leave_application" ? "Grant leave" : "Approve correction"}
                    </button>
                    <button className="secondary-button danger" type="button" onClick={() => decideApproval(row, "rejected")}>
                      Reject
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          />
        ) : detailKey === "payable" ? (
          <DetailList
            rows={filteredDetailRows}
            getTitle={(row) => row.employeeName}
            getMeta={(row) => `Net payable ${currency(row.netPayable)}`}
            getStatus={(row) => currency(row.grossSalary)}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.employeeName}</h3>
                <p><strong>Gross salary:</strong> {currency(row.grossSalary)}</p>
                <p><strong>Leave deduction:</strong> {currency(row.attendanceSummary.leaveDeductionAmount)}</p>
                <p><strong>Advance:</strong> {currency(row.advanceDeduction)}</p>
                <p><strong>Welfare:</strong> {currency(row.staffWelfare)}</p>
                <p><strong>Reimbursement:</strong> {currency(row.reimbursementAmount)}</p>
                <p><strong>Incentive:</strong> {currency(row.incentivePayable)}</p>
                <p><strong>Net payable:</strong> {currency(row.netPayable)}</p>
              </div>
            )}
          />
        ) : (
          <DetailList
            rows={filteredDetailRows}
            getTitle={(row) => row.employee.fullName}
            getMeta={(row) => `${row.summary.code} · In ${formatTime(row.punchIn?.timestamp)} · Out ${formatTime(row.punchOut?.timestamp)}`}
            getStatus={(row) => row.store?.code || ""}
            getPhotoPath={(row) => row.punchIn?.photoPath || row.punchOut?.photoPath}
            getRowClass={attendanceStatusClass}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.employee.fullName}</h3>
                <div className="proof-strip">
                  <AttendancePhotoThumb path={row.punchIn?.photoPath} name={row.employee.fullName} />
                  <span>
                    <strong>Punch-in proof</strong>
                    <small>{row.punchIn?.photoPath ? "Photo captured" : "No punch-in photo"}</small>
                  </span>
                </div>
                <p><strong>Store:</strong> {row.store?.name || "-"}</p>
                <p><strong>Status:</strong> {row.summary.status} ({row.summary.code})</p>
                <p><strong>Punch in:</strong> {formatTime(row.punchIn?.timestamp)}</p>
                <p><strong>Punch out:</strong> {formatTime(row.punchOut?.timestamp)}</p>
                <p><strong>Late:</strong> {row.summary.late ? "Yes" : "No"}</p>
                <p><strong>Early going:</strong> {row.summary.earlyDeparture ? "Yes" : "No"}</p>
                <p><strong>Distance:</strong> {row.punchIn?.distanceMeters == null ? "-" : `${Math.round(Number(row.punchIn.distanceMeters))} m`}</p>
                <p><strong>Geofence:</strong> {row.punchIn ? (row.punchIn.insideGeofence ? "Inside" : "Outside") : "-"}</p>
                <p><strong>Coordinates:</strong> <CoordinateLink latitude={row.punchIn?.latitude} longitude={row.punchIn?.longitude} /></p>
              </div>
            )}
          />
        )}
      </article>
      ) : null}
    </section>
  );
}

function BankRecoPanel({ state, updateState, user }) {
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    storeId: state.stores[0]?.id || "",
    recoDate: new Date().toISOString().slice(0, 10),
    cashInHand: "",
    bankCredit: "",
    posMachineSettlement: "",
    upiReceived: "",
    cardReceived: "",
    notes: "",
    status: "pending"
  });
  const canMarkDone = hasUserPermission(user, "can_mark_reconciliation_done");
  const saveReco = async (status = form.status) => {
    setMessage("");
    try {
      if (status === "done" && !canMarkDone) throw new Error("You do not have permission to mark reconciliation done.");
      const row = {
        id: uuid("bank-reco"),
        ...form,
        cashInHand: Number(form.cashInHand || 0),
        bankCredit: Number(form.bankCredit || 0),
        posMachineSettlement: Number(form.posMachineSettlement || 0),
        upiReceived: Number(form.upiReceived || 0),
        cardReceived: Number(form.cardReceived || 0),
        status,
        enteredBy: user.name || user.email,
        markedDoneBy: status === "done" ? user.name || user.email : "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const savedRow = await persistRequiredEntity("bank_reco", row, "Daily reconciliation");
      updateState((current) => ({ ...current, bankReco: upsertById(current.bankReco || [], savedRow) }));
      setMessage(`Daily reconciliation ${status}.`);
    } catch (error) {
      console.error("Daily reconciliation save failed", error);
      setMessage(error.message || "Daily reconciliation could not be saved.");
    }
  };
  return (
    <section>
      <Header title="Daily Reconciliation" subtitle="Cash, UPI, card/POS settlement and bank credit status by store." />
      <div className="panel-grid two">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>Daily reconciliation entry</h2>
          <div className="form-grid">
            <label>Store<select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>{state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <Input label="Reco date" type="date" value={form.recoDate} onChange={(recoDate) => setForm({ ...form, recoDate })} />
            <Input label="Cash in hand" type="number" value={form.cashInHand} onChange={(cashInHand) => setForm({ ...form, cashInHand })} />
            <Input label="Bank credit" type="number" value={form.bankCredit} onChange={(bankCredit) => setForm({ ...form, bankCredit })} />
            <Input label="POS machine settlement" type="number" value={form.posMachineSettlement} onChange={(posMachineSettlement) => setForm({ ...form, posMachineSettlement })} />
            <Input label="UPI received" type="number" value={form.upiReceived} onChange={(upiReceived) => setForm({ ...form, upiReceived })} />
            <Input label="Card received" type="number" value={form.cardReceived} onChange={(cardReceived) => setForm({ ...form, cardReceived })} />
            <label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="pending">pending</option><option value="done">done</option></select></label>
          </div>
          <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={() => saveReco("pending")}>Save pending</button>
            <button className="primary-button" type="button" disabled={!canMarkDone} onClick={() => saveReco("done")}>Mark done</button>
          </div>
          {message ? <p className="notice">{message}</p> : null}
        </form>
        <article className="panel dashboard-detail">
          <h2>Daily Reconciliation Pending / Done</h2>
          <DetailList
            rows={state.bankReco || []}
            getTitle={(row) => state.stores.find((store) => store.id === row.storeId)?.name || "Store"}
            getMeta={(row) => `${row.recoDate || "-"} · Cash ${currency(row.cashInHand || 0)} · POS ${currency(row.posMachineSettlement || 0)}`}
            getStatus={(row) => row.status}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{state.stores.find((store) => store.id === row.storeId)?.name || "Store"}</h3>
                <p><strong>Date:</strong> {row.recoDate || "-"}</p>
                <p><strong>Cash:</strong> {currency(row.cashInHand || 0)}</p>
                <p><strong>Bank credit:</strong> {currency(row.bankCredit || 0)}</p>
                <p><strong>POS settlement:</strong> {currency(row.posMachineSettlement || 0)}</p>
                <p><strong>UPI:</strong> {currency(row.upiReceived || 0)}</p>
                <p><strong>Card:</strong> {currency(row.cardReceived || 0)}</p>
                <p><strong>Status:</strong> {row.status}</p>
              </div>
            )}
          />
        </article>
      </div>
    </section>
  );
}

function CommunicationPanel({ state }) {
  return (
    <section>
      <Header title="Communication" subtitle="WhatsApp, SMS, customer messages, order-ready messages, reminders, review requests and internal communication." />
      <div className="module-tile-grid">
        {["WhatsApp messages", "SMS messages", "Order-ready messages", "Customer reminders", "Review requests", "Internal communication", "Birthday list", "Anniversary list"].map((label) => (
          <article className="module-tile-card" key={label}>
            <Target size={24} />
            <strong>{label}</strong>
            <small>Connected with Optical CRM customers and orders.</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function CnTrackingPanelV2({ state, updateState, user, focus, onFocusHandled }) {
  const employee = userEmployee(state, user);
  const autoStoreId = userPrimaryStoreId(state, user);
  const broadCnAccess = isBroadCnUser(user);
  const canProcess = broadCnAccess || isCnResponsible(state, user) || hasUserPermission(user, "can_process_cn");
  const canCreate = Boolean(broadCnAccess || autoStoreId) && (broadCnAccess || user.role === "employee" || hasUserPermission(user, "can_create_cn_request") || canProcess);
  const canImport = broadCnAccess || hasUserPermission(user, "can_override_cn_status");
  const canViewAll = broadCnAccess || hasUserPermission(user, "can_view_all_store_cn") || canProcess;
  const canExportCn = broadCnAccess || canProcess || hasUserPermission(user, "can_export_cn_reports") || hasUserPermission(user, "can_export_vendor_awaited_cn");
  const canInternalExport = broadCnAccess || hasUserPermission(user, "can_view_internal_cn_export");
  const canCorrectCnStatus = canAdminCorrectStatus(user, "can_override_cn_status");
  const defaultLayer = canProcess || broadCnAccess ? "work" : canCreate ? "create" : "all";
  const [layer, setLayer] = useState(defaultLayer);
  const [activeBucket, setActiveBucket] = useState("new");
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState({ query: "", storeId: "all", status: "all" });
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [createdSuccess, setCreatedSuccess] = useState(null);
  const [isSubmittingCn, setIsSubmittingCn] = useState(false);
  const [bucketDrawer, setBucketDrawer] = useState("");
  const [actionError, setActionError] = useState("");
  const [form, setForm] = useState({
    storeId: broadCnAccess ? (state.stores[0]?.id || autoStoreId) : autoStoreId,
    previousOrderNo: "",
    newOrderNo: "",
    productType: "Lens",
    productDetails: "",
    quantity: 1,
    reasonForReturn: "Coating Issue",
    otherReasonText: "",
    complaintNote: "",
    orderReferencePhoto: "",
    supportingPhoto: "",
    oldItemPhoto: ""
  });
  const [caseDraft, setCaseDraft] = useState({
    vendorName: "",
    productNameAsPerDc: "",
    vendorReferenceNo: "",
    companyFormNo: "",
    dispatchDate: "",
    pickupDate: "",
    pickedBy: "",
    sentThrough: "",
    cnNumber: "",
    cnDate: "",
    cnAmount: "",
    settlementAmount: "",
    differenceAmount: "",
    ledgerUpdated: false,
    remarks: "",
    closureRemarks: "",
    cnCopyPhoto: "",
    vendorFormPhoto: "",
    followUpNote: "",
    closureOutcome: "",
    extractionId: ""
  });
  const [importState, setImportState] = useState({ rows: [], sheets: [], duplicates: [], possibleDuplicates: [], needsReview: [], ready: [], statusCounts: {}, manualMappings: {}, includePossibleDuplicates: false, errorRows: [], batch: null, file: null, message: "" });
  const [cnCreditMode, setCnCreditMode] = useState("manual");
  const [cnExtraction, setCnExtraction] = useState({ status: "idle", message: "", fields: {}, lowConfidenceFields: [], confidenceScore: 0 });
  const { actionState, runAction } = useActionStatus();
  const [exportFilter, setExportFilter] = useState({
    vendor: "all",
    storeId: "all",
    managerId: "all",
    reportType: "awaited",
    includeRejected: false,
    format: "vendor",
    pickupFrom: "",
    pickupTo: "",
    createdFrom: "",
    createdTo: ""
  });

  const visibleCases = (state.cnRequests || [])
    .filter((request) => canViewCnCase(state, user, request))
    .map((request) => ({ ...request, displayStatus: normalizeCnStatus(request.status, request), bucketKey: cnBucketKey(request) }))
    .sort((a, b) => String(b.createdAt || b.createdDate || "").localeCompare(String(a.createdAt || a.createdDate || "")));

  const filteredCases = visibleCases
    .filter((request) => filter.storeId === "all" || request.storeId === filter.storeId)
    .filter((request) => filter.status === "all" || normalizeCnStatus(request.status, request) === filter.status)
    .filter((request) => {
      const query = filter.query.trim().toLowerCase();
      if (!query) return true;
      return `${request.cnTicketNumber || ""} ${request.previousOrderNo || ""} ${request.newOrderNo || ""} ${request.vendorName || ""} ${request.vendorReferenceNo || ""} ${request.vendorDocumentReference || ""} ${request.reasonForReturn || ""} ${request.productDetails || ""} ${request.importedStatusText || ""}`.toLowerCase().includes(query);
    });

  const selected = visibleCases.find((request) => request.id === selectedId) || null;
  const bucketCounts = Object.fromEntries(CN_WORK_BUCKETS.map((bucket) => [bucket.key, filteredCases.filter((request) => cnBucketMatches(request, bucket.key)).length]));
  const vendorOptions = [...new Set(visibleCases.map(cnVendorLabel).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const awaitedExportRows = cnAwaitedExportRows(visibleCases, state, {
    ...exportFilter,
    ageingOnly: exportFilter.reportType === "ageing30",
    includeRejected: Boolean(exportFilter.includeRejected)
  });
  const defaultAwaitedExportRows = cnAwaitedExportRows(visibleCases, state, { ageingOnly: false, includeRejected: false });

  useEffect(() => {
    if (!focus) return;
    setLayer("work");
    setActiveBucket("new");
    if (focus.requestId) setSelectedId(focus.requestId);
    onFocusHandled?.();
  }, [focus, onFocusHandled]);

  useEffect(() => {
    if (!selected) return;
    setCaseDraft({
      vendorName: selected.vendorName || "",
      productNameAsPerDc: selected.productNameAsPerDc || selected.productDetails || "",
      vendorReferenceNo: selected.vendorReferenceNo || "",
      companyFormNo: selected.companyFormNo || selected.vendorDocumentReference || "",
      dispatchDate: selected.dispatchDate || "",
      pickupDate: selected.pickupDate || selected.dispatchDate || "",
      pickedBy: selected.pickedBy || selected.collectedBy || "",
      sentThrough: selected.sentThrough || "",
      cnNumber: selected.cnNumber || "",
      cnDate: selected.cnDate || selected.cnReceivedDate || "",
      cnAmount: selected.cnAmount || "",
      settlementAmount: selected.settlementAmount || "",
      differenceAmount: selected.differenceAmount || "",
      ledgerUpdated: Boolean(selected.ledgerUpdated),
      remarks: selected.remarks || "",
      closureRemarks: "",
      cnCopyPhoto: selected.cnCopyPhoto || "",
      vendorFormPhoto: selected.vendorFormPhoto || "",
      followUpNote: "",
      closureOutcome: selected.closureOutcome || cnFinalOutcome(selected),
      extractionId: ""
    });
    setCnCreditMode("manual");
    setCnExtraction({ status: "idle", message: "", fields: {}, lowConfidenceFields: [], confidenceScore: 0 });
  }, [selected?.id]);

  const updateRequest = async (request, patch, note = "") => {
    if (!request?.id) throw new Error("No CN case selected.");
    const oldStatus = normalizeCnStatus(request.status, request);
    const newStatus = patch.status ? normalizeCnStatus(patch.status, { ...request, ...patch }) : oldStatus;
    const event = note ? {
      id: uuid("cn-note"),
      at: new Date().toISOString(),
      by: user.name || user.email,
      note,
      oldStatus,
      newStatus,
      reason: patch.statusCorrectionReason || "",
      isCorrection: Boolean(patch.statusCorrectionReason)
    } : null;
    const updated = {
      ...request,
      ...patch,
      status: newStatus,
      cnActivity: event ? [event, ...(request.cnActivity || [])] : (request.cnActivity || []),
      updatedAt: new Date().toISOString()
    };
    const savedRequest = await persistRequiredEntity("cn_requests", updated, "CN case");
    updateState((current) => ({
      ...current,
      cnRequests: (current.cnRequests || []).map((item) => (item.id === savedRequest.id ? savedRequest : item))
    }));
    setSelectedId(savedRequest.id);
    setMessage("CN case updated.");
    if (oldStatus !== newStatus || patch.vendorName || patch.pickupDate || patch.cnNumber || patch.statusCorrectionReason) {
      await notifySuperAdmins(state, updateState, {
        module: "CN Tracking",
        title: oldStatus !== newStatus ? "CN Status Changed" : "CN Updated",
        message: `CN ${savedRequest.cnTicketNumber || savedRequest.cnTicketNo || savedRequest.id} ${oldStatus !== newStatus ? `moved from ${oldStatus} to ${newStatus}` : "was updated"}.`,
        priority: ["Rejected", "Needs Review", "Not Picked Up"].includes(newStatus) || patch.statusCorrectionReason ? "high" : "normal",
        entityType: "cn_request",
        entityId: savedRequest.id,
        storeId: savedRequest.storeId,
        createdBy: user.id
      });
    }
    return savedRequest;
  };

  const runCnAction = (key, labels, action) =>
    runAction(
      key,
      labels,
      async (setStep) => {
        setActionError("");
        setMessage(labels.loading || "Updating...");
        const result = await action(setStep);
        setMessage(labels.successMessage || labels.success || "Updated.");
        return result;
      },
      (error) => {
        const detail = error?.message || labels.errorMessage || labels.error || "Action failed. Please try again.";
        setActionError(detail);
        setMessage(detail);
      }
    );

  const notifyUsers = async (notifications) => {
    await Promise.all(notifications.map((notification) => persistOptionalEntity("notifications", notification).catch(() => null)));
    updateState((current) => ({ ...current, notifications: [...notifications, ...(current.notifications || [])] }));
  };

  const handleFile = async (field, file, setter = setForm, targetRequest = selected) => {
    if (!file) return;
    try {
      setMessage("Compressing and uploading photo...");
      const fileTypes = {
        orderReferencePhoto: "order_reference_photo",
        supportingPhoto: "supporting_photo",
        oldItemPhoto: "old_lens_or_chashma",
        vendorFormPhoto: "vendor_return_form",
        cnCopyPhoto: "credit_note_copy"
      };
      const upload = await uploadAppImage({
        file,
        module: "cn",
        recordType: "cn_request",
        recordId: targetRequest?.id || form.cnTicketNumber || "draft-cn",
        storeId: targetRequest?.storeId || form.storeId || autoStoreId,
        staffId: employee?.id || user.employeeId,
        fileType: fileTypes[field] || "other",
        uploadedBy: user.id
      });
      setter((current) => ({ ...current, [field]: upload.storagePath || upload.path || upload.url }));
      setMessage("Photo uploaded.");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const scanCnPhoto = async (file) => {
    if (!selected || !file) return;
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_received"))) {
      setMessage("You do not have permission to update CN received details.");
      return;
    }
    try {
      setCnCreditMode("scan");
      setCnExtraction({ status: "pending", message: "Uploading CN photo and trying to read it...", fields: {}, lowConfidenceFields: [], confidenceScore: 0 });
      setMessage("Uploading CN photo...");
      const upload = await uploadAppImage({
        file,
        module: "cn",
        recordType: "cn_request",
        recordId: selected.id,
        storeId: selected.storeId,
        staffId: employee?.id || user.employeeId,
        fileType: "credit_note_copy",
        uploadedBy: user.id
      });
      const imageText = await tryReadTextFromImage(file);
      const extraction = buildCnExtractionResult({ imageText: imageText.text, fileName: file.name, request: selected });
      const filePath = upload.storagePath || upload.path || upload.url;
      const extractionRecord = {
        id: uuid("cn-extraction"),
        cnCaseId: selected.id,
        fileId: upload.id,
        documentType: "credit_note",
        extractedText: imageText.text,
        extractedFieldsJson: extraction.fields,
        confidenceScore: Math.max(extraction.confidenceScore, imageText.confidence || 0),
        extractionStatus: imageText.text ? extraction.status : "failed",
        extractedAt: new Date().toISOString(),
        finalCnNumber: "",
        finalCnDate: "",
        finalCnAmount: 0,
        finalVendorReference: "",
        remarks: imageText.message
      };
      await persistOptionalEntity("cn_document_extractions", extractionRecord);
      updateState((current) => ({
        ...current,
        cnDocumentExtractions: [extractionRecord, ...(current.cnDocumentExtractions || [])]
      }));
      setCaseDraft((current) => ({
        ...current,
        vendorName: current.vendorName || extraction.fields.vendorName || selected.vendorName || "",
        cnNumber: current.cnNumber || extraction.fields.cnNumber || "",
        cnDate: current.cnDate || extraction.fields.cnDate || "",
        cnAmount: current.cnAmount || extraction.fields.cnAmount || extraction.fields.totalAmount || "",
        settlementAmount: current.settlementAmount || extraction.fields.totalAmount || extraction.fields.cnAmount || "",
        vendorReferenceNo: current.vendorReferenceNo || extraction.fields.vendorReferenceNo || selected.vendorReferenceNo || "",
        remarks: current.remarks || extraction.fields.remarks || imageText.message,
        cnCopyPhoto: filePath,
        extractionId: extractionRecord.id
      }));
      setCnExtraction({
        ...extraction,
        status: extractionRecord.extractionStatus,
        message: imageText.text ? "CN photo read. Please verify every field before confirming." : imageText.message,
        filePath,
        fileId: upload.id,
        extractionId: extractionRecord.id
      });
      setMessage(imageText.text ? "CN photo read. Check and confirm the fields." : "CN photo saved. OCR was not available, enter details manually.");
    } catch (error) {
      setCnExtraction({ status: "failed", message: error.message, fields: {}, lowConfidenceFields: [], confidenceScore: 0 });
      setMessage(error.message);
    }
  };

  const submitCn = async () => {
    if (isSubmittingCn) return;
    setMessage("");
    if (!canCreate) return setMessage("You do not have permission to create CN request.");
    if (!form.previousOrderNo.trim() && !form.newOrderNo.trim()) return setMessage("Add previous or new order number.");
    const requestStoreId = broadCnAccess ? form.storeId : autoStoreId;
    if (!requestStoreId) return setMessage("Store is required.");
    setIsSubmittingCn(true);
    setMessage("Saving CN request...");
    try {
      const now = new Date().toISOString();
      const managers = cnManagerUserIds(state);
      const request = {
        id: uuid("cn"),
        ...nextCnNumbers(state),
        ...form,
        storeId: requestStoreId,
        createdBy: user.id,
        createdByName: employee?.fullName || user.name || user.email,
        createdDate: now.slice(0, 10),
        createdAt: now,
        cnResponsibleId: managers[0] || "",
        cnResponsibleIds: managers,
        status: "New Request",
        quantity: Number(form.quantity || 1),
        packetChecklist: { oldItemReceived: false, orderFormReceived: false, productMatches: false, packetComplete: false },
        ledgerUpdated: false,
        source: "app"
      };
      const savedRequest = await persistRequiredEntity("cn_requests", request, "CN request");
      const notifications = managers.map((userId) => createAppNotification({
        userId,
        module: "CN Tracking",
        title: "New CN Request",
        message: `New CN Request from ${storeShortName(state, requestStoreId)} - ${request.cnTicketNumber}`,
        linkedRecordType: "cn_request",
        linkedRecordId: savedRequest.id
      }));
      await notifyUsers(notifications);
      await notifySuperAdmins(state, updateState, {
        module: "CN Tracking",
        title: "New CN Created",
        message: `CN ${savedRequest.cnTicketNumber || request.cnTicketNumber} was created for ${storeShortName(state, requestStoreId)}.`,
        priority: "normal",
        entityType: "cn_request",
        entityId: savedRequest.id,
        storeId: savedRequest.storeId || requestStoreId,
        createdBy: user.id
      });
      updateState((current) => ({ ...current, cnRequests: [savedRequest, ...(current.cnRequests || [])] }));
      setLastSubmitted(savedRequest);
      setCreatedSuccess(savedRequest);
      setSelectedId(savedRequest.id);
      setLayer(canProcess ? "work" : "all");
      setActiveBucket("new");
      setMessage(`${savedRequest.cnTicketNumber} created. Print CN Packet Slip and send the packet.`);
      setForm((current) => ({
        ...current,
        previousOrderNo: "",
        newOrderNo: "",
        productType: "Lens",
        productDetails: "",
        quantity: 1,
        reasonForReturn: "Coating Issue",
        otherReasonText: "",
        complaintNote: "",
        orderReferencePhoto: "",
        supportingPhoto: "",
        oldItemPhoto: ""
      }));
    } catch (error) {
      const message = error?.message || "CN request could not be saved.";
      setMessage(`CN request could not be saved online: ${message}. Run the latest CN SQL once in Supabase, refresh, and try again.`);
    } finally {
      setIsSubmittingCn(false);
    }
  };

  const printPacketSlip = async (request) => {
    printCnPacketSlip(request, state);
    if (normalizeCnStatus(request.status, request) === "New Request") {
      await updateRequest(request, { status: "New Request", packetPrintedAt: new Date().toISOString() }, "CN Packet Slip printed");
    }
  };

  const assignVendor = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_vendor_details"))) throw new Error("You do not have permission to assign vendor.");
    if (!caseDraft.vendorName.trim() || caseDraft.vendorName === "__OTHER__") throw new Error("Vendor / company is required.");
    const updated = await updateRequest(selected, {
      vendorName: caseDraft.vendorName,
      productNameAsPerDc: caseDraft.productNameAsPerDc,
      vendorReferenceNo: caseDraft.vendorReferenceNo,
      companyFormNo: caseDraft.companyFormNo,
      remarks: caseDraft.remarks,
      vendorAssignedAt: new Date().toISOString(),
      status: "Pickup Awaited"
    }, "Vendor assigned");
    setActiveBucket("pickup");
    setBucketDrawer("pickup");
    return updated;
  };

  const markPickedUp = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_vendor_details"))) throw new Error("You do not have permission to mark vendor pickup.");
    const updated = await updateRequest(selected, {
      pickupDate: caseDraft.pickupDate || new Date().toISOString().slice(0, 10),
      dispatchDate: caseDraft.pickupDate || caseDraft.dispatchDate || new Date().toISOString().slice(0, 10),
      pickedBy: caseDraft.pickedBy,
      collectedBy: caseDraft.pickedBy,
      sentThrough: caseDraft.sentThrough,
      vendorReferenceNo: caseDraft.vendorReferenceNo,
      companyFormNo: caseDraft.companyFormNo,
      remarks: caseDraft.remarks,
      status: "CN Awaited"
    }, "Vendor picked up packet");
    setActiveBucket("awaited");
    setBucketDrawer("awaited");
    return updated;
  };

  const saveCnCredit = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_received"))) throw new Error("You do not have permission to update CN received details.");
    const cnAmount = Number(caseDraft.cnAmount || 0);
    if (!caseDraft.cnNumber || !caseDraft.cnDate || !cnAmount) throw new Error("CN number, CN date and CN amount are required.");
    const updated = await updateRequest(selected, {
      cnNumber: caseDraft.cnNumber,
      cnDate: caseDraft.cnDate,
      cnReceivedDate: caseDraft.cnDate,
      cnAmount,
      settlementAmount: Number(caseDraft.settlementAmount || cnAmount),
      differenceAmount: Number(caseDraft.differenceAmount || 0),
      cnCopyPhoto: caseDraft.cnCopyPhoto,
      ledgerUpdated: Boolean(caseDraft.ledgerUpdated),
      remarks: caseDraft.remarks,
      status: "CN Credited"
    }, "CN credit entered");
    if (caseDraft.extractionId) {
      const extractionRecord = {
        id: caseDraft.extractionId,
        cnCaseId: selected.id,
        fileId: cnExtraction.fileId || "",
        documentType: "credit_note",
        extractedText: cnExtraction.extractedText || "",
        extractedFieldsJson: cnExtraction.fields || {},
        confidenceScore: cnExtraction.confidenceScore || 0,
        extractionStatus: "verified",
        extractedAt: cnExtraction.extractedAt || new Date().toISOString(),
        verifiedBy: user.id,
        verifiedAt: new Date().toISOString(),
        finalCnNumber: caseDraft.cnNumber,
        finalCnDate: caseDraft.cnDate,
        finalCnAmount: cnAmount,
        finalVendorReference: caseDraft.vendorReferenceNo || updated.vendorReferenceNo || "",
        remarks: caseDraft.remarks
      };
      await persistOptionalEntity("cn_document_extractions", extractionRecord);
      updateState((current) => ({
        ...current,
        cnDocumentExtractions: upsertById(current.cnDocumentExtractions || [], extractionRecord)
      }));
    }
    setActiveBucket("credited");
    setBucketDrawer("credited");
    return updated;
  };

  const saveCnCreditDraft = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_received"))) throw new Error("You do not have permission to update CN received details.");
    return updateRequest(selected, {
      draftCnNumber: caseDraft.cnNumber,
      draftCnDate: caseDraft.cnDate,
      draftCnAmount: caseDraft.cnAmount,
      draftSettlementAmount: caseDraft.settlementAmount,
      draftDifferenceAmount: caseDraft.differenceAmount,
      cnCopyPhoto: caseDraft.cnCopyPhoto,
      remarks: caseDraft.remarks,
      status: normalizeCnStatus(selected.status, selected)
    }, "CN credit details saved");
  };

  const markProblem = async (status = "Needs Review") => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_verify_cn_packet"))) throw new Error("You do not have permission to update this CN case.");
    const updated = await updateRequest(selected, {
      status,
      missingItemReason: caseDraft.remarks || selected.missingItemReason || "",
      remarks: caseDraft.remarks
    }, status === "Rejected" ? "CN rejected" : "CN needs review");
    setActiveBucket(status === "Rejected" ? "rejected" : "review");
    setBucketDrawer(status === "Rejected" ? "rejected" : "review");
    return updated;
  };

  const addFollowUp = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!caseDraft.followUpNote.trim()) throw new Error("Add note text first.");
    const updated = await updateRequest(selected, {
      lastFollowUpNote: caseDraft.followUpNote,
      lastFollowUpAt: new Date().toISOString()
    }, caseDraft.followUpNote);
    setCaseDraft((current) => ({ ...current, followUpNote: "" }));
    return updated;
  };

  const reapplyRejected = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_process_cn"))) throw new Error("You do not have permission to reapply CN.");
    const updated = await updateRequest(selected, { status: "Reapplied", reappliedAt: new Date().toISOString() }, "Rejected CN reapplied; workflow restarted from vendor pickup");
    setActiveBucket("pickup");
    setBucketDrawer("pickup");
    return updated;
  };

  const updateLedgerOnly = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_ledger"))) throw new Error("You do not have permission to update CN ledger.");
    const entry = {
      id: `ledger-${selected.id}`,
      storeId: selected.storeId,
      vendorName: selected.vendorName || caseDraft.vendorName || "",
      entryType: "credit_note",
      amount: Number(selected.cnAmount || caseDraft.cnAmount || 0),
      linkedRecordType: "cn_request",
      linkedRecordId: selected.id,
      notes: `${selected.cnTicketNumber} ${selected.cnNumber || caseDraft.cnNumber || ""}`,
      createdAt: new Date().toISOString(),
      createdBy: user.id
    };
    console.log("[CN] saving ledger", entry);
    await persistOptionalEntity("company_ledger_entries", entry);
    const updated = await updateRequest(selected, { ledgerUpdated: true, status: "CN Credited" }, "Ledger updated");
    updateState((current) => ({ ...current, companyLedgerEntries: upsertById(current.companyLedgerEntries || [], entry) }));
    return updated;
  };

  const correctCnStatus = async (newStatus, label) => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!canCorrectCnStatus) throw new Error("Only Admin / Super Admin can correct CN status.");
    const oldStatus = normalizeCnStatus(selected.status, selected);
    const reason = window.prompt(`Change Status?\n\nYou are moving this CN from ${oldStatus} to ${newStatus}. Please enter the reason for correction.`);
    if (!reason || !reason.trim()) throw new Error("Reason is required before changing status.");
    const correction = statusCorrectionRecord({
      oldStatus,
      newStatus,
      reason: reason.trim(),
      user,
      module: "cn"
    });
    const updated = await updateRequest(
      selected,
      {
        status: newStatus,
        statusCorrectedAt: correction.changedAt,
        statusCorrectedBy: correction.changedByName,
        statusCorrectionReason: correction.reason,
        statusCorrections: [correction, ...(selected.statusCorrections || [])]
      },
      `Status changed from ${oldStatus} to ${newStatus}. Reason: ${correction.reason}`
    );
    await persistOptionalEntity("status_change_logs", {
      id: correction.id,
      module: "cn",
      recordType: "cn_request",
      recordId: selected.id,
      oldStatus: correction.oldStatus,
      newStatus: correction.newStatus,
      changedBy: correction.changedBy,
      changedByName: correction.changedByName,
      reason: correction.reason,
      createdAt: correction.changedAt
    }).catch(() => null);
    setActiveBucket(cnBucketKey(updated));
    setBucketDrawer("");
    setMessage(label || `Moved to ${newStatus}.`);
    return updated;
  };

  const closeCase = async () => {
    if (!selected) throw new Error("Select a CN case first.");
    if (!(canProcess || broadCnAccess || hasUserPermission(user, "can_close_cn"))) throw new Error("You do not have permission to close CN.");
    const updated = await updateRequest(selected, {
      status: "Closed",
      closedAt: new Date().toISOString(),
      closedBy: user.id,
      closedByName: user.name || user.email,
      closureRemarks: caseDraft.closureRemarks,
      closureOutcome: caseDraft.closureOutcome || cnFinalOutcome(selected, caseDraft)
    }, caseDraft.closureRemarks || "CN case closed");
    setActiveBucket("closed");
    setBucketDrawer("closed");
    return updated;
  };

  const parseImportFile = async (file) => {
    if (!file) return;
    setImportState({ rows: [], sheets: [], duplicates: [], possibleDuplicates: [], needsReview: [], ready: [], statusCounts: {}, manualMappings: {}, includePossibleDuplicates: false, errorRows: [], batch: null, file, message: "Reading Excel file..." });
    try {
      const preview = await parseCnExcelWorkbook(file, state, user);
      setImportState(preview);
    } catch (error) {
      setImportState((current) => ({ ...current, message: error.message }));
    }
  };

  const applyImportMapping = async (sheetName, mapping) => {
    if (!importState.file) return;
    const manualMappings = { ...(importState.manualMappings || {}), [sheetName]: mapping };
    setImportState((current) => ({ ...current, manualMappings, message: "Rechecking Excel with your mapping..." }));
    try {
      const preview = await parseCnExcelWorkbook(importState.file, state, user, {
        manualMappings,
        includePossibleDuplicates: importState.includePossibleDuplicates
      });
      setImportState(preview);
    } catch (error) {
      setImportState((current) => ({ ...current, message: error.message }));
    }
  };

  const togglePossibleDuplicates = async (checked) => {
    if (!importState.file) return;
    setImportState((current) => ({ ...current, includePossibleDuplicates: checked, message: checked ? "Including possible duplicates in preview..." : "Excluding possible duplicates from preview..." }));
    try {
      const preview = await parseCnExcelWorkbook(importState.file, state, user, {
        manualMappings: importState.manualMappings || {},
        includePossibleDuplicates: checked
      });
      setImportState(preview);
    } catch (error) {
      setImportState((current) => ({ ...current, message: error.message }));
    }
  };

  const downloadImportErrorReport = () => {
    const rows = importState.errorRows || [];
    if (!rows.length) return setMessage("No import warning/error rows to download.");
    downloadSheet(`${filePrefix(state.settings)}-cn-import-error-report.csv`, rows, [
      ["Sheet", (row) => row.sheetName],
      ["Row", (row) => row.rowNumber],
      ["Reason", (row) => row.reason],
      ["Missing fields", (row) => row.missingFields],
      ["Suggested fix", (row) => row.suggestedFix]
    ]);
  };

  const syncImportedCnRows = async (records, syncingBatch) => {
    const failedRows = [];
    let syncedRows = 0;
    const chunkSize = 25;

    const pushFailure = (record, error, suggestedFix = "Check CN SQL migration/RLS permissions, then retry import.") => {
      failedRows.push({
        sheetName: record?.importedSheetName || "-",
        rowNumber: record?.importedRowNumber || "-",
        cnTicketNumber: record?.cnTicketNumber || record?.id || "-",
        reason: error?.message || String(error || "Could not save this CN row"),
        missingFields: "",
        suggestedFix
      });
    };

    try {
      await persistOptionalEntity("import_batches", syncingBatch);
    } catch (error) {
      pushFailure({ id: syncingBatch.id, importedSheetName: "Import batch" }, error, "CN rows are visible in the app. Import batch summary could not be saved.");
    }

    for (let index = 0; index < records.length; index += chunkSize) {
      const chunk = records.slice(index, index + chunkSize);
      const results = await Promise.all(chunk.map(async (record) => {
        try {
          await persistOptionalEntity("cn_requests", record);
          return { ok: true, record };
        } catch (error) {
          return { ok: false, record, error };
        }
      }));

      results.forEach((result) => {
        if (result.ok) {
          syncedRows += 1;
        } else {
          pushFailure(result.record, result.error);
        }
      });

      const completedRows = Math.min(index + chunk.length, records.length);
      setImportState((current) => current.batch?.id === syncingBatch.id ? {
        ...current,
        message: `${records.length} CN row(s) added to the app. Syncing to Supabase: ${completedRows}/${records.length} done.`
      } : current);
      await new Promise((resolve) => setTimeout(resolve, 20));
    }

    const finalBatch = {
      ...syncingBatch,
      status: failedRows.length && !syncedRows ? "failed" : failedRows.length ? "sync_failed" : "imported",
      importedAt: new Date().toISOString(),
      importedRows: syncedRows,
      failedRows: failedRows.length,
      statusCounts: CN_STATUSES.reduce((counts, status) => {
        counts[status] = records.filter((record) => normalizeCnStatus(record.status, record) === status).length;
        return counts;
      }, {})
    };

    try {
      await persistOptionalEntity("import_batches", finalBatch);
    } catch (error) {
      pushFailure({ id: finalBatch.id, importedSheetName: "Import batch" }, error, "CN rows may still be saved. Import batch summary could not be updated.");
    }

    updateState((current) => ({
      ...current,
      importBatches: upsertById(current.importBatches || [], finalBatch),
      settings: {
        ...current.settings,
        cnLastSyncedAt: finalBatch.importedAt,
        cnLastImportBatchId: finalBatch.id
      }
    }));

    setImportState((current) => current.batch?.id === syncingBatch.id ? {
      ...current,
      batch: finalBatch,
      errorRows: [...failedRows, ...(current.errorRows || [])],
      message: failedRows.length
        ? `${syncedRows}/${records.length} CN row(s) synced to Supabase. ${failedRows.length} row(s) need checking; download the error report.`
        : `CN Import Completed. ${records.length} row(s) imported and synced successfully.`
    } : current);
  };

  const confirmImport = async () => {
    if (!importState.batch || !canImport) {
      setImportState((current) => ({ ...current, message: "You do not have permission to import CN data." }));
      return;
    }
    if (!importState.ready.length) {
      setImportState((current) => ({ ...current, message: "No rows are ready to import. If all rows are possible duplicates, tick 'Import possible duplicates also' and preview again." }));
      return;
    }
    const syncingBatch = { ...importState.batch, status: "syncing", startedAt: new Date().toISOString() };
    const records = importState.ready.map((row) => ({
      ...row.record,
      importBatchId: syncingBatch.id
    }));
    updateState((current) => {
      const existingIds = new Set((current.cnRequests || []).map((request) => request.id));
      const newRecords = records.filter((record) => !existingIds.has(record.id));
      return {
        ...current,
        cnRequests: [...newRecords, ...(current.cnRequests || [])],
        importBatches: upsertById(current.importBatches || [], syncingBatch)
      };
    });
    const previewStatusCounts = { ...(importState.statusCounts || {}) };
    setImportState((current) => ({
      ...current,
      batch: syncingBatch,
      statusCounts: previewStatusCounts,
      message: `${records.length} CN row(s) added to the app. Syncing to Supabase in the background...`
    }));
    syncImportedCnRows(records, syncingBatch);
  };

  const resetImportedCnData = async () => {
    if (!canImport) return setMessage("Only Director/CEO/Admin can reset imported CN data.");
    const importedCases = (state.cnRequests || []).filter((request) => (
      request.source === "excel_import" ||
      request.importBatchId ||
      request.importedAt ||
      request.importedSheetName ||
      request.importedRowNumber ||
      String(request.cnTicketNumber || "").startsWith("CN-IMP-")
    ));
    if (!importedCases.length) {
      setImportState((current) => ({ ...current, message: "No imported CN rows found to reset." }));
      return;
    }
    downloadSheet(`${filePrefix(state.settings)}-cn-import-backup-before-reset.csv`, importedCases, cnColumns);
    const ok = window.confirm(`Backup downloaded. Delete ${importedCases.length} old imported/test CN rows and import batch records? Manual app-created CN cases will not be deleted.`);
    if (!ok) return;
    await Promise.all(importedCases.map((request) => deleteEntity("cn_requests", request.id).catch(() => null)));
    const importBatches = (state.importBatches || []);
    await Promise.all(importBatches.map((batch) => deleteEntity("import_batches", batch.id).catch(() => null)));
    const importedIds = new Set(importedCases.map((request) => request.id));
    updateState((current) => ({
      ...current,
      cnRequests: (current.cnRequests || []).filter((request) => !importedIds.has(request.id)),
      importBatches: [],
      settings: {
        ...current.settings,
        cnLastSyncedAt: new Date().toISOString(),
        cnLastImportBatchId: "",
        cnResetCompletedAt: new Date().toISOString()
      }
    }));
    setSelectedId("");
    setBucketDrawer("");
    setImportState({ rows: [], sheets: [], duplicates: [], possibleDuplicates: [], needsReview: [], ready: [], statusCounts: {}, manualMappings: {}, includePossibleDuplicates: false, errorRows: [], batch: null, file: null, message: "CN Data Reset Completed. Old imported CN data has been cleared. You can now start entering CN cases fresh from today." });
    setMessage("CN Data Reset Completed. Old imported CN data has been cleared.");
  };

  const cnColumns = [
    ["CN Ticket", (row) => row.cnTicketNumber],
    ["Store", (row) => storeShortName(state, row.storeId)],
    ["Vendor", (row) => row.vendorName || "-"],
    ["Status", (row) => normalizeCnStatus(row.status, row)],
    ["Current status", (row) => cnCurrentStatusText(row)],
    ["Created date", (row) => row.createdDate || ""],
    ["Vendor assigned", (row) => row.vendorAssignedAt || ""],
    ["Sent / pickup date", (row) => row.pickupDate || row.dispatchDate || ""],
    ["CN date", (row) => row.cnDate || row.cnReceivedDate || ""],
    ["Prev Order", (row) => row.previousOrderNo || "-"],
    ["New Order", (row) => row.newOrderNo || "-"],
    ["Reason", (row) => row.reasonForReturn || "-"],
    ["CN No", (row) => row.cnNumber || "-"],
    ["CN Amount", (row) => row.cnAmount || ""],
    ["Final result", (row) => normalizeCnStatus(row.status, row) === "Closed" ? cnFinalOutcome(row) : ""],
    ["Remarks", (row) => row.remarks || row.importedStatusText || ""]
  ];

  const downloadAwaitedVendorExcel = async (rows = awaitedExportRows, vendor = exportFilter.vendor, internal = exportFilter.format === "internal") => {
    if (!canExportCn) return setMessage("You do not have permission to export CN reports.");
    if (!rows.length) return setMessage("No awaited CN cases found for this export.");
    await downloadVendorAwaitedCnExcel({ rows, state, user, vendor, internal });
    setMessage("Vendor-wise awaited CN Excel downloaded.");
  };

  const downloadAwaitedVendorPdf = async () => {
    if (!canExportCn) return setMessage("You do not have permission to export CN reports.");
    if (exportFilter.vendor === "all") return setMessage("Select one vendor for PDF summary.");
    if (!awaitedExportRows.length) return setMessage("No awaited CN cases found for this vendor.");
    await downloadVendorAwaitedCnPdf({ rows: awaitedExportRows, state, user, vendor: exportFilter.vendor, internal: exportFilter.format === "internal" });
    setMessage("Vendor PDF summary downloaded.");
  };

  const downloadAwaitedVendorCsv = () => {
    if (!canExportCn) return setMessage("You do not have permission to export CN reports.");
    if (!awaitedExportRows.length) return setMessage("No awaited CN cases found for this export.");
    downloadSheet(`${filePrefix(state.settings)}-awaited-cn.csv`, awaitedExportRows, vendorShareCnColumns(exportFilter.format === "internal"));
    setMessage("Awaited CN CSV downloaded.");
  };

  const renderCaseList = (rows, compact = false) => (
    <div className={compact ? "tap-list compact" : "tap-list"}>
      {rows.length ? rows.map((row) => (
        <CnCaseCard key={row.id} request={row} state={state} selected={selectedId === row.id} onOpen={() => setSelectedId(row.id)} />
      )) : <div className="empty-state">No CN cases in this bucket.</div>}
    </div>
  );

  const drawerBucket = CN_WORK_BUCKETS.find((bucket) => bucket.key === bucketDrawer);
  const drawerCases = drawerBucket ? filteredCases.filter((request) => cnBucketMatches(request, drawerBucket.key)) : [];
  const bucketModal = drawerBucket ? (
    <div className="modal-backdrop cn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setBucketDrawer("")}>
      <section className="modal cn-modal cn-list-drawer" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>{drawerBucket.label}</h2>
            <p>{drawerCases.length} cases · search and store filters above are applied</p>
          </div>
          <button className="icon-only" type="button" onClick={() => setBucketDrawer("")} aria-label="Close CN list">
            <XCircle size={22} />
          </button>
        </div>
        {drawerBucket.key === "ageing30" ? <CnAgeingHint /> : null}
        {canExportCn && ["awaited", "ageing30"].includes(drawerBucket.key) ? (
          <div className="button-row cn-drawer-actions">
            <button className="secondary-button" type="button" onClick={() => downloadAwaitedVendorExcel(cnAwaitedExportRows(drawerCases, state, { ageingOnly: drawerBucket.key === "ageing30" }), "all", false)}>
              <FileSpreadsheet size={16} /> Download This List
            </button>
            <button className="secondary-button" type="button" onClick={() => downloadAwaitedVendorExcel(cnAwaitedExportRows(drawerCases, state, { ageingOnly: drawerBucket.key === "ageing30" }), "all", true)}>
              Internal Excel
            </button>
          </div>
        ) : null}
        {renderCaseList(drawerCases)}
      </section>
    </div>
  ) : null;

  const successModal = createdSuccess ? (
    <div className="modal-backdrop cn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setCreatedSuccess(null)}>
      <section className="modal cn-modal cn-success-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>CN Created Successfully</h2>
            <p>CN Ticket No: {createdSuccess.cnTicketNumber}</p>
          </div>
          <button className="icon-only" type="button" onClick={() => setCreatedSuccess(null)} aria-label="Close success screen">
            <XCircle size={22} />
          </button>
        </div>
        <div className="cn-status-banner cn-tone-new">
          <strong>{createdSuccess.cnTicketNumber}</strong>
          <small>Print the CN Packet Slip and send it with the physical packet.</small>
        </div>
        <div className="button-row cn-success-actions">
          <button className="primary-button" type="button" onClick={() => printPacketSlip(createdSuccess)}>
            <Printer size={16} /> Print CN Slip
          </button>
          <button className="secondary-button" type="button" onClick={() => { setSelectedId(createdSuccess.id); setCreatedSuccess(null); }}>
            View CN
          </button>
          <button className="secondary-button" type="button" onClick={() => { setLayer("create"); setCreatedSuccess(null); }}>
            Create Another CN
          </button>
          <button className="secondary-button" type="button" onClick={() => { setLayer("work"); setCreatedSuccess(null); }}>
            Back to CN Dashboard
          </button>
        </div>
      </section>
    </div>
  ) : null;

  const detailModal = selected ? (
    <div className="modal-backdrop cn-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setSelectedId("")}>
      <section className="modal cn-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>CN Case</h2>
            <p>{selected.cnTicketNumber} · {storeShortName(state, selected.storeId)}</p>
          </div>
          <button className="icon-only" type="button" onClick={() => setSelectedId("")} aria-label="Close CN case">
            <XCircle size={22} />
          </button>
        </div>
        <CnCaseDetailsV2
          state={state}
          request={selected}
          user={user}
          canProcess={canProcess}
          caseDraft={caseDraft}
          setCaseDraft={setCaseDraft}
          handleFile={handleFile}
          printPacketSlip={printPacketSlip}
          assignVendor={assignVendor}
          markPickedUp={markPickedUp}
          saveCnCredit={saveCnCredit}
          saveCnCreditDraft={saveCnCreditDraft}
          scanCnPhoto={scanCnPhoto}
          cnCreditMode={cnCreditMode}
          setCnCreditMode={setCnCreditMode}
          cnExtraction={cnExtraction}
          actionError={actionError}
          actionState={actionState}
          runCnAction={runCnAction}
          canCorrectCnStatus={canCorrectCnStatus}
          correctCnStatus={correctCnStatus}
          markProblem={markProblem}
          addFollowUp={addFollowUp}
          reapplyRejected={reapplyRejected}
          updateLedgerOnly={updateLedgerOnly}
          closeCase={closeCase}
        />
      </section>
    </div>
  ) : null;

  return (
    <section>
      <Header title="CN Tracking" subtitle="Simple CN Manager work queue, old CN import, vendor follow-up and closure." />
      {message ? <p className="notice">{message}</p> : null}
      <div className="layer-tabs" role="tablist" aria-label="CN Tracking sections">
        <button className={layer === "work" ? "active" : ""} type="button" onClick={() => setLayer("work")}>CN Dashboard</button>
        {canCreate ? <button className={layer === "create" ? "active" : ""} type="button" onClick={() => setLayer("create")}>Create CN</button> : null}
        <button className={layer === "all" ? "active" : ""} type="button" onClick={() => setLayer("all")}>All CN Cases</button>
        {canImport ? <button className={layer === "import" ? "active" : ""} type="button" onClick={() => setLayer("import")}>Import Old CN Data</button> : null}
        <button className={layer === "reports" ? "active" : ""} type="button" onClick={() => setLayer("reports")}>Reports</button>
      </div>

      {["work", "all", "reports"].includes(layer) ? (
        <article className="panel filter-strip cn-filter-strip">
          <Input label="Search CN / order / vendor / reason" value={filter.query} onChange={(query) => setFilter({ ...filter, query })} />
          <label>Status
            <select value={filter.status} onChange={(event) => setFilter({ ...filter, status: event.target.value })}>
              <option value="all">All statuses</option>
              {CN_STATUSES.map((status) => <option key={status}>{status}</option>)}
            </select>
          </label>
          <label>Store
            <select value={filter.storeId} onChange={(event) => setFilter({ ...filter, storeId: event.target.value })}>
              <option value="all">All stores</option>
              {(canViewAll ? state.stores : state.stores.filter((store) => store.id === autoStoreId || user.storeIds?.includes(store.id))).map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
          </label>
        </article>
      ) : null}

      {layer === "work" ? (
        <>
          {canExportCn ? (
            <div className="button-row cn-dashboard-actions">
              <button className="secondary-button" type="button" onClick={() => downloadAwaitedVendorExcel(defaultAwaitedExportRows, "all", false)}>
                <FileSpreadsheet size={16} /> Download Awaited CN Vendor List
              </button>
            </div>
          ) : null}
          <div className="cn-status-grid">
            {CN_WORK_BUCKETS.map((bucket) => (
              <button key={bucket.key} className={`${activeBucket === bucket.key ? "active" : ""} ${bucket.tone || ""}`} type="button" onClick={() => { setActiveBucket(bucket.key); setBucketDrawer(bucket.key); }}>
                <span>{bucket.label}</span>
                <strong>{bucketCounts[bucket.key] || 0}</strong>
                <small>{bucket.action}</small>
              </button>
            ))}
          </div>
          <p className="notice compact">
            Tap any box to open its CN list in a separate window. This keeps the dashboard short even when there are hundreds of CN cases.
            {state.settings?.cnLastSyncedAt ? ` Last synced: ${new Date(state.settings.cnLastSyncedAt).toLocaleString("en-IN")}.` : ""}
          </p>
        </>
      ) : null}

      {layer === "create" ? (
        <div className="panel-grid two cn-create-grid">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Create CN Request</h2>
            <p className="notice compact">{broadCnAccess ? "Admin access: select any store." : `Store auto-filled: ${storeShortName(state, autoStoreId)}.`} Customer name is not required.</p>
            <div className="form-grid">
              {broadCnAccess ? (
                <label>Store
                  <select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>
                    {state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                  </select>
                </label>
              ) : null}
              <Input label="Previous order no" value={form.previousOrderNo} onChange={(previousOrderNo) => setForm({ ...form, previousOrderNo })} />
              <Input label="New order no" value={form.newOrderNo} onChange={(newOrderNo) => setForm({ ...form, newOrderNo })} />
              <label>Product type
                <select value={form.productType} onChange={(event) => setForm({ ...form, productType: event.target.value })}>
                  {CN_PRODUCT_TYPES.map((type) => <option key={type}>{type}</option>)}
                </select>
              </label>
              <Input label="Quantity" type="number" value={form.quantity} onChange={(quantity) => setForm({ ...form, quantity })} />
              <label>Reason
                <select value={form.reasonForReturn} onChange={(event) => setForm({ ...form, reasonForReturn: event.target.value })}>
                  {CN_REASONS.map((reason) => <option key={reason}>{reason}</option>)}
                </select>
              </label>
              {form.reasonForReturn === "Other" ? <Input label="Other reason" value={form.otherReasonText} onChange={(otherReasonText) => setForm({ ...form, otherReasonText })} /> : null}
            </div>
            <label>Product / lens / frame details
              <textarea value={form.productDetails} onChange={(event) => setForm({ ...form, productDetails: event.target.value })} />
            </label>
            <label>Remarks
              <textarea value={form.complaintNote} onChange={(event) => setForm({ ...form, complaintNote: event.target.value })} />
            </label>
            <div className="cn-photo-grid">
              <CnPhotoInput label="CN / order reference photo" value={form.orderReferencePhoto} onFile={(file) => handleFile("orderReferencePhoto", file)} />
              <CnPhotoInput label="Supporting photo" value={form.supportingPhoto} onFile={(file) => handleFile("supportingPhoto", file)} />
              <CnPhotoInput label="Old lens/chashma photo" value={form.oldItemPhoto} onFile={(file) => handleFile("oldItemPhoto", file)} />
            </div>
            <div className="button-row">
              <button className="primary-button" type="button" disabled={isSubmittingCn} onClick={submitCn}>{isSubmittingCn ? "Saving CN..." : "Submit CN request"}</button>
              {lastSubmitted ? <button className="secondary-button" type="button" onClick={() => printPacketSlip(lastSubmitted)}><Printer size={16} /> Print CN Packet Slip</button> : null}
            </div>
          </form>
          <article className="panel">
            <h2>Packet slip checklist</h2>
            <div className="checklist-card">
              <span>[ ] Old Lens / Chashma</span>
              <span>[ ] Order Form</span>
              <span>[ ] Product / Frame / Lens</span>
            </div>
            <p>Photos are optional. The app-generated CN Packet Slip replaces handwritten internal CN paperwork.</p>
          </article>
        </div>
      ) : null}

      {layer === "all" ? (
        <>
          <div className="detail-list-layout cn-workbench">
            <article className="panel">{renderCaseList(filteredCases)}</article>
          </div>
        </>
      ) : null}

      {layer === "import" && canImport ? (
        <CnImportPanel
          importState={importState}
          parseImportFile={parseImportFile}
          confirmImport={confirmImport}
          resetImportedCnData={resetImportedCnData}
          applyImportMapping={applyImportMapping}
          togglePossibleDuplicates={togglePossibleDuplicates}
          downloadImportErrorReport={downloadImportErrorReport}
          onViewDashboard={() => setLayer("work")}
        />
      ) : null}

      {layer === "reports" ? (
        <div className="stacked-panels">
          <article className="panel">
            <div className="panel-title-row">
              <h2>Vendor-wise Awaited CN</h2>
              <span className="status-pill">{awaitedExportRows.length} rows</span>
            </div>
            <div className="form-grid">
              <label>Vendor / Company
                <select value={exportFilter.vendor} onChange={(event) => setExportFilter({ ...exportFilter, vendor: event.target.value })}>
                  <option value="all">All vendors, separate Excel sheets</option>
                  {vendorOptions.map((vendor) => <option key={vendor} value={vendor}>{vendor}</option>)}
                </select>
              </label>
              <label>Store
                <select value={exportFilter.storeId} onChange={(event) => setExportFilter({ ...exportFilter, storeId: event.target.value })}>
                  <option value="all">All stores</option>
                  {state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                </select>
              </label>
              <label>Report type
                <select value={exportFilter.reportType} onChange={(event) => setExportFilter({ ...exportFilter, reportType: event.target.value })}>
                  <option value="awaited">Only CN Awaited</option>
                  <option value="ageing30">Only CN Awaited &gt; 30 Days</option>
                </select>
              </label>
              <label>Format
                <select value={exportFilter.format} onChange={(event) => setExportFilter({ ...exportFilter, format: event.target.value })}>
                  <option value="vendor">Vendor Share Format</option>
                  {canInternalExport ? <option value="internal">Internal Full Format</option> : null}
                </select>
              </label>
              <Input label="Pickup from" type="date" value={exportFilter.pickupFrom} onChange={(pickupFrom) => setExportFilter({ ...exportFilter, pickupFrom })} />
              <Input label="Pickup to" type="date" value={exportFilter.pickupTo} onChange={(pickupTo) => setExportFilter({ ...exportFilter, pickupTo })} />
              <Input label="Created from" type="date" value={exportFilter.createdFrom} onChange={(createdFrom) => setExportFilter({ ...exportFilter, createdFrom })} />
              <Input label="Created to" type="date" value={exportFilter.createdTo} onChange={(createdTo) => setExportFilter({ ...exportFilter, createdTo })} />
              <label className="toggle-line"><input type="checkbox" checked={exportFilter.includeRejected} onChange={(event) => setExportFilter({ ...exportFilter, includeRejected: event.target.checked })} /> Include rejected/reapplied cases</label>
            </div>
            <div className="button-row">
              <button className="primary-button" type="button" disabled={!canExportCn} onClick={() => downloadAwaitedVendorExcel()}>
                <FileSpreadsheet size={16} /> Excel vendor-wise
              </button>
              <button className="secondary-button" type="button" disabled={!canExportCn || exportFilter.vendor === "all"} onClick={() => downloadAwaitedVendorExcel(awaitedExportRows, exportFilter.vendor, exportFilter.format === "internal")}>
                Selected vendor Excel
              </button>
              <button className="secondary-button" type="button" disabled={!canExportCn || exportFilter.vendor === "all"} onClick={downloadAwaitedVendorPdf}>
                PDF summary
              </button>
              <button className="secondary-button" type="button" disabled={!canExportCn} onClick={downloadAwaitedVendorCsv}>
                CSV
              </button>
            </div>
            <DataTable rows={awaitedExportRows.slice(0, 60)} columns={vendorShareCnColumns(exportFilter.format === "internal")} />
          </article>
          <article className="panel">
            <div className="panel-title-row">
              <h2>All CN Reports</h2>
              <button className="secondary-button" type="button" onClick={() => downloadSheet(`${filePrefix(state.settings)}-cn-tracking.csv`, visibleCases, cnColumns)}>
                <FileSpreadsheet size={16} /> Download CN sheet
              </button>
            </div>
            <DataTable rows={visibleCases} columns={cnColumns} />
          </article>
        </div>
      ) : null}
      {bucketModal}
      {successModal}
      {detailModal}
    </section>
  );
}

function CnAgeingHint() {
  return <p className="notice compact">These are picked-up CN cases where credit note has not come for more than 30 days.</p>;
}

function CnCaseCard({ request, state, selected, onOpen }) {
  const isClosed = normalizeCnStatus(request.status, request) === "Closed";
  const dateSummary = cnDateSummary(request);
  return (
    <button className={`tap-row cn-case-row ${cnStatusTone(request)} ${selected ? "active" : ""}`} type="button" onClick={onOpen}>
      <span>
        <strong>{request.cnTicketNumber} · {storeShortName(state, request.storeId)}</strong>
        <small>{request.previousOrderNo || "-"} → {request.newOrderNo || "-"} · {request.productType || "Lens"} · {request.reasonForReturn || "-"}</small>
        <small>{request.vendorName ? `Vendor: ${request.vendorName}` : "Vendor not assigned"} · {isClosed ? `Final: ${cnFinalOutcome(request)}` : `Age ${cnAgeDays(request)} days`}</small>
        <small>{dateSummary.length ? dateSummary.join(" · ") : "Dates not available"}</small>
        <small className="cn-current-line">{cnCurrentStatusText(request)}</small>
      </span>
      <em>{isClosed ? cnFinalOutcome(request) : cnActionNeeded(request)}</em>
      <ChevronRight className="row-chevron" size={17} />
    </button>
  );
}

function CnVendorField({ value, onChange }) {
  const knownValue = value === "__OTHER__" ? "Other" : CN_VENDOR_OPTIONS.includes(value) ? value : value ? "Other" : "";
  return (
    <>
      <label>Vendor / company
        <select value={knownValue} onChange={(event) => onChange(event.target.value === "Other" ? "__OTHER__" : event.target.value)}>
          <option value="">Select vendor</option>
          {CN_VENDOR_OPTIONS.map((vendor) => <option key={vendor} value={vendor}>{vendor}</option>)}
        </select>
      </label>
      {knownValue === "Other" ? (
        <Input label="Other vendor / company" value={value === "__OTHER__" ? "" : value} onChange={onChange} placeholder="Type vendor name" />
      ) : null}
    </>
  );
}

function CnCaseDetailsV2({
  state,
  request,
  user,
  canProcess,
  caseDraft,
  setCaseDraft,
  handleFile,
  printPacketSlip,
  assignVendor,
  markPickedUp,
  saveCnCredit,
  saveCnCreditDraft,
  scanCnPhoto,
  cnCreditMode,
  setCnCreditMode,
  cnExtraction,
  actionError,
  actionState,
  runCnAction,
  canCorrectCnStatus,
  correctCnStatus,
  markProblem,
  addFollowUp,
  reapplyRejected,
  updateLedgerOnly,
  closeCase
}) {
  const status = normalizeCnStatus(request.status, request);
  const bucket = cnBucketKey(request);
  const canClose = canProcess || hasUserPermission(user, "can_close_cn");
  const dateSummary = cnDateSummary(request);
  return (
    <div className="cn-case-detail">
      <div className="detail-title-row">
        <div>
          <h3>{request.cnTicketNumber}</h3>
          <p>{storeShortName(state, request.storeId)} · {status} · Age {cnAgeDays(request)} days</p>
        </div>
        <button className="secondary-button" type="button" onClick={() => printPacketSlip(request)}>
          <Printer size={16} /> Print CN Packet Slip
        </button>
      </div>
      <div className={`cn-status-banner ${cnStatusTone(request)}`}>
        <strong>Current status: {cnCurrentStatusText(request)}</strong>
        <small>{dateSummary.length ? dateSummary.join(" · ") : "No important dates saved yet."}</small>
      </div>
      {actionError ? (
        <div className="notice error cn-action-error">
          <strong>Could not save this CN change.</strong>
          <span>{actionError}</span>
        </div>
      ) : null}
      <div className="detail-fields">
        <p><strong>Previous order:</strong> {request.previousOrderNo || "-"}</p>
        <p><strong>New order:</strong> {request.newOrderNo || "-"}</p>
        <p><strong>Product type:</strong> {request.productType || "Lens"}</p>
        <p><strong>Reason:</strong> {request.reasonForReturn || "-"}</p>
        <p><strong>Vendor:</strong> {request.vendorName || "-"}</p>
        <p><strong>Vendor assigned:</strong> {cnReadableDate(request.vendorAssignedAt) || "-"}</p>
        <p><strong>Sent / picked up:</strong> {cnReadableDate(request.pickupDate || request.dispatchDate) || "-"}</p>
        <p><strong>CN date:</strong> {cnReadableDate(request.cnDate || request.cnReceivedDate) || "-"}</p>
        <p><strong>Current action:</strong> {cnActionNeeded(request)}</p>
        {status === "Closed" ? <p><strong>Final result:</strong> {cnFinalOutcome(request)}</p> : null}
        {request.closedAt ? <p><strong>Closed:</strong> {new Date(request.closedAt).toLocaleString("en-IN")} by {request.closedByName || "-"}</p> : null}
        {request.importedStatusText ? <p><strong>Old Excel status:</strong> {request.importedStatusText}</p> : null}
      </div>

      <OpticalSection title="Submitted photos" subtitle="All photos are optional" defaultOpen>
        <div className="cn-photo-grid readonly">
          <PhotoPreview src={request.orderReferencePhoto || request["fast" + "SlipPhoto"]} label="CN / order reference" />
          <PhotoPreview src={request.supportingPhoto || request["cn" + "FormPhoto"]} label="Supporting photo" />
          <PhotoPreview src={request.oldItemPhoto} label="Old lens/chashma" />
        </div>
      </OpticalSection>

      {(bucket === "new" || bucket === "review" || bucket === "rejected" || bucket === "pickup") ? (
        <OpticalSection title="Assign / change vendor" subtitle="Move case to Pickup Awaited">
          <div className="form-grid">
            <CnVendorField value={caseDraft.vendorName} onChange={(vendorName) => setCaseDraft({ ...caseDraft, vendorName })} />
            <Input label="Product / lens details" value={caseDraft.productNameAsPerDc} onChange={(productNameAsPerDc) => setCaseDraft({ ...caseDraft, productNameAsPerDc })} />
            <Input label="Vendor reference no optional" value={caseDraft.vendorReferenceNo} onChange={(vendorReferenceNo) => setCaseDraft({ ...caseDraft, vendorReferenceNo })} />
          </div>
          <label>Remarks optional<textarea value={caseDraft.remarks} onChange={(event) => setCaseDraft({ ...caseDraft, remarks: event.target.value })} /></label>
          <div className="button-row">
            <ActionButton
              className="primary-button"
              disabled={!canProcess}
              action={actionState("cn-assign-vendor")}
              labels={{ loading: "Saving Vendor...", success: "Moved to Pickup Awaited ✓", error: "Could not move. Try Again" }}
              onClick={() => runCnAction(
                "cn-assign-vendor",
                {
                  loading: "Saving Vendor...",
                  success: "Moved to Pickup Awaited ✓",
                  successMessage: "Vendor saved. Order moved to Pickup Awaited.",
                  error: "Could not move. Try Again"
                },
                async (setStep) => {
                  setStep("Moving to Pickup Awaited...");
                  return assignVendor();
                }
              )}
            >
              Save Vendor & Move to Pickup Awaited
            </ActionButton>
            <ActionButton
              className="secondary-button danger"
              disabled={!canProcess}
              action={actionState("cn-needs-review")}
              labels={{ loading: "Updating...", success: "Needs Review ✓", error: "Could not update. Try Again" }}
              onClick={() => runCnAction("cn-needs-review", { loading: "Updating...", success: "Needs Review ✓" }, () => markProblem("Needs Review"))}
            >
              Mark Needs Review
            </ActionButton>
          </div>
        </OpticalSection>
      ) : null}

      {(bucket === "pickup" || (bucket === "review" && request.vendorName)) ? (
        <OpticalSection title="Vendor pickup" subtitle="Vendor assigned, packet waiting for pickup">
          <div className="form-grid">
            <Input label="Pickup date" type="date" value={caseDraft.pickupDate} onChange={(pickupDate) => setCaseDraft({ ...caseDraft, pickupDate })} />
            <Input label="Picked by / vendor person optional" value={caseDraft.pickedBy} onChange={(pickedBy) => setCaseDraft({ ...caseDraft, pickedBy })} />
            <Input label="Sent through optional" value={caseDraft.sentThrough} onChange={(sentThrough) => setCaseDraft({ ...caseDraft, sentThrough })} />
            <Input label="Vendor document/reference no optional" value={caseDraft.companyFormNo} onChange={(companyFormNo) => setCaseDraft({ ...caseDraft, companyFormNo })} />
          </div>
          <label>Remarks optional<textarea value={caseDraft.remarks} onChange={(event) => setCaseDraft({ ...caseDraft, remarks: event.target.value })} /></label>
          <ActionButton
            className="primary-button"
            disabled={!canProcess}
            action={actionState("cn-mark-picked-up")}
            labels={{ loading: "Updating...", success: "Moved to CN Awaited ✓", error: "Could not update. Try Again" }}
            onClick={() => runCnAction(
              "cn-mark-picked-up",
              { loading: "Updating...", success: "Moved to CN Awaited ✓", successMessage: "Vendor pickup saved. CN moved to CN Awaited." },
              () => markPickedUp()
            )}
          >
            Mark Picked Up & Move to CN Awaited
          </ActionButton>
        </OpticalSection>
      ) : null}

      {(bucket === "awaited" || bucket === "review" || bucket === "credited") ? (
        <OpticalSection title="Mark CN Credited" subtitle="Scan CN photo to fill details, or enter manually. Nothing updates until you confirm.">
          <div className="button-row">
            <button className={cnCreditMode === "scan" ? "primary-button" : "secondary-button"} type="button" onClick={() => setCnCreditMode("scan")}>
              <Camera size={16} /> Scan CN Photo
            </button>
            <button className={cnCreditMode === "manual" ? "primary-button" : "secondary-button"} type="button" onClick={() => setCnCreditMode("manual")}>
              Enter Manually
            </button>
          </div>
          {cnCreditMode === "scan" ? (
            <>
              <div className="cn-photo-grid">
                <CnPhotoInput label={caseDraft.cnCopyPhoto ? "Retry Scan" : "Scan CN Photo"} value={caseDraft.cnCopyPhoto} onFile={scanCnPhoto} />
              </div>
              {cnExtraction.status !== "idle" ? (
                <div className={`cn-extraction-box ${cnExtraction.status}`}>
                  <strong>{cnExtraction.status === "pending" ? "Reading CN photo..." : cnExtraction.status === "failed" ? "Manual check needed" : "Review extracted CN data"}</strong>
                  <small>{cnExtraction.message}</small>
                  {cnExtraction.confidenceScore ? <small>Confidence: {Math.round(cnExtraction.confidenceScore * 100)}%</small> : null}
                  {cnExtraction.lowConfidenceFields?.length ? <small>Please verify: {cnExtraction.lowConfidenceFields.join(", ")}</small> : null}
                </div>
              ) : null}
            </>
          ) : null}
          <div className="form-grid">
            <Input label="CN number" value={caseDraft.cnNumber} onChange={(cnNumber) => setCaseDraft({ ...caseDraft, cnNumber })} />
            <Input label="CN date" type="date" value={caseDraft.cnDate} onChange={(cnDate) => setCaseDraft({ ...caseDraft, cnDate })} />
            <Input label="CN amount" type="number" value={caseDraft.cnAmount} onChange={(cnAmount) => setCaseDraft({ ...caseDraft, cnAmount })} />
            <Input label="Settlement amount optional" type="number" value={caseDraft.settlementAmount} onChange={(settlementAmount) => setCaseDraft({ ...caseDraft, settlementAmount })} />
            <Input label="Difference amount optional" type="number" value={caseDraft.differenceAmount} onChange={(differenceAmount) => setCaseDraft({ ...caseDraft, differenceAmount })} />
            <Input label="Vendor reference optional" value={caseDraft.vendorReferenceNo || ""} onChange={(vendorReferenceNo) => setCaseDraft({ ...caseDraft, vendorReferenceNo })} />
            <label className="toggle-line"><input type="checkbox" checked={caseDraft.ledgerUpdated} onChange={(event) => setCaseDraft({ ...caseDraft, ledgerUpdated: event.target.checked })} /> Ledger updated</label>
          </div>
          <label>Remarks optional<textarea value={caseDraft.remarks} onChange={(event) => setCaseDraft({ ...caseDraft, remarks: event.target.value })} /></label>
          <div className="button-row">
            <ActionButton
              className="primary-button"
              disabled={!canProcess}
              action={actionState("cn-save-credit")}
              labels={{ loading: "Updating...", success: "CN Credited ✓", error: "Could not update. Try Again" }}
              onClick={() => runCnAction(
                "cn-save-credit",
                { loading: "Updating...", success: "CN Credited ✓", successMessage: "CN credited details saved." },
                () => saveCnCredit()
              )}
            >
              Confirm & Update CN
            </ActionButton>
            <ActionButton
              className="secondary-button"
              disabled={!canProcess}
              action={actionState("cn-save-credit-draft")}
              labels={{ loading: "Saving...", success: "Saved ✓", error: "Try Again" }}
              onClick={() => runCnAction("cn-save-credit-draft", { loading: "Saving...", success: "Saved ✓" }, () => saveCnCreditDraft())}
            >
              Save entered details
            </ActionButton>
            <ActionButton
              className="secondary-button danger"
              disabled={!canProcess}
              action={actionState("cn-mark-rejected")}
              labels={{ loading: "Updating...", success: "Rejected ✓", error: "Could not update. Try Again" }}
              onClick={() => runCnAction("cn-mark-rejected", { loading: "Updating...", success: "Rejected ✓" }, () => markProblem("Rejected"))}
            >
              Mark Rejected
            </ActionButton>
            <ActionButton
              className="secondary-button danger"
              disabled={!canProcess}
              action={actionState("cn-needs-review-credit")}
              labels={{ loading: "Updating...", success: "Needs Review ✓", error: "Could not update. Try Again" }}
              onClick={() => runCnAction("cn-needs-review-credit", { loading: "Updating...", success: "Needs Review ✓" }, () => markProblem("Needs Review"))}
            >
              Mark Needs Review
            </ActionButton>
          </div>
        </OpticalSection>
      ) : null}

      {bucket === "rejected" ? (
        <OpticalSection title="Rejected CN" subtitle="Reapply or close the case">
          <div className="button-row">
            <ActionButton className="primary-button" disabled={!canProcess} action={actionState("cn-reapply")} labels={{ loading: "Updating...", success: "Reapplied ✓", error: "Try Again" }} onClick={() => runCnAction("cn-reapply", { loading: "Updating...", success: "Reapplied ✓" }, () => reapplyRejected())}>
              Reapply
            </ActionButton>
            <ActionButton className="secondary-button danger" disabled={!canClose} action={actionState("cn-close-rejected")} labels={{ loading: "Closing...", success: "Closed ✓", error: "Try Again" }} onClick={() => runCnAction("cn-close-rejected", { loading: "Closing...", success: "Closed ✓" }, () => closeCase())}>
              Close
            </ActionButton>
          </div>
        </OpticalSection>
      ) : null}

      {bucket === "credited" ? (
        <OpticalSection title="Close CN" subtitle="CN does not close automatically. Close only after review.">
          {!request.ledgerUpdated ? (
            <ActionButton className="secondary-button" disabled={!canProcess} action={actionState("cn-ledger")} labels={{ loading: "Updating...", success: "Ledger Updated ✓", error: "Try Again" }} onClick={() => runCnAction("cn-ledger", { loading: "Updating...", success: "Ledger Updated ✓" }, () => updateLedgerOnly())}>
              Mark ledger updated
            </ActionButton>
          ) : null}
          <label>Final result
            <select value={caseDraft.closureOutcome || cnFinalOutcome(request, caseDraft)} onChange={(event) => setCaseDraft({ ...caseDraft, closureOutcome: event.target.value })}>
              <option>Approved</option>
              <option>Partially approved</option>
              <option>Rejected</option>
              <option>Closed / needs review</option>
            </select>
          </label>
          <label>Closure remarks optional<textarea value={caseDraft.closureRemarks} onChange={(event) => setCaseDraft({ ...caseDraft, closureRemarks: event.target.value })} /></label>
          <ActionButton className="primary-button" disabled={!canClose} action={actionState("cn-close")} labels={{ loading: "Closing...", success: "Closed ✓", error: "Try Again" }} onClick={() => runCnAction("cn-close", { loading: "Closing...", success: "Closed ✓" }, () => closeCase())}>
            Close CN
          </ActionButton>
        </OpticalSection>
      ) : null}

      {canCorrectCnStatus ? (
        <OpticalSection title="Admin status correction" subtitle="Use only when a CN status was marked wrongly. Reason is compulsory.">
          <div className="status-correction-box">
            <p><strong>Current status:</strong> {status}</p>
            <div className="button-row compact-action-row">
              <ActionButton
                className="secondary-button"
                action={actionState("cn-correct-assigned")}
                labels={{ loading: "Updating Status...", success: "Moved Back to Assigned ✓", error: "Could not update. Try Again" }}
                onClick={() => runCnAction("cn-correct-assigned", { loading: "Updating Status...", success: "Moved Back to Assigned ✓" }, () => correctCnStatus("New Request", "Moved Back to Assigned"))}
              >
                Move Back to Assigned
              </ActionButton>
              <ActionButton
                className="secondary-button danger"
                action={actionState("cn-correct-not-picked")}
                labels={{ loading: "Updating Status...", success: "Marked as Not Picked Up ✓", error: "Could not update. Try Again" }}
                onClick={() => runCnAction("cn-correct-not-picked", { loading: "Updating Status...", success: "Marked as Not Picked Up ✓" }, () => correctCnStatus("Pickup Awaited", "Marked as Not Picked Up"))}
              >
                Mark as Not Picked Up
              </ActionButton>
              <ActionButton
                className="secondary-button"
                action={actionState("cn-correct-pickup-awaited")}
                labels={{ loading: "Updating Status...", success: "Moved to Pickup Awaited ✓", error: "Could not update. Try Again" }}
                onClick={() => runCnAction("cn-correct-pickup-awaited", { loading: "Updating Status...", success: "Moved to Pickup Awaited ✓" }, () => correctCnStatus("Pickup Awaited", "Moved to Pickup Awaited"))}
              >
                Move to Pickup Awaited
              </ActionButton>
            </div>
          </div>
        </OpticalSection>
      ) : null}

      <OpticalSection title="Follow-up / notes" subtitle="Keep complete audit trail">
        <label>Add note<textarea value={caseDraft.followUpNote} onChange={(event) => setCaseDraft({ ...caseDraft, followUpNote: event.target.value })} /></label>
        <ActionButton className="secondary-button" action={actionState("cn-add-note")} labels={{ loading: "Saving...", success: "Note Added ✓", error: "Try Again" }} onClick={() => runCnAction("cn-add-note", { loading: "Saving...", success: "Note Added ✓" }, () => addFollowUp())}>
          Add note
        </ActionButton>
        <div className="audit-mini-list">
          {(request.cnActivity || []).map((event) => (
            <small key={event.id || event.at}>
              {new Date(event.at).toLocaleString("en-IN")} · {event.by}: {event.note}
              {event.reason ? ` · Reason: ${event.reason}` : ""}
            </small>
          ))}
          {request.lastFollowUpNote ? <small>Last follow-up: {request.lastFollowUpNote}</small> : null}
        </div>
      </OpticalSection>

      <div className="cn-mobile-action-bar">
        <button className="secondary-button" type="button" onClick={() => printPacketSlip(request)}>
          <Printer size={16} /> Print CN Slip
        </button>
        {bucket === "new" || bucket === "review" ? (
          <ActionButton className="primary-button" disabled={!canProcess} action={actionState("cn-assign-vendor")} labels={{ loading: "Saving Vendor...", success: "Moved ✓", error: "Try Again" }} onClick={() => runCnAction("cn-assign-vendor", { loading: "Saving Vendor...", success: "Moved to Pickup Awaited ✓", successMessage: "Vendor saved. Order moved to Pickup Awaited." }, () => assignVendor())}>
            Assign Vendor
          </ActionButton>
        ) : null}
        {bucket === "pickup" ? (
          <ActionButton className="primary-button" disabled={!canProcess} action={actionState("cn-mark-picked-up")} labels={{ loading: "Updating...", success: "Moved ✓", error: "Try Again" }} onClick={() => runCnAction("cn-mark-picked-up", { loading: "Updating...", success: "Moved to CN Awaited ✓" }, () => markPickedUp())}>
            Mark Picked Up
          </ActionButton>
        ) : null}
        {bucket === "awaited" ? (
          <button className="primary-button" type="button" disabled={!canProcess} onClick={() => setCnCreditMode("scan")}>Mark CN Credited</button>
        ) : null}
        {bucket === "credited" ? (
          <ActionButton className="primary-button" disabled={!canClose} action={actionState("cn-close")} labels={{ loading: "Closing...", success: "Closed ✓", error: "Try Again" }} onClick={() => runCnAction("cn-close", { loading: "Closing...", success: "Closed ✓" }, () => closeCase())}>
            Close CN
          </ActionButton>
        ) : null}
        {bucket === "rejected" ? (
          <ActionButton className="primary-button" disabled={!canProcess} action={actionState("cn-reapply")} labels={{ loading: "Updating...", success: "Reapplied ✓", error: "Try Again" }} onClick={() => runCnAction("cn-reapply", { loading: "Updating...", success: "Reapplied ✓" }, () => reapplyRejected())}>
            Reapply
          </ActionButton>
        ) : null}
      </div>
    </div>
  );
}

function CnImportPanel({ importState, parseImportFile, confirmImport, resetImportedCnData, applyImportMapping, togglePossibleDuplicates, downloadImportErrorReport, onViewDashboard }) {
  const statusSamples = CN_STATUSES.reduce((samples, status) => {
    samples[status] = importState.rows.filter((row) => normalizeCnStatus(row.record.status, row.record) === status).slice(0, 3);
    return samples;
  }, {});
  const hasWarnings = Boolean(importState.errorRows?.length);
  const isImporting = ["importing", "syncing"].includes(importState.batch?.status);
  const isCompleted = ["imported", "sync_failed", "failed"].includes(importState.batch?.status);
  const possibleDuplicateCount = importState.possibleDuplicates?.length || 0;
  const importButtonText = importState.batch?.status === "syncing"
    ? "Rows added - syncing online..."
    : importState.batch?.status === "imported"
      ? "Import completed"
      : importState.batch?.status === "sync_failed"
        ? "Imported with sync warnings"
        : isImporting
          ? "Importing CN rows..."
          : `Confirm final import (${importState.ready.length})`;
  return (
    <article className="panel form-panel">
      <h2>Import Old CN Data</h2>
      <p className="notice compact">Fresh CN Tracking starts from app entries. Excel upload remains available for future controlled imports only: preview first, confirm later.</p>
      <div className="cn-reset-box">
        <strong>Reset CN Data</strong>
        <small>This downloads a backup first, then deletes old imported/test CN rows and old import batches only. Manual app-created CN cases stay safe.</small>
        <button className="secondary-button danger" type="button" onClick={resetImportedCnData}>Reset CN Data</button>
      </div>
      <label className="file-button">
        Upload Excel / Future Import
        <input type="file" accept=".xlsx,.xls" onChange={(event) => parseImportFile(event.target.files?.[0])} />
      </label>
      {importState.message ? <p className="notice">{importState.message}</p> : null}
      {isCompleted ? (
        <div className={`cn-import-complete ${importState.batch?.status === "imported" ? "success" : "warning"}`}>
          <h3>CN Import Completed</h3>
          <div className="detail-fields">
            <p><strong>Import batch ID:</strong> {importState.batch?.id || "-"}</p>
            <p><strong>File name:</strong> {importState.batch?.fileName || importState.file?.name || "-"}</p>
            <p><strong>Total rows read:</strong> {importState.batch?.totalRows ?? importState.rows.length}</p>
            <p><strong>Rows imported:</strong> {importState.batch?.importedRows ?? importState.ready.length}</p>
            <p><strong>Duplicate rows:</strong> {importState.batch?.duplicateRows ?? importState.duplicates.length}</p>
            <p><strong>Failed rows:</strong> {importState.batch?.failedRows ?? 0}</p>
            <p><strong>Last synced at:</strong> {importState.batch?.importedAt ? new Date(importState.batch.importedAt).toLocaleString("en-IN") : "-"}</p>
          </div>
          <div className="cn-status-grid compact">
            {CN_STATUSES.map((status) => (
              <button type="button" key={status}><span>{status}</span><strong>{importState.batch?.statusCounts?.[status] ?? importState.statusCounts?.[status] ?? 0}</strong></button>
            ))}
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={downloadImportErrorReport}>Download Import Report</button>
            <button className="secondary-button" type="button" onClick={onViewDashboard}>View CN Dashboard</button>
            <label className="secondary-button file-inline-button">
              Import Another File
              <input type="file" accept=".xlsx,.xls" onChange={(event) => parseImportFile(event.target.files?.[0])} />
            </label>
          </div>
        </div>
      ) : null}
      {importState.rows.length ? (
        <>
          <div className="cn-status-grid compact">
            <button type="button"><span>Total rows</span><strong>{importState.rows.length}</strong></button>
            <button type="button"><span>Will import</span><strong>{importState.ready.length}</strong></button>
            <button type="button"><span>CN Awaited</span><strong>{importState.statusCounts?.["CN Awaited"] || 0}</strong></button>
            <button type="button"><span>Pickup Awaited</span><strong>{importState.statusCounts?.["Pickup Awaited"] || 0}</strong></button>
            <button type="button"><span>CN Credited</span><strong>{importState.statusCounts?.["CN Credited"] || 0}</strong></button>
            <button type="button"><span>Rejected</span><strong>{importState.statusCounts?.Rejected || 0}</strong></button>
            <button type="button"><span>Duplicates</span><strong>{importState.duplicates.length}</strong></button>
            <button type="button"><span>Possible duplicates</span><strong>{importState.possibleDuplicates?.length || 0}</strong></button>
            <button type="button"><span>Needs review</span><strong>{importState.needsReview.length}</strong></button>
          </div>
          <label className={`check-row cn-import-duplicate-toggle ${possibleDuplicateCount ? "" : "disabled"}`}>
            <input
              type="checkbox"
              disabled={!possibleDuplicateCount || isImporting}
              checked={Boolean(importState.includePossibleDuplicates)}
              onChange={(event) => togglePossibleDuplicates(event.target.checked)}
            />
            <span>
              <strong>Import possible duplicates also</strong>
              <small>{possibleDuplicateCount ? `${possibleDuplicateCount} possible duplicate row(s) found. Tick this only if you want to import them too.` : "No possible duplicate rows found in this preview."}</small>
            </span>
          </label>
          {hasWarnings ? (
            <div className="notice warning">
              <strong>Review needed before import.</strong>
              <span>{importState.errorRows.length} warning/error row(s) found. Download the report if you want to check them in Excel.</span>
              <button className="secondary-button" type="button" onClick={downloadImportErrorReport}>Download Import Error Report</button>
            </div>
          ) : null}
          <div className="cn-import-sheets">
            {(importState.sheets || []).map((sheet) => (
              <CnSheetMappingPanel key={sheet.sheetName} sheet={sheet} onApply={applyImportMapping} />
            ))}
          </div>
          <div className="cn-import-samples">
            {CN_STATUSES.map((status) => statusSamples[status]?.length ? (
              <section key={status}>
                <h3>{status}</h3>
                {statusSamples[status].map((row) => (
                  <small key={`${status}-${row.sheetName}-${row.rowNumber}`}>{row.sheetName} row {row.rowNumber}: {row.record.previousOrderNo || row.record.vendorReferenceNo || "-"} · {row.record.importedStatusText || "No Excel status"}</small>
                ))}
              </section>
            ) : null)}
          </div>
          <div className="tap-list compact">
            {importState.rows.slice(0, 25).map((row) => (
              <div className="tap-row" key={`${row.sheetName}-${row.rowNumber}`}>
                <span>
                  <strong>{row.record.cnTicketNumber} · {row.sheetName}</strong>
                  <small>{row.record.previousOrderNo || "-"} · {row.record.vendorReferenceNo || "-"} · {row.record.importedStatusText || "No status"}</small>
                </span>
                <em>{row.isDuplicate ? "Duplicate" : row.possibleDuplicate ? "Possible duplicate" : normalizeCnStatus(row.record.status, row.record)}</em>
              </div>
            ))}
          </div>
          {!importState.ready.length ? (
            <p className="notice warning compact">
              <strong>No rows ready for final import.</strong>
              <span>If rows are showing as possible duplicates, tick the duplicate option above. Actual duplicates are still blocked to protect existing CN data.</span>
            </p>
          ) : null}
          <button
            className="primary-button"
            type="button"
            disabled={isImporting || !importState.ready.length || ["imported", "sync_failed"].includes(importState.batch?.status)}
            onClick={confirmImport}
          >
            {importButtonText}
          </button>
        </>
      ) : null}
    </article>
  );
}

function CnSheetMappingPanel({ sheet, onApply }) {
  const [open, setOpen] = useState(false);
  const [mapping, setMapping] = useState(sheet.mapping || {});
  useEffect(() => {
    setMapping(sheet.mapping || {});
  }, [sheet.sheetName, sheet.headerRowIndex, JSON.stringify(sheet.mapping || {})]);
  const mappedCount = sheet.mappedFields?.length || 0;
  const headerOptions = sheet.headersOriginal || [];
  return (
    <section className="cn-import-sheet-card">
      <button className="tap-row" type="button" onClick={() => setOpen((value) => !value)}>
        <span>
          <strong>{sheet.sheetName} · Header row {sheet.headerRowIndex || "not found"}</strong>
          <small>{sheet.totalRows || 0} row(s) · {mappedCount} mapped · {(sheet.unmappedFields || []).length} unmapped</small>
        </span>
        <em>{open ? "Close" : "Review"}</em>
      </button>
      {open ? (
        <div className="cn-import-sheet-body">
          {sheet.warnings?.length ? (
            <div className="notice warning compact">
              {sheet.warnings.map((warning) => <small key={warning}>{warning}</small>)}
            </div>
          ) : <p className="notice compact">Mapping looks okay. Still review sample rows before import.</p>}
          <div className="cn-import-map-summary">
            <small><strong>Mapped:</strong> {(sheet.mappedFields || []).join(", ") || "-"}</small>
            <small><strong>Not mapped:</strong> {(sheet.unmappedFields || []).join(", ") || "-"}</small>
          </div>
          <div className="cn-status-grid compact">
            <button type="button"><span>Rows</span><strong>{sheet.totalRows || 0}</strong></button>
            <button type="button"><span>CN Awaited</span><strong>{sheet.statusCounts?.["CN Awaited"] || 0}</strong></button>
            <button type="button"><span>Pickup Awaited</span><strong>{sheet.statusCounts?.["Pickup Awaited"] || 0}</strong></button>
            <button type="button"><span>CN Credited</span><strong>{sheet.statusCounts?.["CN Credited"] || 0}</strong></button>
            <button type="button"><span>Rejected</span><strong>{sheet.statusCounts?.Rejected || 0}</strong></button>
            <button type="button"><span>Needs Review</span><strong>{sheet.statusCounts?.["Needs Review"] || 0}</strong></button>
          </div>
          <div className="form-grid two">
            {CN_IMPORT_FIELDS.map((field) => (
              <label key={`${sheet.sheetName}-${field.key}`}>
                {field.label}
                <select value={mapping[field.key] ?? -1} onChange={(event) => setMapping((current) => ({ ...current, [field.key]: Number(event.target.value) }))}>
                  <option value={-1}>Not mapped</option>
                  {headerOptions.map((header, index) => (
                    <option key={`${field.key}-${index}`} value={index}>{index + 1}. {header || `Column ${index + 1}`}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <button className="secondary-button" type="button" onClick={() => onApply(sheet.sheetName, mapping)}>Apply mapping for this sheet</button>
          <div className="tap-list compact">
            {(sheet.sampleRows || []).map((row) => (
              <div className="tap-row" key={`${sheet.sheetName}-sample-${row.rowNumber}`}>
                <span>
                  <strong>Row {row.rowNumber}: {row.record.previousOrderNo || row.record.vendorReferenceNo || "-"}</strong>
                  <small>{row.record.productDetails || "-"} · {row.record.importedStatusText || "No status"} · {row.record.pickupDate || row.record.createdDate || "-"}</small>
                </span>
                <em>{row.isDuplicate ? "Duplicate" : row.possibleDuplicate ? "Possible duplicate" : normalizeCnStatus(row.record.status, row.record)}</em>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function CnTrackingPanel({ state, updateState, user, focus, onFocusHandled }) {
  return <CnTrackingPanelV2 state={state} updateState={updateState} user={user} focus={focus} onFocusHandled={onFocusHandled} />;
}

function LegacyCnTrackingPanel({ state, updateState, user, focus, onFocusHandled }) {
  const employee = userEmployee(state, user);
  const autoStoreId = userPrimaryStoreId(state, user);
  const broadCnAccess = isBroadCnUser(user);
  const [layer, setLayer] = useState("requests");
  const [selectedId, setSelectedId] = useState("");
  const [filter, setFilter] = useState({ status: "all", storeId: "all", query: "" });
  const [message, setMessage] = useState("");
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [responsibleIds, setResponsibleIds] = useState(cnResponsibleUserIds(state));
  const emptyCnForm = {
    storeId: broadCnAccess ? (state.stores[0]?.id || autoStoreId) : autoStoreId,
    previousOrderNo: "",
    newOrderNo: "",
    productType: "Lens",
    productDetails: "",
    quantity: 1,
    reasonForReturn: "Coating Issue",
    otherReasonText: "",
    complaintNote: "",
    orderReferencePhoto: "",
    supportingPhoto: "",
    oldItemPhoto: ""
  };
  const [form, setForm] = useState(emptyCnForm);
  const [officeForm, setOfficeForm] = useState({});
  const [cnReview, setCnReview] = useState({});
  const canCreate = Boolean(broadCnAccess || autoStoreId) && (broadCnAccess || user.role === "employee" || hasUserPermission(user, "can_create_cn_request") || hasUserPermission(user, "can_process_cn"));
  const canProcess = broadCnAccess || isCnResponsible(state, user) || hasUserPermission(user, "can_process_cn");
  const canAssign = broadCnAccess || hasUserPermission(user, "can_assign_cn_responsible");
  const canViewAll = broadCnAccess || hasUserPermission(user, "can_view_all_store_cn") || canProcess;
  const requests = (state.cnRequests || [])
    .filter((request) => canViewCnCase(state, user, request))
    .filter((request) => filter.status === "all" || request.status === filter.status)
    .filter((request) => filter.storeId === "all" || request.storeId === filter.storeId)
    .filter((request) => {
      const query = filter.query.trim().toLowerCase();
      if (!query) return true;
      return `${request.cnTicketNumber || ""} ${request.cnaNumber || ""} ${request.previousOrderNo || ""} ${request.newOrderNo || ""} ${request.vendorName || ""}`.toLowerCase().includes(query);
    })
    .sort((a, b) => String(b.createdAt || b.createdDate || "").localeCompare(String(a.createdAt || a.createdDate || "")));
  const selected = requests.find((request) => request.id === selectedId) || null;
  const today = new Date().toISOString().slice(0, 10);
  const counts = CN_STATUSES.map((status) => [status, (state.cnRequests || []).filter((request) => canViewCnCase(state, user, request) && request.status === status).length]);
  const ageingCount = (state.cnRequests || []).filter((request) => canViewCnCase(state, user, request) && !["Closed", "Rejected"].includes(request.status) && request.createdDate && ((Date.now() - new Date(request.createdDate).getTime()) / 86400000) > 15).length;

  useEffect(() => {
    if (!focus) return;
    setLayer("requests");
    if (focus.requestId) setSelectedId(focus.requestId);
    if (focus.list === "new") setFilter((current) => ({ ...current, status: "CN Request Created" }));
    onFocusHandled?.();
  }, [focus, onFocusHandled]);

  useEffect(() => {
    if (selected) {
      setOfficeForm({
        vendorName: selected.vendorName || "",
        vendorReferenceNo: selected.vendorReferenceNo || "",
        productNameAsPerDc: selected.productNameAsPerDc || "",
        dcNumber: selected.dcNumber || "",
        companyFormNo: selected.companyFormNo || "",
        dispatchDate: selected.dispatchDate || "",
        collectedBy: selected.collectedBy || "",
        sentThrough: selected.sentThrough || "",
        amountExclusiveTax: selected.amountExclusiveTax || "",
        vendorFormPhoto: selected.vendorFormPhoto || "",
        remarks: selected.remarks || ""
      });
      setCnReview({
        cnNumber: selected.cnNumber || "",
        cnDate: selected.cnDate || selected.cnReceivedDate || "",
        cnAmount: selected.cnAmount || "",
        settlementAmount: selected.settlementAmount || "",
        differenceAmount: selected.differenceAmount || "",
        vendorName: selected.vendorName || "",
        remarks: selected.remarks || "",
        cnCopyPhoto: selected.cnCopyPhoto || ""
      });
    }
  }, [selected?.id]);

  const updateRequest = async (request, patch) => {
    const updated = { ...request, ...patch, updatedAt: new Date().toISOString() };
    const savedRequest = await persistRequiredEntity("cn_requests", updated, "CN case");
    updateState((current) => ({
      ...current,
      cnRequests: (current.cnRequests || []).map((item) => (item.id === savedRequest.id ? savedRequest : item))
    }));
    setMessage("CN case updated.");
    return savedRequest;
  };

  const notifyUsers = async (notifications) => {
    await Promise.all(notifications.map((notification) => persistOptionalEntity("notifications", notification).catch(() => null)));
    updateState((current) => ({ ...current, notifications: [...notifications, ...(current.notifications || [])] }));
  };

  const handleFile = async (field, file, setter = setForm) => {
    try {
      setMessage("Compressing and uploading photo...");
      const fileTypes = {
        orderReferencePhoto: "order_reference_photo",
        supportingPhoto: "supporting_photo",
        oldItemPhoto: "old_lens_or_chashma",
        vendorFormPhoto: "vendor_return_form",
        cnCopyPhoto: "credit_note_copy"
      };
      const upload = await uploadAppImage({
        file,
        module: "cn",
        recordType: "cn_request",
        recordId: selected?.id || form.cnTicketNumber || "draft-cn",
        storeId: selected?.storeId || form.storeId || autoStoreId,
        staffId: employee?.id || user.employeeId,
        fileType: fileTypes[field] || "other",
        uploadedBy: user.id
      });
      setter((current) => ({ ...current, [field]: upload.storagePath || upload.path || upload.url }));
      setMessage("Photo uploaded.");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const submitCn = async () => {
    setMessage("");
    if (!canCreate) {
      setMessage("You do not have permission to create CN request.");
      return;
    }
    if (!form.previousOrderNo.trim() && !form.newOrderNo.trim()) {
      setMessage("Add previous or new order number.");
      return;
    }
    const requestStoreId = broadCnAccess ? form.storeId : autoStoreId;
    if (!requestStoreId) {
      setMessage("Store is required.");
      return;
    }
    const numbers = nextCnNumbers(state);
    const now = new Date().toISOString();
    const request = {
      id: uuid("cn"),
      ...numbers,
      storeId: requestStoreId,
      createdBy: user.id,
      createdByName: employee?.fullName || user.name || user.email,
      createdDate: today,
      createdAt: now,
      cnResponsibleId: cnResponsibleUserIds(state)[0] || "",
      cnResponsibleIds: cnResponsibleUserIds(state),
      status: "CN Request Created",
      ...form,
      quantity: Number(form.quantity || 1),
      packetChecklist: {
        oldItemReceived: false,
        orderFormReceived: false,
        productMatches: false,
        packetComplete: false
      },
      ledgerUpdated: false
    };
    const savedRequest = await persistRequiredEntity("cn_requests", request, "CN request");
    const notifications = cnResponsibleUserIds(state).map((userId) => createAppNotification({
      userId,
      module: "CN Tracking",
      title: "New CN request",
      message: `New CN request created from ${storeShortName(state, requestStoreId)} - ${request.cnTicketNumber}`,
      linkedRecordType: "cn_request",
      linkedRecordId: savedRequest.id
    }));
    await notifyUsers(notifications);
    updateState((current) => ({ ...current, cnRequests: [savedRequest, ...(current.cnRequests || [])] }));
    setLastSubmitted(savedRequest);
    setSelectedId(savedRequest.id);
    setLayer("requests");
    setForm(emptyCnForm);
    setMessage(`${savedRequest.cnTicketNumber} submitted. Print CN Packet Slip and send packet to CN desk.`);
  };

  const printPacketSlip = async (request) => {
    printCnPacketSlip(request, state);
    if (request.status === "CN Request Created" && (request.createdBy === user.id || broadCnAccess || canProcess || hasUserPermission(user, "can_print_cn_packet_slip"))) {
      await updateRequest(request, { status: "CN Packet Printed" });
      if (lastSubmitted?.id === request.id) setLastSubmitted({ ...request, status: "CN Packet Printed" });
    }
  };

  const saveResponsible = async () => {
    if (!canAssign || !responsibleIds.length) return;
    const settings = { ...state.settings, cnTrackingResponsibleUserIds: responsibleIds };
    try {
      const savedSettings = await persistRequiredEntity("app_settings", { id: "global", settings }, "CN Manager settings");
      updateState((current) => ({ ...current, settings: savedSettings.settings || settings }));
      setMessage(`CN Manager assigned to ${responsibleIds.map((id) => userDisplayName(state, id)).join(", ")}.`);
    } catch (error) {
      setMessage(error.message || "CN Manager settings could not be saved.");
    }
  };

  const markIncomplete = async () => {
    if (!selected) return;
    const updated = await updateRequest(selected, { status: "Packet Incomplete", missingItemReason: officeForm.missingItemReason || "Packet incomplete" });
    await notifyUsers([createAppNotification({
      userId: updated.createdBy,
      module: "CN Tracking",
      title: "CN packet incomplete",
      message: `${updated.cnTicketNumber} packet incomplete: ${updated.missingItemReason}`,
      linkedRecordType: "cn_request",
      linkedRecordId: updated.id
    })]);
  };

  const saveOfficeDetails = async (status = "Vendor Identified") => {
    if (!selected || !(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_vendor_details"))) return;
    await updateRequest(selected, { ...officeForm, amountExclusiveTax: Number(officeForm.amountExclusiveTax || 0), status });
  };

  const uploadCnCopy = async (file) => {
    if (!selected) return;
    try {
      setMessage("Compressing and uploading CN copy...");
      const upload = await uploadAppImage({
        file,
        module: "cn",
        recordType: "cn_request",
        recordId: selected.id,
        storeId: selected.storeId,
        staffId: employee?.id || user.employeeId,
        fileType: "credit_note_copy",
        uploadedBy: user.id
      });
      const extracted = parseCnTextForDetails(`${file?.name || ""} ${selected.vendorName || ""} ${selected.dcNumber || ""}`);
      setCnReview((current) => ({ ...current, ...extracted, vendorName: current.vendorName || selected.vendorName || "", cnCopyPhoto: upload.storagePath || upload.path || upload.url }));
      setMessage("CN photo uploaded. Review extracted fields or edit manually before saving.");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const saveCnReceived = async () => {
    if (!selected || !(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_received"))) return;
    const cnAmount = Number(cnReview.cnAmount || 0);
    const expected = Number(selected.amountExclusiveTax || 0);
    const differenceAmount = Number(cnReview.differenceAmount || Math.max(0, expected - cnAmount));
    const status = expected && cnAmount < expected ? "Short / Difference" : "CN Received";
    if (status === "Short / Difference" && (!differenceAmount || !cnReview.remarks)) {
      setMessage("Difference amount and remark are required for short CN.");
      return;
    }
    await updateRequest(selected, {
      ...cnReview,
      cnReceivedDate: cnReview.cnDate || today,
      cnAmount,
      settlementAmount: Number(cnReview.settlementAmount || cnAmount),
      differenceAmount,
      status
    });
  };

  const updateLedger = async () => {
    if (!selected || !(canProcess || broadCnAccess || hasUserPermission(user, "can_update_cn_ledger"))) return;
    const amount = Number(selected.cnAmount || cnReview.cnAmount || 0);
    const entry = {
      id: `ledger-${selected.id}`,
      storeId: selected.storeId,
      vendorName: selected.vendorName || cnReview.vendorName || "",
      entryType: "credit_note",
      amount,
      linkedRecordType: "cn_request",
      linkedRecordId: selected.id,
      notes: `${selected.cnTicketNumber} ${selected.cnNumber || cnReview.cnNumber || ""}`,
      createdAt: new Date().toISOString(),
      createdBy: user.id
    };
    await persistOptionalEntity("company_ledger_entries", entry);
    const updated = await updateRequest(selected, { ledgerUpdated: true, status: "Ledger Updated" });
    updateState((current) => ({
      ...current,
      companyLedgerEntries: upsertById(current.companyLedgerEntries || [], entry),
      cnRequests: (current.cnRequests || []).map((item) => (item.id === updated.id ? updated : item))
    }));
  };

  const closeCn = async () => {
    if (!selected || !(canProcess || broadCnAccess || hasUserPermission(user, "can_close_cn"))) return;
    if (!selected.ledgerUpdated) {
      setMessage("Update ledger before closing CN.");
      return;
    }
    await updateRequest(selected, { status: "Closed", closedAt: new Date().toISOString() });
  };

  const requestColumns = [
    ["CN Ticket", (row) => row.cnTicketNumber],
    ["Store", (row) => storeShortName(state, row.storeId)],
    ["Prev Order No", (row) => row.previousOrderNo || "-"],
    ["New Order No", (row) => row.newOrderNo || "-"],
    ["Reason", (row) => row.reasonForReturn],
    ["Qty", (row) => row.quantity],
    ["Created By", (row) => row.createdByName || userDisplayName(state, row.createdBy)],
    ["Created At", (row) => row.createdAt ? new Date(row.createdAt).toLocaleString("en-IN") : row.createdDate],
    ["Status", (row) => row.status]
  ];

  return (
    <section>
      <Header title="CN Tracking" subtitle="Store CN requests, packet verification, vendor CN receipt and ledger closure." />
      {message ? <p className="notice">{message}</p> : null}
      <div className="layer-tabs" role="tablist" aria-label="CN Tracking sections">
        <button className={layer === "requests" ? "active" : ""} type="button" onClick={() => setLayer("requests")}>Requests</button>
        {canCreate ? <button className={layer === "create" ? "active" : ""} type="button" onClick={() => setLayer("create")}>Create CN</button> : null}
        {canAssign ? <button className={layer === "settings" ? "active" : ""} type="button" onClick={() => setLayer("settings")}>Responsibility</button> : null}
      </div>

      {layer === "create" ? (
        <div className="panel-grid two cn-create-grid">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>CN request</h2>
            <p className="notice compact">
              {broadCnAccess ? "Admin access: select the store for this CN request." : `Store is auto-filled from login: ${storeShortName(state, autoStoreId)}.`} Customer name is not required.
            </p>
            <div className="form-grid">
              {broadCnAccess ? (
                <label>Store
                  <select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>
                    {state.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                  </select>
                </label>
              ) : null}
              <Input label="Previous order no" value={form.previousOrderNo} onChange={(previousOrderNo) => setForm({ ...form, previousOrderNo })} />
              <Input label="New order no" value={form.newOrderNo} onChange={(newOrderNo) => setForm({ ...form, newOrderNo })} />
              <label>Product type
                <select value={form.productType} onChange={(event) => setForm({ ...form, productType: event.target.value })}>
                  {CN_PRODUCT_TYPES.map((type) => <option key={type}>{type}</option>)}
                </select>
              </label>
              <Input label="Quantity" type="number" value={form.quantity} onChange={(quantity) => setForm({ ...form, quantity })} />
              <label>Reason
                <select value={form.reasonForReturn} onChange={(event) => setForm({ ...form, reasonForReturn: event.target.value })}>
                  {CN_REASONS.map((reason) => <option key={reason}>{reason}</option>)}
                </select>
              </label>
              {form.reasonForReturn === "Other" ? <Input label="Other reason" value={form.otherReasonText} onChange={(otherReasonText) => setForm({ ...form, otherReasonText })} /> : null}
            </div>
            <label>Product / lens / frame details
              <textarea value={form.productDetails} onChange={(event) => setForm({ ...form, productDetails: event.target.value })} placeholder="Frame/lens details, model no, barcode no, lens type, index" />
            </label>
            <label>Remarks
              <textarea value={form.complaintNote} onChange={(event) => setForm({ ...form, complaintNote: event.target.value })} />
            </label>
            <div className="cn-photo-grid">
              <CnPhotoInput label="CN / order reference photo" value={form.orderReferencePhoto} onFile={(file) => handleFile("orderReferencePhoto", file)} />
              <CnPhotoInput label="Supporting photo" value={form.supportingPhoto} onFile={(file) => handleFile("supportingPhoto", file)} />
              <CnPhotoInput label="Old lens/chashma photo" value={form.oldItemPhoto} onFile={(file) => handleFile("oldItemPhoto", file)} />
            </div>
            <div className="button-row">
              <button className="primary-button" type="button" onClick={submitCn}>Submit CN request</button>
              {lastSubmitted ? <button className="secondary-button" type="button" onClick={() => printPacketSlip(lastSubmitted)}><Printer size={16} /> Print CN Packet Slip</button> : null}
            </div>
          </form>
          <article className="panel">
            <h2>Packet reminder</h2>
            <div className="checklist-card">
              <span>[ ] Old Lens/Chashma</span>
              <span>[ ] Order Form</span>
              <span>[ ] Product / Frame / Lens</span>
            </div>
            <p>The app-generated CN Packet Slip replaces the old internal handwritten form. After submit, CN Managers are notified automatically.</p>
          </article>
        </div>
      ) : null}

      {layer === "settings" && canAssign ? (
        <article className="panel form-panel">
          <h2>CN Manager</h2>
          <p>Currently assigned: {cnResponsibleUserIds(state).map((id) => userDisplayName(state, id)).join(", ") || "Not assigned"}</p>
          <div className="store-checks">
            {state.users.filter((item) => item.active !== false).map((item) => (
              <label className="toggle-line" key={item.id}>
                <input
                  type="checkbox"
                  checked={responsibleIds.includes(item.id)}
                  onChange={(event) => {
                    setResponsibleIds((current) =>
                      event.target.checked ? [...new Set([...current, item.id])] : current.filter((id) => id !== item.id)
                    );
                  }}
                />
                {userDisplayName(state, item.id)} - {item.role.replace("_", " ")}
              </label>
            ))}
          </div>
          <button className="primary-button" type="button" onClick={saveResponsible}>Save CN Managers</button>
        </article>
      ) : null}

      {layer === "requests" ? (
        <>
          <div className="cn-status-grid">
            {counts.map(([status, count]) => (
              <button key={status} className={filter.status === status ? "active" : ""} type="button" onClick={() => setFilter({ ...filter, status })}>
                <span>{status}</span>
                <strong>{count}</strong>
              </button>
            ))}
            <button className="danger" type="button" onClick={() => setFilter({ ...filter, status: "all" })}>
              <span>Ageing &gt; 15 days</span>
              <strong>{ageingCount}</strong>
            </button>
          </div>
          <div className="panel filter-strip">
            <Input label="Search order / CN ticket / vendor" value={filter.query} onChange={(query) => setFilter({ ...filter, query })} />
            <label>Status
              <select value={filter.status} onChange={(event) => setFilter({ ...filter, status: event.target.value })}>
                <option value="all">All status</option>
                {CN_STATUSES.map((status) => <option key={status}>{status}</option>)}
              </select>
            </label>
            <label>Store
              <select value={filter.storeId} onChange={(event) => setFilter({ ...filter, storeId: event.target.value })}>
                <option value="all">All stores</option>
                {(canViewAll ? state.stores : state.stores.filter((store) => store.id === autoStoreId || user.storeIds?.includes(store.id))).map((store) => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="detail-list-layout cn-workbench">
            <div className="tap-list">
              {requests.length ? requests.map((row) => (
                <button key={row.id} className={`tap-row ${selectedId === row.id ? "active" : ""}`} type="button" onClick={() => setSelectedId(row.id)}>
                  <span>
                    <strong>{row.cnTicketNumber} · {storeShortName(state, row.storeId)}</strong>
                    <small>{row.previousOrderNo || "-"} → {row.newOrderNo || "-"} · {row.reasonForReturn} · Qty {row.quantity}</small>
                  </span>
                  <em>{row.status}</em>
                  <ChevronRight className="row-chevron" size={17} />
                </button>
              )) : <div className="empty-state">No CN requests yet.</div>}
            </div>
            <div className="detail-card panel">
              {selected ? (
                <CnCaseDetails
                  state={state}
                  request={selected}
                  requestColumns={requestColumns}
                  canProcess={canProcess}
                  officeForm={officeForm}
                  setOfficeForm={setOfficeForm}
                  cnReview={cnReview}
                  setCnReview={setCnReview}
                  handleFile={handleFile}
                  updateRequest={updateRequest}
                  markIncomplete={markIncomplete}
                  saveOfficeDetails={saveOfficeDetails}
                  uploadCnCopy={uploadCnCopy}
                  saveCnReceived={saveCnReceived}
                  updateLedger={updateLedger}
                  closeCn={closeCn}
                  user={user}
                  printPacketSlip={printPacketSlip}
                />
              ) : (
                <p>Select a CN request to process.</p>
              )}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

function CnPhotoInput({ label, value, onFile, required = false }) {
  const url = useStoredFileUrl(value);
  return (
    <label className="file-button cn-file-card">
      <span>{label}{required ? " *" : ""}</span>
      <input type="file" accept="image/*" capture="environment" onChange={(event) => onFile(event.target.files?.[0])} />
      {value && url ? <img src={url} alt={label} /> : <small>{value ? "Loading photo..." : "Tap to add photo"}</small>}
    </label>
  );
}

function CnCaseDetails({
  state,
  request,
  requestColumns,
  canProcess,
  officeForm,
  setOfficeForm,
  cnReview,
  setCnReview,
  handleFile,
  updateRequest,
  markIncomplete,
  saveOfficeDetails,
  uploadCnCopy,
  saveCnReceived,
  updateLedger,
  closeCn,
  user,
  printPacketSlip
}) {
  const checklist = request.packetChecklist || {};
  const setChecklist = async (key, checked) => {
    const next = { ...checklist, [key]: checked };
    next.packetComplete = Boolean(next.oldItemReceived && next.orderFormReceived && next.productMatches);
    await updateRequest(request, { packetChecklist: next });
  };
  const canVerify = canProcess || hasUserPermission(user, "can_verify_cn_packet");
  const canVendorDetails = canProcess || hasUserPermission(user, "can_update_cn_vendor_details");
  const canReceived = canProcess || hasUserPermission(user, "can_update_cn_received");
  const canOverride = canProcess || hasUserPermission(user, "can_override_cn_status");
  return (
    <div className="cn-case-detail">
      <div className="detail-title-row">
        <div>
          <h3>{request.cnTicketNumber}</h3>
          <p>{storeShortName(state, request.storeId)} · CNA {request.cnaNumber} · {request.status}</p>
        </div>
        <button className="secondary-button" type="button" disabled={!hasUserPermission(user, "can_print_cn_packet_slip") && !canProcess && request.createdBy !== user.id} onClick={() => printPacketSlip(request)}>
          <Printer size={16} /> Print CN Packet Slip
        </button>
      </div>
      <DataTable rows={[request]} columns={requestColumns} plain />

      <OpticalSection title="Packet photos" subtitle="Submitted by sales staff" defaultOpen>
        <div className="cn-photo-grid readonly">
          <PhotoPreview src={request.orderReferencePhoto || request["fast" + "SlipPhoto"]} label="CN / order reference" />
          <PhotoPreview src={request.supportingPhoto || request["cn" + "FormPhoto"]} label="Supporting photo" />
          <PhotoPreview src={request.oldItemPhoto} label="Old lens/chashma" />
        </div>
      </OpticalSection>

      <OpticalSection title="Packet verification" subtitle="Check physical packet before vendor processing" defaultOpen>
        <div className="checklist-card">
          {[
            ["oldItemReceived", "Old lens/chashma received"],
            ["orderFormReceived", "Order Form received"],
            ["productMatches", "Product matches CN request"]
          ].map(([key, label]) => (
            <label className="toggle-line" key={key}>
              <input type="checkbox" disabled={!canVerify} checked={Boolean(checklist[key])} onChange={(event) => setChecklist(key, event.target.checked)} />
              {label}
            </label>
          ))}
        </div>
        <div className="form-grid">
          <Input label="Missing item reason" value={officeForm.missingItemReason || request.missingItemReason || ""} onChange={(missingItemReason) => setOfficeForm({ ...officeForm, missingItemReason })} />
        </div>
        <div className="button-row">
          <button className="secondary-button danger" type="button" disabled={!canVerify} onClick={markIncomplete}>Mark Packet Incomplete</button>
          <button className="primary-button" type="button" disabled={!canVerify || !checklist.packetComplete} onClick={() => updateRequest(request, { status: "Vendor Pickup Awaited" })}>Move to Vendor Pickup Awaited</button>
        </div>
      </OpticalSection>

      <OpticalSection title="Vendor / company processing" subtitle="Office CN desk updates after packet verification">
        <div className="form-grid">
          <Input label="Vendor / company" value={officeForm.vendorName || ""} onChange={(vendorName) => setOfficeForm({ ...officeForm, vendorName })} />
          <Input label="Vendor reference no" value={officeForm.vendorReferenceNo || ""} onChange={(vendorReferenceNo) => setOfficeForm({ ...officeForm, vendorReferenceNo })} />
          <Input label="DC / LRF / DN / company form no" value={officeForm.companyFormNo || ""} onChange={(companyFormNo) => setOfficeForm({ ...officeForm, companyFormNo })} />
          <Input label="Product / lens details" value={officeForm.productNameAsPerDc || ""} onChange={(productNameAsPerDc) => setOfficeForm({ ...officeForm, productNameAsPerDc })} />
          <Input label="Pickup / dispatch date" type="date" value={officeForm.dispatchDate || ""} onChange={(dispatchDate) => setOfficeForm({ ...officeForm, dispatchDate })} />
          <Input label="Collected by / sent through" value={officeForm.collectedBy || ""} onChange={(collectedBy) => setOfficeForm({ ...officeForm, collectedBy })} />
          <Input label="Sent through" value={officeForm.sentThrough || ""} onChange={(sentThrough) => setOfficeForm({ ...officeForm, sentThrough })} />
          <Input label="Amount exclusive tax" type="number" value={officeForm.amountExclusiveTax || ""} onChange={(amountExclusiveTax) => setOfficeForm({ ...officeForm, amountExclusiveTax })} />
        </div>
        <label>Remarks
          <textarea value={officeForm.remarks || ""} onChange={(event) => setOfficeForm({ ...officeForm, remarks: event.target.value })} />
        </label>
        <div className="cn-photo-grid">
          <CnPhotoInput label="Vendor return form photo" value={officeForm.vendorFormPhoto} onFile={(file) => handleFile("vendorFormPhoto", file, setOfficeForm)} />
        </div>
        <div className="button-row">
          <button className="secondary-button" type="button" disabled={!canVendorDetails} onClick={() => saveOfficeDetails("Vendor Identified")}>Save vendor details</button>
          <button className="primary-button" type="button" disabled={!canVendorDetails} onClick={() => saveOfficeDetails("Sent to Vendor / Company")}>Sent to vendor/company</button>
          <button className="primary-button" type="button" disabled={!canVendorDetails} onClick={() => saveOfficeDetails("CN Awaited")}>CN Awaited</button>
        </div>
      </OpticalSection>

      <OpticalSection title="CN received and ledger" subtitle="Upload CN photo if available, or enter details manually. Then update ledger and close.">
        <div className="cn-photo-grid">
          <CnPhotoInput label="Printed CN copy photo" value={cnReview.cnCopyPhoto} onFile={uploadCnCopy} />
        </div>
        <div className="form-grid">
          <Input label="CN number" value={cnReview.cnNumber || ""} onChange={(cnNumber) => setCnReview({ ...cnReview, cnNumber })} />
          <Input label="CN received date" type="date" value={cnReview.cnDate || ""} onChange={(cnDate) => setCnReview({ ...cnReview, cnDate })} />
          <Input label="CN amount" type="number" value={cnReview.cnAmount || ""} onChange={(cnAmount) => setCnReview({ ...cnReview, cnAmount })} />
          <Input label="Settlement amount" type="number" value={cnReview.settlementAmount || ""} onChange={(settlementAmount) => setCnReview({ ...cnReview, settlementAmount })} />
          <Input label="Short / difference amount" type="number" value={cnReview.differenceAmount || ""} onChange={(differenceAmount) => setCnReview({ ...cnReview, differenceAmount })} />
          <Input label="Vendor name" value={cnReview.vendorName || ""} onChange={(vendorName) => setCnReview({ ...cnReview, vendorName })} />
        </div>
        <label>CN received remarks
          <textarea value={cnReview.remarks || ""} onChange={(event) => setCnReview({ ...cnReview, remarks: event.target.value })} />
        </label>
        <div className="button-row">
          <button className="secondary-button" type="button" disabled={!canReceived} onClick={saveCnReceived}>Save CN received</button>
          <button className="secondary-button" type="button" disabled={!canProcess && !hasUserPermission(user, "can_update_cn_ledger")} onClick={updateLedger}>Update ledger</button>
          <button className="primary-button" type="button" disabled={!canProcess && !hasUserPermission(user, "can_close_cn")} onClick={closeCn}>Close CN</button>
        </div>
      </OpticalSection>

      {canOverride ? (
        <OpticalSection title="Director / CEO override" subtitle="Use only when status needs manual correction.">
          <div className="form-grid">
            <label>Status
              <select value={request.status} onChange={(event) => updateRequest(request, { status: event.target.value })}>
                {CN_STATUSES.map((status) => <option key={status}>{status}</option>)}
              </select>
            </label>
          </div>
        </OpticalSection>
      ) : null}
    </div>
  );
}

const CODE39 = {
  "0": "101001101101",
  "1": "110100101011",
  "2": "101100101011",
  "3": "110110010101",
  "4": "101001101011",
  "5": "110100110101",
  "6": "101100110101",
  "7": "101001011011",
  "8": "110100101101",
  "9": "101100101101",
  A: "110101001011",
  B: "101101001011",
  C: "110110100101",
  D: "101011001011",
  E: "110101100101",
  F: "101101100101",
  G: "101010011011",
  H: "110101001101",
  I: "101101001101",
  J: "101011001101",
  K: "110101010011",
  L: "101101010011",
  M: "110110101001",
  N: "101011010011",
  O: "110101101001",
  P: "101101101001",
  Q: "101010110011",
  R: "110101011001",
  S: "101101011001",
  T: "101011011001",
  U: "110010101011",
  V: "100110101011",
  W: "110011010101",
  X: "100101101011",
  Y: "110010110101",
  Z: "100110110101",
  "-": "100101011011",
  ".": "110010101101",
  " ": "100110101101",
  "*": "100101101101"
};

function code39Bars(value) {
  const text = `*${String(value || "").toUpperCase().replace(/[^A-Z0-9 .-]/g, "")}*`;
  return text.split("").flatMap((char, charIndex) => {
    const pattern = CODE39[char] || CODE39["-"];
    const bars = pattern.split("").map((bit, index) => (
      `<span style="display:inline-block;width:${bit === "1" ? 2 : 1}px;height:42px;background:${index % 2 === 0 ? "#111" : "transparent"}"></span>`
    ));
    return charIndex === text.length - 1 ? bars : [...bars, '<span style="display:inline-block;width:2px;height:42px"></span>'];
  }).join("");
}

function printCnPacketSlip(request, state) {
  const store = state.stores.find((item) => item.id === request.storeId);
  const createdAt = request.createdAt ? new Date(request.createdAt).toLocaleString("en-IN") : "-";
  const html = `<!doctype html>
    <html>
      <head>
        <title>${request.cnTicketNumber}</title>
        <style>
          @page { size: 58mm auto; margin: 4mm; }
          body { width: 50mm; margin: 0; font: 12px Arial, sans-serif; color: #111; }
          h1 { font-size: 14px; margin: 0 0 6px; text-align: center; }
          p { margin: 2px 0; }
          .line { border-top: 1px dashed #111; margin: 6px 0; }
          .barcode { margin: 7px 0 2px; text-align: center; white-space: nowrap; overflow: hidden; }
          .ticket { text-align: center; font-weight: 700; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>FRAMESBAY CN PACKET</h1>
        <p><b>CN Ticket No.:</b> ${request.cnTicketNumber || "-"}</p>
        <p><b>Store:</b> ${store?.code || store?.name || "-"}</p>
        <p><b>Created By:</b> ${request.createdByName || "-"}</p>
        <p><b>Created:</b> ${createdAt}</p>
        <p><b>Prev Order:</b> ${request.previousOrderNo || "-"}</p>
        <p><b>New Order:</b> ${request.newOrderNo || "-"}</p>
        <p><b>Reason:</b> ${request.reasonForReturn || "-"}</p>
        <p><b>Product Type:</b> ${request.productType || "Lens"}</p>
        <p><b>Qty:</b> ${request.quantity || "-"}</p>
        <p><b>Remarks:</b> ${request.complaintNote || request.productDetails || "-"}</p>
        <div class="line"></div>
        <p><b>Packet Checklist:</b></p>
        <p>[ ] Old Lens/Chashma</p>
        <p>[ ] Order Form</p>
        <p>[ ] Product / Frame / Lens</p>
        <div class="barcode">${code39Bars(request.cnTicketNumber)}</div>
        <div class="ticket">${request.cnTicketNumber}</div>
        <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
      </body>
    </html>`;
  const printWindow = window.open("", "_blank", "width=360,height=640");
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
}

function printOrderFormSlip(order, state) {
  const store = state.stores.find((item) => item.id === order.storeId);
  const pairs = order.orderDetails?.opticalPairs?.length
    ? order.orderDetails.opticalPairs
    : [{
        frameDetails: order.orderDetails?.frameDetails || "",
        lensDetails: order.orderDetails?.lensDetails || "",
        notes: order.orderDetails?.specialNotes || ""
      }];
  const rightDistance = order.power?.rightEye?.distance || {};
  const leftDistance = order.power?.leftEye?.distance || {};
  const rightAdd = order.power?.rightEye?.add || {};
  const leftAdd = order.power?.leftEye?.add || {};
  const measurements = order.power?.measurements || {};
  const products = order.orderDetails?.products || order.orderDetails?.extraProducts || [];
  const productLines = products.length
    ? products.map((product, index) => `
      <p><b>Item ${index + 1}:</b> ${opticalProductLabel(product.type)} - ${product.name || "-"}</p>
      <p><b>Barcode:</b> ${product.barcode || "-"}</p>
      <p><b>Qty:</b> ${product.quantity || 1} <b>Amount:</b> ${product.amount || "-"}</p>
      ${product.tintRequired ? `<p><b>Tint:</b> ${product.tintAsSample ? "As per sample" : "Required"}</p>` : ""}
    `).join("")
    : pairs.map((pair, index) => `
      <p><b>Item ${index + 1} Frame:</b> ${pair.frameDetails || pair.frameStatus || "-"}</p>
      <p><b>Item ${index + 1} Lens:</b> ${pair.lensDetails || pair.lensStatus || "-"}</p>
      <p><b>Tint:</b> ${pair.tintRequired ? pair.tintAsSample ? "As per sample" : "Required" : "No"}</p>
    `).join("");
  const html = `<!doctype html>
    <html>
      <head>
        <title>${order.orderNumber || "Order Form"}</title>
        <style>
          @page { size: 80mm auto; margin: 4mm; }
          body { width: 72mm; margin: 0; font: 12px Arial, sans-serif; color: #111; }
          h1 { font-size: 15px; margin: 0 0 6px; text-align: center; }
          h2 { font-size: 12px; margin: 7px 0 3px; border-top: 1px dashed #111; padding-top: 5px; }
          p { margin: 2px 0; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; }
          .barcode { margin: 7px 0 2px; text-align: center; white-space: nowrap; overflow: hidden; }
          .ticket { text-align: center; font-weight: 700; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>FRAMESBAY ORDER FORM</h1>
        <p><b>Order No.:</b> ${order.orderNumber || "-"}</p>
        <p><b>Store:</b> ${store?.code || store?.name || "-"}</p>
        <p><b>Customer:</b> ${order.customer?.name || order.customer?.walkInId || "-"}</p>
        <p><b>Order date:</b> ${order.date || "-"}</p>
        <p><b>Delivery date:</b> ${order.deliveryDate || "-"}</p>
        <h2>Frame / Lens</h2>
        ${productLines}
        <p><b>Lens type:</b> ${order.orderDetails?.lensDetails || "-"}</p>
        <p><b>Lens brand:</b> ${order.orderDetails?.products?.find((product) => product.type === "lens")?.name || "-"}</p>
        <p><b>Index / Coating:</b> ${order.orderDetails?.pairSummary || "-"}</p>
        <h2>Right Eye Power</h2>
        <div class="grid">
          <p>SPH: ${rightDistance.sph || "-"}</p><p>CYL: ${rightDistance.cyl || "-"}</p>
          <p>AXIS: ${rightDistance.ax || "-"}</p><p>ADD: ${rightAdd.sph || "-"}</p>
          <p>PD: ${measurements.pd || "-"}</p><p>FH: ${measurements.fh || "-"}</p>
        </div>
        <h2>Left Eye Power</h2>
        <div class="grid">
          <p>SPH: ${leftDistance.sph || "-"}</p><p>CYL: ${leftDistance.cyl || "-"}</p>
          <p>AXIS: ${leftDistance.ax || "-"}</p><p>ADD: ${leftAdd.sph || "-"}</p>
          <p>PD: ${measurements.pd || "-"}</p><p>FH: ${measurements.fh || "-"}</p>
        </div>
        <h2>Notes</h2>
        <p><b>Remarks:</b> ${order.orderDetails?.specialNotes || "-"}</p>
        <p><b>Salesperson:</b> ${order.salespersonName || "-"}</p>
        <p><b>Optometrist:</b> ${order.power?.prescribedBy || "-"}</p>
        <div class="barcode">${code39Bars(order.orderNumber || order.id)}</div>
        <div class="ticket">${order.orderNumber || order.id}</div>
        <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
      </body>
    </html>`;
  const printWindow = window.open("", "_blank", "width=420,height=720");
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
}

function CompanyLedgerPanel({ state }) {
  const rows = state.stores.map((store) => {
    const orders = (state.opticalFastSlips || []).filter((order) => order.storeId === store.id);
    const receivable = orders.reduce((sum, order) => sum + orderBalance(order), 0);
    const sales = orders.reduce((sum, order) => sum + orderTotal(order), 0);
    const purchases = (state.crmPurchases || []).filter((purchase) => purchase.storeId === store.id).reduce((sum, row) => sum + Number(row.totalAmount || 0), 0);
    return { id: store.id, store, sales, receivable, payable: purchases };
  });
  return (
    <section>
      <Header title="Company Ledger" subtitle="Company-wise ledger, receivables, payables and balances." />
      <article className="panel dashboard-detail">
        <DetailList
          rows={rows}
          getTitle={(row) => row.store.name}
          getMeta={(row) => `Sales ${currency(row.sales)} · Receivable ${currency(row.receivable)}`}
          getStatus={(row) => currency(row.receivable - row.payable)}
          renderDetails={(row) => (
            <div className="detail-fields">
              <h3>{row.store.name}</h3>
              <p><strong>Total sales:</strong> {currency(row.sales)}</p>
              <p><strong>Receivable:</strong> {currency(row.receivable)}</p>
              <p><strong>Payable:</strong> {currency(row.payable)}</p>
              <p><strong>Balance:</strong> {currency(row.receivable - row.payable)}</p>
            </div>
          )}
        />
      </article>
    </section>
  );
}

function Header({ title, subtitle, children }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function LatestUpdateBanner() {
  const storageKey = `sparkingcircle-update-log-${APP_UPDATE_LOG.version}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== "dismissed";
    } catch {
      return true;
    }
  });
  if (!open) {
    return (
      <button className="latest-update-chip" type="button" onClick={() => setOpen(true)}>
        What changed in latest update?
      </button>
    );
  }
  return (
    <article className="latest-update-banner">
      <div>
        <strong>{APP_UPDATE_LOG.title}</strong>
        <small>Version {APP_UPDATE_LOG.version}</small>
      </div>
      <ul>
        {APP_UPDATE_LOG.changes.map((change) => <li key={change}>{change}</li>)}
      </ul>
      <button
        type="button"
        onClick={() => {
          try {
            localStorage.setItem(storageKey, "dismissed");
          } catch {
            /* ignore storage failures */
          }
          setOpen(false);
        }}
      >
        Hide
      </button>
    </article>
  );
}

function InstallAppButton() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  const install = async () => {
    if (promptEvent) {
      promptEvent.prompt();
      await promptEvent.userChoice;
      setPromptEvent(null);
      return;
    }
    setMessage("On Android Chrome: tap ⋮ menu, then Add to Home screen or Install app.");
  };

  return (
    <span className="install-area">
      <button type="button" onClick={install}>Install app</button>
      {message ? <small>{message}</small> : null}
    </span>
  );
}

function NotificationBell({ notifications, user, onOpen, onMarkRead, onMarkAllRead }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const panelRef = useRef(null);
  const canSeeAdminNotifications = isSuperAdminUser(user);
  const myNotificationsAll = (notifications || [])
    .filter((notification) => notification.userId === user.id || notification.userId === user.authUserId || canSeeAdminNotifications)
    .filter((notification, index, rows) => {
      if (!canSeeAdminNotifications) return true;
      const key = `${notification.title || ""}|${notification.message || ""}|${notification.entityType || notification.linkedRecordType || ""}|${notification.entityId || notification.linkedRecordId || ""}|${notification.createdAt || ""}`;
      return rows.findIndex((row) => `${row.title || ""}|${row.message || ""}|${row.entityType || row.linkedRecordType || ""}|${row.entityId || row.linkedRecordId || ""}|${row.createdAt || ""}` === key) === index;
    })
    .slice()
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  const unreadCount = myNotificationsAll.filter((notification) => !notification.isRead).length;
  const myNotifications = myNotificationsAll
    .filter((notification) => {
      if (filter === "unread") return !notification.isRead;
      if (filter === "critical") return ["critical", "high"].includes(notification.priority);
      return true;
    })
    .slice(0, 60);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div className={`notification-wrap ${open ? "notification-open" : ""}`} ref={panelRef}>
      <button className="notification-bell" type="button" onClick={() => setOpen((value) => !value)} aria-label="Notifications">
        <Bell size={18} />
        {unreadCount ? <span>{unreadCount}</span> : null}
      </button>
      {open ? (
        <div className="notification-panel">
          <div className="notification-head">
            <span>
              <strong>Notifications</strong>
              <small>{unreadCount} unread · newest first</small>
            </span>
            <button className="icon-only" type="button" aria-label="Close notifications" onClick={() => setOpen(false)}>
              <XCircle size={18} />
            </button>
          </div>
          <div className="notification-filter-row">
            {[
              ["all", "All"],
              ["unread", "Unread"],
              ["critical", "Critical"]
            ].map(([key, label]) => (
              <button key={key} className={filter === key ? "active" : ""} type="button" onClick={() => setFilter(key)}>
                {label}
              </button>
            ))}
          </div>
          <div className="notification-actions">
            <button className="mini-button" type="button" disabled={!unreadCount} onClick={() => onMarkAllRead?.()}>
              Mark all as read
            </button>
            <button className="mini-button" type="button" onClick={() => setFilter("all")}>
              View all notifications
            </button>
          </div>
          {myNotifications.length ? (
            myNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${notification.isRead ? "" : "unread"}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpen(notification);
                  }}
                >
                  <span>
                    <strong>{notification.title}</strong>
                    <small>
                      {notification.module || notification.type || "App"} · {notification.createdAt ? new Date(notification.createdAt).toLocaleString("en-IN") : ""}
                      {notification.storeName ? ` · ${notification.storeName}` : ""}
                    </small>
                    <em>{notification.message}</em>
                  </span>
                  {["high", "critical"].includes(notification.priority) ? (
                    <b className={`priority-badge ${notification.priority}`}>{notification.priority}</b>
                  ) : null}
                </button>
                {!notification.isRead ? (
                  <button className="link-button notification-read-button" type="button" onClick={() => onMarkRead?.(notification)}>
                    Mark as read
                  </button>
                ) : null}
              </div>
            ))
          ) : (
            <p className="empty-state compact">
              {myNotificationsAll.length ? "No notifications match this filter." : "No notifications yet for this login."}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AttendancePhotoThumb({ path, name }) {
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setUrl("");
    if (!path) return () => {
      active = false;
    };
    getAttendancePhotoUrl(path)
      .then((photoUrl) => {
        if (active) setUrl(photoUrl);
      })
      .catch(() => {
        if (active) setUrl("");
      });
    return () => {
      active = false;
    };
  }, [path]);

  if (!url) {
    return <i className={`row-avatar ${avatarTone(name)}`}>{initials(name)}</i>;
  }

  return (
    <>
      <img
        className="row-photo"
        src={url}
        alt={`${name} attendance proof`}
        loading="lazy"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      />
      {open && typeof document !== "undefined" ? createPortal(
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setOpen(false)}>
          <div className="modal photo-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <strong>{name}</strong>
                <small>Attendance proof photo</small>
              </div>
              <button className="icon-only" type="button" onClick={() => setOpen(false)} aria-label="Close photo">
                <XCircle size={18} />
              </button>
            </div>
            <img className="proof-photo-large" src={url} alt={`${name} attendance proof enlarged`} />
          </div>
        </div>,
        document.body
      ) : null}
    </>
  );
}

function DetailList({ rows, getTitle, getMeta, getStatus, getPhotoPath, getRowClass, renderDetails }) {
  const [selectedId, setSelectedId] = useState("");
  const selectedRow = rows.find((row) => String(row.id || row.employeeId) === selectedId);

  useEffect(() => {
    if (selectedId && !rows.some((row) => String(row.id || row.employeeId) === selectedId)) {
      setSelectedId("");
    }
  }, [rows, selectedId]);

  return (
    <div className="detail-list-layout">
      <div className="tap-list">
        {rows.length ? (
          rows.map((row) => {
            const id = String(row.id || row.employeeId);
            const title = getTitle(row);
            return (
              <button
                key={id}
                className={["tap-row", selectedId === id ? "active" : "", getRowClass?.(row) || ""].filter(Boolean).join(" ")}
                type="button"
                onClick={() => setSelectedId(id)}
              >
                <AttendancePhotoThumb path={getPhotoPath?.(row)} name={title} />
                <span>
                  <strong>{title}</strong>
                  <small>{getMeta(row)}</small>
                </span>
                {getStatus ? <em>{getStatus(row)}</em> : null}
                <ChevronRight className="row-chevron" size={17} />
              </button>
            );
          })
        ) : (
          <div className="empty-state">No records yet.</div>
        )}
      </div>
      <div className="detail-card panel">
        {selectedRow ? renderDetails(selectedRow) : <p>Select a name to see full details.</p>}
      </div>
    </div>
  );
}

function UsersPanel({ state, updateState, employees, user, addAudit }) {
  const [usersLayer, setUsersLayer] = useState("people");
  const layers = [
    ["people", "Directory"],
    ["setup", "Salary setup"],
    ["logins", "Logins"]
  ];

  return (
    <section>
      <Header title="Users" subtitle="Manage employees, admins, salary details, shift rules, weekly offs and login IDs in one place." />
      <div className="layer-tabs" role="tablist" aria-label="User management sections">
        {layers.map(([key, label]) => (
          <button key={key} className={usersLayer === key ? "active" : ""} type="button" onClick={() => setUsersLayer(key)}>
            {label}
          </button>
        ))}
      </div>
      {usersLayer === "people" ? (
        <EmployeesPanel state={state} updateState={updateState} employees={employees} user={user} addAudit={addAudit} embedded />
      ) : null}
      {usersLayer === "setup" ? <UserSetupOverview state={state} employees={employees} /> : null}
      {usersLayer === "logins" ? (
        <AdminUsersPanel state={state} updateState={updateState} user={user} employees={employees} embedded />
      ) : null}
    </section>
  );
}

function UserSetupOverview({ state, employees }) {
  const exportPrefix = filePrefix(state.settings);
  const managerName = (employeeId) => state.employees.find((employee) => employee.id === employeeId)?.fullName || "No manager";
  const setupColumns = [
    ["Employee", (row) => row.fullName],
    ["Designation", (row) => row.designation || "-"],
    ["Reporting manager", (row) => managerName(row.reportingManagerId)],
    ["App role", (row) => {
      const login = state.users.find((item) => item.employeeId === row.id);
      return login ? login.role.replace("_", " ") : "No login";
    }],
    ["Current salary", (row) => {
      const salary = latestEffectiveRow(state.salaryHistory.filter((item) => item.employeeId === row.id));
      return currency(salary?.monthlySalary ?? row.monthlySalary);
    }],
    ["Salary from", (row) => latestEffectiveRow(state.salaryHistory.filter((item) => item.employeeId === row.id))?.effectiveDate || row.joiningDate],
    ["Weekly off", (row) => latestEffectiveRow(state.weeklyOffHistory.filter((item) => item.employeeId === row.id))?.weeklyOffDay || row.weeklyOffDay || "-"],
    ["Shift", (row) => {
      const shift = latestEffectiveRow(state.shiftHistory.filter((item) => item.employeeId === row.id));
      return shift ? `${shift.startTime}-${shift.endTime}` : `${state.settings.defaultShiftStart}-${state.settings.defaultShiftEnd}`;
    }],
    ["Incentive rules", (row) => {
      const rules = state.incentiveRuleHistory
        .filter((rule) => rule.employeeId === row.id && rule.active !== false)
        .sort((a, b) => Number(a.threshold || 0) - Number(b.threshold || 0));
      if (!row.incentiveEligible) return "Not eligible";
      if (!rules.length) return "No rule added";
      return rules
        .map((rule) => `${currency(rule.threshold)}: ${percent(Number(rule.rate || 0) * 100)}${Number(rule.splitPercent ?? 100) !== 100 ? `, split ${percent(rule.splitPercent)}` : ""}`)
        .join(" | ");
    }]
  ];

  return (
    <section className="panel-section">
      <div className="section-title-row">
        <div>
          <h2>Salary and incentive setup</h2>
          <p>This shows what is currently saved for every employee.</p>
        </div>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-user-salary-incentive-setup.csv`, employees, setupColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </div>
      <DataTable rows={employees} columns={setupColumns} />
    </section>
  );
}

function EmployeesPanel({ state, updateState, employees, user, addAudit, embedded = false }) {
  const exportPrefix = filePrefix(state.settings);
  const [form, setForm] = useState({ ...emptyEmployee, storeId: state.stores[0]?.id || "" });
  const [loginForm, setLoginForm] = useState({
    createLogin: true,
    role: "employee",
    businessRoleKey: "store_staff",
    loginId: "",
    password: ""
  });
  const [loginTouched, setLoginTouched] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    startTime: state.settings.defaultShiftStart,
    endTime: state.settings.defaultShiftEnd,
    fullDayOutBy: state.settings.fullDayOutBy,
    halfDayCutoff: state.settings.halfDayCutoff,
    graceMinutes: state.settings.graceMinutes
  });
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("Admin update");
  const [saveStatus, setSaveStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const currentLoginUser = form.id ? state.users.find((item) => item.employeeId === form.id) : null;
  const managerName = (employeeId) => state.employees.find((employee) => employee.id === employeeId)?.fullName || "No reporting manager";
  const normalizedEmployeeSearch = employeeSearch.trim().toLowerCase();
  const displayedEmployees = (showArchived ? employees : employees.filter((employee) => employee.active !== false))
    .filter((employee) => {
      if (!normalizedEmployeeSearch) return true;
      const store = state.stores.find((item) => item.id === employee.storeId);
      return [
        employee.fullName,
        employee.employeeCode,
        employee.phone,
        employee.email,
        employee.designation,
        store?.name,
        store?.code,
        managerName(employee.reportingManagerId)
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedEmployeeSearch);
    });
  const reportingManagerOptions = state.users
    .filter((item) => item.active !== false && item.employeeId)
    .map((item) => state.employees.find((employee) => employee.id === item.employeeId))
    .filter((employee) => employee && employee.id !== form.id);

  const applyShiftPreset = (preset) => {
    if (preset === "fixed") {
      setShiftForm({
        startTime: state.settings.defaultShiftStart,
        endTime: state.settings.defaultShiftEnd,
        fullDayOutBy: state.settings.fullDayOutBy,
        halfDayCutoff: state.settings.halfDayCutoff,
        graceMinutes: state.settings.graceMinutes
      });
    }
    if (preset === "part_time") {
      setShiftForm({
        startTime: "12:00",
        endTime: "18:00",
        fullDayOutBy: "18:00",
        halfDayCutoff: "15:00",
        graceMinutes: 15
      });
    }
    if (preset === "no_timing") {
      setShiftForm({
        startTime: "00:00",
        endTime: "23:59",
        fullDayOutBy: "00:00",
        halfDayCutoff: "23:59",
        graceMinutes: 1440
      });
    }
  };

  useEffect(() => {
    if (!form.id) {
      setShiftForm({
        startTime: state.settings.defaultShiftStart,
        endTime: state.settings.defaultShiftEnd,
        fullDayOutBy: state.settings.fullDayOutBy,
        halfDayCutoff: state.settings.halfDayCutoff,
        graceMinutes: state.settings.graceMinutes
      });
      return;
    }
    const latestShift = state.shiftHistory
      .filter((item) => item.employeeId === form.id)
      .slice()
      .sort((a, b) => String(b.effectiveDate).localeCompare(String(a.effectiveDate)))[0];
    setShiftForm({
      startTime: latestShift?.startTime || state.settings.defaultShiftStart,
      endTime: latestShift?.endTime || state.settings.defaultShiftEnd,
      fullDayOutBy: latestShift?.fullDayOutBy || state.settings.fullDayOutBy,
      halfDayCutoff: latestShift?.halfDayCutoff || state.settings.halfDayCutoff,
      graceMinutes: Number(latestShift?.graceMinutes ?? state.settings.graceMinutes)
    });
  }, [form.id, state.settings, state.shiftHistory]);

  useEffect(() => {
    if (currentLoginUser) {
      setLoginForm((current) => ({
        ...current,
        createLogin: true,
        role: currentLoginUser.role,
        businessRoleKey: businessRolePresetByKey(currentLoginUser.permissions?.__businessRoleKey)?.key || "",
        loginId: displayLoginId(currentLoginUser.email),
        password: ""
      }));
      setLoginTouched(true);
      return;
    }
    if (!loginTouched) {
      setLoginForm((current) => ({
        ...current,
        loginId: suggestedEmployeeLogin(form)
      }));
    }
  }, [currentLoginUser?.id, form.fullName, form.phone, form.email, loginTouched]);

  const resetEmployeeForm = ({ keepStatus = false } = {}) => {
    setForm({ ...emptyEmployee, storeId: state.stores[0]?.id || "" });
    setLoginForm({ createLogin: true, role: "employee", businessRoleKey: "store_staff", loginId: "", password: "" });
    setLoginTouched(false);
    if (!keepStatus) setSaveStatus("");
  };

  const editEmployee = (employee) => {
    const login = state.users.find((item) => item.employeeId === employee.id);
    setForm(employee);
    setLoginForm({
      createLogin: true,
      role: login?.role || "employee",
      businessRoleKey: businessRolePresetByKey(login?.permissions?.__businessRoleKey)?.key || "",
      loginId: login ? displayLoginId(login.email) : suggestedEmployeeLogin(employee),
      password: ""
    });
    setLoginTouched(Boolean(login));
  };

  const saveEmployee = async () => {
    setSaving(true);
    setSaveStatus("Saving employee...");
    try {
      const existing = state.employees.find((employee) => employee.id === form.id);
      const employee = {
        ...form,
        id: form.id || uuid("emp"),
        email: String(form.email || "").trim() || null,
        phone: String(form.phone || "").trim(),
        storeId: form.storeId || null,
        exitDate: form.exitDate || null,
        reportingManagerId: form.reportingManagerId || null,
        monthlySalary: Number(form.monthlySalary || 0),
        openingLeaveBalance: Number(form.openingLeaveBalance || 0),
        openingLoanBalance: Number(form.openingLoanBalance || 0)
      };
      if (!employee.employeeCode || !employee.fullName || !employee.joiningDate) {
        throw new Error("Employee ID, full name and joining date are required.");
      }
      const duplicateCode = state.employees.find(
        (item) => item.id !== employee.id && String(item.employeeCode || "").trim().toLowerCase() === String(employee.employeeCode || "").trim().toLowerCase()
      );
      if (duplicateCode) {
        throw new Error(`Employee ID "${employee.employeeCode}" is already used by ${duplicateCode.fullName}. Please use a new ID.`);
      }
      if (employee.reportingManagerId && employee.reportingManagerId === employee.id) {
        throw new Error("An employee cannot report to himself or herself.");
      }
      const savedEmployee = await persistRequiredEntity("employees", employee, "Employee");

      const nextState = { ...state };
      nextState.employees = existing
        ? state.employees.map((item) => (item.id === savedEmployee.id ? savedEmployee : item))
        : [...state.employees, savedEmployee];

      const historyAdds = [];
      if (!existing || Number(existing.monthlySalary) !== Number(employee.monthlySalary)) {
        historyAdds.push(["salaryHistory", "salary_history", { employeeId: employee.id, monthlySalary: employee.monthlySalary }]);
      }
      if (!existing || existing.storeId !== employee.storeId) {
        historyAdds.push(["storeHistory", "employee_store_history", { employeeId: employee.id, storeId: employee.storeId }]);
      }
      if (!existing || existing.weeklyOffDay !== employee.weeklyOffDay) {
        historyAdds.push(["weeklyOffHistory", "weekly_off_history", { employeeId: employee.id, weeklyOffDay: employee.weeklyOffDay }]);
      }
      const latestShift = state.shiftHistory
        .filter((item) => item.employeeId === employee.id)
        .slice()
        .sort((a, b) => String(b.effectiveDate).localeCompare(String(a.effectiveDate)))[0];
      const normalizedShift = {
        employeeId: employee.id,
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        fullDayOutBy: shiftForm.fullDayOutBy,
        halfDayCutoff: shiftForm.halfDayCutoff,
        graceMinutes: Number(shiftForm.graceMinutes || 0)
      };
      const shiftChanged =
        !latestShift ||
        latestShift.startTime !== normalizedShift.startTime ||
        latestShift.endTime !== normalizedShift.endTime ||
        latestShift.fullDayOutBy !== normalizedShift.fullDayOutBy ||
        latestShift.halfDayCutoff !== normalizedShift.halfDayCutoff ||
        Number(latestShift.graceMinutes) !== Number(normalizedShift.graceMinutes);
      if (!existing || shiftChanged) {
        historyAdds.push(["shiftHistory", "shift_history", normalizedShift]);
      }

      for (const [stateKey, tableName, values] of historyAdds) {
        const row = { id: uuid(stateKey), ...values, effectiveDate, reason, changedBy: user.name };
        await persistRequiredEntity(tableName, row, "Employee history");
        nextState[stateKey] = [...nextState[stateKey], row];
      }

      let loginMessage = "";
      if (loginForm.createLogin && loginForm.loginId) {
        const authEmail = loginIdentifierToAuthEmail(loginForm.loginId);
        const existingLogin = state.users.find((item) => item.email === authEmail || item.employeeId === employee.id);
        const loginRole = user.role === "super_admin" ? loginForm.role : "employee";
        const resolvedBusinessRoleKey = loginRole === "super_admin" ? "owner_super_admin" : loginForm.businessRoleKey;
        const businessPreset = businessRolePresetByKey(resolvedBusinessRoleKey);
        const businessPermissions = permissionsForBusinessRole(resolvedBusinessRoleKey);
        const loginPermissions = loginRole === "super_admin"
          ? {
              ...permissionsForBusinessRole("owner_super_admin"),
              __businessRoleKey: businessPreset?.key || "owner_super_admin",
              __businessRoleLabel: businessPreset?.label || "Owner / Super Admin"
            }
          : {
              ...businessPermissions,
              __businessRoleKey: businessPreset?.key || "",
              __businessRoleLabel: businessPreset?.label || "Unassigned"
            };
        const loginUser = {
          id: existingLogin?.id || uuid("user"),
          authUserId: existingLogin?.authUserId || null,
          name: savedEmployee.fullName,
          email: authEmail,
          role: loginRole,
          employeeId: savedEmployee.id,
          storeIds: loginRole === "super_admin" ? state.stores.map((store) => store.id) : [savedEmployee.storeId].filter(Boolean),
          permissions: loginPermissions,
          active: true
        };
        let savedLoginUser = await persistRequiredEntity("users", loginUser, "User login");
        savedLoginUser = await refetchSavedEntity("users", savedLoginUser?.id || loginUser.id, savedLoginUser || loginUser);
        if (loginForm.password) {
          const authResult = await createAuthAccount({ email: authEmail, password: loginForm.password });
          if (authResult.user?.id) {
            loginUser.authUserId = authResult.user.id;
            savedLoginUser = await persistRequiredEntity("users", loginUser, "User login");
            savedLoginUser = await refetchSavedEntity("users", savedLoginUser?.id || loginUser.id, savedLoginUser || loginUser);
          }
          loginMessage = authResult.alreadyExists
            ? ` Login profile saved; auth user may already exist for ${displayLoginId(authEmail)}.`
            : ` Login created for ${displayLoginId(authEmail)}.`;
        } else {
          loginMessage = existingLogin
            ? ` Login details updated for ${displayLoginId(authEmail)}.`
            : " Login ID saved, but password was blank so auth password was not created.";
        }
        nextState.users = existingLogin
          ? nextState.users.map((item) => (item.id === existingLogin.id ? savedLoginUser : item))
          : [...nextState.users, savedLoginUser];
      }

      const log = createAuditLog({
        entityType: "employee",
        entityId: savedEmployee.id,
        action: existing ? "updated" : "created",
        oldValue: existing || null,
        newValue: savedEmployee,
        reason,
        effectiveDate,
        user
      });
      updateState({ ...nextState, auditLogs: [log, ...nextState.auditLogs] });
      await persistAuditLog(log);
      if (
        !existing ||
        existing.designation !== savedEmployee.designation ||
        existing.storeId !== savedEmployee.storeId ||
        Number(existing.monthlySalary || 0) !== Number(savedEmployee.monthlySalary || 0)
      ) {
        await notifySuperAdmins({ ...state, ...nextState }, updateState, {
          module: "Users",
          title: existing?.designation !== savedEmployee.designation ? "User Designation Updated" : (existing ? "Employee Updated" : "Employee Added"),
          message: `${savedEmployee.fullName} ${existing?.designation !== savedEmployee.designation ? `designation changed to ${savedEmployee.designation || "Unassigned"}` : "profile was saved"}.`,
          priority: "normal",
          entityType: "employee",
          entityId: savedEmployee.id,
          storeId: savedEmployee.storeId,
          createdBy: user.id
        });
      }
      setSaveStatus(`Saved successfully.${loginMessage}`);
      if (existing) {
        setForm(savedEmployee);
      } else {
        resetEmployeeForm({ keepStatus: true });
      }
    } catch (error) {
      console.error("Employee save failed", error);
      setSaveStatus(`Could not save. Please try again. ${error.message || ""}`.trim());
    } finally {
      setSaving(false);
    }
  };

  const canDelete = (employeeId) =>
    !state.punches.some((punch) => punch.employeeId === employeeId) &&
    !state.payrollRuns.some((run) => (run.items || []).some((item) => item.employeeId === employeeId));
  const deleteEmployee = async (employee) => {
    setSaving(true);
    setSaveStatus(`Removing ${employee.fullName}...`);
    try {
      const linkedLogin = state.users.find((item) => item.employeeId === employee.id);
      if (linkedLogin?.role === "super_admin" && linkedLogin.id === user.id) {
        setSaveStatus("This is your own Super Admin identity. Edit the details instead of deleting it.");
        return;
      }
      const reportingEmployees = state.employees.filter((item) => item.reportingManagerId === employee.id);
      for (const teamMember of reportingEmployees) {
        await persistRequiredEntity("employees", { ...teamMember, reportingManagerId: null }, "Employee reporting manager");
      }
      if (linkedLogin) {
        await persistRequiredEntity("users", { ...linkedLogin, active: false, employeeId: null }, "Linked login");
      }

      if (canDelete(employee.id)) {
        await deleteEntity("employees", employee.id);
        updateState((current) => ({
          ...current,
          employees: current.employees
            .filter((item) => item.id !== employee.id)
            .map((item) => (item.reportingManagerId === employee.id ? { ...item, reportingManagerId: null } : item)),
          users: linkedLogin
            ? current.users.map((item) => (item.id === linkedLogin.id ? { ...item, active: false, employeeId: null } : item))
            : current.users
        }));
        setSaveStatus(`${employee.fullName} deleted. Linked login was deactivated.`);
        return;
      }

      const archivedEmployee = {
        ...employee,
        active: false,
        exitDate: employee.exitDate || new Date().toISOString().slice(0, 10),
        reportingManagerId: null
      };
      const savedArchivedEmployee = await persistRequiredEntity("employees", archivedEmployee, "Employee archive");
      updateState((current) => ({
        ...current,
        employees: current.employees
          .map((item) => {
            if (item.id === employee.id) return savedArchivedEmployee;
            if (item.reportingManagerId === employee.id) return { ...item, reportingManagerId: null };
            return item;
          }),
        users: linkedLogin
          ? current.users.map((item) => (item.id === linkedLogin.id ? { ...item, active: false, employeeId: null } : item))
          : current.users
      }));
      setSaveStatus(`${employee.fullName} has old attendance/payroll history, so the app archived them and deactivated their login.`);
    } catch (error) {
      setSaveStatus(error.message || "Employee could not be removed.");
    } finally {
      setSaving(false);
    }
  };
  const employeeSheetColumns = [
    ["Employee ID", (row) => row.employeeCode],
    ["Name", (row) => row.fullName],
    ["Phone", (row) => row.phone || ""],
    ["Email", (row) => row.email || ""],
    ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
    ["Designation", (row) => row.designation || ""],
    ["Reporting manager", (row) => managerName(row.reportingManagerId)],
    ["App role", (row) => state.users.find((item) => item.employeeId === row.id)?.role.replace("_", " ") || ""],
    ["Monthly salary", (row) => row.monthlySalary],
    ["Weekly off", (row) => row.weeklyOffDay || ""],
    ["Joining date", (row) => row.joiningDate],
    ["Exit date", (row) => row.exitDate || ""],
    ["Status", (row) => (row.active ? "Active" : "Inactive")]
  ];
  const employeeMeta = (row) => {
    const store = state.stores.find((item) => item.id === row.storeId);
    const login = state.users.find((item) => item.employeeId === row.id);
    return `${row.employeeCode || "No ID"} · ${row.designation || "No designation"} · Reports to ${managerName(row.reportingManagerId)} · ${store?.code || "No store"} · ${login ? login.role.replace("_", " ") : "No login"}`;
  };
  const renderEmployeeDetails = (row) => {
    const store = state.stores.find((item) => item.id === row.storeId);
    const login = state.users.find((item) => item.employeeId === row.id);
    const latestShift = latestEffectiveRow(state.shiftHistory.filter((item) => item.employeeId === row.id));
    return (
      <div className="detail-fields">
        <div className="detail-title-row">
          <div>
            <h3>{row.fullName}</h3>
            <p>{row.designation || "No designation added"}</p>
          </div>
          <span className={`status-pill ${row.active ? "ok" : "danger"}`}>{row.active ? "Active" : "Inactive"}</span>
        </div>
        <p><strong>Employee ID:</strong> {row.employeeCode || "-"}</p>
        <p><strong>Store:</strong> {store?.name || "-"}</p>
        <p><strong>Phone:</strong> {row.phone || "-"}</p>
        <p><strong>Email:</strong> {row.email || "-"}</p>
        <p><strong>Reporting manager:</strong> {managerName(row.reportingManagerId)}</p>
        <p><strong>Login:</strong> {login ? `${displayLoginId(login.email)} (${login.role.replace("_", " ")})` : "Not created"}</p>
        <p><strong>Monthly salary:</strong> {currency(row.monthlySalary)}</p>
        <p><strong>Weekly off:</strong> {row.weeklyOffDay || "-"}</p>
        <p><strong>Shift:</strong> {latestShift ? `${latestShift.startTime}-${latestShift.endTime}` : `${state.settings.defaultShiftStart}-${state.settings.defaultShiftEnd}`}</p>
        <p><strong>Joining:</strong> {row.joiningDate || "-"} {row.exitDate ? `· Exit: ${row.exitDate}` : ""}</p>
        <p><strong>Opening loan:</strong> {currency(row.openingLoanBalance || 0)} · <strong>Opening leave:</strong> {row.openingLeaveBalance || 0}</p>
        <p><strong>Incentive:</strong> {row.incentiveEligible ? "Eligible" : "Not eligible"} · <strong>Reimbursement:</strong> {row.reimbursementEligible ? "Eligible" : "Not eligible"}</p>
        {row.bankDetails ? <p><strong>Bank:</strong> {row.bankDetails}</p> : null}
        {row.notes ? <p><strong>Notes:</strong> {row.notes}</p> : null}
        <div className="button-row">
          <button className="primary-button" type="button" onClick={() => editEmployee(row)}>
            Edit details
          </button>
          <button className="secondary-button danger" type="button" disabled={saving} onClick={() => deleteEmployee(row)}>
            {canDelete(row.id) ? "Delete" : "Archive"}
          </button>
        </div>
        {!canDelete(row.id) ? <p className="helper-text">This employee has history. Archive will remove active access but keep old records safe.</p> : null}
      </div>
    );
  };

  return (
    <section className={embedded ? "panel-section" : ""}>
      {embedded ? (
        <div className="section-title-row">
          <div>
            <h2>Employee details</h2>
            <p>Salary, store, weekly off and shift changes create effective-dated history.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-employees.csv`, employees, employeeSheetColumns)}>
            <FileSpreadsheet size={17} /> Download sheet
          </button>
          <button className="secondary-button" type="button" onClick={() => setShowArchived((value) => !value)}>
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      ) : (
        <Header title="Employees" subtitle="Salary, store, weekly off and shift changes create effective-dated history.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-employees.csv`, employees, employeeSheetColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
        <button className="secondary-button" type="button" onClick={() => setShowArchived((value) => !value)}>
          {showArchived ? "Hide archived" : "Show archived"}
        </button>
        </Header>
      )}
      <div className="split-layout">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>{form.id ? "Edit employee" : "Add employee"}</h2>
          <div className="form-grid">
            <Input label="Employee ID" value={form.employeeCode} onChange={(employeeCode) => setForm({ ...form, employeeCode })} />
            <Input label="Full name" value={form.fullName} onChange={(fullName) => setForm({ ...form, fullName })} />
            <Input
              label="Phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              pattern="[0-9]*"
              maxLength={15}
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone: phone.replace(/\D/g, "") })}
            />
            <Input label="Email" value={form.email} onChange={(email) => setForm({ ...form, email })} />
            <label>
              Store
              <select value={form.storeId || ""} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>
                <option value="">Head office / no assigned store</option>
                {state.stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Designation" value={form.designation} onChange={(designation) => setForm({ ...form, designation })} placeholder="Example: Optometrist, Store Manager" />
            <label>
              Reporting manager
              <select value={form.reportingManagerId || ""} onChange={(event) => setForm({ ...form, reportingManagerId: event.target.value })}>
                <option value="">No reporting manager</option>
                {reportingManagerOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName} - {employee.designation || "User"}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Monthly salary" type="number" value={form.monthlySalary} onChange={(monthlySalary) => setForm({ ...form, monthlySalary })} placeholder="Example: 28300" />
            <label>
              Weekly off
              <select value={form.weeklyOffDay} onChange={(event) => setForm({ ...form, weeklyOffDay: event.target.value })}>
                <option value="">Not confirmed</option>
                {WEEKDAYS.map((day) => (
                  <option key={day}>{day}</option>
                ))}
              </select>
            </label>
            <Input label="Joining date" type="date" value={form.joiningDate} onChange={(joiningDate) => setForm({ ...form, joiningDate })} />
            <Input label="Exit date" type="date" value={form.exitDate} onChange={(exitDate) => setForm({ ...form, exitDate })} />
            <Input label="Opening leave" type="number" value={form.openingLeaveBalance} onChange={(openingLeaveBalance) => setForm({ ...form, openingLeaveBalance: Number(openingLeaveBalance) })} />
            <Input label="Opening loan" type="number" value={form.openingLoanBalance} onChange={(openingLoanBalance) => setForm({ ...form, openingLoanBalance: Number(openingLoanBalance) })} />
            <label>
              Timing type
              <select onChange={(event) => applyShiftPreset(event.target.value)} defaultValue="">
                <option value="" disabled>Choose preset</option>
                <option value="fixed">Fixed shop shift</option>
                <option value="part_time">Part-time / flexible</option>
                <option value="no_timing">No timing required</option>
              </select>
            </label>
            <Input label="Shift start" type="time" value={shiftForm.startTime} onChange={(startTime) => setShiftForm({ ...shiftForm, startTime })} />
            <Input label="Shift end" type="time" value={shiftForm.endTime} onChange={(endTime) => setShiftForm({ ...shiftForm, endTime })} />
            <Input label="Full day out by" type="time" value={shiftForm.fullDayOutBy} onChange={(fullDayOutBy) => setShiftForm({ ...shiftForm, fullDayOutBy })} />
            <Input label="Half day cutoff" type="time" value={shiftForm.halfDayCutoff} onChange={(halfDayCutoff) => setShiftForm({ ...shiftForm, halfDayCutoff })} />
            <Input label="Grace minutes" type="number" value={shiftForm.graceMinutes} onChange={(graceMinutes) => setShiftForm({ ...shiftForm, graceMinutes: Number(graceMinutes) })} />
            <label>
              Effective date
              <input type="date" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
            </label>
            <Input label={form.id ? "Reason for change" : "Setup note"} value={reason} onChange={setReason} />
          </div>
          <div className="embedded-login-card">
            <div>
              <strong>App login</strong>
              <span>Create the employee login ID and password here itself.</span>
            </div>
            <label className="toggle-line">
              <input
                type="checkbox"
                checked={loginForm.createLogin}
                onChange={(event) => setLoginForm({ ...loginForm, createLogin: event.target.checked })}
              />{" "}
              Enable login for this person
            </label>
            {loginForm.createLogin ? (
              <div className="form-grid compact-login-grid">
                <label>
                  Business role preset
                  <select
                    value={loginForm.businessRoleKey || ""}
                    onChange={(event) => {
                      const preset = businessRolePresetByKey(event.target.value);
                      const nextRole = user.role === "super_admin" && preset ? preset.appRole : "employee";
                      setLoginForm({
                        ...loginForm,
                        businessRoleKey: preset?.key || "",
                        role: nextRole
                      });
                      if (!preset) return;
                      setForm((current) => ({
                        ...current,
                        designation: current.designation || preset.designation
                      }));
                    }}
                  >
                    <option value="">Unassigned</option>
                    {BUSINESS_ROLE_PRESETS.map((preset) => (
                      <option key={preset.key} value={preset.key}>{preset.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  App role
                  <select
                    value={loginForm.role}
                    onChange={(event) => {
                      const nextRole = event.target.value;
                      setLoginForm({
                        ...loginForm,
                        role: nextRole,
                        businessRoleKey: nextRole === "super_admin" ? "owner_super_admin" : loginForm.businessRoleKey === "owner_super_admin" ? "store_manager" : loginForm.businessRoleKey
                      });
                    }}
                    disabled={user.role !== "super_admin"}
                  >
                    <option value="employee">Employee</option>
                    <option value="admin">Manager / Admin</option>
                    <option value="super_admin">Owner / Super Admin</option>
                  </select>
                </label>
                <Input
                  label="Login ID / phone"
                  value={loginForm.loginId}
                  onChange={(loginId) => {
                    setLoginTouched(true);
                    setLoginForm({ ...loginForm, loginId });
                  }}
                  placeholder="9876543210 or name@company.in"
                />
                <Input
                  label={currentLoginUser ? "New password optional" : "Temporary password"}
                  type="password"
                  value={loginForm.password}
                  onChange={(password) => setLoginForm({ ...loginForm, password })}
                  placeholder={currentLoginUser ? "Leave blank to keep old password" : "Example: Company@123"}
                />
                <label>
                  Login status
                  <input value={currentLoginUser ? "Login already exists" : "New login will be created"} readOnly />
                </label>
              </div>
            ) : null}
          </div>
          <label className="toggle-line">
            <input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} /> Active
          </label>
          <label className="toggle-line">
            <input type="checkbox" checked={form.incentiveEligible} onChange={(event) => setForm({ ...form, incentiveEligible: event.target.checked })} /> Incentive eligible
          </label>
          <label className="toggle-line">
            <input type="checkbox" checked={form.reimbursementEligible} onChange={(event) => setForm({ ...form, reimbursementEligible: event.target.checked })} /> Reimbursement eligible
          </label>
          <textarea placeholder="Bank details" value={form.bankDetails} onChange={(event) => setForm({ ...form, bankDetails: event.target.value })} />
          <textarea placeholder="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          <p className="helper-text">
            Salary changes use the Effective date above. Example: if salary changes on 20 May, payroll uses old salary before 20 May and new salary from 20 May.
          </p>
          <div className="button-row">
            <button className="primary-button" type="button" disabled={saving} onClick={saveEmployee}>
              {saving ? "Saving..." : "Save employee"}
            </button>
            <button className="secondary-button" type="button" onClick={resetEmployeeForm}>
              Clear
            </button>
          </div>
          {saveStatus ? <p className="notice">{saveStatus}</p> : null}
        </form>
        <div className="directory-panel">
          <div className="panel-title-row">
            <div>
              <h2>Employee directory</h2>
              <p>{displayedEmployees.length} shown · {employees.length} total</p>
            </div>
          </div>
          <div className="staff-search-bar">
            <Search size={17} />
            <input value={employeeSearch} onChange={(event) => setEmployeeSearch(event.target.value)} placeholder="Search employee, store, phone, role" />
            {employeeSearch ? (
              <button type="button" aria-label="Clear search" onClick={() => setEmployeeSearch("")}>
                <XCircle size={17} />
              </button>
            ) : null}
          </div>
          <DetailList
            rows={displayedEmployees}
            getTitle={(row) => row.fullName}
            getMeta={employeeMeta}
            getStatus={(row) => (row.active ? "ACTIVE" : "OFF")}
            renderDetails={renderEmployeeDetails}
          />
        </div>
      </div>
    </section>
  );
}

function StoresPanel({ state, updateState, user }) {
  const exportPrefix = filePrefix(state.settings);
  const [selectedStoreDetailId, setSelectedStoreDetailId] = useState("");
  const [form, setForm] = useState({
    name: "",
    code: "",
    address: "",
    coordinates: "",
    radiusMeters: 150,
    geofenceEnabled: true,
    active: true
  });
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("Store update");
  const [saveStatus, setSaveStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const useCurrentLocation = () => {
    setSaveStatus("Getting current location...");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coordinates = formatCoordinates(position.coords.latitude, position.coords.longitude);
        setForm((current) => ({ ...current, coordinates }));
        setSaveStatus(`Current location added: ${coordinates}`);
      },
      () => setSaveStatus("Location permission was not allowed. You can still paste coordinates manually."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const currentFormCoordinates = parseCoordinates(form.coordinates);
  const activeStores = state.stores.filter((store) => store.active !== false);
  const selectedStoreDetail = activeStores.find((store) => store.id === selectedStoreDetailId);
  const storeMetricRows = activeStores.map((store) => ({
    store,
    metrics: buildStoreMetrics(state, store, state.employees)
  }));

  const saveStore = async () => {
    setSaving(true);
    setSaveStatus("Saving store...");
    try {
      const coordinatesBlank = form.coordinates === "" || form.coordinates == null;
      const parsedCoordinates = parseCoordinates(form.coordinates);
      const radiusBlank = form.radiusMeters === "" || form.radiusMeters == null;
      if (!form.name || !form.code) throw new Error("Store name and store code are required.");
      if (form.geofenceEnabled && (coordinatesBlank || !parsedCoordinates || radiusBlank)) {
        throw new Error("Coordinates and radius are required when geofence is ON. Paste coordinates like 28.631014, 77.435203.");
      }

      const existing = state.stores.find((store) => store.id === form.id);
      const store = {
        ...form,
        id: form.id || uuid("store"),
        name: String(form.name || "").trim(),
        code: String(form.code || "").trim().toUpperCase(),
        address: String(form.address || "").trim(),
        latitude: parsedCoordinates ? parsedCoordinates.latitude : 0,
        longitude: parsedCoordinates ? parsedCoordinates.longitude : 0,
        radiusMeters: radiusBlank ? 0 : Number(form.radiusMeters)
      };
      delete store.coordinates;
      if (!Number.isFinite(store.latitude) || !Number.isFinite(store.longitude) || !Number.isFinite(store.radiusMeters)) {
        throw new Error("Coordinates and radius must be valid numbers.");
      }

      const savedStore = await persistRequiredEntity("stores", store, "Store");
      const geofenceRow = {
        id: uuid("geofence"),
        storeId: savedStore.id,
        latitude: savedStore.latitude,
        longitude: savedStore.longitude,
        radiusMeters: savedStore.radiusMeters,
        geofenceEnabled: savedStore.geofenceEnabled,
        effectiveDate,
        reason,
        changedBy: user.name
      };
      await persistRequiredEntity("geofence_history", geofenceRow, "Store geofence history");
      const log = createAuditLog({
        entityType: "store",
        entityId: savedStore.id,
        action: existing ? "updated" : "created",
        oldValue: existing || null,
        newValue: savedStore,
        reason,
        effectiveDate,
        user
      });
      await persistAuditLog(log);
      updateState((current) => ({
        ...current,
        stores: existing ? current.stores.map((item) => (item.id === savedStore.id ? savedStore : item)) : [...current.stores, savedStore],
        geofenceHistory: [...current.geofenceHistory, geofenceRow],
        auditLogs: [log, ...current.auditLogs]
      }));
      await notifySuperAdmins(state, updateState, {
        module: "Stores",
        title: existing ? "Store Updated" : "Store Added",
        message: `${savedStore.name} was ${existing ? "updated" : "added"}.`,
        priority: "normal",
        entityType: "store",
        entityId: savedStore.id,
        storeId: savedStore.id,
        createdBy: user.id
      });
      setSaveStatus("Store saved.");
      setForm({ name: "", code: "", address: "", coordinates: "", radiusMeters: 150, geofenceEnabled: true, active: true });
    } catch (error) {
      console.error("Store save failed", error);
      setSaveStatus(error.message || "Store could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const storeSheetColumns = [
    ["Code", (row) => row.code],
    ["Name", (row) => row.name],
    ["Address", (row) => row.address || ""],
    ["Coordinates", (row) => formatCoordinates(row.latitude, row.longitude)],
    ["Latitude", (row) => row.latitude],
    ["Longitude", (row) => row.longitude],
    ["Radius meters", (row) => row.radiusMeters],
    ["Geofence", (row) => (row.geofenceEnabled ? "On" : "Off")],
    ["Status", (row) => (row.active ? "Active" : "Inactive")]
  ];

  return (
    <section>
      <Header title="Stores" subtitle="Store-wise sales, collection, staff, orders, bank reco and inventory overview.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-stores.csv`, state.stores, storeSheetColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <div className="store-card-grid">
        {storeMetricRows.map(({ store, metrics }) => (
          <button className="store-overview-card panel" type="button" key={store.id} onClick={() => setSelectedStoreDetailId(store.id)}>
            <div className="store-card-head">
              <strong>{store.name}</strong>
              <span>{store.code}</span>
            </div>
            <div className="store-mini-grid">
              <span>Yesterday Sale <b>{currency(metrics.yesterdaySale)}</b></span>
              <span>Yesterday Collection <b>{currency(metrics.yesterdayCollection)}</b></span>
              <span>Month Sale <b>{currency(metrics.monthSale)}</b></span>
              <span>Month Collection <b>{currency(metrics.monthCollection)}</b></span>
              <span>Staff Present <b>{metrics.staffPresent}</b></span>
              <span>Staff Absent <b>{metrics.staffAbsent}</b></span>
              <span>Orders Due <b>{metrics.ordersDueToday}</b></span>
              <span>Pending Orders <b>{metrics.pendingOrders}</b></span>
              <span>Ready Not Delivered <b>{metrics.readyNotDelivered}</b></span>
              <span>Payment Pending <b>{metrics.deliveredPaymentPending}</b></span>
              <span>Pending Balance <b>{currency(metrics.pendingBalance)}</b></span>
              <span>Bank Reco <b>{metrics.bankRecoStatus}</b></span>
              <span>Low Stock <b>{metrics.lowStockCount}</b></span>
              <span>Top Salesperson <b>{metrics.topSalesperson}</b></span>
              <span>Manager <b>{metrics.storeManager}</b></span>
              <span>Cash Submitted <b>{metrics.cashSubmitted ? "Yes" : "No"}</b></span>
              <span>POS Submitted <b>{metrics.posSubmitted ? "Yes" : "No"}</b></span>
            </div>
          </button>
        ))}
      </div>
      {selectedStoreDetail ? (
        <StoreDetailView
          state={state}
          store={selectedStoreDetail}
          metrics={buildStoreMetrics(state, selectedStoreDetail, state.employees)}
          onClose={() => setSelectedStoreDetailId("")}
        />
      ) : null}
      <div className="split-layout">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>{form.id ? "Edit store" : "Add store"}</h2>
          <div className="form-grid">
            <Input label="Store name" value={form.name} onChange={(name) => setForm({ ...form, name })} />
            <Input label="Store code" value={form.code} onChange={(code) => setForm({ ...form, code })} />
            <Input label="Address" value={form.address} onChange={(address) => setForm({ ...form, address })} />
            <Input label="Coordinates" value={form.coordinates} onChange={(coordinates) => setForm({ ...form, coordinates })} placeholder="28.631014, 77.435203" />
            <Input label="Radius meters" type="number" value={form.radiusMeters} onChange={(radiusMeters) => setForm({ ...form, radiusMeters })} />
            <label>
              Effective date
              <input type="date" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
            </label>
            <Input label="Reason" value={reason} onChange={setReason} />
          </div>
          <label className="toggle-line">
            <input type="checkbox" checked={form.geofenceEnabled} onChange={(event) => setForm({ ...form, geofenceEnabled: event.target.checked })} /> Geofence enabled
          </label>
          <label className="toggle-line">
            <input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} /> Active
          </label>
          <p className="helper-text">Paste coordinates in this format: latitude, longitude. Example: 28.631014, 77.435203</p>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={useCurrentLocation}>
              <MapPin size={17} /> Use my current location
            </button>
            {currentFormCoordinates ? (
              <a
                className="secondary-button"
                href={mapsLink(currentFormCoordinates.latitude, currentFormCoordinates.longitude)}
                target="_blank"
                rel="noreferrer"
              >
                Open map
              </a>
            ) : null}
            <button className="primary-button" type="button" disabled={saving} onClick={saveStore}>
              {saving ? "Saving..." : "Save store"}
            </button>
          </div>
          {saveStatus ? <p className="notice">{saveStatus}</p> : null}
        </form>
        <DataTable
          rows={state.stores}
          columns={[
            ["Code", (row) => row.code],
            ["Name", (row) => row.name],
            ["Coordinates", (row) => <CoordinateLink latitude={row.latitude} longitude={row.longitude} />],
            ["Radius", (row) => `${row.radiusMeters} m`],
            ["Geofence", (row) => (row.geofenceEnabled ? "On" : "Off")],
            ["Status", (row) => (row.active ? "Active" : "Inactive")]
          ]}
          actions={(row) => (
            <button className="mini-button" type="button" onClick={() => setForm({ ...row, coordinates: formatCoordinates(row.latitude, row.longitude) })}>
              Edit
            </button>
          )}
        />
      </div>
    </section>
  );
}

function StoreDetailView({ state, store, metrics, onClose }) {
  const storeEmployees = state.employees.filter((employee) => employee.storeId === store.id && employee.active !== false);
  const storeOrders = (state.opticalFastSlips || []).filter((order) => order.storeId === store.id);
  const lowStock = (state.crmProducts || []).filter((product) => product.storeId === store.id && Number(product.stockQty || 0) <= Number(product.reorderLevel || 0));
  const cards = [
    ["Today Sale", currency(metrics.todaySale)],
    ["Yesterday Sale", currency(metrics.yesterdaySale)],
    ["Yesterday Collection", currency(metrics.yesterdayCollection)],
    ["Month Sale", currency(metrics.monthSale)],
    ["Month Collection", currency(metrics.monthCollection)],
    ["Pending Balance", currency(metrics.pendingBalance)],
    ["Delivered Payment Pending", metrics.deliveredPaymentPending],
    ["Orders Due Today", metrics.ordersDueToday],
    ["Ready Not Delivered", metrics.readyNotDelivered],
    ["Lab Pending", metrics.labPending],
    ["Staff Present", metrics.staffPresent],
    ["Staff Absent", metrics.staffAbsent],
    ["Late Punch", metrics.latePunch],
    ["Bank Reco Status", metrics.bankRecoStatus],
    ["Low Stock Count", metrics.lowStockCount]
  ];
  return (
    <article className="panel store-detail-panel">
      <div className="panel-title-row">
        <div>
          <h2>{store.name}</h2>
          <p>{store.address || store.code}</p>
        </div>
        <button className="mini-button" type="button" onClick={onClose}>Close</button>
      </div>
      <div className="hq-status-card-grid compact">
        {cards.map(([label, value]) => (
          <article className="hq-status-card" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </div>
      <div className="store-detail-sections">
        <OpticalSection title="Store Snapshot" subtitle={`${metrics.storeManager} · ${store.code}`} defaultOpen>
          <p><strong>Manager:</strong> {metrics.storeManager}</p>
          <p><strong>Top salesperson:</strong> {metrics.topSalesperson}</p>
          <p><strong>Cash in hand submitted:</strong> {metrics.cashSubmitted ? "Yes" : "No"}</p>
          <p><strong>POS settlement submitted:</strong> {metrics.posSubmitted ? "Yes" : "No"}</p>
        </OpticalSection>
        <OpticalSection title="Sales & Collection" subtitle={`${currency(metrics.monthSale)} sale · ${currency(metrics.monthCollection)} collection`}>
          <CrmOrderList state={state} rows={storeOrders.slice(0, 15)} />
        </OpticalSection>
        <OpticalSection title="Attendance" subtitle={`${metrics.staffPresent} present · ${metrics.staffAbsent} absent`}>
          <DetailList
            rows={storeEmployees}
            getTitle={(row) => row.fullName}
            getMeta={(row) => `${row.designation || "Employee"} · ${row.employeeCode || "-"}`}
            getStatus={(row) => row.active === false ? "OFF" : "ACTIVE"}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.fullName}</h3>
                <p><strong>Designation:</strong> {row.designation || "-"}</p>
                <p><strong>Phone:</strong> {row.phone || "-"}</p>
                <p><strong>Weekly off:</strong> {row.weeklyOffDay || "-"}</p>
              </div>
            )}
          />
        </OpticalSection>
        <OpticalSection title="Orders" subtitle={`${metrics.ordersDueToday} due today · ${metrics.pendingOrders} pending`}>
          <CrmOrderList state={state} rows={storeOrders.filter((order) => orderBalance(order) > 0 || !/delivered/i.test(orderDeliveryStatus(order, state))).slice(0, 20)} />
        </OpticalSection>
        <OpticalSection title="Payments" subtitle={`${currency(metrics.pendingBalance)} pending balance`}>
          <CrmPaymentsLayer state={state} orders={storeOrders} />
        </OpticalSection>
        <OpticalSection title="Bank Reco" subtitle={metrics.bankRecoStatus}>
          <p><strong>Status:</strong> {metrics.bankRecoStatus}</p>
          <p><strong>Cash submitted:</strong> {metrics.cashSubmitted ? "Yes" : "No"}</p>
          <p><strong>POS settlement:</strong> {metrics.posSubmitted ? "Yes" : "No"}</p>
        </OpticalSection>
        <OpticalSection title="Inventory Alerts" subtitle={`${metrics.lowStockCount} low stock`}>
          <DetailList
            rows={lowStock}
            getTitle={(row) => row.name}
            getMeta={(row) => `${row.productType || "-"} · Stock ${row.stockQty || 0}`}
            getStatus={() => "LOW"}
            renderDetails={(row) => (
              <div className="detail-fields">
                <h3>{row.name}</h3>
                <p><strong>SKU:</strong> {row.productCode || "-"}</p>
                <p><strong>Stock:</strong> {row.stockQty || 0}</p>
                <p><strong>Reorder level:</strong> {row.reorderLevel || 0}</p>
              </div>
            )}
          />
        </OpticalSection>
        <OpticalSection title="Staff Performance" subtitle={`Top: ${metrics.topSalesperson}`}>
          <p><strong>Top salesperson:</strong> {metrics.topSalesperson}</p>
          <p><strong>Staff count:</strong> {storeEmployees.length}</p>
        </OpticalSection>
      </div>
    </article>
  );
}

function AttendancePanel({ state, updateState, employees, selectedMonth, setSelectedMonth, user, addAudit }) {
  const exportPrefix = filePrefix(state.settings);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoMessage, setPhotoMessage] = useState("");
  const monthDates = useMemo(() => {
    const range = monthRange(selectedMonth);
    return eachDate(range.start, range.end);
  }, [selectedMonth]);
  const today = localDateOnly();
  const [selectedDate, setSelectedDate] = useState(monthDates.includes(today) ? today : monthDates[0]);
  const [manualForm, setManualForm] = useState({
    employeeId: employees[0]?.id || "",
    date: monthDates.includes(today) ? today : monthDates[0],
    status: "P",
    punchIn: "11:00",
    punchOut: "21:00",
    reason: "Manual attendance fix"
  });
  const [manualMessage, setManualMessage] = useState("");
  const [monthlyStoreFilter, setMonthlyStoreFilter] = useState("all");
  const [monthlyEmployeeQuery, setMonthlyEmployeeQuery] = useState("");
  const [monthlyEmployeeId, setMonthlyEmployeeId] = useState(employees[0]?.id || "");
  const [monthlyStatusFilter, setMonthlyStatusFilter] = useState("all");

  useEffect(() => {
    setSelectedDate((current) => (monthDates.includes(current) ? current : monthDates.includes(today) ? today : monthDates[0]));
  }, [monthDates, today]);

  useEffect(() => {
    setManualForm((current) => ({
      ...current,
      date: selectedDate,
      employeeId: employees.some((employee) => employee.id === current.employeeId) ? current.employeeId : employees[0]?.id || ""
    }));
  }, [employees, selectedDate]);

  const formatPunchTime = (value) =>
    value
      ? new Date(value).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit"
        })
      : "-";
  const attendanceRows = state.punches.filter((punch) => employees.some((employee) => employee.id === punch.employeeId)).slice().reverse();
  const getDailyRows = (date) =>
    employees
      .map((employee) => buildEmployeeAttendanceDay(state, employee, date))
      .filter(Boolean);

  const monthlyStoreOptions = state.stores.filter((store) => employees.some((employee) => employee.storeId === store.id));
  const monthlyEmployeeOptions = employees
    .filter((employee) => monthlyStoreFilter === "all" || employee.storeId === monthlyStoreFilter)
    .filter((employee) => {
      const query = monthlyEmployeeQuery.trim().toLowerCase();
      if (!query) return true;
      const store = state.stores.find((item) => item.id === employee.storeId);
      return `${employee.fullName} ${employee.employeeCode || ""} ${employee.designation || ""} ${store?.name || ""} ${store?.code || ""}`.toLowerCase().includes(query);
    });
  const selectedMonthlyEmployee = monthlyEmployeeOptions.find((employee) => employee.id === monthlyEmployeeId) || monthlyEmployeeOptions[0] || employees[0] || null;

  useEffect(() => {
    if (selectedMonthlyEmployee && selectedMonthlyEmployee.id !== monthlyEmployeeId) {
      setMonthlyEmployeeId(selectedMonthlyEmployee.id);
    }
  }, [selectedMonthlyEmployee?.id, monthlyEmployeeId]);

  const workingHoursForRow = (row) => {
    if (!row?.punchIn?.timestamp || !row?.punchOut?.timestamp) return "";
    const diffMs = new Date(row.punchOut.timestamp).getTime() - new Date(row.punchIn.timestamp).getTime();
    if (!Number.isFinite(diffMs) || diffMs <= 0) return "";
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.round((diffMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };
  const employeeMonthlyRows = selectedMonthlyEmployee
    ? monthDates.map((date) => getDailyRows(date).find((row) => row.employee.id === selectedMonthlyEmployee.id)).filter(Boolean)
    : [];
  const filteredEmployeeMonthlyRows = employeeMonthlyRows.filter((row) => {
    if (monthlyStatusFilter === "all") return true;
    if (monthlyStatusFilter === "late") return row.summary.late;
    if (monthlyStatusFilter === "not_marked") return !row.punchIn && !row.punchOut && row.summary.code === "A";
    return row.summary.code === monthlyStatusFilter;
  });
  const monthlySummary = {
    totalDays: monthDates.length,
    present: employeeMonthlyRows.filter((row) => row.summary.code === "P" || row.summary.code === "P*").length,
    absent: employeeMonthlyRows.filter((row) => row.summary.code === "A").length,
    halfDay: employeeMonthlyRows.filter((row) => row.summary.code === "HD").length,
    paidLeave: employeeMonthlyRows.filter((row) => row.summary.code === "L").length,
    unpaidLeave: 0,
    weeklyOff: employeeMonthlyRows.filter((row) => row.summary.code === "WO" || row.summary.code === "P*").length,
    late: employeeMonthlyRows.filter((row) => row.summary.late).length,
    salaryDays: employeeMonthlyRows.reduce((sum, row) => sum + Number(row.summary.presentUnits || 0), 0)
  };
  const selectedEmployeeDayRow = selectedMonthlyEmployee
    ? employeeMonthlyRows.find((row) => row.employee.id === selectedMonthlyEmployee.id && row.id.startsWith(`${selectedDate}-`))
    : null;
  const monthlyAttendanceColumns = [
    ["Date", (row) => row.id.slice(0, 10)],
    ["Day", (row) => new Date(`${row.id.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long" })],
    ["Employee", (row) => row.employee.fullName],
    ["Store", (row) => row.store?.name || ""],
    ["Punch In", (row) => formatPunchTime(row.punchIn?.timestamp)],
    ["Punch Out", (row) => formatPunchTime(row.punchOut?.timestamp)],
    ["Status", (row) => `${row.summary.status} (${row.summary.code})`],
    ["Late", (row) => (row.summary.late ? "Yes" : "No")],
    ["Working Hours", (row) => workingHoursForRow(row)],
    ["Notes", (row) => row.punchIn?.deviceInfo || row.punchOut?.deviceInfo || ""]
  ];

  const submitManualAttendance = async () => {
    setManualMessage("");
    try {
      const employee = employees.find((item) => item.id === manualForm.employeeId);
      if (!employee) throw new Error("Please select an employee.");
      if (!manualForm.date) throw new Error("Please select a date.");
      if (!manualForm.reason.trim()) throw new Error("Please write a reason. It is needed for audit log.");
      const existingPunches = state.punches.filter(
        (punch) => punch.employeeId === employee.id && attendancePunchDate(punch) === manualForm.date
      );
      const punchChanges = [];
      const manualDevice = `Manual attendance by ${user?.name || user?.email || "admin"}`;
      const upsertManualPunch = (type, time) => {
        if (!time) return;
        const existing =
          type === "in"
            ? existingPunches.find((punch) => punch.type === type)
            : [...existingPunches].reverse().find((punch) => punch.type === type);
        if (existing) {
          punchChanges.push({
            ...existing,
            timestamp: localDateTimeIso(manualForm.date, time),
            deviceInfo: `${existing.deviceInfo || "Attendance punch"} · Timing corrected by ${user?.name || user?.email || "admin"}`
          });
          return;
        }
        punchChanges.push({
          id: uuid("punch"),
          employeeId: employee.id,
          storeId: employee.storeId,
          date: manualForm.date,
          type,
          timestamp: localDateTimeIso(manualForm.date, time),
          latitude: null,
          longitude: null,
          gpsAccuracy: null,
          distanceMeters: null,
          insideGeofence: null,
          exceptionRequired: false,
          photoPath: "",
          deviceInfo: manualDevice
        });
      };
      upsertManualPunch("in", manualForm.punchIn);
      upsertManualPunch("out", manualForm.punchOut);
      const savedPunchChanges = [];
      for (const punch of punchChanges) {
        savedPunchChanges.push(await persistRequiredEntity("attendance_punches", punch, "Attendance punch"));
      }
      const requestedValue = {
        ...attendanceValueForCode(manualForm.status),
        status: `${attendanceValueForCode(manualForm.status).status} - manual admin mark`,
        punchInTime: manualForm.punchIn || "",
        punchOutTime: manualForm.punchOut || ""
      };
      const correction = {
        id: uuid("correction"),
        employeeId: employee.id,
        date: manualForm.date,
        oldValue: null,
        requestedValue,
        requestedBy: user?.name || user?.email || "Admin",
        approvedBy: user?.name || user?.email || "Admin",
        reason: manualForm.reason.trim(),
        status: "approved",
        createdAt: new Date().toISOString(),
        decidedAt: new Date().toISOString()
      };
      const savedCorrection = await persistRequiredEntity("attendance_corrections", correction, "Attendance correction");
      const log = createAuditLog({
        entityType: "attendance_correction",
        entityId: correction.id,
        action: "manual_attendance",
        oldValue: null,
        newValue: requestedValue,
        reason: correction.reason,
        effectiveDate: correction.date,
        user
      });
      await persistAuditLog(log);
      updateState((current) => ({
        ...current,
        punches: [
          ...current.punches.map((punch) => savedPunchChanges.find((changed) => changed.id === punch.id) || punch),
          ...savedPunchChanges.filter((changed) => !current.punches.some((punch) => punch.id === changed.id))
        ],
        corrections: [savedCorrection, ...(current.corrections || [])],
        attendanceOverrides: [
          ...(current.attendanceOverrides || []).filter((item) => !(item.employeeId === employee.id && item.date === manualForm.date)),
          { id: savedCorrection.id, employeeId: employee.id, date: manualForm.date, ...requestedValue }
        ],
        auditLogs: [log, ...(current.auditLogs || [])]
      }));
      await notifySuperAdmins(state, updateState, {
        module: "Attendance",
        title: "Attendance Corrected",
        message: `${employee.fullName} attendance for ${manualForm.date} was marked ${manualForm.status}.`,
        priority: "normal",
        entityType: "attendance_correction",
        entityId: savedCorrection.id,
        storeId: employee.storeId,
        createdBy: user.id
      });
      setManualMessage(
        punchChanges.length
          ? `Attendance saved for ${employee.fullName}. Timing and status marked ${manualForm.status}.`
          : `Status marked ${manualForm.status} for ${employee.fullName}.`
      );
    } catch (error) {
      setManualMessage(error.message || "Manual attendance could not be saved.");
    }
  };

  const calendarDays = monthDates.map((date) => {
    const rows = getDailyRows(date);
    return {
      date,
      rows,
      present: rows.filter((row) => row.summary.presentUnits > 0).length,
      absent: rows.filter((row) => row.summary.code === "A").length,
      missing: rows.filter((row) => row.summary.code === "M").length,
      outside: rows.filter((row) => [row.punchIn, row.punchOut].some((punch) => punch?.insideGeofence === false)).length
    };
  });
  const selectedDay = calendarDays.find((day) => day.date === selectedDate) || calendarDays[0];
  const selectedRows = selectedDay?.rows || [];
  const visibleEmployeeIds = new Set(employees.map((employee) => employee.id));
  const selectedDateTasks = (state.workTasks || []).filter((task) => {
    if (!visibleEmployeeIds.has(task.assignedTo)) return false;
    const completedDate = task.completedAt ? String(task.completedAt).slice(0, 10) : "";
    return task.dueDate === selectedDate || completedDate === selectedDate;
  });
  const selectedDaySheetColumns = [
    ["Date", () => selectedDate],
    ["Employee", (row) => row.employee.fullName],
    ["Store", (row) => row.store?.name || ""],
    ["Status", (row) => `${row.summary.status} (${row.summary.code})`],
    ["Punch in", (row) => formatPunchTime(row.punchIn?.timestamp)],
    ["Punch out", (row) => formatPunchTime(row.punchOut?.timestamp)],
    ["Late", (row) => (row.summary.late ? "Yes" : "No")],
    ["Early going", (row) => (row.summary.earlyDeparture ? "Yes" : "No")],
    ["Distance meters", (row) => row.punchIn?.distanceMeters ?? ""],
    ["Geofence", (row) => (row.punchIn ? (row.punchIn.insideGeofence ? "Inside" : "Outside") : "")],
    ["Coordinates", (row) => formatCoordinates(row.punchIn?.latitude, row.punchIn?.longitude)],
    ["Map link", (row) => mapsLink(row.punchIn?.latitude, row.punchIn?.longitude)]
  ];
  const attendanceSheetColumns = [
    ["Date", (row) => row.date],
    ["Employee", (row) => employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Type", (row) => row.type],
    ["Time", (row) => new Date(row.timestamp).toLocaleString("en-IN")],
    ["Latitude", (row) => row.latitude ?? ""],
    ["Longitude", (row) => row.longitude ?? ""],
    ["GPS accuracy", (row) => row.gpsAccuracy ?? ""],
    ["Distance meters", (row) => row.distanceMeters ?? ""],
    ["Geofence", (row) => (row.insideGeofence ? "Inside" : "Outside")],
    ["Map link", (row) => mapsLink(row.latitude, row.longitude)],
    ["Exception required", (row) => (row.exceptionRequired ? "Yes" : "No")],
    ["Photo saved", (row) => (row.photoPath ? "Yes" : "No")],
    ["Device", (row) => row.deviceInfo || ""]
  ];

  const openPhoto = async (punch) => {
    setPhotoMessage("Opening photo...");
    try {
      const url = await getAttendancePhotoUrl(punch.photoPath);
      setPhotoPreview({ punch, url });
      setPhotoMessage("");
    } catch (error) {
      setPhotoMessage(error.message || "Photo could not be opened.");
    }
  };

  return (
    <section>
      <Header title="Attendance" subtitle="Photo proof, GPS, distance and exception flags are stored with every punch.">
        <input
          className="month-picker"
          type="month"
          value={selectedMonth}
          onChange={(event) => setSelectedMonth(event.target.value)}
          aria-label="Attendance month"
        />
        <button
          className="secondary-button"
          type="button"
          onClick={() => downloadSheet(`${exportPrefix}-attendance-${selectedDate}.csv`, selectedRows, selectedDaySheetColumns)}
        >
          <FileSpreadsheet size={17} /> Selected date
        </button>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-attendance.csv`, attendanceRows, attendanceSheetColumns)}>
          <FileSpreadsheet size={17} /> All punches
        </button>
      </Header>
      {photoMessage ? <p className="notice">{photoMessage}</p> : null}
      <article className="panel monthly-attendance-panel">
        <div className="panel-title-row">
          <div>
            <h2>Monthly Attendance</h2>
            <p>Search an employee and see the full month record, calendar, punch proof and daily table.</p>
          </div>
          <button
            className="secondary-button"
            type="button"
            disabled={!selectedMonthlyEmployee}
            onClick={() => downloadSheet(
              `${exportPrefix}-${selectedMonthlyEmployee?.employeeCode || selectedMonthlyEmployee?.fullName || "employee"}-${selectedMonth}-monthly-attendance.csv`,
              filteredEmployeeMonthlyRows,
              monthlyAttendanceColumns
            )}
          >
            <FileSpreadsheet size={17} /> Download monthly CSV
          </button>
        </div>
        <div className="form-grid compact-form-grid">
          <label>
            Store
            <select value={monthlyStoreFilter} onChange={(event) => setMonthlyStoreFilter(event.target.value)}>
              <option value="all">All stores</option>
              {monthlyStoreOptions.map((store) => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </label>
          <label>
            Search employee
            <input value={monthlyEmployeeQuery} onChange={(event) => setMonthlyEmployeeQuery(event.target.value)} placeholder="Example: Sumit" />
          </label>
          <label>
            Employee
            <select value={selectedMonthlyEmployee?.id || ""} onChange={(event) => setMonthlyEmployeeId(event.target.value)}>
              {monthlyEmployeeOptions.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName} - {employee.designation || "Employee"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={monthlyStatusFilter} onChange={(event) => setMonthlyStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="P">Present</option>
              <option value="HD">Half Day</option>
              <option value="A">Absent / Not marked</option>
              <option value="L">Leave</option>
              <option value="WO">Weekly Off</option>
              <option value="M">Missing punch</option>
              <option value="late">Late</option>
            </select>
          </label>
        </div>
        {selectedMonthlyEmployee ? (
          <>
            <div className="monthly-attendance-head">
              <span>
                <strong>{selectedMonthlyEmployee.fullName} - {formatMonth(selectedMonth)}</strong>
                <small>
                  {state.stores.find((store) => store.id === selectedMonthlyEmployee.storeId)?.name || "No store"} · {selectedMonthlyEmployee.designation || "No designation"}
                </small>
              </span>
              <span className="status-chip">{monthlySummary.salaryDays} salary days</span>
            </div>
            <div className="attendance-summary-grid">
              {[
                ["Total days", monthlySummary.totalDays, "neutral"],
                ["Present", monthlySummary.present, "ok"],
                ["Absent", monthlySummary.absent, "danger"],
                ["Half day", monthlySummary.halfDay, "warning"],
                ["Paid leave", monthlySummary.paidLeave, "info"],
                ["Weekly off", monthlySummary.weeklyOff, "neutral"],
                ["Late", monthlySummary.late, "warning"]
              ].map(([label, value, tone]) => (
                <span className={`attendance-summary-card ${tone}`} key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </span>
              ))}
            </div>
            <div className="employee-month-calendar">
              {monthDates.map((date) => {
                const row = employeeMonthlyRows.find((item) => item.id.startsWith(`${date}-`));
                const code = row?.summary.code || "A";
                const isSelected = date === selectedDate;
                return (
                  <button
                    key={date}
                    className={[
                      "employee-month-day",
                      attendanceStatusClass(row || { summary: { code } }) || "status-unknown",
                      isSelected ? "active" : "",
                      row?.summary.late ? "late" : ""
                    ].filter(Boolean).join(" ")}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                  >
                    <strong>{Number(date.slice(-2))}</strong>
                    <small>{code === "A" && !row?.punchIn && !row?.punchOut ? "Not Marked" : code}</small>
                  </button>
                );
              })}
            </div>
            <div className="monthly-date-detail">
              <div>
                <h3>{formatDisplayDate(selectedDate)}</h3>
                <p>
                  <strong>Status:</strong> {selectedEmployeeDayRow?.summary.status || "Not Marked"} ({selectedEmployeeDayRow?.summary.code || "A"})
                </p>
                <p><strong>Punch in:</strong> {formatPunchTime(selectedEmployeeDayRow?.punchIn?.timestamp)}</p>
                <p><strong>Punch out:</strong> {formatPunchTime(selectedEmployeeDayRow?.punchOut?.timestamp)}</p>
                <p><strong>Late:</strong> {selectedEmployeeDayRow?.summary.late ? "Yes" : "No"}</p>
                <p><strong>Working hours:</strong> {workingHoursForRow(selectedEmployeeDayRow) || "-"}</p>
                <p><strong>Location:</strong> <CoordinateLink latitude={selectedEmployeeDayRow?.punchIn?.latitude} longitude={selectedEmployeeDayRow?.punchIn?.longitude} /></p>
                <p><strong>Marked by / notes:</strong> {selectedEmployeeDayRow?.punchIn?.deviceInfo || selectedEmployeeDayRow?.punchOut?.deviceInfo || "-"}</p>
              </div>
              <div className="monthly-photo-actions">
                <AttendancePhotoThumb path={selectedEmployeeDayRow?.punchIn?.photoPath || selectedEmployeeDayRow?.punchOut?.photoPath} name={selectedMonthlyEmployee.fullName} />
                <button className="secondary-button" type="button" disabled={!selectedEmployeeDayRow?.punchIn?.photoPath} onClick={() => openPhoto(selectedEmployeeDayRow?.punchIn)}>
                  Punch-in photo
                </button>
                <button className="secondary-button" type="button" disabled={!selectedEmployeeDayRow?.punchOut?.photoPath} onClick={() => openPhoto(selectedEmployeeDayRow?.punchOut)}>
                  Punch-out photo
                </button>
              </div>
            </div>
            <div className="responsive-table-wrap">
              <table className="monthly-attendance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                    <th>Status</th>
                    <th>Late</th>
                    <th>Working Hours</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployeeMonthlyRows.map((row) => {
                    const date = row.id.slice(0, 10);
                    return (
                      <tr key={row.id}>
                        <td>{date}</td>
                        <td>{new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" })}</td>
                        <td>{formatPunchTime(row.punchIn?.timestamp)}</td>
                        <td>{formatPunchTime(row.punchOut?.timestamp)}</td>
                        <td><span className={`status-pill ${attendanceStatusClass(row)}`}>{row.summary.status} ({row.summary.code})</span></td>
                        <td>{row.summary.late ? "Yes" : "No"}</td>
                        <td>{workingHoursForRow(row) || "-"}</td>
                        <td>{row.punchIn?.deviceInfo || row.punchOut?.deviceInfo || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="empty-state compact">No employee found for this filter.</p>
        )}
      </article>
      <article className="panel manual-attendance-panel">
        <div className="panel-title-row">
          <div>
            <h2>Manual attendance fix</h2>
            <p>Use this when someone forgot punch-in, forgot punch-out, or HR needs to mark full day / half day / leave for one date.</p>
          </div>
          <span className="status-chip">Admin action</span>
        </div>
        <div className="form-grid compact-form-grid">
          <label>
            Employee
            <select value={manualForm.employeeId} onChange={(event) => setManualForm({ ...manualForm, employeeId: event.target.value })}>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.fullName}</option>
              ))}
            </select>
          </label>
          <label>
            Date
            <input
              type="date"
              value={manualForm.date}
              onChange={(event) => {
                setManualForm({ ...manualForm, date: event.target.value });
                setSelectedDate(event.target.value);
              }}
            />
          </label>
          <label>
            Status
            <select value={manualForm.status} onChange={(event) => setManualForm({ ...manualForm, status: event.target.value })}>
              <option value="P">Full day present</option>
              <option value="HD">Half day</option>
              <option value="A">Absent</option>
              <option value="L">Leave</option>
              <option value="EX">Exception approved</option>
              <option value="M">Missing punch</option>
            </select>
          </label>
          <label>
            Punch-in time
            <input type="time" value={manualForm.punchIn} onChange={(event) => setManualForm({ ...manualForm, punchIn: event.target.value })} />
          </label>
          <label>
            Punch-out time
            <input type="time" value={manualForm.punchOut} onChange={(event) => setManualForm({ ...manualForm, punchOut: event.target.value })} />
          </label>
          <label>
            Reason
            <input value={manualForm.reason} onChange={(event) => setManualForm({ ...manualForm, reason: event.target.value })} placeholder="Example: employee forgot punch-out" />
          </label>
        </div>
        <div className="button-row">
          <button className="primary-button" type="button" disabled={!employees.length} onClick={submitManualAttendance}>
            Save manual attendance
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setManualForm({ ...manualForm, punchIn: "", punchOut: "", status: "A", reason: "Marked absent by admin" })}
          >
            Mark absent
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setManualForm({ ...manualForm, punchIn: "11:00", punchOut: "21:00", status: "P", reason: "Manual full day attendance" })}
          >
            Full day
          </button>
        </div>
        {manualMessage ? <p className="notice">{manualMessage}</p> : null}
      </article>
      <article className="panel attendance-calendar-panel">
        <div className="panel-title-row">
          <div>
            <h2>{formatMonth(selectedMonth)}</h2>
            <p>Tap any date to see who was present, absent, late or missing punch on that day.</p>
          </div>
          <span className="status-chip">{formatDisplayDate(selectedDate)}</span>
        </div>
        <div className="calendar-week-head">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="attendance-calendar">
          {Array.from({ length: (new Date(`${monthDates[0]}T00:00:00`).getDay() + 6) % 7 }).map((_, index) => (
            <span key={`blank-${index}`} className="calendar-blank" />
          ))}
          {calendarDays.map((day) => {
            const dateNumber = Number(day.date.slice(-2));
            const isSelected = day.date === selectedDate;
            const hasIssue = day.absent || day.missing || day.outside;
            return (
              <button
                key={day.date}
                className={[
                  "calendar-day",
                  isSelected ? "active" : "",
                  day.date === today ? "today" : "",
                  hasIssue ? "has-issue" : ""
                ].filter(Boolean).join(" ")}
                type="button"
                onClick={() => setSelectedDate(day.date)}
              >
                <strong>{dateNumber}</strong>
                <span>{day.present} P</span>
                <small>{day.missing ? `${day.missing} missing` : `${day.absent} absent`}</small>
              </button>
            );
          })}
        </div>
      </article>
      <article className="panel day-attendance-panel">
        <div className="panel-title-row">
          <div>
            <h2>{formatDisplayDate(selectedDate)}</h2>
            <p>
              {selectedDay.present} present · {selectedDay.absent} absent · {selectedDay.missing} missing punch · {selectedDay.outside} outside location
            </p>
          </div>
        </div>
      </article>
      <article className="panel day-task-panel">
        <div className="panel-title-row">
          <div>
            <h2>Tasks on this date</h2>
            <p>Due tasks and completed tasks for the selected attendance date.</p>
          </div>
          <span className="status-chip">{selectedDateTasks.length} tasks</span>
        </div>
        <div className="task-list">
          {selectedDateTasks.length ? selectedDateTasks.map((task) => {
            const employee = employees.find((item) => item.id === task.assignedTo);
            const completedDate = task.completedAt ? String(task.completedAt).slice(0, 10) : "";
            const taskDateLabel = completedDate === selectedDate
              ? `Completed ${new Date(task.completedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
              : `Due ${task.dueDate || "-"}`;
            return (
              <div className={`task-date-card ${task.status === "completed" ? "done" : ""}`} key={task.id}>
                <span>
                  <strong>{task.title}</strong>
                  <small>{employee?.fullName || "Employee"} · {taskDateLabel}</small>
                </span>
                <b>{task.status === "completed" ? "DONE" : "PENDING"}</b>
              </div>
            );
          }) : <p>No tasks on this date.</p>}
        </div>
      </article>
      <DetailList
        rows={selectedRows}
        getTitle={(row) => row.employee.fullName}
        getMeta={(row) => `In ${formatPunchTime(row.punchIn?.timestamp)} · Out ${formatPunchTime(row.punchOut?.timestamp)} · ${row.store?.code || row.store?.name || "-"}`}
        getStatus={(row) => row.summary.code}
        getPhotoPath={(row) => row.punchIn?.photoPath || row.punchOut?.photoPath}
        getRowClass={attendanceStatusClass}
        renderDetails={(row) => (
          <div className="detail-fields">
            <h3>{row.employee.fullName}</h3>
            <div className="proof-strip">
              <AttendancePhotoThumb path={row.punchIn?.photoPath} name={row.employee.fullName} />
              <span>
                <strong>Punch-in proof</strong>
                <small>{row.punchIn?.photoPath ? "Photo captured" : "No punch-in photo"}</small>
              </span>
            </div>
            <p><strong>Date:</strong> {formatDisplayDate(selectedDate)}</p>
            <p><strong>Store:</strong> {row.store?.name || "-"}</p>
            <p><strong>Status:</strong> {row.summary.status} ({row.summary.code})</p>
            <p><strong>Punch in:</strong> {formatPunchTime(row.punchIn?.timestamp)}</p>
            <p><strong>Punch out:</strong> {formatPunchTime(row.punchOut?.timestamp)}</p>
            <p><strong>Late:</strong> {row.summary.late ? "Yes" : "No"}</p>
            <p><strong>Early going:</strong> {row.summary.earlyDeparture ? "Yes" : "No"}</p>
            <p><strong>Distance:</strong> {row.punchIn?.distanceMeters == null ? "-" : `${Math.round(Number(row.punchIn.distanceMeters))} m`}</p>
            <p><strong>GPS accuracy:</strong> {row.punchIn?.gpsAccuracy == null ? "-" : `${Math.round(Number(row.punchIn.gpsAccuracy))} m`}</p>
            <p><strong>Geofence:</strong> {row.punchIn ? (row.punchIn.insideGeofence ? "Inside" : "Outside") : "-"}</p>
            <p><strong>Coordinates:</strong> <CoordinateLink latitude={row.punchIn?.latitude} longitude={row.punchIn?.longitude} /></p>
            <p><strong>Device:</strong> {row.punchIn?.deviceInfo || row.punchOut?.deviceInfo || "-"}</p>
            <div className="button-row">
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  setManualForm({
                    employeeId: row.employee.id,
                    date: selectedDate,
                    status: row.summary.code === "P*" ? "P" : row.summary.code || "P",
                    punchIn: row.punchIn?.timestamp ? new Date(row.punchIn.timestamp).toTimeString().slice(0, 5) : "11:00",
                    punchOut: row.punchOut?.timestamp ? new Date(row.punchOut.timestamp).toTimeString().slice(0, 5) : "21:00",
                    reason: "Manual attendance fix"
                  });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                Mark / fix attendance
              </button>
              <button className="secondary-button" type="button" disabled={!row.punchIn?.photoPath} onClick={() => openPhoto(row.punchIn)}>
                Punch-in photo
              </button>
              <button className="secondary-button" type="button" disabled={!row.punchOut?.photoPath} onClick={() => openPhoto(row.punchOut)}>
                Punch-out photo
              </button>
            </div>
          </div>
        )}
      />
      {photoPreview ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-head">
              <div>
                <strong>{employees.find((employee) => employee.id === photoPreview.punch.employeeId)?.fullName || "Punch photo"}</strong>
                <small>{new Date(photoPreview.punch.timestamp).toLocaleString("en-IN")}</small>
              </div>
              <button className="icon-only" type="button" onClick={() => setPhotoPreview(null)}>
                <XCircle size={18} />
              </button>
            </div>
            <img className="punch-photo-preview" src={photoPreview.url} alt="Attendance punch proof" />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function WorkPanel({ state, updateState, employees, user, focus = "", onFocusHandled }) {
  const exportPrefix = filePrefix(state.settings);
  const allAssignableEmployees = (state.employees || []).filter((employee) => employee.active !== false);
  const ownEmployee = state.employees.find((employee) => employee.id === user.employeeId);
  const immediateTargetEmployees = getImmediateReportEmployees(state.employees, user.employeeId);
  const targetAssignableEmployees = user.employeeId && immediateTargetEmployees.length ? immediateTargetEmployees : employees;
  const getEmployeeName = (id) => state.employees.find((employee) => employee.id === id)?.fullName || "Employee";
  const getTaskSenderName = (task) => {
    const sender = state.users.find((item) => item.id === task.assignedBy);
    return sender?.employeeId ? getEmployeeName(sender.employeeId) : sender?.name || "User";
  };
  const [taskStatusTab, setTaskStatusTab] = useState("pending");
  const [taskMetricFilter, setTaskMetricFilter] = useState("");
  const taskStatusRef = useRef(null);
  const targetStatusRef = useRef(null);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    assignedTo: allAssignableEmployees[0]?.id || employees[0]?.id || "",
    dueDate: new Date().toISOString().slice(0, 10),
    priority: "Normal"
  });
  const [targetForm, setTargetForm] = useState({
    employeeId: targetAssignableEmployees[0]?.id || "",
    storeId: targetAssignableEmployees[0]?.storeId || "",
    month: new Date().toISOString().slice(0, 7),
    targetAmount: "",
    achievedAmount: 0,
    notes: ""
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!taskForm.assignedTo && allAssignableEmployees[0]?.id) setTaskForm((current) => ({ ...current, assignedTo: allAssignableEmployees[0].id }));
    if (!targetAssignableEmployees.some((employee) => employee.id === targetForm.employeeId) && targetAssignableEmployees[0]?.id) {
      setTargetForm((current) => ({ ...current, employeeId: targetAssignableEmployees[0].id, storeId: targetAssignableEmployees[0].storeId || "" }));
    }
  }, [allAssignableEmployees, targetAssignableEmployees, taskForm.assignedTo, targetForm.employeeId]);

  const visibleEmployeeIds = new Set(employees.map((employee) => employee.id));
  const taskRows = (state.workTasks || []).filter((task) => visibleEmployeeIds.has(task.assignedTo) || task.assignedBy === user.id);
  const activeTaskRows = taskRows.filter((task) => !isTaskArchived(task));
  const archivedTaskRows = taskRows.filter((task) => isTaskArchived(task));
  const ownOpenTasks = ownEmployee
    ? (state.workTasks || []).filter((task) => task.assignedTo === ownEmployee.id && task.status !== "completed" && !isTaskArchived(task))
    : [];
  const pendingTaskRows = activeTaskRows.filter((task) => task.status !== "completed");
  const completedTaskRows = activeTaskRows.filter((task) => task.status === "completed");
  const todayDate = new Date().toISOString().slice(0, 10);
  const overdueTaskRows = pendingTaskRows.filter((task) => task.dueDate && task.dueDate < todayDate);
  const completedTodayRows = completedTaskRows.filter((task) => task.completedAt?.slice(0, 10) === todayDate);
  const pendingReviewRows = taskRows.filter((task) => task.reviewStatus === "pending_review");
  const visibleTaskRows = taskMetricFilter === "completedToday"
    ? completedTodayRows
    : taskMetricFilter === "overdue"
      ? overdueTaskRows
    : taskMetricFilter === "pendingReview"
      ? pendingReviewRows
      : taskStatusTab === "archived"
        ? archivedTaskRows
      : taskStatusTab === "completed"
        ? completedTaskRows
        : pendingTaskRows;
  const targetEmployeeIds = new Set(targetAssignableEmployees.map((employee) => employee.id));
  const targetRows = (state.salesTargets || []).filter((target) => targetEmployeeIds.has(target.employeeId));
  const pendingReview = pendingReviewRows.length;
  const targetsEnabled = isModuleEnabled(state.settings, "salesTargets");
  const canManageTargets =
    targetsEnabled &&
    (user.role === "super_admin" || user.permissions?.salesTargets || user.permissions?.allEmployees || targetAssignableEmployees.length > 0);
  const targetAverage = cumulativeTargetProgress(targetRows);

  const jumpToTasks = (tab, metric = "", shouldScroll = true) => {
    setTaskStatusTab(tab);
    setTaskMetricFilter(metric);
    if (shouldScroll) {
      window.setTimeout(() => taskStatusRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }
  };

  const jumpToTargets = (shouldScroll = true) => {
    setTaskMetricFilter("");
    if (shouldScroll) {
      window.setTimeout(() => targetStatusRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }
  };

  useEffect(() => {
    if (focus === "targets" && canManageTargets) {
      jumpToTargets(false);
      onFocusHandled?.();
    }
    if (focus === "tasks") {
      jumpToTasks("pending", "", false);
      onFocusHandled?.();
    }
  }, [focus, canManageTargets]);

  const taskColumns = [
    ["Task", (row) => row.title],
    ["Employee", (row) => getEmployeeName(row.assignedTo)],
    ["Assigned by", (row) => getTaskSenderName(row)],
    ["Status", (row) => taskStatusLabel(row)],
    ["Review", (row) => row.reviewStatus || "-"],
    ["Due", (row) => row.dueDate || ""],
    ["Priority", (row) => row.priority || "Normal"]
  ];
  const targetColumns = [
    ["Employee", (row) => employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
    ["Month", (row) => row.month],
    ["Target", (row) => row.targetAmount],
    ["Achieved", (row) => row.achievedAmount || 0],
    ["Progress", (row) => `${targetProgress(row)}%`]
  ];

  const saveTask = async () => {
    setMessage("");
    try {
      if (!taskForm.title || !taskForm.assignedTo) throw new Error("Task title and employee are required.");
      const row = {
        id: uuid("task"),
        ...taskForm,
        assignedBy: user.id,
        status: "assigned",
        reviewStatus: "not_submitted",
        createdAt: new Date().toISOString()
      };
      const savedTask = await persistRequiredEntity("work_tasks", row, "Task");
      updateState((current) => ({ ...current, workTasks: [savedTask, ...(current.workTasks || [])] }));
      setTaskForm({ title: "", description: "", assignedTo: allAssignableEmployees[0]?.id || "", dueDate: new Date().toISOString().slice(0, 10), priority: "Normal" });
      setMessage(`Task assigned to ${getEmployeeName(row.assignedTo)}. You can track it here under task status.`);
    } catch (error) {
      setMessage(error.message || "Task could not be saved. If this is live, run the Work module SQL once.");
    }
  };

  const saveTarget = async () => {
    setMessage("");
    try {
      if (!targetForm.employeeId || !targetForm.month || !targetForm.targetAmount) throw new Error("Employee, month and target amount are required.");
      const employee = targetAssignableEmployees.find((item) => item.id === targetForm.employeeId);
      if (!employee) throw new Error("You can assign targets only to your direct team.");
      const row = {
        id: uuid("target"),
        ...targetForm,
        storeId: targetForm.storeId || employee?.storeId || null,
        targetAmount: Number(targetForm.targetAmount || 0),
        achievedAmount: Number(targetForm.achievedAmount || 0),
        assignedBy: user.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const savedTarget = await persistRequiredEntity("sales_targets", row, "Sales target");
      updateState((current) => ({ ...current, salesTargets: [savedTarget, ...(current.salesTargets || [])] }));
      setTargetForm({
        employeeId: targetAssignableEmployees[0]?.id || "",
        storeId: targetAssignableEmployees[0]?.storeId || "",
        month: new Date().toISOString().slice(0, 7),
        targetAmount: "",
        achievedAmount: 0,
        notes: ""
      });
      setMessage("Sales target saved.");
    } catch (error) {
      setMessage(error.message || "Sales target could not be saved. If this is live, run the Work module SQL once.");
    }
  };

  const updateTask = async (task, changes) => {
    try {
      const updated = { ...task, ...changes, updatedAt: new Date().toISOString() };
      const savedTask = await persistRequiredEntity("work_tasks", updated, "Task");
      updateState((current) => ({
        ...current,
        workTasks: (current.workTasks || []).map((item) => (item.id === task.id ? savedTask : item))
      }));
      setMessage("Task updated.");
    } catch (error) {
      setMessage(error.message || "Task could not be updated.");
    }
  };

  const completeOwnTask = async (task) => {
    if (!ownEmployee) return;
    await updateTask(task, {
      status: "completed",
      completedAt: new Date().toISOString(),
      completedBy: ownEmployee.id,
      reviewStatus: "pending_review"
    });
    setMessage("Task completed. It is now waiting for manager review.");
  };

  const updateTargetAchieved = async (target, achievedAmount) => {
    const updated = { ...target, achievedAmount: Number(achievedAmount || 0), updatedAt: new Date().toISOString() };
    const savedTarget = await persistRequiredEntity("sales_targets", updated, "Sales target");
    updateState((current) => ({
      ...current,
      salesTargets: (current.salesTargets || []).map((item) => (item.id === target.id ? savedTarget : item))
    }));
  };

  function TaskDetail({ row }) {
    const [draft, setDraft] = useState({
      title: row.title || "",
      description: row.description || "",
      assignedTo: row.assignedTo || "",
      dueDate: row.dueDate || "",
      priority: row.priority || "Normal"
    });

    useEffect(() => {
      setDraft({
        title: row.title || "",
        description: row.description || "",
        assignedTo: row.assignedTo || "",
        dueDate: row.dueDate || "",
        priority: row.priority || "Normal"
      });
    }, [row.id]);

    const employee = state.employees.find((item) => item.id === row.assignedTo);
    const canModifyTask =
      user.role === "super_admin" ||
      row.assignedBy === user.id ||
      row.assignedTo === user.employeeId ||
      visibleEmployeeIds.has(row.assignedTo);
    const markClosed = () => updateTask(row, {
      status: "completed",
      completedAt: row.completedAt || new Date().toISOString(),
      completedBy: row.completedBy || user.employeeId || row.assignedTo,
      reviewStatus: "reviewed",
      reviewedBy: user.id,
      reviewedAt: new Date().toISOString()
    });

    return (
      <div className="detail-fields">
        <h3>{row.title}</h3>
        <p><strong>Employee:</strong> {employee?.fullName || "-"}</p>
        <p><strong>Assigned by:</strong> {getTaskSenderName(row)}</p>
        <p><strong>Status:</strong> {row.status || "assigned"}</p>
        <p><strong>Review:</strong> {row.reviewStatus || "-"}</p>
        <p><strong>Due:</strong> {row.dueDate || "-"}</p>
        <p><strong>Priority:</strong> {row.priority || "Normal"}</p>
        <p><strong>Completed:</strong> {row.completedAt ? new Date(row.completedAt).toLocaleString("en-IN") : "-"}</p>
        {canModifyTask ? (
          <div className="inline-edit-box">
            <Input label="Task title" value={draft.title} onChange={(title) => setDraft({ ...draft, title })} />
            <label>
              Employee
              <select value={draft.assignedTo} onChange={(event) => setDraft({ ...draft, assignedTo: event.target.value })}>
                {allAssignableEmployees.map((employeeOption) => (
                  <option key={employeeOption.id} value={employeeOption.id}>{employeeOption.fullName}</option>
                ))}
              </select>
            </label>
            <Input label="Due date" type="date" value={draft.dueDate} onChange={(dueDate) => setDraft({ ...draft, dueDate })} />
            <label>
              Priority
              <select value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>
                <option>Normal</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </label>
            <textarea placeholder="Task details" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            <div className="button-row">
              <button className="primary-button" type="button" onClick={() => updateTask(row, draft)}>
                Save task
              </button>
              {row.status !== "completed" ? (
                <button className="secondary-button" type="button" onClick={markClosed}>
                  Close task
                </button>
              ) : null}
              {row.reviewStatus === "pending_review" ? (
                <button className="secondary-button" type="button" onClick={() => updateTask(row, { reviewStatus: "reviewed", reviewedBy: user.id, reviewedAt: new Date().toISOString() })}>
                  Mark reviewed
                </button>
              ) : null}
              <button className="secondary-button" type="button" onClick={() => updateTask(row, { status: "assigned", reviewStatus: "not_submitted", completedAt: null, completedBy: null, reviewedAt: null, reviewedBy: null })}>
                Reopen
              </button>
            </div>
          </div>
        ) : (
          <p><strong>Details:</strong> {row.description || "-"}</p>
        )}
      </div>
    );
  }

  return (
    <section>
      <Header title="Work" subtitle="Assign tasks, review completion and track sales targets.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-tasks.csv`, taskRows, taskColumns)}>
          <FileSpreadsheet size={17} /> Tasks sheet
        </button>
        {canManageTargets ? (
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-sales-targets.csv`, targetRows, targetColumns)}>
          <FileSpreadsheet size={17} /> Targets sheet
        </button>
        ) : null}
      </Header>
      <div className="kpi-grid">
        <Kpi
          label="Open tasks"
          value={pendingTaskRows.length}
          tone="kpi-pending"
          active={taskStatusTab === "pending" && !taskMetricFilter}
          onClick={() => jumpToTasks("pending")}
        />
        <Kpi
          label="Overdue tasks"
          value={overdueTaskRows.length}
          tone="kpi-absent"
          active={taskMetricFilter === "overdue"}
          onClick={() => jumpToTasks("pending", "overdue")}
        />
        <Kpi
          label="Completed today"
          value={completedTodayRows.length}
          tone="kpi-present"
          active={taskMetricFilter === "completedToday"}
          onClick={() => jumpToTasks("completed", "completedToday")}
        />
        <Kpi
          label="Pending manager review"
          value={pendingReview}
          tone="kpi-approval"
          active={taskMetricFilter === "pendingReview"}
          onClick={() => jumpToTasks("pending", "pendingReview")}
        />
        {canManageTargets ? <Kpi label="Target achieved" value={`${targetAverage}%`} tone="kpi-target" onClick={jumpToTargets} /> : null}
      </div>
      {message ? <p className="notice">{message}</p> : null}
      {ownEmployee ? (
        <article className="panel my-work-panel">
          <div className="panel-title-row">
            <div>
              <h2>My tasks</h2>
              <p>{ownEmployee.fullName} · {ownEmployee.designation || "No designation"}</p>
            </div>
            <span className="status-chip">{ownOpenTasks.length} pending</span>
          </div>
          <div className="task-list">
            {ownOpenTasks.length ? ownOpenTasks.map((task) => (
              <label className="task-check-card" key={task.id}>
                <input type="checkbox" onChange={() => completeOwnTask(task)} />
                <span>
                  <strong>{task.title}</strong>
                  <small>Due {task.dueDate || "-"} · {task.priority || "Normal"}</small>
                </span>
              </label>
            )) : <p>No task pending for your own profile.</p>}
          </div>
        </article>
      ) : (
        <p className="notice">This login is not linked to an employee profile yet. Link it from Users if this person should receive tasks or designation.</p>
      )}
      <div className="split-layout">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>Assign task</h2>
          <div className="form-grid">
            <Input label="Task title" value={taskForm.title} onChange={(title) => setTaskForm({ ...taskForm, title })} />
            <label>
              Employee
              <select value={taskForm.assignedTo} onChange={(event) => setTaskForm({ ...taskForm, assignedTo: event.target.value })}>
                {allAssignableEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.fullName}</option>)}
              </select>
            </label>
            <Input label="Due date" type="date" value={taskForm.dueDate} onChange={(dueDate) => setTaskForm({ ...taskForm, dueDate })} />
            <label>
              Priority
              <select value={taskForm.priority} onChange={(event) => setTaskForm({ ...taskForm, priority: event.target.value })}>
                <option>Normal</option>
                <option>High</option>
                <option>Urgent</option>
              </select>
            </label>
          </div>
          <textarea placeholder="Task details" value={taskForm.description} onChange={(event) => setTaskForm({ ...taskForm, description: event.target.value })} />
          <button className="primary-button" type="button" onClick={saveTask}>Assign task</button>
        </form>
        {canManageTargets ? (
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>Assign sales target</h2>
          <div className="form-grid">
            <label>
              Employee
              <select
                value={targetForm.employeeId}
                onChange={(event) => {
                  const employee = targetAssignableEmployees.find((item) => item.id === event.target.value);
                  setTargetForm({ ...targetForm, employeeId: event.target.value, storeId: employee?.storeId || "" });
                }}
              >
                {targetAssignableEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.fullName}</option>)}
              </select>
            </label>
            <Input label="Month" type="month" value={targetForm.month} onChange={(month) => setTargetForm({ ...targetForm, month })} />
            <Input label="Target amount" type="number" value={targetForm.targetAmount} onChange={(targetAmount) => setTargetForm({ ...targetForm, targetAmount })} />
            <Input label="Current achieved" type="number" value={targetForm.achievedAmount} onChange={(achievedAmount) => setTargetForm({ ...targetForm, achievedAmount })} />
          </div>
          <textarea placeholder="Notes" value={targetForm.notes} onChange={(event) => setTargetForm({ ...targetForm, notes: event.target.value })} />
          <button className="primary-button" type="button" onClick={saveTarget}>Save target</button>
        </form>
        ) : null}
      </div>
      <div className="panel-grid two">
        <article className="panel" ref={taskStatusRef}>
          <div className="panel-title-row">
            <div>
              <h2>
                {taskMetricFilter === "completedToday"
                  ? "Completed today"
                  : taskMetricFilter === "overdue"
                    ? "Overdue tasks"
                  : taskMetricFilter === "pendingReview"
                    ? "Pending manager review"
                  : taskStatusTab === "archived"
                    ? "Archived tasks"
                    : "Task status"}
              </h2>
              <p>Completed tasks auto-archive after {TASK_ARCHIVE_DAYS} days. Tap a task for full status.</p>
            </div>
          </div>
          <div className="status-tabs" role="tablist" aria-label="Task status filter">
            <button
              className={taskStatusTab === "pending" ? "active" : ""}
              type="button"
              onClick={() => {
                setTaskMetricFilter("");
                setTaskStatusTab("pending");
              }}
            >
              Pending
              <strong>{pendingTaskRows.length}</strong>
            </button>
            <button
              className={taskStatusTab === "completed" ? "active" : ""}
              type="button"
              onClick={() => {
                setTaskMetricFilter("");
                setTaskStatusTab("completed");
              }}
            >
              Completed
              <strong>{completedTaskRows.length}</strong>
            </button>
            <button
              className={taskStatusTab === "archived" ? "active" : ""}
              type="button"
              onClick={() => {
                setTaskMetricFilter("");
                setTaskStatusTab("archived");
              }}
            >
              Archived
              <strong>{archivedTaskRows.length}</strong>
            </button>
          </div>
          <DetailList
            rows={visibleTaskRows}
            getTitle={(row) => row.title}
            getMeta={(row) => `${getEmployeeName(row.assignedTo)} · By ${getTaskSenderName(row)} · Due ${row.dueDate || "-"} · ${row.priority || "Normal"}`}
            getStatus={(row) => row.reviewStatus === "pending_review" ? "REVIEW" : row.status === "completed" ? "DONE" : "OPEN"}
            renderDetails={(row) => <TaskDetail row={row} />}
          />
        </article>
        {canManageTargets ? (
        <article className="panel" ref={targetStatusRef}>
          <h2>Sales targets</h2>
          <div className="task-list">
            {targetRows.length ? targetRows.map((target) => {
              const employee = employees.find((item) => item.id === target.employeeId);
              const progress = targetProgress(target);
              return (
                <div className="target-card-mini" key={target.id}>
                  <strong>{employee?.fullName || "Employee"} · {formatMonth(target.month)}</strong>
                  <span>{currency(target.achievedAmount || 0)} / {currency(target.targetAmount || 0)} · {progress}% achieved</span>
                  <i><b style={{ width: `${Math.min(100, progress)}%` }} /></i>
                  <Input label="Update achieved" type="number" value={target.achievedAmount || 0} onChange={(value) => updateTargetAchieved(target, value)} />
                </div>
              );
            }) : <p>No sales targets yet.</p>}
          </div>
        </article>
        ) : null}
      </div>
    </section>
  );
}

function CorrectionsPanel({ state, updateState, user, employees = state.employees }) {
  const exportPrefix = filePrefix(state.settings);
  const employeeIds = new Set(employees.map((employee) => employee.id));
  const [approvalLayer, setApprovalLayer] = useState("leave");
  const [form, setForm] = useState({
    employeeId: employees[0]?.id || "",
    date: new Date().toISOString().slice(0, 10),
    requestedCode: "P",
    reason: ""
  });

  const submitCorrection = async () => {
    const requestedValue = {
      ...attendanceValueForCode(form.requestedCode),
      status: `${attendanceValueForCode(form.requestedCode).status} - correction requested`
    };
    const correction = {
      id: uuid("correction"),
      employeeId: form.employeeId,
      date: form.date,
      oldValue: null,
      requestedValue,
      requestedBy: user.name,
      reason: form.reason,
      status: "pending",
      createdAt: new Date().toISOString()
    };
    const savedCorrection = await persistRequiredEntity("attendance_corrections", correction, "Attendance correction");
    updateState((current) => ({ ...current, corrections: [savedCorrection, ...current.corrections] }));
  };

  const decide = async (correction, status) => {
    const updated = { ...correction, status, approvedBy: user.name, decidedAt: new Date().toISOString() };
    const savedCorrection = await persistRequiredEntity("attendance_corrections", updated, "Attendance correction");
    const isLeaveApplication = correction.requestedValue?.kind === "leave_application";
    const log = createAuditLog({
      entityType: isLeaveApplication ? "leave_application" : "attendance_correction",
      entityId: correction.id,
      action: isLeaveApplication ? `leave_${status}` : status,
      oldValue: correction.oldValue,
      newValue: correction.requestedValue,
      reason: correction.reason,
      effectiveDate: correction.date,
      user
    });
    await persistAuditLog(log).catch(() => null);
    updateState((current) => ({
      ...current,
      corrections: current.corrections.map((item) => (item.id === correction.id ? savedCorrection : item)),
      attendanceOverrides:
        status === "approved"
          ? [
              ...current.attendanceOverrides.filter((item) => item.id !== correction.id),
              { id: correction.id, employeeId: correction.employeeId, date: correction.date, ...correction.requestedValue }
            ]
          : current.attendanceOverrides.filter((item) => item.id !== correction.id),
      auditLogs: [log, ...current.auditLogs]
    }));
  };
  const decideLeaveGroup = async (leaveGroup, status) => {
    const rows = leaveGroup.children?.length ? leaveGroup.children : [leaveGroup];
    const decidedAt = new Date().toISOString();
    const updatedRows = rows.map((row) => ({ ...row, status, approvedBy: user.name, decidedAt }));
    const savedRows = [];
    for (const row of updatedRows) {
      savedRows.push(await persistRequiredEntity("attendance_corrections", row, "Leave application"));
    }
    const log = createAuditLog({
      entityType: "leave_application",
      entityId: leaveGroup.id,
      action: `leave_${status}`,
      oldValue: null,
      newValue: {
        ...leaveGroup.requestedValue,
        dates: rows.map((row) => row.date)
      },
      reason: leaveGroup.reason,
      effectiveDate: leaveGroup.requestedValue?.fromDate || leaveGroup.date,
      user
    });
    await persistAuditLog(log);
    const updatedById = new Map(savedRows.map((row) => [row.id, row]));
    const updatedIds = new Set(savedRows.map((row) => row.id));
    updateState((current) => ({
      ...current,
      corrections: current.corrections.map((item) => updatedById.get(item.id) || item),
      attendanceOverrides:
        status === "approved"
          ? [
              ...current.attendanceOverrides.filter((item) => !updatedIds.has(item.id)),
              ...savedRows.map((row) => ({ id: row.id, employeeId: row.employeeId, date: row.date, ...row.requestedValue }))
            ]
          : current.attendanceOverrides.filter((item) => !updatedIds.has(item.id)),
      auditLogs: [log, ...current.auditLogs]
    }));
  };
  const visibleApprovalRows = state.corrections.filter((item) => employeeIds.has(item.employeeId));
  const leaveRows = groupLeaveApplications(visibleApprovalRows.filter((item) => item.requestedValue?.kind === "leave_application"));
  const correctionRows = visibleApprovalRows.filter((item) => item.requestedValue?.kind !== "leave_application").slice().reverse();
  const correctionSheetColumns = [
    ["Date", (row) => row.date],
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Requested", (row) => row.requestedValue?.code || ""],
    ["Reason", (row) => row.reason || ""],
    ["Requested by", (row) => row.requestedBy || ""],
    ["Approved by", (row) => row.approvedBy || ""],
    ["Status", (row) => row.status],
    ["Created", (row) => row.createdAt || ""],
    ["Decided", (row) => row.decidedAt || ""]
  ];
  const leaveSheetColumns = [
    ["From", (row) => row.requestedValue?.fromDate || row.date],
    ["To", (row) => row.requestedValue?.toDate || row.date],
    ["Days", (row) => row.requestedValue?.days || row.children?.length || 1],
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Leave type", (row) => row.requestedValue?.leaveType || row.requestedValue?.status || ""],
    ["Reason", (row) => row.reason || ""],
    ["Requested by", (row) => row.requestedBy || ""],
    ["Approved by", (row) => row.approvedBy || ""],
    ["Status", (row) => row.status],
    ["Created", (row) => row.createdAt || ""],
    ["Decided", (row) => row.decidedAt || ""]
  ];
  const activeRows = approvalLayer === "leave" ? leaveRows : correctionRows;
  const activeColumns = approvalLayer === "leave" ? leaveSheetColumns : correctionSheetColumns;

  return (
    <section>
      <Header title="Approvals" subtitle="Handle leave applications separately from attendance corrections.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-${approvalLayer === "leave" ? "leave-applications" : "attendance-corrections"}.csv`, activeRows, activeColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <div className="layer-tabs" role="tablist" aria-label="Approval sections">
        <button className={approvalLayer === "leave" ? "active" : ""} type="button" onClick={() => setApprovalLayer("leave")}>
          Leave Applications
          <strong>{leaveRows.filter((row) => row.status === "pending").length}</strong>
        </button>
        <button className={approvalLayer === "corrections" ? "active" : ""} type="button" onClick={() => setApprovalLayer("corrections")}>
          Attendance Corrections
          <strong>{correctionRows.filter((row) => row.status === "pending").length}</strong>
        </button>
      </div>
      {approvalLayer === "corrections" ? (
      <form className="panel inline-form" onSubmit={(event) => event.preventDefault()}>
        <label>
          Employee
          <select value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
              </option>
            ))}
          </select>
        </label>
        <Input label="Date" type="date" value={form.date} onChange={(date) => setForm({ ...form, date })} />
        <label>
          Requested status
          <select value={form.requestedCode} onChange={(event) => setForm({ ...form, requestedCode: event.target.value })}>
            {["P", "HD", "A", "L", "EX"].map((code) => (
              <option key={code}>{code}</option>
            ))}
          </select>
        </label>
        <Input label="Reason" value={form.reason} onChange={(reason) => setForm({ ...form, reason })} />
        <button className="primary-button" type="button" onClick={submitCorrection}>
          Add correction request
        </button>
      </form>
      ) : (
        <article className="panel field-help-card">
          <strong>Leave Applications</strong>
          <span>Employees apply from their Leave screen. Approving leave here marks that date as leave for attendance and payroll.</span>
        </article>
      )}
      <DataTable
        rows={activeRows}
        columns={approvalLayer === "leave"
          ? [
              ["Date range", (row) => `${formatDisplayDate(row.requestedValue?.fromDate || row.date)} to ${formatDisplayDate(row.requestedValue?.toDate || row.date)}`],
              ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
              ["Leave type", (row) => row.requestedValue?.leaveType || row.requestedValue?.status || ""],
              ["Days", (row) => row.requestedValue?.days || row.children?.length || 1],
              ["Reason", (row) => row.reason],
              ["Status", (row) => row.status]
            ]
          : [
              ["Date", (row) => row.date],
              ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
              ["Requested", (row) => row.requestedValue?.code],
              ["Reason", (row) => row.reason],
              ["Status", (row) => row.status]
            ]}
        actions={(row) =>
          row.status === "pending" || user.role === "super_admin" ? (
            <>
              {row.status !== "approved" ? <button className="mini-button" type="button" onClick={() => approvalLayer === "leave" ? decideLeaveGroup(row, "approved") : decide(row, "approved")}>
                {approvalLayer === "leave" ? "Grant leave" : "Approve correction"}
              </button> : null}
              {row.status !== "rejected" ? <button className="mini-button danger" type="button" onClick={() => approvalLayer === "leave" ? decideLeaveGroup(row, "rejected") : decide(row, "rejected")}>
                Reject
              </button> : null}
              {user.role === "super_admin" && row.status !== "pending" ? <button className="mini-button" type="button" onClick={() => approvalLayer === "leave" ? decideLeaveGroup(row, "pending") : decide(row, "pending")}>
                Reopen
              </button> : null}
            </>
          ) : null
        }
      />
    </section>
  );
}

function LedgerPanel({ kind, state, updateState, employees, user }) {
  const exportPrefix = filePrefix(state.settings);
  const isAdvance = kind === "advances";
  const [form, setForm] = useState({
    employeeId: employees[0]?.id || state.employees[0]?.id || "",
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    type: isAdvance ? "salary_advance" : "Travel",
    payrollMonth: new Date().toISOString().slice(0, 7),
    description: "",
    notes: ""
  });
  const key = isAdvance ? "advancesLoans" : "reimbursements";
  const tableName = isAdvance ? "advances_loans" : "reimbursements";
  const ledgerRows = state[key].slice().reverse();
  const ledgerSheetColumns = [
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Date", (row) => row.date],
    ["Type", (row) => row.type],
    ["Amount", (row) => row.amount],
    ["Payroll month", (row) => row.payrollMonth],
    ["Status", (row) => row.status],
    ["Notes", (row) => row.notes || row.description || ""],
    ["Added by", (row) => row.addedBy || ""],
    ["Approved by", (row) => row.approvedBy || ""]
  ];

  const save = async () => {
    const record = {
      id: uuid(isAdvance ? "advance" : "reimbursement"),
      ...form,
      amount: Number(form.amount || 0),
      status: "approved",
      addedBy: user.name,
      approvedBy: user.name,
      createdAt: new Date().toISOString(),
      deductedAmount: isAdvance ? Number(form.amount || 0) : 0,
      closingBalance: 0
    };
    const savedRecord = await persistRequiredEntity(tableName, record, isAdvance ? "Advance / loan" : "Reimbursement");
    updateState((current) => ({ ...current, [key]: [...current[key], savedRecord] }));
  };

  return (
    <section>
      <Header title={isAdvance ? "Advances & Loans" : "Reimbursements"} subtitle="Approved entries are picked up automatically in payroll.">
        <button
          className="secondary-button"
          type="button"
          onClick={() => downloadSheet(isAdvance ? `${exportPrefix}-advances-loans.csv` : `${exportPrefix}-reimbursements.csv`, ledgerRows, ledgerSheetColumns)}
        >
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <form className="panel inline-form" onSubmit={(event) => event.preventDefault()}>
        <label>
          Employee
          <select value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
              </option>
            ))}
          </select>
        </label>
        <Input label="Date" type="date" value={form.date} onChange={(date) => setForm({ ...form, date })} />
        <label>
          Type
          <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
            {(isAdvance ? ["salary_advance", "loan", "other_deduction"] : ["Travel", "Petrol", "Food", "Courier", "Other"]).map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </label>
        <Input label="Amount" type="number" value={form.amount} onChange={(amount) => setForm({ ...form, amount })} />
        <Input label="Payroll month" type="month" value={form.payrollMonth} onChange={(payrollMonth) => setForm({ ...form, payrollMonth })} />
        <Input label="Notes" value={form.notes} onChange={(notes) => setForm({ ...form, notes })} />
        <button className="primary-button" type="button" onClick={save}>
          Save
        </button>
      </form>
      <DataTable
        rows={ledgerRows}
        columns={[
          ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
          ["Date", (row) => row.date],
          ["Type", (row) => row.type],
          ["Amount", (row) => currency(row.amount)],
          ["Payroll", (row) => row.payrollMonth],
          ["Status", (row) => row.status]
        ]}
      />
    </section>
  );
}

function CollectionsPanel({ state, updateState, selectedMonth }) {
  const exportPrefix = filePrefix(state.settings);
  const collectionRows = state.storeCollections.filter((row) => row.month === selectedMonth);
  const collectionSheetColumns = [
    ["Month", (row) => row.month],
    ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
    ["Store code", (row) => state.stores.find((store) => store.id === row.storeId)?.code || ""],
    ["Collection", (row) => row.amount]
  ];

  const setAmount = (storeId, amount) => {
    updateState((current) => {
      const existing = current.storeCollections.find((item) => item.storeId === storeId && item.month === selectedMonth);
      const row = { id: existing?.id || uuid("collection"), storeId, month: selectedMonth, amount: Number(amount || 0) };
      return {
        ...current,
        storeCollections: existing
          ? current.storeCollections.map((item) => (item.id === existing.id ? row : item))
          : [...current.storeCollections, row]
      };
    });
  };

  return (
    <section>
      <Header title="Store Collections" subtitle="Monthly collections feed configurable incentive rules.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-store-collections-${selectedMonth}.csv`, collectionRows, collectionSheetColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <div className="panel-grid">
        {state.stores.map((store) => {
          const row = state.storeCollections.find((item) => item.storeId === store.id && item.month === selectedMonth);
          return (
            <article className="panel" key={store.id}>
              <h2>{store.name}</h2>
              <Input label={`${selectedMonth} collection`} type="number" value={row?.amount || ""} onChange={(amount) => setAmount(store.id, amount)} />
            </article>
          );
        })}
      </div>
    </section>
  );
}

function IncentiveRulesPanel({ state, updateState, employees, user }) {
  const exportPrefix = filePrefix(state.settings);
  const visibleEmployeeIds = new Set(employees.map((employee) => employee.id));
  const visibleStoreIds = new Set(employees.map((employee) => employee.storeId));
  const visibleStores = user.role === "super_admin"
    ? state.stores
    : state.stores.filter((store) => visibleStoreIds.has(store.id));
  const visibleRules = state.incentiveRuleHistory.filter(
    (rule) => visibleEmployeeIds.has(rule.employeeId) || visibleStoreIds.has(rule.storeId)
  );
  const [form, setForm] = useState({
    employeeId: employees[0]?.id || "",
    storeId: employees[0]?.storeId || visibleStores[0]?.id || "",
    threshold: 300000,
    ratePercent: 1,
    splitPercent: 100,
    payDelayMonths: 3,
    active: true,
    effectiveDate: new Date().toISOString().slice(0, 10),
    reason: "Incentive rule update"
  });
  const [message, setMessage] = useState("");

  const reset = () => {
    setForm({
      employeeId: employees[0]?.id || "",
      storeId: employees[0]?.storeId || visibleStores[0]?.id || "",
      threshold: 300000,
      ratePercent: 1,
      splitPercent: 100,
      payDelayMonths: 3,
      active: true,
      effectiveDate: new Date().toISOString().slice(0, 10),
      reason: "Incentive rule update"
    });
  };

  const editRule = (rule) => {
    setForm({
      ...rule,
      ratePercent: Number(rule.rate || 0) * 100,
      splitPercent: Number(rule.splitPercent ?? 100),
      payDelayMonths: Number(rule.payDelayMonths ?? 3),
      effectiveDate: rule.effectiveDate || new Date().toISOString().slice(0, 10),
      reason: rule.reason || "Incentive rule update"
    });
    setMessage("");
  };

  const saveRule = async () => {
    setMessage("Saving incentive rule...");
    try {
      if (!form.employeeId || !form.storeId) throw new Error("Employee and store are required.");
      const existing = state.incentiveRuleHistory.find((rule) => rule.id === form.id);
      const rule = {
        id: form.id || uuid("incentive-rule"),
        employeeId: form.employeeId,
        storeId: form.storeId,
        threshold: Number(form.threshold || 0),
        rate: Number(form.ratePercent || 0) / 100,
        splitPercent: Number(form.splitPercent || 100),
        payDelayMonths: Number(form.payDelayMonths || 3),
        active: form.active !== false,
        effectiveDate: form.effectiveDate,
        reason: form.reason,
        changedBy: user.name
      };
      const savedRule = await persistRequiredEntity("incentive_rule_history", rule, "Incentive rule");
      const log = createAuditLog({
        entityType: "incentive_rule",
        entityId: rule.id,
        action: existing ? "updated" : "created",
        oldValue: existing || null,
        newValue: savedRule,
        reason: form.reason,
        effectiveDate: form.effectiveDate,
        user
      });
      await persistAuditLog(log);
      updateState((current) => ({
        ...current,
        incentiveRuleHistory: existing
          ? current.incentiveRuleHistory.map((item) => (item.id === savedRule.id ? savedRule : item))
          : [...current.incentiveRuleHistory, savedRule],
        auditLogs: [log, ...current.auditLogs]
      }));
      setMessage("Incentive rule saved.");
      reset();
    } catch (error) {
      setMessage(error.message || "Incentive rule could not be saved.");
    }
  };

  const ruleSheetColumns = [
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
    ["Threshold", (row) => row.threshold],
    ["Incentive %", (row) => percent(Number(row.rate || 0) * 100)],
    ["Split %", (row) => percent(row.splitPercent ?? 100)],
    ["Pay delay months", (row) => row.payDelayMonths ?? 3],
    ["Effective date", (row) => row.effectiveDate],
    ["Status", (row) => (row.active === false ? "Inactive" : "Active")]
  ];

  return (
    <section>
      <Header title="Incentives" subtitle="Edit employee incentive thresholds, percentage, store and iVision split rules.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-incentive-rules.csv`, visibleRules, ruleSheetColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <div className="summary-strip">
        {employees.map((employee) => {
          const rules = visibleRules
            .filter((rule) => rule.employeeId === employee.id && rule.active !== false)
            .sort((a, b) => Number(a.threshold || 0) - Number(b.threshold || 0));
          return (
            <article className="summary-pill" key={employee.id}>
              <strong>{employee.fullName}</strong>
              <span>
                {rules.length
                  ? rules.map((rule) => `${currency(rule.threshold)} -> ${percent(Number(rule.rate || 0) * 100)}${Number(rule.splitPercent ?? 100) !== 100 ? ` (${percent(rule.splitPercent)} split)` : ""}`).join(" | ")
                  : "No incentive rule"}
              </span>
            </article>
          );
        })}
      </div>
      <div className="split-layout">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>{form.id ? "Edit incentive rule" : "Add incentive rule"}</h2>
          <div className="field-help-card">
            <strong>How to add incentive</strong>
            <span>Example: threshold ₹3,00,000 and incentive 1% means the employee gets 1% when store collection reaches ₹3,00,000.</span>
            <span>Split % is used when one store collection is shared between employees, like iVision Alam/Gulveer split.</span>
          </div>
          <div className="form-grid">
            <label>
              Employee
              <select value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Store
              <select value={form.storeId} onChange={(event) => setForm({ ...form, storeId: event.target.value })}>
                {visibleStores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Collection threshold (₹)" type="number" value={form.threshold} onChange={(threshold) => setForm({ ...form, threshold })} placeholder="300000" />
            <Input label="Incentive percent (%)" type="number" value={form.ratePercent} onChange={(ratePercent) => setForm({ ...form, ratePercent })} placeholder="1 or 1.5 or 2" />
            <Input label="Split percent (%)" type="number" value={form.splitPercent} onChange={(splitPercent) => setForm({ ...form, splitPercent })} placeholder="100, or 50 for shared store" />
            <Input label="Paid after months" type="number" value={form.payDelayMonths} onChange={(payDelayMonths) => setForm({ ...form, payDelayMonths })} placeholder="3" />
            <Input label="Effective date" type="date" value={form.effectiveDate} onChange={(effectiveDate) => setForm({ ...form, effectiveDate })} />
            <Input label="Reason" value={form.reason} onChange={(reason) => setForm({ ...form, reason })} />
          </div>
          <label className="toggle-line">
            <input type="checkbox" checked={form.active !== false} onChange={(event) => setForm({ ...form, active: event.target.checked })} /> Active
          </label>
          <div className="button-row">
            <button className="primary-button" type="button" onClick={saveRule}>
              Save incentive
            </button>
            <button className="secondary-button" type="button" onClick={reset}>
              Clear
            </button>
          </div>
          {message ? <p className="notice">{message}</p> : null}
        </form>
        <DataTable
          rows={visibleRules}
          columns={ruleSheetColumns}
          actions={(row) => (
            <button className="mini-button" type="button" onClick={() => editRule(row)}>
              Edit
            </button>
          )}
        />
      </div>
    </section>
  );
}

function PayrollPanel({ state, updateState, employees, selectedMonth, setSelectedMonth, user, addAudit }) {
  const exportPrefix = filePrefix(state.settings);
  const payrollDetailsRef = useRef(null);
  const payrollRun = state.payrollRuns.find((run) => run.month === selectedMonth);
  const jumpToPayrollDetails = () => {
    window.setTimeout(() => payrollDetailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };
  const payrollSheetColumns = [
    ["Employee", (row) => row.employeeName],
    ["Store", (row) => row.storeName],
    ["Gross salary", (row) => row.grossSalary],
    ["Present days", (row) => row.attendanceSummary.presentDays],
    ["Half days", (row) => row.attendanceSummary.halfDays],
    ["Absent days", (row) => row.attendanceSummary.absentDays],
    ["Offs taken", (row) => row.attendanceSummary.totalOffsTaken],
    ["Leave deduction days", (row) => row.attendanceSummary.leaveDeductionDays],
    ["Leave deduction amount", (row) => row.attendanceSummary.leaveDeductionAmount],
    ["Advance deduction", (row) => row.advanceDeduction],
    ["Staff welfare", (row) => row.staffWelfare],
    ["Reimbursement", (row) => row.reimbursementAmount],
    ["Incentive", (row) => row.incentivePayable],
    ["Net payable", (row) => row.netPayable]
  ];

  const generate = () => {
    const run = generatePayrollRun({
      month: selectedMonth,
      employees,
      punches: state.punches,
      attendanceOverrides: state.attendanceOverrides,
      salaryHistory: state.salaryHistory,
      storeHistory: state.storeHistory,
      weeklyOffHistory: state.weeklyOffHistory,
      shiftHistory: state.shiftHistory,
      leaveBalances: state.leaveBalances,
      advancesLoans: state.advancesLoans,
      reimbursements: state.reimbursements,
      incentiveRecords: state.incentiveRecords,
      settings: state.settings,
      status: "Draft"
    });
    updateState((current) => ({
      ...current,
      payrollRuns: [run, ...current.payrollRuns.filter((item) => item.month !== selectedMonth)]
    }));
  };

  const setStatus = async (status) => {
    if (!payrollRun) return;
    const reason = window.prompt(status === "Locked" ? "Reason for locking payroll" : "Super Admin unlock reason");
    if (!reason) return;
    const updated = { ...payrollRun, status, lockReason: reason, lockedBy: user.name, lockedAt: new Date().toISOString() };
    const log = createAuditLog({
      entityType: "payroll",
      entityId: payrollRun.id,
      action: status === "Locked" ? "locked" : "unlocked",
      oldValue: { status: payrollRun.status },
      newValue: { status },
      reason,
      effectiveDate: `${selectedMonth}-01`,
      user
    });
    await persistAuditLog(log);
    updateState((current) => ({
      ...current,
      payrollRuns: current.payrollRuns.map((run) => (run.month === selectedMonth ? updated : run)),
      auditLogs: [log, ...current.auditLogs]
    }));
  };

  return (
    <section>
      <Header title="Payroll" subtitle="Preview, review, lock, export salary sheet and download salary slips.">
        <label className="month-picker">
          Month
          <input type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
        </label>
        <button className="primary-button" type="button" onClick={generate}>
          Generate payroll
        </button>
      </Header>
      {payrollRun ? (
        <>
          <div className="kpi-grid">
            <Kpi label="Status" value={payrollRun.status} onClick={jumpToPayrollDetails} />
            <Kpi label="Gross" value={currency(payrollRun.totals.grossSalary)} onClick={jumpToPayrollDetails} />
            <Kpi label="Deductions" value={currency(payrollRun.totals.leaveDeductionAmount + payrollRun.totals.advanceDeduction)} onClick={jumpToPayrollDetails} />
            <Kpi label="Net payable" value={currency(payrollRun.totals.netPayable)} onClick={jumpToPayrollDetails} />
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={() => setStatus("Locked")}>
              <Lock size={17} /> Lock payroll
            </button>
            {user.role === "super_admin" ? (
              <button className="secondary-button" type="button" onClick={() => setStatus("Draft")}>
                Unlock
              </button>
            ) : null}
            <button
              className="secondary-button"
              type="button"
              onClick={async () => {
                const blob = await exportPayrollExcel({ payrollRun, employees: state.employees, stores: state.stores, companyName: state.settings.companyName });
                downloadBlob(blob, `${exportPrefix}-salary-sheet-${selectedMonth}.xlsx`);
              }}
            >
              <FileSpreadsheet size={17} /> Excel salary sheet
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => downloadSheet(`${exportPrefix}-payroll-${selectedMonth}.csv`, payrollRun.items, payrollSheetColumns)}
            >
              <FileSpreadsheet size={17} /> Quick CSV
            </button>
          </div>
          <div ref={payrollDetailsRef}>
            <DataTable
              rows={payrollRun.items}
              columns={[
                ["Employee", (row) => row.employeeName],
                ["Gross", (row) => currency(row.grossSalary)],
                ["Offs taken", (row) => row.attendanceSummary.totalOffsTaken],
                ["Leave deduction", (row) => currency(row.attendanceSummary.leaveDeductionAmount)],
                ["Advance", (row) => currency(row.advanceDeduction)],
                ["Welfare", (row) => currency(row.staffWelfare)],
                ["Reimbursement", (row) => currency(row.reimbursementAmount)],
                ["Net", (row) => currency(row.netPayable)]
              ]}
              actions={(row) => {
                const employee = state.employees.find((item) => item.id === row.employeeId);
                const store = state.stores.find((item) => item.id === employee?.storeId);
                return (
                  <button
                    className="mini-button"
                    type="button"
                    onClick={async () => {
                      const blob = await generateSalarySlipPdf({ payrollItem: row, employee, store, companyName: state.settings.companyName });
                      downloadBlob(blob, `${employee.employeeCode}-${selectedMonth}-salary-slip.pdf`);
                    }}
                  >
                    Slip
                  </button>
                );
              }}
            />
          </div>
        </>
      ) : (
        <div className="empty-state">Generate payroll preview for {formatMonth(selectedMonth)}.</div>
      )}
    </section>
  );
}

function ReportsPanel({ state, employees, selectedMonth }) {
  const exportPrefix = filePrefix(state.settings);
  const payrollRun = state.payrollRuns.find((run) => run.month === selectedMonth);
  const employeeReportRows = employees;
  const employeeReportColumns = [
    ["Employee", (row) => row.fullName],
    ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
    ["Punches this month", (row) => state.punches.filter((punch) => punch.employeeId === row.id && punch.date.startsWith(selectedMonth)).length],
    ["Corrections", (row) => state.corrections.filter((item) => item.employeeId === row.id).length]
  ];
  const punchReportColumns = [
    ["Date", (row) => row.date],
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Type", (row) => row.type],
    ["Time", (row) => new Date(row.timestamp).toLocaleString("en-IN")],
    ["Distance meters", (row) => row.distanceMeters ?? ""],
    ["Inside geofence", (row) => (row.insideGeofence ? "Yes" : "No")],
    ["Coordinates", (row) => formatCoordinates(row.latitude, row.longitude)],
    ["Map link", (row) => mapsLink(row.latitude, row.longitude)]
  ];
  const monthlyPunchRows = state.punches.filter((punch) => punch.date.startsWith(selectedMonth));
  const outsideRows = state.punches.filter((punch) => punch.insideGeofence === false);

  return (
    <section>
      <Header title="Reports" subtitle="Operational reports with month, store and employee filters ready for Phase 2 expansion." />
      <div className="button-row report-actions">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-monthly-attendance-${selectedMonth}.csv`, monthlyPunchRows, punchReportColumns)}>
          <FileSpreadsheet size={17} /> Monthly attendance
        </button>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-outside-geofence.csv`, outsideRows, punchReportColumns)}>
          <FileSpreadsheet size={17} /> Outside geofence
        </button>
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-employee-report-${selectedMonth}.csv`, employeeReportRows, employeeReportColumns)}>
          <FileSpreadsheet size={17} /> Employee report
        </button>
      </div>
      <div className="panel-grid two">
        {[
          ["Missing punch report", state.punches.filter((punch) => punch.date.startsWith(selectedMonth)).length],
          ["Outside geofence report", state.punches.filter((punch) => punch.insideGeofence === false).length],
          ["Advance / loan report", state.advancesLoans.length],
          ["Reimbursement report", state.reimbursements.length],
          ["Payroll summary", payrollRun ? currency(payrollRun.totals.netPayable) : "Not generated"],
          ["Audit log report", state.auditLogs.length]
        ].map(([label, value]) => (
          <article className="panel report-tile" key={label}>
            <strong>{label}</strong>
            <span>{value}</span>
          </article>
        ))}
      </div>
      <DataTable
        rows={employeeReportRows}
        columns={employeeReportColumns}
      />
    </section>
  );
}

function BackupPanel({ state, employees, selectedMonth }) {
  const [message, setMessage] = useState("");
  const exportPrefix = filePrefix(state.settings);
  const companyName = getCompanyName(state.settings);
  const payrollRun = state.payrollRuns.find((run) => run.month === selectedMonth);
  const today = new Date().toISOString().slice(0, 10);
  const employeeIds = new Set(employees.map((employee) => employee.id));
  const scopedPunches = state.punches.filter((punch) => employeeIds.has(punch.employeeId));
  const scopedCorrections = state.corrections.filter((item) => employeeIds.has(item.employeeId));
  const scopedTasks = (state.workTasks || []).filter((task) => employeeIds.has(task.assignedTo) || employeeIds.has(task.assignedBy));
  const scopedTargets = (state.salesTargets || []).filter((target) => employeeIds.has(target.employeeId));
  const scopedAdvances = state.advancesLoans.filter((item) => employeeIds.has(item.employeeId));
  const scopedReimbursements = state.reimbursements.filter((item) => employeeIds.has(item.employeeId));
  const scopedSlips = (state.opticalFastSlips || []).filter((slip) => !slip.salespersonId || employeeIds.has(slip.salespersonId));
  const employeeName = (id) => state.employees.find((employee) => employee.id === id)?.fullName || "";
  const storeName = (id) => state.stores.find((store) => store.id === id)?.name || "";

  const backupAll = async () => {
    setMessage("Preparing full backup...");
    try {
      const blob = await exportFullBackupExcel({ state, companyName });
      downloadBlob(blob, `${exportPrefix}-full-app-backup-${today}.xlsx`);
      setMessage("Full app backup downloaded. Keep it private.");
    } catch (error) {
      setMessage(error.message || "Backup could not be created.");
    }
  };

  const sheetGroups = [
    {
      title: "People and company",
      items: [
        {
          label: "Employees",
          filename: `${exportPrefix}-employees.csv`,
          rows: employees,
          columns: [
            ["Employee ID", (row) => row.employeeCode],
            ["Name", (row) => row.fullName],
            ["Phone", (row) => row.phone],
            ["Email", (row) => row.email],
            ["Store", (row) => storeName(row.storeId)],
            ["Designation", (row) => row.designation],
            ["Reporting manager", (row) => employeeName(row.reportingManagerId)],
            ["Salary", (row) => row.monthlySalary],
            ["Weekly off", (row) => row.weeklyOffDay],
            ["Active", (row) => (row.active === false ? "No" : "Yes")]
          ]
        },
        {
          label: "Stores",
          filename: `${exportPrefix}-stores.csv`,
          rows: state.stores,
          columns: [
            ["Code", (row) => row.code],
            ["Name", (row) => row.name],
            ["Address", (row) => row.address],
            ["Latitude", (row) => row.latitude],
            ["Longitude", (row) => row.longitude],
            ["Radius", (row) => row.radiusMeters],
            ["Geofence", (row) => (row.geofenceEnabled ? "On" : "Off")],
            ["Active", (row) => (row.active === false ? "No" : "Yes")]
          ]
        },
        {
          label: "App users",
          filename: `${exportPrefix}-app-users.csv`,
          rows: state.users,
          columns: [
            ["Name", (row) => row.name],
            ["Login", (row) => row.email],
            ["Role", (row) => row.role],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Stores", (row) => (row.storeIds || []).map(storeName).join(", ")],
            ["Active", (row) => (row.active === false ? "No" : "Yes")]
          ]
        }
      ]
    },
    {
      title: "Attendance and approvals",
      items: [
        {
          label: "Attendance punches",
          filename: `${exportPrefix}-attendance-punches.csv`,
          rows: scopedPunches,
          columns: [
            ["Date", (row) => row.date],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Type", (row) => row.type],
            ["Time", (row) => row.timestamp ? new Date(row.timestamp).toLocaleString("en-IN") : ""],
            ["Distance meters", (row) => row.distanceMeters ?? ""],
            ["Inside geofence", (row) => row.insideGeofence === false ? "No" : "Yes"],
            ["Coordinates", (row) => formatCoordinates(row.latitude, row.longitude)],
            ["Map link", (row) => mapsLink(row.latitude, row.longitude)]
          ]
        },
        {
          label: "Corrections",
          filename: `${exportPrefix}-corrections.csv`,
          rows: scopedCorrections,
          columns: [
            ["Date", (row) => row.date],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Type", (row) => row.type],
            ["Status", (row) => row.status],
            ["Reason", (row) => row.reason],
            ["Requested by", (row) => row.requestedBy],
            ["Reviewed by", (row) => row.reviewedBy]
          ]
        },
        {
          label: "Leave applications",
          filename: `${exportPrefix}-leave-applications.csv`,
          rows: (state.leaveApplications || []).filter((item) => employeeIds.has(item.employeeId)),
          columns: [
            ["From", (row) => row.fromDate],
            ["To", (row) => row.toDate],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Type", (row) => row.leaveType],
            ["Days", (row) => row.days],
            ["Status", (row) => row.status],
            ["Reason", (row) => row.reason]
          ]
        }
      ]
    },
    {
      title: "Work and sales",
      items: [
        {
          label: "Tasks",
          filename: `${exportPrefix}-tasks.csv`,
          rows: scopedTasks,
          columns: [
            ["Title", (row) => row.title],
            ["Assigned to", (row) => employeeName(row.assignedTo)],
            ["Assigned by", (row) => employeeName(row.assignedBy)],
            ["Due date", (row) => row.dueDate],
            ["Status", (row) => row.status],
            ["Priority", (row) => row.priority],
            ["Notes", (row) => row.notes]
          ]
        },
        {
          label: "Sales targets",
          filename: `${exportPrefix}-sales-targets.csv`,
          rows: scopedTargets,
          columns: [
            ["Month", (row) => row.month],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Target", (row) => row.targetAmount],
            ["Achieved", (row) => row.achievedAmount],
            ["Percent", (row) => `${targetProgress(row)}%`]
          ]
        }
      ]
    },
    {
      title: "Money and optical orders",
      items: [
        {
          label: "Optical orders",
          filename: `${exportPrefix}-optical-orders.csv`,
          rows: scopedSlips,
          columns: [
            ["Order", (row) => row.orderNumber],
            ["Date", (row) => row.date],
            ["Delivery", (row) => row.deliveryDate],
            ["Customer", (row) => row.customer?.name || row.customerName],
            ["Phone", (row) => row.customer?.phone],
            ["Salesperson", (row) => row.salespersonName],
            ["Order type", (row) => labelForOpticalOrderType(row.orderDetails?.orderType)],
            ["Products", (row) => (row.orderDetails?.products || row.orderDetails?.extraProducts || []).map((product) => `${product.quantity || 1} x ${product.name || opticalProductLabel(product.type)}`).join(" | ")],
            ["Subtotal", (row) => row.payment?.subtotalAmount ?? row.payment?.totalAmount],
            ["Discount", (row) => row.payment?.discountAmount || 0],
            ["Total", (row) => row.payment?.totalAmount],
            ["Advance", (row) => row.payment?.advanceReceived],
            ["Balance", (row) => computedOrderBalance(row)],
            ["Payment", (row) => row.payment?.paymentMode]
          ]
        },
        {
          label: "Advances / loans",
          filename: `${exportPrefix}-advances-loans.csv`,
          rows: scopedAdvances,
          columns: [
            ["Date", (row) => row.date],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Type", (row) => row.type],
            ["Amount", (row) => row.amount],
            ["Payroll month", (row) => row.payrollMonth],
            ["Status", (row) => row.status],
            ["Notes", (row) => row.notes]
          ]
        },
        {
          label: "Reimbursements",
          filename: `${exportPrefix}-reimbursements.csv`,
          rows: scopedReimbursements,
          columns: [
            ["Date", (row) => row.date],
            ["Employee", (row) => employeeName(row.employeeId)],
            ["Category", (row) => row.category],
            ["Amount", (row) => row.amount],
            ["Payroll month", (row) => row.payrollMonth],
            ["Status", (row) => row.status],
            ["Notes", (row) => row.notes]
          ]
        },
        {
          label: "Payroll items",
          filename: `${exportPrefix}-payroll-${selectedMonth}.csv`,
          rows: payrollRun?.items || [],
          columns: [
            ["Employee", (row) => row.employeeName],
            ["Gross", (row) => row.grossSalary],
            ["Leave deduction", (row) => row.attendanceSummary?.leaveDeductionAmount],
            ["Advance deduction", (row) => row.advanceDeduction],
            ["Reimbursement", (row) => row.reimbursementAmount],
            ["Staff welfare", (row) => row.staffWelfare],
            ["Net payable", (row) => row.netPayable]
          ]
        }
      ]
    },
    {
      title: "CRM, inventory and communication",
      items: [
        {
          label: "CRM products / inventory",
          filename: `${exportPrefix}-crm-products.csv`,
          rows: state.crmProducts || [],
          columns: [
            ["Code", (row) => row.productCode],
            ["Name", (row) => row.name],
            ["Type", (row) => row.productType],
            ["Brand", (row) => row.brand],
            ["Stock", (row) => row.stockQty],
            ["Reorder", (row) => row.reorderLevel],
            ["Cost", (row) => row.costPrice],
            ["Selling", (row) => row.sellingPrice]
          ]
        },
        {
          label: "CRM purchases",
          filename: `${exportPrefix}-crm-purchases.csv`,
          rows: state.crmPurchases || [],
          columns: [
            ["Date", (row) => row.billDate],
            ["Vendor", (row) => row.vendorName],
            ["Invoice", (row) => row.invoiceNumber],
            ["Product", (row) => row.productName],
            ["Qty", (row) => row.quantity],
            ["Amount", (row) => row.totalAmount],
            ["Paid", (row) => row.paidAmount],
            ["Status", (row) => row.status]
          ]
        },
        {
          label: "CRM communication",
          filename: `${exportPrefix}-crm-communication.csv`,
          rows: state.crmCommunications || [],
          columns: [
            ["Follow-up", (row) => row.followUpDate],
            ["Customer", (row) => row.customerName],
            ["Phone", (row) => row.customerPhone],
            ["Type", (row) => row.messageType],
            ["Status", (row) => row.status],
            ["Notes", (row) => row.notes]
          ]
        },
        {
          label: "CRM prescriptions",
          filename: `${exportPrefix}-crm-prescriptions.csv`,
          rows: state.crmPrescriptions || [],
          columns: [
            ["Date", (row) => row.date],
            ["Customer", (row) => row.customerName],
            ["Phone", (row) => row.customerPhone],
            ["Right", (row) => opticalPowerLine(row.rightEye)],
            ["Left", (row) => opticalPowerLine(row.leftEye)],
            ["ADD", (row) => row.addPower],
            ["PD", (row) => row.pd]
          ]
        },
        {
          label: "CRM appointments",
          filename: `${exportPrefix}-crm-appointments.csv`,
          rows: state.crmAppointments || [],
          columns: [
            ["Date", (row) => row.appointmentDate],
            ["Time", (row) => row.appointmentTime],
            ["Customer", (row) => row.customerName],
            ["Phone", (row) => row.customerPhone],
            ["Purpose", (row) => row.purpose],
            ["Status", (row) => row.status]
          ]
        },
        {
          label: "CN tracking",
          filename: `${exportPrefix}-cn-tracking.csv`,
          rows: state.cnRequests || [],
          columns: [
            ["CN Ticket", (row) => row.cnTicketNumber],
            ["CNA No", (row) => row.cnaNumber],
            ["Store", (row) => storeName(row.storeId)],
            ["Previous order", (row) => row.previousOrderNo],
            ["New order", (row) => row.newOrderNo],
            ["Reason", (row) => row.reasonForReturn],
            ["Qty", (row) => row.quantity],
            ["Status", (row) => row.status],
            ["Vendor", (row) => row.vendorName],
            ["CN number", (row) => row.cnNumber],
            ["CN date", (row) => row.cnDate],
            ["CN amount", (row) => row.cnAmount],
            ["Ledger updated", (row) => row.ledgerUpdated ? "Yes" : "No"],
            ["Created by", (row) => row.createdByName],
            ["Created at", (row) => row.createdAt]
          ]
        },
        {
          label: "Notifications",
          filename: `${exportPrefix}-notifications.csv`,
          rows: state.notifications || [],
          columns: [
            ["Time", (row) => row.createdAt],
            ["User", (row) => userDisplayName(state, row.userId)],
            ["Module", (row) => row.module],
            ["Title", (row) => row.title],
            ["Message", (row) => row.message],
            ["Linked record", (row) => row.linkedRecordId],
            ["Read", (row) => row.isRead ? "Yes" : "No"]
          ]
        },
        {
          label: "CN document extractions",
          filename: `${exportPrefix}-cn-document-extractions.csv`,
          rows: state.cnDocumentExtractions || [],
          columns: [
            ["Extracted at", (row) => row.extractedAt],
            ["CN case", (row) => row.cnCaseId],
            ["File", (row) => row.fileId],
            ["Status", (row) => row.extractionStatus],
            ["Confidence", (row) => row.confidenceScore],
            ["Final CN no", (row) => row.finalCnNumber],
            ["Final CN date", (row) => row.finalCnDate],
            ["Final CN amount", (row) => row.finalCnAmount],
            ["Verified by", (row) => userDisplayName(state, row.verifiedBy)],
            ["Verified at", (row) => row.verifiedAt],
            ["Remarks", (row) => row.remarks]
          ]
        },
        {
          label: "Company ledger entries",
          filename: `${exportPrefix}-company-ledger-entries.csv`,
          rows: state.companyLedgerEntries || [],
          columns: [
            ["Time", (row) => row.createdAt],
            ["Store", (row) => storeName(row.storeId)],
            ["Vendor", (row) => row.vendorName],
            ["Type", (row) => row.entryType],
            ["Amount", (row) => row.amount],
            ["Linked record", (row) => row.linkedRecordId],
            ["Notes", (row) => row.notes]
          ]
        },
        {
          label: "App file metadata",
          filename: `${exportPrefix}-app-files.csv`,
          rows: state.appFiles || [],
          columns: [
            ["Uploaded", (row) => row.uploadedAt],
            ["Module", (row) => row.module],
            ["Record type", (row) => row.recordType],
            ["Record ID", (row) => row.recordId],
            ["Store", (row) => storeName(row.storeId)],
            ["Staff", (row) => employeeName(row.staffId)],
            ["File type", (row) => row.fileType],
            ["Bucket", (row) => row.storageBucket],
            ["Path", (row) => row.storagePath],
            ["Original size", (row) => row.originalFileSizeBytes],
            ["Compressed size", (row) => row.compressedFileSizeBytes],
            ["Width", (row) => row.imageWidth],
            ["Height", (row) => row.imageHeight]
          ]
        },
        {
          label: "Bank reco",
          filename: `${exportPrefix}-bank-reco.csv`,
          rows: state.bankReco || [],
          columns: [
            ["Date", (row) => row.recoDate],
            ["Store", (row) => state.stores.find((store) => store.id === row.storeId)?.name || ""],
            ["Cash in hand", (row) => row.cashInHand],
            ["Bank credit", (row) => row.bankCredit],
            ["POS settlement", (row) => row.posMachineSettlement],
            ["UPI received", (row) => row.upiReceived],
            ["Card received", (row) => row.cardReceived],
            ["Status", (row) => row.status],
            ["Notes", (row) => row.notes]
          ]
        }
      ]
    },
    {
      title: "Audit",
      items: [
        {
          label: "Audit logs",
          filename: `${exportPrefix}-audit-logs.csv`,
          rows: state.auditLogs,
          columns: [
            ["Time", (row) => row.createdAt],
            ["Changed by", (row) => row.changedBy],
            ["Action", (row) => row.action],
            ["Entity", (row) => row.entityType],
            ["Reason", (row) => row.reason],
            ["Effective date", (row) => row.effectiveDate]
          ]
        }
      ]
    }
  ];

  return (
    <section>
      <Header title="Backup & Downloads" subtitle="One place for full backup and all important sheets." />
      {message ? <p className="notice">{message}</p> : null}
      <article className="panel backup-hero">
        <div>
          <span className="status-chip">Recommended</span>
          <h2>Full app backup</h2>
          <p>Downloads one Excel file with readable sheets plus restore JSON for rebuilding the company data later.</p>
        </div>
        <button className="primary-button" type="button" onClick={backupAll}>
          <Download size={17} /> Download full backup
        </button>
      </article>
      <div className="backup-download-grid">
        {sheetGroups.map((group) => (
          <article className="panel backup-group" key={group.title}>
            <h3>{group.title}</h3>
            <div className="button-row">
              {group.items.map((item) => (
                <button
                  className="secondary-button"
                  type="button"
                  key={item.filename}
                  onClick={() => downloadSheet(item.filename, item.rows, item.columns)}
                >
                  <FileSpreadsheet size={16} /> {item.label}
                </button>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdminUsersPanel({ state, updateState, user, employees = state.employees, embedded = false }) {
  const exportPrefix = filePrefix(state.settings);
  const defaultBusinessRoleKey = embedded && user.role === "super_admin" ? "store_manager" : "store_staff";
  const [form, setForm] = useState({
    role: embedded && user.role === "super_admin" ? "admin" : "employee",
    businessRoleKey: defaultBusinessRoleKey,
    employeeId: employees[0]?.id || "",
    name: "",
    email: "",
    password: "",
    storeIds: state.stores[0]?.id ? [state.stores[0].id] : [],
    allEmployees: false,
    permissions: permissionsForBusinessRole(defaultBusinessRoleKey)
  });
  const [message, setMessage] = useState("");
  const [savingLogin, setSavingLogin] = useState(false);
  const [loginSaveState, setLoginSaveState] = useState("idle");
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "" });
  const [passwordMessage, setPasswordMessage] = useState("");
  const visibleEmployeeIds = new Set(employees.map((employee) => employee.id));
  const visibleUsers = user.role === "super_admin"
    ? state.users
    : state.users.filter((row) => row.id === user.id || (row.employeeId && visibleEmployeeIds.has(row.employeeId)));

  const selectedEmployee = employees.find((employee) => employee.id === form.employeeId);
  const suggestedEmployeeEmail = selectedEmployee
    ? suggestedEmployeeLogin(selectedEmployee)
    : "";

  useEffect(() => {
    if (form.role === "employee" && selectedEmployee) {
      setForm((current) => ({
        ...current,
        name: selectedEmployee.fullName,
        email: current.email && !current.email.endsWith(".local") ? current.email : suggestedEmployeeEmail,
        storeIds: selectedEmployee.storeId ? [selectedEmployee.storeId] : []
      }));
    }
  }, [form.role, form.employeeId, selectedEmployee?.id, suggestedEmployeeEmail]);

  useEffect(() => {
    if (embedded && user.role === "super_admin" && form.role === "employee") {
      setForm((current) => ({ ...current, role: "admin", employeeId: "", name: current.name || "Store Admin" }));
    }
  }, [embedded, user.role, form.role]);

  const toggleStore = (storeId) => {
    setForm((current) => ({
      ...current,
      storeIds: current.storeIds.includes(storeId)
        ? current.storeIds.filter((id) => id !== storeId)
        : [...current.storeIds, storeId]
    }));
  };

  const togglePermission = (permission) => {
    setForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [permission]: !current.permissions?.[permission]
      }
    }));
  };

  const applyBusinessRolePreset = (roleKey) => {
    const preset = businessRolePresetByKey(roleKey);
    const selectedEmployeeForPreset = state.employees.find((employee) => employee.id === form.employeeId);
    setLoginSaveState("idle");
    if (!preset) {
      setForm((current) => ({
        ...current,
        businessRoleKey: "",
        permissions: permissionsForBusinessRole(""),
        allEmployees: false
      }));
      return;
    }
    setForm((current) => ({
      ...current,
      businessRoleKey: preset.key,
      role: user.role === "super_admin" ? preset.appRole : "employee",
      employeeId: preset.appRole === "super_admin" ? "" : current.employeeId,
      name: current.name || selectedEmployeeForPreset?.fullName || preset.designation,
      allEmployees: preset.allEmployees,
      storeIds: preset.allEmployees
        ? state.stores.map((store) => store.id)
        : selectedEmployeeForPreset?.storeId
          ? [selectedEmployeeForPreset.storeId]
          : current.storeIds,
      permissions: permissionsForBusinessRole(preset.key)
    }));
  };

  const saveLogin = async () => {
    setMessage("");
    setLoginSaveState("idle");
    if (!form.email || !form.password) {
      setMessage("Login email or phone and password are required.");
      setLoginSaveState("error");
      return;
    }

    setSavingLogin(true);
    setLoginSaveState("saving");
    setMessage("Saving...");
    try {
      const authEmail = loginIdentifierToAuthEmail(form.email);
      const existingUser = state.users.find((item) => item.email === authEmail || (form.role === "employee" && item.employeeId === form.employeeId));
      const businessPreset = businessRolePresetByKey(form.businessRoleKey);
      const normalizedPermissions = form.role === "super_admin"
        ? {
            ...permissionsForBusinessRole("owner_super_admin"),
            __businessRoleKey: businessPreset?.key || "owner_super_admin",
            __businessRoleLabel: businessPreset?.label || "Owner / Super Admin"
          }
        : {
            ...permissionsForBusinessRole(form.businessRoleKey),
            ...form.permissions,
            allEmployees: form.allEmployees,
            __businessRoleKey: businessPreset?.key || "",
            __businessRoleLabel: businessPreset?.label || "Unassigned"
          };
      const loginUser = {
        id: existingUser?.id || uuid("user"),
        authUserId: existingUser?.authUserId || null,
        name: selectedEmployee?.fullName || form.name,
        email: authEmail,
        role: user.role === "super_admin" ? form.role : "employee",
        employeeId: form.employeeId || null,
        storeIds:
          form.role === "employee" || user.role !== "super_admin"
            ? [selectedEmployee?.storeId].filter(Boolean)
            : form.allEmployees
              ? state.stores.map((store) => store.id)
              : form.storeIds,
        permissions: normalizedPermissions,
        active: true
      };

      let savedLoginUser = await persistRequiredEntity("users", loginUser, "User login");
      savedLoginUser = await refetchSavedEntity("users", savedLoginUser?.id || loginUser.id, savedLoginUser || loginUser);
      const authResult = await createAuthAccount({ email: authEmail, password: form.password });
      if (authResult.user?.id) {
        loginUser.authUserId = authResult.user.id;
        savedLoginUser = await persistRequiredEntity("users", loginUser, "User login");
        savedLoginUser = await refetchSavedEntity("users", savedLoginUser?.id || loginUser.id, savedLoginUser || loginUser);
      }
      updateState((current) => ({
        ...current,
        users: existingUser
          ? current.users.map((item) => (item.id === existingUser.id ? savedLoginUser : item))
          : [...current.users, savedLoginUser]
      }));
      if (
        !existingUser ||
        existingUser.role !== savedLoginUser.role ||
        existingUser.permissions?.__businessRoleKey !== savedLoginUser.permissions?.__businessRoleKey
      ) {
        await notifySuperAdmins(state, updateState, {
          module: "Users",
          title: "User Role Updated",
          message: `${savedLoginUser.name || displayLoginId(savedLoginUser.email)} role was changed to ${businessRoleLabelFromPermissions(savedLoginUser.permissions) || savedLoginUser.role.replace("_", " ")}.`,
          priority: "high",
          entityType: "user",
          entityId: savedLoginUser.id,
          storeId: savedLoginUser.storeIds?.[0] || "",
          createdBy: user.id
        });
      }
      setMessage(
        authResult.alreadyExists
          ? `Saved successfully. Auth user may already exist for ${displayLoginId(authEmail)}.`
          : `Saved successfully. Login created for ${displayLoginId(authEmail)}.`
      );
      setLoginSaveState("saved");
    } catch (error) {
      console.error("User login save failed", error);
      setMessage(`Could not save. Please try again. ${error.message || ""}`.trim());
      setLoginSaveState("error");
    } finally {
      setSavingLogin(false);
    }
  };

  const changeOwnPassword = async () => {
    setPasswordMessage("");
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      setPasswordMessage("Password must be at least 6 characters.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage("Both password boxes must match.");
      return;
    }
    if (!hasSupabaseConfig) {
      setPasswordMessage("Password change is available after Supabase login is active.");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
    if (error) {
      setPasswordMessage(error.message);
      return;
    }
    setPasswordForm({ newPassword: "", confirmPassword: "" });
    setPasswordMessage("Your password has been changed.");
  };

  const toggleUserActive = async (row) => {
    if (row.id === user.id) {
      setMessage("You cannot deactivate your own login while using it.");
      return;
    }
    if (user.role !== "super_admin" && !visibleUsers.some((item) => item.id === row.id)) {
      setMessage("You can only edit users from your assigned stores.");
      return;
    }
    const updated = { ...row, active: row.active === false };
    setMessage("Saving...");
    try {
      const savedUser = await persistRequiredEntity("users", updated, "User");
      updateState((current) => ({
        ...current,
        users: current.users.map((item) => (item.id === row.id ? savedUser : item))
      }));
      setMessage(`Saved successfully. ${displayLoginId(row.email)} is now ${savedUser.active ? "active" : "inactive"}.`);
    } catch (error) {
      console.error("User active status save failed", error);
      setMessage(`Could not save. Please try again. ${error.message || ""}`.trim());
    }
  };
  const userSheetColumns = [
    ["Name", (row) => row.name],
    ["Login ID", (row) => displayLoginId(row.email)],
    ["Type", (row) => row.role.replace("_", " ")],
    ["Business role", (row) => businessRoleLabelFromPermissions(row.permissions)],
    ["Designation", (row) => {
      const employee = state.employees.find((item) => item.id === row.employeeId);
      return employee?.designation || (row.role === "admin" ? "Admin" : row.role === "super_admin" ? "Super Admin" : "");
    }],
    ["Employee", (row) => state.employees.find((employee) => employee.id === row.employeeId)?.fullName || ""],
    ["Stores", (row) => (row.storeIds || []).map((id) => state.stores.find((store) => store.id === id)?.code).filter(Boolean).join(", ")],
    ["Status", (row) => (row.active === false ? "Inactive" : "Active")]
  ];
  const userMeta = (row) => {
    const employee = state.employees.find((item) => item.id === row.employeeId);
    const stores = (row.storeIds || []).map((id) => state.stores.find((store) => store.id === id)?.code).filter(Boolean).join(", ");
    const businessRole = businessRoleLabelFromPermissions(row.permissions) || employee?.designation || row.role.replace("_", " ");
    return `${businessRole} · ${employee?.fullName || stores || "No store"} · ${displayLoginId(row.email)}`;
  };
  const renderUserDetails = (row) => {
    const employee = state.employees.find((item) => item.id === row.employeeId);
    const storeNames = (row.storeIds || []).map((id) => state.stores.find((store) => store.id === id)?.name).filter(Boolean);
    return (
      <div className="detail-fields">
        <div className="detail-title-row">
          <div>
            <h3>{row.name}</h3>
            <p>{row.role.replace("_", " ")}</p>
          </div>
          <span className={`status-pill ${row.active === false ? "danger" : "ok"}`}>{row.active === false ? "Inactive" : "Active"}</span>
        </div>
        <p><strong>Login ID:</strong> {displayLoginId(row.email)}</p>
        <p><strong>User type:</strong> {row.role.replace("_", " ")}</p>
        <p><strong>Business role:</strong> {businessRoleLabelFromPermissions(row.permissions)}</p>
        <p><strong>Linked employee:</strong> {employee?.fullName || "-"}</p>
        <p><strong>Designation:</strong> {employee?.designation || (row.role === "super_admin" ? "Super Admin" : row.role === "admin" ? "Admin" : "-")}</p>
        <p><strong>Store access:</strong> {storeNames.length ? storeNames.join(", ") : row.role === "super_admin" ? "All stores" : "-"}</p>
        <p><strong>Permissions:</strong> {activePermissionSummary(row.permissions)}</p>
        <div className="button-row">
          <button className={row.active === false ? "primary-button" : "secondary-button danger"} type="button" onClick={() => toggleUserActive(row)}>
            {row.active === false ? "Activate login" : "Deactivate login"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className={embedded ? "panel-section" : ""}>
      {embedded ? (
        <div className="section-title-row">
          <div>
            <h2>Admin access and password tools</h2>
            <p>Employee login is now created inside Add/Edit Employee. Use this for store admins and access control.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-app-users.csv`, visibleUsers, userSheetColumns)}>
            <FileSpreadsheet size={17} /> Download sheet
          </button>
        </div>
      ) : (
        <Header title="Users" subtitle="Create employee and admin login IDs from inside the app.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-admin-users.csv`, state.users, userSheetColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
        </Header>
      )}
      <div className="split-layout">
        <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
          <h2>{embedded ? "Create admin login" : "Create login ID and password"}</h2>
          <label>
            Business role preset
            <select value={form.businessRoleKey || ""} onChange={(event) => applyBusinessRolePreset(event.target.value)}>
              <option value="">Unassigned</option>
              {BUSINESS_ROLE_PRESETS.map((preset) => (
                <option key={preset.key} value={preset.key}>{preset.label}</option>
              ))}
            </select>
          </label>
          <p className="helper-text">
            {businessRolePresetByKey(form.businessRoleKey)?.description || "Unassigned role. Select a preset to auto-fill access."}
          </p>
          <div className="login-type-switch admin-user-switch" role="group" aria-label="User type">
            {!embedded ? (
              <button
                className={form.role === "employee" ? "active" : ""}
                type="button"
                onClick={() => {
                  setLoginSaveState("idle");
                  setForm({
                    ...form,
                    role: "employee",
                    businessRoleKey: "store_staff",
                    permissions: permissionsForBusinessRole("store_staff"),
                    allEmployees: false
                  });
                }}
              >
                <UserRound size={17} /> Employee
              </button>
            ) : null}
            {user.role === "super_admin" ? (
              <button
                className={form.role === "admin" ? "active" : ""}
                type="button"
                onClick={() => {
                  setLoginSaveState("idle");
                  setForm({
                    ...form,
                    role: "admin",
                    businessRoleKey: form.businessRoleKey === "owner_super_admin" ? "store_manager" : form.businessRoleKey,
                    employeeId: "",
                    permissions: form.businessRoleKey === "owner_super_admin" ? permissionsForBusinessRole("store_manager") : form.permissions,
                    allEmployees: form.businessRoleKey === "owner_super_admin" ? false : form.allEmployees
                  });
                }}
              >
                <ShieldCheck size={17} /> Store Admin
              </button>
            ) : null}
            {user.role === "super_admin" && !embedded ? (
              <button
                className={form.role === "super_admin" ? "active" : ""}
                type="button"
                onClick={() => {
                  setLoginSaveState("idle");
                  setForm({
                    ...form,
                    role: "super_admin",
                    businessRoleKey: "owner_super_admin",
                    employeeId: "",
                    allEmployees: true,
                    storeIds: state.stores.map((store) => store.id),
                    permissions: permissionsForBusinessRole("owner_super_admin")
                  });
                }}
              >
                <ShieldCheck size={17} /> Owner
              </button>
            ) : null}
          </div>
          <div className="form-grid">
            {form.role === "employee" ? (
              <label>
                Employee
                <select value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
            <Input label="Admin name / designation" value={form.name} onChange={(name) => setForm({ ...form, name })} placeholder="Example: Noida Store Manager" />
          )}
          <Input
            label="Login email or phone"
            value={form.email}
            onChange={(email) => setForm({ ...form, email })}
            placeholder="9876543210 or name@company.com"
          />
            <Input label="Password for this login" type="password" value={form.password} onChange={(password) => setForm({ ...form, password })} />
          </div>
          {form.role === "admin" ? (
            <div className="store-checks">
              <label className="toggle-line">
                <input
                  type="checkbox"
                  checked={form.allEmployees}
                  onChange={(event) => setForm({ ...form, allEmployees: event.target.checked, storeIds: event.target.checked ? state.stores.map((store) => store.id) : form.storeIds })}
                />
                All employees access (CEO / Director / HR Head)
              </label>
              <label>
                Link to employee profile
                <select value={form.employeeId || ""} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
                  <option value="">No linked employee</option>
                  {state.employees.filter((employee) => employee.active !== false).map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName} - {employee.designation || "Employee"}
                    </option>
                  ))}
                </select>
              </label>
              <strong>Store access</strong>
              {state.stores.map((store) => (
                <label className="toggle-line" key={store.id}>
                  <input type="checkbox" disabled={form.allEmployees} checked={form.storeIds.includes(store.id)} onChange={() => toggleStore(store.id)} />
                  {store.name}
                </label>
              ))}
              <strong>Responsibilities</strong>
              <p className="helper-text">Preset applied. Open a group below only if you want to fine-tune this person.</p>
              {PERMISSION_GROUPS.map((group, index) => (
                <details className="permission-group" key={group.title} open={index === 0}>
                  <summary>{group.title}</summary>
                  {group.items.map((key) => (
                    <label className="toggle-line" key={key}>
                      <input type="checkbox" checked={Boolean(form.permissions?.[key])} onChange={() => togglePermission(key)} />
                      {PERMISSION_LABELS[key] || key}
                    </label>
                  ))}
                </details>
              ))}
            </div>
          ) : (
            <p className="notice">
              Employee will login with this email/phone and password, then open the Punch In / Punch Out screen.
            </p>
          )}
          <button className="primary-button" type="button" disabled={savingLogin} onClick={saveLogin}>
            {savingLogin ? "Saving..." : loginSaveState === "saved" ? "Saved ✓" : loginSaveState === "error" ? "Could not save" : "Save login"}
          </button>
          {message ? <p className="notice">{message}</p> : null}
        </form>
        <div className="stacked-panels">
          <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
            <h2>Change my password</h2>
            <div className="form-grid">
              <Input label="New password" type="password" value={passwordForm.newPassword} onChange={(newPassword) => setPasswordForm({ ...passwordForm, newPassword })} />
              <Input label="Confirm password" type="password" value={passwordForm.confirmPassword} onChange={(confirmPassword) => setPasswordForm({ ...passwordForm, confirmPassword })} />
            </div>
            <button className="primary-button" type="button" onClick={changeOwnPassword}>
              Change my password
            </button>
            {passwordMessage ? <p className="notice">{passwordMessage}</p> : null}
            <p className="helper-text">
              For a forgotten password, employee can ask admin for reset. Hard reset of another person's Supabase password needs the secure reset function or Supabase Authentication screen.
            </p>
          </form>
          <DetailList
            rows={visibleUsers}
            getTitle={(row) => row.name}
            getMeta={userMeta}
            getStatus={(row) => (row.active === false ? "OFF" : row.role === "super_admin" ? "OWNER" : row.role.toUpperCase())}
            renderDetails={renderUserDetails}
          />
        </div>
      </div>
    </section>
  );
}

function SettingsPanel({ state, updateState, user }) {
  const [settings, setSettings] = useState(state.settings);
  const [settingsTab, setSettingsTab] = useState("company");
  const [logoMessage, setLogoMessage] = useState("");
  const [ownerForm, setOwnerForm] = useState({
    name: "",
    loginId: "",
    password: ""
  });
  const [setupMessage, setSetupMessage] = useState("");
  const updateModule = (key, enabled) => {
    setSettings((current) => ({
      ...current,
      enabledModules: {
        ...(current.enabledModules || {}),
        [key]: enabled
      }
    }));
  };

  const selectLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoMessage("Please select an image file.");
      return;
    }
    try {
      setLogoMessage("Compressing and uploading logo...");
      const upload = await uploadAppImage({
        file,
        module: "company",
        recordType: "company_settings",
        recordId: "global",
        fileType: "other",
        uploadedBy: user.id
      });
      setSettings((current) => ({ ...current, companyLogo: upload.storagePath || upload.path || upload.url }));
      setLogoMessage("Logo added. Click Save settings to keep it.");
    } catch (error) {
      setLogoMessage(error.message || "Logo could not be added.");
    }
  };

  const save = async () => {
    const log = createAuditLog({
      entityType: "app_settings",
      entityId: "global",
      action: "updated",
      oldValue: state.settings,
      newValue: settings,
      reason: "Global settings update",
      effectiveDate: new Date().toISOString().slice(0, 10),
      user
    });
    const savedSettings = await persistRequiredEntity("app_settings", { id: "global", settings }, "App settings");
    await persistAuditLog(log);
    updateState((current) => ({ ...current, settings: savedSettings.settings || settings, auditLogs: [log, ...current.auditLogs] }));
  };

  const saveCompanyLaunch = async () => {
    setSetupMessage("Saving company setup...");
    try {
      if (!settings.companyName) throw new Error("Company name is required.");
      if (!ownerForm.name || !ownerForm.loginId) throw new Error("Owner name and owner login are required.");
      const authEmail = loginIdentifierToAuthEmail(ownerForm.loginId);
      const existingOwner = state.users.find((item) => item.email === authEmail);
      const existingOwnerEmployee = existingOwner?.employeeId
        ? state.employees.find((employee) => employee.id === existingOwner.employeeId)
        : null;
      const ownerEmployee = {
        ...(existingOwnerEmployee || {}),
        id: existingOwnerEmployee?.id || uuid("emp"),
        employeeCode: existingOwnerEmployee?.employeeCode || "OWNER-001",
        fullName: ownerForm.name,
        phone: existingOwnerEmployee?.phone || "",
        email: authEmail,
        storeId: existingOwnerEmployee?.storeId || null,
        designation: existingOwnerEmployee?.designation || "Owner / Super Admin",
        reportingManagerId: existingOwnerEmployee?.reportingManagerId || null,
        monthlySalary: Number(existingOwnerEmployee?.monthlySalary || 0),
        weeklyOffDay: existingOwnerEmployee?.weeklyOffDay || "",
        joiningDate: existingOwnerEmployee?.joiningDate || new Date().toISOString().slice(0, 10),
        exitDate: existingOwnerEmployee?.exitDate || null,
        active: true,
        openingLeaveBalance: Number(existingOwnerEmployee?.openingLeaveBalance || 0),
        openingLoanBalance: Number(existingOwnerEmployee?.openingLoanBalance || 0),
        incentiveEligible: existingOwnerEmployee?.incentiveEligible ?? false,
        reimbursementEligible: existingOwnerEmployee?.reimbursementEligible ?? true,
        bankDetails: existingOwnerEmployee?.bankDetails || "",
        notes: existingOwnerEmployee?.notes || "Company owner profile."
      };
      const ownerUser = {
        id: existingOwner?.id || uuid("user"),
        authUserId: existingOwner?.authUserId || null,
        name: ownerForm.name,
        email: authEmail,
        role: "super_admin",
        employeeId: ownerEmployee.id,
        storeIds: state.stores.map((store) => store.id),
        permissions: { all: true },
        active: true
      };

      const savedSettings = await persistRequiredEntity("app_settings", { id: "global", settings }, "Company settings");
      const savedOwnerEmployee = await persistRequiredEntity("employees", ownerEmployee, "Owner employee");
      let savedOwnerUser = await persistRequiredEntity("users", ownerUser, "Owner login");

      let loginMessage = "Owner profile saved.";
      if (ownerForm.password) {
        const authResult = await createAuthAccount({ email: authEmail, password: ownerForm.password });
        if (authResult.user?.id) {
          ownerUser.authUserId = authResult.user.id;
          savedOwnerUser = await persistRequiredEntity("users", ownerUser, "Owner login");
        }
        loginMessage = authResult.alreadyExists
          ? "Owner login profile saved. This login may already exist in Authentication."
          : "Owner login created.";
      }

      const log = createAuditLog({
        entityType: "company_setup",
        entityId: "global",
        action: "owner_setup",
        oldValue: { settings: state.settings, owner: existingOwner || null },
        newValue: { settings: savedSettings.settings || settings, owner: savedOwnerUser },
        reason: "Company launch setup",
        effectiveDate: new Date().toISOString().slice(0, 10),
        user
      });
      await persistAuditLog(log);
      updateState((current) => ({
        ...current,
        settings: savedSettings.settings || settings,
        employees: existingOwnerEmployee
          ? current.employees.map((employee) => (employee.id === savedOwnerEmployee.id ? savedOwnerEmployee : employee))
          : [...current.employees, savedOwnerEmployee],
        users: existingOwner
          ? current.users.map((item) => (item.id === existingOwner.id ? savedOwnerUser : item))
          : [...current.users, savedOwnerUser],
        auditLogs: [log, ...current.auditLogs]
      }));
      setOwnerForm({ name: "", loginId: "", password: "" });
      setSetupMessage(`${loginMessage} Now this company can login as Super Admin and add staff.`);
    } catch (error) {
      setSetupMessage(error.message || "Company setup could not be saved.");
    }
  };

  return (
    <section>
      <Header title="Settings" subtitle="Company branding, shift, grace time, outside-location mode and welfare rule." />
      <form className="panel form-panel" onSubmit={(event) => event.preventDefault()}>
        <div className="settings-tabs" role="tablist" aria-label="Settings sections">
          {[
            ["company", "Company"],
            ["owner", "Owner"],
            ["modules", "Modules"],
            ["rules", "Rules"]
          ].map(([key, label]) => (
            <button key={key} className={settingsTab === key ? "active" : ""} type="button" onClick={() => setSettingsTab(key)}>
              {label}
            </button>
          ))}
        </div>

        {settingsTab === "company" ? (
          <div className="settings-layer">
            <div className="brand-settings-card">
              <BrandMark settings={settings} />
              <div>
                <strong>{getCompanyName(settings)}</strong>
                <span>{getAppName(settings)}</span>
              </div>
              <label className="logo-upload">
                Upload logo
                <input type="file" accept="image/*" onChange={(event) => selectLogo(event.target.files?.[0])} />
              </label>
              {settings.companyLogo ? (
                <button className="mini-button" type="button" onClick={() => setSettings({ ...settings, companyLogo: "" })}>
                  Remove logo
                </button>
              ) : null}
            </div>
            {logoMessage ? <p className="notice">{logoMessage}</p> : null}
            <div className="form-grid">
              <Input label="Company name" value={settings.companyName} onChange={(companyName) => setSettings({ ...settings, companyName })} />
              <Input label="App name" value={getAppName(settings)} onChange={(appName) => setSettings({ ...settings, appName })} placeholder={PRODUCT_NAME} />
            </div>
            <button className="primary-button" type="button" onClick={save}>
              Save company settings
            </button>
          </div>
        ) : null}

        {settingsTab === "owner" ? (
          <div className="settings-layer">
            <div className="company-launch-card">
              <div>
                <span className="status-chip">Owner identity</span>
                <h2>Set up owner / super admin</h2>
                <p>This creates a real employee profile for the owner, not just a login.</p>
              </div>
              <div className="launch-steps">
                <span>1. Owner profile</span>
                <span>2. Super Admin login</span>
                <span>3. Add staff</span>
              </div>
            </div>
            <div className="form-grid">
              <Input label="Owner / Super Admin name" value={ownerForm.name} onChange={(name) => setOwnerForm({ ...ownerForm, name })} placeholder="Example: Company Owner" />
              <Input label="Owner login email or phone" value={ownerForm.loginId} onChange={(loginId) => setOwnerForm({ ...ownerForm, loginId })} placeholder="9876543210 or owner@company.com" />
              <Input label="Owner password" type="password" value={ownerForm.password} onChange={(password) => setOwnerForm({ ...ownerForm, password })} placeholder="Example: Company@123" />
            </div>
            <button className="secondary-button" type="button" onClick={saveCompanyLaunch}>
              Save owner and create login
            </button>
            {setupMessage ? <p className="notice">{setupMessage}</p> : null}
          </div>
        ) : null}

        {settingsTab === "modules" ? (
          <div className="settings-layer">
            <div className="company-launch-card">
              <div>
                <span className="status-chip">Modules</span>
                <h2>Choose what this company uses</h2>
                <p>Keep attendance simple for companies that do not need sales targets or extra HR modules.</p>
              </div>
            </div>
            <div className="module-toggle-grid">
              {[
                ["attendance", "Attendance"],
                ["work", "Tasks / Work"],
                ["salesTargets", "Sales targets"],
                ["opticalOrders", "Optical Orders"],
                ["payroll", "Payroll"],
                ["advances", "Advances / loans"],
                ["reimbursements", "Reimbursements"],
                ["incentives", "Incentives"]
              ].map(([key, label]) => (
                <label className="module-toggle-card" key={key}>
                  <span>
                    <strong>{label}</strong>
                    <small>{isModuleEnabled(settings, key) ? "Visible in app" : "Hidden for this company"}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={isModuleEnabled(settings, key)}
                    onChange={(event) => updateModule(key, event.target.checked)}
                  />
                </label>
              ))}
            </div>
            <button className="primary-button" type="button" onClick={save}>
              Save modules
            </button>
          </div>
        ) : null}

        {settingsTab === "rules" ? (
          <div className="settings-layer">
            <div className="form-grid">
              <Input label="Shift start" type="time" value={settings.defaultShiftStart} onChange={(defaultShiftStart) => setSettings({ ...settings, defaultShiftStart })} />
              <Input label="Shift end" type="time" value={settings.defaultShiftEnd} onChange={(defaultShiftEnd) => setSettings({ ...settings, defaultShiftEnd })} />
              <Input label="Full day out by" type="time" value={settings.fullDayOutBy} onChange={(fullDayOutBy) => setSettings({ ...settings, fullDayOutBy })} />
              <Input label="Half day cutoff" type="time" value={settings.halfDayCutoff} onChange={(halfDayCutoff) => setSettings({ ...settings, halfDayCutoff })} />
              <Input label="Grace minutes" type="number" value={settings.graceMinutes} onChange={(graceMinutes) => setSettings({ ...settings, graceMinutes: Number(graceMinutes) })} />
              <Input label="Staff welfare per present day" type="number" value={settings.staffWelfarePerPresentDay} onChange={(staffWelfarePerPresentDay) => setSettings({ ...settings, staffWelfarePerPresentDay: Number(staffWelfarePerPresentDay) })} />
              <label>
                Outside geofence
                <select value={settings.outsideGeofenceMode} onChange={(event) => setSettings({ ...settings, outsideGeofenceMode: event.target.value })}>
                  <option value="exception">Allow punch and mark outside</option>
                  <option value="block">Block punch outside store</option>
                </select>
              </label>
            </div>
            <button className="primary-button" type="button" onClick={save}>
              Save rules
            </button>
          </div>
        ) : null}

      </form>
    </section>
  );
}

function AuditPanel({ state }) {
  const exportPrefix = filePrefix(state.settings);
  const auditColumns = [
    ["When", (row) => row.createdAt ? new Date(row.createdAt).toLocaleString("en-IN") : ""],
    ["Entity", (row) => row.entityType],
    ["Action", (row) => row.action],
    ["Changed by", (row) => row.changedBy],
    ["Effective", (row) => row.effectiveDate],
    ["Reason", (row) => row.reason],
    ["Old value", (row) => row.oldValue || ""],
    ["New value", (row) => row.newValue || ""]
  ];
  return (
    <section>
      <Header title="Audit Logs" subtitle="Important changes include old value, new value, reason and effective date.">
        <button className="secondary-button" type="button" onClick={() => downloadSheet(`${exportPrefix}-audit-logs.csv`, state.auditLogs, auditColumns)}>
          <FileSpreadsheet size={17} /> Download sheet
        </button>
      </Header>
      <DataTable
        rows={state.auditLogs}
        columns={auditColumns.slice(0, 6)}
      />
    </section>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "", inputMode, autoComplete, pattern, maxLength }) {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        pattern={pattern}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function DataTable({ rows, columns, actions, plain = false }) {
  return (
    <div className={`table-wrap ${plain ? "" : "panel"}`}>
      <table>
        <thead>
          <tr>
            {columns.map(([label]) => (
              <th key={label}>{label}</th>
            ))}
            {actions ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row) => (
              <tr key={row.id || row.employeeId}>
                {columns.map(([label, render]) => (
                  <td key={label} data-label={label}>
                    {render(row)}
                  </td>
                ))}
                {actions ? (
                  <td className="actions-cell" data-label="Actions">
                    {actions(row)}
                  </td>
                ) : null}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)}>No records yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function debugJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function SaveHealthCheckScreen({ state, currentUser, mode, onBack }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);

  const runHealthCheck = async () => {
    const steps = [];
    const addStep = (title, ok, detail) => {
      steps.push({ title, ok, detail });
      setResults([...steps]);
    };

    setRunning(true);
    setResults([]);
    try {
      addStep("Supabase configuration", hasSupabaseConfig, {
        mode,
        hasSupabaseConfig,
        message: hasSupabaseConfig ? "Supabase URL/key are present." : "Supabase URL/key are missing. App will only use local state."
      });
      if (!hasSupabaseConfig) return;

      const authResult = await supabase.auth.getUser();
      if (authResult.error) {
        console.error("Save health auth error", authResult.error);
        addStep("Current logged-in Supabase user", false, authResult.error);
        return;
      }
      const authUser = authResult.data.user || null;
      addStep("Current logged-in Supabase user", Boolean(authUser), {
        id: authUser?.id || null,
        email: authUser?.email || null,
        appCurrentUser: currentUser ? { id: currentUser.id, email: currentUser.email, role: currentUser.role, employeeId: currentUser.employeeId, storeIds: currentUser.storeIds } : null
      });

      const profileByAuth = (state.users || []).find((user) => user.authUserId === authUser?.id);
      const profileByEmail = (state.users || []).find((user) => String(user.email || "").toLowerCase() === String(authUser?.email || "").toLowerCase());
      addStep("Current user role/profile", Boolean(profileByAuth || profileByEmail || currentUser), {
        matchedByAuthId: profileByAuth || null,
        matchedByEmail: profileByEmail || null,
        currentUser: currentUser || null
      });

      const testId = crypto.randomUUID ? crypto.randomUUID() : uuid("save-health");
      const now = new Date().toISOString();
      const testRow = {
        id: testId,
        test_key: `save-health-${Date.now()}`,
        test_value: "inserted",
        created_by: currentUser?.id || "",
        created_by_email: authUser?.email || currentUser?.email || "",
        inactive: false,
        created_at: now,
        updated_at: now
      };

      const insertResult = await supabase.from("app_save_health_checks").insert(testRow).select().single();
      if (insertResult.error) {
        console.error("Save health insert error", insertResult.error);
        addStep("Insert test row", false, insertResult.error);
        return;
      }
      addStep("Insert test row", true, insertResult.data);

      const readResult = await supabase.from("app_save_health_checks").select("*").eq("id", testId).single();
      if (readResult.error) {
        console.error("Save health read error", readResult.error);
        addStep("Read same test row", false, readResult.error);
        return;
      }
      addStep("Read same test row", true, readResult.data);

      const updateResult = await supabase
        .from("app_save_health_checks")
        .update({ test_value: "updated", inactive: true, updated_at: new Date().toISOString() })
        .eq("id", testId)
        .select()
        .single();
      if (updateResult.error) {
        console.error("Save health update error", updateResult.error);
        addStep("Update test row", false, updateResult.error);
        return;
      }
      addStep("Update test row", true, updateResult.data);

      const deleteResult = await supabase.from("app_save_health_checks").delete().eq("id", testId);
      if (deleteResult.error) {
        console.error("Save health delete error", deleteResult.error);
        addStep("Delete test row", false, deleteResult.error);
        return;
      }
      addStep("Delete test row", true, { deletedId: testId });
    } catch (error) {
      console.error("Save health check failed", error);
      addStep("Unexpected health-check failure", false, error);
    } finally {
      setRunning(false);
    }
  };

  return (
    <main className="app-shell">
      <section className="panel-section">
        <Header title="Supabase Save Health Check" subtitle="Temporary debug page for insert, read, update and delete testing.">
          <button className="secondary-button" type="button" onClick={onBack}>Back to app</button>
        </Header>
        <article className="panel form-panel">
          <p className="notice">Run this once after installing the health-check SQL. It will show the exact Supabase error if writes are blocked by RLS, missing tables, wrong columns, or login mismatch.</p>
          <button className="primary-button" type="button" disabled={running} onClick={runHealthCheck}>
            {running ? "Running..." : "Run Supabase Save Health Check"}
          </button>
        </article>
        <div className="panel-grid two">
          {results.map((step) => (
            <article className="panel" key={step.title}>
              <h2>{step.ok ? "✓" : "✕"} {step.title}</h2>
              <pre className="debug-output">{debugJson(step.detail)}</pre>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const { state, mode, loading, error, updateState } = useAppState();
  const [saveFailure, setSaveFailure] = useState(null);

  const currentUser = useMemo(() => {
    if (!state) return null;
    return state.users.find((user) => user.id === state.currentUserId) || null;
  }, [state]);

  useEffect(() => {
    const onSaveFailed = (event) => setSaveFailure(event.detail || null);
    window.addEventListener("framesbay-save-failed", onSaveFailed);
    return () => window.removeEventListener("framesbay-save-failed", onSaveFailed);
  }, []);

  useEffect(() => {
    if (!state) return;
    document.title = `${getCompanyName(state.settings)} ${getAppName(state.settings)}`;
  }, [state?.settings]);

  if (loading) return <div className="app-loading">Loading app...</div>;
  if (error) return <div className="app-loading error-text">{error}</div>;
  if (!state) return null;
  if (!currentUser) {
    return <PublicHome state={state} mode={mode} updateState={updateState} />;
  }
  const showSaveHealthCheck = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("save-health") === "1";
  const logout = async () => {
    if (mode === "supabase") await supabase.auth.signOut();
    updateState((current) => ({ ...current, currentUserId: "" }));
  };

  if (showSaveHealthCheck) {
    return <SaveHealthCheckScreen state={state} currentUser={currentUser} mode={mode} onBack={() => window.history.pushState({}, "", window.location.pathname)} />;
  }

  return (
    <>
      <div className="top-strip">
        <span className="top-brand">
          <BrandMark settings={state.settings} /> {getCompanyName(state.settings)} {getAppName(state.settings)}
        </span>
        <InstallAppButton />
        <button type="button" onClick={logout}>
          <LogOut size={15} /> Logout
        </button>
      </div>
      {currentUser.role === "employee" ? (
        <EmployeeDashboard state={state} user={currentUser} updateState={updateState} onLogout={logout} />
      ) : (
        <AdminShell state={state} user={currentUser} updateState={updateState} onLogout={logout} />
      )}
      {saveFailure && (
        <div className="modal-backdrop schema-error-backdrop" role="dialog" aria-modal="true" onClick={() => setSaveFailure(null)}>
          <section className="modal schema-error-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <p className="eyebrow">Online save</p>
                <h2>{saveFailure.title || "Save Failed"}</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setSaveFailure(null)} aria-label="Close save error">
                <XCircle size={22} />
              </button>
            </div>
            <p className="schema-error-message">{saveFailure.message || "Your data was not saved online."}</p>
            <div className="schema-error-details">
              <strong>Technical details</strong>
              <pre>{saveFailure.technicalDetails || "Unknown Supabase error."}</pre>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(saveFailure.technicalDetails || "")}
              >
                Copy Error
              </button>
              <button type="button" className="primary" onClick={() => setSaveFailure(null)}>
                Close
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
